import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

/* ═══════════════════════════════════════════════════════════════
   PiARIS v1.8.2 — (Pi)ARIS
   Accessibility Remediation Intelligence & Scanning
   Enterprise Compliance Platform · PWA-Ready
   © 2026 Pi Consultants · pi-consulting.us

   Shared constants, helpers, components, and demo data
   are imported from ./shared/. All product views remain inline.
   ═══════════════════════════════════════════════════════════════ */

import { APP_VERSION, APP_EXPECTED_LINES, APP_NAME, APP_FULL, PI_NAME, PI_WEB,
  W, VAL_CATS, PAGES, PDFS, P, P_LIGHT, P_DARK, applyTheme, F, FC, PI_LOGO, SC, PCOLS, CSS, FIX_DEFAULTS, WCAG_KB,
  AI_WEB_SYSTEM_PROMPT, AI_PDF_SYSTEM_PROMPT } from "./shared/constants";
import { countBySev, sumBySev, countRemediated, countOpen, totalElements,
  kbNav, sS, sG, hashStr, detectSiteType, agg,
  classifyFixCategory, fixCategoryLabel, normFix, shortNum, classifyAiError, normalizeSelector,
  mergeAiSuggestionsIntoIssues,
  groupWorkbenchIssues, bboxToOverlayPct,
  computeComplianceScore,
  wpId, docId, issueId, canonicalUrl, entityKey, issueFingerprint, matchesSearch, htmlForAi } from "./shared/helpers";
import { auditPdfBase64, diffAudits } from "./shared/pdf-audit-client";
import { E, PiLogo, PiArisLockup, A11yIcon, Pill, SevB, Ring, Card, MC } from "./shared/components";
import { SubViewHero, PageWithSideRails,
  WatchtowerShowcase, DocMayaShowcase, Pa11yLabShowcase, DeltaReconShowcase } from "./shared/products";
import { ProductLockup } from "./shared/products/ProductLockup";
import { AuditReportView } from "./shared/audit";
import { buildScan } from "./shared/demo-data";
import AdminPanel from "./admin/AdminPanel";

/* Module-load banner — fires exactly once per page load, not per render.
   Previously this lived inside the PARIS component body and was emitted on
   every render (hundreds per second under hot state churn). */
if (typeof window !== "undefined" && !window.__piarisBannerLogged) {
  window.__piarisBannerLogged = true;
  /* CRA replaces process.env.REACT_APP_* with a literal at build time. The old
     `typeof process !== "undefined"` guard is always FALSE in the browser (a
     bare `process` isn't defined; only `process.env.X` is replaced), so it
     short-circuited the SHA to "dev". Use the value directly. */
  var _buildDate = process.env.REACT_APP_BUILD_DATE || new Date().toISOString().split("T")[0];
  var _buildSha = (process.env.REACT_APP_BUILD_SHA || "dev").slice(0, 7);
  window.__PARIS_BUILD__ = { version: APP_VERSION, sha: _buildSha, date: _buildDate };
  console.log("[PiARIS] v"+APP_VERSION+" | build "+_buildSha+" | Expected lines: "+APP_EXPECTED_LINES+" | Build: "+_buildDate);
}

/* ═══ MAIN COMPONENT ═══ */
export default function PARIS(){

  /* ═══════════════════════════════════════════════════════════════
   * MODULE: app-router                            [status: ready]
   * PURPOSE: Path-based URL routing for all app views
   *
   * READS:     window.location (pathname + search)
   * WRITES:    window.history (pushState)
   * CALLS:     URLSearchParams
   * EFFECTS:   popstate listener (defined below at line ~121)
   * EMITS:     browser history entries
   *
   * EXPORTS:   { ROUTE_MAP, slugFor, navigateTo, goHome, _route }
   * IMPORTS:   None (pure browser API)
   *
   * CLOSURE:   ROUTE_REV is built from ROUTE_MAP; must stay co-located
   * ENTANGLED: setMod() at line ~102 reads webAuditTab/docAuditTab/
   *            labWebTab/labDocTab to preserve sub-tab state across
   *            module switches. When extracting, these must be passed
   *            in or routing needs a subscribe API.
   * ═══════════════════════════════════════════════════════════════ */

  /* ── Path-based routing ──
     URL pattern: /{tab}/{sessionId}
     Examples: /dashboard/sess-xxx, /web-audit/sess-xxx, /doc-audit/sess-xxx
     Sub-tabs get their own slug so each is bookmarkable:
       /web-audit/sess-xxx         → Web Audit › Pages list
       /web-audit-report/sess-xxx  → Web Audit › Audit Report
       /doc-audit/sess-xxx         → Doc Audit › Documents list
       /doc-audit-report/sess-xxx  → Doc Audit › Audit Report
     Homepage: / (no session loaded) */
  var ROUTE_MAP={
    dashboard:"dashboard",
    "audit-report":"audit", /* legacy — redirects to /web-audit-report */
    "web-audit":"webAudit",
    "web-audit-report":"webAudit",  /* same mod, different sub-tab */
    "doc-audit":"docAudit",
    "doc-audit-report":"docAudit",  /* same mod, different sub-tab */
    "delta-recon":"compare",
    "pa11y-web":"aiLabWeb","pa11y-web-report":"aiLabWeb",
    "pa11y-doc":"aiLabDoc","pa11y-doc-report":"aiLabDoc",
    "pa11y-lab":"aiLabWeb",  /* legacy redirect */
    "pdf-validator":"validator",
    remediation:"remediation",reports:"reports","import":"import",
    licenses:"licenses",docmaya:"docMaya"
  };
  /* Which slug to use when a mod+subtab pair needs a URL. Priority-match:
     checks specific slugs first, falls back to mod-level. */
  function slugFor(mod,subTab){
    if(mod==="webAudit"&&subTab==="report")return"web-audit-report";
    if(mod==="webAudit")return"web-audit";
    if(mod==="docAudit"&&subTab==="report")return"doc-audit-report";
    if(mod==="docAudit")return"doc-audit";
    if(mod==="aiLabWeb"&&subTab==="report")return"pa11y-web-report";
    if(mod==="aiLabWeb")return"pa11y-web";
    if(mod==="aiLabDoc"&&subTab==="report")return"pa11y-doc-report";
    if(mod==="aiLabDoc")return"pa11y-doc";
    return ROUTE_REV[mod]||"dashboard";
  }
  var ROUTE_REV={};Object.keys(ROUTE_MAP).forEach(function(k){
    /* First-in wins for reverse mapping so basic slugs (web-audit, doc-audit) are canonical */
    if(!ROUTE_REV[ROUTE_MAP[k]])ROUTE_REV[ROUTE_MAP[k]]=k;
  });
  var _route=(function(){
    try{
      var parts=window.location.pathname.replace(/^\/|\/$/g,"").split("/");
      /* Also support legacy ?scan= for old links */
      var p=new URLSearchParams(window.location.search);
      var slug=parts[0]||"";
      var tab=slug&&ROUTE_MAP[slug]?ROUTE_MAP[slug]:null;
      var scan=parts[1]||p.get("scan")||null;
      if(!tab&&scan)tab="dashboard";
      /* Sub-tab derivation: only set when the URL explicitly encodes it via
         the -report suffix. Otherwise leave null so the default (list) wins. */
      var subTab=null;
      if(slug==="web-audit-report"||slug==="doc-audit-report"||slug==="audit-report"||slug==="pa11y-web-report"||slug==="pa11y-doc-report")subTab="report";
      return{scan:scan,tab:tab,subTab:subTab,slug:slug};
    }catch(e){return{scan:null,tab:null,subTab:null,slug:""}}
  })();
  /* Admin hash takes precedence over path-based init when present, so reloading on
     #/admin/users lands the user in the app shell on the Users admin tab. */
  function _readAdminHashSection(){
    if(typeof window==="undefined")return null;
    var h=window.location.hash||"";
    var m=h.match(/^#\/admin(?:\/([a-z0-9-]+))?\/?$/i);
    if(!m)return null;
    var s=(m[1]||"licenses").toLowerCase();
    /* Slug aliases so external links survive minor wording shifts.
       "activity-log" → "activity" was the live bug PR-5.6 fixed:
       pasting #/admin/activity-log returned "licenses" (the fallback)
       and rendered the Licenses table instead of Activity Log. */
    var ALIAS={"activity-log":"activity","docmaya":"docmaya-keys"};
    if(ALIAS[s])s=ALIAS[s];
    /* PR-K.4: intake-queue added so the "Open Intake Queue →" deep link
       from the internal notify email (#/admin/intake-queue) lands on the
       right tab instead of falling back to "licenses". */
    var known=["overview","licenses","users","intake-queue","docmaya-keys","revenue","activity"];
    return known.indexOf(s)>=0?s:"licenses";
  }
  var _adminHashOnInit=_readAdminHashSection();
  /* PA11YLab modules show app view even without a session ID (fresh start) */
  var _initView=_adminHashOnInit?"app":(_route.scan?"app":(_route.tab==="aiLabWeb"||_route.tab==="aiLabDoc")?"app":"home");
  var _v=useState(_initView),view=_v[0],setView=_v[1];
  var _initMod=_adminHashOnInit?"licenses":(_route.tab||"dashboard");
  var _mod=useState(_initMod),mod=_mod[0],_setModRaw=_mod[1];
  var _currentScanId=useRef(_route.scan);

  /* Navigate to a path-based URL. Accepts an optional subTab so web/doc audit
     sub-tabs get their own shareable URL (/web-audit-report/sess-xxx etc). */
  function navigateTo(tab,scanId,subTab){
    var slug=slugFor(tab,subTab);
    var path=scanId?"/"+slug+"/"+scanId:"/";
    window.history.pushState({tab:tab,scan:scanId,subTab:subTab},"",path);
  }
  /* Wrap setMod: switch tab + update URL */
  function setMod(m){
    _setModRaw(m);
    /* PA11YLab modules use their own session ref, not the Watchtower scan ID */
    if(m==="aiLabWeb"||m==="aiLabDoc"){
      var lsid=_labSessionId.current;
      var sub=null;
      if(m==="aiLabWeb")sub=labWebTab;
      else if(m==="aiLabDoc")sub=labDocTab;
      if(lsid){navigateTo(m,lsid,sub)}
      else{setView("app");window.history.pushState({tab:m},"","/"+slugFor(m,sub))}
      return;
    }
    var sid=_currentScanId.current;
    if(sid){
      var sub=null;
      if(m==="webAudit")sub=webAuditTab;
      else if(m==="docAudit")sub=docAuditTab;
      navigateTo(m,sid,sub);
    }
  }
  /* Go home: clear state + navigate to / */
  function goHome(){
    _currentScanId.current=null;
    setView("home");_setModRaw("dashboard");setSel(null);
    window.history.pushState({},"","/");
  }
  /* Browser back/forward support */
  useEffect(function(){
    function onPop(){
      var parts=window.location.pathname.replace(/^\/|\/$/g,"").split("/");
      var slug=parts[0]||"";
      var tab=slug&&ROUTE_MAP[slug]?ROUTE_MAP[slug]:null;
      var scan=parts[1]||null;
      /* Sub-tab from popstate slug — only set when the URL ends in -report */
      var sub=(slug==="web-audit-report"||slug==="doc-audit-report"||slug==="audit-report"||slug==="pa11y-web-report"||slug==="pa11y-doc-report")?"report":"list";
      if(tab==="aiLabWeb"||tab==="aiLabDoc"){
        /* PA11Y Lab: separate session ref */
        _setModRaw(tab);setView("app");
        if(tab==="aiLabWeb"){_setLabWebTabRaw(sub);setLabWebStage(sub==="report"?"audit":"add");}
        if(tab==="aiLabDoc"){_setLabDocTabRaw(sub);setLabDocStage(sub==="report"?"audit":"add");}
        if(scan&&scan!==_labSessionId.current){
          _labSessionId.current=scan;
          loadPageSession(scan);
        }
      } else if(scan&&tab){
        _setModRaw(tab);
        if(tab==="webAudit")_setWebAuditTabRaw(sub);
        if(tab==="docAudit")_setDocAuditTabRaw(sub);
        if(scan!==_currentScanId.current){
          _currentScanId.current=scan;
          loadSessionIntoApp({id:scan,domain:""});
        }
      } else {
        setView("home");_setModRaw("dashboard");
      }
    }
    window.addEventListener("popstate",onPop);
    return function(){window.removeEventListener("popstate",onPop)};
  },[]);

  /* Admin hash sync — listens for #/admin/<section> changes (back/forward,
     pasted URL edits) and switches mod+adminTab. Tab clicks update the hash
     via setAdminTab(); this effect handles inbound changes.
     PR-5.6: every hash change also refetches entitlements so the header
     tier pill never lags behind admin state. Cheap (single GET to
     /api/user/entitlements) and guards the regression where the pill
     read "Signed in · Free" on Overview and only flipped to Admin
     after the first tab click. */
  useEffect(function(){
    if(typeof window==="undefined")return;
    function onHash(){
      var section=_readAdminHashSection();
      if(!section)return;
      _setModRaw("licenses");
      setView("app");
      /* silent: hash is already authoritative; don't re-write it */
      setAdminTab(section,{silent:true});
      refetchEntitlements();
    }
    window.addEventListener("hashchange",onHash);
    return function(){window.removeEventListener("hashchange",onHash)};
  },[]);
  /* ═══ END MODULE: app-router ═══ */

  /* ═══════════════════════════════════════════════════════════════
   * MODULE: auth-sessions                         [status: wip]
   * PURPOSE: Authentication + scan session list management
   *
   * READS:     /.auth/me, /api/sessions
   * WRITES:    authUser, authLoading, userSessions, sessionsLoading
   * CALLS:     fetch
   * EFFECTS:   Initial auth check on mount
   * EMITS:     Toast notifications
   *
   * EXPORTS:   { authUser, userSessions, loadUserSessions, signIn, signOut }
   * IMPORTS:   showToast (from ui-toast module)
   *
   * CLOSURE:   None
   * ENTANGLED: Session list is re-fetched by many call sites; consider
   *            a sessions store with subscribe API before extraction.
   * ═══════════════════════════════════════════════════════════════ */

  /* ── Auth & sessions ── */
  var _authUser=useState(null),authUser=_authUser[0],setAuthUser=_authUser[1];
  var _authLoading=useState(true),authLoading=_authLoading[0],setAuthLoading=_authLoading[1];
  /* Effective entitlements for the signed-in user. Shape:
     { admin: bool, products: {watchtower,deltaRecon,docMaya,pa11yLab}, licenses: [...] }
     null when not loaded yet OR when unauthenticated. */
  var _entitlements=useState(null),entitlements=_entitlements[0],setEntitlements=_entitlements[1];
  var _userSessions=useState([]),userSessions=_userSessions[0],setUserSessions=_userSessions[1];
  var _sessionsLoading=useState(false),sessionsLoading=_sessionsLoading[0],setSessionsLoading=_sessionsLoading[1];

  /* ── Core scan data ── */
  var _d=useState(""),domain=_d[0],setDomain=_d[1];
  var _wp=useState([]),wp=_wp[0],setWp=_wp[1];            /* web pages array */
  var _docs=useState([]),dc=_docs[0],setDc=_docs[1];      /* documents array */
  var _isDemo=useState(false),isDemo=_isDemo[0],setIsDemo=_isDemo[1];
  var _scanMeta=useState(null),scanMeta=_scanMeta[0],setScanMeta=_scanMeta[1];
  var _brokenLinks=useState([]),brokenLinks=_brokenLinks[0],setBrokenLinks=_brokenLinks[1];
  var _bgJob=useState(null),bgJob=_bgJob[0],setBgJob=_bgJob[1]; /* {phase,processed,total,updated} or null */
  /* Sub-tabs inside Web Audit / Doc Audit modules: "list" (default) or "report" */
  /* v0.6.10: initialize sub-tab state from URL slug so /web-audit-report/sess-xxx
     lands directly on the report view without a flash of the list view. */
  var _initWebSub=(_route.tab==="webAudit"&&_route.subTab==="report")?"report":"list";
  var _initDocSub=(_route.tab==="docAudit"&&_route.subTab==="report")?"report":"list";
  var _webAuditTab=useState(_initWebSub),webAuditTab=_webAuditTab[0],_setWebAuditTabRaw=_webAuditTab[1];
  var _docAuditTab=useState(_initDocSub),docAuditTab=_docAuditTab[0],_setDocAuditTabRaw=_docAuditTab[1];
  /* Wrapped setters — changing the sub-tab updates the URL too */
  function setWebAuditTab(t){
    _setWebAuditTabRaw(t);
    var sid=_currentScanId.current;
    if(sid&&mod==="webAudit")navigateTo("webAudit",sid,t);
  }
  function setDocAuditTab(t){
    _setDocAuditTabRaw(t);
    var sid=_currentScanId.current;
    if(sid&&mod==="docAudit")navigateTo("docAudit",sid,t);
  }
  /* ═══════════════════════════════════════════════════════════════
   * MODULE: pa11y-lab-state                       [status: ready]
   * PURPOSE: React state + refs for PA11YLab Web Audit / Doc Audit
   *
   * READS:     _route.tab, _route.subTab, _route.scan, mod
   * WRITES:    labWebTab, labDocTab, _labSessionId, labSession,
   *            labSessions, labDocs, labSelDoc
   * CALLS:     navigateTo() on sub-tab change
   * EFFECTS:   See pa11y-lab-functions module (loadPageSession on mount)
   * EMITS:     history.pushState via navigateTo
   *
   * EXPORTS:   { labWebTab, setLabWebTab, labDocTab, setLabDocTab,
   *              _labSessionId, labSession, setLabSession,
   *              labSessions, setLabSessions, labDocs, setLabDocs,
   *              labSelDoc, setLabSelDoc }
   * IMPORTS:   navigateTo, _route, mod (from app-router module)
   *
   * CLOSURE:   None — fully self-contained
   * ENTANGLED: None
   * ═══════════════════════════════════════════════════════════════ */

  /* ── PA11Y Lab sub-tabs (same pattern as web/doc audit) ── */
  var _initLabWebSub=(_route.tab==="aiLabWeb"&&_route.subTab==="report")?"report":"list";
  var _initLabDocSub=(_route.tab==="aiLabDoc"&&_route.subTab==="report")?"report":"list";
  var _labWebTab=useState(_initLabWebSub),labWebTab=_labWebTab[0],_setLabWebTabRaw=_labWebTab[1];
  var _labDocTab=useState(_initLabDocSub),labDocTab=_labDocTab[0],_setLabDocTabRaw=_labDocTab[1];
  /* PA11Y workflow stages (Add/Upload → Audit → Remediate → Package) — the
     chevron-flow stage the user is on, per surface (web / doc). */
  var _initLabWebStage=(_route.tab==="aiLabWeb"&&_route.subTab==="report")?"audit":"add";
  var _initLabDocStage=(_route.tab==="aiLabDoc"&&_route.subTab==="report")?"audit":"add";
  var _labWebStage=useState(_initLabWebStage),labWebStage=_labWebStage[0],setLabWebStage=_labWebStage[1];
  var _labDocStage=useState(_initLabDocStage),labDocStage=_labDocStage[0],setLabDocStage=_labDocStage[1];
  var labAuditedRef=useRef("");   // last HTML auto-audited, so entering Audit runs the engines once per page
  useEffect(function(){
    if(labWebStage==="audit"&&aiHtml&&aiHtml.trim()&&labAuditedRef.current!==aiHtml&&Object.keys(labRunning).length===0&&!aiLoading){
      labAuditedRef.current=aiHtml; labRunAllWeb();
    }
  },[labWebStage,aiHtml]);
  var _labSessionId=useRef((_route.tab==="aiLabWeb"||_route.tab==="aiLabDoc")?_route.scan:null);
  var _labSession=useState(null),labSession=_labSession[0],setLabSession=_labSession[1];
  var _labSessions=useState([]),labSessions=_labSessions[0],setLabSessions=_labSessions[1];
  var _labDocs=useState([]),labDocs=_labDocs[0],setLabDocs=_labDocs[1];
  var _labSelDoc=useState(0),labSelDoc=_labSelDoc[0],setLabSelDoc=_labSelDoc[1];
  function setLabWebTab(t){
    _setLabWebTabRaw(t);
    var sid=_labSessionId.current;
    if(sid&&mod==="aiLabWeb")navigateTo("aiLabWeb",sid,t);
  }
  function setLabDocTab(t){
    _setLabDocTabRaw(t);
    var sid=_labSessionId.current;
    if(sid&&mod==="aiLabDoc")navigateTo("aiLabDoc",sid,t);
  }
  var _sel=useState(null),sel=_sel[0],setSel=_sel[1];       /* selected list index */
  var _hiIss=useState(null),hiIss=_hiIss[0],setHiIss=_hiIss[1]; /* highlighted issue index */
  /* Watchtower bulk workflow (Part 3): staged Load → Triage → Audit →
     Remediate → Package over the loaded scan. bulkSel = selected item keys
     ("w:"+idx web page, "d:"+idx document) for the working set. */
  var _bulkStage=useState("load"),bulkStage=_bulkStage[0],setBulkStage=_bulkStage[1];
  var _bulkSel=useState({}),bulkSel=_bulkSel[0],setBulkSel=_bulkSel[1];

  /* ── Delta Recon — scan comparison state ── */
  var _cmpDomain=useState(null),cmpDomain=_cmpDomain[0],setCmpDomain=_cmpDomain[1]; /* selected domain to compare */
  var _cmpA=useState(null),cmpA=_cmpA[0],setCmpA=_cmpA[1]; /* session ID for Scan A */
  var _cmpB=useState(null),cmpB=_cmpB[0],setCmpB=_cmpB[1]; /* session ID for Scan B */
  var _cmpDataA=useState(null),cmpDataA=_cmpDataA[0],setCmpDataA=_cmpDataA[1]; /* loaded scan A data */
  var _cmpDataB=useState(null),cmpDataB=_cmpDataB[0],setCmpDataB=_cmpDataB[1]; /* loaded scan B data */
  var _cmpResult=useState(null),cmpResult=_cmpResult[0],setCmpResult=_cmpResult[1]; /* diff result */
  var _cmpLoading=useState(false),cmpLoading=_cmpLoading[0],setCmpLoading=_cmpLoading[1];
  var _cmpStep=useState(1),cmpStep=_cmpStep[0],setCmpStep=_cmpStep[1]; /* guided flow step: 1=domain, 2=scans, 3=results */

  /* ── Platform-level scan picker (Session 46) ──
     Shared scan loader modal used by both Watchtower Dashboard and Delta Recon.
     Scan data is platform-level — not owned by any product. The picker merges
     cloud sessions (userSessions from /api/sessions) and local saved scans
     (savedScans from browser storage) into a single grouped-by-domain view. */
  /** Whether the scan picker modal is open */
  var _scanPickerOpen=useState(false),scanPickerOpen=_scanPickerOpen[0],setScanPickerOpen=_scanPickerOpen[1];
  /** Where to route after loading a scan: "watchtower" → Dashboard, "compare" → Delta Recon step 2 */
  var _scanPickerTarget=useState("watchtower"),scanPickerTarget=_scanPickerTarget[0],setScanPickerTarget=_scanPickerTarget[1];

  /* ── Device preview toolbar + pop-out workbench (Session 46) ──
     Device toolbar lets auditors preview the client's site at mobile/tablet/desktop widths
     inside the split-pane iframes. Accessibility issues change by viewport — a nav that
     works on desktop might become a hamburger with keyboard traps on mobile.
     Pop-out opens the workbench table in a separate window so practitioners with two
     monitors can have full-viewport preview on one screen and full workbench on the other. */
  /** Preview device width for audited site iframes: "full"|"mobile"|"tablet"|"desktop" */
  var _previewDevice=useState("desktop"),previewDevice=_previewDevice[0],setPreviewDevice=_previewDevice[1];
  /** Whether workbench is popped out to a separate window (main window hides workbench table) */
  var _popOutActive=useState(false),popOutActive=_popOutActive[0],setPopOutActive=_popOutActive[1];
  /** Whether THIS window is the pop-out workbench (detected from ?mode=workbench URL param). */
  var isPopOutWorkbench=typeof window!=="undefined"&&window.location.search.indexOf("mode=workbench")>-1;
  /** Pending page index to select after workbench pop-out finishes loading session data */
  var _pendingWbPage=useState(null),pendingWbPage=_pendingWbPage[0],setPendingWbPage=_pendingWbPage[1];
  /** Device width presets in px. "full" uses 100% of the pane width.
      Values match standard breakpoints: iPhone SE (375), iPad (768), laptop (1280). */
  var DEVICE_WIDTHS={mobile:375,tablet:768,desktop:1280,full:"100%"};
  /* Device viewports (w×h in CSS px) for the TRUE-TO-VIEWPORT scaled preview:
     the iframe is rendered at the device's real width (so the site's own media
     queries fire — desktop shows its horizontal menu, mobile its hamburger) and
     then transform-scaled to fit whatever pane width is available, instead of
     being squeezed into the pane (which forced every site to its mobile layout). */
  var DEVICE_VP={mobile:{w:375,h:812,i:"\u{1F4F1}",l:"Mobile"},tablet:{w:768,h:1024,i:"\u{1F4F2}",l:"Tablet"},desktop:{w:1280,h:900,i:"\u{1F5A5}️",l:"Desktop"}};
  /* PA11Y Lab preview: chosen viewport + measured pane width (ResizeObserver). */
  var _labPvDev=useState((typeof window!=="undefined"&&window.innerWidth<900)?"mobile":"desktop"),labPvDev=_labPvDev[0],setLabPvDev=_labPvDev[1];
  var _labPvW=useState(0),labPvW=_labPvW[0],setLabPvW=_labPvW[1];
  /* Watchtower split-pane: measured pane width for the same scaled preview. */
  var _wtPaneW=useState(0),wtPaneW=_wtPaneW[0],setWtPaneW=_wtPaneW[1];

  /* Mobile detection + split-pane toggle */
  var _isMobile=useState(typeof window!=="undefined"&&window.innerWidth<900),isMobile=_isMobile[0],setIsMobile=_isMobile[1];
  var _navOpen=useState(false),navOpen=_navOpen[0],setNavOpen=_navOpen[1];
  var _exportOpen=useState(false),exportOpen=_exportOpen[0],setExportOpen=_exportOpen[1];
  var _inspPane=useState("original"),inspPane=_inspPane[0],setInspPane=_inspPane[1];
  var _panesLocked=useState(true),panesLocked=_panesLocked[0],setPanesLocked=_panesLocked[1];
  var panesLockedRef=React.useRef(true);
  /* Keep ref in sync with state — scroll handlers read the ref (not stale closure) */
  panesLockedRef.current=panesLocked;

  /* ── Dark Mode (Session 49) ── */
  var _darkMode=useState(function(){
    var saved=typeof localStorage!=="undefined"?localStorage.getItem("piaris-theme"):null;
    if(saved)return saved==="dark";
    return typeof window!=="undefined"&&window.matchMedia&&window.matchMedia("(prefers-color-scheme:dark)").matches;
  }),darkMode=_darkMode[0],setDarkMode=_darkMode[1];
  /* Apply theme synchronously before render — P must be correct when JSX evaluates.
     The key prop on root div forces full re-mount so all children pick up new P values. */
  applyTheme(darkMode);
  useEffect(function(){
    if(typeof localStorage!=="undefined")localStorage.setItem("piaris-theme",darkMode?"dark":"light");
    /* Also set document background for areas outside the React root */
    document.body.style.background=P.bg;
    document.body.style.color=P.slate;
  },[darkMode]);
  function toggleDarkMode(){setDarkMode(function(prev){return!prev})}

  useEffect(function(){
    function onResize(){setIsMobile(window.innerWidth<900)}
    window.addEventListener("resize",onResize);
    return function(){window.removeEventListener("resize",onResize)};
  },[]);

  /* Entitlements + profile refetch — extracted so it can be re-triggered
     on hash change (PR-5.6) without re-running the whole /.auth/me +
     loadUserSessions dance. The header tier pill reads `authUser.tier`
     and the product tile gating reads `entitlements.products`; without
     a refetch, navigating Overview → Licenses left the pill on a stale
     "Free" until the next mount-level fetch. Two parallel requests so
     either landing first updates its slice. */
  function refetchEntitlements(){
    var ent = fetch("/api/user/entitlements",{credentials:"include"})
      .then(function(r){return r.ok?r.json():null})
      .then(function(e){
        if(e && e.products) setEntitlements(e);
      })
      .catch(function(){});
    var prof = fetch("/api/user",{credentials:"include"})
      .then(function(r){return r.ok?r.json():null})
      .then(function(p){
        if(p && !p.error){
          setAuthUser(function(prev){
            return prev ? Object.assign({}, prev, p) : p;
          });
        }
      })
      .catch(function(){});
    return Promise.all([ent, prof]);
  }

  /* Auth — check login status on mount */
  function recheckAuth(){
    setAuthLoading(true);
    fetch("/.auth/me",{cache:"no-store"}).then(function(r){return r.json()}).then(function(data){
      if(data&&data.clientPrincipal){
        var p=data.clientPrincipal;
        var u={id:p.userId,name:p.userDetails,provider:p.identityProvider,roles:p.userRoles||[]};
        setAuthUser(u);
        fetch("/api/user",{credentials:"include"}).then(function(r){return r.json()}).then(function(profile){
          if(profile&&!profile.error){setAuthUser(Object.assign({},u,profile))}
        }).catch(function(){});
        /* Effective entitlements — used to dim product tiles the user
           isn't licensed for. Admin users see admin:true and we treat
           every product as enabled. Anonymous users get default-off. */
        refetchEntitlements();
        loadUserSessions();
      } else {
        setAuthUser(null);
        setEntitlements(null);
      }
      setAuthLoading(false);
    }).catch(function(){setAuthLoading(false)});
  }
  useEffect(function(){
    recheckAuth();
    /* Re-check auth when tab becomes visible again (handles redirect back from Microsoft login) */
    function onVisible(){
      if(document.visibilityState==="visible"){recheckAuth()}
    }
    document.addEventListener("visibilitychange",onVisible);
    /* bfcache restore after same-tab OAuth redirect */
    function onPageShow(e){if(e.persisted){recheckAuth()}}
    window.addEventListener("pageshow",onPageShow);
    /* One-shot retry 1.5s after mount — covers full-reload after OAuth redirect
       where cookies may not be available on the very first /.auth/me fetch */
    var retryTimer=setTimeout(function(){recheckAuth()},1500);
    return function(){
      document.removeEventListener("visibilitychange",onVisible);
      window.removeEventListener("pageshow",onPageShow);
      clearTimeout(retryTimer);
    };
  },[]);

  function loadUserSessions(){
    setSessionsLoading(true);
    fetch("/api/sessions?limit=200",{credentials:"include"}).then(function(r){return r.json()}).then(function(data){
      if(data&&data.sessions){
        setUserSessions(data.sessions);
        /* Conservative backfill: only match local scans where exactly ONE cloud
           session exists for the normalized domain. Scans with 0 or multiple
           matches stay as orphans and get flagged in the UI for deletion —
           fuzzy timestamp matching can produce wrong results. */
        sG("aris-scans-index").then(function(idx){
          if(!idx||!idx.length)return;
          function norm(d){return (d||"").toLowerCase().replace(/^www\./,"").replace(/\/.*$/,"")}
          var bucket={};
          data.sessions.forEach(function(cs){
            var key=norm(cs.domain);
            if(!key)return;
            if(!bucket[key])bucket[key]=[];
            bucket[key].push(cs.id);
          });
          var changed=false,matched=0,ambiguous=0,noMatch=0;
          idx.forEach(function(entry){
            if(entry.sessionId)return;
            if(!entry.domain){noMatch++;return}
            var cands=bucket[norm(entry.domain)]||[];
            if(cands.length===1){entry.sessionId=cands[0];changed=true;matched++}
            else if(cands.length>1)ambiguous++;
            else noMatch++;
          });
          console.log("[Backfill] matched="+matched+" ambiguous="+ambiguous+" noMatch="+noMatch+" cloudTotal="+data.sessions.length);
          if(changed){
            setSavedScans(idx);
            sS("aris-scans-index",idx);
          }
        });
      }
      setSessionsLoading(false);
    }).catch(function(){setSessionsLoading(false)});
  }

  /** Load a cloud session — lazy loading: summary first, then page stubs, detail on demand */
  function loadSessionIntoApp(session){
    if(!session)return;
    showToast("\u23F3 Loading "+session.domain+"...");

    /* Step 1: Fetch summary (1 API call — no page data) */
    fetch("/api/session-stream/"+session.id+"/summary",{credentials:"include"})
      .then(function(r){
        if(!r.ok)throw new Error("summary "+r.status);
        return r.json();
      })
      .then(function(summaryData){
        if(!summaryData||!summaryData.scan){
          /* Try legacy fallback for old sessions */
          return fetch("/api/session/"+session.id,{credentials:"include"}).then(function(r){return r.json()}).then(function(legacy){
            if(legacy&&!legacy.error&&(legacy.webPages||legacy.documents)){
              setDomain(legacy.domain||"");
              setWp(legacy.webPages||[]);setDc(legacy.documents||[]);setIsDemo(false);
              setView("app");_setModRaw("dashboard");setSel(null);setDashDrill(null);
              _currentScanId.current=session.id;navigateTo("dashboard",session.id);
              showToast("\u2713 Session loaded (legacy): "+legacy.domain);
            } else { showToast("No scan data found","error"); }
          });
        }

        var dom=summaryData.scan.domain||session.domain||"";
        setDomain(dom);
        setScanMeta({sessionId:summaryData.scan.sessionId||null,templateTokens:(summaryData.scanMeta||{}).templateTokens||0,
          templateSamples:(summaryData.scanMeta||{}).skippedTemplateUrls||[],templateDedup:summaryData.scan.templateDedup||null,
          completedAt:summaryData.scan.completedAt||null,summary:summaryData.summary||null,
          brokenCmsTemplate:summaryData.brokenCmsTemplate||0,brokenHref:summaryData.brokenHref||0,
          aiExecutiveSummary:summaryData.aiExecutiveSummary||null,
          authRequired:summaryData.authRequired||{count:0,liveAudited:0,samples:[]},
          crossOriginRedirects:summaryData.crossOriginRedirects||{count:0,samples:[]}});
        setBrokenLinks(summaryData.brokenLinks||[]);

        /* Process documents */
        var dcs=(summaryData.documents||[]).map(function(d,idx){
          var pdfIssues=(d.accessibilityIssues||[]).map(function(ai,ii){
            var wcag=ai.wcag||"";var info=W[wcag]||{n:ai.issue||"Unknown",l:"A",p:"Unknown",r:ai.fix||""};
            return{id:"d"+idx+"-"+ii,wcag:wcag,sev:ai.sev||"minor",desc:ai.issue||"",count:1,
              el:"",fix:ai.fix||"",info:info,remediated:false,engine:"paris-pdf",loc:{x:50,y:50,zone:"body"}};
          });
          var tc=pdfIssues.reduce(function(x,i){return x+i.count},0);
          return{name:d.name||"",url:d.url||"",ext:d.ext||"pdf",
            pages:d.pageCount||0,pageCount:d.pageCount||0,
            department:d.department||"",functionalType:d.functionalType||"",
            foundOn:d.foundOn||d.referrer||"",
            tagged:d.isTagged||false,hasStructTree:d.hasStructTree||false,
            lang:d.lang||"",title:d.title||"",
            producer:d.producer||"",creator:d.creator||"",
            fileSize:d.fileSize||0,riskScore:d.riskScore||0,riskLevel:d.riskLevel||"low",
            priority:d.riskLevel==="high"?"critical":d.riskLevel==="medium"?"high":"medium",
            views:0,lastScanned:d.lastScanned||"",
            issues:pdfIssues,totalIssues:tc,wcagsFailed:pdfIssues.length,
            critCount:pdfIssues.filter(function(i){return i.sev==="critical"}).reduce(function(x,i){return x+i.count},0),
            majCount:pdfIssues.filter(function(i){return i.sev==="major"}).reduce(function(x,i){return x+i.count},0),
            minCount:pdfIssues.filter(function(i){return i.sev==="minor"}).reduce(function(x,i){return x+i.count},0),
            remediatedCount:0,valResults:[]};
        });
        setDc(dcs);

        /* Step 2: Multi-strategy page loader with fallbacks.
           Reality: not all scans store pages the same way. Three possible layouts:
           (a) Page child docs in Cosmos (modern)  → /session-stream/:id/pages
           (b) Legacy export endpoint              → /session-stream/:id/export
           (c) webPages array on session doc       → /session/:id
           Try (a), then (b), then (c). Never setWp([]) silently — always diagnose. */
        var pickerPageCount = (session && (session.pageCount || (session.summary && session.summary.totalPages))) || 0;
        function finalizeLoad(stubs, source){
          setWp(stubs||[]);setIsDemo(false);setView("app");setSel(null);setDashDrill(null);
          /* Preserve the URL tab if user refreshed on /web-audit/sess-xxx */
          var keepTab=_route.tab||"dashboard";
          _setModRaw(keepTab);
          var count = (stubs && stubs.length) || 0;
          if (count > 0) {
            showToast("\u2705 "+dom+" \u2014 "+count+" pages, "+dcs.length+" docs loaded"+(source?" ("+source+")":""));
          } else if (pickerPageCount > 0) {
            showToast("\u26A0 Expected "+pickerPageCount+" pages but loaded 0 \u2014 "+dcs.length+" docs only. Scan may be in an older format. Try re-scanning.","error");
          } else {
            showToast("\u2705 "+dom+" \u2014 "+dcs.length+" docs (no web pages)");
          }
          /* Resolve cloud session ID for API calls (runPendingJobs, etc).
             _currentScanId.current is preserved — it holds whatever ID is in the
             URL ("what you see is what you get"). Only navigate to set URL if
             we haven't yet (e.g. loaded via picker, not via URL). */
          var sid=(summaryData&&summaryData.scan&&summaryData.scan.sessionId)
            ||(summaryData&&summaryData.scan&&summaryData.scan.id)
            ||session.id||session.sessionId||null;
          if(sid){
            /* If no current URL ID, this is a first-time picker load — set both */
            if(!_currentScanId.current){
              _currentScanId.current=sid;
              navigateTo(keepTab,sid);
            } else {
              /* URL already has an ID (local or cloud) — keep it as-is */
              var curPath=window.location.pathname.replace(/^\/|\/$/g,"");
              var expectedPath=(ROUTE_REV[keepTab]||"dashboard")+"/"+_currentScanId.current;
              if(curPath!==expectedPath)navigateTo(keepTab,_currentScanId.current);
            }
            runPendingJobs(sid);
          }
        }
        function tryLegacyExport(){
          console.log("[Loader] Trying legacy export endpoint...");
          fetch("/api/session-stream/"+session.id+"/export?offset=0&limit=100",{credentials:"include"})
            .then(function(r){if(!r.ok)throw new Error("export "+r.status);return r.json()})
            .then(function(expData){
              if(expData&&expData.webPages&&expData.webPages.length>0){
                console.log("[Loader] Legacy export returned",expData.webPages.length,"pages");
                importFromJson(JSON.stringify(expData));
                showToast("\u2705 "+dom+" loaded \u2014 "+expData.webPages.length+" pages (legacy export)");
              } else {
                console.log("[Loader] Legacy export empty, trying session doc...");
                tryLegacySessionDoc();
              }
            })
            .catch(function(e){
              console.warn("[Loader] Legacy export failed:",e.message);
              tryLegacySessionDoc();
            });
        }
        function tryLegacySessionDoc(){
          console.log("[Loader] Trying direct session doc...");
          fetch("/api/session/"+session.id,{credentials:"include"})
            .then(function(r){return r.json()})
            .then(function(legacy){
              if(legacy&&!legacy.error&&legacy.webPages&&legacy.webPages.length>0){
                console.log("[Loader] Session doc returned",legacy.webPages.length,"pages");
                setDomain(legacy.domain||dom);
                finalizeLoad(legacy.webPages,"session doc");
              } else {
                console.log("[Loader] All fallbacks exhausted \u2014 no pages available");
                finalizeLoad([],null);
              }
            })
            .catch(function(e){
              console.error("[Loader] Session doc fetch failed:",e.message);
              finalizeLoad([],null);
            });
        }
        /* Primary: page child docs (modern format) */
        var allStubs=[];
        function fetchPageStubs(offset){
          fetch("/api/session-stream/"+session.id+"/pages?offset="+offset+"&limit=200",{credentials:"include"})
            .then(function(r){if(!r.ok)throw new Error("pages "+r.status);return r.json()})
            .then(function(pgData){
              var pages = pgData.pages || [];
              /* If the FIRST request returns zero AND the picker said pages exist,
                 fall back immediately — don't silently accept empty */
              if (offset === 0 && pages.length === 0) {
                console.warn("[Loader] /pages returned empty on first request (server total:",pgData.total,", picker count:",pickerPageCount,"). Falling back to legacy export.");
                tryLegacyExport();
                return;
              }
              var stubs = pages.map(function(pg){
                var stubIssues=(pg.wcagSummary||[]).map(function(ws,wi){
                  var wcag=ws[0]||"";var info=W[wcag]||{n:"Unknown",l:"A",p:"Unknown",r:""};
                  return{id:"s"+wi,wcag:wcag,sev:"minor",desc:"",count:ws[1]||0,el:"",fix:"",info:info,
                    remediated:false,engine:"stub",loc:{x:0,y:0,zone:"body"}};
                });
                return{url:pg.url||"",p:pg.p||"/",n:pg.n||pg.title||pg.p||"/",d:pg.d||0,
                  status:pg.status||200,screenshot:pg.screenshot||"",dom:"",
                  issues:stubIssues,totalIssues:pg.issueCount||0,wcagsFailed:stubIssues.length,
                  critCount:pg.criticalCount||0,majCount:pg.majorCount||0,minCount:pg.minorCount||0,
                  brokenCmsTemplate:pg.brokenCmsTemplate||0,brokenHref:pg.brokenHref||0,
                  remediatedCount:0,isArchived:pg.isArchived||false,
                  _stub:true,_pageIndex:pg.pageIndex,_sessionId:session.id};
              });
              allStubs=allStubs.concat(stubs);
              if(pgData.hasMore){
                showToast("\u23F3 Loading page list... "+allStubs.length+"/"+pgData.total);
                fetchPageStubs(offset+200);
              } else {
                finalizeLoad(allStubs,null);
              }
            })
            .catch(function(e){
              console.error("[Loader] /pages fetch failed at offset",offset,":",e.message);
              if (offset === 0) {
                /* First request failed entirely — try legacy */
                tryLegacyExport();
              } else {
                /* Partial load — use what we have but warn */
                showToast("\u26A0 Loaded "+allStubs.length+" pages (partial — server error at offset "+offset+")","error");
                finalizeLoad(allStubs,"partial");
              }
            });
        }
        if(summaryData.totalPages>0){
          fetchPageStubs(0);
        } else if (pickerPageCount > 0) {
          /* Summary says 0 pages but picker promised some — definitely try legacy */
          console.log("[Loader] Summary said 0 pages but picker promised",pickerPageCount,", trying legacy...");
          tryLegacyExport();
        } else {
          /* Summary=0 and picker=0 — probably docs-only scan */
          tryLegacyExport();
        }
      })
      .catch(function(e){
        console.error("loadSessionIntoApp summary error:",e.message);
        /* Fallback to legacy */
        fetch("/api/session/"+session.id,{credentials:"include"}).then(function(r){return r.json()}).then(function(legacy){
          if(legacy&&!legacy.error&&(legacy.webPages||legacy.documents)){
            setDomain(legacy.domain||"");
            setWp(legacy.webPages||[]);setDc(legacy.documents||[]);setIsDemo(false);
            setView("app");_setModRaw("dashboard");setSel(null);setDashDrill(null);
            showToast("\u2713 Session loaded (legacy): "+legacy.domain);
          } else { showToast("Failed to load session: "+e.message,"error"); }
        }).catch(function(){showToast("Failed to load session: "+e.message,"error")});
      });
  }

  /** Lazy-load full page detail when user selects a page in Web Audit.
      Replaces the stub in wp[] with full page data including issues. */
  function loadPageDetail(pageIdx){
    var page=wp[pageIdx];
    if(!page)return Promise.resolve();
    if(!page._stub)return Promise.resolve(); /* Already loaded */
    var sessionId=page._sessionId||(scanMeta&&scanMeta.sessionId);
    var pIdx=page._pageIndex!=null?page._pageIndex:pageIdx;
    if(!sessionId){console.warn("No sessionId for page detail");return Promise.resolve()}
    return fetch("/api/session-stream/"+sessionId+"/page?index="+pIdx,{credentials:"include"})
      .then(function(r){if(!r.ok)throw new Error("page detail "+r.status);return r.json()})
      .then(function(fullPage){
        /* v1.0: AI Review payload lookup — the ai-review scan-job stored
           reviews keyed by issue fingerprint (wcag|sev|ruleId|selector|idx).
           Mirror the same fingerprint in ai-review.js so the client can
           match the server's reviews back to each UI issue. */
        var aiReviewMap=(fullPage.aiReview&&fullPage.aiReview.issueReviews)||{};
        function _fpIssue(i,idx){
          return [
            i.wcag||"no-wcag",
            i.sev||"minor",
            i.axeId||i.ruleId||"no-rule",
            (i.selector||i.el||"").slice(0,64),
            idx
          ].join("|");
        }
        /* Per-issue fix array persisted by the workbench (Session 46).
           Index-aligned with fullPage.issues. Lets us rehydrate pFix +
           aiFix + manualFix + accepted state on reopen without needing
           the user to re-run Build Remediation. WCAG check guards
           against shape drift where issuesFixes was saved against a
           different issue ordering. */
        var savedFixArr=Array.isArray(fullPage.issuesFixes)?fullPage.issuesFixes:[];
        /* Hydrate issues with WCAG info + attach AI review if present */
        var iss=(fullPage.issues||[]).map(function(i,ii){
          var wcag=i.wcag||"";var info=W[wcag]||{n:i.wcagName||"Unknown",l:"A",p:"Unknown",r:i.fix||""};
          var fp=_fpIssue(i,ii);
          var aiR=aiReviewMap[fp]||null;
          var saved=savedFixArr[ii];
          var validSaved=saved&&(!saved.wcag||saved.wcag===wcag);
          return{id:"w"+pageIdx+"-"+ii,wcag:wcag,sev:i.sev||"minor",desc:i.desc||"",count:i.count||1,
            el:i.el||"",fix:i.fix||"",info:info,
            remediated:!!(validSaved&&saved.acceptedFix)||i.remediated||false,
            engine:i.engine||"axe",
            priority:i.priority||"P3",impactedGroups:i.impactedGroups||["All Users"],
            loc:{x:50,y:50,zone:"body"},selector:i.selector||"",nodes:i.nodes||[],
            _aiReview:aiR,
            pFix:validSaved?(saved.pFix||""):"",
            aiFix:validSaved?(saved.aiFix||""):"",
            manualFix:validSaved?(saved.manualFix||""):"",
            acceptedFix:validSaved?(saved.acceptedFix||""):"",
            acceptedSrc:validSaved?(saved.acceptedSrc||""):"",
            fixCategory:classifyFixCategory({wcag:wcag,desc:i.desc||"",el:i.el||""})};
        });
        var tc=iss.reduce(function(x,i){return x+i.count},0);
        var fixesBuilt=iss.some(function(it){return it.pFix||it.aiFix||it.manualFix});
        /* Replace stub with full page */
        setWp(function(prev){return prev.map(function(p,i){
          if(i!==pageIdx)return p;
          return Object.assign({},p,{
            issues:iss,totalIssues:tc,wcagsFailed:iss.length,
            critCount:iss.filter(function(x){return x.sev==="critical"}).reduce(function(x,i){return x+i.count},0),
            majCount:iss.filter(function(x){return x.sev==="major"}).reduce(function(x,i){return x+i.count},0),
            minCount:iss.filter(function(x){return x.sev==="minor"}).reduce(function(x,i){return x+i.count},0),
            screenshot:fullPage.screenshot||p.screenshot,
            dom:fullPage.dom||p.dom||"",
            aiEnrichment:fullPage.aiEnrichment||null,
            fixScript:fullPage.fixScript||null,patches:fullPage.patches||null,
            fixSummary:fullPage.fixSummary||null,colorSuggestions:fullPage.colorSuggestions||null,
            fixesBuilt:fixesBuilt,
            _stub:false
          });
        })});
        /* If Cosmos has aiEnrichment but the per-issue aiFix column came
           back empty (e.g. an older session where Build Smart Remediation
           ran before the merge-on-burst fix shipped), run the merger
           inline using the just-fetched data. persist:true writes the
           merged result back to Cosmos so subsequent reopens are a
           one-step hydrate. Also seed the aiEnrich state map so the
           workbench's "AI suggestions cached" indicator picks it up. */
        if(fullPage.aiEnrichment&&!iss.some(function(it){return it.aiFix})){
          setAiEnrich(function(prev){var n=Object.assign({},prev);n[pageIdx]=fullPage.aiEnrichment;return n});
          mergeAiFixesOntoPage(pageIdx, fullPage.aiEnrichment, {persist: true});
        } else if(fullPage.aiEnrichment){
          /* aiFix is already populated, but keep the raw enrichment cached
             in state so the AI Suggestions panel renders without a refetch. */
          setAiEnrich(function(prev){var n=Object.assign({},prev);n[pageIdx]=fullPage.aiEnrichment;return n});
        }
      })
      .catch(function(e){
        console.error("Failed to load page detail:",e.message);
        showToast("Failed to load page details: "+e.message,"error");
      });
  }

  function deleteSession(sessionId){
    if(!window.confirm("Delete this session? This cannot be undone."))return;
    fetch("/api/session/"+sessionId,{method:"DELETE",credentials:"include"}).then(function(r){return r.json()}).then(function(){
      setUserSessions(function(prev){return prev.filter(function(s){return s.id!==sessionId})});
      showToast("Session deleted");
    }).catch(function(){showToast("Failed to delete session")});
  }

  /** Auto-load full issue detail (sev, selector, nodes[]) for all stub pages.
      Called when user opens the Audit Report sub-tab so we render real data
      instead of fake stub issues with hardcoded sev:"minor". */
  var _auditLoading=useRef(false);
  var _auditLoaded=useRef({}); /* sessionId → true once loaded */
  var _auditProgress=useState(null),auditProgress=_auditProgress[0],setAuditProgress=_auditProgress[1];

  /* v1.0: AI Review state + runner. Polls an ai-review scan-job chunk by
     chunk (same pattern as the recompute-pages background loader). When
     the job completes, refetches the session summary (for
     aiExecutiveSummary) and reloads all page details (for per-issue
     aiReview blocks), then the UI auto-rerenders. */
  var _aiRunning=useRef(false);
  var _aiProgress=useState(null),aiProgress=_aiProgress[0],setAiProgress=_aiProgress[1];
  /* Doc remediation hooks — must be at top level per React rules of hooks */
  var _docRemRunning=useRef(false);
  var remediatedBytes=useRef({}); /* docKey -> remediated PDF base64 (sync preview), for Download + Certify */
  var sealedBytes=useRef({}); /* docKey -> sealed+signed PDF base64, for Download Sealed */
  var _docRemProgress=useState(null),docRemProgress=_docRemProgress[0],setDocRemProgress=_docRemProgress[1];
  function runAiReview(){
    var sid=(scanMeta&&scanMeta.sessionId)||_currentScanId.current;
    if(!sid){showToast("No session loaded","error");return}
    if(_aiRunning.current){showToast("AI Review already running","error");return}
    _aiRunning.current=true;
    setAiProgress({phase:"starting",processed:0,total:0,costUsd:0});
    showToast("\uD83C\uDFAF Starting Pi Consultants AI Review\u2026");
    fetch("/api/ai-review",{method:"POST",credentials:"include",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({sessionId:sid})})
      .then(function(r){return r.json()})
      .then(function(d){
        if(!d||!d.ok||!d.jobId){
          _aiRunning.current=false;setAiProgress(null);
          showToast("AI Review failed to start: "+(d&&d.error||"unknown"),"error");
          return;
        }
        /* Poll the job chunk by chunk */
        (function pollChunk(){
          fetch("/api/scan-jobs/"+d.jobId+"/run",{method:"POST",credentials:"include"})
            .then(function(r){return r.json()})
            .then(function(res){
              if(!res){_aiRunning.current=false;setAiProgress(null);return}
              var p=res.progress||{};
              setAiProgress({phase:p.phase||"pages",processed:p.processed||0,total:p.total||0,costUsd:p.costUsd||0});
              if(res.status==="complete"){
                _aiRunning.current=false;
                setAiProgress({phase:"done",processed:p.total||0,total:p.total||0,costUsd:p.costUsd||0});
                setTimeout(function(){setAiProgress(null)},3000);
                showToast("\u2705 AI Review complete \u2014 refreshing audit data\u2026");
                /* Refetch summary for aiExecutiveSummary + clear audit-loaded
                   cache so loadAllPageDetails re-fetches pages with aiReview. */
                fetch("/api/session-stream/"+sid+"/summary",{credentials:"include"})
                  .then(function(r){return r.json()})
                  .then(function(summaryData){
                    if(summaryData&&summaryData.aiExecutiveSummary){
                      setScanMeta(function(prev){
                        return Object.assign({},prev||{},{aiExecutiveSummary:summaryData.aiExecutiveSummary});
                      });
                    }
                    /* Reset audit loaded cache so all pages re-hydrate with aiReview */
                    _auditLoaded.current={};
                    _auditLoading.current=false;
                    /* Force re-hydrate if user is on the audit report tab */
                    if(mod==="webAudit"&&webAuditTab==="report"){
                      loadAllPageDetails();
                    }
                  })
                  .catch(function(){});
              } else if(res.status==="failed"){
                _aiRunning.current=false;setAiProgress(null);
                showToast("AI Review failed: "+(res.error||"unknown"),"error");
              } else {
                setTimeout(pollChunk,400);
              }
            })
            .catch(function(e){
              _aiRunning.current=false;setAiProgress(null);
              showToast("AI Review network error: "+e.message,"error");
            });
        })();
      })
      .catch(function(e){
        _aiRunning.current=false;setAiProgress(null);
        showToast("AI Review request failed: "+e.message,"error");
      });
  }

  function loadAllPageDetails(){
    var sid=(scanMeta&&scanMeta.sessionId)||_currentScanId.current;
    if(!sid||_auditLoading.current||_auditLoaded.current[sid])return;
    /* Find all stub pages (they have _stub:true or empty issues arrays) */
    var stubIndexes=[];
    for(var i=0;i<wp.length;i++){
      var pg=wp[i];
      /* A page needs loading if it's a stub, OR its issues array exists but
         contains only stub issues (engine: "stub" or hardcoded sev minor w/o el) */
      var isStub=pg._stub===true;
      var hasRealIssues=(pg.issues||[]).some(function(iss){return iss.engine&&iss.engine!=="stub"});
      if(isStub||(pg.issues&&pg.issues.length>0&&!hasRealIssues)||(pg.issues&&pg.issues.length===0&&pg.totalIssues>0)){
        stubIndexes.push(i);
      }
    }
    if(stubIndexes.length===0){_auditLoaded.current[sid]=true;return}
    _auditLoading.current=true;
    setAuditProgress({loaded:0,total:stubIndexes.length});
    console.log("[AuditLoad] Loading full detail for",stubIndexes.length,"pages");

    /* Parallel with a concurrency limit of 4 to avoid overwhelming the API */
    var CONCURRENCY=4;
    var nextIdx=0;
    var loadedCount=0;
    function runNext(){
      if(nextIdx>=stubIndexes.length)return Promise.resolve();
      var myIdx=stubIndexes[nextIdx++];
      return loadPageDetail(myIdx).catch(function(e){
        console.warn("[AuditLoad] page",myIdx,"failed:",e.message);
      }).then(function(){
        loadedCount++;
        setAuditProgress({loaded:loadedCount,total:stubIndexes.length});
        return runNext();
      });
    }
    var workers=[];
    for(var w=0;w<CONCURRENCY;w++)workers.push(runNext());
    Promise.all(workers).then(function(){
      console.log("[AuditLoad] All pages loaded");
      _auditLoading.current=false;
      _auditLoaded.current[sid]=true;
      setTimeout(function(){setAuditProgress(null)},800);
    });
  }

  /* Trigger bulk load when entering Audit Report sub-tab */
  useEffect(function(){
    if(mod==="webAudit"&&webAuditTab==="report"&&wp.length>0){
      loadAllPageDetails();
    }
  },[mod,webAuditTab,wp.length]);

  /** Background job runner — auto-processes queued scan jobs (recompute-pages, pdf-audit).
      Called once after session load. Shows pulsing "Processing..." in dashboard.
      If no pending job exists, checks whether pages need backfill and auto-creates one. */
  var _jobRunning=useRef(false);
  function runPendingJobs(sessionId){
    if(!sessionId||_jobRunning.current){console.log("[BgJob] skip:",!sessionId?"no sid":"already running");return}
    console.log("[BgJob] checking for pending jobs, session:",sessionId);
    fetch("/api/scan-jobs?sessionId="+encodeURIComponent(sessionId),{credentials:"include"})
      .then(function(r){console.log("[BgJob] list response:",r.status);return r.json()})
      .then(function(data){
        console.log("[BgJob] jobs found:",JSON.stringify((data.jobs||[]).map(function(j){return{id:j.id,status:j.status,type:j.jobType}})));
        var jobs=(data.jobs||[]).filter(function(j){return j.status==="queued"||j.status==="processing"});
        if(jobs.length>0){
          console.log("[BgJob] resuming existing job:",jobs[0].id);
          startJobLoop(jobs[0]);
        } else {
          var completed=(data.jobs||[]).filter(function(j){return j.status==="complete"&&(j.jobType==="recompute-pages"||j.jobType==="full-recompute")});
          if(completed.length>0){console.log("[BgJob] already backfilled, skip");return}
          console.log("[BgJob] no jobs exist, auto-creating recompute-pages...");
          fetch("/api/scan-jobs",{method:"POST",credentials:"include",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify({sessionId:sessionId,jobType:"recompute-pages"})})
            .then(function(r){console.log("[BgJob] create response:",r.status);return r.json()})
            .then(function(d){
              console.log("[BgJob] created:",JSON.stringify(d));
              if(d&&d.ok&&d.jobId){
                startJobLoop({id:d.jobId,progress:d.progress||{}});
              }
            })
            .catch(function(e){console.error("[BgJob] create failed:",e.message)});
        }
      })
      .catch(function(e){console.error("[BgJob] list failed:",e.message)});
  }
  function startJobLoop(job){
    console.log("[BgJob] starting loop for job:",job.id,"total:",job.progress?job.progress.total:"?");
    _jobRunning.current=true;
    setBgJob({phase:"pages",processed:0,total:job.progress?job.progress.total:0,updated:0});
    (function runChunk(){
      fetch("/api/scan-jobs/"+job.id+"/run",{method:"POST",credentials:"include"})
        .then(function(r){console.log("[BgJob] run response:",r.status);return r.json()})
        .then(function(d){
          console.log("[BgJob] chunk result:",d?d.status:"null",d&&d.progress?d.progress.processed+"/"+d.progress.total:"");
          if(!d||d.error){console.error("[BgJob] chunk error:",d&&d.error);_jobRunning.current=false;setBgJob(null);return}
          var p=d.progress||{};
          setBgJob({phase:p.phase||"pages",processed:p.processed||0,total:p.total||0,updated:p.updated||0});
          if(d.status==="complete"){
            _jobRunning.current=false;
            setBgJob({phase:"done",processed:p.total||0,total:p.total||0,updated:p.updated||0});
            setTimeout(function(){setBgJob(null)},2500);
          } else if(d.status==="failed"){
            _jobRunning.current=false;
            setBgJob(null);
            showToast("Background processing failed","error");
          } else {
            setTimeout(runChunk,300);
          }
        })
        .catch(function(){_jobRunning.current=false;setBgJob(null)});
    })();
  }

  /* ── Platform-Level Scan Picker Functions (Session 46) ──
     These functions control the shared scan picker modal that is accessed from both
     the Watchtower Dashboard ("Load Scan") and Delta Recon ("Load Scan") headers.
     The picker merges cloud + local sessions, groups by domain, and routes the
     selected scan to the product that opened the picker. */

  /** Open the scan picker modal. Refreshes cloud sessions if user is authenticated.
      @param {string} target - "watchtower" routes to Dashboard, "compare" routes to Delta Recon step 2 */
  function openScanPicker(target){
    setScanPickerTarget(target||"watchtower");setScanPickerOpen(true);
    /* Auto-refresh cloud sessions if we don't have any yet */
    if(authUser&&(userSessions.length===0||!sessionsLoading)){loadUserSessions()}
  }
  /** Close the scan picker modal without loading anything */
  function closeScanPicker(){setScanPickerOpen(false)}

  /** Load a cloud session from the picker and route to the target product.
      BUG FIX: importFromJson() always calls setMod("dashboard") at the end of its
      execution. For Delta Recon routing, we use a 200ms setTimeout to override that
      setMod("dashboard") AFTER importFromJson completes. Without this delay, the user
      always lands on Dashboard even when loading from Delta Recon's Load Scan button.
      @param {Object} session - cloud session object from userSessions */
  function pickerLoadSession(session){
    closeScanPicker();
    loadSessionIntoApp(session);
    if(scanPickerTarget==="compare"){
      var dom=session.domain||"";
      /* 200ms delay: importFromJson sets setMod("dashboard") synchronously.
         This setTimeout fires after that completes, overriding to "compare". */
      setTimeout(function(){
        if(dom){setCmpDomain(dom);setCmpStep(2)}
        setMod("compare");
      },200);
    }
  }
  /** Load a local saved scan from the picker.
      Same 200ms setTimeout fix applies for Delta Recon routing.
      @param {string} scanId - local saved scan ID from browser storage */
  function pickerLoadLocalScan(scanId){
    closeScanPicker();loadScan(scanId);
    if(scanPickerTarget==="compare"){
      /* Same override delay — loadScan internally calls importFromJson → setMod("dashboard") */
      setTimeout(function(){setMod("compare")},200);
    }
  }

  /* ── Pop-out Workbench (Session 46, URL routing Session 49) ──
     Allows practitioners with two monitors to view the split-pane preview on one screen
     and the workbench table on another. Uses BroadcastChannel API for bidirectional
     communication between the two windows. When an issue is clicked in the workbench
     window, the preview window highlights that element. When a fix is accepted in the
     workbench, the preview window updates its right-pane marker from red to green.
     Pop-out URL routing: ?mode=workbench&scan=ID&page=0&domain=example.com
     fetches session via paginated export and selects the specified page. */

  /** BroadcastChannel ref — persists across renders, shared between both windows.
      Channel name "piaris-workbench" is the coordination namespace. */
  var wbChannel=useRef(null);
  useEffect(function(){
    if(typeof BroadcastChannel==="undefined")return;
    wbChannel.current=new BroadcastChannel("piaris-workbench");
    /* Listen for messages from the other window */
    wbChannel.current.onmessage=function(ev){
      var d=ev.data;
      if(!d||!d.action)return;
      /* Issue highlighting sync — click in workbench highlights in preview and vice versa */
      if(d.action==="highlight"&&d.issIdx!=null){setHiIss(d.issIdx)}
      if(d.action==="clearHighlight"){setHiIss(null)}
      /* Window lifecycle — detect when pop-out is closed to restore inline workbench */
      if(d.action==="popOutClosed"){setPopOutActive(false)}
      /* Fix acceptance sync — workbench accepts fix, preview updates markers */
      if(d.action==="acceptFix"&&d.pageIdx!=null&&d.issIdx!=null){acceptIssueFix(d.pageIdx,d.issIdx,d.source)}
      if(d.action==="clearFix"&&d.pageIdx!=null&&d.issIdx!=null){clearIssueFix(d.pageIdx,d.issIdx)}
      if(d.action==="manualFix"&&d.pageIdx!=null&&d.issIdx!=null){setManualFix(d.pageIdx,d.issIdx,d.text)}
      if(d.action==="acceptAll"&&d.pageIdx!=null){acceptAllPageFixes(d.pageIdx)}
      if(d.action==="buildFile"&&d.pageIdx!=null){buildRemediationFile(d.pageIdx)}
      /* Full state sync — used when pop-out first opens to hydrate the workbench */
      if(d.action==="syncState"&&d.wp){setWp(d.wp)}
      /* Pop-out requests state from parent (when BroadcastChannel sync missed initial send) */
      if(d.action==="requestSync"){wbBroadcast({action:"syncState",wp:wp})}
    };
    return function(){if(wbChannel.current)wbChannel.current.close()};
  },[]);

  /** Broadcast a message to the other window (preview ↔ workbench).
      Wrapped in try/catch because postMessage can fail if the channel is closed.
      @param {Object} msg - message object with action and optional data fields */
  function wbBroadcast(msg){if(wbChannel.current){try{wbChannel.current.postMessage(msg)}catch(e){}}}

  /* ── Workbench pop-out URL routing (Session 49) ──
     When this window IS the pop-out (?mode=workbench), parse scan/page/domain
     from URL params, fetch session via paginated export, and select the page. */
  useEffect(function(){
    if(!isPopOutWorkbench)return;
    var params=new URLSearchParams(window.location.search);
    var scanId=params.get("scan");
    var pageIdx=parseInt(params.get("page"))||0;
    var domainParam=params.get("domain")||"";
    if(scanId&&scanId!=="local"){
      setPendingWbPage(pageIdx);
      loadSessionIntoApp({id:scanId,domain:domainParam});
    } else {
      /* Local scan — request state from parent window via BroadcastChannel */
      setPendingWbPage(pageIdx);
      wbBroadcast({action:"requestSync"});
    }
  },[]);

  /* When pop-out workbench data loads, select the pending page and switch to audit view */
  useEffect(function(){
    if(pendingWbPage===null)return;
    if(wp&&wp.length>pendingWbPage){
      setSel(pendingWbPage);
      /* importFromJson sets mod to "dashboard" — override after 200ms (same pattern as pickerLoadSession) */
      setTimeout(function(){setMod("audit")},200);
      setPendingWbPage(null);
    }
  },[wp,pendingWbPage]);

  /** Open the workbench in a separate window with URL routing.
      Pop-out window fetches session from API and selects the specified page.
      @param {number} pageIdx - current page index in wp array */
  function popOutWorkbench(pageIdx){
    var page=wp[pageIdx];if(!page)return;
    var scanId=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:"local";
    var url=window.location.origin+"/?mode=workbench&scan="+encodeURIComponent(scanId)+"&page="+pageIdx+"&domain="+encodeURIComponent(domain||"");
    var w=window.open(url,"piaris-workbench","width=900,height=700,menubar=no,toolbar=no,location=no,status=no");
    if(w){setPopOutActive(true);wbBroadcast({action:"syncState",wp:wp})}
    else{showToast("Pop-up blocked \u2014 allow pop-ups for paris.pi-consulting.us","error")}
  }

  /* PWA — manifest, service worker, install prompt */
  var _deferredPrompt=useRef(null);
  var _isPwa=useState(typeof window!=="undefined"&&(window.matchMedia("(display-mode:standalone)").matches||window.navigator.standalone===true)),isPwa=_isPwa[0],setIsPwa=_isPwa[1];
  var _canInstall=useState(false),canInstall=_canInstall[0],setCanInstall=_canInstall[1];
  useEffect(function(){
    /* Inject PWA manifest dynamically */
    try{
      var manifest={
        name:"PiARIS \u2014 Accessibility Intelligence",
        short_name:"PiARIS",
        description:"Pi Consultants Accessibility Remediation Intelligence & Scanning",
        start_url:window.location.href.split("?")[0],
        display:"standalone",
        background_color:"#F8FAFC",
        theme_color:P.pri,
        orientation:"any",
        categories:["accessibility","productivity","utilities"],
        icons:[{src:"data:image/svg+xml,"+encodeURIComponent('<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 64 64\"><rect width=\"64\" height=\"64\" rx=\"14\" fill=\"%234F46E5\"/><text x=\"32\" y=\"44\" font-family=\"serif\" font-size=\"36\" font-weight=\"bold\" fill=\"white\" text-anchor=\"middle\">\u03C0</text></svg>'),sizes:"64x64",type:"image/svg+xml",purpose:"any maskable"}]
      };
      var blob=new Blob([JSON.stringify(manifest)],{type:"application/json"});
      var url=URL.createObjectURL(blob);
      var link=document.createElement("link");
      link.rel="manifest";link.href=url;
      document.head.appendChild(link);
      /* Mobile meta tags */
      var meta=function(n,c){var m=document.createElement("meta");m.name=n;m.content=c;document.head.appendChild(m)};
      meta("mobile-web-app-capable","yes");
      meta("apple-mobile-web-app-capable","yes");
      meta("apple-mobile-web-app-status-bar-style","black-translucent");
      meta("apple-mobile-web-app-title","PiARIS");
      meta("theme-color","#4F46E5");
    }catch(e){console.log("PWA manifest:",e.message)}
    /* Capture install prompt */
    function onPrompt(ev){ev.preventDefault();_deferredPrompt.current=ev;setCanInstall(true)}
    window.addEventListener("beforeinstallprompt",onPrompt);
    /* Register service worker for offline support */
    if("serviceWorker" in navigator){
      try{
        var swCode="var CACHE='paris-v"+APP_VERSION+"';var SKIP=['.clarity.ms','clarity.ms','applicationinsights','dc.services.visualstudio','.auth'];self.addEventListener('install',function(e){self.skipWaiting()});self.addEventListener('fetch',function(e){var u=e.request.url;for(var i=0;i<SKIP.length;i++){if(u.indexOf(SKIP[i])>-1)return}if(e.request.method!=='GET')return;e.respondWith(caches.match(e.request).then(function(r){return r||fetch(e.request).then(function(resp){if(resp.status===200&&resp.type==='basic'){var rc=resp.clone();caches.open(CACHE).then(function(c){c.put(e.request,rc)})}return resp}).catch(function(){return caches.match(e.request)})}))});self.addEventListener('activate',function(e){e.waitUntil(caches.keys().then(function(ks){return Promise.all(ks.filter(function(k){return k!==CACHE}).map(function(k){return caches.delete(k)}))}))})";
        var swBlob=new Blob([swCode],{type:"application/javascript"});
        var swUrl=URL.createObjectURL(swBlob);
        navigator.serviceWorker.register(swUrl,{scope:"/"}).catch(function(){});
      }catch(e){}
    }
    return function(){window.removeEventListener("beforeinstallprompt",onPrompt)};
  },[]);
  function installPwa(){
    if(_deferredPrompt.current){
      _deferredPrompt.current.prompt();
      _deferredPrompt.current.userChoice.then(function(r){
        if(r.outcome==="accepted"){setIsPwa(true);setCanInstall(false);showToast("\u2713 PiARIS installed to home screen!")}
        _deferredPrompt.current=null;
      });
    }
  }
  /* ── Scan progress state ── */
  var _prog=useState(0),prog=_prog[0],setProg=_prog[1];
  var _phase=useState(""),phase=_phase[0],setPhase=_phase[1];
  var _vpat=useState(false),vpat=_vpat[0],setVpat=_vpat[1];
  var timer=useRef(null);

  /* ═══════════════════════════════════════════════════════════════
   * MODULE: pa11y-lab-engine-state                [status: ready]
   * PURPOSE: Engine-specific state for PA11YLab (axe, HTMLCS, AI, PDF)
   *
   * READS:     None (pure state containers)
   * WRITES:    aiHtml, aiRes, aiFixed, aiLoading, aiImg, aiAlt, aiPdf,
   *            aiPdfRes, labUrl, labAxeRes, labHtmlcsRes, labRunning,
   *            labFetchErr, pdfStructRes, labPreview, labShowPreview,
   *            pdfEngines, pdfRemForm, pdfRemStatus
   * CALLS:     useState
   * EFFECTS:   None
   * EMITS:     None
   *
   * EXPORTS:   All state + setters (as a hook: usePa11yEngineState)
   * IMPORTS:   None
   *
   * CLOSURE:   None
   * ENTANGLED: aiLoading is shared between Web (AI fix) and Doc (AI PDF)
   *            analysis paths. Split into aiWebLoading/aiDocLoading before
   *            extracting modules separately.
   * ═══════════════════════════════════════════════════════════════ */

  /* ── PA11Y Lab state ── */
  /* aiTab removed — replaced by module-level navigation (aiLabWeb/aiLabDoc) */
  var _aiHtml=useState(""),aiHtml=_aiHtml[0],setAiHtml=_aiHtml[1];
  var _aiRes=useState(null),aiRes=_aiRes[0],setAiRes=_aiRes[1];
  var _aiFixed=useState(""),aiFixed=_aiFixed[0],setAiFixed=_aiFixed[1];
  var _aiLoading=useState(false),aiLoading=_aiLoading[0],setAiLoading=_aiLoading[1];
  var _aiImg=useState(null),aiImg=_aiImg[0],setAiImg=_aiImg[1];
  var _aiAlt=useState(""),aiAlt=_aiAlt[0],setAiAlt=_aiAlt[1];
  var _aiPdf=useState(null),aiPdf=_aiPdf[0],setAiPdf=_aiPdf[1];
  var _aiPdfRes=useState(null),aiPdfRes=_aiPdfRes[0],setAiPdfRes=_aiPdfRes[1];
  var _labUrl=useState(""),labUrl=_labUrl[0],setLabUrl=_labUrl[1];
  var _lastEvalId=useState(null),lastEvalId=_lastEvalId[0],setLastEvalId=_lastEvalId[1];
  var _reportPdf=useState(null),reportPdf=_reportPdf[0],setReportPdf=_reportPdf[1];
  var _aiModelUsed=useState(null),aiModelUsed=_aiModelUsed[0],setAiModelUsed=_aiModelUsed[1];
  var _reviewCount=useState(null),reviewCount=_reviewCount[0],setReviewCount=_reviewCount[1];
  var _reviewPanel=useState(null),reviewPanel=_reviewPanel[0],setReviewPanel=_reviewPanel[1];
  var _labAxeRes=useState(null),labAxeRes=_labAxeRes[0],setLabAxeRes=_labAxeRes[1];
  /* PR-6.1 per-image alt-text triage: altDecisions[src] = {state,alt,...}; altBusy = src being AI'd. */
  var _altDecisions=useState({}),altDecisions=_altDecisions[0],setAltDecisions=_altDecisions[1];
  var _altBusy=useState(null),altBusy=_altBusy[0],setAltBusy=_altBusy[1];
  var _labHtmlcsRes=useState(null),labHtmlcsRes=_labHtmlcsRes[0],setLabHtmlcsRes=_labHtmlcsRes[1];
  var _labRunning=useState({}),labRunning=_labRunning[0],setLabRunning=_labRunning[1];
  var _labFetchErr=useState(""),labFetchErr=_labFetchErr[0],setLabFetchErr=_labFetchErr[1];
  var _pdfStructRes=useState(null),pdfStructRes=_pdfStructRes[0],setPdfStructRes=_pdfStructRes[1];
  var _labPreview=useState(""),labPreview=_labPreview[0],setLabPreview=_labPreview[1];
  var previewFrameRef=useRef(null);                          // the report-view live preview iframe (for click-to-pinpoint)
  var _hlInfo=useState(null),hlInfo=_hlInfo[0],setHlInfo=_hlInfo[1];   // { snippet, total, idx } of the active highlight
  var _labShowPreview=useState(true),labShowPreview=_labShowPreview[0],setLabShowPreview=_labShowPreview[1];
  /* PR-5.15b: per-WCAG-group expand/collapse state for the grouped
     issue list. Keyed by container.key + "::" + group.groupKey so the
     same WCAG checkpoint can be open on one page and closed on another.
     PR-5.15c will add a highlight-selection state alongside this. */
  var _labExpandedGroups=useState({}),labExpandedGroups=_labExpandedGroups[0],setLabExpandedGroups=_labExpandedGroups[1];
  /* PR-5.15c 2/2 — PA11Y Lab PDF viewer shell. Renders the uploaded
     PDF (held as base64 in aiPdf.data) to page images via the client's
     own pdfjs, co-located with the grouped issue list in the Audit
     Report. labPdfPages maps pageNum → { url, ptW, ptH } where ptW/ptH
     are the page's UNSCALED point dimensions (the overlay's coordinate
     reference; bbox geometry from the server is in the same PDF-point
     space). Status: none | loading | ready | error. */
  var _labPdfPages=useState({}),labPdfPages=_labPdfPages[0],setLabPdfPages=_labPdfPages[1];
  var _labPdfStatus=useState("none"),labPdfStatus=_labPdfStatus[0],setLabPdfStatus=_labPdfStatus[1];
  var _labPdfNumPages=useState(0),labPdfNumPages=_labPdfNumPages[0],setLabPdfNumPages=_labPdfNumPages[1];
  var _labPrevIdx=useState(0),labPrevIdx=_labPrevIdx[0],setLabPrevIdx=_labPrevIdx[1];  /* PDF preview pager: index into the rendered pageNums */
  var labPdfDocRef=useRef(null);
  /* PR-5.15c 3/3 — interactive PDF pinpoint. Clicking a finding instance in
     the grouped list pins it: the viewer jumps to its page, draws that
     instance's bbox solid (others on the page dashed), and a stepper walks
     the finding's located instances. null = nothing pinned, so the overlay
     falls back to drawing every resolved box (calibration/overview layer).
     Shape: { key, idx, members:[{i,page,bbox,sev,label,text}] }. */
  var _labDocPin=useState(null),labDocPin=_labDocPin[0],setLabDocPin=_labDocPin[1];
  function toggleLabGroup(rowKey){
    setLabExpandedGroups(function(prev){
      var n=Object.assign({},prev);
      if(n[rowKey])delete n[rowKey];else n[rowKey]=true;
      return n;
    });
  }

  /* ── PDF engine + remediation state ── */
  var _pdfEngines=useState({structural:true,ai:false,veraEnabled:false}),pdfEngines=_pdfEngines[0],setPdfEngines=_pdfEngines[1];
  var _pdfRemForm=useState(null),pdfRemForm=_pdfRemForm[0],setPdfRemForm=_pdfRemForm[1];
  var _pdfRemStatus=useState(""),pdfRemStatus=_pdfRemStatus[0],setPdfRemStatus=_pdfRemStatus[1];
  /* PR-6.6c: full-remediation pipeline state. Tracks each step
     (metadata → autotag → alt injection → re-audit) so the UI can show
     where we are in real time. result holds the diff + final PDF for
     the inline "before/after" panel + download. */
  var _remPipeline=useState({step:"idle",progress:[]}),remPipeline=_remPipeline[0],setRemPipeline=_remPipeline[1];
  var _remResult=useState(null),remResult=_remResult[0],setRemResult=_remResult[1];
  /* Bulk remediation modal — preflight list (already-remediated docs,
     provenance), per-doc exclude, then poll-driven batch processing. */
  var _bulkRem=useState(null),bulkRem=_bulkRem[0],setBulkRem=_bulkRem[1];
  /* PR-5.13: AI engine availability — preflight probe of /api/ai (GET)
     on PA11Y Lab mount. Tri-state:
       null  → still probing
       true  → ANTHROPIC_API_KEY is configured server-side
       false → key missing; SPA disables the AI checkbox and surfaces
               a visible "Unavailable in this environment" label so
               users don't run an audit and quietly miss half of it. */
  var _aiAvailable=useState(null),aiAvailable=_aiAvailable[0],setAiAvailable=_aiAvailable[1];

  /* ── Reports + Dashboard drill state ── */
  var _rptTab=useState("executive"),rptTab=_rptTab[0],setRptTab=_rptTab[1];
  var _rptDoc=useState(null),rptDoc=_rptDoc[0],setRptDoc=_rptDoc[1];
  var _dashDrill=useState(null),dashDrill=_dashDrill[0],setDashDrill=_dashDrill[1];

  /* ── Staging + saved scans ── */
  var _staging=useState([]),staging=_staging[0],setStaging=_staging[1];
  var _savedScans=useState([]),savedScans=_savedScans[0],setSavedScans=_savedScans[1];
  var _compareId=useState(null),compareId=_compareId[0],setCompareId=_compareId[1]; /* legacy inline compare — superseded by Delta Recon */

  var _storageOk=useState(null),storageOk=_storageOk[0],setStorageOk=_storageOk[1];

  /* ── DocMaya PDF → HTML Conversion state ── */
  var _domainLicensed=useState(false),domainLicensed=_domainLicensed[0],setDomainLicensed=_domainLicensed[1];
  var _convStatus=useState({}),convStatus=_convStatus[0],setConvStatus=_convStatus[1];
  var _convLoading=useState({}),convLoading=_convLoading[0],setConvLoading=_convLoading[1];
  /* PR-K.26: Doc Audit deep-audit state — per docIndex
     { status: "running"|"complete"|"failed", total?, critical?, error? }.
     Drives the ♿ Audit button states on the Doc Audit document list. */
  var _docAuditStatus=useState({}),docAuditStatus=_docAuditStatus[0],setDocAuditStatus=_docAuditStatus[1];

  /* ── DocMaya Monitoring & Analytics (Session 46, Feature #6) ──
     Tracks conversion stats per domain: total conversions, success/failure rates,
     most-accessed PDFs, quota usage. Data fetched from /api/docmaya/analytics.
     If the endpoint doesn't exist yet (404), displays a "coming soon" placeholder
     with stats derived from local convStatus data as a fallback. */
  var _dmAnalytics=useState(null),dmAnalytics=_dmAnalytics[0],setDmAnalytics=_dmAnalytics[1];
  var _dmAnalyticsLoading=useState(false),dmAnalyticsLoading=_dmAnalyticsLoading[0],setDmAnalyticsLoading=_dmAnalyticsLoading[1];

  /** Fetch DocMaya analytics for the current domain.
      Tries /api/docmaya/analytics first. If 404, builds local stats from convStatus.
      Called when user navigates to the Monitoring tab. */
  function fetchDmAnalytics(){
    if(!domain){setDmAnalytics(null);return}
    setDmAnalyticsLoading(true);
    fetch("/api/docmaya/analytics?domain="+encodeURIComponent(domain),{credentials:"include"})
      .then(function(r){return r.ok?r.json():null})
      .then(function(data){
        if(data&&data.analytics){
          setDmAnalytics(data.analytics);
        } else {
          /* Fallback: build analytics from local conversion data.
             This runs when the backend endpoint isn't deployed yet. */
          var completed=0;var failed=0;var totalPages=0;
          Object.keys(convStatus).forEach(function(k){
            var cs=convStatus[k];
            if(cs&&cs.status==="complete"){completed++;totalPages+=(cs.pagesConverted||1)}
            else if(cs&&cs.status==="error"){failed++}
          });
          setDmAnalytics({
            domain:domain,period:"current",
            totalConversions:completed+failed,
            successful:completed,failed:failed,
            successRate:completed+failed>0?Math.round(completed/(completed+failed)*100):0,
            totalPagesConverted:totalPages,
            quotaUsed:completed,quotaLimit:domainLicense?domainLicense.pagesPerDomain||25:25,
            topPdfs:dc.slice(0,5).map(function(d,i){return{name:d.name,views:d.views||0,converted:!!convStatus[i]}}),
            source:"local" /* Indicates fallback — not from server */
          });
        }
        setDmAnalyticsLoading(false);
      })
      .catch(function(){
        setDmAnalyticsLoading(false);
        setDmAnalytics({domain:domain,totalConversions:0,successful:0,failed:0,successRate:0,
          totalPagesConverted:0,quotaUsed:0,quotaLimit:10,topPdfs:[],source:"error"});
      });
  }

  /* ── AI Web Enrichment state ── */
  var _aiEnrich=useState({}),aiEnrich=_aiEnrich[0],setAiEnrich=_aiEnrich[1];
  var _aiEnrichLoading=useState({}),aiEnrichLoading=_aiEnrichLoading[0],setAiEnrichLoading=_aiEnrichLoading[1];

  /* ── Build Progress + Download Flow (Session 46, Features #3 + #4) ──
     Tracks progress of Build Remediation Package across all pages.
     Shows a progress bar on the Dashboard instead of individual toasts per page.
     When build completes, a prominent download CTA appears on the Dashboard. */
  /** Build progress: {active:bool, total:number, completed:number, failed:number, currentPage:string} */
  var _buildProgress=useState({active:false,total:0,completed:0,failed:0,currentPage:""}),buildProgress=_buildProgress[0],setBuildProgress=_buildProgress[1];
  /** Whether the package build is complete and ready for download */
  var _packageReady=useState(false),packageReady=_packageReady[0],setPackageReady=_packageReady[1];
  /** Ref that always holds the latest aiEnrich state.
      WHY: mergeAiFixesOntoPage runs inside setInterval callbacks (retries every 3s).
      JavaScript closures capture the aiEnrich value at creation time — if we read
      aiEnrich directly inside the callback, we get the stale value from when the
      interval was created, not the current value. useRef.current always points to
      the latest value because the useEffect below updates it on every aiEnrich change. */
  var aiEnrichRef=useRef({});
  useEffect(function(){aiEnrichRef.current=aiEnrich},[aiEnrich]);

  /* Load saved scan index on mount + verify storage is working */
  useEffect(function(){
    /* Storage health check */
    sS("aris-health-check","ok").then(function(r){
      if(r){
        setStorageOk(true);
        /* Load saved scans index */
        sG("aris-scans-index").then(function(idx){if(idx&&idx.length)setSavedScans(idx)});
      } else {
        setStorageOk(false);
      }
    }).catch(function(){setStorageOk(false)});
  },[]);

  /* ── Domain License Enforcement (Session 46, Feature #5) ──
     On domain load, fetches the full license details for the scanned domain.
     This drives UI enforcement: gating Build Remediation, reports, and verify
     behind license tier. Free tier auto-provisioned if no license exists.
     domainLicense shape: {tier, status, pagesPerDomain, scansPerYear, scansUsedThisYear, domainsRegistered, maxDomains, domain} */
  var _domainLicense=useState(null),domainLicense=_domainLicense[0],setDomainLicense=_domainLicense[1];

  useEffect(function(){
    if(!domain || wp.length===0) { setDomainLicensed(false); setDomainLicense(null); return; }
    /* Step 1: Check if domain has a license and fetch full details */
    fetch("/api/license/"+encodeURIComponent(domain)+"/detail",{credentials:"include"})
      .then(function(r){ return r.ok ? r.json() : null; })
      .then(function(data){
        if(data && data.license && data.license.status==="active"){
          setDomainLicensed(true);
          /* Admin override — always show enterprise tier */
          var isAdm=authUser&&((authUser.roles||[]).indexOf("admin")>-1||((authUser.name||"").toLowerCase().indexOf("@pi-consulting.us")>-1||(authUser.name||"").toLowerCase().indexOf("@piconsultants.us")>-1));
          if(isAdm){
            setDomainLicense(Object.assign({},data.license,{tier:"enterprise",pagesPerDomain:99999,scansPerYear:999,maxDomains:99}));
          } else {
            setDomainLicense(data.license);
          }
          return;
        }
        /* No active license — auto-provision free tier */
        return fetch("/api/license/"+encodeURIComponent(domain)+"/auto-provision",{
          method:"POST",credentials:"include",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({domain:domain})
        }).then(function(r2){ return r2.ok ? r2.json() : null; })
          .then(function(prov){
            if(prov && (prov.provisioned || prov.reason==="already_licensed")){
              setDomainLicensed(true);
              /* If admin, override to enterprise regardless of what was provisioned */
              var isAdminUser=authUser&&((authUser.roles||[]).indexOf("admin")>-1||((authUser.name||"").toLowerCase().indexOf("@pi-consulting.us")>-1||(authUser.name||"").toLowerCase().indexOf("@piconsultants.us")>-1));
              if(isAdminUser){
                setDomainLicense({tier:"enterprise",status:"active",pagesPerDomain:99999,scansPerYear:999,scansUsedThisYear:0,domainsRegistered:[domain],maxDomains:99,domain:domain});
              } else if(prov.license){
                setDomainLicense(prov.license);
              } else {
                setDomainLicense({tier:"free",status:"active",pagesPerDomain:25,scansPerYear:1,scansUsedThisYear:0,domainsRegistered:[domain],maxDomains:1,domain:domain});
              }
              if(prov.provisioned && !isAdminUser) showToast("\u2705 Free tier activated \u2014 25 pages/scan, 1 scan/year. Upgrade for more.");
            } else if(authUser){
              /* Auto-provision didn't work but user is signed in — grant default license
                 so build/report/verify buttons aren't blocked. */
              var isAdmin=(authUser.roles||[]).indexOf("admin")>-1||((authUser.name||"").toLowerCase().indexOf("@pi-consulting.us")>-1||(authUser.name||"").toLowerCase().indexOf("@piconsultants.us")>-1);
              setDomainLicensed(true);
              setDomainLicense(isAdmin
                ?{tier:"enterprise",status:"active",pagesPerDomain:99999,scansPerYear:999,scansUsedThisYear:0,domainsRegistered:[domain],maxDomains:99,domain:domain}
                :{tier:"free",status:"active",pagesPerDomain:25,scansPerYear:1,scansUsedThisYear:0,domainsRegistered:[domain],maxDomains:1,domain:domain});
            } else { setDomainLicensed(false); setDomainLicense(null); }
          });
      })
      .catch(function(){
        /* License API unreachable — if signed in, grant default license */
        if(authUser){
          var isAdmin=(authUser.roles||[]).indexOf("admin")>-1||((authUser.name||"").toLowerCase().indexOf("@pi-consulting.us")>-1||(authUser.name||"").toLowerCase().indexOf("@piconsultants.us")>-1);
          setDomainLicensed(true);
          setDomainLicense(isAdmin
            ?{tier:"enterprise",status:"active",pagesPerDomain:99999,scansPerYear:999,scansUsedThisYear:0,domainsRegistered:[domain],maxDomains:99,domain:domain}
            :{tier:"free",status:"active",pagesPerDomain:25,scansPerYear:1,scansUsedThisYear:0,domainsRegistered:[domain],maxDomains:1,domain:domain});
        } else { setDomainLicensed(false); setDomainLicense(null); }
      });
  },[domain,wp.length]);

  /** Check if a license-gated action is allowed. Returns true if allowed.
      If not allowed, shows an upgrade toast and returns false.
      @param {string} action - "build"|"report"|"verify"|"scan"
      @returns {boolean} whether the action is permitted */
  function licenseGate(action){
    /* Admin bypass — platform owners have unlimited access to all features.
       Checks: "admin" role from Azure auth, OR @pi-consulting.us email */
    if(authUser){
      var roles=authUser.roles||[];
      var email=(authUser.name||authUser.email||"").toLowerCase();
      if(roles.indexOf("admin")>-1||email.indexOf("@pi-consulting.us")>-1||email.indexOf("@piconsultants.us")>-1)return true;
    }
    if(!domainLicense){
      showToast("\u{1F510} Sign in and scan a domain to access "+action+" features","error");
      return false;
    }
    /* v3.0: All tiers get pro reports + fix builds. Only gate on scan credits. */
    if(action==="scan"){
      var scansUsed=domainLicense.scansUsedThisYear||0;
      var scansPerYear=domainLicense.scansPerYear||1;
      if(scansUsed>=scansPerYear){
        showToast("\u26A0 All "+scansPerYear+" scan credits used this year. Upgrade or renew your license.","error");
        setUpgradeModal({domain:domain,selectedTier:"starter"});
        return false;
      }
    }
    return true;
  }

  /* Auto-load scan from URL path (/dashboard/sess-xxx) — uses FAST picker path
     (summary → stubs) instead of slow /export endpoint. Preloads card data
     from sessionStorage so dashboard shows numbers immediately.
     Accepts EITHER a cloud session ID (sess-xxx) OR a local scan ID (timestamp) —
     resolves local IDs to their matching cloud session via saved index. */
  useEffect(function(){
    var rawId=_route.scan||new URLSearchParams(window.location.search).get("scan");
    if(!rawId)return;
    if(isPopOutWorkbench)return;

    /* Resolve ID: if it's a local scan ID (numeric, no "sess-" prefix),
       look up the matching cloud sessionId in the local index. */
    function resolveScanId(cb){
      if(rawId.indexOf("sess-")===0){cb(rawId,null);return}
      /* Looks like a local ID — try to find the cloud sessionId */
      sG("aris-scans-index").then(function(idx){
        if(!idx||!idx.length){cb(rawId,null);return}
        var localIdNum=isNaN(rawId)?null:parseInt(rawId,10);
        var entry=idx.find(function(x){return x.id===localIdNum||x.id===rawId||String(x.id)===rawId});
        if(entry&&entry.sessionId){cb(entry.sessionId,entry)}
        else{cb(rawId,entry||null)}
      }).catch(function(){cb(rawId,null)});
    }

    resolveScanId(function(cloudSid,localEntry){
      console.log("[Route] Auto-loading scan — URL id:",rawId,"API id:",cloudSid,"localEntry:",!!localEntry,"hasSessionId:",!!(localEntry&&localEntry.sessionId));
      _currentScanId.current=rawId;

      /* Dead entry: URL has a local-ID shape but no matching saved scan, and
         it doesn't look like a cloud session ID. Nothing to load — offer delete. */
      var looksLikeCloudId=rawId.indexOf("sess-")===0;
      if(!localEntry&&!looksLikeCloudId){
        console.warn("[Route] No saved scan matches id:",rawId);
        if(window.confirm("This scan ("+rawId+") is no longer available.\n\nReturn to home?")){
          goHome();
        }
        return;
      }

      /* Local entry WITHOUT cloud sessionId → load from IndexedDB */
      if(localEntry&&!localEntry.sessionId){
        console.log("[Route] Loading from IndexedDB (no cloud sessionId)");
        _setModRaw(_route.tab||"dashboard");
        loadScan(localEntry.id);
        return;
      }
      /* Local entry WITH sessionId, or direct cloud URL → API path */
      doAutoLoad(cloudSid,localEntry);
    });

    function doAutoLoad(scanId,localEntry){
    /* Try to hydrate from pre-stashed card data (set when user clicked Open This Scan).
       Check both the raw URL ID and the resolved cloud ID. */
    var preload=null;
    try{
      var raw=sessionStorage.getItem("paris_preload_"+rawId)
            ||sessionStorage.getItem("paris_preload_"+scanId);
      if(raw){
        preload=JSON.parse(raw);
        sessionStorage.removeItem("paris_preload_"+rawId);
        sessionStorage.removeItem("paris_preload_"+scanId);
      }
    }catch(e){}
    if(!preload&&localEntry){
      preload={
        domain:localEntry.domain,webPages:localEntry.webPages,documents:localEntry.documents,
        pdfCount:localEntry.pdfCount||0,pdfUntagged:localEntry.pdfUntagged||0,
        totalIssues:localEntry.totalIssues,criticalIssues:localEntry.criticalIssues
      };
    }

    if(preload){
      console.log("[Route] Hydrating from preload:",preload.domain,preload.webPages,"pages,",preload.documents,"docs");
      setDomain(preload.domain||"");
      setWp([]);
      setDc([]);
      setScanMeta({sessionId:scanId,templateTokens:0,templateSamples:[],templateDedup:null,completedAt:null,
        summary:{
          totalPages:preload.webPages||0,
          activePages:preload.webPages||0,
          totalElements:preload.totalIssues||0,
          critical:preload.criticalIssues||0,
          major:0,minor:0,totalRules:0,pagesWithIssues:0,
          score:null
        }});
      setIsDemo(false);setView("app");_setModRaw(_route.tab||"dashboard");setSel(null);setDashDrill(null);
    } else {
      showToast("\u23F3 Loading scan...");
    }

    /* Fast load path: same as picker — call loadSessionIntoApp */
    loadSessionIntoApp({id:scanId,domain:preload?preload.domain:""});
    }/* end doAutoLoad */
  },[]);

  /* ── PR-K.26: Deep PDF audit (shared PA11Y Lab engine) ──
     Runs the same Matterhorn/WCAG PDF audit PA11Y Lab uses against
     documents this Watchtower scan discovered, then refreshes the
     in-memory dc entries with the findings. This replaced the Doc
     Audit screen's previous DocMaya HTML-conversion routing:
     conversion gives users an HTML alternative today, but auditing
     identifies what's wrong with the actual PDF so it can be fixed. */
  function auditDocs(docIndices){
    if(!Array.isArray(docIndices)) docIndices=[docIndices];
    var scanId=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:null;
    if(!scanId){
      try{ scanId=new URLSearchParams(window.location.search).get("scan"); }catch(e){}
    }
    if(!scanId){
      showToast("No active scan session. Run a scan first.","error");
      return;
    }
    setDocAuditStatus(function(prev){
      var n=Object.assign({},prev);
      docIndices.forEach(function(idx){ n[idx]={status:"running"}; });
      return n;
    });
    fetch("/api/doc-audit-batch",{
      method:"POST",
      credentials:"include",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ sessionId:scanId, docIndices:docIndices })
    })
    .then(function(r){ return r.json().catch(function(){return{}}).then(function(b){return{status:r.status,body:b}}); })
    .then(function(out){
      if(out.status<200||out.status>=300||!out.body.ok){
        var msg=(out.body&&out.body.error)||("HTTP "+out.status);
        setDocAuditStatus(function(prev){
          var n=Object.assign({},prev);
          docIndices.forEach(function(idx){ n[idx]={status:"failed",error:msg}; });
          return n;
        });
        showToast("Audit failed: "+msg,"error");
        return;
      }
      var byIdx={};
      (out.body.results||[]).forEach(function(r){ byIdx[r.docIndex]=r; });
      /* Refresh the in-memory doc entries with the deep findings —
         same mapping the session loader applies to accessibilityIssues
         so the rest of the Doc Audit UI reads them unchanged. */
      setDc(function(prev){
        return prev.map(function(d,i){
          var r=byIdx[i];
          if(!r||!r.ok) return d;
          var pdfIssues=(r.accessibilityIssues||[]).map(function(ai,ii){
            var wcag=ai.wcag||"";var info=W[wcag]||{n:ai.issue||"Unknown",l:"A",p:"Unknown",r:ai.fix||""};
            return{id:"d"+i+"-"+ii,wcag:wcag,sev:ai.sev||"minor",desc:ai.issue||"",count:1,
              el:"",fix:ai.fix||"",info:info,remediated:false,engine:"paris-pdf-ua",loc:{x:50,y:50,zone:"body"}};
          });
          var tc=pdfIssues.length;
          return Object.assign({},d,{
            issues:pdfIssues,totalIssues:tc,wcagsFailed:pdfIssues.length,
            critCount:pdfIssues.filter(function(x){return x.sev==="critical"}).length,
            majCount:pdfIssues.filter(function(x){return x.sev==="major"}).length,
            minCount:pdfIssues.filter(function(x){return x.sev==="minor"}).length
          });
        });
      });
      setDocAuditStatus(function(prev){
        var n=Object.assign({},prev);
        (out.body.results||[]).forEach(function(r){
          n[r.docIndex]=r.ok
            ?{status:"complete",total:r.issueCount,critical:r.critical}
            :{status:"failed",error:r.error||"audit_failed"};
        });
        return n;
      });
      var okCount=(out.body.results||[]).filter(function(r){return r.ok}).length;
      showToast("Audited "+okCount+" document"+(okCount===1?"":"s")+".");
    })
    .catch(function(e){
      setDocAuditStatus(function(prev){
        var n=Object.assign({},prev);
        docIndices.forEach(function(idx){ n[idx]={status:"failed",error:e.message}; });
        return n;
      });
      showToast("Audit request failed: "+e.message,"error");
    });
  }

  /* ── DocMaya PDF → HTML Conversion functions ── */
  /** Trigger DocMaya PDF→HTML conversion for one or more documents.
      Posts to /api/pdf-convert, then polls for completion status.
      PR-K.26: no longer wired to the Doc Audit list button — kept for
      the DocMaya module's own surfaces. */
  function convertToHtml(docIndices){
    if(!Array.isArray(docIndices)) docIndices=[docIndices];
    var scanId=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:null;
    if(!scanId){
      try{ scanId=new URLSearchParams(window.location.search).get("scan"); }catch(e){}
    }
    if(!scanId){
      showToast("No active scan session. Run a scan first.","error");
      return;
    }
    var newLoading=Object.assign({},convLoading);
    docIndices.forEach(function(idx){ newLoading[idx]=true; });
    setConvLoading(newLoading);

    fetch("/api/pdf-convert/"+scanId+"/convert",{
      method:"POST",
      credentials:"include",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({
        docIndices:docIndices,
        priority:"normal",
        options:{ocr:true,preserveImages:true,generateToc:true,format:"both"}
      })
    })
    .then(function(r){ return r.json(); })
    .then(function(data){
      var updated=Object.assign({},convLoading);
      docIndices.forEach(function(idx){ updated[idx]=false; });
      setConvLoading(updated);
      if(data.ok){
        showToast("Conversion started for "+data.queued+" document"+(data.queued>1?"s":"")+".");
        pollConversionStatus(scanId);
      } else if(data.error==="insufficient_credits"){
        showToast(data.message||"Conversion quota exceeded.","error");
      } else {
        showToast(data.error||"Conversion failed.","error");
      }
    })
    .catch(function(e){
      var updated=Object.assign({},convLoading);
      docIndices.forEach(function(idx){ updated[idx]=false; });
      setConvLoading(updated);
      showToast("Conversion request failed: "+e.message,"error");
    });
  }

  /** Poll /api/pdf-convert status every 3s until all conversions complete or fail */
  function pollConversionStatus(scanId){
    var poll=function(){
      fetch("/api/pdf-convert/"+scanId+"/convert-status",{credentials:"include"})
        .then(function(r){ return r.ok?r.json():null; })
        .then(function(data){
          if(!data||!data.conversions) return;
          var statusMap={};
          data.conversions.forEach(function(c){
            statusMap[c.docIndex]={
              status:c.status,
              complianceGain:c.complianceGain,
              beforeIssues:c.beforeIssues,
              afterIssues:c.afterIssues,
              error:c.error,
              completedAt:c.completedAt
            };
          });
          setConvStatus(statusMap);

          /* Mark completed docs as remediated + update staging status */
          data.conversions.forEach(function(c){
            if(c.status==="complete"){
              setDc(function(prev){return prev.map(function(d,i){
                if(i!==c.docIndex)return d;
                if(d.remediatedCount===d.totalIssues)return d; /* already marked */
                var ni=d.issues.map(function(iss){return Object.assign({},iss,{remediated:true})});
                return Object.assign({},d,{issues:ni,remediatedCount:d.totalIssues,conversionResult:{complianceGain:c.complianceGain,beforeIssues:c.beforeIssues,afterIssues:c.afterIssues}});
              })});
              setStaging(function(prev){return prev.map(function(s){
                if(s.type==="pdf"&&s.docIndex===c.docIndex&&s.status==="generating"){
                  return Object.assign({},s,{status:"staged"});
                }
                return s;
              })});
            } else if(c.status==="failed"){
              setStaging(function(prev){return prev.map(function(s){
                if(s.type==="pdf"&&s.docIndex===c.docIndex&&s.status==="generating"){
                  return Object.assign({},s,{status:"failed"});
                }
                return s;
              })});
            }
          });

          var inProgress=data.conversions.some(function(c){
            return c.status!=="complete"&&c.status!=="failed";
          });
          if(inProgress){
            setTimeout(poll,3000);
          } else {
            var completed=data.conversions.filter(function(c){return c.status==="complete"}).length;
            var failed=data.conversions.filter(function(c){return c.status==="failed"}).length;
            if(completed>0){
              showToast(completed+" document"+(completed>1?"s":"")+" converted to accessible HTML."+(failed>0?" "+failed+" failed.":""));
            }
          }
        })
        .catch(function(){/* silent — stop polling on network error */});
    };
    setTimeout(poll,2000);
  }

  /* ── Download helpers for CSV and JSON export ── */

  /* ── AI Web Enrichment ── */
  /* ── Persist remediation data to Cosmos ── */
  /** Persist remediation data (AI enrichment, fix scripts, screenshots) to Cosmos via PATCH API.
      Called after AI enrich succeeds, fix script generated, or screenshot captured. */
  function persistPageData(pageIdx, data){
    var sid=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:null;
    if(!sid){return}
    fetch("/api/session-stream/"+sid+"/page/"+pageIdx,{
      method:"PATCH",
      headers:{"Content-Type":"application/json"},
      credentials:"include",
      body:JSON.stringify(data)
    }).then(function(r){return r.json()}).then(function(res){
      if(res.ok){console.log("[PiARIS] Persisted page "+pageIdx+" remediation data to Cosmos")}
      else{console.warn("[PiARIS] Persist failed:",res.error)}
    }).catch(function(e){console.warn("[PiARIS] Persist error:",e.message)});
  }

  /* ── Screenshot capture from proxy iframe ── */
  /** Capture a screenshot of a specific element in an iframe.
      Uses SVG foreignObject rendering for cross-origin-safe capture.
      @param {Ref} frameRef - iframe ref (proxyFrameRef or remFrameRef)
      @param {Object} issue - issue with selector/el to find the element
      @param {string} mode - "before" or "after" (stamps label on image)
      @param {Function} callback - (dataUrl, error) */
  function captureElementScreenshot(frameRef, issue, mode, callback){
    if(!frameRef||!frameRef.current){callback(null,"No iframe ref");return}
    var iframe=frameRef.current;
    try{
      var iDoc=iframe.contentDocument||iframe.contentWindow.document;
      if(!iDoc){callback(null,"Cannot access iframe document");return}
      /* Find the element */
      var el=null;
      if(issue.selector){try{el=iDoc.querySelector(issue.selector)}catch(e){}}
      if(!el&&issue.el){
        var tagMatch=(issue.el||"").match(/<(\w+)/);
        if(tagMatch){
          var candidates=iDoc.querySelectorAll(tagMatch[1]);
          for(var j=0;j<candidates.length;j++){
            if(candidates[j].outerHTML.indexOf((issue.el||"").substring(1,30))>-1){el=candidates[j];break}
          }
        }
      }
      if(!el){
        /* Fallback: capture viewport center area */
        var fallbackCanvas=iDoc.createElement("canvas");
        fallbackCanvas.width=600;fallbackCanvas.height=300;
        var fCtx=fallbackCanvas.getContext("2d");
        fCtx.fillStyle="#F8FAFC";fCtx.fillRect(0,0,600,300);
        fCtx.fillStyle="#64748B";fCtx.font="14px Inter, system-ui, sans-serif";
        fCtx.fillText("Element not found — viewport capture",20,30);
        fCtx.fillText("WCAG "+issue.wcag+": "+(issue.desc||"").substring(0,60),20,55);
        fCtx.fillText("Selector: "+(issue.selector||"none"),20,75);
        callback(fallbackCanvas.toDataURL("image/jpeg",0.85),null);
        return;
      }
      /* Scroll to element and highlight */
      el.scrollIntoView({block:"center"});
      var borderColor=mode==="after"?"#059669":"#DC2626";
      var prevOutline=el.style.outline;
      var prevBg=el.style.backgroundColor;
      el.style.outline="3px solid "+borderColor;
      el.style.backgroundColor=borderColor==="059669"?"rgba(5,150,105,0.08)":"rgba(220,38,38,0.08)";
      /* Wait for scroll + repaint */
      setTimeout(function(){
        try{
          var rect=el.getBoundingClientRect();
          /* Add padding around the element */
          var pad=20;
          var x=Math.max(0,rect.left-pad);
          var y=Math.max(0,rect.top-pad);
          var w=Math.min(rect.width+pad*2,iframe.clientWidth-x);
          var h=Math.min(rect.height+pad*2,400);
          /* Create canvas and draw from iframe */
          var canvas=iDoc.createElement("canvas");
          canvas.width=w*2;canvas.height=h*2;
          var ctx=canvas.getContext("2d");
          ctx.scale(2,2);
          /* Use foreignObject SVG rendering for cross-origin-safe capture */
          var data='<svg xmlns="http://www.w3.org/2000/svg" width="'+w+'" height="'+h+'">'
            +'<foreignObject width="100%" height="100%">'
            +'<div xmlns="http://www.w3.org/1999/xhtml" style="overflow:hidden;width:'+w+'px;height:'+h+'px;">'
            +el.outerHTML
            +'</div></foreignObject></svg>';
          var svgBlob=new Blob([data],{type:"image/svg+xml;charset=utf-8"});
          var svgUrl=URL.createObjectURL(svgBlob);
          var img=new Image();
          img.onload=function(){
            ctx.drawImage(img,0,0,w,h);
            URL.revokeObjectURL(svgUrl);
            /* Add mode label */
            ctx.fillStyle="rgba(0,0,0,0.7)";
            ctx.fillRect(0,0,w*2,24);
            ctx.fillStyle="#fff";ctx.font="bold 11px Inter, system-ui, sans-serif";
            ctx.fillText(mode==="after"?"\u2705 AFTER — Fix Applied":"\u{1F534} BEFORE — Issue Present",6,16);
            ctx.fillText("WCAG "+(issue.wcag||"")+" | "+(issue.desc||"").substring(0,50),w>200?w*0.6:6,16);
            /* Restore element styles */
            el.style.outline=prevOutline;
            el.style.backgroundColor=prevBg;
            callback(canvas.toDataURL("image/jpeg",0.85),null);
          };
          img.onerror=function(){
            URL.revokeObjectURL(svgUrl);
            el.style.outline=prevOutline;
            el.style.backgroundColor=prevBg;
            callback(null,"SVG render failed");
          };
          img.src=svgUrl;
        }catch(renderErr){
          el.style.outline=prevOutline;
          el.style.backgroundColor=prevBg;
          callback(null,"Render error: "+renderErr.message);
        }
      },300);
    }catch(e){callback(null,"Capture error: "+e.message)}
  }

  /* Capture before/after pair and persist to Cosmos */
  /** Capture before/after screenshot pair for an issue, upload to blob, persist URLs to Cosmos */
  function captureIssuePair(pageIdx, issueIdx){
    var page=wp[pageIdx];if(!page)return;
    var issue=page.issues[issueIdx];if(!issue)return;
    showToast("\u{1F4F7} Capturing before/after screenshots...");
    /* Before: from baseline pane */
    captureElementScreenshot(proxyFrameRef, issue, "before", function(beforeImg, beforeErr){
      if(beforeErr){console.warn("[PiARIS] Before capture failed:",beforeErr)}
      /* After: from deliverable pane */
      captureElementScreenshot(remFrameRef, issue, "after", function(afterImg, afterErr){
        if(afterErr){console.warn("[PiARIS] After capture failed:",afterErr)}
        if(!beforeImg&&!afterImg){showToast("Screenshot capture failed","error");return}
        /* Upload to blob via screenshot API */
        var sid=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:null;
        if(!sid){
          /* Store locally on issue object if no session */
          setWp(function(prev){return prev.map(function(p,pi){
            if(pi!==pageIdx)return p;
            var ni=p.issues.map(function(iss,ii){
              if(ii!==issueIdx)return iss;
              return Object.assign({},iss,{screenshotBefore:beforeImg||null,screenshotAfter:afterImg||null,screenshotAt:new Date().toISOString()});
            });
            return Object.assign({},p,{issues:ni});
          })});
          showToast("\u{1F4F7} Screenshots captured"+(beforeImg?" (before)":"")+(afterImg?" (after)":""));
          return;
        }
        /* Upload to blob */
        var batch=[];
        if(beforeImg)batch.push({pageIndex:pageIdx+"-iss-"+issueIdx+"-before",image:beforeImg});
        if(afterImg)batch.push({pageIndex:pageIdx+"-iss-"+issueIdx+"-after",image:afterImg});
        fetch("/api/screenshot",{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          credentials:"include",
          body:JSON.stringify({sessionId:sid,batch:batch})
        }).then(function(r){return r.json()}).then(function(res){
          var urls={};
          (res.results||[]).forEach(function(r){
            if(r.ok&&r.url){
              if(String(r.pageIndex).indexOf("-before")>-1)urls.before=r.url;
              else if(String(r.pageIndex).indexOf("-after")>-1)urls.after=r.url;
            }
          });
          /* Store URLs on issue */
          setWp(function(prev){return prev.map(function(p,pi){
            if(pi!==pageIdx)return p;
            var ni=p.issues.map(function(iss,ii){
              if(ii!==issueIdx)return iss;
              return Object.assign({},iss,{
                screenshotBefore:urls.before||beforeImg||null,
                screenshotAfter:urls.after||afterImg||null,
                screenshotAt:new Date().toISOString()
              });
            });
            return Object.assign({},p,{issues:ni});
          })});
          /* Persist to Cosmos */
          persistPageData(pageIdx,{issueUpdates:[{index:issueIdx,screenshotBefore:urls.before||null,screenshotAfter:urls.after||null}]});
          var count=(urls.before?1:0)+(urls.after?1:0);
          showToast("\u{1F4F7} "+count+" screenshot"+(count!==1?"s":"")+" captured and saved");
        }).catch(function(e){
          showToast("Screenshot upload failed: "+e.message,"error");
        });
      });
    });
  }

  /** Shared AI enrichment — calls /api/enrich-web for any item (page or doc).
      @param {string} key - Cache key for aiEnrich/aiEnrichLoading state ("0" for page, "d0" for doc)
      @param {Object} item - The page or doc object with {issues[], url, n/name}
      @param {Object} opts - {headings:[], label:string, persistPageIdx:number|null} */
  function aiEnrichItem(key, item, opts){
    if(!item)return;
    if(aiEnrichLoading[key])return;
    setAiEnrichLoading(function(prev){var n=Object.assign({},prev);n[key]=true;return n});

    var issues=item.issues.map(function(iss,idx){return{idx:idx,wcag:iss.wcag,sev:iss.sev,desc:iss.desc,count:iss.count,el:iss.el||"",fix:iss.fix||"",selector:iss.selector||""}});

    var body={
      url:item.url||item.n||item.name||"",
      issues:issues,
      pageTitle:item.n||item.name||"",
      pageHeadings:opts.headings||[],
      images:[],links:[],forms:[],lang:"en",textSamples:[]
    };

    fetch("/api/enrich-web",{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      credentials:"include",
      body:JSON.stringify(body)
    })
    .then(function(r){return r.ok?r.json():null})
    .then(function(data){
      if(data&&data.aiSuggestions){
        setAiEnrich(function(prev){var n=Object.assign({},prev);n[key]=data;return n});
        var count=data.aiSuggestions.length;
        showToast("\u{1F9E0} AI generated "+count+" fix suggestion"+(count!==1?"s":"")+" for "+opts.label);
        /* Persist to Cosmos for web pages (not docs — no page index).
           Also fan out into the workbench: merge AI suggestions onto
           issue.aiFix so the AI Fix column populates without waiting
           for the per-page Build Remediation File button, AND persist
           the merged per-issue fixes (persist:true) so reopening the
           session re-renders the column from Cosmos. */
        if(opts.persistPageIdx!==null&&opts.persistPageIdx!==undefined){
          persistPageData(opts.persistPageIdx, {aiEnrichment: data});
          mergeAiFixesOntoPage(opts.persistPageIdx, data, {persist: true});
        }
      } else if(data&&data.skipped){
        showToast("AI enrichment unavailable \u2014 API key not configured.","error");
      }
    })
    .catch(function(e){
      console.error("[aiEnrich] error for "+key+":",e);
      showToast("AI enrichment failed: "+e.message,"error");
    })
    .finally(function(){
      setAiEnrichLoading(function(prev){var n=Object.assign({},prev);n[key]=false;return n});
    });
  }

  /** AI enrich a web page — extracts heading context, persists results to Cosmos */
  function aiEnrichPage(pageIdx){
    var page=wp[pageIdx];
    if(!page)return;
    /* Extract headings from issue descriptions for richer AI context */
    var headings=[];
    (page.issues||[]).forEach(function(iss){
      if(iss.desc&&(iss.desc.indexOf("heading")>-1||iss.desc.indexOf("H1")>-1||iss.desc.indexOf("H2")>-1)){
        var hMatch=iss.el&&iss.el.match(/<h(\d)/i);
        if(hMatch)headings.push({level:parseInt(hMatch[1]),text:(iss.el||"").replace(/<[^>]+>/g,"").substring(0,80)});
      }
    });
    aiEnrichItem(pageIdx, page, {headings:headings, label:page.n, persistPageIdx:pageIdx});
  }

  /** AI enrich a PDF document — no heading extraction, no Cosmos persistence */
  function aiEnrichDoc(docIdx){
    var doc=dc[docIdx];
    if(!doc)return;
    aiEnrichItem("d"+docIdx, doc, {headings:[], label:doc.name, persistPageIdx:null});
  }

  /* ── Batch AI Enrich All Pages ── */
  function aiEnrichAllPages(){
    if(!wp||wp.length===0)return;
    var toEnrich=[];
    for(var i=0;i<wp.length;i++){
      if(!aiEnrich[i]&&!aiEnrichLoading[i]&&wp[i].issues&&wp[i].issues.length>0){
        toEnrich.push(i);
      }
    }
    if(toEnrich.length===0){showToast("All pages already enriched or have no issues.");return;}
    showToast("\u{1F9E0} AI enriching "+toEnrich.length+" pages\u2026 This may take a moment.");
    // Stagger calls to avoid hammering the API
    toEnrich.forEach(function(idx,i){
      setTimeout(function(){aiEnrichPage(idx)},i*1500);
    });
  }

  function downloadAsJson(data){
    var json=JSON.stringify(data,null,2);
    var blob=new Blob([json],{type:"application/json;charset=utf-8;"});
    var a=document.createElement("a");
    a.href=URL.createObjectURL(blob);
    var domain=(data.scan&&data.scan.domain)||"scan";
    a.download="PiARIS_"+domain.replace(/[^a-z0-9]/gi,"_")+"_"+new Date().toISOString().slice(0,10)+".json";
    document.body.appendChild(a);a.click();a.remove();
    showToast("\u{1F4E6} JSON exported: "+domain);
  }
  function downloadAsCsv(data){
    function esc(v){var s=String(v==null?"":v);return/[",\n]/.test(s)?'"'+s.replace(/"/g,'""')+'"':s}
    var domain=(data.scan&&data.scan.domain)||"scan";
    var pages=data.webPages||[];
    var docs=data.documents||[];
    /* Page inventory */
    var csv="--- PAGE INVENTORY ---\n";
    csv+="URL,Depth,Title,Status,Issues,Critical\n";
    pages.forEach(function(p){
      var ic=0,cc=0;(p.issues||[]).forEach(function(i){var c=i.count||1;ic+=c;if(i.sev==="critical")cc+=c});
      csv+=esc(p.url)+","+esc(p.d||p.depth||0)+","+esc(p.n||p.title||"")+","+esc(p.status||200)+","+esc(ic)+","+esc(cc)+"\n";
    });
    /* Document inventory */
    if(docs.length>0){
      csv+="\n--- DOCUMENT INVENTORY ---\n";
      csv+="File Name,URL,Department,Extension,Functional Type,Pages,Tagged,Risk\n";
      docs.forEach(function(d){
        csv+=esc(d.name)+","+esc(d.url)+","+esc(d.department||"")+","+esc(d.ext||"")+","+esc(d.functionalType||"")+","+esc(d.pageCount||0)+","+esc(d.isTagged?"Yes":"No")+","+esc(d.riskLevel||"")+"\n";
      });
    }
    /* Accessibility issues */
    var hasIssues=pages.some(function(p){return p.issues&&p.issues.length>0});
    if(hasIssues){
      csv+="\n--- ACCESSIBILITY ISSUES ---\n";
      csv+="Page URL,Engine,WCAG,Severity,Rule,Description,Elements,Fix\n";
      pages.forEach(function(p){
        (p.issues||[]).forEach(function(iss){
          csv+=esc(p.url)+","+esc(iss.engine||"axe")+","+esc(iss.wcag||"")+","+esc(iss.sev||"")+","+esc(iss.ruleId||iss.axeId||"")+","+esc(iss.desc||"")+","+esc(iss.count||0)+","+esc(iss.fix||"")+"\n";
        });
      });
    }
    var blob=new Blob([csv],{type:"text/csv;charset=utf-8;"});
    var a=document.createElement("a");
    a.href=URL.createObjectURL(blob);
    a.download="PiARIS_"+domain.replace(/[^a-z0-9]/gi,"_")+"_"+new Date().toISOString().slice(0,10)+".csv";
    document.body.appendChild(a);a.click();a.remove();
    showToast("\u{1F4E5} CSV exported: "+pages.length+" pages, "+(hasIssues?"with issues":"no issues"));
  }

  /* ── Toast, filters, PDF viewer state ── */
  var _toast=useState(null),toast=_toast[0],setToast=_toast[1];
  var _sevFilter=useState("all"),sevFilter=_sevFilter[0],setSevFilter=_sevFilter[1];
  var _pageSearch=useState(""),pageSearch=_pageSearch[0],setPageSearch=_pageSearch[1];
  var _pageListLimit=useState(50),pageListLimit=_pageListLimit[0],setPageListLimit=_pageListLimit[1];
  var _pdfPage=useState(1),pdfPage=_pdfPage[0],setPdfPage=_pdfPage[1];
  var _pdfPages=useState({}),pdfPages=_pdfPages[0],setPdfPages=_pdfPages[1];  /* rendered page data URLs */
  var _pdfLoading=useState(false),pdfLoading=_pdfLoading[0],setPdfLoading=_pdfLoading[1];
  var _pdfMode=useState("none"),pdfMode=_pdfMode[0],setPdfMode=_pdfMode[1]; /* "canvas"|"embed"|"none" */
  var pdfDocRef=useRef(null);  /* pdf.js document instance */
  /** Show a toast notification — auto-dismisses after 3s. type: "success" (default) or "error" */
  /* Errors get a longer window than success — a 3s "saved!" is fine,
     but an actionable config-broken message needs time to read. */
  function showToast(msg,type){var t=type||"success";setToast({msg:msg,type:t});setTimeout(function(){setToast(null)},t==="error"?10000:3000);}

  /** Parse a CSV file of URLs and POST to /api/import-csv.
      @param file      - File object from <input type=file>
      @param type      - "web" | "pdf" | "mixed"
      @param label     - Human label for the import session */
  function importUrlsFromCsv(file,type,label){
    if(!file){showToast("No file selected","error");return}
    var reader=new FileReader();
    reader.onload=function(ev){
      var text=ev.target.result||"";
      /* Parse: one URL per line, optional label/name/department columns after comma.
         Accepts formats:
           https://example.com/page
           https://example.com/doc.pdf, Annual Report, Finance
           "https://x.com/a", "Home Page" */
      var lines=text.split(/\r?\n/).map(function(l){return l.trim()}).filter(function(l){return l&&!l.startsWith("#")});
      /* Drop header row if it doesn't look like a URL */
      if(lines.length>0&&!/^https?:\/\//i.test(lines[0]))lines.shift();
      var urls=[];
      for(var i=0;i<lines.length;i++){
        var line=lines[i];
        /* Simple CSV split — handles basic quoted fields */
        var cols=line.split(",").map(function(c){return c.replace(/^\s*"?|"?\s*$/g,"").trim()});
        var url=cols[0];
        if(!url||!/^https?:\/\//i.test(url))continue;
        urls.push({url:url,label:cols[1]||"",name:cols[1]||"",department:cols[2]||""});
      }
      if(urls.length===0){
        showToast("No valid URLs found in the file (must start with http:// or https://)","error");
        return;
      }
      showToast("\u23F3 Importing "+urls.length+" URLs\u2026");
      fetch("/api/import-csv",{method:"POST",credentials:"include",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({type:type||"mixed",urls:urls,label:label||file.name})})
        .then(function(r){return r.json()})
        .then(function(d){
          if(!d||!d.ok){showToast("Import failed: "+(d&&d.error||"unknown"),"error");return}
          showToast("\u2705 Imported "+d.counts.web+" pages, "+d.counts.pdf+" PDFs across "+d.counts.domains+" domain(s)");
          /* Navigate to the new session's dashboard */
          setTimeout(function(){window.location.href="/dashboard/"+d.sessionId},400);
        })
        .catch(function(e){showToast("Import failed: "+e.message,"error")});
    };
    reader.onerror=function(){showToast("Failed to read file","error")};
    reader.readAsText(file);
  }

  /** Trigger a hidden file input to pick a CSV and run importUrlsFromCsv. */
  function pickCsvImport(type){
    var input=document.createElement("input");
    input.type="file";
    input.accept=".csv,.txt,text/csv,text/plain";
    input.onchange=function(ev){
      var f=ev.target.files&&ev.target.files[0];
      if(f)importUrlsFromCsv(f,type||"mixed",f.name);
    };
    input.click();
  }

  function saveScan(){
    if(!wp.length){showToast("No scan data to save","error");return;}
    var wAgg=agg(wp),dAgg=agg(dc);
    var tI=wAgg.tI+dAgg.tI,tF=wAgg.tRem+dAgg.tRem;
    var pdfCount=dc.filter(function(d){return d.ext==="pdf"}).length;
    var pdfUntagged=dc.filter(function(d){return d.ext==="pdf"&&!d.tagged}).length;
    var pdfHighRisk=dc.filter(function(d){return d.riskLevel==="high"}).length;
    var entry={
      id:Date.now(),
      sessionId:(scanMeta&&scanMeta.sessionId)||null,
      domain:domain,
      date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
      time:new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}),
      webPages:wp.length,
      documents:dc.length,
      totalAssets:wp.length+dc.length,
      totalIssues:tI,
      criticalIssues:wAgg.tC+dAgg.tC,
      score:tI>0?Math.round(tF/tI*100):100,
      remediated:tF,
      pdfCount:pdfCount,
      pdfUntagged:pdfUntagged,
      pdfHighRisk:pdfHighRisk
    };
    var newIdx=[entry].concat(savedScans);
    setSavedScans(newIdx);
    /* Await all storage writes */
    Promise.all([
      sS("aris-scans-index",newIdx),
      sS("aris-scan-"+entry.id+"-wp",wp),
      sS("aris-scan-"+entry.id+"-dc",dc),
      sS("aris-scan-"+entry.id+"-rem",[wSt.tRem,dSt.tRem]),
      sS("aris-scan-"+entry.id+"-stg",staging)
    ]).then(function(){
      showToast("\u2713 Saved: "+domain+" \u2014 "+entry.webPages+" pages, "+entry.documents+" docs, "+entry.totalIssues+" issues");
    }).catch(function(e){
      showToast("Storage error: "+e.message,"error");
    });
    /* Also save to cloud if authenticated */
    if(authUser){
      var cloudPayload={
        overallScore:overallScore||entry.score,
        webPages:wp.map(function(p){return{url:p.url,title:p.title,issueCount:p.issues?p.issues.length:0}}),
        documents:dc.map(function(d){return{url:d.url,title:d.title||d.name,type:d.ext,tagged:d.tagged}}),
        summary:{total:tI,critical:wAgg.tC+dAgg.tC,major:wAgg.tMj+dAgg.tMj,minor:wAgg.tMn+dAgg.tMn,remediated:tF,pages:wp.length,docs:dc.length},
        version:APP_VERSION
      };
      /* Create session if none exists, then save */
      var apiUrl="/api/session";
      fetch(apiUrl,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({domain:domain,config:{}})}).then(function(r){return r.json()}).then(function(sess){
        if(sess&&sess.sessionId){
          return fetch(apiUrl+"/"+sess.sessionId+"/save",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(cloudPayload)});
        }
      }).then(function(){loadUserSessions()}).catch(function(e){console.warn("Cloud save failed:",e.message)});
    }
  }

  /* Load a saved scan by ID (from IndexedDB only — no API calls).
     Works even when savedScans state hasn't populated yet (reads from store). */
  function loadScan(id){
    /* Read index directly in case state isn't populated yet (auto-load on refresh) */
    sG("aris-scans-index").then(function(idx){
      var entry=(idx||[]).find(function(s){return s.id===id});
      if(!entry){showToast("Scan not found","error");return;}
      showToast("\u23F3 Loading "+entry.domain+"...");
      setDomain(entry.domain);
      return Promise.all([
        sG("aris-scan-"+id+"-wp"),
        sG("aris-scan-"+id+"-dc"),
        sG("aris-scan-"+id+"-rem"),
        sG("aris-scan-"+id+"-stg"),
        Promise.resolve(entry)
      ]);
    }).then(function(res){
      if(!res)return;
      var entry=res[4];
      var wpData=res[0],dcData=res[1];
      /* Dead local scan — index entry exists but data is gone */
      if(!wpData&&!dcData){
        if(window.confirm("This scan's data is missing from local storage.\n\nDelete the stale entry?")){
          deleteScan(id);goHome();
        }
        return;
      }
      if(wpData)setWp(wpData);
      if(dcData)setDc(dcData);
      if(res[3])setStaging(res[3]);
      setIsDemo(false);setScanMeta(null);setBrokenLinks([]);setView("app");setSel(null);setDashDrill(null);
      /* Preserve the tab from URL path if set, else dashboard */
      _setModRaw(_route.tab||"dashboard");
      showToast("\u2713 Loaded: "+entry.domain+" \u2014 "+entry.webPages+" pages, "+entry.documents+" docs");
    }).catch(function(e){
      showToast("Load failed: "+e.message,"error");
    });
  }

  /* Delete a saved scan */
  function deleteScan(id){
    var entry=savedScans.find(function(s){return s.id===id});
    var newIdx=savedScans.filter(function(s){return s.id!==id});
    setSavedScans(newIdx);
    Promise.all([
      sS("aris-scans-index",newIdx),
      sS("aris-scan-"+id+"-wp",[]),
      sS("aris-scan-"+id+"-dc",[]),
      sS("aris-scan-"+id+"-rem",[]),
      sS("aris-scan-"+id+"-stg",[])
    ]).then(function(){
      showToast("\u{1F5D1} Deleted: "+(entry?entry.domain:"scan"));
    });
  }

  /* Get previous scans of the same domain for comparison */
  function getDomainHistory(dom){
    return savedScans.filter(function(s){return s.domain===dom}).sort(function(a,b){return b.id-a.id});
  }

  var scan=useCallback(function(){
    if(!domain.trim())return;
    var cl=domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
    setDomain(cl);
    var data=buildScan(cl);
    setProg(0);setView("scanning");setPhase("Discovering pages...");setSel(null);setVpat(false);
    var tot=(data.webPages.length+data.documents.length)*3,st=0;
    timer.current=setInterval(function(){
      st++;
      setProg(Math.min(99,Math.round(st/tot*100)));
      setPhase(st<data.webPages.length*2?"Scanning web pages...":"Validating documents...");
      if(st>=tot){
        clearInterval(timer.current);setProg(100);
        setWp(data.webPages);setDc(data.documents);setIsDemo(true);setScanMeta(null);setBrokenLinks([]);
        var scanIssueTotal=data.webPages.reduce(function(x,p){return x+(p.totalIssues||0)},0);
        showToast("\u2705 Scan complete: "+data.webPages.length+" pages, "+data.documents.length+" docs, "+scanIssueTotal+" issues");
        setTimeout(function(){setView("app");setMod("dashboard")},300);
      }
    },30);
  },[domain]);

  useEffect(function(){return function(){clearInterval(timer.current)}},[]);

  /* ── Lazy DOM Load (Session 47) ──
     When user selects a page in Web Audit, fetch the captured DOM from Cosmos
     on demand (not loaded with export to keep payload small). Caches on pg.dom. */
  useEffect(function(){
    if(sel===null||mod!=="webAudit")return;
    var pg=wp[sel];
    if(!pg||pg.dom)return; /* Already have DOM or no page */
    var sid=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:null;
    if(!sid)return;
    /* Fetch DOM from page-update GET endpoint */
    fetch("/api/session-stream/"+sid+"/page/"+sel,{credentials:"include"})
      .then(function(r){return r.ok?r.json():null})
      .then(function(data){
        if(data&&data.dom){
          /* Cache DOM on the page object in state */
          setWp(function(prev){return prev.map(function(p,i){
            if(i!==sel)return p;
            return Object.assign({},p,{dom:data.dom});
          })});
        }
      })
      .catch(function(e){console.warn("DOM lazy-load failed for page "+sel+":",e.message)});
  },[sel,mod]);

  /* ── PDF.JS REAL RENDERING ── */
  var currentPdfUrl=useMemo(function(){
    if(sel===null||mod!=="docAudit")return null;
    var sorted=dc.slice().sort(function(a,b){return b.views-a.views});
    var doc=sorted[sel];
    return doc&&doc.url&&doc.url!==doc.name?doc.url:null;
  },[sel,mod,dc]);

  /* Load PDF document when selected doc changes */
  useEffect(function(){
    pdfDocRef.current=null;
    setPdfPages({});
    setPdfMode("none");
    setPdfLoading(false);
    if(!currentPdfUrl)return;
    /* Initialize pdf.js */
    if(typeof window.pdfjsLib==="undefined"){setPdfMode("embed");return;}
    try{window.pdfjsLib.GlobalWorkerOptions.workerSrc="/vendor/pdfjs/pdf.worker.min.js";}catch(e){}
    setPdfLoading(true);
    var cancelled=false;
    /* Proxy through our server to bypass CORS restrictions */
    var proxyUrl="/api/pdf-proxy?url="+encodeURIComponent(currentPdfUrl);
    window.pdfjsLib.getDocument({url:proxyUrl,withCredentials:false}).promise.then(function(pdf){
      if(cancelled)return;
      pdfDocRef.current=pdf;
      setPdfMode("canvas");
      setPdfLoading(false);
    }).catch(function(err){
      if(cancelled)return;
      console.warn("PDF.js load failed:",err.message||err);
      /* Fallback: try direct URL (might work for same-origin PDFs) */
      window.pdfjsLib.getDocument({url:currentPdfUrl,withCredentials:false}).promise.then(function(pdf){
        if(cancelled)return;
        pdfDocRef.current=pdf;
        setPdfMode("canvas");
        setPdfLoading(false);
      }).catch(function(err2){
        if(cancelled)return;
        console.warn("PDF.js direct load also failed:",err2.message||err2);
        setPdfMode("embed");
        setPdfLoading(false);
      });
    });
    return function(){cancelled=true;};
  },[currentPdfUrl]);

  /* Render current page to canvas data URL when page changes */
  useEffect(function(){
    if(pdfMode!=="canvas"||!pdfDocRef.current)return;
    if(pdfPages[pdfPage])return; /* Already rendered */
    var pdf=pdfDocRef.current;
    var pg=Math.min(pdfPage,pdf.numPages);
    var cancelled=false;
    /* Timeout: if page doesn't render in 6s, fall back to Google Docs Viewer */
    var timeout=setTimeout(function(){
      if(!cancelled&&!pdfPages[pg]){console.warn("PDF page render timeout, falling back to embed");setPdfMode("embed");}
    },6000);
    pdf.getPage(pg).then(function(page){
      if(cancelled)return;
      var scale=2.5;
      var vp=page.getViewport({scale:scale});
      var canvas=document.createElement("canvas");
      canvas.width=vp.width;canvas.height=vp.height;
      var ctx=canvas.getContext("2d");
      page.render({canvasContext:ctx,viewport:vp}).promise.then(function(){
        if(cancelled)return;
        clearTimeout(timeout);
        setPdfPages(function(prev){var n=Object.assign({},prev);n[pg]=canvas.toDataURL("image/jpeg",0.85);return n;});
      }).catch(function(e){if(!cancelled){clearTimeout(timeout);console.warn("Page render failed:",e);setPdfMode("embed");}});
    }).catch(function(e){if(!cancelled){clearTimeout(timeout);console.warn("getPage failed:",e);setPdfMode("embed");}});
    /* Pre-render next page */
    if(pg<pdf.numPages&&!pdfPages[pg+1]){
      pdf.getPage(pg+1).then(function(page){
        if(cancelled)return;
        var scale=2.5;var vp=page.getViewport({scale:scale});
        var c2=document.createElement("canvas");c2.width=vp.width;c2.height=vp.height;
        page.render({canvasContext:c2.getContext("2d"),viewport:vp}).promise.then(function(){
          if(cancelled)return;
          setPdfPages(function(prev){var n=Object.assign({},prev);n[pg+1]=c2.toDataURL("image/jpeg",0.85);return n;});
        }).catch(function(){});
      }).catch(function(){});
    }
    return function(){cancelled=true;clearTimeout(timeout);};
  },[pdfPage,pdfMode]);

  /* PR-5.15c 2/2 — load + render the PA11Y Lab uploaded PDF to page
     images. Self-contained (separate from the Watchtower URL viewer
     above): the lab holds the PDF as base64 in aiPdf.data, so we decode
     to bytes and hand them straight to pdfjs (getDocument({data})). We
     render every audited page once, caching a JPEG data URL plus the
     page's UNSCALED point dimensions — the overlay needs points, not
     pixels, because the server's bbox geometry is in PDF-point space.
     Re-runs whenever a new PDF is uploaded (aiPdf identity changes). */
  useEffect(function(){
    labPdfDocRef.current=null;
    setLabPdfPages({});setLabPdfNumPages(0);
    if(!aiPdf||!aiPdf.data){setLabPdfStatus("none");return;}
    if(typeof window.pdfjsLib==="undefined"){setLabPdfStatus("error");return;}
    try{window.pdfjsLib.GlobalWorkerOptions.workerSrc="/vendor/pdfjs/pdf.worker.min.js";}catch(e){}
    var bytes;
    try{var bin=atob(aiPdf.data);bytes=new Uint8Array(bin.length);for(var bi=0;bi<bin.length;bi++)bytes[bi]=bin.charCodeAt(bi);}
    catch(e){setLabPdfStatus("error");return;}
    setLabPdfStatus("loading");
    var cancelled=false;
    /* RENDER_SCALE only affects raster sharpness; the overlay is
       percent-based so it's scale-independent. 2.0 is crisp without
       being huge. */
    var RENDER_SCALE=2.0;
    window.pdfjsLib.getDocument({data:bytes}).promise.then(function(pdf){
      if(cancelled)return;
      labPdfDocRef.current=pdf;
      setLabPdfNumPages(pdf.numPages);
      var maxRender=Math.min(pdf.numPages,50);
      function renderPage(p){
        if(cancelled||p>maxRender)return Promise.resolve();
        return pdf.getPage(p).then(function(page){
          if(cancelled)return;
          var vp=page.getViewport({scale:RENDER_SCALE});
          var canvas=document.createElement("canvas");
          canvas.width=vp.width;canvas.height=vp.height;
          return page.render({canvasContext:canvas.getContext("2d"),viewport:vp}).promise.then(function(){
            if(cancelled)return;
            var url=canvas.toDataURL("image/jpeg",0.82);
            setLabPdfPages(function(prev){
              var n=Object.assign({},prev);
              n[p]={url:url,ptW:vp.width/RENDER_SCALE,ptH:vp.height/RENDER_SCALE};
              return n;
            });
            return renderPage(p+1);
          });
        });
      }
      return renderPage(1).then(function(){if(!cancelled)setLabPdfStatus("ready");});
    }).catch(function(e){if(!cancelled){console.warn("Lab PDF render failed:",e&&e.message);setLabPdfStatus("error");}});
    return function(){cancelled=true;};
  },[aiPdf]);

  /* ── IMPORT FROM EXTENSION JSON OR CSV ── */
  var fileInputRef=useRef(null);
  var _dragOver=useState(false),dragOver=_dragOver[0],setDragOver=_dragOver[1];
  function importScanFile(ev){
    var file=ev.target.files&&ev.target.files[0];
    if(!file)return;
    var isCSV=file.name.toLowerCase().endsWith(".csv");
    var reader=new FileReader();
    reader.onload=function(e){
      try{
        if(isCSV){importFromCsv(e.target.result);}
        else{importFromJson(e.target.result);}
      }catch(err){alert("Error importing: "+err.message);}
    };
    reader.readAsText(file);
    ev.target.value="";
  }
  /** Import scan data from PiARIS JSON (extension export or session-stream export).
      Normalizes issues, hydrates WCAG info, restores AI enrichment state. */
  function importFromJson(text){
    try{
    var data=JSON.parse(text);
    if(!data.webPages&&!data.documents){showToast("Invalid PiARIS JSON: missing webPages/documents","error");return;}
    var dom=data.scan?data.scan.domain:"imported-scan";
    setDomain(dom);
    var wps=(data.webPages||[]).map(function(pg,idx){
      var iss=(pg.issues||[]).map(function(i,ii){
        var wcag=i.wcag||"";
        var info=W[wcag]||{n:i.wcagName||i.desc||i.ruleId||"Unknown",l:"A",p:"Unknown",r:i.fix||""};
        return{id:"w"+idx+"-"+ii,wcag:wcag,sev:i.sev||"minor",desc:i.desc||"",count:i.count||1,
          el:i.el||"",fix:i.fix||"",info:info,remediated:i.remediated||false,engine:i.engine||"axe",
          priority:i.priority||"P3",impactedGroups:i.impactedGroups||["All Users"],
          loc:{x:50,y:50,zone:"body"},selector:i.selector||"",nodes:i.nodes||[],
          pFix:"",aiFix:"",manualFix:"",acceptedFix:"",acceptedSrc:"",fixCategory:""};
      });
      var tc=iss.reduce(function(x,i){return x+i.count},0);
      return{url:pg.url||dom+pg.p,p:pg.p||"/",n:pg.n||pg.title||pg.p||"/",d:pg.d||pg.depth||0,
        status:pg.status||200,screenshot:pg.screenshot||"",dom:pg.dom||"",issues:iss,totalIssues:tc,wcagsFailed:iss.length,
        critCount:iss.filter(function(i){return i.sev==="critical"}).reduce(function(x,i){return x+i.count},0),
        majCount:iss.filter(function(i){return i.sev==="major"}).reduce(function(x,i){return x+i.count},0),
        minCount:iss.filter(function(i){return i.sev==="minor"}).reduce(function(x,i){return x+i.count},0),
        remediatedCount:0,isArchived:pg.isArchived||false};
    });
    var dcs=(data.documents||[]).map(function(d){
      var pdfIssues=(d.accessibilityIssues||[]).map(function(ai,ii){
        var wcag=ai.wcag||"";
        var info=W[wcag]||{n:ai.issue||"Unknown",l:"A",p:"Unknown",r:ai.fix||""};
        return{id:"d"+ii,wcag:wcag,sev:ai.sev||"minor",desc:ai.issue||"",count:1,
          el:"",fix:ai.fix||"",info:info,remediated:false,engine:"paris-pdf",
          loc:{x:50,y:50,zone:"body"}};
      });
      var tc=pdfIssues.reduce(function(x,i){return x+i.count},0);
      return{name:d.name||"",url:d.url||"",ext:d.ext||"pdf",
        pages:d.pageCount||0,pageCount:d.pageCount||0,
        department:d.department||"",functionalType:d.functionalType||"",
        foundOn:d.foundOn||d.referrer||"",
        tagged:d.isTagged||false,hasStructTree:d.hasStructTree||false,
        lang:d.lang||"",title:d.title||"",
        producer:d.producer||"",creator:d.creator||"",
        imageCount:d.imageCount||0,hasForm:d.hasForm||false,
        hasBookmarks:d.hasBookmarks||false,isEncrypted:d.isEncrypted||false,
        fileSize:d.fileSize||0,
        riskScore:d.riskScore||0,riskLevel:d.riskLevel||"low",
        priority:d.riskLevel==="high"?"critical":d.riskLevel==="medium"?"high":"medium",
        views:0,
        lastScanned:d.lastScanned||new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}),
        issues:pdfIssues,totalIssues:tc,wcagsFailed:pdfIssues.length,
        critCount:pdfIssues.filter(function(i){return i.sev==="critical"}).reduce(function(x,i){return x+i.count},0),
        majCount:pdfIssues.filter(function(i){return i.sev==="major"}).reduce(function(x,i){return x+i.count},0),
        minCount:pdfIssues.filter(function(i){return i.sev==="minor"}).reduce(function(x,i){return x+i.count},0),
        remediatedCount:0,valResults:[]};
    });
    var totalIssueCount=wps.reduce(function(x,p){return x+p.totalIssues},0);
    setScanMeta(data.scan?{sessionId:data.scan.sessionId||null,templateTokens:data.scan.templateTokens||0,templateSamples:data.scan.templateSamples||[],templateDedup:data.scan.templateDedup||null}:null);
    setBrokenLinks(data.brokenLinks||[]);
    /* Hydrate persisted remediation data from Cosmos */
    var hydratedEnrich={};
    for(var hi=0;hi<wps.length;hi++){
      var srcPg=(data.webPages||[])[hi];
      if(srcPg&&srcPg.aiEnrichment){hydratedEnrich[hi]=srcPg.aiEnrichment}
      if(srcPg&&srcPg.fixScript){wps[hi]=Object.assign({},wps[hi],{fixScript:srcPg.fixScript,patches:srcPg.patches||[],fixSummary:srcPg.fixSummary||{}})}
      /* Hydrate per-issue fix data (Session 46 workbench) */
      if(srcPg&&srcPg.issuesFixes&&Array.isArray(srcPg.issuesFixes)){
        var fixArr=srcPg.issuesFixes;
        var hydratedIssues=wps[hi].issues.map(function(iss,issIdx){
          if(issIdx>=fixArr.length)return iss;
          var saved=fixArr[issIdx];
          if(!saved||saved.wcag&&saved.wcag!==iss.wcag)return iss;
          return Object.assign({},iss,{pFix:saved.pFix||"",aiFix:saved.aiFix||"",manualFix:saved.manualFix||"",
            acceptedFix:saved.acceptedFix||"",acceptedSrc:saved.acceptedSrc||"",
            fixCategory:classifyFixCategory(iss),remediated:!!(saved.acceptedFix)});
        });
        wps[hi]=Object.assign({},wps[hi],{issues:hydratedIssues,fixesBuilt:hydratedIssues.some(function(i){return i.pFix||i.aiFix||i.manualFix})});
      }
    }
    if(Object.keys(hydratedEnrich).length>0){setAiEnrich(hydratedEnrich)}
    setWp(wps);setDc(dcs);setIsDemo(false);setView("app");_setModRaw("dashboard");setSel(null);setDashDrill(null);
    showToast("\u2705 Imported: "+dom+" \u2014 "+wps.length+" pages, "+dcs.length+" docs, "+totalIssueCount+" issues found");
    }catch(e){showToast("Import error: "+e.message,"error");}
  }
  /** Import scan data from CSV format (exported from PiARIS or extension) */
  function importFromCsv(text){
    /* Parse PiARIS CSV — has 3 sections: PAGE INVENTORY, DOCUMENT INVENTORY, ACCESSIBILITY ISSUES */
    var lines=text.split("\n");
    var section="",pageRows=[],docRows=[],issueRows=[];
    for(var i=0;i<lines.length;i++){
      var line=lines[i].trim();
      if(line.indexOf("--- PAGE INVENTORY")>-1){section="pages";continue;}
      if(line.indexOf("--- DOCUMENT INVENTORY")>-1){section="docs";continue;}
      if(line.indexOf("--- ACCESSIBILITY ISSUES")>-1){section="issues";continue;}
      if(!line||line.indexOf("URL,")===0||line.indexOf("File Name,")===0||line.indexOf("Page URL,")===0)continue;
      var cols=parseCsvLine(line);
      if(section==="pages"&&cols.length>=4)pageRows.push(cols);
      else if(section==="docs"&&cols.length>=4)docRows.push(cols);
      else if(section==="issues"&&cols.length>=7)issueRows.push(cols);
    }
    /* Build page map: URL → page object */
    var pageMap={};
    pageRows.forEach(function(r){
      var url=r[0]||"";
      pageMap[url]={url:url,p:"/",n:r[2]||url,d:parseInt(r[1])||0,status:parseInt(r[3])||200,issues:[]};
      try{pageMap[url].p=new URL(url).pathname}catch(e){}
    });
    /* Attach issues to pages: CSV cols = [PageURL, Engine, WCAG, Severity, Rule, Description, Elements, HTML, Fix] */
    issueRows.forEach(function(r){
      var url=r[0]||"";
      if(!pageMap[url])pageMap[url]={url:url,p:"/",n:url,d:0,status:200,issues:[]};
      var wcag=r[2]||"";
      var info=W[wcag]||{n:r[5]||r[4]||"Unknown",l:"A",p:"Unknown",r:r[8]||""};
      pageMap[url].issues.push({id:"c"+pageMap[url].issues.length,wcag:wcag,sev:r[3]||"minor",
        desc:r[5]||"",count:parseInt(r[6])||1,el:r[7]||"",fix:r[8]||"",info:info,remediated:false,
        engine:r[1]||"axe",loc:{x:50,y:50,zone:"body"}});
    });
    /* Convert to arrays */
    var wps=Object.values(pageMap).map(function(pg){
      var tc=pg.issues.reduce(function(x,i){return x+i.count},0);
      return Object.assign({},pg,{totalIssues:tc,wcagsFailed:pg.issues.length,
        critCount:pg.issues.filter(function(i){return i.sev==="critical"}).reduce(function(x,i){return x+i.count},0),
        majCount:pg.issues.filter(function(i){return i.sev==="major"}).reduce(function(x,i){return x+i.count},0),
        minCount:pg.issues.filter(function(i){return i.sev==="minor"}).reduce(function(x,i){return x+i.count},0),
        remediatedCount:0});
    });
    var dcs=docRows.map(function(r){
      return{name:r[0]||"",url:r[1]||"",ext:r[3]||"pdf",pages:parseInt(r[5])||0,pageCount:parseInt(r[5])||0,
        department:r[2]||"",foundOn:"",tagged:false,producer:"",priority:"medium",views:0,
        lastScanned:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}),
        issues:[],totalIssues:0,wcagsFailed:0,critCount:0,majCount:0,minCount:0,remediatedCount:0,valResults:[]};
    });
    var dom="imported-csv";if(wps.length>0){try{dom=new URL(wps[0].url).hostname}catch(e){}}
    setDomain(dom);setWp(wps);setDc(dcs);setIsDemo(false);setScanMeta(null);setBrokenLinks([]);setView("app");_setModRaw("dashboard");setSel(null);setDashDrill(null);
  }
  function parseCsvLine(line){
    var cols=[],cur="",inQ=false;
    for(var i=0;i<line.length;i++){
      var ch=line[i];
      if(ch==='"'){if(inQ&&line[i+1]==='"'){cur+='"';i++;}else{inQ=!inQ;}}
      else if(ch===","&&!inQ){cols.push(cur);cur="";}
      else{cur+=ch;}
    }
    cols.push(cur);return cols;
  }

  /* agg() imported from shared/helpers */
  var wSt=useMemo(function(){return agg(wp)},[wp]);
  var dSt=useMemo(function(){return agg(dc)},[dc]);
  var totalI=wSt.tI+dSt.tI;
  var totalFixed=wSt.tRem+dSt.tRem;
  var comp=useMemo(function(){return computeComplianceScore(wp,dc,W,scanMeta&&scanMeta.summary?scanMeta.summary:null)},[wp,dc,scanMeta]);
  var overallScore=comp.activeScore;

  /** Template deduplication (Session 47) — separates template issues from page-specific.
      STRICT RULES for template qualification:
      1. Must have a non-empty CSS selector (no selector = page-specific)
      2. Selector must be specific — bare tags (div, span, p, img, a) don't count
         because "some <img> has no alt" on 500 pages could be 500 different images
      3. Must appear on 2+ pages with the SAME normalized selector
      4. Contrast issues additionally need matching element context (same selector)
      Template = same broken element in a shared component (header/footer/nav).
      Page-specific = unique to one page OR too generic to confirm as template.
      @returns {Object} { groups, templateGroupCount, templateInstanceCount, pageSpecificCount, actionableCount } */
  var templateStats=useMemo(function(){
    /* Bare tags that are too generic to prove template membership */
    var GENERIC_TAGS=/^(div|span|p|a|img|h[1-6]|li|td|th|tr|ul|ol|dl|dt|dd|section|article|header|footer|nav|main|aside|button|input|select|textarea|label|form|table|iframe|meta|link|figure|figcaption)$/i;
    /** Check if a selector is specific enough to identify a shared template element.
        Must contain a class, ID, attribute, or compound selector — not just a bare tag. */
    function isSpecificSelector(sel){
      if(!sel||sel==="*")return false;
      /* Must have at least one of: .class, #id, [attr], or multi-part combinator */
      if(/[.#\[]/.test(sel))return true;
      /* Multi-part selector like "nav > a" is specific enough */
      if(/[> +~]/.test(sel))return true;
      /* Single bare tag is not specific */
      var bare=sel.trim().replace(/:[\w-]+(\([^)]*\))?/g,""); /* strip pseudo-classes */
      if(GENERIC_TAGS.test(bare))return false;
      /* Anything else (custom elements, data selectors) passes */
      return true;
    }
    var groups={};
    wp.forEach(function(pg,pi){
      (pg.issues||[]).forEach(function(iss){
        var nSel=normalizeSelector(iss.selector||"");
        var descKey=(iss.desc||"").substring(0,60).toLowerCase().replace(/\s+/g," ");
        var key=(iss.wcag||"")+"||"+nSel+"||"+descKey;
        if(!groups[key]){
          groups[key]={wcag:iss.wcag||"",sel:iss.selector||"",nSel:nSel,sev:iss.sev||"major",
            desc:iss.desc||"",info:iss.info||{},pages:[],count:0,
            isSpecific:isSpecificSelector(nSel)};
        }
        if(groups[key].pages.indexOf(pi)===-1)groups[key].pages.push(pi);
        groups[key].count+=(iss.count||1);
      });
    });
    var keys=Object.keys(groups);
    var templateGroupCount=0;
    var templateInstanceCount=0;
    var pageSpecificCount=0;
    keys.forEach(function(k){
      var g=groups[k];
      /* Template ONLY if: appears on 2+ pages AND has a specific selector */
      if(g.pages.length>1&&g.isSpecific){
        templateGroupCount++;
        templateInstanceCount+=g.count;
      } else {
        pageSpecificCount+=g.count;
      }
    });
    var actionableCount=templateGroupCount+pageSpecificCount;
    return{groups:groups,templateGroupCount:templateGroupCount,
      templateInstanceCount:templateInstanceCount,pageSpecificCount:pageSpecificCount,
      actionableCount:actionableCount,totalPages:wp.length};
  },[wp]);

  /** Mark all issues in an array as remediated — ONLY used after Verify Fixes confirms actual resolution.
      NOT called by Build Remediation Package. */
  function markAllFixed(arr,setter){setter(arr.map(function(item){var ni=item.issues.map(function(iss){return Object.assign({},iss,{remediated:true})});return Object.assign({},item,{issues:ni,remediatedCount:item.totalIssues})}))}
  /** Build Remediation Package — generates fix scripts for ALL pages that need them.
      Unlike buildRemediationFile (single page), this is the dashboard-level batch operation.
      Progress is tracked via buildProgress state, which the Dashboard renders as a progress bar.
      When all pages complete, packageReady flips to true and a download CTA appears.
      Stagger: pages are processed 2 seconds apart to avoid hammering the /api/remediate endpoint.
      Does NOT inflate compliance score — fixes are generated, not deployed. */
  function buildRemediationPackage(){
    /* License enforcement — free tier limited to 5 builds/month */
    if(!licenseGate("build"))return;
    var toFix=wp.filter(function(pg){return pg.issues&&pg.issues.length>0&&!pg.fixScript});
    if(toFix.length===0){
      showToast("All pages already have fix scripts generated. Check Remediation Center for downloads.");
      setPackageReady(true);
      return;
    }
    /* Initialize progress tracking — 2 phases per page: remediate + AI enrich */
    var totalSteps=toFix.length*2;
    setBuildProgress({active:true,total:totalSteps,completed:0,failed:0,currentPage:toFix[0].n||"Page 1",phase:"fixes"});
    setPackageReady(false);
    showToast("\u{1F9E0} Building smart remediation for "+toFix.length+" page"+(toFix.length!==1?"s":"")+" (fixes + AI enrichment)\u2026");
    /* Process pages sequentially with 2s stagger to avoid API overload */
    var completedSteps=0;var failedCount=0;
    toFix.forEach(function(pg,i){
      setTimeout(function(){
        /* Phase 1: Generate rule-based fix script */
        setBuildProgress(function(prev){return Object.assign({},prev,{currentPage:pg.n||"Page "+(i+1),phase:"fixes"})});
        remediatePage(pg);
        /* After ~3s (remediation done), advance progress and start AI enrichment */
        setTimeout(function(){
          completedSteps++;
          var pageNow=wp.find(function(p){return p.url===pg.url});
          if(!pageNow||!pageNow.fixScript){failedCount++}
          /* Phase 2: AI Enrich this page */
          var pgIdx=wp.indexOf(pg);
          if(pgIdx>=0&&!aiEnrich[pgIdx]&&!aiEnrichLoading[pgIdx]){
            setBuildProgress(function(prev){return Object.assign({},prev,{completed:completedSteps,phase:"enriching",currentPage:pg.n||"Page "+(i+1)})});
            aiEnrichPage(pgIdx);
          }
          /* After ~5s more (AI done), mark AI step complete */
          setTimeout(function(){
            completedSteps++;
            setBuildProgress(function(prev){
              var allDone=completedSteps>=totalSteps;
              if(allDone){
                setPackageReady(true);
                showToast("\u2705 Smart remediation complete \u2014 "+toFix.length+" page"+(toFix.length!==1?"s":"")+" with fixes + AI enrichment. Review in Remediation Center.");
              }
              return Object.assign({},prev,{completed:completedSteps,failed:failedCount,
                active:!allDone,currentPage:allDone?"Complete":prev.currentPage,
                phase:allDone?"done":"enriching"});
            });
          },5000);
        },3000);
      },i*3000);
    });
  }
  /** Toggle a single issue's remediated status (manual override by auditor) */
  function toggleIssue(pageIdx,issIdx,isWeb){var setter=isWeb?setWp:setDc;setter(function(prev){return prev.map(function(item,pi){if(pi!==pageIdx)return item;var ni=item.issues.map(function(iss,ii){return ii===issIdx?Object.assign({},iss,{remediated:!iss.remediated}):iss});return Object.assign({},item,{issues:ni,remediatedCount:ni.filter(function(i){return i.remediated}).reduce(function(x,i){return x+i.count},0)})})})}
  /** Generate fix script for a single web page via /api/remediate.
      Sets fixBuilding:true during generation. Does NOT mark issues as remediated. */
  function remediatePage(pg){
    var idx=wp.indexOf(pg);if(idx<0)return;
    /* Mark page as fix-building (does NOT change remediation status or score) */
    setWp(function(prev){return prev.map(function(p,i){if(i!==idx)return p;return Object.assign({},p,{fixBuilding:true})})});
    var ts=new Date().toLocaleTimeString();
    setStaging(function(prev){return prev.concat([
      {type:"web",name:pg.n,url:pg.url,issues:pg.totalIssues,critical:pg.critCount,time:ts,status:"generating",stage:"remediation"}
    ])});
    showToast("\u{1F527} Generating fix script for "+pg.n+"\u2026");
    /* Call the remediation API to generate real fixes */
    var issueData=pg.issues.map(function(iss){return{wcag:iss.wcag,sev:iss.sev,desc:iss.desc,count:iss.count,el:iss.el||"",fix:iss.fix||""}});
    fetch("/api/remediate",{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      credentials:"include",
      body:JSON.stringify({
        url:pg.url,
        issues:issueData,
        pageTitle:pg.n||"",
        context:pg.context||null
      })
    })
    .then(function(r){return r.ok?r.json():null})
    .then(function(data){
      if(data&&data.fixScript){
        /* Store fix script and patches on the page object */
        setWp(function(prev){return prev.map(function(p,i){
          if(i!==idx)return p;
          return Object.assign({},p,{fixScript:data.fixScript,patches:data.patches||[],fixSummary:data.summary||{},fixSource:data.source||"unknown"})
        })});
        /* Update staging entry */
        setStaging(function(prev){return prev.map(function(s){
          if(s.url===pg.url&&s.status==="generating"){
            return Object.assign({},s,{status:"staged",fixScript:data.fixScript,patchCount:(data.patches||[]).length,source:data.source||""})
          }
          return s;
        })});
        var src=data.source==="ai"?"\u{1F9E0} AI":"Rule-based";
        showToast("\u2713 "+src+" fix script generated: "+(data.patches||[]).length+" patches for "+pg.n);
        /* Persist to Cosmos */
        persistPageData(idx, {fixScript: data.fixScript, patches: data.patches||[], fixSummary: data.summary||{}});
      } else {
        showToast("Fix generation returned no results","error");
        setStaging(function(prev){return prev.map(function(s){
          return s.url===pg.url&&s.status==="generating"?Object.assign({},s,{status:"failed"}):s;
        })});
      }
    })
    .catch(function(e){
      showToast("Fix generation failed: "+e.message,"error");
      setStaging(function(prev){return prev.map(function(s){
        return s.url===pg.url&&s.status==="generating"?Object.assign({},s,{status:"failed"}):s;
      })});
    });
  }

  /* ═══ SESSION 46 — WORKBENCH: Build Remediation File + Accept/Reject ═══
     Core workbench functions for the per-issue fix workflow:
     
     DATA MODEL (per issue):
       issue.pFix        — PiARIS rule-engine fix (from /api/remediate patches)
       issue.aiFix       — AI semantic fix (from /api/enrich-web), or "same" if endorses PiARIS
       issue.manualFix   — Auditor typed override (always takes precedence)
       issue.acceptedFix — The chosen fix that ships in the remediation package
       issue.acceptedSrc — "piaris"|"ai"|"manual" — which source was accepted
       issue.fixCategory — Auto-classified category for smart defaults
     
     FIX PRECEDENCE (highest to lowest):
       1. Manual fix — auditor typed it, auto-accepted, never questioned
       2. PiARIS + AI agree — both produce same fix, auto-accepted
       3. Smart default — category determines winner (FIX_DEFAULTS map)
       4. User choice — must click accept button per issue
     
     BUTTON TERMINOLOGY:
       "Build Remediation File" — single page context (workbench)
       "Build Remediation Package" — multi-page context (dashboard)
     ═══════════════════════════════════════════════════════════════════ */

  /** Build Remediation File for a single page.
      Step 1: Calls /api/remediate for rule-based PiARIS fixes (patches with before/after HTML).
      Step 2: Triggers AI enrichment via aiEnrichPage if not already done.
      Step 3: Polls for AI results via setInterval (AI enrichment is async, takes 5-15s).
      Does NOT auto-accept any fixes — fills in pFix and aiFix columns for auditor review.
      @param {number} pageIdx - index in wp array */
  function buildRemediationFile(pageIdx){
    /* License enforcement — free tier limited to 5 builds/month */
    if(!licenseGate("build"))return;
    var page=wp[pageIdx];
    if(!page||!page.issues||page.issues.length===0){showToast("No issues to remediate on this page.");return}
    showToast("\u{1F527} Building remediation file for "+page.n+"\u2026");
    /* Mark page as building — UI shows spinner, disables build button */
    setWp(function(prev){return prev.map(function(p,i){if(i!==pageIdx)return p;return Object.assign({},p,{fixBuilding:true,fixesBuilt:false})})});
    /* Prepare issue data for the /api/remediate endpoint */
    var issueData=page.issues.map(function(iss){return{wcag:iss.wcag,sev:iss.sev,desc:iss.desc,count:iss.count,el:iss.el||"",fix:iss.fix||"",selector:iss.selector||""}});
    fetch("/api/remediate",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
      body:JSON.stringify({url:page.url,issues:issueData,pageTitle:page.n||"",context:page.context||null})
    }).then(function(r){return r.ok?r.json():null}).then(function(pData){
      /* Session 47/49: Map patches to issues. Strategy:
         1. Index-based: patches[i] matches issues[i] (API receives issues in order)
         2. WCAG+selector compound key (when selectors are present)
         3. WCAG-only fallback (last resort)
         Session 49 fix: clone patches and use a separate usedSet to prevent
         persistent _used flags from poisoning subsequent calls. */
      var patches=(pData&&pData.patches)||[];
      /* Clone patches to avoid mutating originals (prevents _used persistence) */
      var patchClones=patches.map(function(p,idx){return Object.assign({},p,{_patchIdx:idx})});
      /* Build WCAG+selector lookup — store ALL patches per key in arrays for dedup */
      var pFixMap={};
      patchClones.forEach(function(patch){
        var key=(patch.wcag||"")+"||"+(patch.selector||"");
        if(!pFixMap[key])pFixMap[key]=[];
        pFixMap[key].push(patch);
      });
      /* Separate used-tracking set (keyed by _patchIdx) — never mutates patch objects */
      var usedSet={};
      /* Update each issue with its PiARIS fix and auto-classify the fix category */
      setWp(function(prev){return prev.map(function(p,pi){
        if(pi!==pageIdx)return p;
        var newIssues=p.issues.map(function(iss,issIdx){
          var cat=classifyFixCategory(iss);
          var patchMatch=null;
          /* Strategy 1: index-based — most reliable when API preserves issue order */
          if(issIdx<patchClones.length&&patchClones[issIdx]&&(patchClones[issIdx].wcag||"")===(iss.wcag||"")&&!usedSet[issIdx]){
            patchMatch=patchClones[issIdx];usedSet[issIdx]=true;
          }
          /* Strategy 2: WCAG+selector compound key (both non-empty) */
          if(!patchMatch&&iss.selector){
            var key1=(iss.wcag||"")+"||"+(iss.selector||"");
            var arr1=pFixMap[key1];
            if(arr1){for(var a=0;a<arr1.length;a++){if(!usedSet[arr1[a]._patchIdx]){patchMatch=arr1[a];usedSet[arr1[a]._patchIdx]=true;break;}}}
          }
          /* Strategy 3: WCAG-only fallback — first unused patch with same WCAG */
          if(!patchMatch){
            for(var k in pFixMap){
              if(k.indexOf((iss.wcag||"")+"||")===0){
                var arr2=pFixMap[k];
                for(var b=0;b<arr2.length;b++){if(!usedSet[arr2[b]._patchIdx]){patchMatch=arr2[b];usedSet[arr2[b]._patchIdx]=true;break;}}
                if(patchMatch)break;
              }
            }
          }
          var pFixText=patchMatch?(patchMatch.after||patchMatch.description||""):"";
          return Object.assign({},iss,{pFix:pFixText,fixCategory:cat});
        });
        return Object.assign({},p,{issues:newIssues,fixBuilding:false,fixesBuilt:true,
          fixScript:pData&&pData.fixScript?pData.fixScript:p.fixScript,patches:patches,
          fixSummary:pData&&pData.summary?pData.summary:p.fixSummary});
      })});
      /* Poll for AI enrichment results — AI is async and typically takes 5-15s.
         We retry mergeAiFixesOntoPage every 3s, up to 8 times (24s max).
         mergeAiFixesOntoPage reads from aiEnrichRef.current (not stale closure). */
      var aiRetryCount=0;
      var aiRetryInterval=setInterval(function(){
        aiRetryCount++;
        mergeAiFixesOntoPage(pageIdx);
        /* 8 retries × 3s = 24s max wait — well beyond typical AI response time */
        if(aiRetryCount>=8){clearInterval(aiRetryInterval)}
      },3000);
      showToast("\u2713 Remediation file built for "+page.n+" \u2014 review fixes in workbench");
      /* Persist PiARIS fixes to Cosmos so they survive page reload */
      if(pData&&pData.fixScript){persistPageData(pageIdx,{fixScript:pData.fixScript,patches:patches,fixSummary:pData&&pData.summary||{}})}
      /* Trigger AI enrichment if this page hasn't been enriched yet */
      if(!aiEnrich[pageIdx])aiEnrichPage(pageIdx);
    }).catch(function(e){
      showToast("Remediation build failed: "+e.message,"error");
      setWp(function(prev){return prev.map(function(p,i){if(i!==pageIdx)return p;return Object.assign({},p,{fixBuilding:false})})});
    });
  }

  /** Merge AI enrichment suggestions onto issue aiFix fields.
      Called by the retry interval in buildRemediationFile AND inline from
      aiEnrichItem (post-burst) and loadPageDetail (rehydrate on reopen).

      Parameters:
        pageIdx       — index in wp array
        explicitData  — optional enrichment payload. When provided, the
                        merger uses this directly instead of reading from
                        aiEnrichRef.current. Lets callers that JUST
                        fetched (and haven't waited for the ref-syncing
                        useEffect) merge synchronously. Pass null/omit to
                        fall back to the ref.
        opts          — { persist: bool }. When true, after merging we
                        persist the per-issue fix array to Cosmos via
                        PATCH /api/session-stream/:id/page/:idx so a
                        reopened session re-renders the AI Fix column
                        without needing the user to re-run anything.

      If AI fix text matches PiARIS fix (via normFix comparison), sets
      aiFix to "same" — the workbench renders this as "✓ Endorses PiARIS
      fix". */
  function mergeAiFixesOntoPage(pageIdx, explicitData, opts){
    var persist = !!(opts && opts.persist);
    setWp(function(prev){
      /* Prefer the explicit data path so callers don't race against the
         ref-syncing useEffect. Fall back to the ref for legacy callers
         (the polling interval in buildRemediationFile). */
      var enrichData = explicitData || aiEnrichRef.current[pageIdx];
      if(!enrichData||!enrichData.aiSuggestions)return prev;
      return prev.map(function(p,pi){
        if(pi!==pageIdx)return p;
        /* Delegate the actual matching to the pure helper so unit tests
           hit the same algorithm. */
        var newIssues = mergeAiSuggestionsIntoIssues(p.issues, enrichData);
        /* Persist the merged per-issue fix array to Cosmos so reopening
           the session rehydrates the AI Fix column without re-running
           anything. Only fires when the caller asked us to (persist:true)
           AND at least one aiFix was actually filled — avoids no-op
           writes when there were no suggestions to merge. */
        if(persist){
          var anyFilled = newIssues.some(function(it,idx){
            return (it.aiFix||"") !== (p.issues[idx] && p.issues[idx].aiFix || "");
          });
          if(anyFilled){
            var fixData = newIssues.map(function(it){
              return {wcag:it.wcag,pFix:it.pFix||"",aiFix:it.aiFix||"",
                manualFix:it.manualFix||"",acceptedFix:it.acceptedFix||"",
                acceptedSrc:it.acceptedSrc||""};
            });
            persistPageData(pageIdx,{issuesFixes:fixData});
          }
        }
        return Object.assign({},p,{issues:newIssues});
      });
    });
  }

  /** Accept a fix for a specific issue. Sets acceptedFix from the chosen source
      and marks the issue as remediated (green marker on right pane).
      @param {number} pageIdx - page index in wp
      @param {number} issIdx - issue index within page.issues
      @param {string} source - "piaris"|"ai"|"manual" — which fix column to accept */
  function acceptIssueFix(pageIdx,issIdx,source){
    setWp(function(prev){return prev.map(function(p,pi){
      if(pi!==pageIdx)return p;
      var newIssues=p.issues.map(function(iss,ii){
        if(ii!==issIdx)return iss;
        /* Resolve fix text from source — AI "same" means use the PiARIS fix text */
        var fixText=source==="piaris"?iss.pFix||"":source==="ai"?(iss.aiFix==="same"?iss.pFix:iss.aiFix||""):iss.manualFix||"";
        return Object.assign({},iss,{acceptedFix:fixText,acceptedSrc:source,remediated:!!fixText});
      });
      return Object.assign({},p,{issues:newIssues});
    })});
    persistIssueFixes(pageIdx);
  }

  /** Accept all fixes for a page using smart defaults precedence.
      Applies fixes in order: Manual > PiARIS+AI agree > Category winner > Skip.
      Uses FIX_DEFAULTS map from shared/constants.js to determine category winners.
      @param {number} pageIdx - page index in wp */
  function acceptAllPageFixes(pageIdx){
    setWp(function(prev){return prev.map(function(p,pi){
      if(pi!==pageIdx)return p;
      var newIssues=p.issues.map(function(iss){
        if(iss.acceptedFix)return iss; /* Already accepted — don't overwrite */
        var cat=iss.fixCategory||classifyFixCategory(iss);var src="";var fixText="";
        /* Priority 1: Manual fix — auditor intent is clear, always wins */
        if(iss.manualFix){src="manual";fixText=iss.manualFix}
        /* Priority 2: Both engines agree — AI said "same" or text matches */
        else if(iss.pFix&&iss.aiFix==="same"){src="piaris";fixText=iss.pFix}
        else if(iss.pFix&&iss.aiFix&&normFix(iss.pFix)===normFix(iss.aiFix)){src="piaris";fixText=iss.pFix}
        /* Priority 3: Smart default — category determines winner */
        else if(iss.pFix&&iss.aiFix){var winner=FIX_DEFAULTS[cat]||"piaris";src=winner;fixText=winner==="piaris"?iss.pFix:(iss.aiFix==="same"?iss.pFix:iss.aiFix)}
        /* Priority 4: Only one fix available — use it */
        else if(iss.pFix){src="piaris";fixText=iss.pFix}
        else if(iss.aiFix&&iss.aiFix!=="same"){src="ai";fixText=iss.aiFix}
        if(!fixText)return iss;
        return Object.assign({},iss,{acceptedFix:fixText,acceptedSrc:src,remediated:true});
      });
      return Object.assign({},p,{issues:newIssues});
    })});
    showToast("\u2713 Smart defaults applied \u2014 review accepted fixes");
    persistIssueFixes(pageIdx);
  }

  /** Clear the accepted fix for a specific issue. Resets to unresolved state.
      Used when the auditor clicks the ↻ reset button on an accepted fix.
      @param {number} pageIdx - page index @param {number} issIdx - issue index */
  function clearIssueFix(pageIdx,issIdx){
    setWp(function(prev){return prev.map(function(p,pi){
      if(pi!==pageIdx)return p;
      var newIssues=p.issues.map(function(iss,ii){if(ii!==issIdx)return iss;return Object.assign({},iss,{acceptedFix:"",acceptedSrc:"",remediated:false})});
      return Object.assign({},p,{issues:newIssues});
    })});
  }

  /** Set a manual fix for a specific issue — auto-accepts it immediately.
      Manual fixes always take top precedence (above PiARIS and AI) because the
      auditor explicitly typed the fix, signaling clear intent. The contentEditable
      div in the Manual Fix column calls this on blur.
      @param {number} pageIdx - page index in wp
      @param {number} issIdx - issue index within page.issues
      @param {string} text - the fix text typed by the auditor */
  function setManualFix(pageIdx,issIdx,text){
    text=(text||"").trim();
    setWp(function(prev){return prev.map(function(p,pi){
      if(pi!==pageIdx)return p;
      var newIssues=p.issues.map(function(iss,ii){
        if(ii!==issIdx)return iss;
        /* Empty text clears the manual fix without accepting */
        if(!text)return Object.assign({},iss,{manualFix:""});
        /* Non-empty text auto-accepts — auditor typed it, intent is clear */
        return Object.assign({},iss,{manualFix:text,acceptedFix:text,acceptedSrc:"manual",remediated:true});
      });
      return Object.assign({},p,{issues:newIssues});
    })});
    if(text)persistIssueFixes(pageIdx);
  }

  /** Inject an accepted fix into the right-side iframe for live simulation.
      Posts a message to the remFrameRef iframe which has a postMessage listener
      (injected by /api/fetch-page v2.0) that applies DOM mutations based on the
      fix category: setAttribute for ARIA, style changes for contrast, element
      wrapping for structure, etc. The right pane shows what the site will look
      like AFTER the client deploys the fix package.
      @param {Object} iss - issue object with selector, el, wcag, fixCategory
      @param {string} fixText - the accepted fix text to apply */
  function injectAcceptedFix(iss,fixText){
    if(!remFrameRef.current)return;
    try{remFrameRef.current.contentWindow.postMessage({action:"applyFix",selector:iss.selector||"",element:iss.el||"",wcag:iss.wcag||"",fixText:fixText,category:iss.fixCategory||classifyFixCategory(iss)},"*")}catch(e){}
  }

  /** Persist all issue fix data for a page to Cosmos DB via PATCH /api/session-stream/{id}/page/{idx}.
      Saves per-issue fix columns (pFix, aiFix, manualFix, acceptedFix, acceptedSrc) so they
      survive page reload and session restore. Called after every accept/clear/manual action.
      @param {number} pageIdx - page index in wp */
  function persistIssueFixes(pageIdx){
    var page=wp[pageIdx];if(!page)return;
    var fixData=page.issues.map(function(iss){return{wcag:iss.wcag,pFix:iss.pFix||"",aiFix:iss.aiFix||"",manualFix:iss.manualFix||"",acceptedFix:iss.acceptedFix||"",acceptedSrc:iss.acceptedSrc||""}});
    persistPageData(pageIdx,{issuesFixes:fixData});
  }

  /** Convert a PDF document to accessible HTML via DocMaya pipeline.
      Adds to staging queue; does NOT mark issues as remediated until verified. */
  function remediateDoc(doc){
    var idx=dc.indexOf(doc);if(idx<0)return;
    /* Call the real DocMaya conversion pipeline */
    convertToHtml(idx);
    /* Add to staging queue with 'generating' status — will update when conversion completes */
    var ts=new Date().toLocaleTimeString();
    setStaging(function(prev){
      /* Avoid duplicate staging entries for the same doc */
      var exists=prev.some(function(s){return s.type==="pdf"&&s.name===doc.name});
      if(exists)return prev;
      return prev.concat([
        {type:"pdf",name:doc.name,url:doc.url||doc.name,issues:doc.totalIssues,critical:doc.critCount,time:ts,status:"generating",stage:"remediation",foundOn:doc.foundOn||"",docIndex:idx}
      ]);
    });
    /* PR-J: copy retext — Watchtower remediation, not DocMaya conversion.
       (This function is orphaned by PR-J's button repoint and kept only for
       follow-up cleanup, but if anything still calls it the toast should
       still say "Remediating PDF…" rather than the old DocMaya wording.) */
    showToast("\u{1F527} Remediating PDF… "+doc.name);
  }
  /** Batch-convert all unconverted PDFs via DocMaya */
  function remediateAllDocs(){
    var toConvert=[];
    for(var di=0;di<dc.length;di++){
      if(dc[di].totalIssues>0&&!convStatus[di]){toConvert.push(di)}
    }
    if(toConvert.length===0){showToast("All documents already converted or have no issues.");return}
    /* PR-J: copy retext — see remediateDoc above for the rationale. */
    showToast("\u{1F527} Remediating PDF… "+toConvert.length+" documents");
    toConvert.forEach(function(idx){remediateDoc(dc[idx])});
  }

  /* ── PR-J: Watchtower source-PDF remediation (Adobe Auto-Tag + alt inject) ──
     These replace the old remediateDoc / remediateAllDocs button handlers.
     Same Adobe-backed pipeline PA11Y Lab's runFullRemediation uses, applied
     to the source PDF byte stream rather than the DocMaya HTML overlay.
     The legacy handlers above are intentionally left in place — orphaned but
     not removed — so a follow-up cleanup commit can drop them once the new
     path is verified in prod. */

  /** Fetch a PDF's raw bytes — direct first, then proxy via /api/pdf-proxy on
      CORS/cross-origin failure. Same fallback shape the pdf.js viewer uses. */
  async function fetchPdfBytes(pdfUrl){
    if(!pdfUrl) throw new Error("doc.url missing — cannot fetch PDF source");
    /* Cross-origin PDFs frequently lack CORS headers, so a same-origin
       proxy is needed. We try the proxy FIRST for any non-relative URL,
       matching the pdf.js loader's preferred path (it found the proxy
       more reliable than the direct fetch on prod customer hosts). */
    var isAbsolute=/^https?:\/\//i.test(pdfUrl);
    var first=isAbsolute?("/api/pdf-proxy?url="+encodeURIComponent(pdfUrl)):pdfUrl;
    var second=isAbsolute?pdfUrl:null;
    try{
      var r=await fetch(first,{credentials:"include"});
      if(r.ok) return await r.arrayBuffer();
      if(!second) throw new Error("pdf-proxy returned HTTP "+r.status);
    }catch(e){
      if(!second) throw e;
    }
    /* Fallback: direct fetch (same-origin PDFs work, cross-origin will
       likely fail again with a CORS error — but we tried). */
    var r2=await fetch(second,{credentials:"include"});
    if(!r2.ok) throw new Error("PDF fetch failed (proxy + direct): HTTP "+r2.status);
    return await r2.arrayBuffer();
  }

  /** ArrayBuffer → base64. Chunked to avoid blowing the stack on
      large PDFs (apply(null, big-array) overflows in V8 around 100K args). */
  function arrayBufferToBase64(buf){
    var bytes=new Uint8Array(buf);
    var CHUNK=0x8000;
    var parts=[];
    for(var i=0;i<bytes.length;i+=CHUNK){
      parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i+CHUNK)));
    }
    return btoa(parts.join(""));
  }

  /** Build the ordered alts:[] array for /api/pdf-inject-alts from
      Watchtower's per-doc AI enrichment cache. Returns [] if no enrichment
      is present (tag-only fallback path). Per the PR-J locked decision:
      Auto-AI with tag-only fallback. */
  function buildWatchtowerAltsFromEnrich(docIdx){
    var enrich=aiEnrich&&aiEnrich["d"+docIdx];
    if(!enrich||!Array.isArray(enrich.altTextSuggestions)||enrich.altTextSuggestions.length===0){
      return [];
    }
    /* Map AI-suggested alts onto the pdf-inject-alts shape: {state, alt, page}.
       Decorative images go in as state:"decorative" (alt:"") so the injector
       writes an empty /Alt; everything else is state:"ai" with the suggested
       string. page is forwarded only when the enrichment recorded one — the
       injector falls back to ordered match otherwise. */
    return enrich.altTextSuggestions.map(function(a){
      var isDecorative=!!a.isDecorative;
      return {
        state: isDecorative ? "decorative" : "ai",
        alt:   isDecorative ? "" : (a.suggestedAlt || ""),
        page:  (typeof a.page === "number") ? a.page : undefined
      };
    });
  }

  /** PR-J: per-doc source-PDF remediation. Fetches the PDF bytes, runs Adobe
      Auto-Tag + alt-inject via the shared helper, downloads the remediated
      PDF, and marks the doc remediated. Failures stay visible (toast) and do
      NOT mark the doc remediated. */
  /* ── Doc lifecycle state machine: Scan → Identify → Remediate → Certify ──
     Pure selector over existing doc fields + the veraPDF conformance verdict
     (sync-certify or worker-merged). Drives the per-doc pill + next action. */
  function docKey(d){ return (d&&(d.url||d.name))||""; }
  function docState(d){
    if(!d) return "imported";
    if(d.seal&&d.seal.done&&d.seal.signed) return "sealed";
    var c=d.conformance;
    if(c){
      if(c.pending) return "certifying";
      if(c.validated&&c.conformant) return "certified";
      if(c.validated) return "needsHuman";
    }
    if(d.conversionResult) return "converted";
    if(d.remediated) return "remediated";
    if(d.audited||(d.issues&&d.issues.length&&d.issues[0]&&d.issues[0].engine)) return "audited";
    return "imported";
  }
  function docStatePill(d){
    var s=docState(d);
    var SM={
      imported:{l:"Imported",bg:P.bgS,fg:P.slateM},
      audited:{l:"Audited",bg:P.priLt,fg:P.pri},
      remediated:{l:"Remediated",bg:"#FEF3C7",fg:"#92400E"},
      certifying:{l:"Certifying…",bg:P.priLt,fg:P.pri},
      needsHuman:{l:"Verified · needs human",bg:P.sMajBg,fg:P.amberDk},
      certified:{l:"Certified ✓",bg:P.emeraldLt,fg:P.emerald},
      sealed:{l:"Sealed 🔏",bg:"#DCFCE7",fg:"#166534"},
      converted:{l:"Converted → HTML",bg:P.emeraldLt,fg:P.emerald}
    };
    var m=SM[s]||SM.imported;
    return E("span",{style:{padding:"2px 8px",borderRadius:10,background:m.bg,color:m.fg,fontSize:10,fontWeight:800,whiteSpace:"nowrap"}},m.l);
  }
  /* getRemediatedB64 — the remediated bytes for a doc: from the sync-preview
     ref, OR (for worker/bulk docs) fetched from the result blob and cached. */
  async function getRemediatedB64(d, idx){
    var b64=remediatedBytes.current[docKey(d)];
    if(b64) return b64;
    if(d&&d.jobId!=null){
      if(typeof idx!=="number"||idx<0) idx=dc.indexOf(d);
      try{
        var r=await fetch("/api/pdf-remediate-batch-result?id="+encodeURIComponent(d.jobId)+"&docIndex="+idx,{credentials:"include"});
        var data=await r.json();
        if(r.ok&&data&&data.pdfBase64){ remediatedBytes.current[docKey(d)]=data.pdfBase64; return data.pdfBase64; }
      }catch(_){}
    }
    return null;
  }
  function downloadB64Pdf(b64, name){
    var raw=atob(b64);var arr=new Uint8Array(raw.length);for(var i=0;i<raw.length;i++)arr[i]=raw.charCodeAt(i);
    var blob=new Blob([arr],{type:"application/pdf"});var url=URL.createObjectURL(blob);
    var a=document.createElement("a");a.href=url;a.download=name;document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
  }
  async function downloadRemediated(d, idx){
    var b64=await getRemediatedB64(d, idx);
    if(!b64){ showToast("Nothing to download yet — remediate first.","error"); return; }
    downloadB64Pdf(b64,(d.name||"document").replace(/\.pdf$/i,"")+"-remediated.pdf");
  }
  async function certifyDoc(d, idx){
    if(typeof idx!=="number"||idx<0) idx=dc.indexOf(d);
    if(idx<0) return;
    var b64=await getRemediatedB64(d, idx);
    if(!b64){ showToast("Remediate the document first, then certify.","error"); return; }
    setDc(function(prev){return prev.map(function(x,i){return i===idx?Object.assign({},x,{conformance:{pending:true}}):x})});
    try{
      var r=await fetch("/api/pdf-certify",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({pdfBase64:b64})});
      var data=await r.json();
      var conf=(data&&data.conformance)||{validated:false,reason:"no_response"};
      if(conf.validated){ conf=Object.assign({},conf,{failedRules:(data&&data.failedRules)||[],autoFixable:!!(data&&data.autoFixable),plan:(data&&data.plan)||[]}); }
      setDc(function(prev){return prev.map(function(x,i){return i===idx?Object.assign({},x,{conformance:conf}):x})});
      if(conf.validated&&conf.conformant) showToast("✅ Certified PDF/UA-1 · veraPDF "+(conf.validatorVersion||""));
      else if(conf.validated) showToast("Verified — not yet conformant ("+(((conf.residual&&conf.residual.structural)||0)+((conf.residual&&conf.residual.human)||0))+" residual)");
      else showToast("Certification unavailable: "+(conf.reason||"error"),"error");
    }catch(e){
      setDc(function(prev){return prev.map(function(x,i){return i===idx?Object.assign({},x,{conformance:{validated:false,reason:errText(e)}}):x})});
      showToast("Certify failed: "+errText(e),"error");
    }
  }

  function downloadSealed(d){
    var b64=sealedBytes.current[docKey(d)];
    if(!b64){ showToast("Nothing sealed yet.","error"); return; }
    downloadB64Pdf(b64,(d.name||"document").replace(/\.pdf$/i,"")+"-certified.pdf");
  }
  async function sealDoc(d, idx){
    if(typeof idx!=="number"||idx<0) idx=dc.indexOf(d);
    if(idx<0) return;
    var b64=await getRemediatedB64(d, idx);
    if(!b64){ showToast("Certify the document first, then seal.","error"); return; }
    var c=d.conformance||{};
    setDc(function(prev){return prev.map(function(x,i){return i===idx?Object.assign({},x,{seal:{pending:true}}):x})});
    try{
      var r=await fetch("/api/pdf-seal",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({pdfBase64:b64,provenance:{validatorVersion:c.validatorVersion,profile:c.profile,verifiedAt:new Date().toISOString()}})});
      var data=await r.json();
      if(!r.ok||(data&&data.error&&!data.pdfBase64)){ throw new Error((data&&data.error)||("HTTP "+r.status)); }
      sealedBytes.current[docKey(d)]=data.pdfBase64;
      var sealInfo={done:true,signed:!!data.signed,provenanceStamped:!!data.provenanceStamped,signer:data.signer||null,at:new Date().toISOString()};
      setDc(function(prev){return prev.map(function(x,i){return i===idx?Object.assign({},x,{seal:sealInfo}):x})});
      showToast(data.signed?("🔏 Sealed & signed"+(data.signer&&data.signer.subject?" · "+data.signer.subject:"")):"🔏 Provenance stamped (signing not configured)");
    }catch(e){
      setDc(function(prev){return prev.map(function(x,i){return i===idx?Object.assign({},x,{seal:null}):x})});
      showToast("Seal failed: "+errText(e),"error");
    }
  }

  async function sourceRemediateDoc(doc, idx){
    if(!doc) return;
    if(typeof idx!=="number"||idx<0) idx=dc.indexOf(doc);
    if(idx<0) return;
    var scanId=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:null;
    if(!scanId){
      try{ scanId=new URLSearchParams(window.location.search).get("scan"); }catch(e){}
    }
    /* scanId is informational for the toast/log; the source-PDF pipeline
       does not require a scan session (Adobe endpoints don't know about it). */

    showToast("\u{1F527} Remediating PDF… "+(doc.name||""));

    var newLoading=Object.assign({},convLoading);
    newLoading[idx]=true;
    setConvLoading(newLoading);

    try{
      var buf=await fetchPdfBytes(doc.url||doc.name);
      var pdfBase64=arrayBufferToBase64(buf);
      var alts=buildWatchtowerAltsFromEnrich(idx);
      var result=await runAutotagPlusInject(pdfBase64, alts);

      /* Hold the remediated bytes client-side for explicit Download +
         Certify — no auto-download (the user pulls it when ready). */
      remediatedBytes.current[docKey(doc)]=result.remediatedPdfBase64;
      delete sealedBytes.current[docKey(doc)]; // re-remediated → any prior seal is stale

      /* Mark remediated in the doc list — same shape the legacy
         convertToHtml completion path used (issues all flagged
         remediated, remediatedCount = totalIssues). */
      setDc(function(prev){return prev.map(function(d,i){
        if(i!==idx) return d;
        var ni=(d.issues||[]).map(function(iss){return Object.assign({},iss,{remediated:true})});
        return Object.assign({},d,{
          issues:ni,
          remediatedCount:d.totalIssues,
          remediated:true,
          conformance:null,
          seal:null,
          sourceRemediation:{
            figuresFound:result.figuresFound,
            injected:result.injected,
            figureCoverage:result.figureCoverage,
            pageAware:result.pageAware,
            at:new Date().toISOString()
          }
        });
      })});

      showToast("✓ PDF remediated — ready to download / certify ("+(result.injected||0)+"/"+(result.figuresFound||0)+" figures)");
    }catch(e){
      /* Server credit-gate errors surface with the original server message
         so the user sees the upgrade hint, not a generic "failed". */
      var msg=(e&&e.serverMessage)||(e&&e.message)||"unknown error";
      if(e&&(e.status===402||e.status===403)){
        showToast("Remediation requires tagged-PDF credits — "+msg,"error");
      } else {
        showToast("Remediation failed: "+msg,"error");
      }
    } finally {
      var l=Object.assign({},convLoading);
      delete l[idx];
      setConvLoading(l);
    }
  }

  /** PR-J: batch wrapper — runs sourceRemediateDoc sequentially over every
      doc that still has issues (and isn't already source-remediated). Sequential
      (not parallel) so Adobe credits aren't burned in a thundering herd; the
      per-doc toast already gives progress feedback. */
  async function sourceRemediateAllDocs(){
    var queue=[];
    for(var di=0;di<dc.length;di++){
      if(dc[di]&&dc[di].totalIssues>0&&!dc[di].remediated) queue.push(di);
    }
    if(queue.length===0){
      showToast("All documents already remediated or have no issues.");
      return;
    }
    showToast("\u{1F527} Remediating PDF… "+queue.length+" document"+(queue.length===1?"":"s"));
    for(var qi=0; qi<queue.length; qi++){
      var idx=queue[qi];
      showToast("\u{1F527} Remediating PDF… ("+(qi+1)+"/"+queue.length+")");
      try{
        await sourceRemediateDoc(dc[idx], idx);
      }catch(_){ /* per-doc errors already toasted by sourceRemediateDoc */ }
    }
    showToast("✅ Batch remediation complete — "+queue.length+" doc"+(queue.length===1?"":"s")+" processed");
  }

  /** Mark a staging entry as deployed (client confirmed fix is live) */
  function deployStaged(idx){setStaging(function(prev){return prev.map(function(s,i){return i===idx?Object.assign({},s,{status:"deployed"}):s})})}
  /** Remove a staging entry (rollback/cancel) */
  function rollbackStaged(idx){setStaging(function(prev){return prev.filter(function(_,i){return i!==idx})})}
  /** Download fix script for a single page as .js file */
  function downloadFixScript(pg){
    if(!pg.fixScript){showToast("No fix script generated yet — click Build Fixes first","error");return;}
    var b=new Blob([pg.fixScript],{type:"application/javascript"});
    var a=document.createElement("a");
    a.href=URL.createObjectURL(b);
    a.download="paris-fix-"+(pg.url||"page").replace(/[^a-z0-9]/gi,"-").substring(0,40)+".js";
    a.click();
  }
  /** Generate CMS-native remediation scripts from patches data.
      Transforms before/after HTML diffs into executable code for specific platforms. */
  function downloadCMSScript(platform){
    var pagesWithPatches=wp.filter(function(p){return p.patches&&p.patches.length>0});
    if(pagesWithPatches.length===0){showToast("No patches to export — run Build Fixes first","error");return}
    var allPatches=[];
    pagesWithPatches.forEach(function(p){
      (p.patches||[]).forEach(function(patch){
        allPatches.push(Object.assign({},patch,{pageUrl:p.url||"",pageTitle:p.n||p.title||""}));
      });
    });
    var out="",ext="",mime="text/plain";
    var d=domain||"site",date=new Date().toISOString().split("T")[0];

    if(platform==="wordpress"){
      ext="php";mime="application/x-php";
      out="<?php\n/**\n * PiARIS Accessibility Remediation — WordPress\n * Domain: "+d+"\n * Generated: "+date+"\n * Patches: "+allPatches.length+"\n *\n * INSTALLATION:\n *   Option A: Upload to wp-content/mu-plugins/piaris-remediate.php (auto-loads)\n *   Option B: Add to functions.php: require_once 'piaris-remediate.php';\n *   Option C: Run once via WP-CLI: wp eval-file piaris-remediate.php\n */\n\n";
      out+="// ── Runtime DOM fixes (same as JS bundle, applied server-side via wp_footer) ──\n";
      out+="add_action('wp_footer', function() {\n  echo '<script>';\n";
      out+="  echo '(function(){\"use strict\";';\n";
      pagesWithPatches.forEach(function(p){
        if(!p.fixScript)return;
        var path="/";try{path=new URL(p.url).pathname}catch(e){}
        out+="  // Page: "+path+"\n";
        out+="  echo 'if(window.location.pathname===\""+path.replace(/'/g,"\\'")+"\"){';\n";
        out+="  echo '"+p.fixScript.replace(/'/g,"\\'").replace(/\n/g,"\\n")+"';\n";
        out+="  echo '}';\n";
      });
      out+="  echo '})();';\n  echo '</script>';\n});\n\n";
      out+="// ── Source-level content filters (modifies stored HTML) ──\n";
      out+="add_filter('the_content', function($content) {\n";
      allPatches.forEach(function(p,i){
        if(!p.before||!p.after||p.before===p.after)return;
        var before=p.before.replace(/'/g,"\\'").replace(/\n/g,"\\n");
        var after=p.after.replace(/'/g,"\\'").replace(/\n/g,"\\n");
        out+="  // Patch "+(i+1)+": WCAG "+p.wcag+" — "+(p.issueName||p.elementType||"fix")+"\n";
        out+="  $content = str_replace(\n    '"+before+"',\n    '"+after+"',\n    $content\n  );\n";
      });
      out+="  return $content;\n});\n";
    }
    else if(platform==="python"){
      ext="py";mime="text/x-python";
      out="#!/usr/bin/env python3\n\"\"\"\nPiARIS Accessibility Remediation — Python\nDomain: "+d+"\nGenerated: "+date+"\nPatches: "+allPatches.length+"\n\nUsage:\n  python3 piaris-remediate.py /path/to/html/files/\n  python3 piaris-remediate.py index.html about.html\n\nRequirements:\n  pip install beautifulsoup4\n\"\"\"\n\nimport os, sys, re, glob\nfrom pathlib import Path\n\ntry:\n    from bs4 import BeautifulSoup\n    HAS_BS4 = True\nexcept ImportError:\n    HAS_BS4 = False\n    print('Warning: beautifulsoup4 not installed. Using regex fallback.')\n    print('Install with: pip install beautifulsoup4')\n\n";
      out+="# ── Patches (before → after) ──\nPATCHES = [\n";
      allPatches.forEach(function(p,i){
        if(!p.before||!p.after||p.before===p.after)return;
        out+="    {\n";
        out+="        'wcag': "+JSON.stringify(p.wcag||"")+",\n";
        out+="        'severity': "+JSON.stringify(p.severity||"minor")+",\n";
        out+="        'element_type': "+JSON.stringify(p.elementType||"")+",\n";
        out+="        'issue': "+JSON.stringify(p.issueName||p.description||"")+",\n";
        out+="        'selector': "+JSON.stringify(p.selector||"")+",\n";
        out+="        'before': "+JSON.stringify(p.before)+",\n";
        out+="        'after': "+JSON.stringify(p.after)+",\n";
        out+="    },\n";
      });
      out+="]\n\n";
      out+="def apply_patches(filepath):\n    \"\"\"Apply all patches to an HTML file.\"\"\"\n    with open(filepath, 'r', encoding='utf-8') as f:\n        content = f.read()\n    original = content\n    applied = 0\n    for patch in PATCHES:\n        if patch['before'] in content:\n            content = content.replace(patch['before'], patch['after'], 1)\n            applied += 1\n            print(f\"  [WCAG {patch['wcag']}] {patch['issue'][:60]}\")\n    if applied > 0:\n        with open(filepath, 'w', encoding='utf-8') as f:\n            f.write(content)\n        print(f\"  ✓ {applied}/{len(PATCHES)} patches applied to {filepath}\")\n    else:\n        print(f\"  — No matching patches in {filepath}\")\n    return applied\n\n";
      out+="def main():\n    if len(sys.argv) < 2:\n        print('Usage: python3 piaris-remediate.py <path-to-html-files>')\n        print('       python3 piaris-remediate.py /var/www/html/')\n        sys.exit(1)\n    paths = sys.argv[1:]\n    total_applied = 0\n    total_files = 0\n    for p in paths:\n        if os.path.isdir(p):\n            files = glob.glob(os.path.join(p, '**', '*.html'), recursive=True)\n            files += glob.glob(os.path.join(p, '**', '*.htm'), recursive=True)\n            files += glob.glob(os.path.join(p, '**', '*.php'), recursive=True)\n        else:\n            files = [p]\n        for f in files:\n            if os.path.isfile(f):\n                print(f'\\nProcessing: {f}')\n                total_applied += apply_patches(f)\n                total_files += 1\n    print(f'\\n{'='*50}')\n    print(f'Done. {total_applied} patches applied across {total_files} files.')\n    print(f'Run PiARIS Verify Fixes to confirm remediation.')\n\nif __name__ == '__main__':\n    main()\n";
    }
    else if(platform==="drupal"){
      ext="module";mime="text/plain";
      out="<?php\n/**\n * @file\n * PiARIS Accessibility Remediation — Drupal Module\n * Domain: "+d+"\n * Generated: "+date+"\n * Patches: "+allPatches.length+"\n *\n * INSTALLATION:\n *   1. Create folder: modules/custom/piaris_remediate/\n *   2. Save this as piaris_remediate.module\n *   3. Create piaris_remediate.info.yml:\n *      name: PiARIS Remediate\n *      type: module\n *      core_version_requirement: ^9 || ^10\n *   4. Enable: drush en piaris_remediate\n *   5. Clear cache: drush cr\n */\n\n";
      out+="use Drupal\\Core\\Render\\Markup;\n\n";
      out+="/**\n * Implements hook_page_attachments().\n * Adds fix script to all pages.\n */\nfunction piaris_remediate_page_attachments(array &$attachments) {\n  $attachments['#attached']['html_head'][] = [\n    [\n      '#type' => 'html_tag',\n      '#tag' => 'script',\n      '#value' => Markup::create(piaris_get_fix_script()),\n      '#attributes' => ['defer' => TRUE],\n    ],\n    'piaris_fixes',\n  ];\n}\n\n";
      out+="function piaris_get_fix_script() {\n  return <<<'JS'\n";
      var bundle="(function(){'use strict';var u=window.location.pathname;\n";
      pagesWithPatches.forEach(function(p){
        if(!p.fixScript)return;
        var path="/";try{path=new URL(p.url).pathname}catch(e){}
        bundle+="if(u==='"+path+"'){"+p.fixScript+"}\n";
      });
      bundle+="})();";
      out+=bundle+"\nJS;\n}\n";
    }
    else{
      /* Granicus / generic: CSV format for content teams */
      ext="csv";mime="text/csv";
      out="\"Page URL\",\"WCAG\",\"Severity\",\"Element Type\",\"Issue\",\"Current HTML (before)\",\"Fixed HTML (after)\",\"CSS Selector\",\"Fix Type\"\n";
      allPatches.forEach(function(p){
        var esc=function(s){return '\"'+(s||"").replace(/"/g,'""').replace(/\n/g," ")+'\"'};
        out+=esc(p.pageUrl)+","+esc(p.wcag)+","+esc(p.severity)+","+esc(p.elementType)+","+esc(p.issueName||"")+","+esc(p.before)+","+esc(p.after)+","+esc(p.selector)+","+esc(p.fixType)+"\n";
      });
    }

    var b=new Blob([out],{type:mime});
    var a=document.createElement("a");a.href=URL.createObjectURL(b);
    a.download="piaris-remediate-"+(d||"site").replace(/[^a-z0-9]/gi,"-")+"."+ext;a.click();
    showToast("\u{2B07} "+platform.charAt(0).toUpperCase()+platform.slice(1)+" remediation script downloaded ("+allPatches.length+" patches)");
  }
  /* Download all patches as a remediation workbook */
  function downloadPatches(pg){
    if(!pg.patches||pg.patches.length===0){showToast("No patches available","error");return;}
    var lines=["# PiARIS Remediation Workbook","# Page: "+pg.url,"# Generated: "+new Date().toISOString(),"# Patches: "+pg.patches.length,""];
    pg.patches.forEach(function(p,i){
      lines.push("## Patch "+(i+1)+": WCAG "+p.wcag+" ("+p.severity+")");
      lines.push(p.description);
      lines.push("");
      lines.push("### Before:");
      lines.push("```html");
      lines.push(p.before);
      lines.push("```");
      lines.push("");
      lines.push("### After:");
      lines.push("```html");
      lines.push(p.after);
      lines.push("```");
      lines.push("");
      lines.push("Selector: `"+p.selector+"`");
      lines.push("Fix type: "+p.fixType);
      lines.push("");
      lines.push("---");
      lines.push("");
    });
    var b=new Blob([lines.join("\n")],{type:"text/markdown"});
    var a=document.createElement("a");
    a.href=URL.createObjectURL(b);
    a.download="paris-patches-"+(pg.url||"page").replace(/[^a-z0-9]/gi,"-").substring(0,40)+".md";
    a.click();
  }
  /* Copy deploy tag to clipboard */
  function copyDeployTag(pg){
    if(!pg.fixSummary||!pg.fixSummary.deployTag){showToast("No deploy tag available","error");return;}
    navigator.clipboard.writeText(pg.fixSummary.deployTag).then(function(){
      showToast("\u{1F4CB} Deploy script tag copied to clipboard!");
    }).catch(function(){showToast("Copy failed — check browser permissions","error")});
  }

  function exportReport(){
    var cmsLinks=brokenLinks.filter(function(b){return b.type==="cms-template"});
    var stdLinks=brokenLinks.filter(function(b){return b.type==="standard"});
    var out={platform:"PiARIS",vendor:"Pi Consultants",website:"pi-consulting.us",exported:new Date().toISOString(),domain:domain,overallScore:overallScore,
      webPages:wp.map(function(p){return{url:p.url,issues:p.issues.map(function(i){return{wcag:i.wcag,name:i.info.n,severity:i.sev,instances:i.count,remediation:i.info.r}})}}),
      documents:dc.map(function(d){return{name:d.name,pages:d.pages,tagged:d.tagged,issues:d.issues.map(function(i){return{wcag:i.wcag,name:i.info.n,severity:i.sev,instances:i.count,remediation:i.info.r}})}}),
      brokenLinks:{
        summary:{total:brokenLinks.length,cmsTemplate:cmsLinks.length,standard:stdLinks.length},
        cmsTemplate:cmsLinks.map(function(b){return{url:b.url,sourcePage:b.sourcePage}}),
        standard:stdLinks.map(function(b){return{url:b.url,sourcePage:b.sourcePage,status:b.status||"",error:b.error||""}})},
      authRequired:scanMeta&&scanMeta.authRequired?scanMeta.authRequired:{count:0,samples:[]},
      crossOriginRedirects:scanMeta&&scanMeta.crossOriginRedirects?scanMeta.crossOriginRedirects:{count:0,samples:[]}};
    var b=new Blob([JSON.stringify(out,null,2)],{type:"application/json"});
    var a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="paris-"+Date.now()+".json";a.click();
  }

  /* ── PROFESSIONAL REMEDIATION REPORT (XLSX) — real .xlsx + AI enrichment merge ── */
  function exportProfessionalReport(){
    /* License enforcement — free tier gets basic summary only, Starter+ gets full XLSX */
    if(!licenseGate("report"))return;
    showToast("Generating professional report...");
    var reportPages=wp.map(function(p,pIdx){
      var enrichData=aiEnrich[pIdx];
      var pageIssues=p.issues.map(function(iss,issIdx){
        var base={wcag:iss.wcag||"",sev:iss.sev||"minor",desc:iss.desc||(iss.info?iss.info.n:""),el:iss.el||"",fix:iss.fix||(iss.info?iss.info.r:""),count:iss.count||1,selector:iss.selector||"",before:iss.before||iss.el||"",after:iss.after||""};
        /* Merge AI enrichment if available for this issue */
        if(enrichData&&enrichData.aiSuggestions){
          var match=null;
          for(var si=0;si<enrichData.aiSuggestions.length;si++){
            var sug=enrichData.aiSuggestions[si];
            if(sug.wcag===iss.wcag&&sug.selector===iss.selector){match=sug;break}
            if(sug.wcag===iss.wcag&&!match){match=sug}
          }
          if(match){
            if(match.before)base.before=match.before;
            if(match.after)base.after=match.after;
            if(match.suggestion)base.aiNarrative=match.suggestion;
            if(match.explanation)base.aiExplanation=match.explanation;
          }
        }
        return base;
      });
      /* Merge AI alt text suggestions as additional context */
      var aiAltTexts=[];
      if(enrichData&&enrichData.altTextSuggestions){
        aiAltTexts=enrichData.altTextSuggestions.map(function(a){return{src:a.src||"",suggestedAlt:a.suggestedAlt||"",isDecorative:a.isDecorative||false}});
      }
      var aiHeadings=null;
      if(enrichData&&enrichData.headingTree&&enrichData.headingTree.suggested){
        aiHeadings=enrichData.headingTree.suggested.filter(function(h){return h.changed});
      }
      return{url:p.url||"",title:p.n||p.url||"",issues:pageIssues,aiAltTexts:aiAltTexts,aiHeadings:aiHeadings}
    });
    var reportDocs=dc.map(function(d,dIdx){
      var enrichData=aiEnrich["d"+dIdx];
      var docIssues=d.issues.map(function(iss){
        var base={wcag:iss.wcag||"",sev:iss.sev||"minor",desc:iss.desc||(iss.info?iss.info.n:""),el:iss.el||"",fix:iss.fix||(iss.info?iss.info.r:""),count:iss.count||1};
        if(enrichData&&enrichData.aiSuggestions){
          for(var si=0;si<enrichData.aiSuggestions.length;si++){
            var sug=enrichData.aiSuggestions[si];
            if(sug.wcag===iss.wcag){
              if(sug.before)base.before=sug.before;
              if(sug.after)base.after=sug.after;
              if(sug.suggestion)base.aiNarrative=sug.suggestion;
              break;
            }
          }
        }
        return base;
      });
      return{name:d.name||"",url:d.url||d.name||"",issues:docIssues}
    });
    var enrichedCount=0;for(var ek in aiEnrich){if(aiEnrich.hasOwnProperty(ek))enrichedCount++}
    var payload={scanId:"scan-"+Date.now(),siteName:domain||"Website",siteUrl:domain?("https://"+domain):"",pages:reportPages,documents:reportDocs,testedWith:"PiARIS Watchtower + axe-core + HTML_CodeSniffer",testedBy:authUser?authUser.name||authUser.email||"PiARIS":"PiARIS Automated Scanner",standard:"WCAG 2.1 AA",scanDate:new Date().toISOString().split("T")[0],aiEnriched:enrichedCount>0};
    if(enrichedCount>0)showToast("\u{1F9E0} Including AI enrichment from "+enrichedCount+" pages...");
    fetch("/api/remediate/report",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)}).then(function(res){if(!res.ok)throw new Error("Report generation failed: "+res.status);var ct=res.headers.get("content-type")||"";if(ct.indexOf("spreadsheetml")>-1||ct.indexOf("octet-stream")>-1){return res.blob().then(function(blob){var fn=(domain||"PiARIS").replace(/[^a-zA-Z0-9]/g,"_")+"_Remediation_Report_"+new Date().toISOString().split("T")[0]+".xlsx";var a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=fn;a.click();showToast("Professional report downloaded!"+(enrichedCount>0?" (AI-enriched)":""))})}return res.json().then(function(reportData){generateXlsxFromReport(reportData)})}).catch(function(err){showToast("Report error: "+err.message)});
  }
  /* Fallback: client-side SpreadsheetML if server returns JSON (legacy compat) */
  function generateXlsxFromReport(data){
    var meta=data.meta||{};var ov=data.overview||{};var findings=data.detailedFindings||[];var colors=data.colorSuggestions||[];var urls=data.urlDetails||[];var wcagRef=data.wcagReference||[];
    var x='<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
    x+='<Styles><Style ss:ID="hd"><Font ss:Bold="1" ss:Size="11" ss:Color="#FFFFFF"/><Interior ss:Color="#1E293B" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:WrapText="1"/></Style>';
    x+='<Style ss:ID="ti"><Font ss:Bold="1" ss:Size="14" ss:Color="#1E293B"/></Style>';
    x+='<Style ss:ID="st"><Font ss:Bold="1" ss:Size="11" ss:Color="#475569"/></Style>';
    x+='<Style ss:ID="cr"><Font ss:Bold="1" ss:Color="#DC2626"/><Interior ss:Color="#FEE2E2" ss:Pattern="Solid"/></Style>';
    x+='<Style ss:ID="mj"><Font ss:Bold="1" ss:Color="#D97706"/><Interior ss:Color="#FEF3C7" ss:Pattern="Solid"/></Style>';
    x+='<Style ss:ID="mn"><Font ss:Color="#2563EB"/><Interior ss:Color="#DBEAFE" ss:Pattern="Solid"/></Style>';
    x+='<Style ss:ID="ps"><Font ss:Bold="1" ss:Color="#059669"/></Style>';
    x+='<Style ss:ID="fl"><Font ss:Bold="1" ss:Color="#DC2626"/></Style>';
    x+='<Style ss:ID="wr"><Alignment ss:WrapText="1" ss:Vertical="Top"/></Style>';
    x+='<Style ss:ID="cd"><Font ss:FontName="Consolas" ss:Size="9"/><Alignment ss:WrapText="1" ss:Vertical="Top"/><Interior ss:Color="#F8FAFC" ss:Pattern="Solid"/></Style>';
    x+='</Styles>\n';
    function sc(v,sid){var e=String(v||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");var t=/^\d+$/.test(String(v))&&String(v).length<10?"Number":"String";return"<Cell"+(sid?' ss:StyleID="'+sid+'"':"")+">"+"<Data ss:Type=\""+t+"\">"+e+"</Data></Cell>"}
    function sr(cells){return"<Row>"+cells.join("")+"</Row>\n"}
    x+='<Worksheet ss:Name="Accessibility Overview"><Table>\n';
    x+=sr([sc("PiARIS Accessibility Remediation Report","ti")]);x+=sr([sc(meta.siteName||"")]);x+=sr([sc("")]);
    x+=sr([sc("Report Date:"),sc(meta.scanDate||"")]);x+=sr([sc("Standard:"),sc(meta.standard||"WCAG 2.1 AA")]);
    x+=sr([sc("Tested With:"),sc(meta.testedWith||"")]);x+=sr([sc("Tested By:"),sc(meta.testedBy||"")]);
    x+=sr([sc("Site URL:"),sc(meta.siteUrl||"")]);x+=sr([sc("")]);
    x+=sr([sc("WCAG Conformance Summary","st")]);
    x+=sr([sc("Level","hd"),sc("Total","hd"),sc("Passed","hd"),sc("Failed","hd"),sc("Status","hd")]);
    x+=sr([sc("Level A"),sc(String(ov.levelA?ov.levelA.total:0)),sc(String(ov.levelA?ov.levelA.passed:0)),sc(String(ov.levelA?ov.levelA.failed:0)),sc(ov.levelA&&ov.levelA.failed===0?"PASS":"FAIL",ov.levelA&&ov.levelA.failed===0?"ps":"fl")]);
    x+=sr([sc("Level AA"),sc(String(ov.levelAA?ov.levelAA.total:0)),sc(String(ov.levelAA?ov.levelAA.passed:0)),sc(String(ov.levelAA?ov.levelAA.failed:0)),sc(ov.levelAA&&ov.levelAA.failed===0?"PASS":"FAIL",ov.levelAA&&ov.levelAA.failed===0?"ps":"fl")]);
    x+=sr([sc("")]);x+=sr([sc("Severity Breakdown","st")]);
    x+=sr([sc("Severity","hd"),sc("Count","hd"),sc("Pct","hd")]);
    var tot=ov.totalIssues||1;
    x+=sr([sc("Critical","cr"),sc(String(ov.critical||0)),sc(Math.round((ov.critical||0)/tot*100)+"%")]);
    x+=sr([sc("Major","mj"),sc(String(ov.major||0)),sc(Math.round((ov.major||0)/tot*100)+"%")]);
    x+=sr([sc("Minor","mn"),sc(String(ov.minor||0)),sc(Math.round((ov.minor||0)/tot*100)+"%")]);
    x+=sr([sc("")]);x+=sr([sc("Total Issues:"),sc(String(ov.totalIssues||0))]);x+=sr([sc("Pages Scanned:"),sc(String(ov.pagesScanned||0))]);
    x+='</Table></Worksheet>\n';
    x+='<Worksheet ss:Name="URL Details"><Table>\n';
    x+=sr([sc("#","hd"),sc("URL","hd"),sc("Title","hd"),sc("Type","hd"),sc("Environment","hd"),sc("Developer","hd"),sc("Status","hd")]);
    for(var ui=0;ui<urls.length;ui++){var u=urls[ui];x+=sr([sc(String(ui+1)),sc(u.url||""),sc(u.title||""),sc(u.type||""),sc("Production"),sc(""),sc("To be started")])}
    x+='</Table></Worksheet>\n';
    x+='<Worksheet ss:Name="Detailed Findings"><Table>\n';
    x+=sr([sc("#","hd"),sc("Page","hd"),sc("Element Type","hd"),sc("Issue","hd"),sc("Actual Result","hd"),sc("Instances","hd"),sc("Count","hd"),sc("Expected","hd"),sc("Recommendation","hd"),sc("Existing Code","hd"),sc("Suggested Code","hd"),sc("Selector","hd"),sc("Severity","hd"),sc("WCAG","hd"),sc("Name","hd"),sc("Level","hd"),sc("508","hd"),sc("Dev","hd"),sc("Date","hd"),sc("Status","hd"),sc("Comments","hd")]);
    for(var fi=0;fi<findings.length;fi++){var f=findings[fi];var sv=f.severity==="critical"?"cr":f.severity==="major"?"mj":"mn";x+=sr([sc(String(fi+1)),sc(f.page||"","wr"),sc(f.elementType||""),sc(f.issueName||"","wr"),sc(f.actualResult||"","wr"),sc(f.instances||"","wr"),sc(String(f.instanceCount||1)),sc(f.expectedResult||"","wr"),sc(f.recommendation||"","wr"),sc(f.existingCode||"","cd"),sc(f.suggestedCode||"","cd"),sc(f.selector||"","cd"),sc(f.severity||"",sv),sc(f.wcag||""),sc(f.wcagName||""),sc(f.wcagLevel||""),sc(f.section508||""),sc(""),sc(""),sc("To be started"),sc("")])}
    x+='</Table></Worksheet>\n';
    x+='<Worksheet ss:Name="Color Suggestions"><Table>\n';
    x+=sr([sc("#","hd"),sc("Page","hd"),sc("Element","hd"),sc("Selector","hd"),sc("FG","hd"),sc("BG","hd"),sc("Ratio","hd"),sc("Target","hd"),sc("Suggested FG","hd"),sc("New Ratio","hd"),sc("WCAG","hd")]);
    for(var ci=0;ci<colors.length;ci++){var c=colors[ci];x+=sr([sc(String(ci+1)),sc(c.page||""),sc(c.element||"","wr"),sc(c.selector||"","cd"),sc(c.foreground||""),sc(c.background||""),sc(String(c.currentRatio||"")),sc(c.targetRatio||"4.5:1"),sc(c.suggestedForeground||""),sc(String(c.newRatio||"")),sc(c.wcag||"")])}
    if(colors.length===0){x+=sr([sc("No contrast issues found","ps")])}
    x+='</Table></Worksheet>\n';
    x+='<Worksheet ss:Name="WCAG 2.1 Reference"><Table>\n';
    x+=sr([sc("Criterion","hd"),sc("Name","hd"),sc("Level","hd"),sc("508","hd"),sc("Description","hd"),sc("Status","hd")]);
    for(var wi=0;wi<wcagRef.length;wi++){var w=wcagRef[wi];x+=sr([sc(w.criterion||""),sc(w.name||""),sc(w.level||""),sc(w.section508||""),sc(w.description||"","wr"),sc(w.status||"",w.status==="PASS"?"ps":"fl")])}
    x+='</Table></Worksheet></Workbook>';
    var blob=new Blob([x],{type:"application/vnd.ms-excel"});var a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=(meta.siteName||"PiARIS").replace(/[^a-zA-Z0-9]/g,"_")+"_Report_"+(meta.scanDate||"report")+".xls";a.click();showToast("Professional report downloaded!");
  }

  /* ── IFRAME HIGHLIGHT BRIDGE ── */
  var proxyFrameRef=React.useRef(null);
  var remFrameRef=React.useRef(null);
  /* Doc Audit pane refs for scroll sync (Session 47) */
  var docLeftPaneRef=React.useRef(null);
  var docRightPaneRef=React.useRef(null);
  var docScrollSyncing=React.useRef(false);
  /* Synced scroll: when one iframe scrolls, the other follows (if locked) */
  var scrollSyncActive=React.useRef(false);
  var scrollSourcePane=React.useRef(null); /* "left" or "right" — which pane the mouse is over */
  function setupScrollSync(frame,otherFrameRef,paneId){
    if(!frame||!frame.contentWindow)return;
    try{
      frame.contentWindow.addEventListener("scroll",function(){
        if(!panesLockedRef.current)return;
        if(scrollSourcePane.current!==paneId)return;
        if(scrollSyncActive.current)return;
        scrollSyncActive.current=true;
        try{
          var other=otherFrameRef.current;
          if(other&&other.contentWindow){
            /* Absolute pixel sync — both iframes show the same page content */
            other.contentWindow.scrollTo(frame.contentWindow.scrollX, frame.contentWindow.scrollY);
          }
        }catch(e){}
        setTimeout(function(){scrollSyncActive.current=false},100);
      });
    }catch(e){}
  }
  /** Doc Audit scroll sync — syncs left/right pane scroll positions.
      Uses docScrollSyncing ref to prevent infinite loop. */
  function docPaneScroll(sourceRef, targetRef){
    if(!panesLockedRef.current||docScrollSyncing.current)return;
    var src=sourceRef.current;var tgt=targetRef.current;
    if(!src||!tgt)return;
    docScrollSyncing.current=true;
    tgt.scrollTop=src.scrollTop;
    setTimeout(function(){docScrollSyncing.current=false},100);
  }
  /* ── Visual Pinpointing — shared highlight bridge for split-pane issue markers ── */

  /** Build a blob URL from captured DOM HTML with PARIS highlight CSS + listener injected.
      Used by the iframe to render the exact page the extension saw, instead of re-fetching.
      @param {string} domHtml - serialized HTML from extension DOM capture (pg.dom)
      @param {string} pageUrl - original page URL for base tag
      @returns {string} blob URL suitable for iframe src */
  var domBlobCache=React.useRef({});
  function buildDomBlobUrl(domHtml, pageUrl){
    /* Return cached blob URL if already built for this content */
    var cacheKey=pageUrl||"";
    if(domBlobCache.current[cacheKey])return domBlobCache.current[cacheKey];

    var html=domHtml;
    /* Inject <base> tag so relative URLs (images, CSS) resolve correctly */
    var baseTag='<base href="'+(pageUrl||"").replace(/"/g,"&quot;")+'" target="_blank">';
    if(/<head[^>]*>/i.test(html)){html=html.replace(/(<head[^>]*>)/i,"$1\n"+baseTag)}
    else if(/<html[^>]*>/i.test(html)){html=html.replace(/(<html[^>]*>)/i,'$1<head>'+baseTag+'</head>')}
    else{html=baseTag+"\n"+html}

    /* PARIS highlight CSS — same as fetch-page proxy injects */
    var parisCss='<style data-paris-proxy="true">'+
      '[data-paris-hl]{outline:3px solid #DC2626 !important;outline-offset:2px !important;background:rgba(220,38,38,0.08) !important;scroll-margin:60px !important;transition:outline 0.2s,background 0.2s}'+
      '[data-paris-hl="major"]{outline-color:#D97706 !important;background:rgba(217,119,6,0.08) !important}'+
      '[data-paris-hl="minor"]{outline-color:#3B82F6 !important;background:rgba(59,130,246,0.08) !important}'+
      '[data-paris-hl="fixed"]{outline-color:#059669 !important;background:rgba(5,150,105,0.08) !important}'+
      'body.paris-focus-mode>*:not([data-paris-hl]){opacity:0.4 !important;transition:opacity 0.2s}'+
      'body.paris-focus-mode [data-paris-hl]{opacity:1 !important}'+
      '@keyframes paris-pulse{0%{outline-width:3px}50%{outline-width:5px}100%{outline-width:3px}}'+
      '@keyframes pulse-fade{0%{opacity:1}50%{opacity:0.55}100%{opacity:1}}'+
      '@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}'+
      '[data-paris-active]{animation:paris-pulse 1.5s ease-in-out infinite !important;z-index:99999 !important;position:relative !important}'+
      '[data-paris-hl]::after{content:attr(data-paris-label);position:absolute;top:-28px;left:0;background:#1E293B;color:#fff;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700;white-space:nowrap;pointer-events:none;opacity:0;transition:opacity 0.2s;z-index:100000}'+
      '[data-paris-active]::after{opacity:1 !important}'+
      '</style>';

    /* PARIS postMessage listener — handles highlight/clear/scrollTo/highlightAll */
    var parisScript='<script data-paris-proxy="true">'+
      '(function(){'+
      'var prevActive=null;'+
      'window.addEventListener("message",function(e){'+
      'var d=e.data;if(!d||!d.action)return;'+
      'if(d.action==="highlight"){'+
      'if(prevActive){prevActive.removeAttribute("data-paris-active")}'+
      'try{var el=null;'+
      'if(d.selector){el=document.querySelector(d.selector);'+
      'if(!el&&d.selector.indexOf("img")>-1){var imgs=document.querySelectorAll("img");for(var i=0;i<imgs.length;i++){if(!imgs[i].getAttribute("alt")||imgs[i].getAttribute("alt")===""){el=imgs[i];break}}}}'+
      'if(!el&&d.element){var tags=d.element.match(/<(\\\\w+)/);if(tags){var cands=document.querySelectorAll(tags[1]);for(var j=0;j<cands.length;j++){if(cands[j].outerHTML.indexOf(d.element.substring(1,40))>-1){el=cands[j];break}}}}'+
      'if(el){el.setAttribute("data-paris-hl",d.severity||"critical");el.setAttribute("data-paris-active","true");if(d.label){el.setAttribute("data-paris-label",d.label)}el.scrollIntoView({behavior:"smooth",block:"center"});document.body.classList.add("paris-focus-mode");prevActive=el;'+
      'window.parent.postMessage({action:"highlightResult",found:true,selector:d.selector},"*")}'+
      'else{window.parent.postMessage({action:"highlightResult",found:false,selector:d.selector},"*")}'+
      '}catch(err){window.parent.postMessage({action:"highlightResult",found:false,error:err.message},"*")}}'+
      'else if(d.action==="clearHighlights"){var hls=document.querySelectorAll("[data-paris-hl]");for(var k=0;k<hls.length;k++){hls[k].removeAttribute("data-paris-hl");hls[k].removeAttribute("data-paris-active");hls[k].removeAttribute("data-paris-label")}document.body.classList.remove("paris-focus-mode");prevActive=null}'+
      'else if(d.action==="scrollTo"){try{var target=document.querySelector(d.selector);if(target){target.scrollIntoView({behavior:"smooth",block:"center"})}}catch(err){}}'+
      'else if(d.action==="highlightAll"){var items=d.items||[];for(var m=0;m<items.length;m++){try{var found=document.querySelector(items[m].selector);if(found){found.setAttribute("data-paris-hl",items[m].severity||"major");if(items[m].label){found.setAttribute("data-paris-label",items[m].label)}}}catch(err){}}}'+
      '});'+
      'window.parent.postMessage({action:"parisProxyReady"},"*");'+
      '})();<\/script>';

    /* Inject CSS before </head> */
    if(/<\/head>/i.test(html)){html=html.replace(/<\/head>/i,parisCss+"\n</head>")}
    else{html=parisCss+"\n"+html}
    /* Inject script before </body> */
    if(/<\/body>/i.test(html)){html=html.replace(/<\/body>/i,parisScript+"\n</body>")}
    else{html=html+"\n"+parisScript}

    var blob=new Blob([html],{type:"text/html;charset=utf-8"});
    var url=URL.createObjectURL(blob);
    /* Cache and track for cleanup */
    domBlobCache.current[cacheKey]=url;
    return url;
  }

  /* Highlights issue element(s) in both baseline (proxyFrameRef) and deliverable (remFrameRef) panes.
     If the issue has a `nodes` array (all violating elements), highlights ALL of them and scrolls to the first.
     Falls back to single-element highlight if nodes not available. */
  function highlightInProxy(issue){
    /* First clear previous highlights */
    clearProxyHighlights();
    var sev=issue.sev||"major";
    var label="WCAG "+(issue.wcag||"");
    /* If nodes array exists with multiple selectors, highlight ALL instances */
    if(issue.nodes&&issue.nodes.length>1){
      var items=issue.nodes.filter(function(n){return n.selector}).map(function(n){return{selector:n.selector,severity:sev,label:label}});
      if(items.length>0){
        var allMsg={action:"highlightAll",items:items};
        if(proxyFrameRef.current){try{proxyFrameRef.current.contentWindow.postMessage(allMsg,"*")}catch(e){}}
        if(remFrameRef.current){try{remFrameRef.current.contentWindow.postMessage(allMsg,"*")}catch(e){}}
        /* Scroll to the first highlighted element */
        var scrollMsg={action:"scrollTo",selector:items[0].selector};
        if(proxyFrameRef.current){try{proxyFrameRef.current.contentWindow.postMessage(scrollMsg,"*")}catch(e){}}
        return;
      }
    }
    /* Fallback: single element highlight (legacy data without nodes array) */
    var msg={action:"highlight",selector:issue.selector||"",element:issue.el||"",severity:sev,label:label};
    if(proxyFrameRef.current){try{proxyFrameRef.current.contentWindow.postMessage(msg,"*")}catch(e){}}
    if(remFrameRef.current){try{remFrameRef.current.contentWindow.postMessage(Object.assign({},msg,{severity:issue.remediated?"fixed":msg.severity}),"*")}catch(e){}}
  }
  function clearProxyHighlights(){
    var msg={action:"clearHighlights"};
    if(proxyFrameRef.current){try{proxyFrameRef.current.contentWindow.postMessage(msg,"*")}catch(e){}}
    if(remFrameRef.current){try{remFrameRef.current.contentWindow.postMessage(msg,"*")}catch(e){}}
  }
  function highlightAllInProxy(issues){
    var items=issues.filter(function(x){return!x.remediated&&x.selector}).map(function(x){return{selector:x.selector,severity:x.sev||"major",label:"WCAG "+(x.wcag||"")}});
    var msg={action:"highlightAll",items:items};
    if(proxyFrameRef.current){try{proxyFrameRef.current.contentWindow.postMessage(msg,"*")}catch(e){}}
    /* Deliverable pane: show fixed items in green, unfixed in original color */
    var remItems=issues.filter(function(x){return x.selector}).map(function(x){return{selector:x.selector,severity:x.remediated?"fixed":x.sev||"major",label:"WCAG "+(x.wcag||"")}});
    if(remFrameRef.current){try{remFrameRef.current.contentWindow.postMessage({action:"highlightAll",items:remItems},"*")}catch(e){}}
  }
  /* Scroll to element in both panes */
  function scrollToInProxy(issue){
    var msg={action:"scrollTo",selector:issue.selector||""};
    if(proxyFrameRef.current){try{proxyFrameRef.current.contentWindow.postMessage(msg,"*")}catch(e){}}
    if(remFrameRef.current){try{remFrameRef.current.contentWindow.postMessage(msg,"*")}catch(e){}}
  }
  /* Issue category label for visual pinpointing tooltips */
  function issueCategoryLabel(iss){
    var wcag=iss.wcag||"";var name=(iss.info&&iss.info.n)||"";
    if(wcag==="1.1.1"||/alt\s*text|image/i.test(name))return"\u{1F5BC} Image: Missing Alt Text";
    if(wcag==="1.4.3"||wcag==="1.4.11"||/contrast/i.test(name))return"\u{1F3A8} Color Contrast";
    if(wcag==="1.3.1"&&/heading/i.test(iss.desc||name))return"H\u20D7 Heading Structure";
    if(wcag==="1.3.1")return"\u{1F3F7} Semantic Structure";
    if(wcag==="2.1.1"||/keyboard/i.test(name))return"\u2328\uFE0F Keyboard Access";
    if(wcag==="2.4.1"||/bypass|skip/i.test(name))return"\u23E9 Skip Navigation";
    if(wcag==="2.4.2"||/title/i.test(name))return"\u{1F4C4} Page Title";
    if(wcag==="2.4.4"||/link/i.test(name))return"\u{1F517} Link Purpose";
    if(wcag==="2.4.6"||/heading.*label/i.test(name))return"H\u20D7 Headings & Labels";
    if(wcag==="3.1.1"||wcag==="3.1.2"||/lang/i.test(name))return"\u{1F30D} Language";
    if(wcag==="3.3.1"||wcag==="3.3.2"||/form|label|input/i.test(name))return"\u{1F4DD} Form Labels";
    if(wcag==="4.1.1"||/pars/i.test(name))return"\u{1F527} HTML Parsing";
    if(wcag==="4.1.2"||/role|aria|name/i.test(name))return"\u2699\uFE0F ARIA / Roles";
    if(wcag==="4.1.3"||/status/i.test(name))return"\u{1F4E2} Status Messages";
    if(wcag==="2.4.3"||/focus/i.test(name))return"\u{1F3AF} Focus Order";
    /* PR-5.13: prefer the issue's category when the WCAG matchers above
       didn't fire. The structural engine emits cat:"Metadata",
       "Structure", "Forms", "Navigation", "Images" on every issue, so
       Author/Subject/Keywords cards that previously showed the generic
       "Accessibility Issue" title now show "Metadata" with a more
       descriptive icon. Description still carries the specifics. */
    if(iss.cat){
      var catIcon = {
        "Metadata":"\u{1F4CB}", "Structure":"\u{1F3F7}", "Images":"\u{1F5BC}",
        "Forms":"\u{1F4DD}", "Navigation":"\u{1F9ED}"
      }[iss.cat] || "\u26A0";
      return catIcon + " " + iss.cat;
    }
    return"\u26A0 "+((iss.info&&iss.info.n)||"Accessibility Issue");
  }

  /* ── ASSESSMENT REPORT WORKFLOW — branded .docx generation ── */
  var _assessLoading=useState(false),assessLoading=_assessLoading[0],setAssessLoading=_assessLoading[1];
  function exportAssessmentReport(){
    setAssessLoading(true);showToast("Preparing assessment report...");
    var siteUrl=domain?("https://"+domain):"";
    var siteInfoPromise=siteUrl?fetch("/api/site-info?url="+encodeURIComponent(siteUrl)).then(function(r){return r.ok?r.json():{}}).catch(function(){return{}}):Promise.resolve({});
    siteInfoPromise.then(function(siteInfo){
      var quality=siteInfo.dataQuality||0;var status=siteInfo.scrapeStatus||"not_attempted";
      if(quality<30||status!=="success"){
        var reason="";
        if(status==="blocked")reason="Site blocked request.";
        else if(status==="timeout")reason="Site timed out.";
        else if(status==="dns_failed")reason="Domain not found.";
        else if(status==="ssl_error")reason="SSL error.";
        else if(quality<30)reason="Limited info extracted.";
        showToast("Site info: "+(reason||"partial data")+". Using available data.");
      }
      return generateAssessmentDocx(siteInfo);
    }).catch(function(err){showToast("Assessment error: "+err.message);setAssessLoading(false)});
  }
  function generateAssessmentDocx(siteInfo){
    siteInfo=siteInfo||{};showToast("Generating assessment"+(siteInfo.companyName?" for "+siteInfo.companyName:"")+"...");
    var siteUrl=domain?("https://"+domain):"";
    var rp=wp.map(function(p){return{url:p.url||"",title:p.n||p.url||"",issues:p.issues.map(function(iss){return{wcag:iss.wcag||"",sev:iss.sev||"minor",desc:iss.desc||(iss.info?iss.info.n:""),el:iss.el||"",fix:iss.fix||(iss.info?iss.info.r:""),count:iss.count||1}})}});
    var rd=dc.map(function(d){return{name:d.name||"",url:d.url||d.name||"",issues:d.issues.map(function(iss){return{wcag:iss.wcag||"",sev:iss.sev||"minor",desc:iss.desc||(iss.info?iss.info.n:""),el:iss.el||"",fix:iss.fix||(iss.info?iss.info.r:""),count:iss.count||1}})}});
    var payload={siteName:siteInfo.companyName||domain||"Website",siteUrl:siteUrl,clientName:siteInfo.companyName||domain||"Client",clientDescription:siteInfo.description||"",clientIndustry:siteInfo.industry||"",clientAddress:siteInfo.address||"",clientPhone:siteInfo.phone||"",clientEmail:siteInfo.email||"",clientCms:siteInfo.cms||"",assessorName:authUser?authUser.name||authUser.email||"PiARIS":"PiARIS Automated Assessor",assessorOrg:"Pi Consultants",scanDate:new Date().toISOString().split("T")[0],standard:"WCAG 2.1 Level AA",pages:rp,documents:rd,includeAiNarrative:true,siteInfo:siteInfo};
    fetch("/api/assessment-report",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)}).then(function(res){
      if(!res.ok)throw new Error("Report failed: "+res.status);return res.blob();
    }).then(function(blob){
      var a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=(domain||"PiARIS").replace(/[^a-zA-Z0-9]/g,"_")+"_Assessment_"+new Date().toISOString().split("T")[0]+".docx";a.click();showToast("Assessment report downloaded!");
    }).catch(function(err){showToast("Assessment error: "+err.message)}).finally(function(){setAssessLoading(false)});
  }

  /* ═══ AI ENGINE ═══ */
  async function callClaude(msgs,sys,maxTokens,model){
    setAiLoading(true);
    try{
      var body={model:model||"claude-sonnet-4-6",max_tokens:maxTokens||4000,messages:msgs};
      if(sys)body.system=sys;
      var r=await fetch("/api/ai",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      var txt=await r.text();
      /* /api/ai can return a plain-text error on failure — read text, then
         parse, so a non-JSON body degrades to a clean message (not a crash). */
      if(!r.ok){ return "Error: "+(txt||("AI backend returned HTTP "+r.status)).slice(0,300); }
      var d; try{ d=JSON.parse(txt); }catch(_){ return "Error: AI backend returned a non-JSON response"+(txt?(": "+txt.slice(0,160)):""); }
      return d.content?d.content.map(function(b){return b.text||""}).join(""):"Error: "+JSON.stringify(d);
    }catch(e){return"Error: "+e.message}
    finally{setAiLoading(false)}
  }

  /* parseAiJson — robustly pull a JSON object/array out of a model response.
     Models (Sonnet especially) often wrap JSON in a ```json fence and/or add a
     sentence of preamble; a naive fence-strip then fails JSON.parse and a good
     result gets discarded. And a long page can TRUNCATE the JSON mid-object
     (output cap) → "Unterminated string". Strategy: prefer a fenced block, try
     as-is, then the outermost {…}/[…] span, then SALVAGE the complete findings
     from a truncated issues array so a partial response still renders. */
  function parseAiJson(raw){
    if(typeof raw!=="string"||!raw.trim())return null;
    var s=raw.trim();
    var fence=s.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if(fence&&fence[1]&&fence[1].trim())s=fence[1].trim();
    try{return JSON.parse(s);}catch(e){}
    var first=s.search(/[\[{]/), last=Math.max(s.lastIndexOf("}"),s.lastIndexOf("]"));
    if(first>=0&&last>first){try{return JSON.parse(s.slice(first,last+1));}catch(e){}}
    return salvageAiJson(s);
  }
  /* salvageAiJson — recover the COMPLETE objects from a truncated "issues"
     array (the trailing partial object never closes, so it's dropped) and
     rebuild a usable result. String/escape-aware so braces inside text don't
     fool it. Synthesizes summary/score from what survived and flags _truncated
     so the UI can say so. */
  /* salvageArray — recover the COMPLETE objects from a named "key":[ … ] array
     in a possibly-truncated JSON string; string/brace-aware. */
  function salvageArray(s,key){
    var am=s.match(new RegExp('"'+key+'"\\s*:\\s*\\['));
    var arrStart=am?(am.index+am[0].length-1):-1;
    if(arrStart<0)return [];
    var depth=0,inStr=false,esc=false,objStart=-1,objs=[];
    for(var i=arrStart+1;i<s.length;i++){var c=s[i];
      if(inStr){if(esc)esc=false;else if(c==="\\")esc=true;else if(c==='"')inStr=false;continue;}
      if(c==='"'){inStr=true;continue;}
      if(c==='{'){if(depth===0)objStart=i;depth++;}
      else if(c==='}'){depth--;if(depth===0&&objStart>=0){objs.push(s.slice(objStart,i+1));objStart=-1;}}
      else if(c===']'&&depth===0)break;
    }
    var out=[];for(var k=0;k<objs.length;k++){try{out.push(JSON.parse(objs[k]));}catch(e){}}
    return out;
  }
  function salvageAiJson(s){
    /* preserve BOTH issues AND verdicts — dropping verdicts here is what left the
       guidance cards blank and let human-judgment criteria slip past. */
    var issues=salvageArray(s,"issues"), verdicts=salvageArray(s,"verdicts");
    if(!issues.length&&!verdicts.length)return null;
    var crit=0,maj=0,min=0;
    issues.forEach(function(it){var sv=String(it&&it.severity||"").toLowerCase();if(sv==="critical")crit++;else if(sv==="major")maj++;else min++;});
    return {issues:issues,verdicts:verdicts,needsManualReview:[],summary:{total:issues.length,critical:crit,major:maj,minor:min},score:Math.max(0,100-crit*12-maj*4-min),_truncated:true};
  }

  async function analyzeHtml(){
    if(!aiHtml.trim())return;
    setAiRes(null);setAiFixed("");
    var raw=await callClaude([{role:"user",content:"Analyze this HTML for WCAG 2.1 AA violations:\n\n"+aiHtml}],AI_WEB_SYSTEM_PROMPT);
    var parsed=parseAiJson(raw);if(parsed)setAiRes(parsed);else setAiRes({error:raw})
    /* Now generate fixed version */
    var sys2="You are PiARIS. Take the HTML below and return ONLY the corrected, fully accessible HTML. Fix all WCAG 2.1 AA violations: add alt text (descriptive, contextual), add lang attr, fix headings hierarchy, add form labels, add ARIA where needed, add skip-nav, ensure keyboard access. Return ONLY the fixed HTML, no explanation.";
    var fixed=await callClaude([{role:"user",content:"Fix all accessibility issues in this HTML and return corrected HTML only:\n\n"+aiHtml}],sys2);
    setAiFixed(fixed);
  }

  async function generateAltText(){
    if(!aiImg)return;
    setAiAlt("");
    var sys="You are PiARIS AI Alt Text Generator. Analyze the image and generate: 1) A concise alt text (under 125 chars) suitable for the alt attribute, 2) A detailed long description for complex images. Consider context: what information does this image convey? What would a screen reader user need to know? Return ONLY JSON: {\"alt\":\"concise alt text\",\"longDesc\":\"detailed description\",\"isDecorative\":false,\"confidence\":\"high|medium|low\"}";
    var msg={role:"user",content:[{type:"image",source:{type:"base64",media_type:aiImg.type,data:aiImg.data}},{type:"text",text:"Generate accessible alt text for this image. Consider it may be on a government/organizational website."}]};
    /* Alt text is the most "obvious" AI task -> route it to the cheaper, faster
       Haiku model (Sonnet stays for analysis/prioritization). */
    var raw=await callClaude([msg],sys,undefined,"claude-haiku-4-5-20251001");
    var parsed=parseAiJson(raw);
    if(parsed&&parsed.alt!=null){setAiAlt(parsed);}
    else if(/^Error:/.test((raw||"").trim())){var g=classifyAiError(raw);setAiAlt(null);showToast(g.title||"Alt text generation failed","error");}
    else{setAiAlt({alt:raw,longDesc:"",isDecorative:false,confidence:"low"});}
  }

  async function analyzePdf(structRes){
    /* Findings-based AI: the PDF is NEVER sent to the model (that whole-document
       send was both the cost and the gateway-timeout sink). We pass only the
       structural findings as text — a few KB — and ask Claude to prioritize and
       write concrete remediation per issue. Cents, seconds, no timeout. */
    var sres=structRes||pdfStructRes;
    if(!sres||!sres.checks){setAiPdfRes({error:"Run Structural Checks first — AI Analysis works from those findings."});return;}
    setAiPdfRes(null);
    var c=sres.checks;
    var meta="File: "+((aiPdf&&aiPdf.name)||"document.pdf")
      +" | Pages: "+(c.pageCount||"?")+" | Tagged: "+(!!c.tagged)
      +" | Title: "+(c.titleValue||"(none)")+" | Lang: "+(c.langValue||"(none)")
      +" | Images: "+(c.imgCount||0)+" | Alt-text coverage: "+(c.altCoverage!=null?c.altCoverage+"%":"?")
      +" | Headings: "+(!!c.headings)+" | Struct tree: "+(!!c.structTree);
    var findings=((sres.issues||[]).map(function(i){
      return "- ["+(i.sev||"minor").toUpperCase()+"] "+(i.cat||"Issue")+(i.wcag?" (WCAG "+i.wcag+")":"")+(i.page?" p."+i.page:"")+": "+(i.desc||"");
    }).join("\n"))||"(no structural issues found)";
    var usr="Document metadata:\n"+meta+"\n\nStructural findings ("+((sres.issues||[]).length)+"):\n"+findings;
    var raw=await callClaude([{role:"user",content:usr}],AI_PDF_SYSTEM_PROMPT,8000);
    /* Robust parse: strip code fences, else fall back to the first {…last}
       slice so a prose-wrapped response still yields JSON. A genuinely
       unparseable (e.g. truncated) response still surfaces via the banner
       with its raw text — a paid call is never silently dropped. */
    var parsed=null;
    try{parsed=JSON.parse(raw.replace(/```json\s*/g,"").replace(/```/g,"").trim());}
    catch(e1){var s=raw.indexOf("{"),en=raw.lastIndexOf("}");if(s>=0&&en>s){try{parsed=JSON.parse(raw.slice(s,en+1));}catch(e2){}}}
    if(parsed&&typeof parsed==="object"){
      setAiPdfRes(parsed);
      if(_labSessionId.current&&labDocs.length>0){patchPageSession({updateDocument:{docIndex:labSelDoc,aiPdfResults:parsed},analyzedAt:new Date().toISOString()})}
    }else{
      setAiPdfRes({error:raw});
    }
  }

  function handleImgUpload(ev){
    var file=ev.target.files[0];if(!file)return;
    var reader=new FileReader();
    reader.onload=function(){var b64=reader.result.split(",")[1];setAiImg({data:b64,type:file.type,name:file.name,preview:reader.result})};
    reader.readAsDataURL(file);
  }
  function handlePdfUpload(ev){
    var file=ev.target.files[0];if(!file)return;
    var reader=new FileReader();
    reader.onload=async function(){
      var b64=reader.result.split(",")[1];
      setAiPdf({data:b64,name:file.name});setLabPrevIdx(0);
      var docEntry={docIndex:0,name:file.name,fileSize:file.size,structuralResults:null,aiPdfResults:null,pageIssues:{}};
      setLabDocs([docEntry]);setLabSelDoc(0);
      /* Auto-create doc page session */
      var psid=await createPageSession("doc",{sourceFileName:file.name,documents:[docEntry]});
      if(psid){navigateTo("aiLabDoc",psid);setView("app");putPageSessionPdf(psid,0,b64,file.name);}
    };
    reader.readAsDataURL(file);
  }
  function handlePdfUploadAdd(ev){
    var file=ev.target.files[0];if(!file)return;
    var reader=new FileReader();
    reader.onload=function(){
      var b64=reader.result.split(",")[1];
      var newDoc={docIndex:labDocs.length,name:file.name,fileSize:file.size,structuralResults:null,aiPdfResults:null,pageIssues:{}};
      setLabDocs(function(prev){return prev.concat(newDoc)});
      setLabSelDoc(labDocs.length);
      setAiPdf({data:b64,name:file.name});
      setPdfStructRes(null);setAiPdfRes(null);setPdfRemForm(null);setPdfRemStatus("");
      patchPageSession({addDocument:newDoc});
      putPageSessionPdf(_labSessionId.current,newDoc.docIndex,b64,file.name);
    };
    reader.readAsDataURL(file);
  }
  function downloadFixed(){
    if(!aiFixed)return;
    var b=new Blob([aiFixed],{type:"text/html"});
    var a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="paris-remediated.html";a.click();
  }

  /* ═══════════════════════════════════════════════════════════════
   * MODULE: pa11y-lab-session-api                 [status: ready]
   * PURPOSE: CRUD operations against /api/page-session + /api/page-sessions
   *
   * READS:     _labSessionId (ref), labDocs, labSelDoc, mod
   * WRITES:    labSession, labSessions, _labSessionId, labDocs,
   *            labUrl, aiHtml, labPreview, labAxeRes, labHtmlcsRes,
   *            aiRes, aiFixed, pdfStructRes, aiPdfRes
   * CALLS:     fetch /api/page-session/*, showToast, navigateTo
   * EFFECTS:   On-mount effect loads session from URL
   * EMITS:     Toast notifications, URL navigation
   *
   * EXPORTS:   { loadPageSession, listPageSessions, createPageSession,
   *              patchPageSession, savePageSession }
   * IMPORTS:   _labSessionId, session state setters, navigateTo, showToast
   *
   * CLOSURE:   Heavy closure on state setters. When extracting, convert
   *            to hook that accepts setters as arguments or returns setters.
   * ENTANGLED: patchPageSession is called from many engine functions
   *            (labRunAxe/Htmlcs/Ai/PdfStructural). Extract engines first
   *            so they can accept patchPageSession as a prop/arg.
   * ═══════════════════════════════════════════════════════════════ */

  /* ── PA11Y Lab session functions ── */
  async function loadPageSession(psessId){
    try{
      var r=await fetch("/api/page-session/"+psessId,{credentials:"include"});
      if(!r.ok){showToast("Page session not found","error");return;}
      var data=await r.json();
      setLabSession(data);
      _labSessionId.current=psessId;
      if(data.mode==="web"){
        setLabUrl(data.sourceUrl||"");
        setAiHtml(data.html||"");
        setLabPreview(data.previewHtml||data.html||"");
        setLabAxeRes(data.axeResults||null);
        setLabHtmlcsRes(data.htmlcsResults||null);
        setAiRes(data.aiResults||null);
        setAiFixed(data.fixedHtml||"");
      } else if(data.mode==="doc"){
        setLabDocs(data.documents||[]);
        setLabSelDoc(0);
        /* Rehydrate the uploaded PDF bytes from server-side blob storage
           so the viewer renders on any device / cold load. Bytes live in
           the page-session blob container (doc-0.pdf), not the Cosmos doc
           (results only). A missing blob (pre-blob session, or an admin
           reset) just leaves aiPdf null → the viewer's graceful "upload
           to preview" fallback. */
        fetch("/api/page-session/"+psessId+"/content?name=doc-0.pdf",{credentials:"include"})
          .then(function(res){return res.ok?res.json():null})
          .then(function(c){if(c&&c.data)setAiPdf({data:c.data,name:c.name||"document.pdf"})})
          .catch(function(){/* fallback stays */});
        var doc0=data.documents&&data.documents[0];
        var structRes=doc0?doc0.structuralResults:null;
        setPdfStructRes(structRes);
        setAiPdfRes(null);   /* AI Audit removed — never resurface stale AI results */
        /* PR-5.13: hydrate the Remediation Workbench form from the
           persisted structural-engine `checks` block. Previously
           pdfRemForm was only ever set by labRunPdfStructural in
           the current tab, so reopening a saved session collapsed
           the Workbench to a single "Apply Fixes & Save PDF"
           button with no editable fields. With this, the same
           Workbench form (Title, Language, Author, Subject,
           Keywords) renders whenever results exist — fresh-upload
           and reopened sessions are now symmetric. */
        if(structRes && structRes.checks){
          var c=structRes.checks;
          setPdfRemForm({
            title: c.titleValue||"",
            lang:  c.langValue||"en-US",
            author: c.author||"",
            subject: c.subject||"",
            keywords: c.keywords||"",
            displayTitle: !!c.displayTitle
          });
        } else {
          setPdfRemForm(null);
        }
      }
    }catch(e){showToast("Failed to load session: "+e.message,"error")}
  }

  async function listPageSessions(mode){
    try{
      var url="/api/page-sessions?limit=10"+(mode?"&mode="+mode:"");
      var r=await fetch(url,{credentials:"include"});
      if(!r.ok)return;
      var data=await r.json();
      setLabSessions(data.sessions||[]);
    }catch(e){/* silent */}
  }

  async function createPageSession(mode,opts){
    try{
      var body=Object.assign({mode:mode},opts||{});
      var r=await fetch("/api/page-session",{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      if(!r.ok){showToast("Failed to create session","error");return null}
      var data=await r.json();
      _labSessionId.current=data.psessId;
      setLabSession({id:data.psessId,mode:mode,createdAt:data.createdAt,summary:{}});
      return data.psessId;
    }catch(e){showToast("Session error: "+e.message,"error");return null}
  }

  async function patchPageSession(fields){
    var sid=_labSessionId.current;
    if(!sid)return;
    try{
      await fetch("/api/page-session/"+sid,{method:"PATCH",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify(fields)});
    }catch(e){/* silent — best-effort persist */}
  }

  /* Persist raw PDF bytes to the page-session's server-side blob store
     (doc-{i}.pdf) so the viewer rehydrates on any device / cold load —
     the Cosmos session holds results only, not the file. Authoritative
     and admin-resettable (DELETE clears the blobs). Best-effort: a
     failure leaves the viewer's "upload to preview" fallback intact. */
  function putPageSessionPdf(psid,docIndex,b64,name){
    if(!psid||!b64)return Promise.resolve();
    return fetch("/api/page-session/"+psid+"/content",{
      method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({name:"doc-"+(docIndex||0)+".pdf",data:b64,contentType:"application/pdf",fileName:name})
    }).then(function(res){
      if(res&&!res.ok){
        /* Bug 3: surface the actual server reason (now a clean 503 from
           /api/page-session/:id/content) so an admin can see "Storage
           not configured" vs a different failure without checking logs. */
        return res.json().then(function(j){
          var reason=(j&&j.error)?j.error:"PDF preview won't be available on reopen";
          showToast(reason,"error");
          return res;
        }).catch(function(){
          showToast("PDF preview won't be available on reopen (HTTP "+res.status+")","error");
          return res;
        });
      }
      return res;
    }).catch(function(){/* network failure: stay silent, best-effort */});
  }

  async function savePageSession(){
    var sid=_labSessionId.current;
    if(!sid)return;
    try{
      var r=await fetch("/api/page-session/"+sid+"/save",{method:"POST",credentials:"include"});
      if(r.ok){
        setLabSession(function(prev){return prev?Object.assign({},prev,{saved:true}):prev});
        showToast("Session saved permanently");
      }
    }catch(e){showToast("Save failed: "+e.message,"error")}
  }

  /* Load page session from URL on initial mount */
  useEffect(function(){
    var psid=_labSessionId.current;
    if(psid&&(mod==="aiLabWeb"||mod==="aiLabDoc")){
      loadPageSession(psid);
    }
    if(mod==="aiLabWeb")listPageSessions("web");
    if(mod==="aiLabDoc")listPageSessions("doc");
  },[]);

  /* PR-5.13: probe AI engine availability once on mount. /api/ai (GET)
     returns { available: bool } based on whether ANTHROPIC_API_KEY is
     set on the Function App. We auto-uncheck the AI Analysis engine
     and disable the checkbox when unavailable so the user can't run
     an audit and silently get only half the engines. */
  useEffect(function(){
    fetch("/api/ai",{credentials:"include"})
      .then(function(r){return r.ok?r.json():{available:false}})
      .then(function(j){
        var avail=!!(j&&j.available);
        setAiAvailable(avail);
        if(!avail){
          setPdfEngines(function(p){return Object.assign({},p,{ai:false})});
        }
      })
      .catch(function(){
        /* If the probe itself fails (network etc.), be conservative
           and assume unavailable rather than letting the user trigger
           a 30s wait for a definite failure. */
        setAiAvailable(false);
        setPdfEngines(function(p){return Object.assign({},p,{ai:false})});
      });
  },[]);

  /* ═══════════════════════════════════════════════════════════════
   * MODULE: pa11y-lab-engines                     [status: wip]
   * PURPOSE: axe-core + HTMLCS + Claude AI engines for PA11YLab
   *
   * READS:     labUrl, aiHtml, aiPdf, labDocs, labSelDoc, _labSessionId
   * WRITES:    aiHtml, labPreview, labAxeRes, labHtmlcsRes, aiRes,
   *            aiFixed, labRunning, labFetchErr, pdfStructRes, aiPdfRes
   * CALLS:     fetch, createPageSession, patchPageSession, navigateTo,
   *            callClaude, showToast
   * EFFECTS:   Creates sandboxed iframes for engine execution
   * EMITS:     DOM iframes (cleaned up after run), toast notifications
   *
   * EXPORTS:   { labFetchPage, labRunAxe, labRunHtmlcs, labRunAi,
   *              labRunAiFix, labRunAllWeb, labRunPdfStructural,
   *              analyzePdf, labRunAllPdf, labRemediatePdf, handlePdfUpload }
   * IMPORTS:   createPageSession, patchPageSession, navigateTo,
   *            callClaude, showToast, state setters
   *
   * CLOSURE:   _axeSrc, _htmlcsSrc refs (local cache)
   * ENTANGLED: labRunAi calls callClaude which lives in a different area;
   *            runEngineInIframe() uses DOM directly — must stay on client.
   *            PDF analysis mixes structural (client) + AI (server) engines.
   * ═══════════════════════════════════════════════════════════════ */

  /* Multi-engine functions */
  var _axeSrc=useRef(null);var _htmlcsSrc=useRef(null);

  async function labFetchPage(){
    if(!labUrl.trim())return;
    setLabFetchErr("");setAiHtml("");setLabPreview("");setLabAxeRes(null);setLabHtmlcsRes(null);setAiRes(null);setAiFixed("");
    setLabRunning(function(p){return Object.assign({},p,{fetch:true})});
    try{
      var r=await fetch("/api/fetch-page?url="+encodeURIComponent(labUrl.trim()));
      /* /api/fetch-page returns sanitized HTML (Content-Type: text/html), NOT
         JSON — errors come back as a JSON body on a non-2xx status. */
      if(!r.ok){ var ej={}; try{ej=await r.json();}catch(_){} setLabFetchErr((ej&&ej.error)||("Fetch failed (HTTP "+r.status+")")); return; }
      var html=await r.text();
      if(!html||!html.trim()){ setLabFetchErr("Fetched page was empty."); return; }
      setAiHtml(html);
      setLabPreview(html);
      setLabWebStage("audit");   /* advance to Audit; the effect runs the engines on the fetched page */
      showToast("Fetched "+Math.round(html.length/1024)+"KB from "+labUrl);
      /* Auto-create page session. Store html ONCE — the server now inlines the
         page's CSS (heavier payload), and the reload path falls back to html
         when previewHtml is absent, so duplicating it would needlessly double
         the Cosmos doc toward its ~2MB limit. */
      var psid=await createPageSession("web",{sourceUrl:labUrl.trim(),html:html});
      if(psid){navigateTo("aiLabWeb",psid);setView("app")}
    }catch(e){setLabFetchErr(e.message)}
    finally{setLabRunning(function(p){var n=Object.assign({},p);delete n.fetch;return n})}
  }
  /* analyzeHtml — the paste path: audit the HTML already in the textarea
     (aiHtml) WITHOUT routing through /api/fetch-page. Mirrors fetch's end
     state (preview + report tab) and kicks the engines. */
  function runPasteAudit(){
    if(!aiHtml.trim()){ showToast("Paste some HTML first.","error"); return; }
    setLabFetchErr("");setLabAxeRes(null);setLabHtmlcsRes(null);setAiRes(null);setAiFixed("");
    setLabPreview(aiHtml);
    setLabWebStage("audit");   /* the Audit effect runs the engines */
  }

  /* Engine source loaders. Self-hosted same-origin from /vendor (CRA ships
     public/vendor/ into build/). The old CDN fallbacks are removed: the CSP
     connect-src doesn't allow cdnjs/squizlabs, so they could only ever throw —
     dead weight that masked the real failure. On any miss we throw a CLEAR
     error the caller surfaces immediately, instead of handing an empty source
     to the runner (which could then only wait out the 30s abort). */
  async function getAxeSrc(){if(_axeSrc.current)return _axeSrc.current;var lr;try{lr=await fetch("/vendor/axe-core/axe.min.js");}catch(e){throw new Error("axe-core fetch failed: "+e.message);}if(!lr.ok)throw new Error("axe-core HTTP "+lr.status+" at /vendor/axe-core/axe.min.js");var t=await lr.text();if(!t||t.length<1000||t.indexOf("axe")===-1)throw new Error("axe-core source invalid ("+(t?t.length:0)+" bytes) — vendor file missing or misrouted");_axeSrc.current=t;return t;}
  async function getHtmlcsSrc(){if(_htmlcsSrc.current)return _htmlcsSrc.current;var lr;try{lr=await fetch("/vendor/htmlcs/HTMLCS.js");}catch(e){throw new Error("HTMLCS fetch failed: "+e.message);}if(!lr.ok)throw new Error("HTMLCS HTTP "+lr.status+" at /vendor/htmlcs/HTMLCS.js");var t=await lr.text();if(!t||t.length<1000||t.indexOf("HTMLCS")===-1)throw new Error("HTMLCS source invalid ("+(t?t.length:0)+" bytes) — vendor file missing or misrouted");_htmlcsSrc.current=t;return t;}

  function runEngineInIframe(html,engineSrc,runScript){
    return new Promise(function(resolve){
      /* No engine source = nothing to run. Fail fast + clearly rather than spin
         up an iframe that could only ever time out. */
      if(!engineSrc||(""+engineSrc).length<1000){resolve({error:"Engine source failed to load (vendor script empty or missing)"});return;}
      var iframe=document.createElement("iframe");
      iframe.style.cssText="width:1280px;height:900px;position:absolute;left:-9999px;top:-9999px;opacity:0;pointer-events:none";
      iframe.setAttribute("sandbox","allow-scripts allow-same-origin");
      document.body.appendChild(iframe);
      var done=false,injected=false,settleTimer=null,hardTimer=null,pollTimer=null;
      function finish(result){if(done)return;done=true;try{clearTimeout(settleTimer)}catch(e){}try{clearTimeout(hardTimer)}catch(e){}try{clearInterval(pollTimer)}catch(e){}try{document.body.removeChild(iframe)}catch(e){}resolve(result);}
      function inject(){
        if(injected||done)return;injected=true;
        try{
          var win=iframe.contentWindow,doc=iframe.contentDocument;
          if(!win||!doc){finish({error:"Engine could not access the page document"});return;}
          var s=doc.createElement("script");s.textContent=engineSrc;doc.head.appendChild(s);
          var s2=doc.createElement("script");s2.textContent="("+runScript.toString()+")(window,document)";doc.head.appendChild(s2);
          /* The iframe is same-origin (allow-same-origin), so the engine writes
             its result onto its OWN window (win.__LAB_RESULT__) and we read it
             straight off here — no postMessage window-targeting to get wrong
             (that fragility is why results never came back and every run waited
             out the 30s abort). Poll because the engine resolves async. */
          pollTimer=setInterval(function(){try{var r=iframe.contentWindow&&iframe.contentWindow.__LAB_RESULT__;if(r)finish(JSON.parse(JSON.stringify(r)));}catch(e){}},120);
          /* the engine was injected but never produced a result — hard cap. */
          hardTimer=setTimeout(function(){finish({error:"Engine timed out (30s)"})},30000);
        }catch(e){finish({error:e.message})}
      }
      iframe.onload=function(){inject();};
      iframe.srcdoc=html;
      /* A page whose external subresources (CSS/fonts on a dead or slow CDN)
         hang would delay `onload` indefinitely and burn the full 30s. The
         srcdoc DOM is parsed long before then, so fall back to running the
         engine after a short settle instead of waiting on resources that may
         never arrive. Healthy pages fire `onload` in <1s and inject first;
         the guard makes the later path a no-op. */
      settleTimer=setTimeout(inject,6000);
    });
  }

  async function labRunAxe(){
    if(!aiHtml.trim())return;
    setLabRunning(function(p){return Object.assign({},p,{axe:true})});setLabAxeRes(null);
    try{
      var src=await getAxeSrc();
      var res=await runEngineInIframe(aiHtml,src,function(win,doc){
        if(!win.axe){win.__LAB_RESULT__={error:"axe-core not loaded"};return;}
        win.axe.run(doc,{runOnly:{type:"tag",values:["wcag2a","wcag2aa","wcag21a","wcag21aa"]},resultTypes:["violations","incomplete"]}).then(function(r){
          var issues=r.violations.map(function(v){
            var wcag="";for(var i=0;i<(v.tags||[]).length;i++){var m=v.tags[i].match(/^wcag(\d)(\d)(\d+)$/);if(m){wcag=m[1]+"."+m[2]+"."+m[3];break;}}
            /* capture axe's computed contrast colours so the server can decide
               small-nudge-auto-fix vs. human-pick (WCAG 1.4.3). */
            var cc=null,n0=v.nodes&&v.nodes[0];
            if(v.id==="color-contrast"&&n0){var chk=((n0.any||[]).concat(n0.all||[])).filter(function(x){return x.data&&x.data.fgColor;})[0];
              if(chk)cc={fg:chk.data.fgColor,bg:chk.data.bgColor,ratio:chk.data.contrastRatio,large:!!(chk.data.fontSize&&parseFloat(chk.data.fontSize)>=18)};}
            return{engine:"axe",ruleId:v.id,wcag:wcag,sev:v.impact==="critical"?"critical":v.impact==="serious"?"major":"minor",desc:v.help,helpUrl:v.helpUrl,count:v.nodes.length,impact:v.impact,el:v.nodes[0]?(v.nodes[0].html||"").slice(0,300):"",fix:v.nodes[0]?(v.nodes[0].failureSummary||"").replace(/Fix (any|all) of the following:\s*/g,"").trim():"",contrast:cc};
          });
          var c=0,mj=0,mn=0;issues.forEach(function(i){if(i.sev==="critical")c+=i.count;else if(i.sev==="major")mj+=i.count;else mn+=i.count;});
          win.__LAB_RESULT__={success:true,issues:issues,incomplete:r.incomplete.length,version:(win.axe&&win.axe.version)||"",summary:{total:issues.length,elements:c+mj+mn,critical:c,major:mj,minor:mn}};
        }).catch(function(e){win.__LAB_RESULT__={error:e.message}});
      });
      setLabAxeRes(res);
      if(res&&res.success)patchPageSession({axeResults:res,analyzedAt:new Date().toISOString()});
    }catch(e){setLabAxeRes({error:e.message})}
    finally{setLabRunning(function(p){var n=Object.assign({},p);delete n.axe;return n})}
  }

  async function labRunHtmlcs(){
    if(!aiHtml.trim())return;
    setLabRunning(function(p){return Object.assign({},p,{htmlcs:true})});setLabHtmlcsRes(null);
    try{
      var src=await getHtmlcsSrc();
      var res=await runEngineInIframe(aiHtml,src,function(win,doc){
        if(!win.HTMLCS){win.__LAB_RESULT__={error:"HTMLCS not loaded"};return;}
        try{win.HTMLCS.process("WCAG2AA",doc,function(){
          var msgs=win.HTMLCS.getMessages();var ruleMap={};
          for(var i=0;i<msgs.length;i++){var m=msgs[i];if(m.type!==1)continue;var code=m.code||"unknown";
            if(!ruleMap[code]){ruleMap[code]={code:code,desc:m.msg||"",count:0,firstEl:"",wcag:""};}
            var wm=code.match(/(\d+)_(\d+)_(\d+)/);if(wm&&!ruleMap[code].wcag)ruleMap[code].wcag=wm[1]+"."+wm[2]+"."+wm[3];
            ruleMap[code].count++;if(!ruleMap[code].firstEl&&m.element)ruleMap[code].firstEl=(m.element.outerHTML||"").slice(0,300);
          }
          var issues=[];var codes=Object.keys(ruleMap);
          for(var j=0;j<codes.length;j++){var r=ruleMap[codes[j]];
            var sev="minor";if(r.wcag==="1.1.1"||r.wcag==="1.3.1"||r.wcag==="4.1.2")sev="critical";
            else if(r.code.indexOf("Principle1")!==-1||r.code.indexOf("Principle4")!==-1)sev="major";
            issues.push({engine:"htmlcs",ruleId:r.code,wcag:r.wcag,sev:sev,desc:r.desc.slice(0,200),count:r.count,el:r.firstEl,fix:r.desc.slice(0,150)});
          }
          var c=0,mj=0,mn=0;issues.forEach(function(i){if(i.sev==="critical")c+=i.count;else if(i.sev==="major")mj+=i.count;else mn+=i.count;});
          win.__LAB_RESULT__={success:true,issues:issues,totalMessages:msgs.length,summary:{total:issues.length,elements:c+mj+mn,critical:c,major:mj,minor:mn}};
        })}catch(e){win.__LAB_RESULT__={error:e.message}}
      });
      setLabHtmlcsRes(res);
      if(res&&res.success)patchPageSession({htmlcsResults:res,analyzedAt:new Date().toISOString()});
    }catch(e){setLabHtmlcsRes({error:e.message})}
    finally{setLabRunning(function(p){var n=Object.assign({},p);delete n.htmlcs;return n})}
  }

  /* Web AI engine: Sonnet (product call) for WCAG reasoning quality. Sonnet is
     ~2.5-3x slower per token, so a large page exceeds the ~22s SWA→Function
     gateway and 504s on a synchronous call. Durable fix: run it as a background
     JOB (callClaudeAsync) off the gateway clock and poll — Sonnet completes at
     any size. A fast SYNC Haiku fallback covers any async-infra hiccup (worker
     not draining yet, endpoints absent) so the engine ALWAYS completes; the
     model that produced the result is surfaced honestly. */
  var WEB_AI_MODEL="claude-sonnet-4-6";
  var WEB_AI_FALLBACK="claude-haiku-4-5-20251001";
  async function callClaudeAsync(msgs,sys,maxTokens,model){
    try{
      var sub=await fetch("/api/ai-async",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
        body:JSON.stringify({messages:msgs,system:sys,max_tokens:maxTokens,model:model})});
      var subTxt=await sub.text(); var sd=null; try{sd=JSON.parse(subTxt);}catch(_){}
      if(!sub.ok||!sd||!sd.jobId) return "Error: "+((sd&&sd.error)||subTxt.slice(0,160)||("HTTP "+sub.status));
      var deadline=Date.now()+180000;
      while(Date.now()<deadline){
        await new Promise(function(r){setTimeout(r,2000);});
        var ps=await fetch("/api/ai-status?id="+encodeURIComponent(sd.jobId),{credentials:"include"});
        var psTxt=await ps.text(); var pd=null; try{pd=JSON.parse(psTxt);}catch(_){}
        if(!ps.ok||!pd) return "Error: "+((pd&&pd.error)||("status HTTP "+ps.status));
        if(pd.status==="done") return pd.content||"";
        if(pd.status==="error") return "Error: "+(pd.error||"AI job failed");
      }
      return "Error: AI job timed out (poll)";
    }catch(e){ return "Error: "+((e&&e.message)||"async AI failed"); }
  }
  async function callClaudeWebAI(msgs,sys,maxTokens){
    setAiLoading(true);
    try{
      var raw=await callClaudeAsync(msgs,sys,maxTokens,WEB_AI_MODEL);   // Sonnet async, off the gateway clock — full maxTokens
      if(typeof raw!=="string"||/^Error:/.test(raw)){
        showToast("Sonnet path unavailable — using the faster model…");
        /* the Haiku net is SYNCHRONOUS (through the gateway), so cap its output
           or it will 504 like Sonnet did; a long result still salvages. */
        raw=await callClaude(msgs,sys,Math.min(maxTokens,2000),WEB_AI_FALLBACK);
        setAiModelUsed(/^Error:/.test(raw)?null:"haiku-fallback");
      } else setAiModelUsed("sonnet");
      return raw;
    } finally { setAiLoading(false); }
  }
  async function labRunAi(){
    if(!aiHtml.trim())return;setAiRes(null);setAiFixed("");setAiModelUsed(null);
    /* 16000 tokens: the response now carries ~25 issues AND ~50 per-criterion
       verdicts; 8000 clipped the verdicts (which come last) and the salvage
       dropped them. The async path has no gateway ceiling, so the headroom is
       free, and salvageAiJson now preserves verdicts even if it still truncates. */
    var raw=await callClaudeWebAI([{role:"user",content:"Analyze this HTML for WCAG 2.1 AA violations:\n\n"+htmlForAi(aiHtml,30000)}],AI_WEB_SYSTEM_PROMPT,16000);
    var parsed=parseAiJson(raw);
    if(parsed){setAiRes(parsed);patchPageSession({aiResults:parsed,analyzedAt:new Date().toISOString()});
      if(parsed._truncated)showToast("⚠ AI response was truncated — showing the "+((parsed.issues&&parsed.issues.length)||0)+" complete findings it returned","error");}
    else{setAiRes({error:raw})}
  }

  async function labRunAiFix(){
    if(!aiHtml.trim())return;
    var sys2="You are PiARIS. Return ONLY the corrected, fully accessible HTML. Fix all WCAG 2.1 AA violations. Return ONLY the fixed HTML, no explanation.";
    var fixed=await callClaudeWebAI([{role:"user",content:"Fix all accessibility issues in this HTML:\n\n"+htmlForAi(aiHtml,30000)}],sys2,8000);
    setAiFixed(fixed);
    patchPageSession({fixedHtml:fixed});
  }
  async function labRunAllWeb(){await Promise.all([labRunAxe(),labRunHtmlcs(),labRunAi()]);}
  /* pageFeatures — cheap element-presence scan of the page HTML. Drives the
     applicability layer: criteria whose triggering element is ABSENT (no media
     → 1.2.x, no forms → 3.3.x, …) are auto-marked Not Applicable server-side so
     they drop out of the human review queue instead of being punted as "needs
     manual review". Conservative on purpose — only clear absences. */
  function pageFeatures(html){
    var h=String(html||"");
    return {
      hasMedia: /<(audio|video|track|embed|object)\b/i.test(h) || /(youtube\.com|youtu\.be|vimeo\.com|wistia|dailymotion)/i.test(h),
      hasForm: /<(form|input|select|textarea|button)\b/i.test(h),
      hasImg: /<(img|svg|picture)\b/i.test(h)
    };
  }
  /* generateWcagReport — POST the engine results to /api/wcag-evaluation and
     download the evidence-first per-criterion PDF (the non-flimsy deliverable). */
  async function generateWcagReport(){
    if(!(labAxeRes||labHtmlcsRes||aiRes)){ showToast("Run the engines first.","error"); return; }
    showToast("📋 Generating evidence-based evaluation report…");
    try{
      var mr=(aiRes&&aiRes.needsManualReview)||[];
      var r=await fetch("/api/wcag-evaluation",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
        body:JSON.stringify({url:labUrl||(scanMeta&&scanMeta.domain)||"",axe:labAxeRes,htmlcs:labHtmlcsRes,ai:aiRes,manualReview:mr,pageFeatures:pageFeatures(aiHtml)})});
      var data=await r.json();
      if(!r.ok||!data.pdfBase64){ throw new Error((data&&data.error)||("HTTP "+r.status)); }
      /* Hold the PDF for an explicit Download click — opening the report must
         NOT auto-prompt a file save (that side-effect fired a save dialog on
         every generate). The button reveals once the report is ready. */
      setReportPdf({b64:data.pdfBase64,name:"wcag-evaluation-"+(new Date().toISOString().slice(0,10))+".pdf"});
      if(data.evalId){ setLastEvalId(data.evalId); setReviewPanel(null);
        setReviewCount(typeof data.reviewCount==="number"?data.reviewCount:((data.evaluation&&data.evaluation.summary&&data.evaluation.summary["needs-review"])||null)); }
      var sm=data.evaluation&&data.evaluation.summary;
      showToast(sm?("📋 Report ready — "+sm.supports+" supports · "+sm["does-not-support"]+" fail · "+(sm["not-applicable"]||0)+" N/A · "+sm["needs-review"]+" review"):"Report ready");
    }catch(e){ showToast("Report failed: "+errText(e),"error"); }
  }

  /* ── Inspection station: the reviewer walks the prioritized human queue ── */
  var WCAG_TEST_GUIDE={
    "1.1.1":{p:"🔊 Screen reader",h:"Read each image's alt — does it convey the same info/function? Are decorative images marked as artifacts?"},
    "1.3.1":{p:"🔊 Screen reader",h:"Do headings, lists, tables and labels announce with the correct roles + relationships?"},
    "1.3.2":{p:"📄 Content order",h:"Disable CSS / linearize — does the content still read in a sensible order?"},
    "1.4.1":{p:"🔍 Visual",h:"Is any information conveyed by colour ALONE (e.g. required fields shown only in red)?"},
    "1.4.3":{p:"🔍 Visual",h:"Check contrast of text over images/gradients (≥4.5:1, or 3:1 for large text)."},
    "1.4.10":{p:"🔍 Zoom",h:"At 320px width / 400% zoom, is content lost or does it require 2-D scrolling?"},
    "1.4.11":{p:"🔍 Visual",h:"Do UI components + meaningful graphics meet 3:1 contrast?"},
    "2.1.1":{p:"⌨️ Keyboard",h:"Tab to AND operate every control by keyboard. Any mouse-only interactions?"},
    "2.1.2":{p:"⌨️ Keyboard",h:"Can you Tab INTO and OUT of every component (menus, modals, embeds)? No traps."},
    "2.4.3":{p:"⌨️ Keyboard",h:"As you Tab, does focus move in an order that matches the visual/reading order?"},
    "2.4.6":{p:"🔊 Screen reader",h:"Are the headings and labels descriptive of their section/control?"},
    "2.4.7":{p:"⌨️ Keyboard",h:"Is there a clearly visible focus indicator on every focusable element?"},
    "3.1.1":{p:"📄 Code",h:"Is <html lang> present and correct for the page's primary language?"},
    "3.2.2":{p:"📝 Forms",h:"Does changing a field's value cause an unexpected change of context?"},
    "3.3.1":{p:"📝 Forms",h:"Trigger a form error — is it identified and described in text (not colour only)?"},
    "3.3.3":{p:"📝 Forms",h:"On an error, is a correction suggested when one is known?"},
    "4.1.2":{p:"🔊 Screen reader",h:"For custom widgets, is the role / name / state correct and announced?"},
    "4.1.3":{p:"🔊 Screen reader",h:"Are status messages announced without moving focus (e.g. aria-live)?"},
    "1.2.2":{p:"🎬 Media",h:"Do prerecorded videos have accurate synchronized captions?"},
    "1.2.3":{p:"🎬 Media",h:"Is there an audio description or a full text alternative for prerecorded video?"}
  };
  function testGuide(sc){ return WCAG_TEST_GUIDE[sc]||{p:"🔍 Manual",h:"Test this criterion against the page per WCAG-EM, then record the result + evidence."}; }

  async function openReviewPanel(evalId){
    if(!evalId){ showToast("Generate an evaluation report first.","error"); return; }
    setReviewPanel({evalId:evalId,loading:true,queue:[],idx:0,note:""});
    try{
      var r=await fetch("/api/determination-review?evalId="+encodeURIComponent(evalId),{credentials:"include"});
      var d=await r.json();
      if(!r.ok) throw new Error((d&&d.error)||("HTTP "+r.status));
      setReviewPanel({evalId:evalId,loading:false,queue:(d.guidance||d.queue||[]),idx:0,total:d.total||0,reviewed:d.reviewed||0,autoPassed:(d.autoPassed||[]),autoPassCount:(typeof d.autoPassCount==="number"?d.autoPassCount:(d.autoPassed?d.autoPassed.length:0)),autoOpen:false,url:d.url||"",note:""});
    }catch(e){ setReviewPanel(null); showToast("Couldn't load the guidance queue: "+errText(e),"error"); }
  }
  async function submitReviewVerdict(verdict){
    var rp=reviewPanel; if(!rp||rp.submitting) return;
    var cur=rp.queue[rp.idx]; if(!cur) return;
    setReviewPanel(function(p){return p?Object.assign({},p,{submitting:true}):p});
    try{
      var r=await fetch("/api/determination-review",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
        body:JSON.stringify({evalId:rp.evalId,sc:cur.sc,verdict:verdict,note:rp.note||null})});
      var d=await r.json();
      if(!r.ok||!d.ok) throw new Error((d&&d.error)||("HTTP "+r.status));
      setReviewPanel(function(p){ if(!p)return p;
        var q=p.queue.slice(); q[p.idx]=Object.assign({},q[p.idx],{human:d.record.human,overturned:d.record.overturned});
        return Object.assign({},p,{queue:q,idx:Math.min(p.idx+1,q.length),submitting:false,note:"",reviewed:(d.progress&&d.progress.reviewed)||p.reviewed});
      });
    }catch(e){ setReviewPanel(function(p){return p?Object.assign({},p,{submitting:false}):p}); showToast("Save failed: "+errText(e),"error"); }
  }

  async function labRunPdfStructural(){
    if(!aiPdf)return;setPdfStructRes(null);setLabRunning(function(p){return Object.assign({},p,{pdfStruct:true})});
    try{var raw=atob(aiPdf.data);var checks={tagged:false,structTree:false,title:false,lang:false,bookmarks:false,metadata:false,hasForms:false,altText:false,headings:false,tabOrder:false,displayTitle:false,titleValue:"",langValue:"",producer:"",creator:"",author:"",subject:"",keywords:"",pageCount:0,pdfVersion:"",fileSize:aiPdf.data.length*0.75};
      checks.tagged=raw.indexOf("/MarkInfo")!==-1&&(raw.indexOf("/Marked true")!==-1||raw.indexOf("/Marked/true")!==-1);
      var tm=raw.match(/\/Title\s*\(([^)]*)\)/);checks.title=!!tm&&tm[1].trim().length>0;checks.titleValue=tm?tm[1]:"";
      var lm=raw.match(/\/Lang\s*\(([^)]*)\)/);checks.lang=!!lm&&lm[1].trim().length>0;checks.langValue=lm?lm[1]:"";
      checks.bookmarks=raw.indexOf("/Outlines")!==-1&&raw.indexOf("/Count")!==-1;
      checks.structTree=raw.indexOf("/StructTreeRoot")!==-1;
      checks.metadata=raw.indexOf("/Metadata")!==-1;
      checks.hasForms=raw.indexOf("/AcroForm")!==-1;
      checks.altText=raw.indexOf("/Alt ")!==-1||raw.indexOf("/Alt(")!==-1;
      checks.headings=(raw.match(/\/H[1-6]\b/g)||[]).length>0;
      checks.tabOrder=raw.indexOf("/Tabs /S")!==-1;
      checks.displayTitle=raw.indexOf("/DisplayDocTitle true")!==-1;
      var pm=raw.match(/\/Producer\s*\(([^)]*)\)/);checks.producer=pm?pm[1]:"";
      var cm=raw.match(/\/Creator\s*\(([^)]*)\)/);checks.creator=cm?cm[1]:"";
      var am=raw.match(/\/Author\s*\(([^)]*)\)/);checks.author=am?am[1]:"";
      var sm=raw.match(/\/Subject\s*\(([^)]*)\)/);checks.subject=sm?sm[1]:"";
      var km=raw.match(/\/Keywords\s*\(([^)]*)\)/);checks.keywords=km?km[1]:"";
      var pg=raw.match(/\/Type\s*\/Page[^s]/g);checks.pageCount=pg?pg.length:0;
      var vm=raw.match(/%PDF-(\d+\.\d+)/);checks.pdfVersion=vm?vm[1]:"?";
      var imgCount=(raw.match(/\/Subtype\s*\/Image/g)||[]).length;
      var altCount=(raw.match(/\/Alt\s*\(/g)||[]).length;
      checks.imgCount=imgCount;checks.altCount=altCount;checks.altCoverage=imgCount>0?Math.round(altCount/imgCount*100):100;
      checks.fontsEmbedded=raw.indexOf("/FontFile")!==-1||raw.indexOf("/FontFile2")!==-1||raw.indexOf("/FontFile3")!==-1;
      var issues=[];
      if(!checks.tagged)issues.push({wcag:"1.3.1",sev:"critical",cat:"Structure",desc:"PDF is not tagged. Screen readers cannot determine reading order or content structure.",fix:"Add tags using Acrobat Pro > Accessibility > Add Tags to Document",remediable:false});
      if(!checks.structTree)issues.push({wcag:"1.3.1",sev:"critical",cat:"Structure",desc:"No structure tree root. Document completely lacks semantic structure.",fix:"Run Make Accessible wizard in Acrobat Pro",remediable:false});
      if(!checks.title)issues.push({wcag:"2.4.2",sev:"major",cat:"Metadata",desc:"Document title is missing or empty.",fix:"Set document title in Properties",remediable:true,field:"title"});
      if(!checks.lang)issues.push({wcag:"3.1.1",sev:"major",cat:"Metadata",desc:"Document language is not set.",fix:"Set document language",remediable:true,field:"lang"});
      if(!checks.displayTitle&&checks.title)issues.push({wcag:"2.4.2",sev:"minor",cat:"Metadata",desc:"Document displays filename instead of title.",fix:"Set DisplayDocTitle to true",remediable:true,field:"displayTitle"});
      if(!checks.bookmarks&&checks.pageCount>4)issues.push({wcag:"2.4.5",sev:"minor",cat:"Navigation",desc:"Multi-page document ("+checks.pageCount+" pages) has no bookmarks.",fix:"Add bookmarks for section headings in Acrobat Pro",remediable:false});
      if(imgCount>0&&altCount<imgCount)issues.push({wcag:"1.1.1",sev:"critical",cat:"Images",desc:imgCount+" image(s) found, only "+altCount+" have alt text ("+checks.altCoverage+"%).",fix:"Add alt text to all images in Acrobat Pro",remediable:false});
      if(!checks.headings&&checks.pageCount>1)issues.push({wcag:"1.3.1",sev:"major",cat:"Structure",desc:"No heading tags (H1-H6) found.",fix:"Add heading hierarchy to tag tree",remediable:false});
      if(checks.hasForms&&!checks.tabOrder)issues.push({wcag:"2.4.3",sev:"major",cat:"Forms",desc:"Form fields present but tab order not set to structure.",fix:"Set tab order to Structure",remediable:false});
      if(!checks.author)issues.push({wcag:"",sev:"minor",cat:"Metadata",desc:"Author field is empty.",fix:"Set author in properties",remediable:true,field:"author"});
      if(!checks.subject)issues.push({wcag:"",sev:"minor",cat:"Metadata",desc:"Subject field is empty.",fix:"Set subject in properties",remediable:true,field:"subject"});
      var cr=issues.filter(function(i){return i.sev==="critical"}).length;var mj=issues.filter(function(i){return i.sev==="major"}).length;var mn=issues.filter(function(i){return i.sev==="minor"}).length;
      var remediableCount=issues.filter(function(i){return i.remediable}).length;
      var score=100-cr*25-mj*10-mn*3;if(score<0)score=0;
      var structResult={checks:checks,issues:issues,summary:{total:issues.length,critical:cr,major:mj,minor:mn,remediable:remediableCount},score:score};
      /* Set the client-side result immediately so the grid + metadata
         form render without waiting on the network. The metadata form
         (pdfRemForm) is always client-checks-driven. */
      setPdfStructRes(structResult);
      setPdfRemForm({title:checks.titleValue||"",lang:checks.langValue||"en-US",author:checks.author||"",subject:checks.subject||"",keywords:checks.keywords||"",displayTitle:checks.displayTitle});

      /* PR-5.15c: upgrade the issue list with the server-side pdfjs
         audit, which emits ONE issue per element (per image, per
         heading, per table, per form) with page + bbox geometry —
         the per-instance breakdown the client-side regex can't
         produce. We send the uploaded PDF's base64 bytes to
         /api/pdf-audit (bytes path) so the server doesn't need a
         fetchable URL. On success we swap in the granular issues and
         recompute the summary; the checks grid + score stay
         client-derived. Any failure (untagged PDF, parse error,
         network) silently keeps the client issue list. */
      try{
        var auditResp=await fetch("/api/pdf-audit",{method:"POST",credentials:"include",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({pdfBase64:aiPdf.data,name:aiPdf.name||"uploaded.pdf"})});
        if(auditResp.ok){
          var auditData=await auditResp.json();
          var sres=auditData&&auditData.results&&auditData.results[0];
          if(sres&&Array.isArray(sres.issues)&&sres.issues.length){
            /* Normalize server issues to the lab issue shape, preserving
               the PR-5.15a `instance` block for the grouped renderer +
               highlight overlay. */
            var serverIssues=sres.issues.map(function(si){
              return {wcag:si.wcag||"",sev:si.sev||"minor",cat:si.cat||"",
                desc:(si.desc||"")+(si.detail?" — "+si.detail:""),
                fix:si.fix||"",checkpoint:si.id||"",
                page:(si.page!=null)?si.page:(si.instance&&si.instance.page!=null?si.instance.page:null),
                instance:si.instance||null,remediable:false,engine:"pdf-audit"};
            });
            /* Keep the client's author/subject metadata issues — the
               server engine has no checkpoint for those two, and they're
               form-remediable so the user should still see them. */
            var metaKeep=issues.filter(function(ci){return ci.field==="author"||ci.field==="subject"});
            var merged=serverIssues.concat(metaKeep);
            var mcr=merged.filter(function(i){return i.sev==="critical"}).length;
            var mmj=merged.filter(function(i){return i.sev==="major"}).length;
            var mmn=merged.filter(function(i){return i.sev==="minor"}).length;
            structResult=Object.assign({},structResult,{
              issues:merged,
              summary:{total:merged.length,critical:mcr,major:mmj,minor:mmn,
                remediable:merged.filter(function(i){return i.remediable}).length},
              _serverAudited:true});
            setPdfStructRes(structResult);
          }
        }
      }catch(serverErr){/* keep client issues — server audit is an enhancement, not a hard dep */}

      if(_labSessionId.current&&labDocs.length>0){
        /* Roll the structural results up to the session-level summary so
           the Recent Sessions list / any summary consumer reflects the
           real score + counts. Previously only the per-doc results were
           written, leaving the top-level summary at its empty defaults
           (score:null, 0 issues) for a session that actually had 50. */
        var ssum=structResult.summary||{};
        patchPageSession({updateDocument:{docIndex:labSelDoc,structuralResults:structResult},
          summary:{score:structResult.score!=null?structResult.score:null,
            totalIssues:ssum.total||0,critical:ssum.critical||0,major:ssum.major||0,minor:ssum.minor||0,
            engines:["Structural"],docCount:labDocs.length},
          analyzedAt:new Date().toISOString()})
        /* Auto-pin signed-in users' sessions on audit completion. The
           TTL expiration is a garbage-collection mechanism for anonymous
           sessions; once a user is identified AND has invested the
           audit work, dropping their session 24h later is hostile.
           Anonymous users still see the manual button (in case the
           default TTL doesn't fit their workflow). */
        if(authUser&&labSession&&!labSession.saved){
          savePageSession().catch(function(){/* best effort — UI shows manual button as fallback */});
        }
      }
      return structResult;
    }catch(e){setPdfStructRes({error:errText(e)||"unknown"});return null;}
    finally{setLabRunning(function(p){var n=Object.assign({},p);delete n.pdfStruct;return n})}
  }

  /* errText — stringify any thrown thing into something a user can read.
     Normal Errors → .message. Strings → themselves. DOM Events (which
     are what <script>.onerror fires) → src URL if present, else a
     readable placeholder instead of "[object Event]". */
  function errText(e){
    if(!e)return "unknown";
    if(typeof e==="string")return e;
    if(e.message)return e.message;
    if(e.error&&e.error.message)return e.error.message;
    if(e.type==="error"&&e.target&&e.target.src)return "failed to load "+e.target.src;
    var s;try{s=String(e);}catch(_){s="";}
    if(!s||s==="[object Event]"||s==="[object Object]")return "load error (no detail)";
    return s;
  }

  /* Load pdf-lib from the self-hosted /vendor path (same origin, same
     SWA deploy) instead of cdnjs — a CDN outage was killing the entire
     remediation pipeline. Returns window.PDFLib on success, throws a
     real Error (not the unhelpful Event the <script> onerror fires
     when stringified) on failure so the calling progress panel can
     show "Failed to load pdf-lib (HTTP 503)" rather than "[object Event]". */
  async function loadPdfLib(){
    if(window.PDFLib)return window.PDFLib;
    var src="/vendor/pdf-lib/pdf-lib.min.js";
    await new Promise(function(res,rej){
      var s=document.createElement("script");
      s.src=src;
      s.onload=res;
      s.onerror=function(){rej(new Error("failed to load pdf-lib from "+src+" — check the bundle was deployed"));};
      document.head.appendChild(s);
    });
    if(!window.PDFLib)throw new Error("pdf-lib loaded but did not expose window.PDFLib");
    return window.PDFLib;
  }

  async function labRemediatePdf(){
    if(!aiPdf||!pdfRemForm)return;setPdfRemStatus("loading");
    try{
      if(!window.PDFLib){await loadPdfLib();}
      var PDFLib=window.PDFLib;var bytes=Uint8Array.from(atob(aiPdf.data),function(c){return c.charCodeAt(0)});
      var pdfDoc=await PDFLib.PDFDocument.load(bytes,{ignoreEncryption:true});
      if(pdfRemForm.title)pdfDoc.setTitle(pdfRemForm.title,{showInWindowTitleBar:true});
      if(pdfRemForm.lang)pdfDoc.setLanguage(pdfRemForm.lang);
      if(pdfRemForm.author)pdfDoc.setAuthor(pdfRemForm.author);
      if(pdfRemForm.subject)pdfDoc.setSubject(pdfRemForm.subject);
      if(pdfRemForm.keywords){var kw=typeof pdfRemForm.keywords==="string"?pdfRemForm.keywords.split(",").map(function(k){return k.trim()}):pdfRemForm.keywords;pdfDoc.setKeywords(kw)}
      pdfDoc.setProducer("PiARIS PA11Y Lab - pi-consulting.us");pdfDoc.setModificationDate(new Date());
      /* saveAsBase64 does the encode internally without spreading a
         giant Uint8Array into String.fromCharCode.apply() — the
         classic "Maximum call stack size exceeded" pattern that
         killed PR-6.6c on any PDF with embedded images >a few KB.
         useObjectStreams:false keeps markers (/Title, /Lang, etc.)
         visible to the regex-based auditPdfBase64 reader. */
      var b64=await pdfDoc.saveAsBase64({dataUri:false,useObjectStreams:false});
      setAiPdf({data:b64,name:aiPdf.name.replace(/\.pdf$/i,"-remediated.pdf")});
      setPdfStructRes(null);setAiPdfRes(null);setPdfRemStatus("done");
      showToast("PDF remediated! Re-run checks to verify.");
    }catch(e){setPdfRemStatus("error: "+(errText(e)||"unknown"))}
  }

  function labDownloadPdf(){
    if(!aiPdf)return;var bytes=atob(aiPdf.data);var arr=new Uint8Array(bytes.length);for(var i=0;i<bytes.length;i++)arr[i]=bytes.charCodeAt(i);
    var blob=new Blob([arr],{type:"application/pdf"});var a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=aiPdf.name||"paris-remediated.pdf";a.click();
  }

  async function labRunAllPdf(){
    /* Sequential: AI now works FROM the structural findings, so Structural
       must finish first and hand its result straight to analyzePdf (React
       state setters won't have flushed within this tick). */
    var sres=pdfEngines.structural?await labRunPdfStructural():pdfStructRes;
    /* AI Audit removed — the structural audit + AI alt-gen in remediation cover it. */
    /* Bug 2: drop the user on the Audit Report tab once results exist —
       otherwise they stay on the Document tab and have to discover the
       tab strip. Only advance if we actually produced findings. */
    if(sres&&!sres.error){setLabDocTab("report");setLabDocStage("audit");}
  }
  function labClearWeb(){setAiHtml("");setLabPreview("");setLabAxeRes(null);setLabHtmlcsRes(null);setAiRes(null);setAiFixed("");setLabUrl("");setLabFetchErr("")}
  function labClearPdf(){setAiPdf(null);setPdfStructRes(null);setAiPdfRes(null);setPdfRemForm(null);setPdfRemStatus("");}


  /* ═══ Two-tier navigation: Products → Sub-views ═══ */
  /* Per-product color identities — threads through nav, sub-nav, heroes, content */
  var PRODUCT_COLORS={
    watchtower:{c1:"#4F46E5",c2:"#0891B2",grad:"linear-gradient(135deg,#1E1B4B,#312E81,#4F46E5)",gradLight:"linear-gradient(135deg,#EEF2FF,#E0E7FF)",active:"#4F46E5",activeBg:"#EEF2FF",pill:"#818CF8",navBg:"linear-gradient(90deg,#EEF2FF 0%,#E0E7FF 50%,#ECFEFF 100%)"},
    compare:{c1:"#D97706",c2:"#EA580C",grad:"linear-gradient(135deg,#78350F,#92400E,#D97706)",gradLight:"linear-gradient(135deg,#FFFBEB,#FEF3C7)",active:"#D97706",activeBg:"#FFFBEB",pill:"#FBBF24",navBg:"linear-gradient(90deg,#FFFBEB 0%,#FEF3C7 50%,#FFF7ED 100%)"},
    docmaya:{c1:"#059669",c2:"#0D9488",grad:"linear-gradient(135deg,#064E3B,#065F46,#059669)",gradLight:"linear-gradient(135deg,#ECFDF5,#D1FAE5)",active:"#059669",activeBg:"#ECFDF5",pill:"#6EE7B7",navBg:"linear-gradient(90deg,#ECFDF5 0%,#D1FAE5 50%,#CCFBF1 100%)"},
    pally:{c1:"#8B5CF6",c2:"#EC4899",grad:"linear-gradient(135deg,#7C3AED,#8B5CF6,#EC4899)",gradLight:"linear-gradient(135deg,#F5F3FF,#EDE9FE)",active:"#8B5CF6",activeBg:"#F5F3FF",pill:"#C4B5FD",navBg:"linear-gradient(90deg,#F5F3FF 0%,#EDE9FE 50%,#FCE7F3 100%)"}
  };
  /* ═══════════════════════════════════════════════════════════════
   * MODULE: product-registry                      [status: ready]
   * PURPOSE: Top-level product definitions (Watchtower, Delta Recon,
   *          DocMaya, PA11Y Lab) and their sub-modules/color themes
   *
   * READS:     None (pure data)
   * WRITES:    None
   * CALLS:     None
   * EFFECTS:   None
   * EMITS:     None
   *
   * EXPORTS:   { PRODUCTS, ADMIN_MODS, PRODUCT_COLORS, getProductForMod }
   * IMPORTS:   None
   *
   * CLOSURE:   None
   * ENTANGLED: None — CLEAN extraction candidate.
   *            Suggested target: /app/src/shared/products/registry.js
   * ═══════════════════════════════════════════════════════════════ */

  var PRODUCTS=[
    {id:"watchtower",l:"Watchtower",i:"\u{1F4E1}",subs:[
      {id:"dashboard",l:"Dashboard",i:"\u{1F3E0}"},{id:"bulkflow",l:"Bulk Workflow",i:"\u{1F5C2}️"},{id:"webAudit",l:"Web Audit",i:"\u{1F310}"},{id:"docAudit",l:"Doc Audit",i:"\u{1F4C4}"},
      {id:"remediation",l:"Remediation",i:"\u{1F527}"},{id:"reports",l:"Reports",i:"\u{1F4CA}"},{id:"import",l:"Import",i:"\u{1F4C2}"}]},
    {id:"compare",l:"Delta Recon",i:"\u{1F504}",subs:[]},
    {id:"docmaya",l:"DocMaya",i:"\u{1F4DD}",subs:[
      {id:"dmOverview",l:"Overview",i:"\u{1F4CA}"},{id:"dmSetup",l:"Setup & Deploy",i:"\u{1F680}"},{id:"dmConversions",l:"Conversions",i:"\u{1F504}"},{id:"dmMonitoring",l:"Monitoring",i:"\u{1F4C8}"}]},
    {id:"pally",l:"PA11Y Lab",i:"\u{1F9EA}",subs:[
      {id:"aiLabWeb",l:"Web Audit",i:"\u{1F310}"},{id:"aiLabDoc",l:"Doc Audit",i:"\u{1F4C4}"},{id:"validator",l:"PDF Quick-Check",i:"\u{1F50D}"}]}
  ];
  var ADMIN_MODS=[{id:"licenses",l:"Licenses",i:"\u{1F4B3}"}];
  /* Product routing: determine active product from mod */
  function getProductForMod(m){
    for(var pi=0;pi<PRODUCTS.length;pi++){
      if(PRODUCTS[pi].id===m)return PRODUCTS[pi].id;
      for(var si=0;si<PRODUCTS[pi].subs.length;si++){
        if(PRODUCTS[pi].subs[si].id===m)return PRODUCTS[pi].id;
      }
    }
    for(var ai=0;ai<ADMIN_MODS.length;ai++){if(ADMIN_MODS[ai].id===m)return"admin"}
    return"watchtower";
  }
  var activeProduct=getProductForMod(mod);
  var activeProductObj=PRODUCTS.find(function(p){return p.id===activeProduct})||PRODUCTS[0];
  var pColors=PRODUCT_COLORS[activeProduct]||PRODUCT_COLORS.watchtower;
  function switchProduct(pid){
    var prod=PRODUCTS.find(function(p){return p.id===pid});
    if(!prod)return;
    if(prod.subs.length>0){setMod(prod.subs[0].id)}
    else if(pid==="compare"){setMod("compare")}
    else{setMod(pid)}
    setSel(null);setDashDrill&&setDashDrill(null);
  }
  /* Legacy flat MODS for backward compat (mobile drawer) */
  var MODS=[{id:"dashboard",l:"Dashboard",i:"\u{1F3E0}"},{id:"audit",l:"Audit Report",i:"\u{1F4CB}"},{id:"webAudit",l:"Web Audit",i:"\u{1F310}"},{id:"docAudit",l:"Doc Audit",i:"\u{1F4C4}"},{id:"compare",l:"Delta Recon",i:"\u{1F504}"},{id:"aiLabWeb",l:"PA11Y Web",i:"\u{1F310}"},{id:"aiLabDoc",l:"PA11Y Doc",i:"\u{1F4C4}"},{id:"validator",l:"PDF Quick-Check",i:"\u{1F50D}"},{id:"remediation",l:"Remediation",i:"\u{1F527}"},{id:"reports",l:"Reports",i:"\u{1F4CA}"},{id:"licenses",l:"Licenses",i:"\u{1F4B3}"}];

  /* ═══ RENDER ═══ */

  /* ── Export dropdown items (CSV / JSON / Save) ── */
  var csvPayload=function(){return{scan:{domain:domain},webPages:wp.map(function(p){return{url:p.url,p:p.p,n:p.n,d:p.d,depth:p.d,title:p.n,status:p.status,issues:p.issues.map(function(i){return{engine:i.engine,ruleId:i.id,axeId:i.id,wcag:i.wcag,sev:i.sev,desc:i.desc,count:i.count,el:i.el,fix:i.fix}}),issueCount:p.totalIssues,criticalCount:p.critCount}}),documents:dc.map(function(d){return{name:d.name,url:d.url,ext:d.ext,department:d.department,functionalType:d.functionalType,pageCount:d.pageCount,isTagged:d.tagged,riskLevel:d.riskLevel}})}};
  var jsonPayload=function(){var scanObj={domain:domain};if(scanMeta){scanObj.templateTokens=scanMeta.templateTokens;scanObj.templateSamples=scanMeta.templateSamples;}return{scan:scanObj,webPages:wp.map(function(p){return{url:p.url,p:p.p,n:p.n,d:p.d,depth:p.d,title:p.n,status:p.status,screenshot:p.screenshot||"",dom:p.dom||"",issues:p.issues.map(function(i){return{engine:i.engine,ruleId:i.id,axeId:i.id,wcag:i.wcag,wcagName:i.info?i.info.n:"",sev:i.sev,desc:i.desc,count:i.count,el:i.el,fix:i.fix,priority:i.priority,impactedGroups:i.impactedGroups}}),issueCount:p.totalIssues,criticalCount:p.critCount}}),documents:dc.map(function(d){return{name:d.name,url:d.url,ext:d.ext,department:d.department,functionalType:d.functionalType,pageCount:d.pageCount,fileSize:d.fileSize,isTagged:d.tagged,riskLevel:d.riskLevel,riskScore:d.riskScore,accessibilityIssues:d.issues?d.issues.map(function(i){return{wcag:i.wcag,sev:i.sev,issue:i.desc,fix:i.fix}}):[]}})}};

  /* Close export dropdown on outside click */
  var exportRef=useRef(null);
  useEffect(function(){
    if(!exportOpen)return;
    function handleClick(ev){if(exportRef.current&&!exportRef.current.contains(ev.target))setExportOpen(false)}
    document.addEventListener("mousedown",handleClick);
    return function(){document.removeEventListener("mousedown",handleClick)};
  },[exportOpen]);

  /* ── NAV BAR — single row: [Logo] [Tabs / Hamburger] [spacer] [Actions] [Auth] ── */
  var isAdminNav=authUser&&((authUser.roles||[]).indexOf("admin")>-1||((authUser.name||"").toLowerCase().indexOf("@pi-consulting.us")>-1||(authUser.name||"").toLowerCase().indexOf("@piconsultants.us")>-1)||authUser.tier==="admin");
  var nav = E("nav",{"aria-label":"PiARIS main navigation",style:{display:"flex",alignItems:"center",padding:"8px 20px",borderBottom:"1.5px solid "+P.bdr,background:P.card,position:"sticky",top:0,zIndex:100,gap:8}},

    /* LEFT — Logo (always). Clicking routes to home (paris.pi-consulting.us root)
       per standard convention. Previously set mod="dashboard" which kept users
       inside the app shell — that violated the "logo = home" expectation. */
    E("div",Object.assign({style:{display:"flex",alignItems:"center",gap:10,cursor:"pointer",flexShrink:0},"aria-label":"Return to home"},kbNav(function(){goHome();setDashDrill(null)})),
      E(PiArisLockup,{size:"small"})),

    /* LEFT-CENTER — Home button (only in app view) */
    view==="app"?E("button",{onClick:function(){goHome();setDashDrill(null)},title:"Return to home","aria-label":"Return to home",style:{padding:"5px 10px",borderRadius:7,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:10,fontWeight:600,flexShrink:0,display:"flex",alignItems:"center",gap:4}},
      E("svg",{width:12,height:12,viewBox:"0 0 24 24",fill:"none",stroke:"currentColor",strokeWidth:2.5,strokeLinecap:"round",strokeLinejoin:"round"},
        E("path",{d:"M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"})),
      "Home"):null,

    /* CENTER — Product tabs with product-specific colors.
       UI product ids → entitlement product keys (api/shared/entitlements.js).
       Tabs the user isn't licensed for stay clickable (so the upgrade CTA
       inside the product is reachable) but render dimmed with a
       ‘not licensed’ tooltip. Admins always see all tabs at full opacity. */
    view==="app"&&!isMobile?E("div",{"aria-label":"PiARIS products",role:"navigation",style:{display:"flex",gap:3,alignItems:"center",flex:"0 1 auto",overflow:"hidden"}},
      (function(){
        var PROD_KEY={watchtower:"watchtower",compare:"deltaRecon",docmaya:"docMaya",pally:"pa11yLab"};
        return PRODUCTS.map(function(p){
        var pc=PRODUCT_COLORS[p.id]||PRODUCT_COLORS.watchtower;
        var isActive=activeProduct===p.id;
        var entKey=PROD_KEY[p.id];
        var licensed = !entitlements
          ? true
          : (entitlements.admin || (entitlements.products && entitlements.products[entKey]));
        return E("button",{key:p.id,onClick:function(){switchProduct(p.id)},
          "aria-current":isActive?"page":undefined,
          "aria-label":licensed?undefined:(p.l+" — not licensed; click to view upgrade options"),
          title:licensed?undefined:"Not licensed — click to view upgrade options",
          style:{padding:"5px 14px",borderRadius:10,border:isActive?"1.5px solid "+pc.c1+"40":"1.5px solid transparent",
            background:isActive?pc.activeBg:"transparent",color:isActive?pc.c1:P.slateM,
            fontSize:10,fontWeight:isActive?800:600,cursor:"pointer",transition:"all .15s",whiteSpace:"nowrap",
            fontFamily:"inherit",minHeight:32,display:"flex",alignItems:"center",gap:4,
            opacity:licensed?1:.55}},
          E("span",{"aria-hidden":"true"},p.i),p.l)});
      })(),
      isDemo?E("span",{style:{padding:"3px 8px",borderRadius:4,background:P.amberLt,border:"1px solid "+P.amber+"40",color:P.amber,fontSize:9,fontWeight:800,letterSpacing:".08em",textTransform:"uppercase",marginLeft:4,whiteSpace:"nowrap"}},"DEMO DATA"):null
    ):null,

    /* Admin gear — visible on home + app, admin only */
    null,/* admin gear moved to the user-actions cluster (right) */

    /* SPACER */
    E("div",{style:{flex:1}}),

    /* RIGHT — Actions dropdown (app view only) */
    view==="app"?E("div",{ref:exportRef,style:{position:"relative",flexShrink:0}},
      E("button",{onClick:function(){setExportOpen(!exportOpen)},"aria-haspopup":"true","aria-expanded":exportOpen?"true":"false","aria-label":"Actions menu",style:{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:8,border:"1.5px solid "+P.bdr,background:exportOpen?P.bgS:P.card,color:P.slateM,fontSize:10,fontWeight:700}},
        E("svg",{width:12,height:12,viewBox:"0 0 24 24",fill:"none",stroke:"currentColor",strokeWidth:2.5,strokeLinecap:"round",strokeLinejoin:"round"},
          E("circle",{cx:12,cy:5,r:1}),E("circle",{cx:12,cy:12,r:1}),E("circle",{cx:12,cy:19,r:1})),
        "Actions"),
      exportOpen?E("div",{className:"exportDrop"},
        E("button",{onClick:function(){saveScan();setExportOpen(false)}},
          E("span",{style:{fontSize:13}},"\uD83D\uDCBE"),E("span",null,"Save Scan")),
        wp.length>0?E("button",{onClick:function(){downloadAsCsv(csvPayload());setExportOpen(false)}},
          E("span",{style:{fontSize:13}},"\u{1F4E5}"),E("span",null,"Export CSV")):null,
        wp.length>0?E("button",{onClick:function(){downloadAsJson(jsonPayload());setExportOpen(false)}},
          E("span",{style:{fontSize:13}},"\u{1F4E6}"),E("span",null,"Export JSON")):null,
        E("div",{style:{height:1,background:P.bdr,margin:"4px 8px"}}),
        canInstall?E("button",{onClick:function(){installPwa();setExportOpen(false)}},
          E("span",{style:{fontSize:13}},"\u{1F4F2}"),E("span",null,"Install App")):null,
        isPwa?E("button",{disabled:true,style:{opacity:.5}},
          E("span",{style:{fontSize:13}},"\u2713"),E("span",null,"PWA Active")):null
      ):null
    ):null,

    /* RIGHT — Hamburger trigger (mobile only, app view only) */
    view==="app"&&isMobile?E("button",{onClick:function(){setNavOpen(true)},"aria-label":"Open module menu","aria-expanded":navOpen?"true":"false",style:{display:"flex",alignItems:"center",justifyContent:"center",width:36,height:36,borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,flexShrink:0,padding:0}},
      E("svg",{width:18,height:18,viewBox:"0 0 24 24",fill:"none",stroke:P.slateM,strokeWidth:2.5,strokeLinecap:"round"},
        E("line",{x1:3,y1:6,x2:21,y2:6}),E("line",{x1:3,y1:12,x2:21,y2:12}),E("line",{x1:3,y1:18,x2:21,y2:18}))
    ):null,

    /* RIGHT — Auth controls (always visible) */
    E("div",{style:{display:"flex",alignItems:"center",flexShrink:0}},
      authUser?E("div",{style:{display:"flex",alignItems:"center",gap:8,padding:"4px 6px 4px 4px",borderRadius:10,background:P.bg,border:"1.5px solid "+P.emerald+"30"}},
        E("div",{style:{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0}},
          (authUser.name||"?").charAt(0).toUpperCase()),
        !isMobile?E("div",{style:{fontSize:11,lineHeight:1.3,minWidth:0}},
          E("div",{style:{fontWeight:700,color:P.slate,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:120}},authUser.name),
          E("div",{style:{display:"flex",alignItems:"center",gap:4}},
            E("span",{style:{display:"inline-block",width:5,height:5,borderRadius:"50%",background:P.emerald,flexShrink:0}}),
            E("span",{style:{color:P.emeraldDk||P.emerald,fontSize:9,fontWeight:700}},"Signed in"),
            E("span",{style:{color:P.slateM,fontSize:9}},"\u00B7"),
            /* PR-5.12: don't render the literal "free" while tier
               is still loading from /api/user. The principal lands
               from /.auth/me first (no tier field) and the SPA used
               to flash "Signed in \u00B7 free" for ~50\u2013200ms until
               /api/user resolved with the actual tier. For an
               admin that read like the admin was looking at a free
               account \u2014 exactly the regression you flagged. Show
               an ellipsis while it's unknown. */
            E("span",{style:{color:P.slateM,fontSize:9,fontWeight:600,textTransform:"capitalize"}},
              authUser.tierLabel || authUser.tier || "\u2026"))):null,
        E("button",{onClick:toggleDarkMode,title:darkMode?"Switch to light mode":"Switch to dark mode","aria-label":"Toggle dark mode",style:{padding:"4px 8px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:12,cursor:"pointer",color:P.slateM,whiteSpace:"nowrap",marginLeft:2}},darkMode?"\u2600\uFE0F":"\u{1F319}"),
        isAdminNav?E("button",{onClick:function(){setView("app");setMod("licenses");setSel(null)},title:"Admin settings","aria-label":"Admin settings",style:{padding:"3px 8px",borderRadius:6,border:"1.5px solid "+(activeProduct==="admin"?P.pri:P.bdr),background:activeProduct==="admin"?P.priLt:P.card,fontSize:16,cursor:"pointer",color:activeProduct==="admin"?P.pri:P.slateM,whiteSpace:"nowrap",marginLeft:2,lineHeight:1}},"⚙️"):null,
        E("button",{onClick:function(){window.location.href="/logout"},title:"Sign out","aria-label":"Sign out",style:{padding:"4px 8px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:9,fontWeight:600,cursor:"pointer",color:P.slateM,whiteSpace:"nowrap",marginLeft:2}},"Sign Out")
      ):authLoading?E("div",{style:{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:10,border:"1.5px solid "+P.bdr,background:P.bg}},
        E("span",{style:{fontSize:11,color:P.slateL}},"\u23f3 Checking sign\u2011in\u2026")
      ):
      E("button",{onClick:function(){window.location.href="/login"},style:{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",boxShadow:"0 2px 12px "+P.pri+"30",whiteSpace:"nowrap",transition:"transform .1s"},onMouseEnter:function(e){e.currentTarget.style.transform="scale(1.03)"},onMouseLeave:function(e){e.currentTarget.style.transform="scale(1)"}},
        E("svg",{width:14,height:14,viewBox:"0 0 24 24",fill:"none",stroke:"currentColor",strokeWidth:2.5,strokeLinecap:"round",strokeLinejoin:"round",style:{flexShrink:0}},
          E("path",{d:"M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"}),
          E("polyline",{points:"10 17 15 12 10 7"}),
          E("line",{x1:15,y1:12,x2:3,y2:12})),
        "Sign In")
    )
  );

  /* ── PRODUCT SUB-NAV — themed per active product ──
     Hidden on the Admin panel (mod==="licenses") because the admin module
     ships its own tablist landmark and the Watchtower sub-nav is irrelevant
     and visually noisy in that context. */
  var subNav = view==="app"&&mod!=="licenses"&&activeProductObj&&activeProductObj.subs.length>0?E("div",{"aria-label":activeProductObj.l+" sub-navigation",role:"navigation",
    className:"subNav",
    style:{display:"flex",gap:4,padding:"6px 20px",
      background:darkMode?P.bgS:pColors.navBg,
      borderBottom:"1.5px solid "+pColors.c1+"15",position:"sticky",top:52,zIndex:99,alignItems:"center",
      overflowX:"auto",WebkitOverflowScrolling:"touch"}},
    /* Product label with colored icon badge */
    E("div",{style:{display:"flex",alignItems:"center",gap:6,marginRight:6}},
      E("div",{style:{width:20,height:20,borderRadius:6,background:"linear-gradient(135deg,"+pColors.c1+","+pColors.c2+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"#fff",flexShrink:0},"aria-hidden":"true"},activeProductObj.i),
      E("span",{style:{fontSize:10,fontWeight:800,color:pColors.c1,textTransform:"uppercase",letterSpacing:".06em"}},activeProductObj.l)),
    /* Divider */
    E("div",{style:{width:1,height:16,background:pColors.c1+"20",marginRight:2},"aria-hidden":"true"}),
    /* Sub-nav pills — product-colored active state */
    activeProductObj.subs.map(function(s){
      var active=mod===s.id;
      return E("button",{key:s.id,onClick:function(){setMod(s.id);setSel(null)},"aria-current":active?"page":undefined,
        style:{padding:"5px 14px",borderRadius:10,border:active?"1.5px solid "+pColors.c1+"30":"1.5px solid transparent",
          background:active?"linear-gradient(135deg,"+pColors.c1+","+pColors.c2+")":"transparent",
          color:active?"#fff":P.slateM,
          fontSize:10.5,fontWeight:active?700:500,cursor:"pointer",transition:"all .15s",whiteSpace:"nowrap",
          fontFamily:"inherit",minHeight:32,display:"flex",alignItems:"center",gap:4,
          boxShadow:active?"0 2px 8px "+pColors.c1+"25":"none"}},
        E("span",{"aria-hidden":"true"},s.i),s.l);
    }),
    /* Admin quick-access within sub-nav */
    activeProduct==="watchtower"?E("div",{style:{marginLeft:"auto",display:"flex",gap:4}},
      ADMIN_MODS.map(function(a){
        return E("button",{key:a.id,onClick:function(){setMod(a.id);setSel(null)},
          style:{padding:"5px 12px",borderRadius:10,border:mod===a.id?"1.5px solid #64748B30":"1.5px solid transparent",
            background:mod===a.id?"#64748B":"transparent",color:mod===a.id?"#fff":"#64748B",
            fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",minHeight:32,
            display:"flex",alignItems:"center",gap:4}},
          E("span",{"aria-hidden":"true"},a.i),a.l);
      })):null
  ):null;

  /* ── MOBILE NAV DRAWER (hamburger menu) ── */
  var navDrawer = navOpen?E("div",null,
    E("div",{className:"navOverlay",onClick:function(){setNavOpen(false)}}),
    E("div",{className:"navDrawer",role:"dialog","aria-label":"Module navigation","aria-modal":"true"},
      E("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px 12px",borderBottom:"1px solid "+P.bdr,marginBottom:8}},
        E("div",{style:{fontSize:13,fontWeight:800,color:P.slate}},"Modules"),
        E("button",{onClick:function(){setNavOpen(false)},"aria-label":"Close menu",style:{width:32,height:32,borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:P.slateM,padding:0}},"\u2715")),
      MODS.map(function(m){return E("button",{key:m.id,onClick:function(){setMod(m.id);setSel(null);setNavOpen(false)},style:{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"12px 20px",border:"none",background:mod===m.id?P.priLt:"transparent",color:mod===m.id?P.pri:P.slate,fontSize:12,fontWeight:mod===m.id?700:500,textAlign:"left",cursor:"pointer",borderLeft:mod===m.id?"3px solid "+P.pri:"3px solid transparent",fontFamily:"inherit"}},
        E("span",{style:{fontSize:16,width:22,textAlign:"center"}},m.i),m.l)}),
      isDemo?E("div",{style:{margin:"12px 16px",padding:"6px 12px",borderRadius:6,background:P.amberLt,border:"1px solid "+P.amber+"40",color:P.amber,fontSize:10,fontWeight:700,textAlign:"center"}},"DEMO DATA"):null
    )
  ):null;

  /* HOME */
  var homePage = E("div",{style:{maxWidth:1100,margin:"0 auto",width:"100%"}},
    /* PWA install banner (mobile only, when installable) */
    canInstall&&isMobile?E("div",{className:"pwa-banner",style:{marginBottom:12,padding:"12px 16px",borderRadius:12,background:"linear-gradient(135deg,#4F46E5,#7C3AED)",color:"#fff",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}},
      E("div",{style:{flex:1,minWidth:180}},
        E("div",{style:{fontSize:13,fontWeight:700}},"\u{1F4F2} Install PiARIS on your device"),
        E("div",{style:{fontSize:10.5,opacity:.85,marginTop:2}},"Audit accessibility offline \u2022 Works like a native app")),
      E("button",{onClick:installPwa,style:{padding:"8px 18px",borderRadius:8,border:"2px solid rgba(255,255,255,.5)",background:"rgba(255,255,255,.15)",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}},"Install"),
      E("button",{onClick:function(){setCanInstall(false)},"aria-label":"Dismiss",style:{background:"none",border:"none",color:"rgba(255,255,255,.6)",fontSize:16,cursor:"pointer",padding:"4px"}},"\u2715")
    ):null,
    /* ── HERO — light theme, logo blends seamlessly via multiply ── */
    E("div",{style:{position:"relative",overflow:"hidden",borderRadius:20,background:darkMode?"linear-gradient(160deg, #0F172A 0%, #1E1B4B 40%, #172040 70%, #0D1F2D 100%)":"linear-gradient(160deg, #F8FAFF 0%, #EEF2FF 40%, #E0F2FE 70%, #ECFDF5 100%)",border:"1.5px solid "+P.bdr,padding:"30px 40px 26px",marginBottom:18}},
      /* Decorative A11Y watermarks */
      E("div",{style:{position:"absolute",top:0,left:0,right:0,bottom:0,overflow:"hidden",pointerEvents:"none"}},
        E("div",{style:{position:"absolute",top:10,right:30,fontSize:110,fontWeight:900,color:P.pri,opacity:.04}},"A11Y"),
        E("div",{style:{position:"absolute",bottom:-8,left:20,fontSize:75,fontWeight:900,color:P.accent,opacity:.04,transform:"rotate(-8deg)"}},"WCAG"),
        E("div",{style:{position:"absolute",top:50,right:180,fontSize:48,fontWeight:900,color:P.emerald,opacity:.04,transform:"rotate(6deg)"}},"508"),
        E("div",{style:{position:"absolute",bottom:30,right:60,fontSize:40,fontWeight:900,color:P.pri,opacity:.03,transform:"rotate(-4deg)"}},"PDF/UA")),
      /* Subtle decorative accent line at top */
      E("div",{style:{position:"absolute",top:0,left:0,right:0,height:4,background:"linear-gradient(90deg, "+P.pri+", "+P.accent+", "+P.emerald+")",borderRadius:"20px 20px 0 0"}}),
      /* Content */
      E("div",{style:{position:"relative",zIndex:2,textAlign:"center"}},
        /* Logo */
        E("div",{style:{display:"flex",justifyContent:"center",marginBottom:10}},
          E(PiArisLockup,{size:"large",home:true})),
        E("div",{style:{fontSize:11,color:P.slateM,fontWeight:600,letterSpacing:".12em",textTransform:"uppercase",marginBottom:12}},APP_FULL),
        E("h1",{style:{fontSize:30,fontWeight:900,margin:"0 0 12px",color:P.slate,lineHeight:1.25,letterSpacing:"-.3px"}},
          "Web accessibility compliance. Automated."),
        E("p",{style:{fontSize:13.5,color:P.slateM,lineHeight:1.7,maxWidth:580,margin:"0 auto 14px"}},
          "Three products, one platform. Scan your web pages, convert your PDFs, and prove compliance \u2014 no vendors, no waiting, no overlays."),
        /* Standards pills */
        E("div",{style:{display:"flex",gap:6,justifyContent:"center",flexWrap:"wrap"}},
          ["WCAG 2.2 AA","Section 508","PDF/UA","ADA Title II","EAA EN 301 549"].map(function(s){
            return E("span",{key:s,style:{padding:"5px 14px",borderRadius:20,background:P.card,fontSize:10.5,fontWeight:600,color:P.pri,border:"1.5px solid "+P.pri+"20",boxShadow:"0 1px 4px "+P.pri+"08"}},s)})),
        E("div",{style:{fontSize:10,color:P.slateL,marginTop:14}},"A "+PI_NAME+" Product \u00B7 "+PI_WEB))),

    /* ═══════════════════════════════════════════════
       FREE SCAN HERO — PR-K.12 P1B
       Full-width call-to-action immediately under the brand hero. Sits
       above the product showcases so the primary funnel (request a free
       scan) gets the most prominent slot on the signed-out home page.
       Indigo to cyan gradient matches the PR-K.11 banner so visitors who
       scroll past the top notice immediate continuity. Staggered
       fade-in respects prefers-reduced-motion.
       ═══════════════════════════════════════════════ */
    E("section",{
      "aria-labelledby":"piaris-free-scan-hero-title",
      style:{
        position:"relative",
        background:"linear-gradient(135deg, #4338CA 0%, #6366F1 35%, #06B6D4 100%)",
        borderRadius:24,
        padding: isMobile ? "24px 22px" : "36px 56px",
        marginBottom:20,
        overflow:"hidden",
        color:"#FFFFFF",
        boxShadow:"0 24px 60px rgba(67, 56, 202, 0.25)"
      }
    },
      E("style",null,
        "@keyframes piaris-hero-fade { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }" +
        ".piaris-hero-f1 { animation: piaris-hero-fade 0.6s ease-out both; }" +
        ".piaris-hero-f2 { animation: piaris-hero-fade 0.6s ease-out 0.10s both; }" +
        ".piaris-hero-f3 { animation: piaris-hero-fade 0.6s ease-out 0.20s both; }" +
        ".piaris-hero-f4 { animation: piaris-hero-fade 0.6s ease-out 0.30s both; }" +
        ".piaris-hero-f5 { animation: piaris-hero-fade 0.6s ease-out 0.40s both; }" +
        ".piaris-hero-cta { transition: transform 0.18s ease, box-shadow 0.18s ease; }" +
        ".piaris-hero-cta:hover { transform: translateY(-2px); box-shadow: 0 14px 36px rgba(0,0,0,0.28); }" +
        "@media (prefers-reduced-motion: reduce) { .piaris-hero-f1,.piaris-hero-f2,.piaris-hero-f3,.piaris-hero-f4,.piaris-hero-f5,.piaris-hero-cta { animation: none !important; transition: none !important; transform: none !important; } }"
      ),
      /* Decorative background circles — aria-hidden so screen
         readers skip them entirely. */
      E("div",{"aria-hidden":"true",style:{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}},
        E("div",{style:{position:"absolute",top:-110,right:-80,width:300,height:300,borderRadius:"50%",background:"rgba(255,255,255,0.08)"}}),
        E("div",{style:{position:"absolute",bottom:-130,left:-100,width:280,height:280,borderRadius:"50%",background:"rgba(255,255,255,0.06)"}}),
        E("div",{style:{position:"absolute",top:"40%",left:"50%",width:140,height:140,borderRadius:"50%",background:"rgba(255,255,255,0.04)",transform:"translate(-50%,-50%)"}})
      ),
      /* Content layer */
      E("div",{style:{position:"relative",zIndex:2,textAlign:"center",maxWidth:720,margin:"0 auto"}},
        /* "PiARIS · Free Scan" eyebrow removed per request (#8) */
        E("h2",{id:"piaris-free-scan-hero-title",className:"piaris-hero-f2",style:{fontSize:isMobile?26:40,fontWeight:900,lineHeight:1.12,margin:"0 0 16px",letterSpacing:"-0.02em"}},
          "Get a free accessibility scan of your site."),
        E("p",{className:"piaris-hero-f3",style:{fontSize:isMobile?14:16,lineHeight:1.6,margin:"0 0 28px",opacity:0.95}},
          "A Pi Consulting analyst runs a WCAG 2.2 AA scan of up to 50 pages across your domain and its subdomains — including any PDFs found within those pages. Remediation-ready report in your inbox within 48 hours. No credit card. No sales calls unless you ask."),
        E("div",{className:"piaris-hero-f4",style:{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap",marginBottom:36}},
          E("a",{href:"/free-scan",className:"piaris-hero-cta",
            style:{background:"#FFFFFF",color:"#4338CA",padding:"16px 34px",borderRadius:999,fontWeight:900,fontSize:15,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:8,boxShadow:"0 8px 28px rgba(0,0,0,0.22)"}},
            "Request my free scan ",
            E("span",{"aria-hidden":"true",style:{fontSize:17,fontWeight:900}},"→"))
        ),
        /* Three big-number cards reinforce the value prop. */
        E("div",{className:"piaris-hero-f5",style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3, 1fr)",gap:14}},
          [
            { num:"50", unit:"pages", label:"across your domain + subdomains" },
            { num:"48",  unit:"hours", label:"to receive your full report" },
            { num:"$0",  unit:"",      label:"no credit card or sales calls" }
          ].map(function(s,i){
            return E("div",{key:i,style:{padding:"18px 14px",background:"rgba(255,255,255,0.12)",borderRadius:14,border:"1px solid rgba(255,255,255,0.18)"}},
              E("div",{style:{display:"flex",alignItems:"baseline",justifyContent:"center",gap:4,lineHeight:1}},
                E("span",{style:{fontSize:38,fontWeight:900,letterSpacing:"-0.025em"}},s.num),
                s.unit?E("span",{style:{fontSize:14,fontWeight:700,opacity:0.85}},s.unit):null
              ),
              E("div",{style:{fontSize:11.5,opacity:0.88,marginTop:6,fontWeight:500,lineHeight:1.45}},s.label)
            );
          })
        )
      )
    ),

    /* ═══════════════════════════════════════════════
       FOUR PRODUCT SHOWCASES — modular, animated, ADA-compliant
       Imported from ./shared/products
       ═══════════════════════════════════════════════ */
    E("div",{style:{display:"flex",flexDirection:"column",gap:18,marginBottom:28}},
      E("div",{"data-story-card":"pally"},
        E(Pa11yLabShowcase,{
          ctaLabel:authUser?"Open PA11Y Lab":"Try PA11Y Lab Free",
          onCta:function(){setView("app");setMod("aiLabWeb")}
        })),
      E("div",{"data-story-card":"watchtower"},
        E(WatchtowerShowcase,{
          ctaLabel:authUser?"Open Watchtower":"Sign In to Start",
          onCta:authUser?function(){setView("app");setMod("dashboard")}:function(){window.location.href="/login"},
          secondaryLabel:"Import Scan",
          onSecondary:authUser?function(){fileInputRef.current&&fileInputRef.current.click()}:null
        })),
      E("div",{"data-story-card":"compare"},
        E(DeltaReconShowcase,{
          ctaLabel:authUser?"Open Delta Recon":"Sign In to Start",
          onCta:authUser?function(){setView("app");setMod("compare")}:function(){window.location.href="/login"}
        })),
      E("div",{"data-story-card":"docmaya"},
        E(DocMayaShowcase,{
          ctaLabel:authUser?"Manage DocMaya":"Sign In to Start",
          onCta:authUser?function(){setView("app");setMod("licenses")}:function(){window.location.href="/login"}
        }))
    ),

/* The demo "Quick Audit" panel was removed; CSV-list loading moved into the
       Watchtower Load-Scan modal (load a session OR a CSV of URLs/PDFs). The
       hidden file input stays — the Watchtower card's "Import Scan"
       secondary triggers it via fileInputRef. */
    E("input",{ref:fileInputRef,type:"file",accept:".json,.csv",onChange:importScanFile,style:{display:"none"}}),

        /* ── CUSTOMER FUNNEL ── */
    E("div",{style:{textAlign:"center",marginBottom:24,padding:"20px 16px",borderRadius:14,background:"linear-gradient(135deg,"+P.bgS+","+P.priLt+"40)",border:"1px solid "+P.bdr}},
      E("div",{style:{fontSize:10,fontWeight:700,color:P.slateL,textTransform:"uppercase",letterSpacing:".1em",marginBottom:12}},"Customer Journey"),
      E("div",{style:{display:"flex",alignItems:"center",justifyContent:"center",gap:4,flexWrap:"wrap"}},
        [{label:"PA11Y Lab",sub:"Free",color:P.pri,to:"aiLabWeb"},{label:"DocMaya",sub:"$99/mo",color:P.emerald},{label:"Watchtower",sub:"$149/mo",color:P.pri,to:"dashboard"},{label:"Standard",sub:"$499/mo",color:P.accent},{label:"Enterprise",sub:"$1,200+/mo",color:P.amber}].map(function(step,idx){
          return E("div",{key:step.label,style:{display:"flex",alignItems:"center",gap:4}},
            E("div",{onClick:step.to?function(){setView("app");setMod(step.to)}:null,title:step.to?("Open "+step.label):null,style:{padding:"6px 14px",borderRadius:8,background:step.color+"12",border:"1.5px solid "+step.color+"25",cursor:step.to?"pointer":"default"}},
              E("div",{style:{fontSize:11,fontWeight:700,color:step.color}},step.label),
              E("div",{style:{fontSize:9,color:P.slateL,fontWeight:600}},step.sub)),
            idx<4?E("div",{style:{fontSize:14,color:P.slateL,fontWeight:700}},"\u2192"):null)}))),

    /* ── MY SCANS ── */
    E("div",{style:{marginBottom:8,display:"flex",alignItems:"center",gap:6}},
      E("span",{style:{width:8,height:8,borderRadius:"50%",background:storageOk===true?"#059669":storageOk===false?"#DC2626":"#94A3B8",flexShrink:0}}),
      E("span",{style:{fontSize:10,color:storageOk===true?"#059669":storageOk===false?"#DC2626":"#94A3B8",fontWeight:600}},
        storageOk===true?"Persistent Storage Active — scans survive sessions":storageOk===false?"Storage Unavailable — scans will not persist":"Checking storage...")),
    savedScans.length>0?E("div",{style:{marginBottom:24}},
      E("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8}},
          E("span",{style:{fontSize:16}},"\uD83D\uDCC1"),
          E("h3",{style:{fontSize:14,fontWeight:800,margin:0}},"My Scans"),
          E("span",{style:{fontSize:10,color:P.slateL,fontWeight:500}},savedScans.length+" saved")),
        E("button",{onClick:function(){
          if(!window.confirm("Clear ALL saved scans? This cannot be undone."))return;
          var ids=savedScans.map(function(s){return s.id});
          setSavedScans([]);
          sS("aris-scans-index",[]).then(function(){
            return Promise.all(ids.map(function(id){
              return Promise.all([sS("aris-scan-"+id+"-wp",[]),sS("aris-scan-"+id+"-dc",[]),sS("aris-scan-"+id+"-rem",[]),sS("aris-scan-"+id+"-stg",[])]);
            }));
          }).then(function(){showToast("\u{1F5D1} All scans cleared")});
        },style:{padding:"4px 10px",borderRadius:6,border:"1px solid "+P.bdr,background:P.card,fontSize:10,color:P.slateL,fontWeight:600}},"\uD83D\uDDD1 Clear All")),
      E("div",{style:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}},
        savedScans.map(function(s){
          var domHistory=getDomainHistory(s.domain);
          var prevScan=domHistory.length>1?domHistory.find(function(h){return h.id!==s.id}):null;
          var trend=prevScan?(s.totalIssues<prevScan.totalIssues?"\u2193":"s.totalIssues>prevScan.totalIssues"?"\u2191":"\u2192"):null;
          var trendColor=prevScan?(s.totalIssues<prevScan.totalIssues?P.emerald:s.totalIssues>prevScan.totalIssues?P.coral:P.slateL):null;
          return E("div",{key:s.id,style:{padding:"14px 16px",borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card}},
            E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}},
              E("div",null,
                E("div",{style:{fontSize:14,fontWeight:800}},s.domain),
                E("div",{style:{fontSize:10,color:P.pri,fontFamily:"monospace",marginTop:2}},s.id),
                E("div",{style:{fontSize:10,color:P.slateL,marginTop:2}},s.date+" at "+s.time)),
              E("div",{style:{display:"flex",gap:4}},
                prevScan?E("button",{onClick:function(){setCompareId(s.id===compareId?null:s.id)},title:"Compare versions","aria-label":"Compare versions",style:{padding:"3px 6px",borderRadius:4,border:"1px solid "+P.bdr,background:P.card,fontSize:10,cursor:"pointer"}},"\u{1F504}"):null,
                E("button",{onClick:function(){deleteScan(s.id)},title:"Delete scan","aria-label":"Delete scan",style:{padding:"3px 6px",borderRadius:4,border:"1px solid "+P.bdr,background:P.card,fontSize:10,color:P.coral,cursor:"pointer"}},"\u2715"))),
            E("div",{style:{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}},
              E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.priLt,fontSize:10,fontWeight:600,color:P.pri}},s.webPages+" pages"),
              s.pdfCount>0?E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.sMinBg,fontSize:10,fontWeight:600,color:P.sMin}},s.pdfCount+" PDFs"):
                E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.priLt,fontSize:10,fontWeight:600,color:P.pri}},s.documents+" docs"),
              E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.coralLt,fontSize:10,fontWeight:600,color:P.coral}},s.totalIssues+" issues"),
              E("div",{style:{padding:"3px 8px",borderRadius:6,background:s.criticalIssues>0?P.coralLt:P.emeraldLt,fontSize:10,fontWeight:600,color:s.criticalIssues>0?P.coral:P.emerald}},s.criticalIssues+" critical"),
              s.pdfUntagged>0?E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.sCritBg,fontSize:10,fontWeight:600,color:P.sCrit}},s.pdfUntagged+" untagged PDFs"):null,
              s.remediated>0?E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.emeraldLt,fontSize:10,fontWeight:600,color:P.emerald}},s.remediated+" fixed"):null),
            compareId===s.id&&prevScan?E("div",{style:{marginBottom:10,padding:"10px 12px",borderRadius:8,background:P.bgS,border:"1px solid "+P.bdr}},
              E("div",{style:{fontSize:10,fontWeight:700,marginBottom:6}},"\u{1F504} Comparison with previous scan ("+prevScan.date+")"),
              E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,fontSize:10}},
                E("div",{style:{fontWeight:600,color:P.slateL}},"Metric"),
                E("div",{style:{fontWeight:600,color:P.slateL}},"Previous"),
                E("div",{style:{fontWeight:600,color:P.slateL}},"Current"),
                E("div",null,"Issues"),
                E("div",null,prevScan.totalIssues),
                E("div",{style:{color:s.totalIssues<prevScan.totalIssues?P.emerald:s.totalIssues>prevScan.totalIssues?P.coral:P.slateM,fontWeight:700}},s.totalIssues+" "+(s.totalIssues<prevScan.totalIssues?"\u2193 \u2212"+(prevScan.totalIssues-s.totalIssues):s.totalIssues>prevScan.totalIssues?"\u2191 +"+(s.totalIssues-prevScan.totalIssues):"\u2014")),
                E("div",null,"Critical"),
                E("div",null,prevScan.criticalIssues),
                E("div",{style:{color:s.criticalIssues<prevScan.criticalIssues?P.emerald:s.criticalIssues>prevScan.criticalIssues?P.coral:P.slateM,fontWeight:700}},s.criticalIssues+" "+(s.criticalIssues<prevScan.criticalIssues?"\u2193":s.criticalIssues>prevScan.criticalIssues?"\u2191":"\u2014")),
                E("div",null,"Assets"),
                E("div",null,prevScan.totalAssets),
                E("div",{style:{fontWeight:700}},s.totalAssets),
                E("div",null,"Remediated"),
                E("div",null,prevScan.remediated||0),
                E("div",{style:{color:P.emerald,fontWeight:700}},s.remediated||0))
            ):null,
            (function(){
              /* Use the LOCAL DB ID for the URL ("what you see is what you get").
                 Auto-load resolves it: cloud sessionId if available, else
                 falls back to loading from IndexedDB. */
              var urlId=s.id;
              var url="/dashboard/"+urlId;
              function openWithPreload(ev){
                if(ev&&ev.preventDefault)ev.preventDefault();
                try{
                  var data=JSON.stringify({
                    domain:s.domain,webPages:s.webPages,documents:s.documents,
                    pdfCount:s.pdfCount||0,pdfUntagged:s.pdfUntagged||0,
                    totalIssues:s.totalIssues,criticalIssues:s.criticalIssues
                  });
                  sessionStorage.setItem("paris_preload_"+urlId,data);
                  if(s.sessionId)sessionStorage.setItem("paris_preload_"+s.sessionId,data);
                }catch(e){}
                window.location.href=url;
              }
              return E("a",{href:url,onClick:openWithPreload,
                style:{display:"block",width:"100%",padding:"8px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",textAlign:"center",textDecoration:"none",boxSizing:"border-box"}},"\u{1F4C2} Open This Scan")
            })())
        }))
    ):null,

    /* ── CLOUD SESSIONS (API-backed, user-specific) ── */
    authUser?E("div",{style:{marginBottom:24}},
      E("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8}},
          E("span",{style:{fontSize:16}},"\u2601\uFE0F"),
          E("h3",{style:{fontSize:14,fontWeight:800,margin:0}},"Cloud Sessions"),
          E("span",{style:{fontSize:10,color:P.slateL,fontWeight:500}},userSessions.length+" sessions"),
          authUser.usage?E("span",{style:{fontSize:10,padding:"3px 8px",borderRadius:6,background:P.priLt,color:P.pri,fontWeight:600}},
            (authUser.usage.pagesThisMonth||0)+"/"+(authUser.limits&&authUser.limits.pages>0?authUser.limits.pages:"\u221E")+" pages this month"):null),
        E("button",{onClick:loadUserSessions,disabled:sessionsLoading,style:{padding:"4px 10px",borderRadius:6,border:"1px solid "+P.bdr,background:P.card,fontSize:10,color:P.slateM,fontWeight:600,cursor:"pointer"}},sessionsLoading?"\u23F3 Loading...":"\u{1F504} Refresh")),
      sessionsLoading&&userSessions.length===0?E("div",{style:{textAlign:"center",padding:24,color:P.slateL,fontSize:12}},"\u23F3 Loading your sessions..."):null,
      userSessions.length>0?E("div",{style:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10}},
        userSessions.map(function(s){
          var scoreColor=s.overallScore>=75?P.emerald:s.overallScore>=50?P.amber:P.coral;
          var sessionUrl="/dashboard/"+s.id;
          return E("div",{key:s.id,onClick:function(){window.location.href=sessionUrl},
            style:{padding:"16px 18px",borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,transition:"border-color .15s",cursor:"pointer"},
            onMouseEnter:function(e){e.currentTarget.style.borderColor=P.pri+"60"},
            onMouseLeave:function(e){e.currentTarget.style.borderColor=P.bdr}},
            E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}},
              E("div",null,
                E("div",{style:{fontSize:14,fontWeight:800,color:P.slate}},s.domain||"Unknown domain"),
                E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},s.name||s.id)),
              E("div",{style:{display:"flex",gap:4}},
                E("span",{style:{padding:"3px 8px",borderRadius:6,fontSize:9,fontWeight:700,
                  background:s.status==="saved"?P.emeraldLt:s.status==="scanning"?P.amberLt:P.priLt,
                  color:s.status==="saved"?P.emerald:s.status==="scanning"?P.amber:P.pri}},s.status),
                E("button",{onClick:function(ev){ev.stopPropagation();deleteSession(s.id)},title:"Delete session","aria-label":"Delete session",
                  style:{padding:"3px 6px",borderRadius:4,border:"1px solid "+P.bdr,background:P.card,fontSize:10,color:P.coral,cursor:"pointer"}},"\u2715"))),
            E("div",{style:{fontSize:10,color:P.slateL,marginBottom:8}},new Date(s.createdAt).toLocaleDateString()+" \u2022 "+new Date(s.createdAt).toLocaleTimeString()),
            E("div",{style:{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}},
              s.pageCount>0?E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.priLt,fontSize:10,fontWeight:600,color:P.pri}},s.pageCount+" pages"):null,
              s.docCount>0?E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.sMinBg,fontSize:10,fontWeight:600,color:P.sMin}},s.docCount+" docs"):null,
              s.overallScore!=null?E("div",{style:{padding:"3px 8px",borderRadius:6,background:scoreColor+"15",fontSize:10,fontWeight:700,color:scoreColor}},"Score: "+s.overallScore+"%"):null,
              s.summary&&s.summary.critical>0?E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.coralLt,fontSize:10,fontWeight:600,color:P.coral}},s.summary.critical+" critical"):null,
              s.engine?E("div",{style:{padding:"3px 8px",borderRadius:6,background:P.bgS,fontSize:9,fontWeight:500,color:P.slateL}},s.engine):null),
            E("a",{href:sessionUrl,onClick:function(ev){ev.preventDefault();window.location.href=sessionUrl},
              style:{display:"block",width:"100%",padding:"8px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",textAlign:"center",textDecoration:"none"}},"\u{1F4C2} Open Session"))
        })
      ):!sessionsLoading?E("div",{style:{textAlign:"center",padding:"28px 20px",borderRadius:12,border:"2px dashed "+P.bdr,color:P.slateL}},
        E("div",{style:{fontSize:28,marginBottom:8}},"\u2601\uFE0F"),
        E("div",{style:{fontSize:12,fontWeight:600,marginBottom:4}},"No cloud sessions yet"),
        E("div",{style:{fontSize:11}},"Scan a site with the Chrome extension or start a new audit")
      ):null
    ):!authLoading?E("div",{style:{marginBottom:24,padding:"20px 24px",borderRadius:14,border:"2px dashed "+P.pri+"30",background:P.priLt+"80",textAlign:"center"}},
      E("div",{style:{fontSize:24,marginBottom:8}},"\u{1F510}"),
      E("div",{style:{fontSize:13,fontWeight:700,marginBottom:6,color:P.slate}},"Sign in to save your sessions to the cloud"),
      E("div",{style:{fontSize:11,color:P.slateM,marginBottom:12}},"Free tier includes 50 pages/month. Sessions persist across devices."),
      E("button",{onClick:function(){window.location.href="/login"},style:{padding:"10px 28px",borderRadius:10,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",boxShadow:"0 2px 12px "+P.pri+"30"}},"Sign In with Microsoft")
    ):null,

    /* ── WHAT MAKES ARIS DIFFERENT ── */
    E("div",{style:{padding:"28px",borderRadius:16,background:"linear-gradient(180deg,"+P.card+" 0%,"+P.priLt+" 100%)",border:"1.5px solid "+P.bdr,marginBottom:24}},
      E("h3",{style:{fontSize:15,fontWeight:800,margin:"0 0 6px",textAlign:"center"}},"Built for real accessibility, not checkbox compliance"),
      E("p",{style:{fontSize:11,color:P.slateM,textAlign:"center",margin:"0 0 20px"}},"Every feature designed from the perspective of users who rely on assistive technology"),
      E("div",{style:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16}},
        [{type:"eye",t:"See exactly where",d:"Visual inspectors show issue markers at exact locations on rendered web pages and inside PDF documents. Click any issue \u2192 see it highlighted.",c:P.pri,c2:P.accent},
         {type:"keyboard",t:"Fix, not just find",d:"AI-powered auto-remediation with staging environment. Review patches before deploying. Rollback anytime. Source-level fixes, no overlay widgets.",c:P.emerald,c2:"#34D399"},
         {type:"doc",t:"Reports auditors accept",d:"7 report types including per-document certification, checkpoint detail, and remediation workbooks. Export HTML, JSON, or print directly.",c:P.coral,c2:P.amber}
        ].map(function(d){return E("div",{key:d.t,style:{textAlign:"center",padding:"20px 14px",borderRadius:12,background:P.card,border:"1px solid "+P.bdr}},
          E("div",{style:{marginBottom:10}},E(A11yIcon,{type:d.type,size:52,c1:d.c,c2:d.c2})),
          E("div",{style:{fontSize:12,fontWeight:700,color:d.c,marginBottom:6}},d.t),
          E("div",{style:{fontSize:10.5,color:P.slateM,lineHeight:1.6}},d.d))}))),

    /* ── WORKFLOW STEPS with connected line ── */
    E("div",{style:{position:"relative",padding:"28px 0",marginBottom:24}},
      E("div",{style:{position:"absolute",top:"50%",left:"10%",right:"10%",height:3,background:"linear-gradient(90deg,"+P.pri+","+P.accent+","+P.emerald+")",borderRadius:2,opacity:.15}}),
      E("div",{style:{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4,position:"relative"}},
        [{n:"1",t:"Scan",d:"Enter domain",icon:"screen"},{n:"2",t:"Audit",d:"Web + Documents",icon:"eye"},{n:"3",t:"Inspect",d:"Visual pinpointing",icon:"keyboard"},{n:"4",t:"Fix",d:"AI remediation",icon:"shield"},{n:"5",t:"Certify",d:"Reports + VPAT",icon:"doc"}].map(function(s){
          return E("div",{key:s.n,style:{textAlign:"center"}},
            E("div",{style:{width:44,height:44,borderRadius:"50%",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:16,fontWeight:900,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:6,boxShadow:"0 4px 12px "+P.pri+"25",border:"3px solid #fff"}},s.n),
            E("div",{style:{fontSize:11,fontWeight:700}},s.t),
            E("div",{style:{fontSize:10.5,color:P.slateL}},s.d))}))),

    /* ── STANDARDS COVERED ── */
    E("div",{style:{textAlign:"center",padding:"16px 0 8px"}},
      E("div",{style:{fontSize:10,color:P.slateL,fontWeight:600,marginBottom:8,textTransform:"uppercase",letterSpacing:".08em"}},"Standards Covered"),
      E("div",{style:{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}},
        ["WCAG 2.1 AA","WCAG 2.2 AA","Section 508","PDF/UA","ADA Title II","EAA EN 301 549","HHS","ISO 14289"].map(function(s){
          return E("span",{key:s,style:{padding:"4px 12px",borderRadius:6,background:P.bgS,border:"1px solid "+P.bdr,fontSize:10.5,fontWeight:600,color:P.slateM}},s)}))),
    /* ── COPYRIGHT ── */
    E("div",{style:{textAlign:"center",padding:"24px 0 8px",borderTop:"1px solid "+P.bdr,marginTop:24}},
      E("div",{style:{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:6}},
        E(PiArisLockup,{size:"small",opacity:.5}),
        E("span",{style:{fontSize:10,color:P.slateL,fontWeight:600}},"\u00A9 2026 "+PI_NAME+". All rights reserved.")),
      E("div",{style:{fontSize:10,color:P.slateM}},PI_WEB+" \u00B7 PiARIS\u2122 v"+APP_VERSION+" \u00B7 "+APP_FULL+" \u00B7 A "+PI_NAME+" Product"+((typeof window!=="undefined"&&window.__PARIS_BUILD__&&window.__PARIS_BUILD__.sha&&window.__PARIS_BUILD__.sha!=="dev")?(" \u00B7 build "+window.__PARIS_BUILD__.sha):"")))
  );

  /* SCANNING */
  var scanPhaseWeb=phase.indexOf("web")>-1||phase.indexOf("Discover")>-1;
  var scanPage = E("div",{role:"status",style:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"75vh",gap:20}},
    E("div",{style:{position:"relative",display:"inline-flex",alignItems:"center",justifyContent:"center"}},
      E(Ring,{v:prog,sz:140,sw:8}),
      E("div",{style:{position:"absolute",fontSize:32,fontWeight:900,color:P.pri}},prog+"%")),
    E("h2",{style:{fontSize:18,fontWeight:700,margin:0}},"Auditing "+domain),
    E("p",{style:{fontSize:12,color:P.accent,fontFamily:FC,margin:0}},phase),
    E("div",{style:{display:"flex",gap:16,marginTop:8}},
      E("div",{style:{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,background:scanPhaseWeb?P.priLt:P.emeraldLt,border:"1.5px solid "+(scanPhaseWeb?P.pri:P.emerald)+"30"}},
        E("div",{style:{width:8,height:8,borderRadius:"50%",background:scanPhaseWeb?P.pri:P.emerald,animation:scanPhaseWeb?"markerPulse 1.2s infinite":"none"}}),
        E("span",{style:{fontSize:10,fontWeight:600,color:scanPhaseWeb?P.pri:P.emerald}},scanPhaseWeb?"\u{1F310} Scanning web pages...":"\u{1F310} Web pages \u2713")),
      E("div",{style:{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,background:!scanPhaseWeb?P.priLt:P.bgS,border:"1.5px solid "+(!scanPhaseWeb?P.pri:P.bdr)+"30"}},
        E("div",{style:{width:8,height:8,borderRadius:"50%",background:!scanPhaseWeb?P.pri:P.slateL,animation:!scanPhaseWeb?"markerPulse 1.2s infinite":"none"}}),
        E("span",{style:{fontSize:10,fontWeight:600,color:!scanPhaseWeb?P.pri:P.slateL}},!scanPhaseWeb?"\u{1F4C4} Validating documents...":"\u{1F4C4} Documents pending"))));

  /* DASHBOARD */
  var allWcag = wSt.tw.concat(dSt.tw).reduce(function(acc,w){var ex=acc.find(function(a){return a.name===w[0]});if(ex)ex.count+=w[1];else acc.push({name:w[0],count:w[1],full:W[w[0]]?W[w[0]].n:""});return acc},[]).sort(function(a,b){return b.count-a.count}).slice(0,10);
  var pdfByViews = dc.slice().sort(function(a,b){return b.views-a.views}).slice(0,8).map(function(d){return{name:d.name.substring(0,18),views:d.views,issues:d.totalIssues}});

  /* Drilldown data builders */
  var allAssets=wp.map(function(p){return{type:"Web",name:p.n,url:p.url,issues:p.totalIssues,critical:p.critCount,major:p.majCount,minor:p.minCount}}).concat(dc.map(function(d){return{type:"PDF",name:d.name,url:d.name,issues:d.totalIssues,critical:d.critCount,major:d.majCount,minor:d.minCount,tagged:d.tagged,pages:d.pages}}));
  var allIssFlat=[];
  wp.forEach(function(p){p.issues.forEach(function(i){allIssFlat.push({asset:p.n,type:"Web",wcag:i.wcag,name:i.info.n,sev:i.sev,count:i.count,desc:i.desc})})});
  dc.forEach(function(d){d.issues.forEach(function(i){allIssFlat.push({asset:d.name,type:"PDF",wcag:i.wcag,name:i.info.n,sev:i.sev,count:i.count,desc:i.desc})})});
  allIssFlat.sort(function(a,b){var o={critical:0,major:1,minor:2};return(o[a.sev]||2)-(o[b.sev]||2)});
  var untaggedDocs=dc.filter(function(d){return!d.tagged});
  var wcagCritList=useMemo(function(){return wSt.tw.map(function(w){return{wcag:w[0],name:W[w[0]]?W[w[0]].n:"",count:w[1]}})},[wSt]);

  function drillPanel(){
    if(!dashDrill)return null;
    var title,rows,headers;
    if(dashDrill==="assets"){
      title="All "+allAssets.length+" Digital Assets";
      headers=["Type","Name","Issues","Critical","Major","Minor"];
      rows=allAssets.map(function(a){return[E("span",{style:{padding:"1px 6px",borderRadius:4,background:a.type==="Web"?P.accentLt:P.coralLt||"#FFF1F0",fontSize:10,fontWeight:700,color:a.type==="Web"?P.accent:P.coral}},a.type),a.name,a.issues,a.critical>0?E("span",{style:{color:P.sCrit,fontWeight:700}},a.critical):0,a.major,a.minor]});
    } else if(dashDrill==="issues"){
      title="All "+allIssFlat.length+" Issues (sorted by severity)";
      headers=["Severity","WCAG","Criterion","Asset","Type","Instances","Description"];
      rows=allIssFlat.map(function(i){return[E(SevB,{s:i.sev}),"WCAG "+i.wcag,i.name,i.asset,i.type,i.count,E("span",{style:{fontSize:10}},i.desc)]});
    } else if(dashDrill==="remediated"){
      title=totalFixed+" Remediated Issues";
      headers=["Category","Fixed","Total","Rate"];
      rows=[["Web Pages",wSt.tRem,wSt.tI,Math.round(wSt.tRem/Math.max(1,wSt.tI)*100)+"%"],["PDF Documents",dSt.tRem,dSt.tI,Math.round(dSt.tRem/Math.max(1,dSt.tI)*100)+"%"],
        [E("strong",null,"Total"),totalFixed,totalI,(overallScore==null?"\u2014":overallScore+"%")]];
      if(staging.length>0){title+=(" \u2014 "+staging.length+" in staging");}
    } else if(dashDrill==="wcags"){
      title=wSt.wc+" WCAG Criteria Violated (Web)";
      headers=["WCAG","Criterion Name","Instances","Pages Affected"];
      rows=wcagCritList.map(function(w){return["WCAG "+w.wcag,w.name,w.count,wp.filter(function(p){return p.issues.some(function(i){return i.wcag===w.wcag})}).length]});
    } else if(dashDrill==="untagged"){
      title=untaggedDocs.length+" Untagged PDF Documents";
      headers=["Document","Pages","Producer","Issues","Critical","Monthly Views"];
      rows=untaggedDocs.map(function(d){return[d.name,d.pages,d.producer,d.totalIssues,E("span",{style:{color:P.sCrit,fontWeight:700}},d.critCount),d.views.toLocaleString()]});
    } else return null;
    return E("div",{style:{marginBottom:16}},
      E(Card,{sx:{maxHeight:320,overflow:"auto"}},
        E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}},
          E("h3",{style:{fontSize:12,fontWeight:700,margin:0}},title),
          E("button",{onClick:function(){setDashDrill(null)},style:{padding:"3px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600,cursor:"pointer"}},"\u2715 Close")),
        E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:10.5}},
          E("thead",null,E("tr",null,headers.map(function(h,i){return E("th",{key:i,style:{background:P.bgS,border:"1px solid "+P.bdr,padding:"5px 8px",fontWeight:700,fontSize:10.5,textAlign:"left",position:"sticky",top:0}},h)}))),
          E("tbody",null,rows.map(function(row,ri){return E("tr",{key:ri,style:{background:ri%2===0?P.card:"#FAFBFE"}},row.map(function(cell,ci){return E("td",{key:ci,style:{border:"1px solid "+P.bdr,padding:"4px 8px"}},cell)}))})))));
  }

  /* ── Background Job Processing Banner (pulsing fade) ── */
  var bgJobBanner=bgJob?E("div",{style:{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderRadius:10,
    background:bgJob.phase==="done"?"linear-gradient(135deg,#ECFDF5,#D1FAE5)":"linear-gradient(135deg,#EFF6FF,#DBEAFE)",
    border:"1.5px solid "+(bgJob.phase==="done"?"#10B98130":"#3B82F630"),marginBottom:14,
    animation:bgJob.phase==="done"?"none":"pulse-fade 1.8s ease-in-out infinite"}},
    bgJob.phase==="done"
      ?E("span",{style:{fontSize:15}},"\u2705")
      :E("span",{style:{fontSize:15,display:"inline-block",animation:"spin 2s linear infinite"}},"\u2699"),
    E("div",{style:{flex:1}},
      bgJob.phase==="done"
        ?E("div",{style:{fontSize:12,fontWeight:600,color:"#059669"}},"Processing complete \u2014 "+bgJob.updated+" pages updated")
        :E("div",null,
            E("div",{style:{fontSize:12,fontWeight:600,color:"#2563EB"}},"Processing scan data\u2026"),
            E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},
              bgJob.processed+"/"+bgJob.total+" "+(bgJob.phase==="pdfs"?"documents":"pages")))),
    bgJob.phase!=="done"&&bgJob.total>0?E("div",{style:{width:60,height:4,borderRadius:2,background:"#3B82F620",overflow:"hidden"}},
      E("div",{style:{width:Math.round(bgJob.processed/bgJob.total*100)+"%",height:"100%",borderRadius:2,background:"#3B82F6",transition:"width .3s"}})):null
  ):null;

  var dashboard = E("div",null,
    bgJobBanner,
    isDemo?E("div",{style:{display:"flex",alignItems:"center",gap:8,padding:"10px 16px",borderRadius:10,background:P.amberLt,border:"1.5px solid "+P.amber+"40",marginBottom:14}},
      E("span",{style:{fontSize:16}},"\uD83D\uDEA7"),
      E("div",null,
        E("div",{style:{fontSize:12,fontWeight:700,color:P.amber}},"Demo Data"),
        E("div",{style:{fontSize:10.5,color:P.slateM}},"This is simulated data for demonstration. Use the Chrome extension or PA11Y Lab for real accessibility analysis."))
    ):null,
    /* ── Inaccessible Pages Section — broken links + auth-required + cross-origin ── */
    (brokenLinks.length>0||(scanMeta&&scanMeta.authRequired&&scanMeta.authRequired.count>0)||(scanMeta&&scanMeta.crossOriginRedirects&&scanMeta.crossOriginRedirects.count>0))?(function(){
      var cmsLinks=brokenLinks.filter(function(b){return b.type==="cms-template"});
      var stdLinks=brokenLinks.filter(function(b){return b.type==="standard"});
      var authSamples=scanMeta&&scanMeta.authRequired?scanMeta.authRequired.samples||[]:[];
      var authCount=scanMeta&&scanMeta.authRequired?scanMeta.authRequired.count||0:0;
      var corsSamples=scanMeta&&scanMeta.crossOriginRedirects?scanMeta.crossOriginRedirects.samples||[]:[];
      var corsCount=scanMeta&&scanMeta.crossOriginRedirects?scanMeta.crossOriginRedirects.count||0:0;
      var inaccessTotal=brokenLinks.length+authCount+corsCount;
      /* Deduplicate CMS links by unique URL (same template token repeats across pages) */
      var cmsUnique={};cmsLinks.forEach(function(b){
        var key=b.url;
        if(!cmsUnique[key])cmsUnique[key]={url:b.url,pages:[]};
        if(cmsUnique[key].pages.indexOf(b.sourcePage)===-1)cmsUnique[key].pages.push(b.sourcePage);
      });
      var cmsDeduped=Object.keys(cmsUnique).map(function(k){return cmsUnique[k]});
      /* Deduplicate standard links */
      var stdUnique={};stdLinks.forEach(function(b){
        var key=b.url;
        if(!stdUnique[key])stdUnique[key]={url:b.url,status:b.status,error:b.error||"",pages:[]};
        if(stdUnique[key].pages.indexOf(b.sourcePage)===-1)stdUnique[key].pages.push(b.sourcePage);
      });
      var stdDeduped=Object.keys(stdUnique).map(function(k){return stdUnique[k]});
      return E("div",{style:{marginBottom:14,borderRadius:12,border:"1.5px solid #E11D4820",background:"linear-gradient(135deg,#FFF5F5,#FEF2F2)",overflow:"hidden"}},
        /* Header */
        E("div",{style:{padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #FEE2E2",flexWrap:"wrap",gap:8}},
          E("div",{style:{display:"flex",alignItems:"center",gap:10}},
            E("span",{style:{fontSize:20}},"\u26A0\uFE0F"),
            E("div",null,
              E("div",{style:{fontSize:14,fontWeight:800,color:P.coralDk}},"Inaccessible Pages Report"),
              E("div",{style:{fontSize:11,color:P.coralDk}},inaccessTotal+" page"+(inaccessTotal!==1?"s":"")+" could not be fully assessed"))),
          E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
            cmsLinks.length>0?E("div",{style:{textAlign:"center",padding:"4px 10px",borderRadius:8,background:P.amberLt}},
              E("div",{style:{fontSize:14,fontWeight:900,color:P.amberDk}},cmsLinks.length),
              E("div",{style:{fontSize:8,fontWeight:700,color:P.amberDk}},"Broken Tpl")):null,
            stdLinks.length>0?E("div",{style:{textAlign:"center",padding:"4px 10px",borderRadius:8,background:P.sCritBg}},
              E("div",{style:{fontSize:14,fontWeight:900,color:P.sCrit}},stdLinks.length),
              E("div",{style:{fontSize:8,fontWeight:700,color:P.coralDk}},"Broken Href")):null,
            authCount>0?E("div",{style:{textAlign:"center",padding:"4px 10px",borderRadius:8,background:"#EDE9FE"}},
              E("div",{style:{fontSize:14,fontWeight:900,color:"#7C3AED"}},authCount),
              E("div",{style:{fontSize:8,fontWeight:700,color:"#6D28D9"}},"Auth-Required")):null,
            corsCount>0?E("div",{style:{textAlign:"center",padding:"4px 10px",borderRadius:8,background:"#E0F2FE"}},
              E("div",{style:{fontSize:14,fontWeight:900,color:"#0284C7"}},corsCount),
              E("div",{style:{fontSize:8,fontWeight:700,color:"#0369A1"}},"Cross-Origin")):null)),
        /* CMS Template broken links */
        cmsDeduped.length>0?E("details",{style:{borderTop:"1px solid #FEE2E2"}},
          E("summary",{style:{padding:"10px 16px",fontSize:12,fontWeight:700,color:P.amberDk,cursor:"pointer",background:P.amberLt}},
            "\u{1F527} CMS Template Broken Links ("+cmsLinks.length+" instances, "+cmsDeduped.length+" unique tokens)"),
          E("div",{style:{padding:"0 16px 12px"}},
            E("div",{style:{fontSize:11,color:P.amberDk,lineHeight:1.6,marginBottom:8}},
              "These links contain unresolved CMS template variables (e.g. ",
              E("code",{style:{padding:"1px 5px",borderRadius:3,background:"#FED7AA",fontSize:10,fontFamily:FC}},"{{url}}"),
              ") that were not processed at publish time. They appear in the rendered HTML and produce 404 errors for users. ",
              E("strong",null,"Fix:")," Update the CMS templates so href attributes render actual URLs. Because these tokens are typically in shared layout templates, fixing one file resolves all instances."),
            E("div",{style:{maxHeight:300,overflow:"auto",borderRadius:8,border:"1px solid #FED7AA"}},
              E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:11}},
                E("thead",null,E("tr",{style:{background:P.amberLt,position:"sticky",top:0}},
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:P.amberDk}},"Template Token"),
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:P.amberDk}},"Found On ("+cmsDeduped.reduce(function(s,c){return s+c.pages.length},0)+" pages)"))),
                E("tbody",null,cmsDeduped.slice(0,100).map(function(item,i){
                  return E("tr",{key:i,style:{borderTop:"1px solid #FED7AA",background:i%2?"#FFFBEB":"transparent"}},
                    E("td",{style:{padding:"5px 10px",fontFamily:FC,wordBreak:"break-all",color:P.amberDk}},item.url),
                    E("td",{style:{padding:"5px 10px",wordBreak:"break-all",color:P.amberDk}},item.pages.slice(0,5).map(function(p,j){
                      return E("div",{key:j},E("a",{href:p,target:"_blank",rel:"noopener",style:{color:P.amberDk,textDecoration:"underline",fontSize:10}},p));
                    }),item.pages.length>5?E("div",{style:{fontSize:10,color:P.amberDk,fontStyle:"italic"}},"\u2026and "+(item.pages.length-5)+" more"):null));
                }))))))
        :null,
        /* Standard broken links */
        stdDeduped.length>0?E("details",{style:{borderTop:"1px solid #FEE2E2"}},
          E("summary",{style:{padding:"10px 16px",fontSize:12,fontWeight:700,color:P.coralDk,cursor:"pointer",background:P.sCritBg}},
            "\u274C Standard Broken Links ("+stdLinks.length+" instances, "+stdDeduped.length+" unique URLs)"),
          E("div",{style:{padding:"0 16px 12px"}},
            E("div",{style:{fontSize:11,color:"#7F1D1D",lineHeight:1.6,marginBottom:8}},
              "These links point to pages or resources that returned HTTP errors (404, 500, timeout, etc.). ",
              E("strong",null,"Fix:")," Update the link URLs or remove the broken links from the source pages."),
            E("div",{style:{maxHeight:300,overflow:"auto",borderRadius:8,border:"1px solid #FECACA"}},
              E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:11}},
                E("thead",null,E("tr",{style:{background:P.coralLt,position:"sticky",top:0}},
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:P.coralDk}},"Broken URL"),
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:P.coralDk,width:60}},"Status"),
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:P.coralDk}},"Found On"))),
                E("tbody",null,stdDeduped.slice(0,200).map(function(item,i){
                  return E("tr",{key:i,style:{borderTop:"1px solid #FECACA",background:i%2?"#FFF5F5":"transparent"}},
                    E("td",{style:{padding:"5px 10px",fontFamily:FC,wordBreak:"break-all",color:"#7F1D1D"}},item.url),
                    E("td",{style:{padding:"5px 10px",fontWeight:700,color:P.sCrit,fontSize:10}},String(item.status||"err")),
                    E("td",{style:{padding:"5px 10px",wordBreak:"break-all",color:P.coralDk}},item.pages.slice(0,5).map(function(p,j){
                      return E("div",{key:j},E("a",{href:p,target:"_blank",rel:"noopener",style:{color:P.coralDk,textDecoration:"underline",fontSize:10}},p));
                    }),item.pages.length>5?E("div",{style:{fontSize:10,color:P.coralDk,fontStyle:"italic"}},"\u2026and "+(item.pages.length-5)+" more"):null));
                }))))))
        :null,
        /* Auth-required pages */
        authCount>0?E("details",{style:{borderTop:"1px solid #FEE2E2"}},
          E("summary",{style:{padding:"10px 16px",fontSize:12,fontWeight:700,color:"#6D28D9",cursor:"pointer",background:"#EDE9FE"}},
            "\uD83D\uDD12 Auth-Required Pages ("+authCount+" page"+(authCount!==1?"s":"")+")"),
          E("div",{style:{padding:"0 16px 12px"}},
            E("div",{style:{fontSize:11,color:"#5B21B6",lineHeight:1.6,marginBottom:8}},
              "These pages are behind login walls (detected via 401 status or redirect-to-login). They cannot be scanned without credentials. ",
              E("strong",null,"Fix:")," Provide auth-required URL patterns in the extension settings so the scanner can use your browser session to audit them."),
            E("div",{style:{maxHeight:300,overflow:"auto",borderRadius:8,border:"1px solid #DDD6FE"}},
              E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:11}},
                E("thead",null,E("tr",{style:{background:"#EDE9FE",position:"sticky",top:0}},
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:"#6D28D9"}},"URL"),
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:"#6D28D9",width:100}},"Detected By"))),
                E("tbody",null,authSamples.slice(0,100).map(function(item,i){
                  return E("tr",{key:i,style:{borderTop:"1px solid #DDD6FE",background:i%2?"#F5F3FF":"transparent"}},
                    E("td",{style:{padding:"5px 10px",fontFamily:FC,wordBreak:"break-all",color:"#5B21B6"}},item.url),
                    E("td",{style:{padding:"5px 10px",fontSize:10,color:"#6D28D9"}},item.detectedBy||"unknown"));
                }))))))
        :null,
        /* Cross-origin redirects */
        corsCount>0?E("details",{style:{borderTop:"1px solid #FEE2E2"}},
          E("summary",{style:{padding:"10px 16px",fontSize:12,fontWeight:700,color:"#0369A1",cursor:"pointer",background:"#E0F2FE"}},
            "\uD83D\uDD00 Cross-Origin Redirects ("+corsCount+" page"+(corsCount!==1?"s":"")+")"),
          E("div",{style:{padding:"0 16px 12px"}},
            E("div",{style:{fontSize:11,color:"#075985",lineHeight:1.6,marginBottom:8}},
              "These pages redirect to external domains. The service worker cannot follow cross-domain redirects due to CORS restrictions. ",
              "They are audited via tab navigation in Phase 3 when possible. ",
              E("strong",null,"Note:")," These are working links \u2014 they just redirect outside the scanned domain."),
            E("div",{style:{maxHeight:300,overflow:"auto",borderRadius:8,border:"1px solid #BAE6FD"}},
              E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:11}},
                E("thead",null,E("tr",{style:{background:"#E0F2FE",position:"sticky",top:0}},
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:"#0369A1"}},"Source URL"),
                  E("th",{style:{padding:"6px 10px",textAlign:"left",fontWeight:700,color:"#0369A1"}},"Error"))),
                E("tbody",null,corsSamples.slice(0,100).map(function(item,i){
                  return E("tr",{key:i,style:{borderTop:"1px solid #BAE6FD",background:i%2?"#F0F9FF":"transparent"}},
                    E("td",{style:{padding:"5px 10px",fontFamily:FC,wordBreak:"break-all",color:"#075985"}},item.url),
                    E("td",{style:{padding:"5px 10px",fontSize:10,color:"#0369A1"}},item.error||"CORS blocked"));
                }))))))
        :null);
    })():scanMeta&&scanMeta.templateTokens>0?E("div",{style:{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 16px",borderRadius:10,background:P.amberLt,border:"1.5px solid #FB923C40",marginBottom:14}},
      E("span",{style:{fontSize:18,flexShrink:0,marginTop:1}},"\u{1F527}"),
      E("div",{style:{flex:1,minWidth:0}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}},
          E("span",{style:{fontSize:12,fontWeight:700,color:P.amberDk}},"CMS Template Tokens Detected"),
          E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.amberLt,fontSize:10,fontWeight:700,color:P.amberDk}},scanMeta.templateTokens+" found")),
        E("div",{style:{fontSize:11,color:P.amberDk,lineHeight:1.6,marginTop:4}},
          "Your site has "+scanMeta.templateTokens+" link"+(scanMeta.templateTokens===1?"":"s")+" containing unrendered template variables. Update CMS templates so href attributes render actual URLs."))
    ):null,
    /* Template dedup info card (Session 49) — shows when scanner skipped template/archive pages */
    scanMeta&&scanMeta.templateDedup&&(scanMeta.templateDedup.templateSkipped>0||scanMeta.templateDedup.archiveSkipped>0)?E("div",{style:{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 16px",borderRadius:10,background:P.sMinBg,border:"1.5px solid #3B82F640",marginBottom:14}},
      E("span",{style:{fontSize:18,flexShrink:0,marginTop:1}},"\u{1F4CA}"),
      E("div",{style:{flex:1,minWidth:0}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}},
          E("span",{style:{fontSize:12,fontWeight:700,color:"#1E40AF"}},"Smart Template Deduplication"),
          scanMeta.templateDedup.templateSkipped>0?E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.sMinBg,fontSize:10,fontWeight:700,color:P.priDk}},scanMeta.templateDedup.templateSkipped.toLocaleString()+" template pages skipped"):null,
          scanMeta.templateDedup.archiveSkipped>0?E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.priLt,fontSize:10,fontWeight:700,color:P.priDk}},scanMeta.templateDedup.archiveSkipped.toLocaleString()+" archived pages skipped"):null),
        E("div",{style:{fontSize:11,color:P.priDk,lineHeight:1.6,marginTop:4}},
          "Identical template pages (events, blog posts, etc.) were sampled at 50 pages per pattern. "+
          (scanMeta.templateDedup.estimatedIssues>0?"Estimated ~"+scanMeta.templateDedup.estimatedIssues.toLocaleString()+" total issues"+(scanMeta.templateDedup.estimatedCritical>0?" (~"+scanMeta.templateDedup.estimatedCritical.toLocaleString()+" critical)":"")+" across skipped pages based on sample averages.":"No additional issues estimated from skipped pages.")))
    ):null,
    /* Archive content breakdown (Session 49) — shown when archived pages are detected */
    (function(){var archPages=wp.filter(function(p){return p.isArchived});
      if(archPages.length===0)return null;
      var archIssues=archPages.reduce(function(x,p){return x+p.totalIssues},0);
      var archCrit=archPages.reduce(function(x,p){return x+p.critCount},0);
      var activePages=wp.length-archPages.length;
      var activeIssues=wp.reduce(function(x,p){return x+p.totalIssues},0)-archIssues;
      return E("div",{style:{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 16px",borderRadius:10,background:P.amberLt,border:"1.5px solid #D9770640",marginBottom:14}},
        E("span",{style:{fontSize:18,flexShrink:0,marginTop:1}},"\u{1F4E6}"),
        E("div",{style:{flex:1,minWidth:0}},
          E("div",{style:{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}},
            E("span",{style:{fontSize:12,fontWeight:700,color:P.amberDk}},"Archived Content Detected"),
            E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.amberLt,fontSize:10,fontWeight:700,color:P.amberDk}},archPages.length+" archived pages"),
            archCrit>0?E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.sCritBg,fontSize:10,fontWeight:700,color:P.sCrit}},archCrit+" critical"):null),
          E("div",{style:{fontSize:11,color:P.amberDk,lineHeight:1.6,marginTop:4}},
            "Active site: "+activePages+" pages, "+activeIssues.toLocaleString()+" issues. "+
            "Archived: "+archPages.length+" pages, "+archIssues.toLocaleString()+" issues"+(archCrit>0?" ("+archCrit+" critical)":"")+".")));
    })(),
    E(SubViewHero,{
      product:"watchtower",
      title:"Compliance Dashboard",
      subtitle:domain,
      right:E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
        E("button",{onClick:function(){openScanPicker("watchtower")},style:{minHeight:38,padding:"8px 14px",borderRadius:8,border:"1.5px solid rgba(255,255,255,0.4)",background:"rgba(255,255,255,0.12)",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{1F4C2} Load Scan"),
        verifyBaseline?E("button",{onClick:clearVerification,title:"Cancel verification mode",style:{minHeight:38,padding:"8px 12px",borderRadius:8,border:"1.5px solid rgba(251,191,36,0.6)",background:"rgba(251,191,36,0.2)",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}},"\u2715 Cancel Verify"):
        E("button",{onClick:startVerification,style:{minHeight:38,padding:"8px 14px",borderRadius:8,border:"1.5px solid rgba(255,255,255,0.4)",background:"rgba(255,255,255,0.12)",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{1F504} Verify Fixes"),
        E("button",{onClick:buildRemediationPackage,style:{minHeight:38,padding:"8px 14px",borderRadius:8,border:"none",background:"#fff",color:P.pri,fontSize:11,fontWeight:800,cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}},"\u{1F9E0} Build Smart Remediation"))
    }),
    /* ── Verification Results Banner ── */
    verifyResult?E("div",{style:{marginBottom:14,borderRadius:12,border:"2px solid "+(verifyResult.regression>0?P.sCrit:verifyResult.resolved>0?P.emerald:P.pri)+"40",background:verifyResult.regression>0?"linear-gradient(135deg,"+P.sCritBg+","+P.coralLt+")":verifyResult.resolved>0?"linear-gradient(135deg,"+P.emeraldLt+","+P.emeraldLt+")":"linear-gradient(135deg,"+P.priLt+","+P.priLt+")",overflow:"hidden"}},
      E("div",{style:{padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}},
        E("div",{style:{display:"flex",alignItems:"center",gap:10}},
          E("span",{style:{fontSize:20}},verifyResult.regression>0?"\u26A0\uFE0F":verifyResult.resolved>0?"\u2705":"\u{1F504}"),
          E("div",null,
            E("div",{style:{fontSize:13,fontWeight:800,color:verifyResult.regression>0?P.sCrit:verifyResult.resolved>0?P.emerald:P.pri}},
              verifyResult.regression>0?"Regressions Detected":verifyResult.resolved>0?verifyResult.resolved+" Issue"+(verifyResult.resolved!==1?"s":"")+" Resolved":"No Changes Detected"),
            E("div",{style:{fontSize:10,color:P.slateM,marginTop:1}},"Compared against baseline from "+new Date(verifyBaseline.timestamp).toLocaleString()))),
        E("div",{style:{display:"flex",gap:8,alignItems:"center"}},
          E("div",{style:{textAlign:"center",padding:"4px 12px"}},
            E("div",{style:{fontSize:18,fontWeight:900,color:P.emerald}},verifyResult.resolved),
            E("div",{style:{fontSize:9,fontWeight:700,color:P.emerald}},"\u2713 Resolved")),
          E("div",{style:{textAlign:"center",padding:"4px 12px"}},
            E("div",{style:{fontSize:18,fontWeight:900,color:P.amber}},verifyResult.persistent),
            E("div",{style:{fontSize:9,fontWeight:700,color:P.amber}},"\u23F8 Open")),
          E("div",{style:{textAlign:"center",padding:"4px 12px"}},
            E("div",{style:{fontSize:18,fontWeight:900,color:P.sCrit}},verifyResult.regression),
            E("div",{style:{fontSize:9,fontWeight:700,color:P.sCrit}},"\u26A0 New")),
          E("div",{style:{textAlign:"center",padding:"4px 12px",borderLeft:"1px solid "+P.bdr}},
            E("div",{style:{fontSize:18,fontWeight:900,color:verifyResult.scoreAfter>verifyResult.scoreBefore?P.emerald:verifyResult.scoreAfter<verifyResult.scoreBefore?P.sCrit:P.pri}},
              (verifyResult.scoreAfter>verifyResult.scoreBefore?"+":"")+(verifyResult.scoreAfter-verifyResult.scoreBefore)+"%"),
            E("div",{style:{fontSize:9,fontWeight:700,color:P.slateL}},"Score \u0394")),
          E("button",{onClick:clearVerification,style:{padding:"6px 12px",borderRadius:6,border:"1px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600,cursor:"pointer"}},"\u2715 Dismiss"))
      )
    ):verifyBaseline&&!verifyResult?E("div",{style:{marginBottom:14,padding:"10px 16px",borderRadius:10,border:"1.5px solid "+P.pri+"40",background:P.priLt,display:"flex",alignItems:"center",gap:10}},
      E("span",{style:{fontSize:16}},"\u{1F504}"),
      E("div",{style:{flex:1}},
        E("div",{style:{fontSize:12,fontWeight:700,color:P.pri}},"Verification Mode Active"),
        E("div",{style:{fontSize:10,color:P.slateM}},isDemo?"Re-scanning "+domain+"...":"Baseline saved with "+verifyBaseline.totalIssues+" issues. Re-scan "+domain+" with the Chrome extension, then import the results.")),
      E("button",{onClick:clearVerification,style:{padding:"6px 12px",borderRadius:6,border:"1px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600,cursor:"pointer"}},"\u2715 Cancel")
    ):null,
    /* ── Watchtower Lifecycle Progress Tracker (Session 46) ──
       Shows the 6-step compliance lifecycle with data-driven completion.
       Each step lights up based on actual application state:
         1. SCAN    — wp.length > 0 (pages loaded from extension or import)
         2. REVIEW  — user is on Dashboard viewing results (auto-completes when scan loaded)
         3. BUILD   — any page has fixScript or fixesBuilt
         4. DOWNLOAD — packageReady or user has downloaded a fix bundle
         5. DEPLOY  — outside PiARIS — user clicks "I've deployed" confirmation
         6. VERIFY  — verifyResult exists with resolved/persistent counts
       Styled with indigo-to-emerald gradient matching Watchtower brand. */
    wp.length>0?E("div",{style:{marginBottom:14,padding:"12px 16px",borderRadius:12,
      border:"1.5px solid "+P.pri+"20",background:"linear-gradient(135deg,#EEF2FF,#F0FDF4)"}},
      (function(){
        /* Derive step completion from real application state */
        var hasPages=wp.length>0;
        var hasBuilt=wp.some(function(p){return p.fixScript||p.fixesBuilt});
        var hasDownload=packageReady||hasBuilt;
        var hasVerify=!!verifyResult;
        var steps=[
          {n:"1",t:"Scan",d:"Scan site with extension",done:hasPages,active:!hasPages,icon:"\u{1F50D}"},
          {n:"2",t:"Review",d:"Audit web pages + documents",done:hasPages,active:hasPages&&!hasBuilt,icon:"\u{1F4CB}"},
          {n:"3",t:"Build Fixes",d:"Generate remediation scripts",done:hasBuilt,active:hasPages&&!hasBuilt,icon:"\u{1F527}"},
          {n:"4",t:"Download",d:"Get fix package for deployment",done:hasDownload&&hasBuilt,active:hasBuilt&&!hasDownload,icon:"\u{2B07}"},
          {n:"5",t:"Deploy",d:"Install fixes on your platform",done:hasVerify,active:hasDownload&&!hasVerify,icon:"\u{1F680}"},
          {n:"6",t:"Verify",d:"Re-scan to confirm resolution",done:hasVerify,active:hasDownload&&!hasVerify,icon:"\u2713"}
        ];
        /* Calculate progress percentage */
        var completedSteps=steps.filter(function(s){return s.done}).length;
        var progressPct=Math.round(completedSteps/steps.length*100);
        return E("div",null,
          /* Header with progress percentage */
          E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}},
            E("div",{style:{fontSize:12,fontWeight:700,color:P.pri}},"Watchtower Compliance Lifecycle"),
            E("div",{style:{display:"flex",alignItems:"center",gap:6}},
              E("span",{style:{fontSize:10,color:P.slateM}},completedSteps+"/"+steps.length+" complete"),
              E("div",{style:{width:60,height:4,borderRadius:2,background:P.bdr,overflow:"hidden"}},
                E("div",{style:{width:progressPct+"%",height:4,borderRadius:2,background:"linear-gradient(90deg,"+P.pri+","+P.emerald+")",transition:"width .5s"}})))),
          /* Connected progress line with step circles */
          E("div",{style:{position:"relative",display:"flex",alignItems:"flex-start",gap:0}},
            /* Background line */
            E("div",{style:{position:"absolute",top:16,left:"5%",right:"5%",height:3,background:P.bdr,borderRadius:2,zIndex:0}}),
            /* Filled line (progress) */
            E("div",{style:{position:"absolute",top:16,left:"5%",width:Math.max(0,progressPct-10)+"%",height:3,borderRadius:2,
              background:"linear-gradient(90deg,"+P.pri+","+P.accent+","+P.emerald+")",transition:"width .5s",zIndex:1}}),
            /* Step circles */
            steps.map(function(s){
              var bg=s.done?"linear-gradient(135deg,"+P.emerald+",#34D399)":s.active?"linear-gradient(135deg,"+P.pri+","+P.accent+")":"#E2E8F0";
              var textCol=s.done||s.active?"#fff":"#94A3B8";
              return E("div",{key:s.n,style:{flex:1,textAlign:"center",position:"relative",zIndex:2}},
                E("div",{style:{width:32,height:32,borderRadius:"50%",background:bg,color:textCol,
                  fontSize:14,fontWeight:800,display:"inline-flex",alignItems:"center",justifyContent:"center",
                  border:"3px solid #fff",boxShadow:s.active?"0 0 0 3px "+P.pri+"30":"0 1px 4px rgba(0,0,0,.1)",
                  transition:"all .3s"}},s.done?"\u2713":s.icon),
                E("div",{style:{fontSize:10,fontWeight:s.done||s.active?700:500,color:s.done?P.emerald:s.active?P.pri:P.slateL,marginTop:4}},s.t),
                E("div",{style:{fontSize:9,color:P.slateM}},s.d));
            })));
      })()
    ):null,
    /* ── Build Progress Bar (Session 46, Feature #3) ──
       Shown during Build Remediation Package. Displays which page is being processed,
       a visual progress bar, and completed/total count. Replaces the old toast-per-page
       pattern with a persistent, scannable progress indicator. */
    buildProgress.active?E("div",{style:{marginBottom:14,padding:"12px 16px",borderRadius:10,border:"1.5px solid "+P.pri+"40",background:P.priLt}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8}},
          E("span",{style:{fontSize:16}},buildProgress.phase==="enriching"?"\u{1F9E0}":"\u{1F527}"),
          E("div",null,
            E("div",{style:{fontSize:12,fontWeight:700,color:P.pri}},
              buildProgress.phase==="enriching"?"AI Enriching\u2026":"Generating Fixes\u2026"),
            E("div",{style:{fontSize:10,color:P.slateM}},buildProgress.currentPage))),
        E("div",{style:{fontSize:12,fontWeight:800,color:P.pri}},buildProgress.completed+"/"+buildProgress.total)),
      /* Progress bar — fills left to right as pages complete */
      E("div",{style:{height:6,borderRadius:3,background:P.bdr,overflow:"hidden"}},
        E("div",{style:{height:6,borderRadius:3,background:"linear-gradient(90deg,"+P.pri+","+P.accent+")",
          width:Math.round(buildProgress.completed/Math.max(buildProgress.total,1)*100)+"%",
          transition:"width .5s ease-out"}})),
      buildProgress.failed>0?E("div",{style:{marginTop:6,fontSize:10,color:P.coral}},"\u26A0 "+buildProgress.failed+" page"+(buildProgress.failed!==1?"s":"")+" failed"):null
    ):null,
    /* ── License Usage Banner (Session 46, Feature #5) ──
       Shows the current domain's license tier, usage, and limits on the Dashboard.
       Provides a visible indicator of how many pages/builds remain before hitting the limit.
       Free tier shows an upgrade CTA. Expired/suspended shows a warning. */
    domainLicense?E("div",{style:{marginBottom:14,padding:"10px 16px",borderRadius:10,
      border:"1.5px solid "+(domainLicense.tier==="free"?"#D97706":P.pri)+"30",
      background:domainLicense.tier==="free"?"#FFFBEB":P.priLt,
      display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}},
      E("span",{style:{fontSize:14}},domainLicense.tier==="free"?"\u{1F513}":"\u{1F512}"),
      E("div",{style:{flex:1,minWidth:200}},
        E("div",{style:{display:"flex",alignItems:"center",gap:6}},
          E("span",{style:{fontSize:11,fontWeight:700,color:domainLicense.tier==="free"?"#92400E":P.pri}},
            domainLicense.tier.charAt(0).toUpperCase()+domainLicense.tier.slice(1)+" License"),
          E("span",{style:{fontSize:10,color:P.slateM}},domain)),
        E("div",{style:{display:"flex",gap:8,marginTop:3,fontSize:10,color:P.slateM}},
          E("span",null,(domainLicense.scansUsedThisYear||0)+"/"+(domainLicense.scansPerYear||1)+" scans"),
          E("span",null,"Up to "+(domainLicense.pagesPerDomain||25)+" pages/scan"))),
      /* Scan usage progress bar */
      domainLicense.scansPerYear>0?E("div",{style:{width:100,flexShrink:0}},
        E("div",{style:{height:4,borderRadius:2,background:P.bdr,overflow:"hidden"}},
          E("div",{style:{height:4,borderRadius:2,transition:"width .3s",
            width:Math.min(100,Math.round((domainLicense.scansUsedThisYear||0)/(domainLicense.scansPerYear||1)*100))+"%",
            background:(domainLicense.scansUsedThisYear||0)>=(domainLicense.scansPerYear||1)?P.coral:
              (domainLicense.scansUsedThisYear||0)>=(domainLicense.scansPerYear||1)*0.8?P.amber:P.pri}})),
        E("div",{style:{fontSize:9,color:P.slateL,textAlign:"right",marginTop:1}},
          Math.round((domainLicense.scansUsedThisYear||0)/Math.max(domainLicense.scansPerYear||1,1)*100)+"%")):null,
      domainLicense.tier==="free"?E("button",{onClick:function(){setUpgradeModal({domain:domain,selectedTier:"starter"})},
        style:{padding:"6px 14px",borderRadius:6,border:"none",
          background:"linear-gradient(135deg,#8B5CF6,#3B82F6)",color:"#fff",
          fontSize:10,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}},"\u2B06 Upgrade"):null
    ):null,
    /* ── Registered Domains section (Session 49) ── */
    domainLicense&&(domainLicense.maxDomains||1)>1?E("div",{style:{marginBottom:14,padding:"10px 14px",borderRadius:10,
      border:"1.5px solid "+P.pri+"20",background:P.priLt}},
      E("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}},
        E("div",{style:{fontSize:11,fontWeight:700,color:P.pri}},
          "\u{1F5A5} Registered Domains ("+(domainLicense.domainsRegistered||[]).length+"/"+(domainLicense.maxDomains||1)+")"),
        (domainLicense.domainsRegistered||[]).length<(domainLicense.maxDomains||1)?E("button",{onClick:function(){
          var newDom=prompt("Enter domain to register (e.g. sub.example.com):");
          if(!newDom||!newDom.trim())return;
          newDom=newDom.trim().toLowerCase().replace(/^www\./,"");
          fetch("/api/license/"+encodeURIComponent(domain)+"/register-domain",{
            method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
            body:JSON.stringify({domain:newDom})
          }).then(function(r){return r.json()}).then(function(data){
            if(data.ok){
              showToast("\u2705 Domain registered: "+newDom);
              setDomainLicense(Object.assign({},domainLicense,{domainsRegistered:data.domainsRegistered}));
            } else { showToast(data.error||"Registration failed","error"); }
          }).catch(function(e){showToast("Error: "+e.message,"error")});
        },style:{padding:"4px 10px",borderRadius:6,border:"none",background:P.pri,color:"#fff",
          fontSize:10,fontWeight:700,cursor:"pointer"}},"\u002B Add"):null),
      E("div",{style:{display:"flex",flexDirection:"column",gap:4}},
        (domainLicense.domainsRegistered||[]).map(function(d){
          return E("div",{key:d,style:{display:"flex",alignItems:"center",justifyContent:"space-between",
            padding:"5px 8px",borderRadius:6,background:P.card+"B3",fontSize:10}},
            E("span",null,d),
            d!==domain?E("button",{onClick:function(){
              if(!window.confirm("Remove "+d+" from your license?"))return;
              fetch("/api/license/"+encodeURIComponent(domain)+"/unregister-domain",{
                method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
                body:JSON.stringify({domain:d})
              }).then(function(r){return r.json()}).then(function(data){
                if(data.ok){
                  showToast("Domain removed: "+d);
                  setDomainLicense(Object.assign({},domainLicense,{domainsRegistered:data.domainsRegistered}));
                } else { showToast(data.error||"Failed","error"); }
              });
            },style:{padding:"2px 8px",borderRadius:4,border:"none",background:P.coral+"20",color:P.coral,
              fontSize:9,fontWeight:700,cursor:"pointer"}},"Remove"):E("span",{style:{fontSize:9,color:P.slateL}},"(primary)"));
        }))
    ):null,
    /* ── Download CTA (Session 46, Feature #4) ──
       Shown when Build Remediation Package completes (packageReady===true) or when
       any pages have fix scripts generated. Provides prominent download buttons
       on the Dashboard so the user doesn't have to navigate to Remediation Center.
       This is the "front and center" download flow requested in the Session 46 handoff. */
    packageReady||wp.some(function(p){return p.fixScript})?E("div",{style:{marginBottom:14,padding:"14px 18px",borderRadius:12,
      border:"2px solid "+P.emerald+"40",background:"linear-gradient(135deg,"+P.emeraldLt+","+P.emeraldLt+")",
      display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}},
      E("div",{style:{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:200}},
        E("div",{style:{width:40,height:40,borderRadius:10,background:P.emerald,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,color:"#fff",flexShrink:0}},"\u2713"),
        E("div",null,
          E("div",{style:{fontSize:13,fontWeight:800,color:P.emerald}},"Smart Remediation Ready"),
          E("div",{style:{fontSize:10,color:P.slateM}},wp.filter(function(p){return p.fixScript}).length+" of "+wp.length+" pages have fix scripts"))),
      E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
        E("button",{onClick:function(){
          /* Download combined fix bundle — all pages' fix scripts in one IIFE-wrapped .js file */
          var pagesWithFixes=wp.filter(function(p){return p.fixScript});
          if(pagesWithFixes.length===0){showToast("No fix scripts to download","error");return}
          var bundle="/* PiARIS Fix Bundle \u2014 "+domain+" \u2014 "+new Date().toISOString().split("T")[0]+" */\n(function(){\n  'use strict';\n  var currentUrl=window.location.pathname;\n";
          pagesWithFixes.forEach(function(p){
            var urlMatch=p.url?("currentUrl==='"+new URL(p.url).pathname+"'"):"true";
            bundle+="  if("+urlMatch+"){\n    "+p.fixScript.split("\n").join("\n    ")+"\n  }\n";
          });
          bundle+="  console.log('[PiARIS] Fix bundle loaded: "+pagesWithFixes.length+" pages');\n})();\n";
          var b=new Blob([bundle],{type:"application/javascript"});
          var a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="piaris-fix-bundle-"+(domain||"site").replace(/[^a-z0-9]/gi,"-")+".js";a.click();
          showToast("\u{2B07} Fix bundle downloaded: "+pagesWithFixes.length+" pages");
        },style:{padding:"8px 16px",borderRadius:8,border:"none",background:P.emerald,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{2B07} Download Fix Bundle"),
        E("button",{onClick:function(){setMod("remediation")},style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.pri+"40",background:P.priLt,color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{1F527} Remediation Center"),
        E("button",{onClick:exportProfessionalReport,style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid #8B5CF640",background:P.priLt,color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{1F4CA} Export XLSX"),
        /* Platform-specific deployment instructions — generates a deploy guide
           tailored to the client's CMS platform (WordPress, Granicus, Drupal, etc.).
           Downloads as a .txt file with step-by-step instructions. */
        E("button",{onClick:function(){
          var platform=detectSiteType(domain||"");
          var pagesWithFixes=wp.filter(function(p){return p.fixScript});
          var count=pagesWithFixes.length;
          var guides={
            wordpress:"PIARIS FIX DEPLOYMENT GUIDE \u2014 WORDPRESS\n"+("=").repeat(50)+"\nDomain: "+(domain||"site")+"\nPages: "+count+" with fix scripts\nGenerated: "+new Date().toISOString().split("T")[0]+"\n\nOPTION A: Plugin Method (Recommended)\n1. Install 'Insert Headers and Footers' or 'WPCode' plugin\n2. Go to wp-admin \u2192 Code Snippets \u2192 Add New\n3. Paste the fix bundle JavaScript\n4. Set location: Site Wide Footer\n5. Activate the snippet\n\nOPTION B: Theme Method\n1. Go to wp-admin \u2192 Appearance \u2192 Theme Editor\n2. Open footer.php (or functions.php)\n3. Add before </body>:\n   <script src=\"/piaris-fixes.js\"></script>\n4. Upload piaris-fix-bundle.js to your theme directory\n\nOPTION C: Script Tag\n1. Upload the fix bundle to your media library or /wp-content/uploads/\n2. Add to header.php:\n   <script src=\"/wp-content/uploads/piaris-fix-bundle-"+(domain||"site").replace(/[^a-z0-9]/gi,"-")+".js\" defer></script>\n",
            granicus:"PIARIS FIX DEPLOYMENT GUIDE \u2014 GRANICUS\n"+("=").repeat(50)+"\nDomain: "+(domain||"site")+"\nPages: "+count+" with fix scripts\n\n1. Log in to Granicus admin\n2. Go to Site Settings \u2192 Header/Footer Code\n3. In the Footer Code section, paste:\n   <script src=\"/piaris-fix-bundle.js\" defer></script>\n4. Upload piaris-fix-bundle.js via File Manager\n5. Publish changes\n\nNOTE: Granicus caches aggressively. After deploying,\nclear the CDN cache or wait for the next cache refresh cycle.\n",
            drupal:"PIARIS FIX DEPLOYMENT GUIDE \u2014 DRUPAL\n"+("=").repeat(50)+"\nDomain: "+(domain||"site")+"\nPages: "+count+" with fix scripts\n\nOPTION A: Libraries YAML\n1. Add to your theme's *.libraries.yml:\n   piaris-fixes:\n     js:\n       js/piaris-fix-bundle.js: { attributes: { defer: true } }\n2. Place fix bundle in themes/YOUR_THEME/js/\n3. Attach in *.info.yml or page.html.twig\n\nOPTION B: Direct Injection\n1. Edit html.html.twig\n2. Add before </body>:\n   <script src=\"/sites/default/files/piaris-fix-bundle.js\" defer></script>\n3. Upload fix bundle to /sites/default/files/\n4. Clear Drupal cache: drush cr\n",
            custom:"PIARIS FIX DEPLOYMENT GUIDE \u2014 CUSTOM SITE\n"+("=").repeat(50)+"\nDomain: "+(domain||"site")+"\nPages: "+count+" with fix scripts\n\n1. Upload piaris-fix-bundle.js to your web server\n2. Add to every page's HTML (before </body>):\n   <script src=\"/piaris-fix-bundle.js\" defer></script>\n3. The script auto-detects the current page and applies\n   the appropriate fixes.\n\nFor CDN deployment:\n   <script src=\"https://your-cdn.com/piaris-fix-bundle.js\" defer></script>\n"
          };
          var guide=guides[platform]||guides.custom;
          guide+="\n\nVERIFICATION\n"+("-").repeat(30)+"\nAfter deploying, use PiARIS Verify Fixes to re-scan and confirm issues are resolved.\nDashboard \u2192 Verify Fixes \u2192 Re-scan with Chrome extension\n\n\u00A9 2026 Pi Consultants \u2014 pi-consulting.us \u2014 PiARIS\u2122 v"+APP_VERSION+"\n";
          var b=new Blob([guide],{type:"text/plain"});
          var a=document.createElement("a");a.href=URL.createObjectURL(b);
          a.download="piaris-deploy-guide-"+platform+"-"+(domain||"site").replace(/[^a-z0-9]/gi,"-")+".txt";a.click();
          showToast("\u{1F4CB} "+platform.charAt(0).toUpperCase()+platform.slice(1)+" deployment guide downloaded");
        },style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.amber+"40",background:P.sMajBg,color:P.amberDk,fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{1F4CB} Deploy Guide"),
        /* CMS-native remediation scripts — executable code for specific platforms */
        E("button",{onClick:function(){var p=detectSiteType(domain||"");downloadCMSScript(p==="wordpress"?"wordpress":p==="drupal"?"drupal":"python")},
          style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid #6366F140",background:P.priLt,color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer"}},
          "\u{1F4DC} "+(function(){var p=detectSiteType(domain||"");return p==="wordpress"?"WordPress .php":p==="drupal"?"Drupal .module":"Python Script"})()),
        E("button",{onClick:function(){downloadCMSScript("csv")},
          style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid #6366F140",background:P.priLt,color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer"}},
          "\u{1F4CB} Patches CSV"))
    ):null,
    /* ═══ COMMAND CENTER — numbers-first, transparent, every number tells a story ═══
       Layout: [Score + Top Deductions] | [Web Pages breakdown] | [Documents breakdown]
       Every number is clearly labeled. No gauges hiding data.
       When no scan is loaded, shows an honest empty state — NOT a fake 100%. */
    (function(){
      /* Derived totals — transparent math. Use summary fallback when pages haven't loaded yet. */
      var ss = comp.summaryStats;
      var totalPages = wp.length > 0 ? wp.length : (ss ? ss.totalPages : 0);
      var activePages = comp.activePages;                            /* not archived — uses summary fallback via comp */
      var archivedPages = comp.archivePages;                         /* archived only — uses summary fallback via comp */
      var templateGroups = templateStats.templateGroupCount || 0;    /* shared template patterns */
      var templateInstances = templateStats.templateInstanceCount||0;/* issues covered by template fixes */
      var totalIssues = wSt.tI > 0 ? wSt.tI + dSt.tI : (ss ? (ss.totalElements || 0) + dSt.tI : dSt.tI);
      var untaggedPdfs = dc.filter(function(d){return !d.tagged}).length;
      /* Broken link totals — SEPARATE from issue counts. Sum from loaded pages,
         fall back to scanMeta.brokenCmsTemplate / brokenHref when pages are stubs. */
      var brokenTpl = wp.reduce(function(a,p){return a+(p.brokenCmsTemplate||0)},0);
      var brokenHref = wp.reduce(function(a,p){return a+(p.brokenHref||0)},0);
      if (brokenTpl === 0 && scanMeta && scanMeta.brokenCmsTemplate) brokenTpl = scanMeta.brokenCmsTemplate;
      if (brokenHref === 0 && scanMeta && scanMeta.brokenHref) brokenHref = scanMeta.brokenHref;
      var authReqCount = scanMeta && scanMeta.authRequired ? scanMeta.authRequired.count : 0;
      var authReqLiveAudited = scanMeta && scanMeta.authRequired ? scanMeta.authRequired.liveAudited : 0;
      var crossOrigCount = scanMeta && scanMeta.crossOriginRedirects ? scanMeta.crossOriginRedirects.count : 0;
      var scoreColor = comp.hasData ? (
        overallScore>=95?P.emerald:overallScore>=85?P.accent:overallScore>=70?P.amber:overallScore>=50?P.coral:P.sCrit
      ) : P.slateL;

      /* StatBlock: big number + label + optional sublabel. The core building block. */
      var StatBlock=function(opts){
        return E("div",{style:{minWidth:0}},
          E("div",{style:{fontSize:opts.sz||26,fontWeight:900,color:opts.color||P.slate,lineHeight:1.05,fontFeatureSettings:'"tnum"'}},
            opts.v==null?"\u2014":(typeof opts.v==="number"?opts.v.toLocaleString():opts.v)),
          E("div",{style:{fontSize:9,fontWeight:700,color:P.slateL,textTransform:"uppercase",letterSpacing:".05em",marginTop:3}},opts.label),
          opts.sub?E("div",{style:{fontSize:9,color:P.slateM,marginTop:2,lineHeight:1.35}},opts.sub):null);
      };

      return E("section",{role:"region","aria-label":"Compliance Command Center",
        className:"cmdCenter",
        style:{position:"relative",overflow:"hidden",borderRadius:20,
          background:darkMode
            ?"linear-gradient(135deg, #0F172A 0%, #1E1B4B 35%, #1A1F3A 65%, #0D1F2D 100%)"
            :"linear-gradient(135deg, #FAFBFE 0%, #EEF2FF 35%, #E0F2FE 70%, #ECFDF5 100%)",
          border:"1.5px solid "+P.bdr,
          marginBottom:16,
          boxShadow:"0 8px 40px "+(darkMode?"rgba(79,70,229,0.15)":"rgba(79,70,229,0.08)")}},
        /* Top accent stripe */
        E("div",{"aria-hidden":"true",style:{position:"absolute",top:0,left:0,right:0,height:4,
          background:"linear-gradient(90deg,"+pColors.c1+","+P.accent+","+P.emerald+","+P.amber+","+P.sCrit+")"}}),
        /* A11Y watermarks */
        E("div",{"aria-hidden":"true",style:{position:"absolute",top:8,right:24,fontSize:110,fontWeight:900,color:P.pri,opacity:darkMode?.04:.03,pointerEvents:"none",userSelect:"none"}},"A11Y"),
        E("div",{"aria-hidden":"true",style:{position:"absolute",bottom:-12,left:18,fontSize:72,fontWeight:900,color:P.accent,opacity:darkMode?.04:.025,transform:"rotate(-6deg)",pointerEvents:"none",userSelect:"none"}},"WCAG"),

        /* Header strip */
        E("div",{style:{position:"relative",zIndex:2,display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}},
          E("div",{style:{display:"flex",alignItems:"center",gap:10}},
            E("span",{"aria-hidden":"true",style:{fontSize:18}},"\u{1F3AF}"),
            E("h2",{style:{fontSize:14,fontWeight:800,margin:0,color:P.slate,textTransform:"uppercase",letterSpacing:".08em"}},"Compliance Command Center")),
          E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
            E("span",{style:{padding:"4px 10px",borderRadius:8,background:scoreColor+"20",color:scoreColor,fontSize:10,fontWeight:800,border:"1px solid "+scoreColor+"40"}},comp.status),
            comp.hasData&&comp.hasCritA?E("span",{style:{padding:"4px 10px",borderRadius:8,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:800,border:"1px solid "+P.sCrit+"40"}},"\u26A0 Level A barrier \u00B7 max 84%"):null)),

        /* 3-column wide layout: Score+Deductions | Web Pages | Documents
           CSS class .cmdGrid handles all responsive breakpoints */
        E("div",{className:"cmdGrid",style:{position:"relative",zIndex:2}},

          /* ═══ COLUMN 1: Score Ring + Top Deductions ═══ */
          E("div",{style:{background:darkMode?"rgba(15,23,42,0.45)":"rgba(255,255,255,0.7)",
            border:"1px solid "+P.bdr,borderRadius:14,padding:"16px 18px",
            backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",
            display:"flex",flexDirection:"column",gap:12}},
            /* Score ring (click → certification report) */
            E("div",{role:comp.hasData?"button":undefined,tabIndex:comp.hasData?0:undefined,
              "aria-label":comp.hasData?("Compliance score "+overallScore+" percent. "+comp.status+". Click for certification report."):"No scan data loaded",
              onClick:comp.hasData?function(){setMod("reports");setRptTab("certification")}:undefined,
              onKeyDown:comp.hasData?function(ev){if(ev.key==="Enter"||ev.key===" "){ev.preventDefault();setMod("reports");setRptTab("certification")}}:undefined,
              style:{display:"flex",flexDirection:"column",alignItems:"center",gap:6,cursor:comp.hasData?"pointer":"default",minHeight:44}},
              /* Ring — show % only if we have a real score, otherwise em-dash/Loading */
              (function(){
                var sz=isMobile?110:128, r=(sz-14)/2, ci=2*Math.PI*r;
                var hasScore = comp.hasData && overallScore != null;
                var pct = hasScore ? overallScore : 0;
                return E("div",{style:{position:"relative",width:sz,height:sz}},
                  E("div",{"aria-hidden":"true",style:{position:"absolute",inset:-12,borderRadius:"50%",background:scoreColor,opacity:hasScore?.12:0,filter:"blur(20px)"}}),
                  E("svg",{width:sz,height:sz,style:{transform:"rotate(-90deg)",position:"relative"},"aria-hidden":"true"},
                    E("circle",{cx:sz/2,cy:sz/2,r:r,fill:"none",
                      stroke:darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)",strokeWidth:11}),
                    E("circle",{cx:sz/2,cy:sz/2,r:r,fill:"none",stroke:scoreColor,strokeWidth:11,
                      strokeDasharray:ci,strokeDashoffset:ci-(pct/100)*ci,strokeLinecap:"round",
                      style:{transition:"stroke-dashoffset .8s ease-out"}})),
                  E("div",{style:{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}},
                    E("div",{style:{fontSize:hasScore?30:22,fontWeight:900,color:P.slate,lineHeight:1,fontFeatureSettings:'"tnum"',animation:(!hasScore&&comp.hasData)?"pulse-fade 1.8s ease-in-out infinite":"none"}},
                      hasScore?(overallScore+"%"):(comp.hasData?"\u2026":"\u2014")),
                    E("div",{style:{fontSize:8,fontWeight:700,color:P.slateL,textTransform:"uppercase",letterSpacing:".05em",marginTop:3}},hasScore?"weighted":(comp.hasData?"calculating":"no data"))));
              })(),
              E("div",{style:{fontSize:11,fontWeight:800,color:scoreColor,textTransform:"uppercase",letterSpacing:".04em",textAlign:"center"}},comp.status),
              comp.hasData?E("div",{style:{fontSize:9,color:P.slateM,textAlign:"center"}},"95+ Compliant \u00B7 85+ Substantial \u00B7 70+ Partial"):E("div",{style:{fontSize:9,color:P.slateL,textAlign:"center"}},"Load or run a scan to begin")),
            /* Top Deductions */
            comp.hasData?E("div",{style:{paddingTop:12,borderTop:"1px solid "+P.bdr}},
              E("div",{style:{display:"flex",alignItems:"center",gap:6,marginBottom:8}},
                E("span",{"aria-hidden":"true",style:{fontSize:12}},"\u{1F4C9}"),
                E("h3",{style:{fontSize:10,fontWeight:800,margin:0,color:P.slate,textTransform:"uppercase",letterSpacing:".05em"}},"Top Deductions")),
              comp.topPenalties.length>0?E("ul",{style:{listStyle:"none",padding:0,margin:0,display:"flex",flexDirection:"column",gap:6}},
                comp.topPenalties.slice(0,5).map(function(p,i){
                  var maxPen=comp.topPenalties[0].penalty||1;
                  var barW=Math.max(8,Math.round(p.penalty/maxPen*100));
                  var sevCol=p.sev==="critical"?P.sCrit:p.sev==="major"?P.amber:P.sMin;
                  return E("li",{key:i,style:{display:"flex",flexDirection:"column",gap:3}},
                    E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",gap:6}},
                      E("span",{style:{fontSize:10,fontWeight:700,color:P.slate,fontFamily:FC,flexShrink:0}},p.wcag),
                      E("span",{style:{fontSize:9,color:P.slateM,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,minWidth:0}},(p.name||"").substring(0,28)),
                      E("span",{style:{fontSize:10,fontWeight:800,color:sevCol,flexShrink:0,fontFeatureSettings:'"tnum"'}},"-"+p.penalty)),
                    E("div",{"aria-hidden":"true",style:{height:5,borderRadius:3,background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)",overflow:"hidden"}},
                      E("div",{style:{height:"100%",width:barW+"%",borderRadius:3,
                        background:"linear-gradient(90deg,"+sevCol+","+sevCol+"80)",
                        transition:"width .6s ease-out"}})));
                })
              ):E("div",{style:{fontSize:10,color:P.emerald,fontStyle:"italic",padding:"6px 0"}},"\u2728 No deductions \u2014 site is clean")
            ):null),

          /* ═══ COLUMN 2: Web Pages — every number labeled ═══ */
          E("div",{style:{background:darkMode?"rgba(15,23,42,0.45)":"rgba(255,255,255,0.7)",
            border:"1px solid "+P.bdr,borderRadius:14,padding:"16px 20px",
            backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",
            display:"flex",flexDirection:"column",gap:14}},
            E("div",{style:{display:"flex",alignItems:"center",gap:6}},
              E("span",{"aria-hidden":"true",style:{fontSize:14}},"\u{1F310}"),
              E("h3",{style:{fontSize:11,fontWeight:800,margin:0,color:P.slate,textTransform:"uppercase",letterSpacing:".06em"}},"Web Pages")),
            /* Row 1: Crawled | Active | Archived — uses .cmdStats3 (3 → 2+1 → stack) */
            E("div",{className:"cmdStats3"},
              StatBlock({v:comp.hasData?totalPages:null,label:"Total Crawled",color:P.slate,sub:comp.hasData?(comp.usingSummaryFallback?"pages loading\u2026":"all pages loaded"):""}),
              StatBlock({v:comp.hasData?activePages:null,label:"Active",color:P.pri,sub:comp.hasData&&archivedPages>0?"not archived":""}),
              StatBlock({v:comp.hasData?archivedPages:null,label:"Archived",color:archivedPages>0?P.amber:P.slateL,sub:comp.hasData&&archivedPages>0?comp.archiveIssues.toLocaleString()+" issues":"none"})),
            /* Row 2: Sharing Templates — clear math */
            E("div",{style:{padding:"10px 12px",borderRadius:10,background:darkMode?"rgba(79,70,229,0.12)":P.priLt,border:"1px dashed "+P.pri+"40"}},
              E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}},
                E("div",null,
                  E("div",{style:{fontSize:18,fontWeight:900,color:P.pri,lineHeight:1,fontFeatureSettings:'"tnum"'}},
                    comp.hasData?(templateGroups+" template "+(templateGroups===1?"fix":"fixes")):"\u2014"),
                  E("div",{style:{fontSize:9,fontWeight:700,color:P.pri,textTransform:"uppercase",letterSpacing:".04em",marginTop:2}},"shared across pages")),
                comp.hasData&&templateInstances>0?E("div",{style:{textAlign:"right"}},
                  E("div",{style:{fontSize:14,fontWeight:900,color:P.pri,fontFeatureSettings:'"tnum"'}},"\u2192 "+templateInstances.toLocaleString()),
                  E("div",{style:{fontSize:9,color:P.pri,fontWeight:600}},"instances resolved")):null)),
            /* Row 3: Inaccessible Pages box — all sub-categories are additive */
            (brokenTpl > 0 || brokenHref > 0 || authReqCount > 0 || crossOrigCount > 0) ? E("div",{style:{marginTop:6,padding:"10px 12px",borderRadius:10,background:darkMode?"rgba(239,68,68,0.08)":"#FEF2F2",border:"1px solid "+(darkMode?"#EF444430":"#FECACA")}},
              E("div",{style:{display:"flex",alignItems:"center",gap:6,marginBottom:8}},
                E("span",{"aria-hidden":"true",style:{fontSize:12}},"\u26A0\uFE0F"),
                E("div",{style:{fontSize:10,fontWeight:800,color:P.coral,textTransform:"uppercase",letterSpacing:".04em"}},"Inaccessible Pages"),
                E("div",{style:{padding:"1px 8px",borderRadius:6,background:P.coralLt,fontSize:10,fontWeight:800,color:P.coral}},(brokenTpl+brokenHref+authReqCount+crossOrigCount)+" total")),
              E("div",{className:"cmdStats3"},
                StatBlock({v:comp.hasData?brokenTpl:null,label:"Broken (Fix Tpl)",color:brokenTpl>0?P.amber:P.slateL,sub:brokenTpl>0?"{{token}} \u2014 1 template fix":""}),
                StatBlock({v:comp.hasData?brokenHref:null,label:"Broken (Fix Href)",color:brokenHref>0?P.coral:P.slateL,sub:brokenHref>0?"404/500 \u2014 update URL":""}),
                StatBlock({v:comp.hasData?authReqCount:null,label:"Auth-Required",color:authReqCount>0?"#8B5CF6":P.slateL,sub:authReqCount>0?(authReqLiveAudited>0?authReqLiveAudited+" live-audited":"login needed"):""})),
              crossOrigCount > 0 ? E("div",{className:"cmdStats3",style:{marginTop:4}},
                StatBlock({v:comp.hasData?crossOrigCount:null,label:"Cross-Origin",color:"#0EA5E9",sub:"external redirect"}),
                StatBlock({v:null,label:"",color:P.slateL,sub:""}),
                StatBlock({v:null,label:"",color:P.slateL,sub:""})
              ) : null
            ) : null,
            /* Row 4: Issues | Critical | Remediated — pure WCAG, broken links excluded */
            E("div",{className:"cmdStats3"},
              StatBlock({v:comp.hasData?(wSt.tI>0?wSt.tI:(ss?ss.totalElements:0)):null,label:"Web Issues",color:(wSt.tI>0||ss&&ss.totalElements>0)?P.sCrit:P.emerald,sub:comp.hasData?(wSt.wc>0?wSt.wc+" WCAG rules":(ss?"from summary":"")):""}),
              StatBlock({v:comp.hasData?(wSt.tC>0?wSt.tC:(ss?ss.critical:0)):null,label:"Critical",color:(wSt.tC>0||ss&&ss.critical>0)?P.coral:P.emerald,sub:comp.hasData&&(wSt.tC>0||ss&&ss.critical>0)?"Level A barriers":""}),
              StatBlock({v:comp.hasData?wSt.tRem:null,label:"Remediated",color:P.emerald,sub:comp.hasData&&wSt.tRem>0?"fixes accepted":""})),
            /* Narrative sentence — plain English reconciliation of every web number above. */
            comp.hasData && totalPages > 0 ? (function(){
              var webIss = wSt.tI > 0 ? wSt.tI : (ss ? (ss.totalElements || 0) : 0);
              var webCrit = wSt.tC > 0 ? wSt.tC : (ss ? (ss.critical || 0) : 0);
              var inaccessTotal = brokenTpl + brokenHref + authReqCount + crossOrigCount;
              var n = totalPages + " page" + (totalPages===1?"":"s") + " crawled, " +
                activePages + " active and " + archivedPages + " archived" +
                (templateGroups > 0 ? ", " + templateGroups + " template fix" + (templateGroups===1?"":"es") + " cover " + templateInstances.toLocaleString() + " issue instance" + (templateInstances===1?"":"s") : "") +
                (inaccessTotal > 0 ? ". " + inaccessTotal + " inaccessible page" + (inaccessTotal===1?"":"s") + " (" +
                  (brokenTpl > 0 ? brokenTpl + " broken template" : "") +
                  (brokenTpl > 0 && brokenHref > 0 ? ", " : "") +
                  (brokenHref > 0 ? brokenHref + " broken href" : "") +
                  (authReqCount > 0 ? (brokenTpl + brokenHref > 0 ? ", " : "") + authReqCount + " auth-required" : "") +
                  (crossOrigCount > 0 ? (brokenTpl + brokenHref + authReqCount > 0 ? ", " : "") + crossOrigCount + " cross-origin" : "") +
                  ")" : "") +
                ". The " + webIss.toLocaleString() + " issue" + (webIss===1?" is":"s are") + " pure WCAG element violations" +
                (webCrit > 0 ? ", of which " + webCrit.toLocaleString() + " " + (webCrit===1?"is":"are") + " critical Level A" : "") + ".";
              return E("div",{style:{padding:"10px 12px",borderRadius:10,background:darkMode?"rgba(99,102,241,0.10)":"#EEF2FF",border:"1px dashed "+P.pri+"40",fontSize:11,color:P.slateM,lineHeight:1.6,marginTop:4}},
                E("span",{"aria-hidden":"true",style:{marginRight:4}},"\u{1F4D0}"),
                n);
            })() : null)),/* ← close web col wrapper */

          /* ═══ COLUMN 3: Documents — explainable 5-column scheme ═══ */
          (function(){
            /* Compute every doc stat from dc so every number is traceable. */
            var pdfs = dc.filter(function(d){return (d.ext||"pdf").toLowerCase()==="pdf"});
            var crawled = pdfs.length;
            var untagged = pdfs.filter(function(d){return !d.tagged}).length;
            var tagged = crawled - untagged;
            /* Inaccessible = tagged docs that still have any structural issues. */
            var inaccessible = pdfs.filter(function(d){return d.tagged && (d.issues||[]).length > 0}).length;
            var cleanTagged = tagged - inaccessible;
            /* Issue + critical = per-element counts across ALL docs, same unit as web column. */
            var docTotalIssues = pdfs.reduce(function(a,d){
              return a + (d.issues||[]).reduce(function(x,i){return x+(i.count||1)}, 0);
            }, 0);
            var docCritical = pdfs.reduce(function(a,d){
              return a + (d.issues||[]).filter(function(i){return i.sev==="critical"}).reduce(function(x,i){return x+(i.count||1)}, 0);
            }, 0);
            /* Secondary-row flags — docs where these conditions apply. */
            var flagProtected = pdfs.filter(function(d){return d.isPasswordProtected||d.passwordProtected}).length;
            var flagEncrypted = pdfs.filter(function(d){return d.isEncrypted}).length;
            var flagForms = pdfs.filter(function(d){return d.hasForm}).length;
            var flagScanned = pdfs.filter(function(d){return d.isScanned||d.imageOnly}).length;
            var hasFlags = flagProtected > 0 || flagEncrypted > 0 || flagForms > 0 || flagScanned > 0;
            /* Partial = docs that are untagged OR inaccessible but we still pulled some data. */
            var analyzable = untagged + inaccessible;
            /* Narrative math contract (all numbers reconcile): */
            var narrative = crawled === 0 ? "No PDFs in this scan." :
              crawled + " PDF" + (crawled===1?"":"s") + " crawled, " +
              untagged + " untagged so " + tagged + " tagged, of those " + tagged + ", " +
              inaccessible + " still have structural problems, " + cleanTagged + " clean tagged. " +
              "The " + docTotalIssues + " issue" + (docTotalIssues===1?"":"s") + " " +
              (docTotalIssues===1?"is":"are") + " spread across the " + analyzable + " doc" +
              (analyzable===1?"":"s") + " we could analyze.";

            return E("div",{style:{background:darkMode?"rgba(15,23,42,0.45)":"rgba(255,255,255,0.7)",
              border:"1px solid "+P.bdr,borderRadius:14,padding:"16px 18px",
              backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",
              display:"flex",flexDirection:"column",gap:12}},
              E("div",{style:{display:"flex",alignItems:"center",gap:6}},
                E("span",{"aria-hidden":"true",style:{fontSize:14}},"\u{1F4C4}"),
                E("h3",{style:{fontSize:11,fontWeight:800,margin:0,color:P.slate,textTransform:"uppercase",letterSpacing:".06em"}},"Documents (PDFs)")),

              /* Row 1: Crawled | Untagged | Inaccessible | Issues | Critical — the five primary columns */
              E("div",{style:{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:6}},
                StatBlock({v:comp.hasData?crawled:null,label:"Crawled",color:P.slate,sub:""}),
                StatBlock({v:comp.hasData?untagged:null,label:"Untagged",color:untagged>0?P.sCrit:P.emerald,sub:untagged>0?"re-generate":""}),
                StatBlock({v:comp.hasData?inaccessible:null,label:"Inaccessible",color:inaccessible>0?P.amber:P.emerald,sub:inaccessible>0?"fix structure":""}),
                StatBlock({v:comp.hasData?docTotalIssues:null,label:"Issues",color:docTotalIssues>0?P.sCrit:P.emerald,sub:""}),
                StatBlock({v:comp.hasData?docCritical:null,label:"Critical",color:docCritical>0?P.coral:P.emerald,sub:docCritical>0?"Level A":""})
              ),

              /* Narrative sentence — plain English reconciliation of every number above. */
              comp.hasData && crawled > 0 ? E("div",{style:{padding:"10px 12px",borderRadius:10,background:darkMode?"rgba(14,165,233,0.10)":"#F0F9FF",border:"1px dashed "+P.accent+"40",fontSize:11,color:P.slateM,lineHeight:1.6}},
                E("span",{"aria-hidden":"true",style:{marginRight:4}},"\u{1F4D0}"),
                narrative
              ) : null,

              /* Secondary row: capability flags — only when any are non-zero. */
              hasFlags ? E("div",null,
                E("div",{style:{fontSize:9,fontWeight:700,color:P.slateL,textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}},"Capability flags"),
                E("div",{style:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4}},
                  StatBlock({v:flagProtected,label:"Protected",color:flagProtected>0?P.amber:P.slateL,sub:flagProtected>0?"ask client":""}),
                  StatBlock({v:flagEncrypted,label:"Encrypted",color:flagEncrypted>0?P.amber:P.slateL,sub:flagEncrypted>0?"no extract":""}),
                  StatBlock({v:flagForms,label:"Forms",color:flagForms>0?P.pri:P.slateL,sub:flagForms>0?"label fields":""}),
                  StatBlock({v:flagScanned,label:"Scanned",color:flagScanned>0?P.amber:P.slateL,sub:flagScanned>0?"needs OCR":""})
                )
              ) : null,

              /* PAC 2026 footer */
              E("div",{style:{marginTop:"auto",paddingTop:10,borderTop:"1px solid "+P.bdr}},
                E("div",{style:{display:"flex",alignItems:"center",gap:6,padding:"8px 10px",borderRadius:8,background:darkMode?"rgba(6,182,212,0.12)":P.accentLt}},
                  E("span",{"aria-hidden":"true",style:{fontSize:12}},"\u2139\uFE0F"),
                  E("div",null,
                    E("div",{style:{fontSize:10,fontWeight:800,color:P.accent}},"PAC 2026 \u00B7 Matterhorn Protocol"),
                    E("div",{style:{fontSize:9,color:P.accent,lineHeight:1.4}},"Assessed in PiARIS \u00B7 separate from web rules"))))
            );
          })(),

        /* Bottom pills — context badges summarizing the whole frame */
        comp.hasData?E("div",{style:{position:"relative",zIndex:2,display:"flex",gap:6,flexWrap:"wrap",marginTop:14,justifyContent:"center"}},
          E("span",{style:{padding:"4px 10px",borderRadius:8,background:P.priLt,color:P.pri,fontSize:9,fontWeight:700}},wSt.wc+" Web WCAG Rules"),
          dSt.wc>0?E("span",{style:{padding:"4px 10px",borderRadius:8,background:P.amberLt,color:P.amberDk||P.amber,fontSize:9,fontWeight:700}},dSt.wc+" Doc Rules"):null,
          templateStats.actionableCount>0?E("span",{style:{padding:"4px 10px",borderRadius:8,background:P.emeraldLt,color:P.emerald,fontSize:9,fontWeight:700}},templateStats.actionableCount.toLocaleString()+" Actionable Fixes"):null,
          totalIssues>0?E("span",{style:{padding:"4px 10px",borderRadius:8,background:P.bgS,color:P.slateM,fontSize:9,fontWeight:700}},totalIssues.toLocaleString()+" Total Issue Instances"):null,
          archivedPages>0?E("span",{style:{padding:"4px 10px",borderRadius:8,background:P.amberLt,color:P.amberDk||P.amber,fontSize:9,fontWeight:700}},archivedPages+" Archived \u00B7 "+comp.archiveIssues.toLocaleString()+" issues"):null,
          E("span",{style:{padding:"4px 10px",borderRadius:8,background:P.accentLt,color:P.accent,fontSize:9,fontWeight:700}},"PAC 2026 \u00B7 PiARIS\u2122")):null);
    })(),
    /* Drilldown panel */
    drillPanel(),
    /* ── Fix Strategy Panel (Session 47) ── 
       Tells the compliance officer exactly what to fix and why.
       Template fixes first (highest leverage), then page-specific. */
    templateStats.templateGroupCount>0?E(Card,{sx:{marginBottom:16,background:"linear-gradient(135deg,#FAFAFE,#EEF2FF)",border:"2px solid #C7D2FE"}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,flexWrap:"wrap",gap:8}},
        E("div",null,
          E("h3",{style:{fontSize:14,fontWeight:800,margin:0,color:P.slate}},"\u{1F3AF} Your Fix Strategy"),
          E("p",{style:{fontSize:11,color:P.slateM,margin:"4px 0 0"}},
            "You have "+totalI.toLocaleString()+" issue instances, but only "+templateStats.actionableCount+" actual fixes needed.")),
        E("div",{style:{textAlign:"right"}},
          E("div",{style:{fontSize:28,fontWeight:800,color:P.pri}},templateStats.actionableCount),
          E("div",{style:{fontSize:10,fontWeight:700,color:P.pri,textTransform:"uppercase"}},"Actionable Fixes"))),
      /* Two-column: Template fixes | Page-specific */
      E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}},
        /* Left: Template fixes — highest leverage */
        E("div",{style:{padding:14,borderRadius:10,background:P.card,border:"1.5px solid #C7D2FE"}},
          E("div",{style:{display:"flex",alignItems:"center",gap:6,marginBottom:10}},
            E("span",{style:{fontSize:14}},"\u{1F501}"),
            E("div",null,
              E("div",{style:{fontSize:12,fontWeight:800,color:P.pri}},"Template Fixes"),
              E("div",{style:{fontSize:10,color:P.pri}},
                templateStats.templateGroupCount+" fixes \u2192 resolves "+templateStats.templateInstanceCount.toLocaleString()+" instances"))),
          E("div",{style:{fontSize:10,color:P.slateM,marginBottom:8,lineHeight:1.5}},
            "These issues repeat across multiple pages due to shared headers, footers, and navigation. Fix once in the template file \u2192 resolved everywhere."),
          /* Top 5 template fixes sorted by pages affected */
          (function(){
            var sorted=Object.keys(templateStats.groups)
              .filter(function(k){return templateStats.groups[k].pages.length>1&&templateStats.groups[k].isSpecific})
              .sort(function(a,b){return templateStats.groups[b].pages.length-templateStats.groups[a].pages.length})
              .slice(0,5);
            return sorted.map(function(k,i){
              var g=templateStats.groups[k];
              var sevCol=g.sev==="critical"?"#DC2626":g.sev==="major"?"#D97706":"#3B82F6";
              var sevBg=g.sev==="critical"?"#FEF2F2":g.sev==="major"?"#FFFBEB":"#EFF6FF";
              return E("div",{key:"tf-"+i,style:{padding:"8px 10px",borderRadius:6,border:"1px solid "+P.bdr,marginBottom:4,background:P.bg}},
                E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center"}},
                  E("div",{style:{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0}},
                    E("span",{style:{fontSize:10,fontWeight:800,color:sevCol,padding:"1px 5px",borderRadius:3,background:sevBg,flexShrink:0}},g.sev.charAt(0).toUpperCase()),
                    E("span",{style:{fontSize:10,fontWeight:700,fontFamily:FC}},g.wcag),
                    E("span",{style:{fontSize:10,color:P.slateM,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},(g.info&&g.info.n)||g.desc.substring(0,40))),
                  E("div",{style:{fontSize:10,fontWeight:700,color:P.pri,flexShrink:0,marginLeft:8}},g.pages.length+" pages")));
            });
          })()),
        /* Right: Page-specific issues */
        E("div",{style:{padding:14,borderRadius:10,background:P.card,border:"1.5px solid #E2E8F0"}},
          E("div",{style:{display:"flex",alignItems:"center",gap:6,marginBottom:10}},
            E("span",{style:{fontSize:14}},"\u{1F4C4}"),
            E("div",null,
              E("div",{style:{fontSize:12,fontWeight:800,color:P.slate}},"Page-Specific Issues"),
              E("div",{style:{fontSize:10,color:P.slateL}},
                templateStats.pageSpecificCount+" issues unique to individual pages"))),
          E("div",{style:{fontSize:10,color:P.slateM,marginBottom:8,lineHeight:1.5}},
            "These issues appear on only one page. Each requires a targeted fix on that specific page."),
          /* Top 5 worst pages by page-specific issue count */
          (function(){
            var pageCounts=wp.map(function(pg,pi){
              var pgSpecific=0;
              (pg.issues||[]).forEach(function(iss){
                var nSel=normalizeSelector(iss.selector||"");
                var descKey=(iss.desc||"").substring(0,60).toLowerCase().replace(/\s+/g," ");
                var tKey=(iss.wcag||"")+"||"+nSel+"||"+descKey;
                var tg=templateStats.groups[tKey];
                if(!tg||tg.pages.length<=1)pgSpecific+=(iss.count||1);
              });
              return{idx:pi,name:pg.n||pg.url||"Page "+(pi+1),pgSpecific:pgSpecific,critCount:pg.critCount||0};
            }).filter(function(p){return p.pgSpecific>0})
              .sort(function(a,b){return b.pgSpecific-a.pgSpecific})
              .slice(0,5);
            return pageCounts.map(function(p,i){
              return E("div",{key:"ps-"+i,onClick:function(){setSel(p.idx);setMod("webAudit")},
                style:{padding:"8px 10px",borderRadius:6,border:"1px solid "+P.bdr,marginBottom:4,cursor:"pointer",background:P.bg,
                  transition:"background .1s"}},
                E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center"}},
                  E("span",{style:{fontSize:10,color:P.slate,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}},p.name),
                  E("div",{style:{display:"flex",gap:6,flexShrink:0,marginLeft:8}},
                    p.critCount>0?E("span",{style:{fontSize:10,fontWeight:700,color:P.sCrit}},p.critCount+" crit"):null,
                    E("span",{style:{fontSize:10,fontWeight:700,color:P.slateM}},p.pgSpecific+" issues"))));
            });
          })()))
    ):null,
    E("div",{className:"rg2",style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:16}},
      E("div",{style:{position:"relative",overflow:"hidden",borderRadius:16,border:"1.5px solid "+P.pri+"20",background:P.card,padding:"16px 18px"}},
        E("div",{style:{position:"absolute",top:0,left:0,right:0,height:3,background:"linear-gradient(90deg,"+P.pri+",#8B5CF6,"+P.accent+")"},"aria-hidden":"true"}),
        E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10}},
          E("div",{style:{width:24,height:24,borderRadius:7,background:"linear-gradient(135deg,"+P.pri+",#8B5CF6)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff"},"aria-hidden":"true"},"\u{1F4CA}"),
          E("h3",{style:{fontSize:13,fontWeight:800,margin:0,color:P.slate}},"Most Violated WCAG Criteria")),
        E(ResponsiveContainer,{width:"100%",height:180},
          E(BarChart,{data:allWcag},
            E(XAxis,{dataKey:"name",tick:{fontSize:10,fill:P.slateM},axisLine:false,tickLine:false}),
            E(YAxis,{tick:{fontSize:10,fill:P.slateL},axisLine:false,tickLine:false,width:35}),
            E(Tooltip,{contentStyle:{background:P.card,border:"1.5px solid "+P.bdr,borderRadius:8,fontSize:11},formatter:function(v,n,p){return[v+" issues",p.payload.full]}}),
            E(Bar,{dataKey:"count",radius:[6,6,0,0]},allWcag.map(function(w,i){return E(Cell,{key:i,fill:PCOLS[i%PCOLS.length]})}))))),
      E("div",{style:{position:"relative",overflow:"hidden",borderRadius:16,border:"1.5px solid "+P.emerald+"20",background:P.card,padding:"16px 18px"}},
        E("div",{style:{position:"absolute",top:0,left:0,right:0,height:3,background:"linear-gradient(90deg,"+P.emerald+",#34D399,"+P.accent+")"},"aria-hidden":"true"}),
        E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10}},
          E("div",{style:{width:24,height:24,borderRadius:7,background:"linear-gradient(135deg,"+P.emerald+",#34D399)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff"},"aria-hidden":"true"},"\u{1F4C4}"),
          E("h3",{style:{fontSize:13,fontWeight:800,margin:0,color:P.slate}},"PDF Priority by Access Frequency")),
        E(ResponsiveContainer,{width:"100%",height:180},
          E(BarChart,{data:pdfByViews},
            E(XAxis,{dataKey:"name",tick:{fontSize:10,fill:P.slateM},axisLine:false,tickLine:false}),
            E(YAxis,{tick:{fontSize:10,fill:P.slateL},axisLine:false,tickLine:false,width:40}),
            E(Tooltip,{contentStyle:{background:P.card,border:"1.5px solid "+P.bdr,borderRadius:8,fontSize:11}}),
            E(Bar,{dataKey:"views",fill:P.pri,radius:[6,6,0,0],name:"Views/mo"}),
            E(Bar,{dataKey:"issues",fill:P.sCrit,radius:[6,6,0,0],name:"Issues"}))))),
    E("div",{className:"rg3",style:{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:12}},
      [{t:"Web Pages",s:wSt.n+" scanned",v:shortNum(wSt.tI),vl:"issues",c:P.pri,c2:P.accent,icon:"\u{1F310}",m:"webAudit"},
       {t:"Documents",s:dSt.n+" validated",v:shortNum(dSt.tI),vl:"issues",c:P.coral,c2:"#F97316",icon:"\u{1F4C4}",m:"docAudit"},
       {t:"Monitoring",s:"Active \u2014 6hr cycle",v:"Live",vl:"Continuous",c:P.emerald,c2:"#34D399",icon:"\u{1F4E1}",m:"monitoring"},
       {t:"VPAT Ready",s:totalFixed+"/"+totalI+" fixed",v:overallScore+"%",vl:"compliant",c:"#8B5CF6",c2:"#6366F1",icon:"\u{1F4DC}",m:"vpat"}
      ].map(function(q){return E("div",{key:q.t,role:"button",tabIndex:0,"aria-label":"Open "+q.t,
        onClick:function(){setMod(q.m);setSel(null)},
        onKeyDown:function(ev){if(ev.key==="Enter"||ev.key===" "){ev.preventDefault();setMod(q.m);setSel(null)}},
        style:{position:"relative",overflow:"hidden",borderRadius:16,cursor:"pointer",
          background:"linear-gradient(135deg,"+q.c+"12,"+q.c2+"08)",
          border:"1.5px solid "+q.c+"25",padding:"20px 18px",
          transition:"transform .15s,box-shadow .15s",minHeight:44}},
        /* Gradient accent stripe */
        E("div",{style:{position:"absolute",top:0,left:0,right:0,height:4,background:"linear-gradient(90deg,"+q.c+","+q.c2+")",borderRadius:"16px 16px 0 0"},"aria-hidden":"true"}),
        /* Background icon watermark */
        E("div",{style:{position:"absolute",bottom:-8,right:-4,fontSize:48,opacity:.06,fontWeight:900,lineHeight:1},"aria-hidden":"true"},q.icon),
        /* Content */
        E("div",{style:{position:"relative",zIndex:2}},
          E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10}},
            E("div",{style:{width:32,height:32,borderRadius:10,background:"linear-gradient(135deg,"+q.c+","+q.c2+")",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:"#fff",flexShrink:0,
              boxShadow:"0 2px 8px "+q.c+"40"},"aria-hidden":"true"},q.icon),
            E("div",{style:{fontSize:13,fontWeight:800,color:P.slate}},q.t)),
          E("div",{style:{display:"flex",alignItems:"baseline",gap:6,marginBottom:4}},
            E("span",{style:{fontSize:26,fontWeight:900,color:q.c,lineHeight:1}},q.v),
            E("span",{style:{fontSize:10,fontWeight:600,color:q.c,opacity:.7}},q.vl)),
          E("div",{style:{fontSize:10,color:P.slateM,fontWeight:500}},q.s)))}))
  );

  /* ISSUE DETAIL CARD */
  /* ══════════════════════════════════════════════════════════════════════
     SMART REMEDIATION ENGINE — AI-powered contextual fixes
     Proves: AI CAN generate meaningful alt text, fix contrast, analyze
     heading order, reading order, and provide contextual suggestions.
     ══════════════════════════════════════════════════════════════════════ */

  /* ── 1. ALT TEXT SUGGESTER — gleans context from filename, parent, classes ── */
  function suggestAltText(el){
    if(!el)return null;
    // Extract img src filename
    var srcMatch=el.match(/src=["']([^"']+)["']/i);
    if(!srcMatch)return null;
    var fullSrc=srcMatch[1];
    var filename=(fullSrc.split("/").pop()||"").split("?")[0].split("#")[0];
    // Strip dimensions, extensions, hashes
    var cleaned=filename.replace(/[-_]\d+x\d+/g,"").replace(/\.\w{2,4}$/,"").replace(/[-_]+/g," ").replace(/\b(img|image|photo|pic|icon|logo|banner)\b/gi,"").trim();
    // Extract context from parent element text
    var titleMatch=el.match(/title=["']([^"']+)["']/i);
    var classMatch=el.match(/class=["']([^"']+)["']/i);
    var classes=(classMatch?classMatch[1]:"").toLowerCase();
    // Determine if decorative
    var isLogo=classes.indexOf("logo")>-1||filename.toLowerCase().indexOf("logo")>-1;
    var isIcon=classes.indexOf("icon")>-1||filename.toLowerCase().indexOf("icon")>-1;
    var isBanner=classes.indexOf("banner")>-1||classes.indexOf("hero")>-1;
    var isCert=filename.toLowerCase().indexOf("certif")>-1||filename.toLowerCase().indexOf("award")>-1;
    // Build suggestion
    var suggestion="";
    if(titleMatch&&titleMatch[1]){
      suggestion=titleMatch[1];
    } else if(isLogo){
      suggestion=cleaned?cleaned+" logo":"Company logo";
    } else if(isIcon){
      suggestion=cleaned?cleaned+" icon":"Decorative icon";
    } else if(isBanner){
      suggestion=cleaned?cleaned+" banner image":"Page banner";
    } else if(isCert){
      suggestion=cleaned?cleaned+" certification badge":"Industry certification";
    } else if(cleaned.length>2){
      suggestion=cleaned.charAt(0).toUpperCase()+cleaned.slice(1);
    }
    // Check if it's an image-in-link (needs to describe destination)
    var isInLink=el.indexOf("<a")>-1||el.match(/^<a\s/i);
    if(isInLink&&!suggestion){
      var hrefMatch=el.match(/href=["']([^"']+)["']/i);
      if(hrefMatch){
        var path=hrefMatch[1].split("/").filter(function(s){return s&&s!=="https:"&&s.indexOf(".")===-1}).pop()||"";
        suggestion=path?"Navigate to "+path.replace(/[-_]/g," "):"Navigate to linked page";
      }
    }
    if(!suggestion)return null;
    return{
      suggestion:suggestion,
      filename:filename,
      isDecorative:false,
      isInLink:!!isInLink,
      confidence:titleMatch?"high":(cleaned.length>4?"medium":"low")
    };
  }

  /* ── 2. CONTRAST FIXER — parse ratio, calculate compliant alternatives ── */
  function parseContrastInfo(desc,el,fix){
    // Source 1: HTMLCS desc — "contrast ratio of 2.04:1"
    var ratioMatch=desc.match(/contrast ratio of (?:at least )?([\d.]+):1/);
    var requiredMatch=desc.match(/at least ([\d.]+):1/);
    // Source 2: axe-core fix — "insufficient color contrast of 3.77 (foreground color: #0071eb, background color: #ddeafb, font size: 14.3pt"
    var axeFix=fix||"";
    var axeRatioM=axeFix.match(/color contrast of ([\d.]+)/);
    var axeFgM=axeFix.match(/foreground color:\s*(#[0-9a-fA-F]{3,8})/i);
    var axeBgM=axeFix.match(/background color:\s*(#[0-9a-fA-F]{3,8})/i);
    var axeFontM=axeFix.match(/font size:\s*([\d.]+)pt/i);
    var axeWeightM=axeFix.match(/font weight:\s*(\w+)/i);
    if(!ratioMatch&&!requiredMatch&&!axeRatioM)return null;
    var currentRatio=axeRatioM?parseFloat(axeRatioM[1]):(ratioMatch?parseFloat(ratioMatch[1]):0);
    // Determine required ratio from font size
    var fontSize=axeFontM?parseFloat(axeFontM[1]):12;
    var isBold=axeWeightM&&(axeWeightM[1]==="bold"||parseInt(axeWeightM[1])>=700);
    var isLarge=fontSize>=18||(fontSize>=14&&isBold);
    var required=requiredMatch?parseFloat(requiredMatch[1]):(isLarge?3:4.5);
    // Extract colors
    var fgMatch=desc.match(/text colou?r to (#[0-9a-fA-F]{3,6})/i)||desc.match(/foreground.*?(#[0-9a-fA-F]{3,6})/i);
    var bgMatch=desc.match(/background.*?(#[0-9a-fA-F]{3,6})/i);
    var fg=axeFgM?axeFgM[1]:(fgMatch?fgMatch[1]:null);
    var bg=axeBgM?axeBgM[1]:(bgMatch?bgMatch[1]:null);
    return{
      currentRatio:currentRatio,
      required:required,
      deficit:Math.round((required-currentRatio)*100)/100,
      fg:fg,
      bg:bg,
      fontSize:Math.round(fontSize),
      isLarge:isLarge
    };
  }

  function suggestContrastFixes(info){
    if(!info)return[];
    var fixes=[];
    // Show actual failing colors when available
    if(info.fg&&info.bg){
      fixes.push({label:"Current colors",desc:info.fg+" on "+info.bg+" = "+info.currentRatio+":1"+(info.fontSize?" at "+info.fontSize+"pt":""),css:"color: "+info.fg+"; background: "+info.bg,confidence:"actual",isSwatch:true});
    }
    // Strategy 1: Darken foreground
    fixes.push({label:"Darken text",desc:"Increase text darkness to meet "+info.required+":1",css:"color: #1a1a1a"+(info.bg?"; background: "+info.bg:""),confidence:"high"});
    // Strategy 2: Lighten background
    if(info.fg){
      fixes.push({label:"Lighten background",desc:"Keep brand color, lighten background",css:"color: "+info.fg+"; background-color: #ffffff",confidence:"high"});
    } else {
      fixes.push({label:"Lighten background",desc:"Use a lighter background to increase contrast",css:"background-color: #ffffff",confidence:"high"});
    }
    // Strategy 3: High contrast pair
    if(info.required>=4.5){
      fixes.push({label:"High contrast pair",desc:"Dark text on light background for "+info.required+":1+",css:"color: #1E293B; background-color: #F8FAFC",confidence:"high"});
    }
    // Strategy 4: Large text option (only if not already large)
    if(info.required>=4.5&&!info.isLarge){
      fixes.push({label:"Increase font size",desc:"Large text (18pt+) only needs 3:1 — current is "+info.currentRatio+":1",css:"font-size: 18pt; font-weight: bold",confidence:"medium"});
    }
    return fixes;
  }

  /* ── 3. HEADING ORDER ANALYZER — builds tree from page issues ── */
  function analyzeHeadingOrder(pageIssues,el){
    // Look for heading skip patterns in the element
    var hMatch=el?el.match(/<h(\d)/i):null;
    if(!hMatch)return null;
    var currentLevel=parseInt(hMatch[1]);
    // Check what the correct level should be based on context
    var analysis={
      currentLevel:currentLevel,
      issues:[]
    };
    // Common heading order problems
    if(currentLevel>2){
      analysis.issues.push({
        problem:"Heading H"+currentLevel+" may skip levels",
        fix:"Ensure preceding headings follow H1 \u2192 H2 \u2192 H3 sequence",
        corrected:"H"+(currentLevel>1?currentLevel-1:1)
      });
    }
    return analysis;
  }

  /* ── 4. READING ORDER HELPER — detects landmark + tab order issues ── */
  function analyzeReadingOrder(el,wcag){
    if(wcag!=="1.3.2"&&wcag!=="2.4.3"&&wcag!=="2.4.1")return null;
    var analysis={tips:[]};
    if(wcag==="2.4.1"){
      analysis.tips.push({icon:"\u23ED",text:"Add skip navigation: <a href=\"#main\" class=\"skip-nav\">Skip to main content</a>",priority:"P1"});
      analysis.tips.push({icon:"\u{1F3AF}",text:"Add id=\"main\" to the main content area",priority:"P1"});
    }
    if(wcag==="2.4.3"){
      analysis.tips.push({icon:"\u2328",text:"Ensure tabindex values follow visual layout order",priority:"P1"});
      analysis.tips.push({icon:"\u{1F504}",text:"Remove tabindex > 0; use DOM order instead",priority:"P2"});
    }
    if(wcag==="1.3.2"){
      analysis.tips.push({icon:"\u{1F4D6}",text:"DOM order must match visual reading flow",priority:"P1"});
      analysis.tips.push({icon:"\u2194",text:"CSS flexbox order or grid can break reading sequence",priority:"P2"});
    }
    return analysis;
  }

  /* ── 5. CONTEXTUAL FIX GENERATOR — specific code suggestions per issue type ── */
  /** Generate context-aware fix suggestion for a web issue based on WCAG criterion and element */
  function generateSmartFix(iss){
    var w=iss.wcag,el=iss.el||"",desc=iss.desc||"";
    var fixes=[];

    // Empty links
    if(w==="4.1.2"&&desc.indexOf("no link content")>-1){
      var hrefM=el.match(/href=["']([^"']+)["']/);
      var classM=el.match(/class=["']([^"']+)["']/);
      var isIcon=(classM?classM[1]:"").indexOf("icon")>-1||(classM?classM[1]:"").indexOf("social")>-1;
      if(isIcon){
        fixes.push({type:"code",label:"Add aria-label for icon link",
          before:el.slice(0,60),
          after:el.replace(/<a /,'<a aria-label="Visit our social media" ')});
      } else {
        fixes.push({type:"code",label:"Add descriptive link text",
          before:"<a href=\"...\"></a>",
          after:"<a href=\"...\">Descriptive link text</a>"});
      }
    }

    // Missing form labels
    if((w==="3.3.2"||w==="4.1.2")&&(desc.indexOf("label")>-1||desc.indexOf("name available")>-1)){
      var nameM=el.match(/name=["']([^"']+)["']/);
      var phM=el.match(/placeholder=["']([^"']+)["']/);
      var idM=el.match(/id=["']([^"']+)["']/);
      var labelText=phM?phM[1]:(nameM?nameM[1].replace(/[-_]/g," "):"Field");
      fixes.push({type:"code",label:"Add visible <label>",
        before:el.slice(0,80),
        after:"<label for=\""+(idM?idM[1]:"field")+"\">"+labelText+"</label>\n"+el.slice(0,80)});
      if(phM){
        fixes.push({type:"tip",label:"Placeholder is not a label",
          text:"Placeholder text disappears on focus. Always pair with a visible <label> element."});
      }
    }

    // Missing submit button
    if(w==="3.2.2"&&desc.indexOf("submit button")>-1){
      fixes.push({type:"code",label:"Add submit button to form",
        before:"<form>...<input>...</form>",
        after:'<form>...<input>...<button type="submit">Search</button></form>'});
    }

    // Duplicate IDs
    if(w==="4.1.1"&&desc.indexOf("Duplicate id")>-1){
      var idMatch=desc.match(/value "([^"]+)"/);
      if(idMatch){
        fixes.push({type:"code",label:"Make IDs unique",
          before:'id="'+idMatch[1]+'" (appears multiple times)',
          after:'id="'+idMatch[1]+'-1", id="'+idMatch[1]+'-2", etc.'});
      }
    }

    // Skip nav broken
    if(w==="2.4.1"&&desc.indexOf("anchor")>-1){
      var anchorM=desc.match(/anchor "([^"]+)"/);
      if(anchorM){
        fixes.push({type:"code",label:"Add missing anchor target",
          before:'<a href="#'+anchorM[1]+'">Skip to content</a> \u2192 target missing',
          after:'<main id="'+anchorM[1]+'"> \u2014 add id to your main content wrapper'});
      }
    }

    // Language attribute
    if(w==="3.1.1"){
      fixes.push({type:"code",label:"Set document language",
        before:"<html>",
        after:'<html lang="en">'});
    }

    // ARIA attributes — required attributes missing
    if(w==="4.1.2"&&desc.indexOf("ARIA")>-1&&desc.indexOf("required")>-1){
      var roleM=el.match(/role=["']([^"']+)["']/);
      var role=roleM?roleM[1]:"widget";
      var ariaMap={tabpanel:["aria-labelledby"],tab:["aria-selected","aria-controls"],
        slider:["aria-valuenow","aria-valuemin","aria-valuemax"],
        combobox:["aria-expanded"],checkbox:["aria-checked"],
        dialog:["aria-label or aria-labelledby"],progressbar:["aria-valuenow"]};
      var needed=ariaMap[role]||["aria-label"];
      fixes.push({type:"code",label:"Add required ARIA attributes for role=\""+role+"\"",
        before:el.slice(0,80),
        after:el.slice(0,40)+" "+needed.join(" ")});
    }

    // ARIA conformance — invalid attribute values
    if(w==="4.1.2"&&desc.indexOf("conform")>-1){
      fixes.push({type:"tip",label:"Invalid ARIA attribute values",
        text:"Check that aria-expanded, aria-selected, aria-checked use 'true'/'false' (strings, not booleans). Verify role values are from the WAI-ARIA spec."});
    }

    // Textarea / input without accessible name
    if(w==="4.1.2"&&(desc.indexOf("name available")>-1||desc.indexOf("accessible name")>-1)){
      var tagM=el.match(/<(\w+)\s/);var tagName=tagM?tagM[1]:"element";
      var idM=el.match(/id=["']([^"']+)["']/);
      var nameM=el.match(/name=["']([^"']+)["']/);
      var phM=el.match(/placeholder=["']([^"']+)["']/);
      var labelSugg=phM?phM[1]:(nameM?nameM[1].replace(/[-_]/g," "):"Field description");
      if(tagName==="textarea"||tagName==="input"||tagName==="select"){
        fixes.push({type:"code",label:"Add accessible name to <"+tagName+">",
          before:el.slice(0,80),
          after:'<label for="'+(idM?idM[1]:"field")+'">'+labelSugg+'</label>\n'+el.slice(0,60)});
        if(!idM){
          fixes.push({type:"tip",label:"Missing id attribute",
            text:"This "+tagName+" has no id — add one so a <label for=\"...\"> can reference it, or use aria-label=\""+labelSugg+"\"."});
        }
      } else {
        fixes.push({type:"code",label:"Add aria-label to interactive element",
          before:el.slice(0,80),
          after:el.replace(/>/,' aria-label="'+labelSugg+'">').slice(0,120)});
      }
    }

    // Elementor tab / accordion patterns — aria-controls missing
    if(w==="4.1.2"&&el.indexOf("elementor-tab")>-1){
      fixes.push({type:"code",label:"Fix Elementor tab accessibility",
        before:el.slice(0,80),
        after:'Add role="tab", aria-selected="true/false", aria-controls="panel-id" to each tab title, and role="tabpanel" to content'});
      fixes.push({type:"tip",label:"Elementor widgets often lack ARIA",
        text:"Consider using an accessibility-enhanced Elementor add-on, or add custom JS to inject proper tab/accordion ARIA patterns."});
    }

    // Elements with ARIA attributes that use unsupported values
    if(w==="4.1.2"&&desc.indexOf("supported ARIA")>-1){
      fixes.push({type:"tip",label:"Unsupported ARIA attributes",
        text:"Remove ARIA attributes that don't belong on this element type. Check the allowed attributes for this element's implicit role in the WAI-ARIA spec."});
    }

    return fixes;
  }

  /* ══════════════════════════════════════════════════════════════════════
     PDF SMART FIX ENGINE — works on document metadata, NOT HTML elements.
     Web Smart Fix parses <img>, <a>, <form> elements.
     PDF Smart Fix uses: wcag, desc, doc.producer, doc.tagged, doc.pages, doc.name
     ══════════════════════════════════════════════════════════════════════ */

  /** Generate context-aware fix suggestion for a PDF issue based on document properties */
  function generatePdfSmartFix(iss,doc){
    if(!doc)return{fixes:[],panels:[]};
    var w=iss.wcag,desc=iss.desc||"",fixes=[],panels=[];
    var isPpt=(doc.producer||"").indexOf("PowerPoint")>-1;
    var isScan=(doc.producer||"").indexOf("PScript")>-1||(doc.producer||"").indexOf("Scan")>-1;
    var isForm=(doc.producer||"").indexOf("LiveCycle")>-1||(doc.name||"").toLowerCase().indexOf("form")>-1;
    var isInDesign=(doc.producer||"").indexOf("InDesign")>-1;
    var pg=doc.pages||1;

    /* 1.1.1 — Images without alt text (PDF-specific) */
    if(w==="1.1.1"){
      var imgCount=Math.max(1,iss.count);
      panels.push({type:"alttext",title:"\u{1F5BC} PDF Image Alt Text",color:P.amberDk,bg:"#FFFBEB",border:"#FDE68A",
        content:[
          {icon:"\u{1F3AF}",text:"This document has ~"+imgCount+" image"+(imgCount>1?"s":"")+" needing alt text",priority:"P1"},
          isPpt?{icon:"\u{1F3AC}",text:"PowerPoint slides: Check slide backgrounds, charts, and decorative graphics separately",priority:"P1"}:
          isScan?{icon:"\u{1F4F7}",text:"Scanned document: Run OCR first (Acrobat \u2192 Scan & OCR \u2192 Recognize Text), then tag images",priority:"P1"}:
          {icon:"\u{1F4DD}",text:"In Acrobat Pro: Reading Order tool \u2192 click each image \u2192 right-click \u2192 Edit Alt Text",priority:"P1"},
          {icon:"\u2699",text:"Decorative images: mark as Artifact (not Figure) — they don't need alt text",priority:"P2"},
          isInDesign?{icon:"\u{1F4E6}",text:"InDesign source: Object \u2192 Object Export Options \u2192 Alt Text tab \u2192 add before re-exporting",priority:"P1"}:null
        ].filter(Boolean)});
    }

    /* 1.3.1 — Heading hierarchy / tag structure */
    if(w==="1.3.1"){
      panels.push({type:"structure",title:"H\u20D7 PDF Tag Structure",color:P.pri,bg:"#F5F3FF",border:"#DDD6FE",
        content:[
          isScan?{icon:"\u26A0",text:"Scanned pages lack real text — OCR first, then verify tag tree",priority:"P1"}:
          {icon:"\u{1F3D7}",text:"Open Tags panel (View \u2192 Navigation Panels \u2192 Tags) to inspect structure",priority:"P1"},
          {icon:"H1",text:"Heading hierarchy must be H1\u2192H2\u2192H3 — no skipping levels",priority:"P1"},
          {icon:"\u{1F4CB}",text:"Tables need <Table>, <TR>, <TH> (with scope), <TD> structure",priority:pg>10?"P1":"P2"},
          {icon:"\u{1F4DD}",text:"Lists must use <L>, <LI>, <Lbl>, <LBody> — not just indented text",priority:"P2"},
          isPpt?{icon:"\u{1F3AC}",text:"PPT slides: Each slide title should be tagged as H1 or H2",priority:"P1"}:null
        ].filter(Boolean)});
      /* Show a tag tree diagram */
      fixes.push({type:"diagram",label:"Expected tag tree structure",
        diagram:["<Document>","  <H1> Document Title","  <H2> Section 1",
          "    <P> Body text...","    <Figure> <Alt: description>","  <H2> Section 2",
          "    <Table>","      <TR> <TH scope=col> Header","      <TR> <TD> Data",
          isPpt?"  <Part> Slide N":"  <H2> Section N"].join("\n")});
    }

    /* 1.3.2 — Reading order (critical for PPT and forms) */
    if(w==="1.3.2"){
      panels.push({type:"readingorder",title:"\u{1F4D6} PDF Reading Order",color:P.accent,bg:"#ECFEFF",border:"#A5F3FC",
        content:[
          isPpt?{icon:"\u{1F3AC}",text:"Slide reading order is set by object layering — check Order panel in PowerPoint",priority:"P1"}:
          isForm?{icon:"\u{1F4CB}",text:"Form fields must follow visual tab order — use Acrobat's Forms \u2192 Edit to set tab sequence",priority:"P1"}:
          {icon:"\u{1F504}",text:"Open Reading Order tool: Touch Up Reading Order \u2192 Show Order Panel \u2192 drag to reorder",priority:"P1"},
          {icon:"\u2328",text:"Tab through the document with a screen reader to verify logical flow",priority:"P1"},
          isPpt?{icon:"\u{1F5BC}",text:"~"+pg+" slides × ~"+(iss.count||3)+" elements each = "+(pg*(iss.count||3))+" reading order points to verify",priority:"P2"}:null,
          {icon:"\u2699",text:"Multi-column layouts: ensure left column reads completely before right column",priority:"P2"}
        ].filter(Boolean)});
    }

    /* 1.4.3 — Contrast in PDFs */
    if(w==="1.4.3"){
      panels.push({type:"contrast",title:"\u{1F3A8} PDF Contrast Analysis",color:P.pri,bg:"#F5F3FF",border:"#DDD6FE",
        content:[
          isPpt?{icon:"\u{1F3AC}",text:"Text-on-image slides: add semi-opaque background behind text overlays",priority:"P1"}:
          {icon:"\u{1F3A8}",text:"Headers/footers often use light gray (#999) — darken to #595959 for 4.5:1 ratio",priority:"P1"},
          {icon:"\u{1F4CA}",text:"Charts: ensure data labels and legends meet 3:1 for non-text, 4.5:1 for text",priority:"P1"},
          {icon:"\u2699",text:"Fix in source application (Word/PPT/InDesign) and re-export — editing PDF colors directly is fragile",priority:"P2"}
        ]});
    }

    /* 2.4.1 — Bookmarks for long documents */
    if(w==="2.4.1"){
      fixes.push({type:"code",label:"Add bookmarks from headings",
        before:"No bookmarks in "+pg+"-page document",
        after:"Acrobat: Bookmarks panel \u2192 Options \u2192 New Bookmarks From Structure\nOR in Word: use Heading styles (auto-generates bookmarks on export)"});
      if(pg>50){
        panels.push({type:"bookmarks",title:"\u{1F4D1} Bookmark Strategy",color:P.emerald,bg:"#F0FDF4",border:"#BBF7D0",
          content:[
            {icon:"\u{1F4CA}",text:pg+"-page doc needs structured bookmark hierarchy — not just page numbers",priority:"P1"},
            {icon:"\u{1F3AF}",text:"Mirror heading structure: each H1/H2 = bookmark, nested for H3+",priority:"P1"},
            {icon:"\u2699",text:"Named destinations for cross-references and TOC links",priority:"P2"}
          ]});
      }
    }

    /* 2.4.2 — Document title */
    if(w==="2.4.2"){
      var cleanName=(doc.name||"").replace(/-/g," ").replace(/\.\w+$/,"");
      fixes.push({type:"code",label:"Set document title in metadata",
        before:"Title: (empty) or \""+doc.name+"\" (filename)",
        after:"Title: \""+cleanName+"\"\nAcrobat: File \u2192 Properties \u2192 Description \u2192 Title\nAlso: Initial View \u2192 Show: Document Title"});
    }

    /* 3.1.1 — Document language */
    if(w==="3.1.1"){
      fixes.push({type:"code",label:"Set document language",
        before:"Language: (not set)",
        after:"Language: en-US\nAcrobat: File \u2192 Properties \u2192 Advanced \u2192 Language\nWord: File \u2192 Options \u2192 Language"});
    }

    /* 4.1.1 — Tag tree integrity */
    if(w==="4.1.1"){
      panels.push({type:"tagtree",title:"\u{1F3D7} Tag Tree Repair",color:P.sCrit,bg:"#FEF2F2",border:"#FECACA",
        content:[
          doc.tagged?{icon:"\u26A0",text:"Tags exist but have structural errors — run Accessibility Checker first",priority:"P1"}:
          {icon:"\u274C",text:"Document is UNTAGGED — requires full remediation. Add Tags: Acrobat \u2192 Accessibility \u2192 Autotag Document",priority:"P1"},
          isScan?{icon:"\u{1F4F7}",text:"Scanned PDF: auto-tags will be unreliable — manual verification required after OCR + autotag",priority:"P1"}:null,
          {icon:"\u2699",text:"After autotagging: verify every <Figure> has alt text, every <Table> has headers, every <H*> is correct level",priority:"P1"}
        ].filter(Boolean)});
    }

    /* 4.1.2 — Semantic roles (critical for untagged) */
    if(w==="4.1.2"){
      panels.push({type:"roles",title:"\u{1F3AF} Semantic Role Mapping",color:P.sCrit,bg:"#FEF2F2",border:"#FECACA",
        content:[
          {icon:"\u{1F4C4}",text:"Every content element needs a role: <P>, <H1-6>, <Figure>, <Table>, <L>, <Link>",priority:"P1"},
          isForm?{icon:"\u{1F4CB}",text:"Form fields must have: /TU (tooltip) for accessible name + proper /FT (field type)",priority:"P1"}:null,
          {icon:"\u{1F527}",text:"Use Reading Order tool to assign roles: select content \u2192 click role button (Text, Figure, etc.)",priority:"P1"},
          {icon:"\u2699",text:"Artifacts: decorative lines, page numbers, watermarks should be marked as Artifact, not tagged",priority:"P2"}
        ].filter(Boolean)});
    }

    /* 2.4.4 — Link text */
    if(w==="2.4.4"){
      fixes.push({type:"code",label:"Fix bare URL link text",
        before:"Link text: \"https://example.com/page/docs/report-v2\"",
        after:"Link text: \"Annual Report\" or \"Download Q3 Report\"\nIn Acrobat: Properties \u2192 Tag \u2192 Alternate Text for link"});
    }

    /* Form-specific issues (detected from producer/filename) */
    if(isForm&&(w==="4.1.2"||w==="1.3.1"||w==="3.3.2"||w==="1.3.2")){
      panels.push({type:"forms",title:"\u{1F4CB} PDF Form Accessibility",color:"#EC4899",bg:"#FDF2F8",border:"#FBCFE8",
        content:[
          {icon:"\u{1F4DD}",text:"Every field needs a tooltip (/TU) — this becomes the accessible name",priority:"P1"},
          {icon:"\u2328",text:"Tab order must match visual layout — Acrobat: Forms \u2192 Edit \u2192 Tab Order \u2192 Order by Row/Column",priority:"P1"},
          {icon:"\u{1F3F7}",text:"Group related fields with <Form> parent tag. Radio buttons need shared group name.",priority:"P1"},
          {icon:"\u2699",text:"Required fields: set /Ff flag + add '(required)' to tooltip text",priority:"P2"},
          {icon:"\u26A0",text:"LiveCycle forms: ensure XML form data is also tagged for AT access",priority:"P2"}
        ]});
    }

    return{fixes:fixes,panels:panels};
  }

  /* ═══ PDF TAG STRUCTURE MAP — equivalent to Page Anatomy Map ═══ */
  var PDF_ZONES=[
    {id:"meta",label:"Metadata",icon:"\u{1F4C4}",checks:["Title","Language","PDF/UA ID"],color:P.pri},
    {id:"tags",label:"Tag Tree",icon:"\u{1F3D7}",checks:["Structure tags","Role map","Artifacts"],color:P.pri},
    {id:"headings",label:"Headings",icon:"H",checks:["H1-H6 hierarchy","No skipped levels"],color:"#3B82F6"},
    {id:"images",label:"Images",icon:"\u{1F5BC}",checks:["Alt text on figures","Decorative as artifact"],color:"#F59E0B"},
    {id:"tables",label:"Tables",icon:"\u{1F4CA}",checks:["TH with scope","Caption","Summary"],color:"#06B6D4"},
    {id:"lists",label:"Lists",icon:"\u{1F4CB}",checks:["L/LI/Lbl/LBody structure"],color:P.slateL},
    {id:"links",label:"Links",icon:"\u{1F517}",checks:["Descriptive text","OBJR mapping"],color:P.sMin},
    {id:"forms",label:"Forms",icon:"\u{1F4DD}",checks:["Field tooltips","Tab order","Required flags"],color:"#EC4899"},
    {id:"reading",label:"Reading Order",icon:"\u{1F4D6}",checks:["Logical sequence","Column flow"],color:"#14B8A6"},
    {id:"fonts",label:"Fonts",icon:"Aa",checks:["Embedded","Unicode map","Actual text"],color:P.slateL}
  ];

  /** PDF Tag Structure Map — visual tree of PDF tag hierarchy for accessibility review */
  function pdfTagMap(doc,isRem){
    if(!doc)return null;
    var issues=doc.issues||[];
    var activeIssues=isRem?issues.filter(function(i){return i.remediated}):issues.filter(function(i){return!i.remediated});
    /* Map WCAG criteria to PDF zones */
    var zoneMap={"1.1.1":"images","1.3.1":"tags","1.3.2":"reading","1.4.3":"fonts","2.4.1":"tags","2.4.2":"meta","2.4.4":"links","3.1.1":"meta","3.3.2":"forms","4.1.1":"tags","4.1.2":"tags"};
    var zoneIssues={};
    activeIssues.forEach(function(iss,idx){
      var zid=zoneMap[iss.wcag]||"tags";
      if(!zoneIssues[zid])zoneIssues[zid]=[];
      zoneIssues[zid].push({iss:iss,idx:idx});
    });
    return E("div",{style:{padding:"6px 8px"}},
      PDF_ZONES.map(function(zone){
        var zi=zoneIssues[zone.id]||[];
        var isEmpty=zi.length===0;
        var hasCrit=zi.some(function(x){return x.iss.sev==="critical"});
        var hasMaj=zi.some(function(x){return x.iss.sev==="major"});
        var borderColor=isRem?(isEmpty?"#E5E7EB":"#059669"):hasCrit?"#DC2626":hasMaj?"#D97706":isEmpty?"#E5E7EB":"#3B82F6";
        var totalEls=zi.reduce(function(x,item){return x+(item.iss.count||1)},0);
        return E("div",{key:zone.id,style:{marginBottom:2,borderRadius:5,border:"1.5px solid "+(isEmpty?borderColor+"50":borderColor),opacity:isEmpty?.35:1,transition:"all .15s"}},
          E("div",{style:{display:"flex",alignItems:"center",padding:"3px 8px",gap:5}},
            E("div",{style:{width:20,height:20,borderRadius:4,background:zone.color+"18",color:zone.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,flexShrink:0}},zone.icon),
            E("span",{style:{fontSize:10,fontWeight:700,color:isEmpty?"#94A3B8":"#374151",minWidth:80}},zone.label),
            isEmpty?E("span",{style:{fontSize:10,color:"#CBD5E1",fontStyle:"italic"}},"\u2014 OK"):
            E("div",{style:{display:"flex",gap:2,flexWrap:"wrap",flex:1}},
              zi.map(function(item,mi){
                var sc=item.iss.sev==="critical"?"#DC2626":item.iss.sev==="major"?"#D97706":"#3B82F6";
                return E("div",{key:mi,onClick:function(ev){ev.stopPropagation();setHiIss(item.idx)},
                  title:"WCAG "+item.iss.wcag+": "+item.iss.desc.slice(0,60),
                  style:{padding:"1px 5px",borderRadius:3,fontSize:10.5,fontWeight:700,cursor:"pointer",
                    background:isRem?"#05966920":sc+"15",color:isRem?"#059669":sc}},
                  isRem?"\u2713":item.iss.wcag,
                  (item.iss.count||1)>1?E("span",{style:{marginLeft:2,opacity:.65,fontSize:10}},"\u00D7"+item.iss.count):null);
              })),
            !isEmpty?E("div",{style:{marginLeft:"auto",padding:"1px 6px",borderRadius:3,flexShrink:0,
              background:isRem?"#ECFDF5":hasCrit?"#FEE2E2":hasMaj?"#FEF3C7":"#DBEAFE",
              color:isRem?"#059669":hasCrit?"#DC2626":hasMaj?"#92400E":"#1D4ED8",
              fontSize:10,fontWeight:700}},totalEls+" el"):null))
      }));
  }

  /* ═══ SMART ISSUE CARD — now PDF-aware ═══ */
  /** Expandable issue card — shows WCAG info, severity, smart fix, AI suggestions, screenshot button */
  function issueCard(iss,i,isWeb,parentDoc){
    var c=SC[iss.sev];
    /* Smart Fix dispatch — Web engine uses HTML element parsing, PDF engine uses doc metadata */
    var altSugg=null,contrastInfo=null,contrastFixes=[],headingInfo=null,readingInfo=null,smartFixes=[];
    var pdfSmart=null;
    if(isWeb===false&&parentDoc){
      /* PDF Smart Fix Engine */
      pdfSmart=generatePdfSmartFix(iss,parentDoc);
      smartFixes=pdfSmart.fixes||[];
    } else {
      /* Web Smart Fix Engine — parses HTML elements */
      altSugg=iss.wcag==="1.1.1"?suggestAltText(iss.el):null;
      contrastInfo=(iss.wcag==="1.4.3"||iss.wcag==="1.4.11")?parseContrastInfo(iss.desc,iss.el,iss.fix):null;
      contrastFixes=suggestContrastFixes(contrastInfo);
      headingInfo=(iss.wcag==="1.3.1"||iss.wcag==="2.4.6")?analyzeHeadingOrder(null,iss.el):null;
      readingInfo=analyzeReadingOrder(iss.el,iss.wcag);
      smartFixes=generateSmartFix(iss);
    }
    var pdfPanels=(pdfSmart&&pdfSmart.panels)||[];
    var hasSmartPanel=altSugg||contrastInfo||headingInfo||readingInfo||smartFixes.length>0||pdfPanels.length>0;

    return E("div",{key:i,style:{padding:"12px 14px",borderRadius:10,border:"1.5px solid "+c[0]+"18",background:c[1],marginBottom:8}},
      /* Header row */
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"start",gap:8,flexWrap:"wrap"}},
        E("div",{style:{flex:1}},
          E("div",{style:{display:"flex",gap:4,alignItems:"center",marginBottom:3,flexWrap:"wrap"}},E(SevB,{s:iss.sev}),E("span",{style:{fontSize:11,fontWeight:800}},"WCAG "+iss.wcag),iss.engine?E("span",{style:{padding:"1px 5px",borderRadius:3,fontSize:10,fontWeight:700,background:iss.engine==="htmlcs"?"#451a03":"#1e3a5f",color:iss.engine==="htmlcs"?"#fde68a":"#93c5fd"}},iss.engine==="htmlcs"?"HTMLCS":"axe"):null,E("span",{style:{fontSize:10,color:P.slateM}},iss.info.n),
            /* Priority + Impact badges */
            iss.priority?E("span",{style:{padding:"1px 5px",borderRadius:3,fontSize:10,fontWeight:700,background:iss.priority==="P1"?"#DC262618":iss.priority==="P2"?"#D9770618":"#2563EB18",color:iss.priority==="P1"?"#DC2626":iss.priority==="P2"?"#D97706":"#2563EB"}},iss.priority):null),
          E("div",{style:{fontSize:12,color:P.slateM,lineHeight:1.5}},iss.desc)),
        E("div",{style:{textAlign:"center",minWidth:50}},E("div",{style:{fontSize:22,fontWeight:800,color:c[0]}},iss.count),E("div",{style:{fontSize:10.5,color:P.slateL}},iss.count===1?"instance":"instances"))),

      /* Code before/after */
      iss.el?E("div",{className:"rg2",style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}},
        E("div",{style:{borderRadius:6,border:"1.5px solid "+P.sCrit+"20",overflow:"hidden"}},E("div",{style:{padding:"2px 8px",background:P.sCritBg,fontSize:10.5,fontWeight:700,color:P.sCrit}},"CURRENT"),E("pre",{style:{margin:0,padding:"6px 8px",fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap"}},iss.el)),
        E("div",{style:{borderRadius:6,border:"1.5px solid "+P.emerald+"20",overflow:"hidden"}},E("div",{style:{padding:"2px 8px",background:P.emeraldLt,fontSize:10.5,fontWeight:700,color:P.emerald}},"FIXED"),E("pre",{style:{margin:0,padding:"6px 8px",fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap"}},iss.fix))):null,

      /* ══ SMART REMEDIATION PANEL ══ */
      hasSmartPanel?E("div",{style:{marginTop:10,borderRadius:8,border:"1.5px solid "+P.accent+"25",background:"linear-gradient(135deg,#F0F9FF,#ECFEFF,#F0FDF4)",overflow:"hidden"}},
        E("div",{style:{padding:"6px 10px",background:P.accent+"12",display:"flex",alignItems:"center",gap:6}},
          E("span",{style:{fontSize:12}},"\u{1F9E0}"),
          E("span",{style:{fontSize:10.5,fontWeight:700,color:P.accent}},"PiARIS Smart Fix"),
          E("span",{style:{fontSize:10,color:P.slateL,marginLeft:"auto"}},"AI-powered suggestion")),

        E("div",{style:{padding:"10px"}},

          /* Alt Text Suggestion */
          altSugg?E("div",{style:{marginBottom:8}},
            E("div",{style:{fontSize:10,fontWeight:700,color:P.amberDk,marginBottom:4}},"\u{1F5BC} Suggested Alt Text"),
            E("div",{style:{padding:"8px 10px",borderRadius:6,background:P.card,border:"1px solid #FDE68A"}},
              E("div",{style:{fontFamily:FC,fontSize:11,fontWeight:600,color:P.slate}},
                'alt="'+altSugg.suggestion+'"'),
              E("div",{style:{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}},
                E("span",{style:{fontSize:10,color:P.slateL}},"\u{1F4C1} Source: "+altSugg.filename),
                E("span",{style:{padding:"1px 6px",borderRadius:3,fontSize:10,fontWeight:600,background:altSugg.confidence==="high"?"#ECFDF5":altSugg.confidence==="medium"?"#FFFBEB":"#F1F5F9",color:altSugg.confidence==="high"?"#059669":altSugg.confidence==="medium"?"#92400E":"#64748B"}},altSugg.confidence+" confidence"),
                altSugg.isInLink?E("span",{style:{fontSize:10,color:P.accent,fontWeight:600}},"\u{1F517} Image is inside a link \u2014 alt should describe destination"):null))):null,

          /* Contrast Fix Panel */
          contrastInfo?E("div",{style:{marginBottom:8}},
            E("div",{style:{fontSize:10,fontWeight:700,color:P.pri,marginBottom:4}},"\u{1F3A8} Contrast Analysis"),
            E("div",{style:{padding:"8px 10px",borderRadius:6,background:P.card,border:"1px solid #DDD6FE"}},
              E("div",{style:{display:"flex",gap:12,alignItems:"center",marginBottom:6,flexWrap:"wrap"}},
                E("div",{style:{textAlign:"center"}},
                  E("div",{style:{fontSize:18,fontWeight:800,color:contrastInfo.currentRatio>=contrastInfo.required?"#059669":"#DC2626"}},contrastInfo.currentRatio+":1"),
                  E("div",{style:{fontSize:10,color:P.slateL}},"Current")),
                E("span",{style:{fontSize:14,color:P.slateL}},"\u2192"),
                E("div",{style:{textAlign:"center"}},
                  E("div",{style:{fontSize:18,fontWeight:800,color:P.emerald}},contrastInfo.required+":1"),
                  E("div",{style:{fontSize:10,color:P.slateL}},"Required")),
                E("div",{style:{padding:"3px 8px",borderRadius:4,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700}},"Gap: "+contrastInfo.deficit),
                contrastInfo.fg&&contrastInfo.bg?E("div",{style:{display:"flex",gap:4,alignItems:"center"}},
                  E("div",{style:{width:50,height:24,borderRadius:4,background:contrastInfo.bg,display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid "+P.bdr}},
                    E("span",{style:{color:contrastInfo.fg,fontSize:11,fontWeight:700}},"Abc")),
                  E("div",{style:{fontSize:10,color:P.slateL}},contrastInfo.fg+" / "+contrastInfo.bg)):null),
              E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,marginBottom:4}},"Accessible Alternatives:"),
              E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
                contrastFixes.map(function(fix,fi){
                  return E("div",{key:fi,style:{flex:"1 1 140px",padding:"6px 8px",borderRadius:5,border:"1px solid "+P.bdr,background:P.bg}},
                    E("div",{style:{fontSize:10,fontWeight:700,color:P.pri}},fix.label),
                    E("div",{style:{fontSize:10,color:P.slateM,marginTop:2}},fix.desc),
                    E("code",{style:{display:"block",fontSize:10,fontFamily:FC,color:P.emerald,marginTop:3,background:P.emeraldLt,padding:"2px 4px",borderRadius:3}},fix.css),
                    fix.isSwatch&&contrastInfo.fg&&contrastInfo.bg?E("div",{style:{display:"flex",gap:4,marginTop:4,alignItems:"center"}},
                      E("div",{style:{width:40,height:18,borderRadius:3,background:contrastInfo.bg,display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid "+P.bdr}},
                        E("span",{style:{color:contrastInfo.fg,fontSize:10,fontWeight:700}},"Aa")),
                      E("span",{style:{fontSize:10,color:P.sCrit,fontWeight:600}},"\u2717 "+contrastInfo.currentRatio+":1")):null);
                })))):null,

          /* Heading Order Analysis */
          headingInfo?E("div",{style:{marginBottom:8}},
            E("div",{style:{fontSize:10,fontWeight:700,color:P.pri,marginBottom:4}},"H\u20D7 Heading Hierarchy Fix"),
            E("div",{style:{padding:"8px 10px",borderRadius:6,background:P.card,border:"1px solid #DDD6FE"}},
              headingInfo.issues.map(function(hi,hii){
                return E("div",{key:hii,style:{display:"flex",gap:8,alignItems:"center",marginBottom:4}},
                  E("span",{style:{padding:"2px 6px",borderRadius:3,background:P.sCritBg,color:P.sCrit,fontFamily:FC,fontSize:10,fontWeight:700}},"H"+headingInfo.currentLevel),
                  E("span",{style:{fontSize:12,color:P.slateL}},"\u2192"),
                  E("span",{style:{padding:"2px 6px",borderRadius:3,background:P.emeraldLt,color:P.emerald,fontFamily:FC,fontSize:10,fontWeight:700}},hi.corrected),
                  E("span",{style:{fontSize:10,color:P.slateM}},hi.problem));
              }))):null,

          /* Reading Order Tips */
          readingInfo?E("div",{style:{marginBottom:8}},
            E("div",{style:{fontSize:10,fontWeight:700,color:P.accent,marginBottom:4}},"\u{1F4D6} Reading Order & Navigation"),
            E("div",{style:{padding:"8px 10px",borderRadius:6,background:P.card,border:"1px solid #A5F3FC"}},
              readingInfo.tips.map(function(tip,ti){
                return E("div",{key:ti,style:{display:"flex",gap:6,alignItems:"start",marginBottom:ti<readingInfo.tips.length-1?4:0}},
                  E("span",{style:{fontSize:12,flexShrink:0}},tip.icon),
                  E("span",{style:{fontSize:10.5,color:P.slate,lineHeight:1.4}},tip.text),
                  E("span",{style:{padding:"1px 5px",borderRadius:3,fontSize:10,fontWeight:700,background:tip.priority==="P1"?"#FEE2E2":"#FEF3C7",color:tip.priority==="P1"?"#DC2626":"#92400E",flexShrink:0}},tip.priority));
              }))):null,

          /* Smart Code Fixes */
          smartFixes.length>0?E("div",null,
            E("div",{style:{fontSize:10,fontWeight:700,color:P.emerald,marginBottom:4}},"\u{1F527} Contextual Fix"+(smartFixes.length>1?"es":"")),
            smartFixes.map(function(sf,si){
              return sf.type==="code"?E("div",{key:si,style:{marginBottom:si<smartFixes.length-1?6:0,padding:"6px 8px",borderRadius:6,background:P.card,border:"1px solid "+P.emerald+"30"}},
                E("div",{style:{fontSize:10,fontWeight:700,color:P.emerald,marginBottom:4}},sf.label),
                E("div",{className:"rg2",style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}},
                  E("div",null,
                    E("div",{style:{fontSize:10,fontWeight:600,color:P.sCrit,marginBottom:2}},"Before:"),
                    E("pre",{style:{margin:0,padding:"4px 6px",borderRadius:4,background:P.sCritBg,fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap",border:"1px solid #FECACA"}},sf.before)),
                  E("div",null,
                    E("div",{style:{fontSize:10,fontWeight:600,color:P.emerald,marginBottom:2}},"After:"),
                    E("pre",{style:{margin:0,padding:"4px 6px",borderRadius:4,background:P.emeraldLt,fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap",border:"1px solid #BBF7D0"}},sf.after)))):
              E("div",{key:si,style:{marginBottom:si<smartFixes.length-1?6:0,padding:"6px 8px",borderRadius:6,background:P.sMajBg,border:"1px solid #FDE68A"}},
                E("div",{style:{fontSize:10,fontWeight:700,color:P.amberDk}},"\u{1F4A1} "+sf.label),
                E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},sf.text));
            })):null,

          /* PDF Smart Fix Panels — contextual guidance for document accessibility */
          pdfPanels.length>0?pdfPanels.map(function(panel,pi){
            return E("div",{key:"pp"+pi,style:{marginBottom:8}},
              E("div",{style:{fontSize:10,fontWeight:700,color:panel.color,marginBottom:4}},panel.title),
              E("div",{style:{padding:"8px 10px",borderRadius:6,background:P.card,border:"1px solid "+panel.border}},
                panel.content.map(function(tip,ti){
                  return E("div",{key:ti,style:{display:"flex",gap:6,alignItems:"start",marginBottom:ti<panel.content.length-1?5:0}},
                    E("span",{style:{fontSize:11,flexShrink:0,width:18,textAlign:"center"}},tip.icon),
                    E("span",{style:{fontSize:10.5,color:P.slate,lineHeight:1.4,flex:1}},tip.text),
                    E("span",{style:{padding:"1px 5px",borderRadius:3,fontSize:10,fontWeight:700,flexShrink:0,
                      background:tip.priority==="P1"?"#FEE2E2":"#FEF3C7",color:tip.priority==="P1"?"#DC2626":"#92400E"}},tip.priority));
                })));
          }):null,

          /* Diagram type fixes (tag tree, heading sequence) */
          smartFixes.filter(function(sf){return sf.type==="diagram"}).length>0?smartFixes.filter(function(sf){return sf.type==="diagram"}).map(function(sf,si){
            return E("div",{key:"diag"+si,style:{marginBottom:6}},
              E("div",{style:{fontSize:10,fontWeight:700,color:P.pri,marginBottom:4}},"\u{1F3D7} "+sf.label),
              E("pre",{style:{margin:0,padding:"8px 10px",borderRadius:6,background:P.bgS,border:"1px solid "+P.bdr,fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap",lineHeight:1.5,color:P.slateM}},sf.diagram));
          }):null

        )):null,

      /* Standard recommendation */
      !hasSmartPanel?E("div",{style:{marginTop:8,padding:"8px 10px",borderRadius:6,background:P.accentLt,border:"1px solid "+P.accent+"20"}},
        E("span",{style:{fontSize:10,fontWeight:700,color:P.accent}},"\u{1F527} "),E("span",{style:{fontSize:11,color:P.slate}},iss.info.r)):null
    );
  }

  /* LIST ITEM */
  /** List item for page/document sidebar — shows name, issue count, severity breakdown */
  function listItem(item,i,isWeb){
    var label=isWeb?item.n:item.name;
    var sub=isWeb?item.url:(item.pages+"pg \u00B7 "+(item.tagged?"Tagged":"UNTAGGED")+(item.riskScore!==undefined?" \u00B7 Risk: "+item.riskScore:"")+(item.producer?" \u00B7 "+item.producer:""));
    return E(Card,{key:i,sx:{cursor:"pointer",paddingLeft:isWeb?(14+item.d*12)+"px":undefined},pad:10,onClick:function(){setSel(i);setHiIss(null);setInspPane("original");setPdfPage(1)}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}},
        E("div",{style:{flex:1,minWidth:0}},
          E("div",{style:{fontSize:12,fontWeight:600}},label,
            !isWeb&&!item.tagged?E("span",{style:{padding:"1px 6px",borderRadius:4,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700,marginLeft:6}},"UNTAGGED"):null,
            !isWeb&&item.riskLevel==="high"?E("span",{style:{padding:"1px 6px",borderRadius:4,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700,marginLeft:4}},"HIGH RISK"):
            !isWeb&&item.riskLevel==="medium"?E("span",{style:{padding:"1px 6px",borderRadius:4,background:P.sMajBg,color:P.amberDk,fontSize:10,fontWeight:700,marginLeft:4}},"MED RISK"):null),
          E("div",{style:{fontSize:10,color:P.slateL,fontFamily:FC,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},sub),
          !isWeb&&(item.foundOn||item.url)?E("div",{style:{display:"flex",gap:8,marginTop:3,alignItems:"center",flexWrap:"wrap"}},
            item.foundOn?E("span",{style:{fontSize:10,color:P.pri,fontWeight:600}},"\u{1F517} Found on: "+item.foundOn):null,
            item.url?E("span",{style:{fontSize:10,color:"#0EA5E9",fontFamily:FC,overflow:"hidden",textOverflow:"ellipsis",maxWidth:280,whiteSpace:"nowrap",display:"inline-block"}},item.url):null,
            item.department?E("span",{style:{padding:"1px 5px",borderRadius:3,background:P.priLt,color:P.pri,fontSize:9,fontWeight:600}},item.department):null,
            item.functionalType?E("span",{style:{padding:"1px 5px",borderRadius:3,background:"#ECFEFF",color:P.accent,fontSize:9,fontWeight:600}},item.functionalType):null
          ):null),
        E("div",{style:{display:"flex",gap:5,alignItems:"center",flexShrink:0}},
          !isWeb&&docState(item)!=="imported"?docStatePill(item):null,
          !isWeb?E("span",{style:{padding:"2px 7px",borderRadius:6,background:item.priority==="critical"?P.sCritBg:item.priority==="high"?P.sMajBg:P.sMinBg,color:item.priority==="critical"?P.sCrit:item.priority==="high"?P.sMaj:P.sMin,fontSize:10,fontWeight:700}},"\u25B2 "+item.priority):null,
          E("span",{style:{fontSize:11,fontWeight:700,color:P.sCrit}},item.totalIssues+" issues"),
          item.critCount>0?E(SevB,{s:"critical",sm:true}):null,
          !isWeb&&domainLicensed?(function(){
            /* PR-K.26: Doc Audit's per-row action now runs the deep
               PDF/UA audit (same engine PA11Y Lab uses) instead of
               routing into DocMaya HTML conversion. Conversion was an
               access workaround, not a fix \u2014 auditing identifies
               what's wrong with the actual document so doc-remediate
               can guide a real repair. Available on every doc (not
               just high-risk): the quick crawl-time pass can misjudge
               risk, the deep audit is the authority. */
            var dIdx=dc.indexOf(item);
            var as=docAuditStatus[dIdx];
            if(as&&as.status==="complete"){
              return E("span",{title:"Deep PDF/UA audit complete",style:{padding:"3px 8px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u267F "+as.total+" issue"+(as.total===1?"":"s"));
            }
            if(as&&as.status==="running"){
              return E("span",{style:{padding:"3px 8px",borderRadius:6,background:P.priLt,color:P.pri,fontSize:10,fontWeight:700,animation:"markerPulse 1.5s infinite"}},"\u23F3 Auditing\u2026");
            }
            if(as&&as.status==="failed"){
              return E("span",{title:as.error||"Audit failed",style:{padding:"3px 8px",borderRadius:6,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700}},"\u2717 Failed");
            }
            return E("button",{onClick:function(ev){ev.stopPropagation();auditDocs(dIdx)},
              title:"Run the deep PDF/UA accessibility audit (Matterhorn + WCAG) on this document",
              style:{padding:"3px 8px",borderRadius:6,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",
                fontSize:10,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}},"\u267F Audit");
          })():null,
          E("span",{style:{color:P.slateL}},"\u2192"))));
  }

  /* DETAIL HEADER */
  /** Detail header bar for selected page/doc — shows title, score ring, severity counts */
  function detailHeader(item,isWeb){
    return E("div",null,
      E("button",{onClick:function(){setSel(null)},style:{padding:"4px 12px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600,marginBottom:12}},"\u2190 Back"),
      E(Card,null,
        E("div",{style:{padding:"12px 16px",borderBottom:"1.5px solid "+P.bdr,background:P.bgS,margin:"-16px -16px 16px",borderRadius:"12px 12px 0 0"}},
          E("h3",{style:{fontSize:16,fontWeight:800,margin:0}},isWeb?item.n:item.name),
          E("div",{style:{fontSize:11,color:P.slateL,fontFamily:FC,margin:"3px 0"}},isWeb?item.url:(item.pages+" pages \u00B7 "+(item.tagged?"Tagged":"UNTAGGED")+" \u00B7 "+item.producer+(!isWeb?" \u00B7 "+item.views.toLocaleString()+" views/mo":""))),
          E("div",{style:{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}},
            E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.sCritBg,color:P.sCrit,fontSize:11,fontWeight:700}},item.wcagsFailed+" WCAG failures \u00B7 "+item.totalIssues+" issues"),
            E(SevB,{s:"critical"}),E("span",{style:{fontWeight:700,fontSize:11}},item.critCount),
            E(SevB,{s:"major"}),E("span",{style:{fontWeight:700,fontSize:11}},item.majCount),
            E(SevB,{s:"minor"}),E("span",{style:{fontWeight:700,fontSize:11}},item.minCount))),
        item.issues.map(function(iss,i){return issueCard(iss,i,isWeb,item)})));
  }

  /* ═══ SHARED ISSUE PANEL (right side of split-pane) ═══ */
  /** Right-side issue panel in split-pane — filterable list of issues for selected page/doc */
  function issuePanel(issues,label,pageIdx,isWeb){
    var filtered=sevFilter==="all"?issues:issues.filter(function(x){return x.sev===sevFilter});
    var openCount=issues.length-issues.filter(function(x){return x.remediated}).length;
    var fixedCount=issues.filter(function(x){return x.remediated}).length;
    var totalElem=issues.reduce(function(s,x){return s+(x.count||1)},0);
    return E("div",{style:{background:P.card,overflow:"auto",maxHeight:558}},
      E("div",{style:{padding:"8px 12px",background:P.bgS,borderBottom:"1px solid "+P.bdr,fontSize:10,fontWeight:700,color:P.slateM,position:"sticky",top:0,zIndex:5}},
        E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}},
          E("span",null,"\u{1F50D} "+issues.length+" WCAG violation"+(issues.length!==1?"s":"")+" ("+totalElem+" element"+(totalElem!==1?"s":"")+")"),
          E("div",{style:{display:"flex",gap:4}},
            fixedCount>0?E("span",{style:{padding:"2px 7px",borderRadius:10,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u2713 "+fixedCount+" fixed"):null,
            E("span",{style:{padding:"2px 7px",borderRadius:10,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700}},openCount+" open"))),
        /* Severity filter toolbar */
        E("div",{style:{display:"flex",gap:3},role:"group","aria-label":"Filter by severity"},
          ["all","critical","major","minor"].map(function(s){
            var active=sevFilter===s;
            var ct=s==="all"?issues.length:issues.filter(function(x){return x.sev===s}).length;
            var col=s==="critical"?P.sCrit:s==="major"?P.sMaj:s==="minor"?P.sMin:P.slateM;
            return E("button",{key:s,"aria-pressed":active?"true":"false",onClick:function(){setSevFilter(active?"all":s)},
              style:{padding:"2px 8px",borderRadius:4,border:"1px solid "+(active?col:P.bdr),
                background:active?col+"15":"transparent",color:active?col:P.slateL,
                fontSize:10,fontWeight:active?700:500,cursor:"pointer"}},
              (s==="all"?"All":s.charAt(0).toUpperCase()+s.slice(1))+" ("+ct+")");
          }))),
      filtered.map(function(iss,i){
        var c=SC[iss.sev]||SC.minor;
        var isActive=hiIss===i;var isRem=iss.remediated;
        return E("div",{key:i,onClick:function(){setHiIss(isActive?null:i);if(!isActive&&isWeb){highlightInProxy(iss);scrollToInProxy(iss)}else if(isActive&&isWeb){clearProxyHighlights()}if(!isActive&&!isWeb&&iss.loc&&iss.loc.pg){setPdfPage(iss.loc.pg)}},onKeyDown:function(ev){if(ev.key==="Enter"||ev.key===" "){ev.preventDefault();setHiIss(isActive?null:i);if(!isActive&&isWeb){highlightInProxy(iss);scrollToInProxy(iss)}else if(isActive&&isWeb){clearProxyHighlights()}if(!isActive&&!isWeb&&iss.loc&&iss.loc.pg){setPdfPage(iss.loc.pg)}}},tabIndex:0,role:"button","aria-expanded":isActive?"true":"false","aria-label":"Issue "+(i+1)+": WCAG "+iss.wcag,style:{
          padding:"10px 12px",borderBottom:"1px solid "+P.bdr,cursor:"pointer",
          background:isRem?P.emeraldLt+"60":isActive?c[1]:i%2===0?P.card:"#FAFBFE",
          borderLeft:isActive?"3px solid "+c[0]:isRem?"3px solid "+P.emerald:"3px solid transparent",
          opacity:isRem?.55:1,transition:"all .15s"}},
          E("div",{style:{display:"flex",alignItems:"center",gap:6,marginBottom:3}},
            E("div",{style:{width:20,height:20,borderRadius:"50%",background:isRem?P.emerald:c[0],color:"#fff",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}},isRem?"\u2713":i+1),
            isRem?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"FIXED"):E(SevB,{s:iss.sev}),
            iss.priority?E("span",{style:{fontSize:10,fontWeight:700,color:iss.priority==="P1"?"#DC2626":iss.priority==="P2"?"#D97706":"#94A3B8"}},iss.priority):null,
            E("span",{style:{fontSize:10.5,fontWeight:700}},"WCAG "+iss.wcag),
            iss.engine?E("span",{style:{padding:"1px 5px",borderRadius:3,fontSize:10,fontWeight:700,background:iss.engine==="htmlcs"?"#451a03":iss.engine==="both"?"#312e81":"#1e3a5f",color:iss.engine==="htmlcs"?"#fde68a":iss.engine==="both"?"#c7d2fe":"#93c5fd"}},iss.engine==="htmlcs"?"HTMLCS":iss.engine==="both"?"BOTH":"axe"):null,
            E("span",{style:{fontSize:10.5,color:P.slateM}},iss.info.n)),
          /* Impacted user groups */
          iss.impactedGroups&&iss.impactedGroups.length>0&&!isRem?E("div",{style:{marginLeft:26,marginBottom:2,display:"flex",gap:3,flexWrap:"wrap"}},
            iss.impactedGroups.map(function(g){
              var gc=g==="Screen Reader Users"?"#818cf8":g==="Keyboard Users"?"#34d399":g==="Low Vision"?"#fbbf24":g==="Color Blind"?"#fb923c":g==="Motor Impaired"?"#f472b6":g==="Cognitive"?"#a78bfa":"#94a3b8";
              return E("span",{key:g,style:{padding:"0px 4px",borderRadius:3,fontSize:10.5,fontWeight:600,background:gc+"18",color:gc}},g);
            })):null,
          E("div",{style:{fontSize:11.5,color:P.slate,marginLeft:26,lineHeight:1.4}},iss.desc,
            E("span",{style:{marginLeft:6,fontSize:10,fontWeight:700,color:c[0]}},iss.count+" "+(iss.count===1?"instance":"instances"))),
          isActive?E("div",{style:{marginLeft:26,marginTop:8}},
            iss.el?E("div",{className:"rg2",style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8}},
              E("div",{style:{borderRadius:6,border:"1.5px solid "+P.sCrit+"20",overflow:"hidden"}},
                E("div",{style:{padding:"2px 8px",background:P.sCritBg,fontSize:10,fontWeight:700,color:P.sCrit}},"CURRENT"),
                E("pre",{style:{margin:0,padding:"5px 8px",fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap"}},iss.el)),
              iss.fix?E("div",{style:{borderRadius:6,border:"1.5px solid "+P.emerald+"20",overflow:"hidden"}},
                E("div",{style:{padding:"2px 8px",background:P.emeraldLt,fontSize:10,fontWeight:700,color:P.emerald}},"FIXED"),
                E("pre",{style:{margin:0,padding:"5px 8px",fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap"}},iss.fix)):null):null,
            E("div",{style:{padding:"6px 10px",borderRadius:6,background:P.accentLt,border:"1px solid "+P.accent+"15",marginBottom:6}},
              E("span",{style:{fontSize:10,fontWeight:700,color:P.accent}},"\u{1F527} "),
              E("span",{style:{fontSize:10.5,color:P.slate}},iss.info.r)),
            pageIdx!==undefined?E("button",{onClick:function(ev){ev.stopPropagation();toggleIssue(pageIdx,i,isWeb)},style:{padding:"5px 12px",borderRadius:6,border:"none",background:iss.remediated?"#F1F5F9":"linear-gradient(135deg,"+P.emerald+","+P.accent+")",color:iss.remediated?P.slateM:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},iss.remediated?"\u21A9 Undo Fix":"\u2713 Mark as Fixed"):null,
            /* Screenshot capture button */
            isWeb&&pageIdx!==undefined?E("button",{onClick:function(ev){ev.stopPropagation();captureIssuePair(pageIdx,i)},style:{padding:"5px 12px",borderRadius:6,border:"1.5px solid #6366F130",background:P.priLt,color:P.pri,fontSize:10,fontWeight:700,cursor:"pointer",marginLeft:4}},"\u{1F4F7} Capture"):null,
            /* Show existing screenshots if captured */
            iss.screenshotBefore||iss.screenshotAfter?E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginTop:8}},
              iss.screenshotBefore?E("div",{style:{borderRadius:6,border:"1.5px solid "+P.sCrit+"30",overflow:"hidden"}},
                E("div",{style:{padding:"2px 8px",background:P.sCritBg,fontSize:10,fontWeight:700,color:P.sCrit}},"\u{1F534} Before"),
                E("img",{src:iss.screenshotBefore,alt:"Before screenshot",style:{width:"100%",display:"block"}})):null,
              iss.screenshotAfter?E("div",{style:{borderRadius:6,border:"1.5px solid "+P.emerald+"30",overflow:"hidden"}},
                E("div",{style:{padding:"2px 8px",background:P.emeraldLt,fontSize:10,fontWeight:700,color:P.emerald}},"\u{1F7E2} After"),
                E("img",{src:iss.screenshotAfter,alt:"After screenshot",style:{width:"100%",display:"block"}})):null):null
          ):null);
      }));
  }

  /* ═══ ISSUE MARKERS overlay (shared) ═══ */
  /** Issue marker overlay — positioned dots on baseline preview pane */
  function markers(issues){
    return issues.map(function(iss,i){
      var isRem=iss.remediated;var col=isRem?P.emerald:iss.sev==="critical"?P.sCrit:iss.sev==="major"?P.amber:"#2563EB";
      var isActive=hiIss===i;
      return E("div",{key:"m"+i,onClick:function(ev){ev.stopPropagation();setHiIss(isActive?null:i)},title:"WCAG "+iss.wcag+": "+iss.desc,style:{
        position:"absolute",left:iss.loc.x+"%",top:iss.loc.y+"%",transform:"translate(-50%,-50%)",
        width:isActive?28:isRem?16:20,height:isActive?28:isRem?16:20,borderRadius:"50%",
        background:col,color:"#fff",fontSize:isActive?11:9,fontWeight:800,
        display:"flex",alignItems:"center",justifyContent:"center",
        cursor:"pointer",zIndex:isActive?20:10,
        boxShadow:isActive?"0 0 0 4px "+col+"40, 0 0 12px "+col+"60":"0 2px 6px rgba(0,0,0,.25)",
        transition:"all .2s",border:"2px solid #fff",
        opacity:isRem?.5:1,animation:isActive&&!isRem?"markerPulse 1.5s infinite":"none"}},isRem?"\u2713":i+1);
    });
  }

  /* ═══ HEADER BAR for split-pane (shared) ═══ */
  /** Header bar for split-pane view — shows page title, AI enrich button, export controls */
  function splitHeader(item,isWeb){
    var idx=isWeb?wp.indexOf(item):dc.indexOf(item);
    var displayId=isWeb?wpId(idx):docId(idx);
    var url=isWeb?item.url:item.url;
    var eKey=url?entityKey(url):"";
    return E("div",{style:{padding:"10px 16px",borderRadius:"10px 10px 0 0",background:P.bgS,border:"1.5px solid "+P.bdr,borderBottom:"none"}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}},
        E("div",null,
          E("span",{title:"Session ID",style:{display:"inline-block",padding:"2px 8px",borderRadius:6,background:P.pri+"15",color:P.pri,fontSize:10,fontWeight:800,fontFamily:FC,marginRight:8,letterSpacing:".04em"}},displayId),
          eKey?E("span",{title:"Stable entity key (same across re-scans)",style:{display:"inline-block",padding:"2px 6px",borderRadius:4,background:P.bgS,color:P.slateL,fontSize:9,fontWeight:600,fontFamily:FC,marginRight:8,border:"1px dashed "+P.bdr}},"\u229B "+eKey):null,
          E("span",{style:{fontSize:15,fontWeight:800}},isWeb?item.n:item.name),
          E("span",{style:{fontSize:11,color:P.slateL,fontFamily:FC,marginLeft:8}},isWeb?item.url:(item.pages+" pages \u00B7 "+item.producer))),
        E("div",{style:{display:"flex",gap:5,flexWrap:"wrap"}},
          E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700}},item.wcagsFailed+" WCAG \u00B7 "+item.totalIssues+" issues"),
          E(SevB,{s:"critical"}),E("span",{style:{fontWeight:700,fontSize:10}},item.critCount),
          E(SevB,{s:"major"}),E("span",{style:{fontWeight:700,fontSize:10}},item.majCount),
          E(SevB,{s:"minor"}),E("span",{style:{fontWeight:700,fontSize:10}},item.minCount),
          (item.remediatedCount||0)>0?E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700,marginLeft:4}},"\u2713 "+(item.remediatedCount||0)+" fixed"):null)),
      /* Template/page-specific breakdown for web pages */
      isWeb&&templateStats.templateGroupCount>0?(function(){
        var pageIdx=wp.indexOf(item);
        if(pageIdx<0)return null;
        var tplCount=0;var pgCount=0;
        (item.issues||[]).forEach(function(iss){
          var nSel=normalizeSelector(iss.selector||"");
          var descKey=(iss.desc||"").substring(0,60).toLowerCase().replace(/\s+/g," ");
          var tKey=(iss.wcag||"")+"||"+nSel+"||"+descKey;
          var tg=templateStats.groups[tKey];
          if(tg&&tg.pages.length>1&&tg.isSpecific){tplCount+=(iss.count||1)}
          else{pgCount+=(iss.count||1)}
        });
        return tplCount>0?E("div",{style:{marginTop:4,fontSize:10,color:P.pri,fontWeight:600}},
          "\u{1F501} "+tplCount+" template (fix once) \u00B7 "+pgCount+" page-specific"):null;
      })():null,
      !isWeb?(item.foundOn||item.url||item.department)?E("div",{style:{display:"flex",gap:10,marginTop:6,alignItems:"center",flexWrap:"wrap",paddingTop:6,borderTop:"1px solid "+P.bdr}},
        item.foundOn?E("div",{style:{display:"flex",alignItems:"center",gap:4}},
          E("span",{style:{fontSize:10,color:P.slateM}},"\u{1F4C4} Source page:"),
          E("span",{style:{fontSize:10,fontWeight:700,color:P.pri,fontFamily:FC}},item.foundOn)):null,
        item.url?E("div",{style:{display:"flex",alignItems:"center",gap:4}},
          E("span",{style:{fontSize:10,color:P.slateM}},"\u{1F517} PDF URL:"),
          E("a",{href:item.url,target:"_blank",rel:"noopener",onClick:function(ev){ev.stopPropagation()},
            style:{fontSize:10,color:"#0EA5E9",fontFamily:FC,textDecoration:"underline",maxWidth:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"inline-block"}},item.url)):null,
        item.department?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.priLt,color:P.pri,fontSize:10,fontWeight:600}},"\u{1F3E2} "+item.department):null,
        item.functionalType?E("span",{style:{padding:"2px 6px",borderRadius:4,background:"#ECFEFF",color:P.accent,fontSize:10,fontWeight:600}},item.functionalType):null
      ):null:null);
  }
  /** Remediated marker overlay — green dots on deliverable preview pane */
  function remMarkers(issues){
    return issues.map(function(iss,i){
      var isRem=iss.remediated;
      var col=isRem?P.emerald:iss.sev==="critical"?P.sCrit:iss.sev==="major"?P.amber:"#2563EB";
      var isActive=hiIss===i;
      return E("div",{key:"rm"+i,onClick:function(ev){ev.stopPropagation();setHiIss(isActive?null:i)},title:(isRem?"FIXED: ":"")+"WCAG "+iss.wcag,style:{
        position:"absolute",left:iss.loc.x+"%",top:iss.loc.y+"%",transform:"translate(-50%,-50%)",
        width:isRem?16:isActive?28:20,height:isRem?16:isActive?28:20,borderRadius:"50%",
        background:col,color:"#fff",fontSize:isRem?7:isActive?11:9,fontWeight:800,
        display:"flex",alignItems:"center",justifyContent:"center",
        cursor:"pointer",zIndex:isActive?20:isRem?5:10,opacity:isRem?.65:1,
        boxShadow:isActive?"0 0 0 3px "+col+"40":"0 1px 4px rgba(0,0,0,.2)",
        transition:"all .2s",border:"2px solid #fff"}},isRem?"\u2713":i+1);
    });
  }
  /** Pane label bar — "Baseline"/"Deliverable" header with optional sync toggle button.
      Session 47: Sync button redesigned for ADA compliance — high contrast, distinct states,
      minimum 44px touch target, visible focus indicator, clear locked/unlocked distinction. */
  function paneLabel(icon,title,sub,color,showSync){return E("div",{style:{padding:"6px 12px",background:color+"0A",borderBottom:"2px solid "+color,display:"flex",justifyContent:"space-between",alignItems:"center"}},E("div",{style:{display:"flex",alignItems:"center",gap:6}},E("span",{style:{fontSize:13}},icon),E("span",{style:{fontSize:11,fontWeight:800,color:color,letterSpacing:"0.02em"}},title)),E("div",{style:{display:"flex",alignItems:"center",gap:8}},E("span",{style:{fontSize:10,color:P.slateM,fontWeight:500}},sub),showSync?E("button",{onClick:function(){setPanesLocked(!panesLocked)},title:panesLocked?"Panes scroll together \u2014 click to scroll independently":"Panes scroll independently \u2014 click to sync","aria-label":panesLocked?"Unlock pane scrolling":"Lock pane scrolling","aria-pressed":panesLocked?"true":"false",style:{padding:"6px 14px",borderRadius:8,border:"2px solid "+(panesLocked?"#1D4ED8":"#DC2626"),background:panesLocked?"#1D4ED8":"#FFFFFF",color:panesLocked?"#FFFFFF":"#DC2626",fontSize:12,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:6,minHeight:36,transition:"all .15s",boxShadow:panesLocked?"0 2px 8px rgba(29,78,216,.3)":"0 2px 8px rgba(220,38,38,.15)"}},panesLocked?"\u{1F512} Synced":"\u{1F513} Independent"):null))}
  /** Mini PAC compliance badge — shows PASS/FAIL with fix count for a page/doc */
  function pacMini(item){var fx=item.issues.filter(function(i){return i.remediated}).length,op=item.issues.length-fx,oc=item.issues.filter(function(i){return!i.remediated&&i.sev==="critical"}).length,sc=item.issues.length>0?Math.round(fx/item.issues.length*100):100,pass=oc===0;return E("div",{style:{padding:"6px 10px",background:pass?"#F0FDF4":"#FEF2F2",borderTop:"1px solid "+(pass?P.emerald:P.sCrit)+"30"}},E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center"}},E("div",{style:{display:"flex",alignItems:"center",gap:5}},E("span",{style:{fontSize:10,fontWeight:800,color:pass?P.emerald:P.sCrit}},"\u2699\uFE0F PAC 2026"),E("span",{style:{padding:"1px 6px",borderRadius:4,fontSize:10,fontWeight:700,background:pass?P.emeraldLt:P.sCritBg,color:pass?P.emerald:P.sCrit}},pass?"PASS":"FAIL")),E("span",{style:{fontSize:10,fontWeight:700,color:pass?P.emerald:P.sCrit}},sc+"% resolved")),E("div",{style:{display:"flex",gap:8,fontSize:10,color:P.slateM,marginTop:2}},E("span",null,"\u2713 "+fx+" fixed"),op>0?E("span",{style:{color:P.sCrit}},"\u2717 "+op+" remaining"):E("span",{style:{color:P.emerald}},"All clear")))}
  /** Remediation summary for deliverable pane — lists open issues or shows "All clear" */
  function remSummary(issues){var open=issues.filter(function(i){return!i.remediated});return open.length>0?open.map(function(iss,i){return E("div",{key:i,style:{padding:"6px 10px",borderBottom:"1px solid "+P.bdr,fontSize:10}},E("div",{style:{display:"flex",gap:4,alignItems:"center"}},E(SevB,{s:iss.sev,sm:true}),E("span",{style:{fontWeight:700}},"WCAG "+iss.wcag),E("span",{style:{color:P.slateM}},iss.desc)))}):E("div",{style:{padding:"20px 12px",textAlign:"center",color:P.emerald}},E("div",{style:{fontSize:24,marginBottom:4}},"\u2713"),E("div",{style:{fontSize:11,fontWeight:700}},"All issues remediated"),E("div",{style:{fontSize:10,color:P.slateM,marginTop:2}},"WCAG 2.1 AA compliance achieved"))}

  /* ═══ ZONE CLASSIFIER — maps HTML elements to page zones ═══ */
  var ZONES=[
    {id:"doc",label:"Document",icon:"\u{1F4C4}",match:["<html","<meta","lang=","<title"],color:P.pri},
    {id:"head",label:"Header",icon:"\u2B50",match:["<header","logo","brand","site-header"],color:P.pri},
    {id:"nav",label:"Navigation",icon:"\u2630",match:["<nav","menu","navbar","nav-","breadcrumb"],color:"#3B82F6"},
    {id:"hero",label:"Hero / Banner",icon:"\u{1F3AC}",match:["hero","banner","carousel","slider","jumbotron"],color:"#06B6D4"},
    {id:"img",label:"Images",icon:"\u{1F5BC}",match:["<img","alt=","<figure","<picture"],color:"#F59E0B"},
    {id:"heading",label:"Headings",icon:"H",match:["<h1","<h2","<h3","<h4","<h5","<h6"],color:P.pri},
    {id:"content",label:"Main Content",icon:"\u{1F4DD}",match:["<main","<article","<section","<p ","<div","<span"],color:P.slateL},
    {id:"form",label:"Forms",icon:"\u{1F4CB}",match:["<form","<input","<select","<textarea","<label","<fieldset","placeholder"],color:"#EC4899"},
    {id:"interactive",label:"Interactive / ARIA",icon:"\u{1F446}",match:["<button","role=","aria-","tabindex"],color:"#F97316"},
    {id:"media",label:"Media / Embeds",icon:"\u{1F3A5}",match:["<iframe","<embed","<video","<audio","<object","youtube"],color:"#14B8A6"},
    {id:"link",label:"Links",icon:"\u{1F517}",match:["<a ","<a\t","href="],color:P.sMin},
    {id:"footer",label:"Footer",icon:"\u{1F3C1}",match:["<footer","copyright","site-footer"],color:P.slateL}
  ];
  /** Classify an HTML element string into a page zone (header, nav, hero, form, content, footer).
      Handles HTML snippets, CSS selectors, and falls back to desc/WCAG code matching. */
  function classifyZone(el,desc,wcag){
    var s=(el||"").toLowerCase();
    var d=(desc||"").toLowerCase();
    var w=wcag||"";
    /* Check element string against zone matchers */
    for(var i=0;i<ZONES.length;i++){
      for(var j=0;j<ZONES[i].match.length;j++){
        if(s.indexOf(ZONES[i].match[j])>-1) return ZONES[i].id;
      }
    }
    /* CSS selector fallback — strip punctuation and check tag names */
    var tagMatch=s.match(/^([a-z]+)/);
    if(tagMatch){
      var tag=tagMatch[1];
      if(tag==="img"||tag==="figure"||tag==="picture")return "img";
      if(tag==="a")return "link";
      if(tag==="input"||tag==="select"||tag==="textarea"||tag==="form"||tag==="fieldset"||tag==="label")return "form";
      if(tag==="button")return "interactive";
      if(tag==="nav")return "nav";
      if(tag==="header")return "head";
      if(tag==="footer")return "footer";
      if(tag==="h1"||tag==="h2"||tag==="h3"||tag==="h4"||tag==="h5"||tag==="h6")return "heading";
      if(tag==="iframe"||tag==="video"||tag==="audio")return "media";
    }
    /* Description/WCAG fallback */
    if(w==="1.1.1"||/image|alt text|img/i.test(d))return "img";
    if(w==="1.4.3"||w==="1.4.11"||/contrast/i.test(d))return "content";
    if(w==="2.4.4"||/link/i.test(d))return "link";
    if(w==="1.3.1"&&/heading/i.test(d))return "heading";
    if(w==="3.3.1"||w==="3.3.2"||/form|label|input/i.test(d))return "form";
    if(w==="4.1.2"||/aria|role|name/i.test(d))return "interactive";
    if(w==="3.1.1"||w==="3.1.2"||/lang/i.test(d))return "doc";
    if(w==="2.4.1"||/skip|bypass/i.test(d))return "nav";
    return "content";
  }

  /* ═══ WEB BROWSER PREVIEW — Page Anatomy Issue Map ═══ */
  /** Web Browser Preview — renders page anatomy map with issue markers, or live iframe proxy */
  function browserPreview(pg,mode){
    var dm=domain||"site";
    var GB="#E5E7EB";
    var pageUrl=pg.url||(dm+pg.p);
    var isRem=mode==="rem";
    var issues=pg.issues||[];
    var fixed=issues.filter(function(i){return i.remediated});
    var total=issues.reduce(function(x,i){return x+(i.remediated?0:i.count||1)},0);
    var score=issues.length>0?Math.round(fixed.length/issues.length*100):100;
    /* Group issues by zone */
    var zoneIssues={};
    issues.forEach(function(iss,idx){
      var zid=classifyZone(iss.el,iss.desc,iss.wcag);
      if(!zoneIssues[zid])zoneIssues[zid]=[];
      zoneIssues[zid].push({iss:iss,idx:idx});
    });
    /* Zone map view — always available as the detailed issue layout */
    var zoneMap=E("div",{style:{flex:1,overflow:"auto",padding:"6px 8px"}},
        ZONES.map(function(zone){
          var zi=zoneIssues[zone.id]||[];
          var activeIssues=isRem?zi.filter(function(x){return x.iss.remediated}):zi.filter(function(x){return!x.iss.remediated});
          var totalEls=activeIssues.reduce(function(x,item){return x+(item.iss.count||1)},0);
          var hasCrit=activeIssues.some(function(x){return x.iss.sev==="critical"});
          var hasMaj=activeIssues.some(function(x){return x.iss.sev==="major"});
          var isEmpty=activeIssues.length===0;
          var borderColor=isRem?(isEmpty?"#E5E7EB":"#059669"):hasCrit?"#DC2626":hasMaj?"#D97706":isEmpty?"#E5E7EB":"#3B82F6";
          return E("div",{key:zone.id,style:{marginBottom:2,borderRadius:5,border:"1.5px solid "+(isEmpty?borderColor+"50":borderColor),
            background:isRem?(isEmpty?"transparent":"#F0FDF408"):hasCrit?"#FEF2F210":hasMaj?"#FFFBEB10":isEmpty?"transparent":"#EFF6FF10",
            transition:"all .15s",opacity:isEmpty?.35:1}},
            E("div",{style:{display:"flex",alignItems:"center",padding:"3px 8px",gap:5}},
              E("div",{style:{width:20,height:20,borderRadius:4,background:zone.color+"18",color:zone.color,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,flexShrink:0}},zone.icon),
              E("span",{style:{fontSize:10,fontWeight:700,color:isEmpty?"#94A3B8":"#374151",minWidth:80}},zone.label),
              isEmpty?E("span",{style:{fontSize:10,color:"#CBD5E1",fontStyle:"italic"}},"\u2014"):
              E("div",{style:{display:"flex",gap:2,flexWrap:"wrap",flex:1}},
                activeIssues.map(function(item,mi){
                  var sc=item.iss.sev==="critical"?"#DC2626":item.iss.sev==="major"?"#D97706":"#3B82F6";
                  var isActive=hiIss===item.idx;
                  return E("div",{key:mi,onClick:function(ev){ev.stopPropagation();setHiIss(isActive?null:item.idx);if(!isActive&&!isRem){highlightInProxy(item.iss)}else if(isActive&&!isRem){clearProxyHighlights()}},
                    title:"WCAG "+item.iss.wcag+": "+(item.iss.desc||"").slice(0,80),
                    style:{padding:"1px 5px",borderRadius:3,fontSize:10.5,fontWeight:700,cursor:"pointer",transition:"all .1s",lineHeight:"14px",
                      background:isRem?"#05966920":isActive?sc:sc+"15",
                      color:isRem?"#059669":isActive?"#fff":sc,
                      border:isActive?"1.5px solid "+sc:"1.5px solid transparent",
                      boxShadow:isActive?"0 0 0 2px "+sc+"25":"none"}},
                    isRem?"\u2713":(item.iss.wcag||"?"),
                    (item.iss.count||1)>1?E("span",{style:{marginLeft:2,opacity:.65,fontSize:10}},"\u00D7"+(item.iss.count)):null);
                })),
              !isEmpty?E("div",{style:{marginLeft:"auto",padding:"1px 6px",borderRadius:3,flexShrink:0,
                background:isRem?"#ECFDF5":hasCrit?"#FEE2E2":hasMaj?"#FEF3C7":"#DBEAFE",
                color:isRem?"#059669":hasCrit?"#DC2626":hasMaj?"#92400E":"#1D4ED8",
                fontSize:10,fontWeight:700}},totalEls+" el"):null))
          ;
        }));
    /* Try live iframe preview + zone overlay */
    var hasUrl=pageUrl&&pageUrl.indexOf("http")===0;
    return E("div",{style:{height:"100%",display:"flex",flexDirection:"column",background:isRem?"#FAFEFA":"#FAFBFE",flex:1}},
      /* Browser chrome */
      E("div",{style:{background:"#F9FAFB",borderBottom:"1px solid "+GB,padding:"6px 10px",display:"flex",alignItems:"center",gap:6,flexShrink:0}},
        E("div",{style:{display:"flex",gap:4}},E("div",{style:{width:8,height:8,borderRadius:"50%",background:"#EF4444"}}),E("div",{style:{width:8,height:8,borderRadius:"50%",background:"#F59E0B"}}),E("div",{style:{width:8,height:8,borderRadius:"50%",background:"#22C55E"}})),
        E("div",{style:{flex:1,padding:"3px 10px",borderRadius:6,background:P.card,border:"1px solid "+GB,fontSize:10,fontFamily:FC,color:P.slateM,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},"\u{1F512} "+pageUrl),
        E("a",{href:pageUrl,target:"_blank",rel:"noopener noreferrer",style:{padding:"3px 8px",borderRadius:5,background:"#4F46E5",color:"#fff",fontSize:10,fontWeight:700,textDecoration:"none",flexShrink:0}},"Open \u2197"),!isRem&&hasUrl?E("button",{onClick:function(){highlightAllInProxy(issues)},style:{padding:"3px 8px",borderRadius:5,background:"#DC2626",color:"#fff",fontSize:10,fontWeight:700,border:"none",cursor:"pointer",flexShrink:0}},"\u{1F534} Highlight All"):null,!isRem&&hasUrl?E("button",{onClick:function(){clearProxyHighlights()},style:{padding:"3px 8px",borderRadius:5,background:"#6B7280",color:"#fff",fontSize:10,fontWeight:700,border:"none",cursor:"pointer",flexShrink:0}},"\u2715 Clear"):null),
      /* Stats bar */
      E("div",{style:{padding:"6px 12px",display:"flex",alignItems:"center",gap:8,borderBottom:"1px solid "+GB,flexShrink:0,background:isRem?"#F0FDF4":"#fff"}},
        E("div",{style:{fontSize:11,fontWeight:800,color:P.slate,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},pg.n||"Page"),
        isRem?E("div",{style:{padding:"2px 10px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},fixed.length+"/"+issues.length+" fixed ("+score+"%)")
          :E("div",{style:{display:"flex",gap:4}},
            issues.filter(function(i){return i.sev==="critical"&&!i.remediated}).length>0?E("div",{style:{padding:"2px 7px",borderRadius:5,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700}},issues.filter(function(i){return i.sev==="critical"&&!i.remediated}).reduce(function(x,i){return x+(i.count||1)},0)+" crit"):null,
            issues.filter(function(i){return i.sev==="major"&&!i.remediated}).length>0?E("div",{style:{padding:"2px 7px",borderRadius:5,background:P.sMajBg,color:P.amberDk,fontSize:10,fontWeight:700}},issues.filter(function(i){return i.sev==="major"&&!i.remediated}).reduce(function(x,i){return x+(i.count||1)},0)+" major"):null,
            issues.filter(function(i){return i.sev==="minor"&&!i.remediated}).length>0?E("div",{style:{padding:"2px 7px",borderRadius:5,background:P.sMinBg,color:P.priDk,fontSize:10,fontWeight:700}},issues.filter(function(i){return i.sev==="minor"&&!i.remediated}).reduce(function(x,i){return x+(i.count||1)},0)+" minor"):null,
            total===0?E("div",{style:{padding:"2px 7px",borderRadius:5,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u2713 Clean"):null)),
      /* Page content — live proxy iframe or screenshot fallback */
      hasUrl?E("div",{style:{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}},
        /* Live page rendered through PiARIS proxy */
        E("div",{style:{position:"relative",flex:"1 1 auto",overflow:"hidden",background:P.slateM,minHeight:200,display:"flex",flexDirection:"column"}},
          E("iframe",{ref:!isRem?proxyFrameRef:remFrameRef,
            onLoad:function(e){if(!isRem){setupScrollSync(e.target,remFrameRef,"left")}else{setupScrollSync(e.target,proxyFrameRef,"right")}},
            src:pg.dom?buildDomBlobUrl(pg.dom,pg.url):"/api/fetch-page?url="+encodeURIComponent(pg.url),
            title:(isRem?"Simulated deliverable":"Baseline")+" view of "+pg.n,
            sandbox:"allow-same-origin allow-scripts",
            referrerPolicy:"no-referrer",
            style:{width:"100%",height:"100%",minHeight:300,border:"none",background:P.card,display:"block",flex:1}
          }),
          /* Issue marker dots overlay — only shown when scan provides real element coordinates.
             Default loc:{x:50,y:50} means no real position data — skip the dots and rely on
             highlightInProxy() postMessage which finds the actual element via selector matching. */
          issues.filter(function(iss){return!isRem?!iss.remediated:iss.remediated}).filter(function(iss){
            /* Skip markers with default coordinates — they cluster at center and mislead */
            return iss.loc&&!(iss.loc.x===50&&iss.loc.y===50)&&!(iss.loc.x===0&&iss.loc.y===0);
          }).map(function(iss,idx){
            var i=issues.indexOf(iss);
            var col=isRem?"#059669":iss.sev==="critical"?"#DC2626":iss.sev==="major"?"#D97706":"#3B82F6";
            var isActive=hiIss===i;
            return E("div",{key:"sm"+i,onClick:function(ev){ev.stopPropagation();setHiIss(isActive?null:i);if(!isActive&&!isRem){highlightInProxy(iss)}else if(isActive){clearProxyHighlights()}},
              title:issueCategoryLabel(iss)+" — WCAG "+(iss.wcag||""),
              style:{position:"absolute",left:(iss.loc.x||50)+"%",top:(iss.loc.y||20)+"%",transform:"translate(-50%,-50%)",
                width:isActive?24:16,height:isActive?24:16,borderRadius:"50%",
                background:col,color:"#fff",fontSize:isActive?9:7,fontWeight:800,
                display:"flex",alignItems:"center",justifyContent:"center",
                cursor:"pointer",zIndex:isActive?20:10,border:"2px solid #fff",
                boxShadow:isActive?"0 0 0 3px "+col+"40, 0 0 10px "+col+"50":"0 1px 4px rgba(0,0,0,.3)",
                transition:"all .15s"}},isRem?"\u2713":i+1,
              isActive?E("div",{style:{position:"absolute",top:-32,left:"50%",transform:"translateX(-50%)",
                background:"#1E293B",color:"#fff",padding:"3px 8px",borderRadius:5,fontSize:10,fontWeight:600,
                whiteSpace:"nowrap",pointerEvents:"none",zIndex:30,boxShadow:"0 4px 12px rgba(0,0,0,.3)"}},
                issueCategoryLabel(iss)):null);
          })
        ),
        /* Zone map below */
        E("div",{style:{flexShrink:0,borderTop:"1.5px solid "+GB,maxHeight:160,overflow:"auto",background:P.bgS}},
          E("div",{style:{padding:"3px 8px",fontSize:10,fontWeight:700,color:P.slateL,background:P.bgS,borderBottom:"1px solid "+GB,display:"flex",justifyContent:"space-between",alignItems:"center"}},
            E("span",null,"\u{1F50D} Issue Zone Map"),
            E("span",{style:{padding:"1px 6px",borderRadius:4,background:P.sMinBg,color:P.priDk,fontSize:9,fontWeight:700}},"\u{1F310} Live Page")),
          zoneMap)
      ):E("div",{style:{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}},
        pg.screenshot?E("div",{style:{position:"relative",flex:"1 1 auto",overflow:"auto",background:P.slateM,minHeight:200}},
          E("img",{src:pg.screenshot,alt:"Screenshot of "+pg.n,style:{width:"100%",display:"block"}}))
        :E("div",{style:{padding:"8px 12px",background:P.sMajBg,borderBottom:"1px solid #FDE68A",fontSize:10,color:P.amberDk,flexShrink:0}},
          "\u26A0\uFE0F No URL available. Import scan data from the PiARIS browser extension."),
        E("div",{style:{flex:1,overflow:"auto"}},zoneMap))
    );
  }

  /* ═══ PLATFORM-LEVEL SCAN PICKER (Session 46) ═══ */
  /** Build grouped session list: all sessions (cloud + local) organized by domain.
      @returns {Array<{domain:string, sessions:Array, latestDate:string}>} */
  function buildGroupedSessions(){
    var groups={};
    (userSessions||[]).forEach(function(s){
      var d=s.domain||"unknown";
      if(!groups[d])groups[d]={domain:d,sessions:[],latestDate:""};
      groups[d].sessions.push({type:"cloud",id:s.id,domain:d,date:s.completedAt||s.createdAt||"",
        pages:s.pageCount||0,docs:s.docCount||0,issues:s.summary?(s.summary.totalRules||0):0,
        critical:s.summary?(s.summary.critical||0):0,score:s.overallScore||0,status:s.status||"saved",raw:s});
      var dt=s.completedAt||s.createdAt||"";
      if(dt>groups[d].latestDate)groups[d].latestDate=dt;
    });
    (savedScans||[]).forEach(function(s){
      var d=s.domain||"unknown";
      if(!groups[d])groups[d]={domain:d,sessions:[],latestDate:""};
      var exists=groups[d].sessions.some(function(x){return x.id===s.id});
      if(!exists){
        groups[d].sessions.push({type:"local",id:s.id,domain:d,date:s.date+" "+s.time,
          pages:s.webPages||0,docs:s.documents||0,issues:s.totalIssues||0,
          critical:s.criticalIssues||0,score:0,status:"local",raw:s});
        if(!groups[d].latestDate)groups[d].latestDate=s.date;
      }
    });
    Object.keys(groups).forEach(function(d){groups[d].sessions.sort(function(a,b){return a.date>b.date?-1:1})});
    return Object.values(groups).sort(function(a,b){return a.latestDate>b.latestDate?-1:1});
  }

  /** Render the platform-level scan picker overlay.
      @returns {ReactElement|null} */
  function renderScanPicker(){
    if(!scanPickerOpen)return null;
    var grouped=buildGroupedSessions();
    var targetLabel=scanPickerTarget==="compare"?"Delta Recon":"Watchtower";
    var totalSessions=grouped.reduce(function(x,g){return x+g.sessions.length},0);
    return E("div",{style:{position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"},onClick:closeScanPicker},
      E("div",{style:{position:"absolute",inset:0,background:"rgba(0,0,0,.4)",animation:"fadeIn .15s"}}),
      E("div",{onClick:function(ev){ev.stopPropagation()},style:{position:"relative",width:"90%",maxWidth:700,maxHeight:"80vh",background:P.card,borderRadius:16,border:"1.5px solid "+P.bdr,boxShadow:"0 20px 60px rgba(0,0,0,.2)",display:"flex",flexDirection:"column",animation:"fadeIn .15s",overflow:"hidden"}},
        E("div",{style:{padding:"16px 20px",borderBottom:"1.5px solid "+P.bdr,display:"flex",alignItems:"center",gap:10,flexShrink:0}},
          E("span",{style:{fontSize:18}},"\u{1F4C2}"),
          E("div",{style:{flex:1}},
            E("div",{style:{fontSize:15,fontWeight:800,color:P.slate}},"Load Scan"),
            E("div",{style:{fontSize:11,color:P.slateM}},totalSessions+" session"+(totalSessions!==1?"s":"")+" across "+grouped.length+" domain"+(grouped.length!==1?"s":"")+" \u2022 Loading into "+targetLabel)),
          E("button",{onClick:closeScanPicker,style:{padding:"4px 10px",borderRadius:6,border:"1px solid "+P.bdr,background:P.card,fontSize:14,cursor:"pointer",color:P.slateM}},"\u2715")),
        E("div",{style:{padding:"10px 20px",borderBottom:"1px solid "+P.bdr,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",flexShrink:0}},
          E("button",{onClick:function(){pickCsvImport("web")},title:"Audit a CSV list of page URLs \u2014 Watchtower fetches them server-side (no CORS)",
            style:{padding:"8px 14px",borderRadius:8,border:"1.5px solid "+P.pri+"40",background:P.priLt,color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}},"\u{1F310} Load URLs (CSV)"),
          E("button",{onClick:function(){pickCsvImport("pdf")},title:"Audit a CSV list of PDF URLs (can span multiple domains)",
            style:{padding:"8px 14px",borderRadius:8,border:"1.5px solid "+P.emerald+"40",background:P.emeraldLt,color:P.emerald,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}},"\u{1F4C4} Load PDFs (CSV)"),
          E("button",{onClick:function(){closeScanPicker();setMod("import")},
            style:{padding:"8px 14px",borderRadius:8,border:"1px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}},"\u{1F4C2} Import File"),
          authUser?E("button",{onClick:loadUserSessions,disabled:sessionsLoading,
            style:{padding:"8px 14px",borderRadius:8,border:"1px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}},
            sessionsLoading?"\u23F3 Refreshing...":"\u{1F504} Refresh"):null),
        E("div",{style:{flex:1,overflow:"auto",padding:"12px 20px"}},
          grouped.length===0?E("div",{style:{textAlign:"center",padding:"40px 20px",color:P.slateL}},
            E("div",{style:{fontSize:32,marginBottom:8}},"\u{1F4C2}"),
            E("div",{style:{fontSize:13,fontWeight:700,marginBottom:4}},"No saved sessions"),
            E("div",{style:{fontSize:11}},"Scan a site with the Chrome extension or import a JSON/CSV file."),
            !authUser?E("button",{onClick:function(){closeScanPicker();window.location.href="/login"},
              style:{marginTop:12,padding:"10px 24px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}},"Sign In to Access Cloud Sessions"):null
          ):grouped.map(function(group){
            return E("div",{key:group.domain,style:{marginBottom:16}},
              E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:8}},
                E("span",{style:{fontSize:14,fontWeight:800,color:P.slate}},group.domain),
                E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.priLt,color:P.pri,fontSize:10,fontWeight:700}},group.sessions.length+" scan"+(group.sessions.length!==1?"s":"")),
                scanPickerTarget==="compare"&&group.sessions.length<2?E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.amberLt,color:P.amber,fontSize:10,fontWeight:600}},"Needs 2+ for compare"):null),
              E("div",{style:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:8}},
                group.sessions.map(function(s){
                  var scoreColor=s.score>=75?P.emerald:s.score>=50?P.amber:s.score>0?P.coral:P.slateL;
                  var dateStr=s.date?new Date(s.date).toLocaleDateString():"";
                  var timeStr=s.date?new Date(s.date).toLocaleTimeString(undefined,{hour:"2-digit",minute:"2-digit"}):"";
                  return E("div",{key:s.id,style:{padding:"12px 14px",borderRadius:10,border:"1.5px solid "+P.bdr,background:P.card,
                    transition:"border-color .15s,box-shadow .15s",cursor:"pointer"},
                    onMouseEnter:function(e){e.currentTarget.style.borderColor=P.pri+"60";e.currentTarget.style.boxShadow="0 2px 12px "+P.pri+"15"},
                    onMouseLeave:function(e){e.currentTarget.style.borderColor=P.bdr;e.currentTarget.style.boxShadow="none"},
                    onClick:function(){if(s.type==="cloud"){pickerLoadSession(s.raw)}else{pickerLoadLocalScan(s.id)}}},
                    E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}},
                      E("div",null,
                        E("div",{style:{fontSize:11,fontWeight:600,color:P.slate}},dateStr+(timeStr?" \u2022 "+timeStr:"")),
                        s.type==="cloud"?E("span",{style:{fontSize:9,padding:"1px 5px",borderRadius:3,background:P.sMinBg,color:P.priDk}},"\u2601 Cloud"):
                          E("span",{style:{fontSize:9,padding:"1px 5px",borderRadius:3,background:P.bgS,color:P.slateM}},"\u{1F4BE} Local")),
                      s.score>0?E("div",{style:{fontSize:14,fontWeight:800,color:scoreColor}},s.score+"%"):null),
                    E("div",{style:{display:"flex",gap:4,flexWrap:"wrap"}},
                      s.pages>0?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.priLt,fontSize:9,fontWeight:600,color:P.pri}},s.pages+" pg"):null,
                      s.docs>0?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.sMinBg,fontSize:9,fontWeight:600,color:P.sMin}},s.docs+" doc"):null,
                      s.issues>0?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.coralLt,fontSize:9,fontWeight:600,color:P.coral}},s.issues+" iss"):null,
                      s.critical>0?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.sCritBg,fontSize:9,fontWeight:600,color:P.sCrit}},s.critical+" crit"):null)
                  );
                })
              )
            );
          })
        )
      )
    );
  }

  /* WEB AUDIT — SPLIT-PANE INSPECTOR */
  /* Lazy-load page detail when selecting a stub page */
  var _pageLoading=useState(false),pageLoading=_pageLoading[0],setPageLoading=_pageLoading[1];
  useEffect(function(){
    if(sel!==null&&wp[sel]&&wp[sel]._stub){
      setPageLoading(true);
      loadPageDetail(sel).then(function(){setPageLoading(false)}).catch(function(){setPageLoading(false)});
    }
  },[sel]);
  var selPage=sel!==null&&mod==="webAudit"?wp[sel]:null;

  /** Render a fix cell in the workbench table.
      @param {string} fixText @param {string} type @param {Object} iss @param {number} pageIdx @param {number} issIdx */
  function renderFixCell(fixText,type,iss,pageIdx,issIdx){
    if(type==="manual"){
      return E("div",{contentEditable:true,suppressContentEditableWarning:true,
        onBlur:function(ev){setManualFix(pageIdx,issIdx,ev.target.textContent)},
        style:{padding:"4px 8px",border:"1px dashed "+(iss.manualFix?P.amber+"80":P.bdr),borderRadius:4,
          fontFamily:FC,fontSize:10,color:iss.manualFix?P.slate:P.slateL,minHeight:28,lineHeight:"1.4",
          background:iss.manualFix?"#FFF7ED":"transparent",cursor:"text",outline:"none"}},
        iss.manualFix||"Type override\u2026");
    }
    if(!fixText||fixText===""){return E("div",{style:{padding:"4px 8px",border:"1px dashed "+P.bdr,borderRadius:4,color:P.slateL,fontSize:10,minHeight:28,display:"flex",alignItems:"center"}},"\u2014")}
    if(type==="ai"&&fixText==="same"){
      return E("div",{style:{padding:"4px 8px",border:"1px solid #DDD6FE",borderRadius:4,background:P.priLt,display:"flex",alignItems:"center",gap:4,minHeight:28}},
        E("span",{style:{color:P.emerald,fontSize:12}},"\u2713"),E("span",{style:{fontSize:10,color:P.pri}},"Endorses PiARIS fix"));
    }
    var bgCol=type==="piaris"?"#F0FDF4":type==="ai"?"#F5F3FF":type==="accepted"?"#DBEAFE":"#FFF7ED";
    var bdCol=type==="piaris"?"#BBF7D0":type==="ai"?"#DDD6FE":type==="accepted"?"#93C5FD":"#FED7AA";
    var srcBadge=null;
    if(type==="accepted"&&iss.acceptedSrc){
      var bC=iss.acceptedSrc==="piaris"?{bg:"#ECFDF5",c:P.emerald}:iss.acceptedSrc==="ai"?{bg:"#F5F3FF",c:"#7C3AED"}:{bg:"#FFF7ED",c:P.amber};
      srcBadge=E("span",{style:{display:"inline-flex",padding:"1px 5px",borderRadius:3,fontSize:8,fontWeight:700,background:bC.bg,color:bC.c,marginLeft:6}},"\u2713 "+(iss.acceptedSrc==="piaris"?"PiARIS":iss.acceptedSrc==="ai"?"AI":"Manual"));
    }
    return E("div",{style:{padding:"4px 8px",border:"1px solid "+bdCol,borderRadius:4,background:bgCol,fontFamily:FC,fontSize:10,color:P.slate,minHeight:28,lineHeight:"1.4",wordBreak:"break-word"}},
      E("code",{style:{fontSize:10}},fixText),srcBadge);
  }
  /** Render accept action buttons for an issue row */
  function renderAcceptActions(iss,pageIdx,issIdx){
    if(iss.acceptedFix){return E("button",{onClick:function(){clearIssueFix(pageIdx,issIdx)},style:{padding:"2px 6px",borderRadius:3,border:"1px solid "+P.bdr,background:P.card,fontSize:9,color:P.slateM,cursor:"pointer"}},"\u21BB")}
    if(!iss.pFix&&!iss.aiFix)return null;
    var abS=function(c){return{padding:"2px 6px",borderRadius:3,border:"1px solid "+c+"40",background:P.card,fontSize:9,fontWeight:600,color:c,cursor:"pointer",whiteSpace:"nowrap"}};
    var btns=[];
    if(iss.pFix&&iss.aiFix==="same"){btns.push(E("button",{key:"both",onClick:function(){acceptIssueFix(pageIdx,issIdx,"piaris")},style:abS(P.emerald)},"\u2713 Accept"))}
    else{
      if(iss.pFix){btns.push(E("button",{key:"p",onClick:function(){acceptIssueFix(pageIdx,issIdx,"piaris")},style:abS(P.emerald)},"\u2713 PiARIS"))}
      if(iss.aiFix&&iss.aiFix!=="same"){btns.push(E("button",{key:"ai",onClick:function(){acceptIssueFix(pageIdx,issIdx,"ai")},style:abS("#7C3AED")},"\u2713 AI"))}
    }
    return E("div",{style:{display:"flex",flexDirection:"column",gap:2}},btns);
  }

  var webAudit = E("div",null,
    E(SubViewHero,{
      product:"watchtower",
      title:"Web Page Audit",
      subtitle:wSt.n+" pages \u00B7 "+wSt.tI+" issues \u00B7 "+wSt.wc+" WCAG criteria",
      right:E("button",{onClick:buildRemediationPackage,style:{minHeight:38,padding:"8px 14px",borderRadius:8,border:"none",background:"#fff",color:P.pri,fontSize:11,fontWeight:800,cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}},"\u{1F9E0} Build Smart Remediation")
    }),
    selPage?E("div",null,
      E("button",{onClick:function(){setSel(null);setHiIss(null)},style:{padding:"5px 14px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600,marginBottom:10}},"\u2190 Back to all pages"),
      splitHeader(selPage,true),
      E("div",{style:{display:"flex",gap:6,margin:"-6px 0 6px",justifyContent:"flex-end",flexWrap:"wrap"}},
        E("button",{onClick:function(){buildRemediationFile(wp.indexOf(selPage))},style:{padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,"+P.emerald+","+P.accent+")",color:"#fff",fontSize:10,fontWeight:700}},
          selPage.fixesBuilt?"\u21BB Rebuild Remediation File":"\u{1F527} Build Remediation File"),
        selPage.fixesBuilt?E("button",{onClick:function(){acceptAllPageFixes(wp.indexOf(selPage))},style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.pri+"40",background:P.priLt,color:P.pri,fontSize:10,fontWeight:700}},"\u2713 Accept All (Smart Defaults)"):null,
        selPage.fixScript?E("button",{onClick:function(){downloadFixScript(selPage)},style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.emerald+"40",background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u{2B07} Download .js"):null,
        selPage.patches&&selPage.patches.length>0?E("button",{onClick:function(){downloadPatches(selPage)},style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid #8B5CF640",background:P.priLt,color:P.pri,fontSize:10,fontWeight:700}},"\u{1F4CB} Patches ("+selPage.patches.length+")"):null,
        selPage.fixBuilding?E("span",{style:{padding:"6px 14px",borderRadius:6,background:P.amberLt,color:P.amber,fontSize:10,fontWeight:700,animation:"markerPulse 1.5s infinite"}},"\u{1F527} Building\u2026"):null),
      /* ── Mobile pane toggle ── */
      isMobile?E("div",{role:"tablist","aria-label":"View toggle",style:{display:"flex",gap:0,margin:"0 0 6px",borderRadius:8,overflow:"hidden",border:"1.5px solid "+P.bdr}},
        E("button",{role:"tab","aria-selected":inspPane==="original"?"true":"false",onClick:function(){setInspPane("original")},style:{flex:1,padding:"10px",border:"none",fontSize:11,fontWeight:700,cursor:"pointer",background:inspPane==="original"?P.sCritBg:"#fff",color:inspPane==="original"?P.sCrit:P.slateL}},"\u{1F534} Baseline"),
        E("button",{role:"tab","aria-selected":inspPane==="remediated"?"true":"false",onClick:function(){setInspPane("remediated")},style:{flex:1,padding:"10px",border:"none",borderLeft:"1.5px solid "+P.bdr,fontSize:11,fontWeight:700,cursor:"pointer",background:inspPane==="remediated"?"#ECFDF5":"#fff",color:inspPane==="remediated"?P.emerald:P.slateL}},"\u{1F7E2} Deliverable")
      ):null,
      /* ═══ DEVICE PREVIEW TOOLBAR (Session 46) ═══ */
      !isMobile&&!isPopOutWorkbench?E("div",{style:{display:"flex",alignItems:"center",gap:4,padding:"4px 8px",background:P.bgS,border:"1.5px solid "+P.bdr,borderBottom:"none",borderRadius:"10px 10px 0 0",marginTop:4}},
        E("span",{style:{fontSize:10,fontWeight:600,color:P.slateM,marginRight:4}},"Preview:"),
        ["desktop","tablet","mobile"].map(function(dv){
          var labels={desktop:"\u{1F4BB} 1280px",tablet:"\u{1F4F1} 768px",mobile:"\u{1F4F1} 375px"};
          var isActive=previewDevice===dv;
          return E("button",{key:dv,onClick:function(){setPreviewDevice(dv)},
            style:{padding:"3px 10px",borderRadius:5,border:"1px solid "+(isActive?P.pri:P.bdr),
              background:isActive?P.priLt:P.card,color:isActive?P.pri:P.slateM,
              fontSize:10,fontWeight:isActive?700:500,cursor:"pointer"}},labels[dv]);
        }),
        E("div",{style:{marginLeft:"auto",display:"flex",gap:4}},
          !popOutActive?E("button",{onClick:function(){popOutWorkbench(wp.indexOf(selPage))},
            title:"Open workbench in separate window",
            style:{padding:"3px 10px",borderRadius:5,border:"1px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:10,fontWeight:500,cursor:"pointer"}},"\u{1F5D7} Pop Out Workbench"):
            E("span",{style:{padding:"3px 10px",borderRadius:5,background:P.sMinBg,color:P.priDk,fontSize:10,fontWeight:600}},"\u{1F517} Workbench in separate window"))
      ):null,
      /* ═══ SPLIT-PANE: Viewport-responsive iframe previews ═══ */
      /* Sync button — centered above both panes */
      !isPopOutWorkbench&&!isMobile?E("div",{style:{display:"flex",justifyContent:"center",padding:"6px 0"}},
        E("button",{onClick:function(){setPanesLocked(!panesLocked)},title:panesLocked?"Panes scroll together \u2014 click to scroll independently":"Panes scroll independently \u2014 click to sync","aria-label":panesLocked?"Unlock pane scrolling":"Lock pane scrolling","aria-pressed":panesLocked?"true":"false",
          style:{padding:"6px 18px",borderRadius:8,border:"2px solid "+(panesLocked?"#1D4ED8":"#DC2626"),background:panesLocked?"#1D4ED8":"#FFFFFF",color:panesLocked?"#FFFFFF":"#DC2626",fontSize:11,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:6,minHeight:32,transition:"all .15s",boxShadow:panesLocked?"0 2px 8px rgba(29,78,216,.2)":"0 2px 8px rgba(220,38,38,.1)"}},panesLocked?"\u{1F512} Synced":"\u{1F513} Independent")):null,
      !isPopOutWorkbench?E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 2px 1fr",border:"1.5px solid "+P.bdr,borderRadius:(!isMobile?"0 0":"10px 10px")+" 0 0",overflow:"hidden",
        minHeight:isMobile?400:popOutActive?"calc(100vh - 200px)":"calc(100vh - 380px)",
        maxHeight:popOutActive?"calc(100vh - 160px)":"calc(100vh - 300px)"}},
        (!isMobile||inspPane==="original")?E("div",{onMouseEnter:function(){scrollSourcePane.current="left"},style:{display:"flex",flexDirection:"column",overflow:"hidden"}},
          !isMobile?paneLabel("\u{1F4CB}","Baseline","Pre-remediation snapshot",P.sCrit):null,
          E("div",{ref:measuredRef(setWtPaneW),style:{position:"relative",overflow:"hidden",flex:1,display:"flex",flexDirection:"column",background:P.bgS}},
            scaledViewport(isMobile?"full":previewDevice,wtPaneW,Math.max(360,(typeof window!=="undefined"?window.innerHeight:900)-(popOutActive?200:380)),function(vw,vh){
              return E("div",{style:{width:"100%",height:"100%",display:"flex",flexDirection:"column"}},browserPreview(selPage))}))):null,
        !isMobile?E("div",{style:{background:P.bdr}}):null,
        (!isMobile||inspPane==="remediated")?E("div",{onMouseEnter:function(){scrollSourcePane.current="right"},style:{display:"flex",flexDirection:"column",overflow:"hidden"}},
          !isMobile?paneLabel("\u{1F6E0}","Simulated Deliverable","Accepted fixes applied",P.emerald):null,
          E("div",{style:{position:"relative",overflow:"hidden",flex:1,display:"flex",flexDirection:"column",background:P.bgS}},
            scaledViewport(isMobile?"full":previewDevice,wtPaneW,Math.max(360,(typeof window!=="undefined"?window.innerHeight:900)-(popOutActive?200:380)),function(vw,vh){
              return E("div",{style:{width:"100%",height:"100%",display:"flex",flexDirection:"column"}},browserPreview(selPage,"rem"))}))):null
      ):null,
      /* ═══ WORKBENCH TABLE: Issue/Fix columns ═══ */
      !popOutActive?(function(){
        var pageIdx=wp.indexOf(selPage);
        var issues=selPage.issues||[];
        var acceptedCount=issues.filter(function(i){return!!i.acceptedFix}).length;
        var totalCount=issues.length;
        return E("div",{style:{border:"1.5px solid "+P.bdr,borderTop:isPopOutWorkbench?"1.5px solid "+P.bdr:"none",borderRadius:isPopOutWorkbench?"10px":"0 0 10px 10px",overflow:"hidden"}},
          E("div",{style:{padding:"8px 12px",background:P.bgS,borderBottom:"1px solid "+P.bdr,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flexShrink:0}},
            E("span",{style:{fontSize:12,fontWeight:800,color:P.slate}},"\u{1F527} Issue / Fix Workbench"),
            E("span",{style:{fontSize:10,color:P.slateM}},acceptedCount+" of "+totalCount+" accepted"),
            /* Template vs page-specific count in workbench header */
            (function(){
              var tplC=0;
              issues.forEach(function(iss){
                var nSel=normalizeSelector(iss.selector||"");
                var descKey=(iss.desc||"").substring(0,60).toLowerCase().replace(/\s+/g," ");
                var tKey=(iss.wcag||"")+"||"+nSel+"||"+descKey;
                var tg=templateStats.groups[tKey];
                if(tg&&tg.pages.length>1&&tg.isSpecific)tplC++;
              });
              return tplC>0?E("span",{style:{fontSize:9,color:P.pri,fontWeight:600,padding:"2px 6px",borderRadius:4,background:P.priLt}},
                "\u{1F501} "+tplC+" template \u00B7 "+(totalCount-tplC)+" page-specific"):null;
            })(),
            acceptedCount>0?E("div",{style:{width:80,height:4,borderRadius:2,background:P.bdr,overflow:"hidden",marginLeft:4}},
              E("div",{style:{width:Math.round(acceptedCount/Math.max(totalCount,1)*100)+"%",height:4,borderRadius:2,background:P.emerald,transition:"width .3s"}})):null,
            isPopOutWorkbench?E("span",{style:{marginLeft:"auto",fontSize:10,color:P.slateL}},"\u{1F517} Synced with preview window"):null),
          /* Desktop: full table | Mobile: card layout */
          isMobile?E("div",{style:{maxHeight:"60vh",overflow:"auto"}},
            issues.map(function(iss,ii){
              var isActive=hiIss===ii;var isAccepted=!!iss.acceptedFix;
              var sevCol=iss.sev==="critical"?P.sCrit:iss.sev==="major"?P.amber:P.sMin;
              return E("div",{key:"wbm-"+ii,onClick:function(){setHiIss(isActive?null:ii);if(!isActive){highlightInProxy(iss);scrollToInProxy(iss)}else{clearProxyHighlights()}},
                style:{padding:"10px 12px",borderBottom:"1px solid "+P.bdr,cursor:"pointer",
                  background:isActive?P.priLt:isAccepted?"#F0FDF408":"transparent"}},
                E("div",{style:{display:"flex",alignItems:"center",gap:6,marginBottom:4}},
                  E("span",{style:{fontFamily:FC,fontSize:12,fontWeight:700}},iss.wcag),
                  E("span",{style:{display:"inline-flex",alignItems:"center",gap:2,padding:"1px 5px",borderRadius:4,fontSize:9,fontWeight:600,background:iss.sev==="critical"?P.sCritBg:iss.sev==="major"?P.sMajBg:P.sMinBg,color:sevCol}},iss.sev.charAt(0).toUpperCase()),
                  E("span",{style:{fontSize:11,color:P.slate}},iss.info?iss.info.n:"")),
                E("div",{style:{fontSize:10,color:P.slateM,marginBottom:6}},(iss.desc||"").substring(0,100)),
                /* WCAG KB on mobile — shown when active */
                isActive&&WCAG_KB[iss.wcag]?E("div",{style:{marginBottom:6,padding:"6px 10px",borderRadius:6,background:P.sMajBg,border:"1px solid #FDE68A"}},
                  E("div",{style:{fontSize:10,fontWeight:700,color:P.amberDk,marginBottom:2}},"\u{1F4A1} "+WCAG_KB[iss.wcag].fix),
                  E("a",{href:WCAG_KB[iss.wcag].url,target:"_blank",rel:"noopener noreferrer",
                    onClick:function(ev){ev.stopPropagation()},
                    style:{fontSize:9,color:P.sMin,fontWeight:600,textDecoration:"underline"}},
                    "\u{1F4D6} W3C Guide \u2197")):null,
                isAccepted?E("div",{style:{padding:"4px 8px",borderRadius:4,background:P.sMinBg,border:"1px solid #93C5FD",fontFamily:FC,fontSize:10,color:P.slate}},
                  E("code",null,iss.acceptedFix),E("span",{style:{marginLeft:6,fontSize:8,fontWeight:700,color:P.priDk}},"\u2713 "+(iss.acceptedSrc||""))):
                E("div",{style:{display:"flex",gap:4}},renderAcceptActions(iss,pageIdx,ii)),
                !isAccepted?E("div",{style:{marginTop:4}},renderFixCell("","manual",iss,pageIdx,ii)):null);
            })
          ):E("div",{style:{overflowX:"auto"}},
            E("table",{style:{width:"100%",borderCollapse:"collapse",minWidth:820}},
              E("thead",null,
                E("tr",null,
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.slateM,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr,width:28}},"#"),
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.slateM,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr}},"Issue"),
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.slateM,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr,width:40}},"Sev"),
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.emerald,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr,minWidth:120}},"PiARIS Fix"),
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.pri,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr,minWidth:120}},"AI Fix"),
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.amber,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr,minWidth:90}},"Manual Fix"),
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.priDk,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr,minWidth:120}},"Accepted Fix"),
                  E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.slateM,textAlign:"left",background:P.bgS,borderBottom:"1px solid "+P.bdr,width:60}},""))),
              E("tbody",null,
                issues.map(function(iss,ii){
                  var isActive=hiIss===ii;var isAccepted=!!iss.acceptedFix;
                  var sevCol=iss.sev==="critical"?P.sCrit:iss.sev==="major"?P.amber:P.sMin;
                  var sevBg=iss.sev==="critical"?P.sCritBg:iss.sev==="major"?P.sMajBg:P.sMinBg;
                  return E("tr",{key:"wb-"+ii,onClick:function(){
                    setHiIss(isActive?null:ii);
                    if(!isActive){
                      /* Try multiple strategies to find and highlight the element in the iframe:
                         1. CSS selector (best) — e.g. "input#search"
                         2. Element tag from el field — e.g. "<input type='text'>"
                         3. WCAG-based search — postMessage asks iframe to find by wcag criteria */
                      highlightInProxy(iss);
                      scrollToInProxy(iss);
                      /* Also try text-based search if selector fails — find element by its content */
                      if(!iss.selector&&iss.el){
                        var tagM=(iss.el||"").match(/<(\w+)/);
                        if(tagM&&proxyFrameRef.current){
                          try{proxyFrameRef.current.contentWindow.postMessage({
                            action:"findAndHighlight",tag:tagM[1],snippet:(iss.el||"").substring(0,50),wcag:iss.wcag
                          },"*")}catch(e){}
                        }
                      }
                    }else{clearProxyHighlights()}},
                    style:{borderBottom:"1px solid "+P.bdr,cursor:"pointer",transition:"background .1s",background:isActive?P.priLt:isAccepted?"#F0FDF408":"transparent"}},
                    E("td",{style:{padding:"6px 8px",fontSize:10,fontWeight:600,color:P.slateL,verticalAlign:"top"}},ii+1),
                    E("td",{style:{padding:"6px 8px",verticalAlign:"top"}},
                      E("div",{style:{display:"flex",alignItems:"center",gap:4}},E("span",{style:{fontFamily:FC,fontSize:11,fontWeight:600}},iss.wcag),E("span",{style:{fontSize:10.5,color:P.slate}},iss.info?iss.info.n:"")),
                      E("div",{style:{fontSize:10,color:P.slateM,marginTop:1}},(iss.desc||"").substring(0,100)),
                      /* Element location — extract element type from desc when el field is empty.
                         The desc field often contains "This textinput element...", "This button element...",
                         "Anchor element found with a valid href...", etc. We parse these patterns
                         to give the user a meaningful element identifier. */
                      (function(){
                        /* Try selector first, then el field, then extract from description */
                        var locText=iss.selector||"";
                        if(!locText&&iss.el){locText=(iss.el||"").replace(/<[^>]*>/g,"").substring(0,50)||iss.el.substring(0,50)}
                        if(!locText){
                          /* Extract element type from description text */
                          var descLower=(iss.desc||"").toLowerCase();
                          var elementMatch=descLower.match(/this\s+([\w-]+(?:\s+[\w-]+)?)\s+element/);
                          if(elementMatch){locText=elementMatch[1]+" element"}
                          else if(descLower.indexOf("anchor element")>-1||descLower.indexOf("link")>-1){locText="anchor / link"}
                          else if(descLower.indexOf("image")>-1||descLower.indexOf("img")>-1){locText="image element"}
                          else if(descLower.indexOf("heading")>-1){locText="heading element"}
                          else if(descLower.indexOf("contrast")>-1){locText="text with low contrast"}
                          else if(descLower.indexOf("form")>-1||descLower.indexOf("label")>-1||descLower.indexOf("input")>-1){locText="form field / input"}
                          else if(descLower.indexOf("table")>-1){locText="table element"}
                          else if(descLower.indexOf("lang")>-1){locText="document language"}
                        }
                        var zoneLabel=iss.fixCategory?fixCategoryLabel(iss.fixCategory):"";
                        return locText?E("div",{style:{display:"flex",gap:4,alignItems:"center",marginTop:3,flexWrap:"wrap"}},
                          E("span",{style:{fontFamily:FC,fontSize:9,color:P.accent,
                            padding:"2px 6px",borderRadius:3,background:P.accentLt,
                            maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},
                            "\u{1F4CD} "+locText),
                          zoneLabel?E("span",{style:{padding:"1px 5px",borderRadius:3,fontSize:8,fontWeight:600,
                            color:FIX_DEFAULTS[iss.fixCategory]==="ai"?"#7C3AED":P.emerald,
                            background:FIX_DEFAULTS[iss.fixCategory]==="ai"?"#F5F3FF":"#F0FDF4"}},zoneLabel):null):
                          E("div",{style:{display:"flex",gap:4,alignItems:"center",marginTop:3}},
                            E("span",{style:{fontSize:9,color:P.slateL,fontStyle:"italic"}},"\u{1F4CD} Click row to search in page"),
                            zoneLabel?E("span",{style:{padding:"1px 5px",borderRadius:3,fontSize:8,fontWeight:600,
                              color:FIX_DEFAULTS[iss.fixCategory]==="ai"?"#7C3AED":P.emerald,
                              background:FIX_DEFAULTS[iss.fixCategory]==="ai"?"#F5F3FF":"#F0FDF4"}},zoneLabel):null);
                      })(),
                      /* Template dedup badge — shows when this issue repeats across multiple pages */
                      (function(){
                        var nSel=normalizeSelector(iss.selector||"");
                        var descKey=(iss.desc||"").substring(0,60).toLowerCase().replace(/\s+/g," ");
                        var tKey=(iss.wcag||"")+"||"+nSel+"||"+descKey;
                        var tg=templateStats.groups[tKey];
                        if(!tg||tg.pages.length<2||!tg.isSpecific)return null;
                        return E("div",{style:{marginTop:3}},
                          E("span",{title:"This issue repeats across "+tg.pages.length+" pages due to a shared template (header, footer, or nav). Fixing it once in the template resolves all "+tg.count+" instances.",
                            style:{fontSize:9,fontWeight:700,color:P.pri,padding:"2px 6px",borderRadius:3,
                            background:P.priLt,border:"1px solid #C7D2FE"}},
                            "\u{1F501} Template \u2014 "+tg.pages.length+"/"+wp.length+" pages \u00B7 fix once \u2192 resolves "+tg.count));
                      })(),
                      /* WCAG Knowledge Base — shows when issue row is active (clicked) */
                      isActive&&WCAG_KB[iss.wcag]?E("div",{style:{marginTop:6,padding:"6px 10px",borderRadius:6,background:P.sMajBg,border:"1px solid #FDE68A"}},
                        E("div",{style:{display:"flex",alignItems:"flex-start",gap:6}},
                          E("span",{style:{fontSize:11,flexShrink:0}},"\u{1F4A1}"),
                          E("div",null,
                            E("div",{style:{fontSize:10,fontWeight:700,color:P.amberDk,marginBottom:2}},"How to fix \u2014 WCAG "+iss.wcag+" "+WCAG_KB[iss.wcag].n),
                            E("div",{style:{fontSize:10,color:P.amberDk,lineHeight:1.5}},WCAG_KB[iss.wcag].fix),
                            E("a",{href:WCAG_KB[iss.wcag].url,target:"_blank",rel:"noopener noreferrer",
                              onClick:function(ev){ev.stopPropagation()},
                              style:{fontSize:9,color:P.sMin,fontWeight:600,textDecoration:"underline",marginTop:4,display:"inline-block"}},
                              "\u{1F4D6} W3C Understanding Document \u2197")))):null),
                    E("td",{style:{padding:"6px 8px",verticalAlign:"top"}},E("span",{style:{display:"inline-flex",alignItems:"center",gap:2,padding:"1px 5px",borderRadius:4,fontSize:9,fontWeight:600,textTransform:"uppercase",background:sevBg,color:sevCol}},E("span",{style:{width:4,height:4,borderRadius:"50%",background:sevCol}}),iss.sev.charAt(0).toUpperCase())),
                    E("td",{style:{padding:"6px 8px",verticalAlign:"top"}},renderFixCell(iss.pFix||"","piaris",iss,pageIdx,ii)),
                    E("td",{style:{padding:"6px 8px",verticalAlign:"top"}},renderFixCell(iss.aiFix||"","ai",iss,pageIdx,ii)),
                    E("td",{style:{padding:"6px 8px",verticalAlign:"top"}},renderFixCell("","manual",iss,pageIdx,ii)),
                    E("td",{style:{padding:"6px 8px",verticalAlign:"top"}},renderFixCell(iss.acceptedFix||"","accepted",iss,pageIdx,ii)),
                    E("td",{style:{padding:"6px 8px",verticalAlign:"top"}},renderAcceptActions(iss,pageIdx,ii)));
                })))),
          E("div",{style:{padding:"6px 12px",background:P.bgS,borderTop:"1px solid "+P.bdr,display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}},
            E("span",{style:{fontSize:10,fontWeight:600,color:P.slateM}},"Fix sources:"),
            E("span",{style:{display:"flex",alignItems:"center",gap:3,fontSize:10,color:P.slateM}},E("span",{style:{width:8,height:8,borderRadius:2,background:P.emeraldLt,border:"1px solid #BBF7D0"}}),"PiARIS"),
            E("span",{style:{display:"flex",alignItems:"center",gap:3,fontSize:10,color:P.slateM}},E("span",{style:{width:8,height:8,borderRadius:2,background:P.priLt,border:"1px solid #DDD6FE"}}),"AI"),
            E("span",{style:{display:"flex",alignItems:"center",gap:3,fontSize:10,color:P.slateM}},E("span",{style:{width:8,height:8,borderRadius:2,background:P.amberLt,border:"1px solid #FED7AA"}}),"Manual"),
            E("span",{style:{display:"flex",alignItems:"center",gap:3,fontSize:10,color:P.slateM}},E("span",{style:{width:8,height:8,borderRadius:2,background:P.sMinBg,border:"1px solid #93C5FD"}}),"Accepted")))
      })():null
    ):E("div",{style:{display:"flex",flexDirection:"column",gap:5}},
      E("div",{style:{marginBottom:8}},
        E("input",{type:"search",placeholder:"\u{1F50D} Search pages by name or URL (use * as wildcard)…",value:pageSearch,onChange:function(ev){setPageSearch(ev.target.value);setPageListLimit(50)},"aria-label":"Search pages — supports * wildcard and multiple words",style:{width:"100%",padding:"8px 12px",borderRadius:8,border:"1.5px solid "+P.bdr,fontSize:11,fontFamily:F,outline:"none",background:P.bgS,color:P.slate}}),
        E("div",{style:{fontSize:9,color:P.slateL,marginTop:3,fontStyle:"italic"}},"Tip: use * as a wildcard — e.g. *budget* or report*2017; multiple words all must match")),
      (pageSearch?wp.filter(function(pg){return matchesSearch((pg.n||"")+" "+(pg.url||""),pageSearch)}):wp)
        .map(function(pg,i){return{pg:pg,idx:i}})
        .sort(function(a,b){
          var aH=a.pg.totalIssues>0?1:0,bH=b.pg.totalIssues>0?1:0;
          if(aH!==bH)return bH-aH;
          if(a.pg.critCount!==b.pg.critCount)return b.pg.critCount-a.pg.critCount;
          if(a.pg.totalIssues!==b.pg.totalIssues)return b.pg.totalIssues-a.pg.totalIssues;
          return(a.pg.n||"").localeCompare(b.pg.n||"");
        })
        .slice(0,pageSearch?9999:pageListLimit)
        .map(function(item){return listItem(item.pg,item.idx,true)}),
      !pageSearch&&wp.length>pageListLimit?E("div",{style:{textAlign:"center",padding:"8px 0"}},
        E("button",{onClick:function(){setPageListLimit(pageListLimit+50)},style:{padding:"6px 16px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:10,fontWeight:600,cursor:"pointer",marginRight:6}},
          "Show More"),
        E("button",{onClick:function(){setPageListLimit(wp.length)},style:{padding:"6px 12px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:10,fontWeight:600,cursor:"pointer"}},
          "All "+wp.length)):null
    )
  );

  /* ═══ PDF DOCUMENT PREVIEW ═══ */
  /** PDF Document Preview — renders PDF via pdf.js canvas, Google Viewer embed, or fallback */
  function pdfPreview(doc,mode){
    var pg=pdfPage,maxPg=doc.pages;
    var navBtnStyle=function(disabled){return{width:32,height:32,borderRadius:"50%",border:"none",background:disabled?"#4B5563":"#6366F1",color:"#fff",fontSize:14,fontWeight:800,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.4:1,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:disabled?"none":"0 2px 8px rgba(99,102,241,.4)"}};
    /* Real PDF content area */
    var pdfContent;
    if(pdfLoading){
      pdfContent=E("div",{style:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flex:1,minHeight:300,background:P.slateM}},
        E("div",{style:{width:40,height:40,border:"4px solid #6366F1",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 1s linear infinite"}}),
        E("div",{style:{color:P.slateL,fontSize:12,marginTop:12}},"Loading PDF…"));
    } else if(pdfMode==="canvas"&&pdfPages[pg]){
      /* Rendered via pdf.js — show actual page image */
      pdfContent=E("div",{style:{background:P.slateM,padding:"4px",overflow:"auto",flex:1,display:"flex",justifyContent:"center",alignItems:"flex-start"}},
        E("div",{style:{position:"relative",width:"100%"}},
          E("img",{src:pdfPages[pg],alt:"Page "+pg+" of "+doc.name,style:{width:"100%",display:"block",borderRadius:2,boxShadow:"0 2px 8px rgba(0,0,0,.3)"}}),
          /* Issue markers overlaid on the real PDF — only when real coordinates exist.
             Default loc:{x:50,y:50} means no real position — skip to avoid misleading markers. */
          doc.issues.filter(function(iss){return(iss.loc.pg||1)===pg}).filter(function(iss){
            return iss.loc&&!(iss.loc.x===50&&iss.loc.y===50)&&!(iss.loc.x===0&&iss.loc.y===0);
          }).map(function(iss,idx){
            var i=doc.issues.indexOf(iss);
            var isRem=iss.remediated;
            var col=mode==="rem"?(isRem?P.emerald:iss.sev==="critical"?P.sCrit:iss.sev==="major"?P.amber:"#2563EB"):(isRem?null:iss.sev==="critical"?P.sCrit:iss.sev==="major"?P.amber:"#2563EB");
            if(!col)return null;
            var isActive=hiIss===i;
            return E("div",{key:"pm"+i,onClick:function(ev){ev.stopPropagation();setHiIss(isActive?null:i)},
              title:issueCategoryLabel(iss)+" — WCAG "+(iss.wcag||""),
              style:{
              position:"absolute",left:iss.loc.x+"%",top:iss.loc.y+"%",transform:"translate(-50%,-50%)",
              width:mode==="rem"&&isRem?14:isActive?26:18,height:mode==="rem"&&isRem?14:isActive?26:18,borderRadius:"50%",
              background:col,color:"#fff",fontSize:mode==="rem"&&isRem?7:isActive?10:8,fontWeight:800,
              display:"flex",alignItems:"center",justifyContent:"center",
              cursor:"pointer",zIndex:isActive?20:mode==="rem"&&isRem?5:10,opacity:mode==="rem"&&isRem?.7:1,
              boxShadow:isActive?"0 0 0 4px "+col+"40, 0 0 12px "+col+"60":"0 2px 6px rgba(0,0,0,.25)",
              transition:"all .2s",border:"2px solid #fff",
              animation:isActive&&!(mode==="rem"&&isRem)?"markerPulse 1.5s infinite":"none"}},
              mode==="rem"&&isRem?"\u2713":i+1,
              /* Tooltip on active marker */
              isActive?E("div",{style:{position:"absolute",top:-36,left:"50%",transform:"translateX(-50%)",
                background:"#1E293B",color:"#fff",padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:600,
                whiteSpace:"nowrap",pointerEvents:"none",zIndex:30,boxShadow:"0 4px 12px rgba(0,0,0,.3)"}},
                issueCategoryLabel(iss)):null);
          })
        )
      );
    } else if(pdfMode==="canvas"&&!pdfPages[pg]){
      /* pdf.js loaded the doc but page not rendered yet */
      pdfContent=E("div",{style:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flex:1,minHeight:300,background:P.slateM}},
        E("div",{style:{width:40,height:40,border:"4px solid #6366F1",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 1s linear infinite"}}),
        E("div",{style:{color:P.slateL,fontSize:12,marginTop:12}},"Rendering page "+pg+"…"));
    } else if(pdfMode==="embed"&&currentPdfUrl){
      /* CORS blocked pdf.js — try proxy through our own API first, then Google Docs Viewer.
         The /api/fetch-page endpoint can proxy the PDF to avoid CORS. If the proxy fails
         (e.g., the PDF server blocks our API too), fall back to Google Docs Viewer. */
      var proxyUrl="/api/fetch-page?url="+encodeURIComponent(currentPdfUrl)+"&raw=true";
      var gviewUrl="https://docs.google.com/gview?url="+encodeURIComponent(currentPdfUrl)+"&embedded=true";
      pdfContent=E("div",{style:{background:P.slateM,height:"100%",minHeight:400,overflow:"hidden",position:"relative",display:"flex",flexDirection:"column"}},
        E("div",{style:{padding:"6px 10px",background:"#292524",borderBottom:"1px solid #44403C",fontSize:10,color:"#FDE68A",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}},
          E("span",null,"\u26A0\uFE0F"),
          E("span",null,"CORS prevented direct rendering."),
          E("a",{href:currentPdfUrl,target:"_blank",rel:"noopener noreferrer",style:{color:"#93C5FD",fontWeight:700,marginLeft:"auto",textDecoration:"underline",flexShrink:0,padding:"2px 8px",background:"#1E293B",borderRadius:4}},"Open PDF \u2197")),
        /* Show both proxy attempt and Google Viewer — first visible one wins */
        E("iframe",{src:gviewUrl,title:doc.name,
          onError:function(e){e.target.style.display="none"},
          style:{flex:1,width:"100%",border:"none"}}),
        /* Fallback content if iframe doesn't render */
        E("div",{style:{position:"absolute",bottom:0,left:0,right:0,padding:"16px 20px",background:"linear-gradient(transparent,#292524 40%)",
          display:"flex",flexDirection:"column",alignItems:"center",gap:8,paddingTop:60}},
          E("div",{style:{fontSize:10,color:"#A8A29E",textAlign:"center",lineHeight:1.5}},
            "If the PDF doesn't load above, the host server may block external viewers."),
          E("a",{href:currentPdfUrl,target:"_blank",rel:"noopener noreferrer",
            style:{padding:"8px 20px",borderRadius:8,background:"#4F46E5",color:"#fff",fontSize:11,fontWeight:700,textDecoration:"none",display:"inline-block"}},
            "\u{1F4C4} Open Original PDF in New Tab")));
    } else if(currentPdfUrl){
      /* No pdf.js available — use Google Docs Viewer directly */
      var gviewUrl2="https://docs.google.com/gview?url="+encodeURIComponent(currentPdfUrl)+"&embedded=true";
      pdfContent=E("div",{style:{background:P.slateM,height:"100%",minHeight:400,overflow:"hidden",position:"relative",display:"flex",flexDirection:"column"}},
        E("div",{style:{padding:"6px 10px",background:"#292524",borderBottom:"1px solid #44403C",fontSize:10,color:"#FDE68A",display:"flex",alignItems:"center",gap:6}},
          E("span",null,"\u26A0\uFE0F"),
          E("span",null,"Rendering via Google Docs Viewer."),
          E("a",{href:currentPdfUrl,target:"_blank",rel:"noopener noreferrer",style:{color:"#93C5FD",fontWeight:700,marginLeft:"auto",textDecoration:"underline",flexShrink:0}},"Open PDF \u2197")),
        E("iframe",{src:gviewUrl2,title:doc.name,style:{flex:1,width:"100%",border:"none",borderRadius:2}}));
    } else {
      /* No URL available — show placeholder message */
      pdfContent=E("div",{style:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flex:1,minHeight:300,background:P.slateM,color:P.slateL,fontSize:12,padding:20,textAlign:"center"}},
        E("div",{style:{fontSize:36,marginBottom:12}},"\uD83D\uDCC4"),
        E("div",{style:{fontWeight:600,marginBottom:8}},"No PDF URL available for preview"),
        E("div",{style:{fontSize:11,color:P.slateM,lineHeight:1.6,maxWidth:340}},
          "This document was imported without a URL. Re-scan the site using the PiARIS browser extension (v0.5.1+) to capture PDF URLs, or open the document directly from the source website."));
    }
    return E("div",{style:{position:"relative",width:"100%",minHeight:400,display:"flex",flexDirection:"column"}},
      /* PDF viewer toolbar */
      E("div",{style:{background:"#3B3B3B",padding:"5px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}},
        E("div",{style:{display:"flex",gap:8,alignItems:"center"}},
          E("span",{style:{fontSize:10,color:"#E5E7EB",fontWeight:600}},"\u{1F4C4}"),
          E("span",{style:{fontSize:10.5,color:P.slateL,fontFamily:FC}},doc.name)),
        E("div",{style:{display:"flex",gap:6,alignItems:"center"}},
          E("button",{"aria-label":"Previous page",onClick:function(){if(pg>1)setPdfPage(pg-1)},disabled:pg<=1,style:navBtnStyle(pg<=1)},"\u25C0"),
          E("span",{style:{fontSize:10,color:"#F3F4F6",fontWeight:700,minWidth:80,textAlign:"center"}},"Page "+pg+" of "+maxPg),
          E("button",{"aria-label":"Next page",onClick:function(){if(pg<maxPg)setPdfPage(pg+1)},disabled:pg>=maxPg,style:navBtnStyle(pg>=maxPg)},"\u25B6"),
          E("span",{style:{fontSize:10,color:P.slateM}},"|"),
          E("span",{style:{fontSize:10,color:P.slateL}},"100%"),
          E("span",{style:{fontSize:10,color:P.slateM}},"|"),
          E("span",{style:{fontSize:10,color:doc.tagged?"#4ADE80":"#F87171",fontWeight:600}},doc.tagged?"Tagged":"UNTAGGED"))),
      pdfContent
    );
  }

  /* DOC AUDIT — SPLIT-PANE PDF INSPECTOR */
  var sortedDc=useMemo(function(){return dc.slice().sort(function(a,b){return b.views-a.views})},[dc]);
  var selDoc=sel!==null&&mod==="docAudit"?sortedDc[sel]:null;
  var docAudit = E("div",null,
    E(SubViewHero,{
      product:"watchtower",
      title:"Document Audit",
      subtitle:dSt.n+" documents \u00B7 "+dSt.tI+" issues \u00B7 Sorted by access frequency",
      right:null
    }),
    /* Secondary action row — kept separate to retain existing inline functions */
    E("div",{style:{display:"flex",justifyContent:"flex-end",marginBottom:14,flexWrap:"wrap",gap:8}},
      E("button",{onClick:function(){
        /* Run PDF accessibility audit on all documents */
        var pdfDocs=sortedDc.filter(function(d){return d.ext==="pdf"&&d.url});
        if(pdfDocs.length===0){showToast("No PDFs to audit","error");return;}
        showToast("\u267F Auditing "+pdfDocs.length+" PDFs for accessibility...");
        /* Batch audit in groups of 10 */
        var allUrls=pdfDocs.map(function(d){return d.url});
        var batchSize=10;
        var auditIdx=0;
        function auditBatch(){
          var batch=allUrls.slice(auditIdx,auditIdx+batchSize);
          if(batch.length===0){showToast("\u2705 PDF audit complete");return;}
          showToast("\u267F Auditing PDFs "+(auditIdx+1)+"-"+(auditIdx+batch.length)+" of "+allUrls.length+"...");
          fetch("/api/pdf-audit",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
            body:JSON.stringify({pdfUrls:batch})
          }).then(function(r){return r.ok?r.json():null}).then(function(data){
            if(data&&data.results){
              /* Merge audit results into doc objects */
              setDc(function(prev){return prev.map(function(d){
                var match=data.results.find(function(r){return r.url===d.url});
                if(!match)return d;
                /* Map audit issues to the same format as web issues */
                var pdfIssues=(match.issues||[]).map(function(iss,ii){
                  var wcag=iss.wcag||"";var info=W[wcag]||{n:iss.desc||"Unknown",l:"A",p:"Unknown",r:iss.fix||""};
                  return{id:"pa-"+ii,wcag:wcag,sev:iss.sev||"minor",desc:iss.desc+(iss.detail?" — "+iss.detail:""),
                    count:1,el:"",fix:iss.fix||"",info:info,remediated:false,engine:"pdf-audit",
                    cat:iss.cat||"",checkpoint:iss.id||"",loc:{x:50,y:50,zone:"body"}};
                });
                var tc=pdfIssues.length;
                return Object.assign({},d,{
                  issues:pdfIssues,totalIssues:tc,wcagsFailed:pdfIssues.length,
                  critCount:pdfIssues.filter(function(i){return i.sev==="critical"}).length,
                  majCount:pdfIssues.filter(function(i){return i.sev==="major"}).length,
                  minCount:pdfIssues.filter(function(i){return i.sev==="minor"}).length,
                  pdfAudit:match.summary||null,pdfStructure:match.structure||null,pdfMetadata:match.metadata||null,
                  audited:true
                });
              })});
            }
            auditIdx+=batchSize;
            if(auditIdx<allUrls.length) setTimeout(auditBatch,500);
            else showToast("\u2705 PDF audit complete — "+allUrls.length+" PDFs checked");
          }).catch(function(e){showToast("PDF audit failed: "+e.message,"error")});
        }
        auditBatch();
      },style:{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#D97706,#B45309)",color:"#fff",fontSize:11,fontWeight:700,marginLeft:6}},
        "\u267F 1 \u00B7 Audit All ("+sortedDc.filter(function(d){return d.ext==="pdf"}).length+")"),
      sortedDc.filter(function(d){return d.ext==="pdf"}).length>0?E("button",{onClick:function(){
        var sid=(scanMeta&&scanMeta.sessionId)||(_currentScanId&&_currentScanId.current)||new URLSearchParams(window.location.search).get("scan");
        openBulkRem(sid);
      },style:{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#0D9488,#0F766E)",color:"#fff",fontSize:11,fontWeight:700,marginLeft:6}},
        "\u{1F4DA} 2 \u00b7 Remediate All ("+sortedDc.filter(function(d){return d.ext==="pdf"}).length+")"):null),
    selDoc?E("div",null,
      E("button",{onClick:function(){setSel(null);setHiIss(null);setPdfPage(1)},style:{padding:"5px 14px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600,marginBottom:10}},"\u2190 Back to all documents"),
      splitHeader(selDoc,false),
      E("div",{style:{display:"flex",gap:6,margin:"-6px 0 6px",justifyContent:"flex-end",flexWrap:"wrap"}},
        E("button",{onClick:function(){sourceRemediateDoc(selDoc, dc.indexOf(selDoc))},style:{padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,"+P.coral+","+P.amber+")",color:"#fff",fontSize:10,fontWeight:700}},
          "\u{1F527} Build Doc Remediation File"),
        /* AI Enrich button for docs */
        (function(){
          var dIdx=dc.indexOf(selDoc);
          var enrichData=aiEnrich["d"+dIdx];
          var loading=aiEnrichLoading["d"+dIdx];
          if(loading) return E("span",{style:{padding:"6px 14px",borderRadius:6,background:P.priLt,color:P.pri,fontSize:10,fontWeight:700,animation:"markerPulse 1.5s infinite"}},"\u{1F9E0} AI Analyzing\u2026");
          if(enrichData&&enrichData.aiSuggestions&&enrichData.aiSuggestions.length>0) return E("span",{style:{padding:"6px 14px",borderRadius:6,background:P.priLt,border:"1px solid #DDD6FE",color:P.pri,fontSize:10,fontWeight:700}},"\u{1F9E0} "+enrichData.aiSuggestions.length+" AI Fixes Ready");
          return E("button",{onClick:function(){aiEnrichDoc(dIdx)},style:{padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#8B5CF6,#7C3AED)",color:"#fff",fontSize:10,fontWeight:700}},"\u{1F9E0} AI Smart Fix");
        })(),
        staging.some(function(s){return s.url===selDoc.name&&s.status==="staged"})?E("span",{style:{padding:"6px 12px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u2713 In Staging"):null),
      /* \u2500\u2500 Remediate \u2192 Certify (per-doc state machine) \u2500\u2500 */
      selDoc.remediated?(function(){
        var dIdx=dc.indexOf(selDoc);
        var hasBytes=!!remediatedBytes.current[docKey(selDoc)]||(selDoc.jobId!=null);
        var c=selDoc.conformance;
        var showCertify=hasBytes&&!(c&&(c.pending||c.validated));
        return E("div",{style:{marginBottom:10,padding:14,borderRadius:12,border:"2px solid "+P.bdr,background:P.card}},
          E("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}},
            E("div",{style:{display:"flex",alignItems:"center",gap:8}},
              E("span",{style:{fontSize:13,fontWeight:800}},"\u{1F527} Remediated"),
              docStatePill(selDoc)),
            E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
              hasBytes?E("button",{onClick:function(){downloadRemediated(selDoc,dIdx)},style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:700,cursor:"pointer"}},"\u2193 Download PDF"):null,
              (c&&c.pending)?E("span",{style:{padding:"6px 14px",borderRadius:6,background:P.priLt,color:P.pri,fontSize:10,fontWeight:700}},"\u23f3 Certifying\u2026"):
                showCertify?E("button",{onClick:function(){certifyDoc(selDoc,dIdx)},style:{padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,"+P.emerald+","+P.accent+")",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u2696\ufe0f Certify (veraPDF)"):null)),
          (c&&c.validated)?E("div",{style:{marginTop:10,padding:10,borderRadius:8,background:c.conformant?P.emeraldLt:P.sMajBg,border:"1px solid "+(c.conformant?P.emerald:P.sMaj)+"30"}},
            c.conformant?
              E("div",null,
                E("div",{style:{fontSize:12,fontWeight:800,color:P.emerald}},"\u2705 PDF/UA-1 conformant \u00b7 verified by veraPDF "+(c.validatorVersion||"")),
                (function(){
                  var sl=selDoc.seal;
                  if(sl&&sl.pending) return E("div",{style:{marginTop:8,fontSize:10,color:P.pri,fontWeight:700}},"\u23f3 Sealing\u2026");
                  if(sl&&sl.done) return E("div",{style:{marginTop:8}},
                    E("div",{style:{fontSize:10.5,fontWeight:700,color:P.emerald}}, sl.signed?("\ud83d\udd0f Sealed & signed"+(sl.signer&&sl.signer.subject?" \u00b7 "+sl.signer.subject:"")+(sl.signer?(sl.signer.selfSigned?" (self-signed)":" (trusted)"):"")):"\ud83d\udd0f Provenance stamped \u2014 signing not yet configured"),
                    E("button",{onClick:function(){downloadSealed(selDoc)},style:{marginTop:6,padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:700,cursor:"pointer"}},"\u2193 Download Sealed PDF"));
                  return E("button",{onClick:function(){sealDoc(selDoc,dIdx)},style:{marginTop:8,padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#0EA5E9,#6366F1)",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\ud83d\udd0f Seal & Sign");
                })()
              ):
              E("div",null,
                E("div",{style:{fontSize:12,fontWeight:800,color:P.amberDk}},"\u26a0\ufe0f Verified \u2014 not yet conformant \u00b7 veraPDF "+(c.validatorVersion||"")),
                E("div",{style:{fontSize:10,color:P.slateM,marginTop:3}},"Residual: "+((c.residual&&c.residual.structural)||0)+" structural \u00b7 "+((c.residual&&c.residual.human)||0)+" human"+(c.autoFixable?" \u00b7 remediation may help":" \u00b7 needs manual / DocMaya")),
                domainLicensed?E("button",{onClick:function(){convertToHtml([dc.indexOf(selDoc)])},style:{marginTop:8,padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#8B5CF6,#6366F1)",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u2728 Convert with DocMaya"):null)
          ):(c&&c.validated===false&&!c.pending)?E("div",{style:{marginTop:8,fontSize:10,color:P.slateL}},"Certification unavailable: "+(c.reason||"error")):null
        );
      })():null,
      /* ── DocMaya conversion result panel ── */
      (function(){
        var dIdx=dc.indexOf(selDoc);
        var cs=convStatus[dIdx];
        if(!cs||cs.status!=="complete") return null;
        return E("div",{style:{marginBottom:10,padding:16,borderRadius:12,border:"2px solid "+P.emerald+"30",background:P.card}},
          E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}},
            E("div",{style:{display:"flex",alignItems:"center",gap:8}},
              E("span",{style:{fontSize:16}},"\u2705"),
              E("span",{style:{fontSize:13,fontWeight:800,color:P.emerald}},"\u2728 DocMaya \u2014 Accessible HTML Generated"),
              E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:11,fontWeight:700}},"+"+cs.complianceGain+"% compliance")),
            E("div",{style:{display:"flex",gap:6}},
              E("button",{onClick:function(){
                var sid=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:new URLSearchParams(window.location.search).get("scan");
                window.open("/api/pdf-convert/"+sid+"/convert-result/"+dIdx+"?format=inline","_blank");
              },style:{padding:"6px 14px",borderRadius:6,border:"none",background:P.pri,color:"#fff",fontSize:10,fontWeight:700}},"\uD83C\uDF10 View HTML"),
              E("button",{onClick:function(){
                var sid=scanMeta&&scanMeta.sessionId?scanMeta.sessionId:new URLSearchParams(window.location.search).get("scan");
                var a=document.createElement("a");
                a.href="/api/pdf-convert/"+sid+"/convert-result/"+dIdx+"?format=inline";
                a.download=(selDoc.name||"document").replace(/\.pdf$/i,"")+"-accessible.html";
                a.click();
              },style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:700}},"\u2193 Download HTML"))),
          cs.beforeIssues&&cs.afterIssues?E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}},
            E("div",{style:{padding:10,borderRadius:8,background:P.sCritBg,border:"1px solid "+P.sCrit+"20"}},
              E("div",{style:{fontSize:10,fontWeight:700,color:P.sCrit,marginBottom:4}},"BEFORE (Original PDF)"),
              E("div",{style:{fontSize:20,fontWeight:800,color:P.sCrit}},cs.beforeIssues.total+" issues"),
              E("div",{style:{fontSize:10,color:P.slateM}},"Critical: "+cs.beforeIssues.critical+" \u00B7 Major: "+(cs.beforeIssues.major||0))),
            E("div",{style:{padding:10,borderRadius:8,background:P.emeraldLt,border:"1px solid "+P.emerald+"20"}},
              E("div",{style:{fontSize:10,fontWeight:700,color:P.emerald,marginBottom:4}},"AFTER (Generated HTML)"),
              E("div",{style:{fontSize:20,fontWeight:800,color:P.emerald}},cs.afterIssues.total+" issues"),
              E("div",{style:{fontSize:10,color:P.slateM}},"Critical: "+cs.afterIssues.critical+" \u00B7 Major: "+(cs.afterIssues.major||0)))):null
        );
      })(),
      /* Mobile pane toggle for docs */
      isMobile?E("div",{role:"tablist","aria-label":"Document view toggle",style:{display:"flex",gap:0,margin:"0 0 6px",borderRadius:8,overflow:"hidden",border:"1.5px solid "+P.bdr}},
        E("button",{role:"tab","aria-selected":inspPane==="original"?"true":"false",onClick:function(){setInspPane("original")},
          style:{flex:1,padding:"10px",border:"none",fontSize:11,fontWeight:700,cursor:"pointer",
            background:inspPane==="original"?P.sCritBg:"#fff",color:inspPane==="original"?P.sCrit:P.slateL}},
          "\u{1F4CB} Baseline \u2014 Issues"),
        E("button",{role:"tab","aria-selected":inspPane==="remediated"?"true":"false",onClick:function(){setInspPane("remediated")},
          style:{flex:1,padding:"10px",border:"none",borderLeft:"1.5px solid "+P.bdr,fontSize:11,fontWeight:700,cursor:"pointer",
            background:inspPane==="remediated"?"#ECFDF5":"#fff",color:inspPane==="remediated"?P.emerald:P.slateL}},
          "\u2705 Simulated Deliverable \u2014 Fixed")
      ):null,
      /* ── Doc Audit split-pane: each column scrolls independently.
           No rigid viewport height — page scrolls naturally. (Session 47) ── */
      /* Sync button — centered above both panes */
      !isMobile?E("div",{style:{display:"flex",justifyContent:"center",padding:"6px 0"}},
        E("button",{onClick:function(){setPanesLocked(!panesLocked)},title:panesLocked?"Panes scroll together":"Panes scroll independently","aria-label":panesLocked?"Unlock pane scrolling":"Lock pane scrolling",
          style:{padding:"6px 18px",borderRadius:8,border:"2px solid "+(panesLocked?"#1D4ED8":"#DC2626"),background:panesLocked?"#1D4ED8":"#FFFFFF",color:panesLocked?"#FFFFFF":"#DC2626",fontSize:11,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:6,minHeight:32,transition:"all .15s",boxShadow:panesLocked?"0 2px 8px rgba(29,78,216,.2)":"0 2px 8px rgba(220,38,38,.1)"}},panesLocked?"\u{1F512} Synced":"\u{1F513} Independent")):null,
      E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 2px 1fr",border:"1.5px solid "+P.bdr,borderTop:"none",borderRadius:"0 0 10px 10px"}},
        (!isMobile||inspPane==="original")?E("div",{ref:docLeftPaneRef,onScroll:function(){docPaneScroll(docLeftPaneRef,docRightPaneRef)},role:"tabpanel","aria-label":"Baseline document with issues",style:{display:"flex",flexDirection:"column",maxHeight:"calc(100vh - 240px)",overflowY:"auto",overflowX:"hidden"}},
          !isMobile?E("div",{style:{padding:"6px 12px",background:P.sCrit+"0A",borderBottom:"2px solid "+P.sCrit,display:"flex",justifyContent:"space-between",alignItems:"center"}},
            E("div",{style:{display:"flex",alignItems:"center",gap:6}},E("span",{style:{fontSize:13}},"\u{1F4CB}"),E("span",{style:{fontSize:11,fontWeight:800,color:P.sCrit,letterSpacing:"0.02em"}},"Baseline")),
            E("div",{style:{display:"flex",alignItems:"center",gap:8}},
              E("span",{style:{fontSize:10,color:P.slateM,fontWeight:500}},"Pre-remediation snapshot"),
              selDoc.url?E("button",{onClick:function(){window.open(selDoc.url,"_blank")},title:"Open PDF in new tab","aria-label":"Open baseline PDF in new tab",style:{padding:"6px 14px",borderRadius:8,border:"2px solid "+P.bdr,background:P.card,color:P.slate,fontSize:12,fontWeight:800,cursor:"pointer",minHeight:36}},"\u2197 Pop Out"):null)):null,
          E("div",{style:{flexShrink:0}},pdfPreview(selDoc)),
          E("div",{style:{borderTop:"1.5px solid "+P.bdr,flexShrink:0}},pdfTagMap(selDoc,false)),
          E("div",{style:{flexShrink:0}},issuePanel(selDoc.issues,"Issues",dc.indexOf(selDoc),false))):null,
        /* Center divider — thin line only, no pill */
        !isMobile?E("div",{style:{background:P.bdr}}):null,
        (!isMobile||inspPane==="remediated")?E("div",{ref:docRightPaneRef,onScroll:function(){docPaneScroll(docRightPaneRef,docLeftPaneRef)},role:"tabpanel","aria-label":"Remediated document",style:{display:"flex",flexDirection:"column",maxHeight:"calc(100vh - 240px)",overflowY:"auto",overflowX:"hidden"}},
          !isMobile?E("div",{style:{padding:"6px 12px",background:P.emerald+"0A",borderBottom:"2px solid "+P.emerald,display:"flex",justifyContent:"space-between",alignItems:"center"}},
            E("div",{style:{display:"flex",alignItems:"center",gap:6}},E("span",{style:{fontSize:13}},"\u2705"),E("span",{style:{fontSize:11,fontWeight:800,color:P.emerald,letterSpacing:"0.02em"}},"Simulated Deliverable")),
            E("div",{style:{display:"flex",alignItems:"center",gap:8}},
              E("span",{style:{fontSize:10,color:P.slateM,fontWeight:500}},"Fixes applied"),
              selDoc.url?E("button",{onClick:function(){window.open(selDoc.url,"_blank")},title:"Open PDF in new tab","aria-label":"Open remediated PDF in new tab",style:{padding:"6px 14px",borderRadius:8,border:"2px solid "+P.bdr,background:P.card,color:P.slate,fontSize:12,fontWeight:800,cursor:"pointer",minHeight:36}},"\u2197 Pop Out"):null)):null,
          E("div",{style:{flexShrink:0}},pdfPreview(selDoc,"rem")),
          E("div",{style:{borderTop:"1.5px solid "+P.bdr,flexShrink:0}},pdfTagMap(selDoc,true)),
          pacMini(selDoc),
          E("div",{style:{flexShrink:0,background:"#FAFEFA"}},
            E("div",{style:{padding:"8px 12px",background:P.bgS,borderBottom:"1px solid "+P.bdr,fontSize:10,fontWeight:700,color:P.slateM}},"\u{1F527} Remediation Summary"),
            remSummary(selDoc.issues),
            /* ── AI Enrichment Results Panel for Docs ── */
            (function(){
              var dIdx=dc.indexOf(selDoc);
              var enrichData=aiEnrich["d"+dIdx];
              if(!enrichData||!enrichData.aiSuggestions||enrichData.aiSuggestions.length===0) return null;
              return E("div",{style:{margin:"8px",borderRadius:8,border:"1.5px solid #DDD6FE",background:"linear-gradient(135deg,#FAFAFE,#F5F3FF)",overflow:"hidden"}},
                E("div",{style:{padding:"8px 12px",background:"#8B5CF620",display:"flex",alignItems:"center",gap:6,borderBottom:"1px solid #DDD6FE"}},
                  E("span",{style:{fontSize:13}},"\u{1F9E0}"),
                  E("span",{style:{fontSize:11,fontWeight:800,color:P.pri}},"PiARIS AI Smart Fixes"),
                  E("span",{style:{padding:"2px 8px",borderRadius:10,background:"#7C3AED",color:"#fff",fontSize:10,fontWeight:700,marginLeft:"auto"}},enrichData.aiSuggestions.length+" suggestions")),
                enrichData.aiSuggestions.map(function(fix,fi){
                  var typeIcon=fix.type==="alt-text"?"\u{1F5BC}":fix.type==="heading-repair"?"H\u20D7":fix.type==="link-purpose"?"\u{1F517}":fix.type==="form-label"?"\u{1F4DD}":fix.type==="contrast"?"\u{1F3A8}":fix.type==="aria"?"\u2699\uFE0F":fix.type==="language"?"\u{1F30D}":"\u{1F527}";
                  var typeLabel=fix.type==="alt-text"?"Alt Text":fix.type==="heading-repair"?"Heading Fix":fix.type==="link-purpose"?"Link Purpose":fix.type==="form-label"?"Form Label":fix.type==="contrast"?"Contrast":fix.type==="aria"?"ARIA":fix.type==="language"?"Language":"Fix";
                  return E("div",{key:"daifix-"+fi,style:{padding:"10px 12px",borderBottom:fi<enrichData.aiSuggestions.length-1?"1px solid #EDE9FE":"none"}},
                    E("div",{style:{display:"flex",gap:6,alignItems:"center",marginBottom:6}},
                      E("span",{style:{fontSize:12}},typeIcon),
                      E("span",{style:{fontSize:10,fontWeight:700,color:P.pri}},typeLabel),
                      E("span",{style:{padding:"1px 6px",borderRadius:3,fontSize:10,fontWeight:600,
                        background:fix.confidence==="high"?"#ECFDF5":fix.confidence==="medium"?"#FFFBEB":"#F1F5F9",
                        color:fix.confidence==="high"?"#059669":fix.confidence==="medium"?"#92400E":"#64748B"}},fix.confidence)),
                    E("div",{style:{fontSize:11,color:P.slate,marginBottom:6}},fix.suggestion),
                    fix.before&&fix.after?E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}},
                      E("div",null,
                        E("div",{style:{fontSize:10,fontWeight:600,color:P.sCrit,marginBottom:2}},"Before:"),
                        E("pre",{style:{margin:0,padding:"4px 6px",borderRadius:4,background:P.sCritBg,fontSize:10,fontFamily:"'SF Mono','Fira Code',monospace",whiteSpace:"pre-wrap",border:"1px solid #FECACA",overflow:"hidden"}},fix.before)),
                      E("div",null,
                        E("div",{style:{fontSize:10,fontWeight:600,color:P.emerald,marginBottom:2}},"After:"),
                        E("pre",{style:{margin:0,padding:"4px 6px",borderRadius:4,background:P.emeraldLt,fontSize:10,fontFamily:"'SF Mono','Fira Code',monospace",whiteSpace:"pre-wrap",border:"1px solid #BBF7D0",overflow:"hidden"}},fix.after))):null,
                    fix.explanation?E("div",{style:{fontSize:10,color:P.slateL,marginTop:4,fontStyle:"italic"}},fix.explanation):null);
                })
              );
            })())):null)
    ):E("div",{style:{display:"flex",flexDirection:"column",gap:5}},
      E("div",{style:{marginBottom:8}},
        E("input",{type:"search",placeholder:"\u{1F50D} Search documents by name (use * as wildcard)…",value:pageSearch,onChange:function(ev){setPageSearch(ev.target.value);setPageListLimit(50)},"aria-label":"Search documents — supports * wildcard and multiple words",style:{width:"100%",padding:"8px 12px",borderRadius:8,border:"1.5px solid "+P.bdr,fontSize:11,fontFamily:F,outline:"none",background:P.bgS,color:P.slate}}),
        E("div",{style:{fontSize:9,color:P.slateL,marginTop:3,fontStyle:"italic"}},"Tip: use * as a wildcard — e.g. *budget* or report*2017; multiple words all must match")),
      (pageSearch?sortedDc.filter(function(d){return matchesSearch((d.name||"")+" "+(d.url||""),pageSearch)}):sortedDc)
        .map(function(d,i){return{d:d,idx:i}})
        .sort(function(a,b){
          var aH=a.d.totalIssues>0?1:0,bH=b.d.totalIssues>0?1:0;
          if(aH!==bH)return bH-aH;
          if(a.d.critCount!==b.d.critCount)return b.d.critCount-a.d.critCount;
          if(a.d.totalIssues!==b.d.totalIssues)return b.d.totalIssues-a.d.totalIssues;
          return(a.d.name||"").localeCompare(b.d.name||"");
        })
        .slice(0,pageSearch?9999:pageListLimit)
        .map(function(item){return listItem(item.d,item.idx,false)}),
      !pageSearch&&sortedDc.length>pageListLimit?E("div",{style:{textAlign:"center",padding:"8px 0"}},
        E("button",{onClick:function(){setPageListLimit(pageListLimit+50)},style:{padding:"6px 16px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:10,fontWeight:600,cursor:"pointer",marginRight:6}},
          "Show More"),
        E("button",{onClick:function(){setPageListLimit(sortedDc.length)},style:{padding:"6px 12px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:10,fontWeight:600,cursor:"pointer"}},
          "All "+sortedDc.length)):null
    )
  );

  /* PDF VALIDATOR */
  var valDoc=sel!==null&&mod==="validator"&&dc[sel]?dc[sel]:null;
  /* PDF Quick-Check is a heuristic PRE-SCAN over the discovered docs
     (dc[].valResults — a category/check model with no rendered pages or
     per-element geometry, unlike Doc Audit). Per the handoff it stays a
     light touch: a11y parity + a clear "pre-scan → authoritative verdict at
     Certify" affordance + a 2-step Select → Pre-scan mini-chevron (NOT the
     4-stage conformance chevron, which would misrepresent a pre-scan). */
  function goValStage(id){ if(id==="select")setSel(null); }
  var valStages=[
    {id:"select",l:"Select",sub:"Pick a document",i:"\u{1F4C2}"},
    {id:"prescan",l:"Pre-scan",sub:"Heuristic check",i:"\u{1F50D}"}
  ];
  var validator = E("div",null,
    E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 4px"}},"\u{1F50D} PDF Quick-Check"),
    E("p",{style:{color:P.slateL,fontSize:11,margin:"0 0 4px"}},"Heuristic pre-scan of structure, metadata, headings, alt text, tables, and contrast - a fast indicator, not the official PDF/UA-1 certificate. The authoritative verdict comes from veraPDF at Certify."),
    chevronFlow(valStages, valDoc?"prescan":"select", { select:true, prescan:!!valDoc }, goValStage, P.pri),
    E("div",{className:"rg2",style:{display:"grid",gridTemplateColumns:"280px 1fr",gap:14}},
      E(Card,{sx:{maxHeight:"calc(100vh - 140px)",overflowY:"auto"},pad:8},
        E("div",{id:"val-doc-list-label",style:{fontSize:10,fontWeight:700,color:P.slateL,textTransform:"uppercase",padding:"4px 6px",marginBottom:4}},"Select Document"),
        E("div",{role:"group","aria-labelledby":"val-doc-list-label"},
        dc.map(function(doc,i){
          var act=sel===i;
          return E("button",{key:i,onClick:function(){setSel(i)},"aria-current":act?"true":undefined,title:doc.name,"aria-label":doc.name,style:{display:"block",width:"100%",textAlign:"left",padding:"6px 8px",borderRadius:6,marginBottom:2,border:"1.5px solid "+(act?P.pri+"30":"transparent"),background:act?P.priLt:"transparent",fontSize:10.5,fontWeight:act?700:500,color:act?P.pri:P.slateM,cursor:"pointer"}},doc.name.substring(0,28));
        }))),
      valDoc&&Array.isArray(valDoc.valResults)&&valDoc.valResults.length?E(Card,null,
        E("h3",{style:{fontSize:15,fontWeight:800,margin:"0 0 8px"}},valDoc.name),
        /* Pre-scan → Certify affordance: this tool is a fast indicator; the
           authoritative PDF/UA-1 verdict is veraPDF at Certify (Doc Audit). */
        E("div",{role:"note",style:{display:"flex",gap:10,alignItems:"flex-start",flexWrap:"wrap",padding:"8px 12px",borderRadius:8,background:P.priLt,border:"1px solid "+P.pri+"30",marginBottom:14}},
          E("span",{"aria-hidden":"true",style:{fontSize:14}},"⚡"),
          E("div",{style:{flex:"1 1 240px",fontSize:11,color:P.slate,lineHeight:1.5}},
            E("strong",null,"Heuristic pre-scan — not a certificate."),
            " These are fast indicators. The authoritative PDF/UA-1 verdict comes from veraPDF at ",E("strong",null,"Certify"),"."),
          E("button",{onClick:function(){setMod("docAudit")},title:"Open Watchtower Doc Audit, where veraPDF Certify runs",style:{padding:"6px 12px",borderRadius:6,border:"1.5px solid "+P.pri,background:"#fff",color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}},"⚖️ Certify in Doc Audit")),
        E("div",{className:"rg3",style:{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:6,marginBottom:16}},
          valDoc.valResults.map(function(vr){
            return E("div",{key:vr.cat.id,role:"img","aria-label":vr.cat.name+": "+vr.score+"%, "+vr.passed+" of "+vr.total+" checks pass",style:{textAlign:"center",padding:8,borderRadius:8,background:vr.score===100?P.emeraldLt:vr.score>=70?P.sMajBg:P.sCritBg,border:"1.5px solid "+(vr.score===100?P.emerald:vr.score>=70?P.sMaj:P.sCrit)+"20"}},
              E("div",{"aria-hidden":"true",style:{fontSize:18}},vr.cat.icon),
              E("div",{"aria-hidden":"true",style:{fontSize:16,fontWeight:800,color:vr.score===100?P.emerald:vr.score>=70?P.sMaj:P.sCrit}},vr.score+"%"),
              E("div",{"aria-hidden":"true",style:{fontSize:10.5,fontWeight:600,color:P.slateM}},vr.cat.name));
          })),
        valDoc.valResults.map(function(vr){
          return E("div",{key:vr.cat.id,style:{marginBottom:10}},
            E("div",{style:{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:"8px 8px 0 0",background:P.bgS,border:"1.5px solid "+P.bdr,borderBottom:"none"}},
              E("span",{"aria-hidden":"true",style:{fontSize:14}},vr.cat.icon),E("span",{style:{fontSize:12,fontWeight:700}},vr.cat.name),E("span",{style:{fontSize:10,color:P.slateL,marginLeft:"auto"}},vr.passed+"/"+vr.total)),
            E("div",{style:{border:"1.5px solid "+P.bdr,borderRadius:"0 0 8px 8px",overflow:"hidden"}},
              vr.results.map(function(ch,ci){
                return E("div",{key:ci,style:{display:"flex",justifyContent:"space-between",padding:"5px 10px",borderBottom:ci<vr.results.length-1?"1px solid "+P.bdr:"none",background:ch.pass?"transparent":P.sCritBg}},
                  E("span",{style:{fontSize:11,color:ch.pass?P.slateM:P.sCrit}},ch.check),
                  E("span",{style:{fontSize:10,fontWeight:700,color:ch.pass?P.emerald:P.sCrit}},ch.pass?"\u2713 Pass":"\u2717 Fail"));
              })));
        })
      ):E(Card,{sx:{display:"flex",alignItems:"center",justifyContent:"center",minHeight:300}},
        E("div",{style:{textAlign:"center",color:P.slateL}},E("div",{"aria-hidden":"true",style:{fontSize:28,marginBottom:6}},valDoc?"\u{1F50D}":"\u2190"),E("div",{style:{fontSize:13}},valDoc?"No deep-validation results for this document.":"Select a document"))))
  );

  /* ── Watchtower Bulk Workflow (Part 3) — Load → Triage → Audit → Remediate
     → Package, composed over the loaded scan (wp/dc). Watchtower LOADS
     externally-discovered scans (PARIS extension or CSV import); there is no
     crawler. Audit/Remediate reuse the single-item engines + verdict
     guardrails per item (compose, don't rewrite). Additive: a new sub-tab
     that threads the EXISTING Watchtower surfaces into one staged flow, plus
     the bulk-only Triage/Select stage. No existing surface changes. ── */
  function goBulkStage(id){ setBulkStage(id); }
  function bulkToggleSel(key){ setBulkSel(function(p){ var n=Object.assign({},p); if(n[key]){delete n[key];}else{n[key]=true;} return n; }); }
  function bulkSetMany(keys,on){ setBulkSel(function(p){ var n=Object.assign({},p); for(var i=0;i<keys.length;i++){ if(on){n[keys[i]]=true;}else{delete n[keys[i]];} } return n; }); }
  var bulkWorkflow=(function(){
    var wAccent=(PRODUCT_COLORS&&PRODUCT_COLORS.watchtower&&PRODUCT_COLORS.watchtower.c1)||P.pri;
    var pages=Array.isArray(wp)?wp:[];
    var docs=Array.isArray(dc)?dc:[];
    var loaded=pages.length>0||docs.length>0;
    var selKeys=Object.keys(bulkSel);
    var selN=selKeys.length;
    var webKeys=pages.map(function(p,i){return "w:"+i;});
    var docKeys=docs.map(function(d,i){return "d:"+i;});
    var stages=[
      {id:"load",l:"Load",sub:"Scan / CSV",i:"\u{1F4E5}"},
      {id:"triage",l:"Triage",sub:"Pick the set",i:"\u{1F9ED}"},
      {id:"audit",l:"Audit",sub:"3 engines · 1280×900",i:"\u{1F50E}"},
      {id:"remediate",l:"Remediate",sub:"Pinpoint & fix",i:"\u{1F6E0}"},
      {id:"package",l:"Package",sub:"Reports · VPAT",i:"\u{1F4E6}"}
    ];
    var reach={ load:true, triage:loaded, audit:selN>0, remediate:selN>0, package:loaded };
    var card=function(kids){ return E(Card,{sx:{borderTop:"2px solid "+wAccent,borderRadius:"0 12px 12px 12px"}},kids); };
    var btn=function(label,onClick,primary){ return E("button",{onClick:onClick,style:{padding:"9px 18px",borderRadius:8,border:primary?"none":"1.5px solid "+P.bdr,background:primary?wAccent:P.card,color:primary?"#fff":P.slate,fontSize:12,fontWeight:700,cursor:"pointer"}},label); };
    var rowEl=function(key,title,sub,crit,total){
      var on=!!bulkSel[key];
      return E("label",{key:key,"data-testid":"wt-triage-row",style:{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderBottom:"1px solid "+P.bdr,cursor:"pointer",background:on?wAccent+"10":"transparent"}},
        E("input",{type:"checkbox",checked:on,onChange:function(){bulkToggleSel(key);},"aria-label":"Select "+title}),
        E("div",{style:{flex:1,minWidth:0}},
          E("div",{style:{fontSize:12,fontWeight:600,color:P.slate,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},title),
          sub?E("div",{style:{fontSize:10,color:P.slateL,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},sub):null),
        E("span",{style:{fontSize:10,fontWeight:700,color:(crit||0)>0?P.sCrit:P.slateM,whiteSpace:"nowrap"}},(crit||0)+" crit · "+(total||0)+" total"));
    };
    var selList=function(){
      return E("div",{style:{maxHeight:"min(55vh,460px)",overflowY:"auto",border:"1px solid "+P.bdr,borderRadius:8,marginBottom:12}},
        selKeys.length?selKeys.map(function(k){
          var parts=k.split(":");var arr=parts[0]==="w"?pages:docs;var it=arr[parseInt(parts[1],10)]||{};
          return E("div",{key:k,style:{padding:"6px 10px",borderBottom:"1px solid "+P.bdr,fontSize:11,color:P.slate,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},(parts[0]==="w"?"🌐 ":"📄 ")+(it.n||it.name||it.url||k));
        }):E("div",{style:{padding:10,fontSize:11,color:P.slateL}},"No items selected — go back to Triage."));
    };
    var sectionHead=function(label,keys){
      return E("div",{style:{display:"flex",alignItems:"center",gap:8,padding:"6px 0",fontSize:11,fontWeight:800,color:P.slate}},
        label,
        E("button",{onClick:function(){bulkSetMany(keys,true);},style:{fontSize:10,fontWeight:700,color:wAccent,background:"none",border:"none",cursor:"pointer"}},"select all"),
        E("button",{onClick:function(){bulkSetMany(keys,false);},style:{fontSize:10,fontWeight:600,color:P.slateL,background:"none",border:"none",cursor:"pointer"}},"clear"));
    };

    var loadStage=card([
      E("div",{key:"h",style:{fontSize:13,fontWeight:800,color:P.slate,marginBottom:6}},"📥 Load a scan"),
      E("p",{key:"p",style:{fontSize:11,color:P.slateM,margin:"0 0 12px",lineHeight:1.5}},"Watchtower works over a scan discovered by the PiARIS extension or a CSV import — it doesn't crawl. Load a saved scan or import a list to begin."),
      loaded?E("div",{key:"sum",style:{padding:"10px 14px",borderRadius:8,background:P.bgS,border:"1px solid "+P.bdr,marginBottom:12,fontSize:12,color:P.slate}},
        E("b",null,domain||"Loaded scan")," · ",pages.length+" page"+(pages.length===1?"":"s")," · ",docs.length+" document"+(docs.length===1?"":"s")):
        E("div",{key:"empty",style:{padding:"14px",borderRadius:8,border:"2px dashed "+P.bdr,color:P.slateL,fontSize:12,marginBottom:12,textAlign:"center"}},"No scan loaded yet."),
      E("div",{key:"acts",style:{display:"flex",gap:8,flexWrap:"wrap"}},
        btn("📂 Load saved scan",function(){openScanPicker("watchtower");}),
        btn("📄 Import CSV",function(){setMod("import");}),
        loaded?btn("Continue to Triage →",function(){goBulkStage("triage");},true):null)
    ]);

    var triageStage=card([
      E("div",{key:"h",style:{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:8}},
        E("div",{style:{fontSize:13,fontWeight:800,color:P.slate}},"🧭 Triage — pick what to work on"),
        E("div",{style:{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}},
          E("span",{style:{fontSize:11,fontWeight:700,color:wAccent}},selN+" selected"),
          E("button",{"data-testid":"wt-audit-selected",onClick:function(){if(selN>0)goBulkStage("audit");},disabled:selN===0,style:{padding:"9px 18px",borderRadius:8,border:"none",background:selN>0?wAccent:"#CBD5E1",color:"#fff",fontSize:12,fontWeight:700,cursor:selN>0?"pointer":"default"}},"Audit selected →"))),
      (templateStats&&templateStats.templateGroupCount>0)?E("div",{key:"shared",style:{padding:"8px 12px",borderRadius:8,background:P.priLt,border:"1px solid "+wAccent+"30",marginBottom:12,fontSize:11,color:P.slate}},
        E("b",null,"🔗 Shared across pages: "),templateStats.templateGroupCount+" template fix"+(templateStats.templateGroupCount===1?"":"es")+" cover "+(templateStats.templateInstanceCount||0).toLocaleString()+" issue instance"+(templateStats.templateInstanceCount===1?"":"s")+" — fix once, applies across matching pages."):null,
      E("div",{key:"tbl","data-testid":"wt-triage-table"},
        pages.length>0?E("div",{style:{marginBottom:14}},
          sectionHead("🌐 Web pages ("+pages.length+")",webKeys),
          E("div",{style:{border:"1px solid "+P.bdr,borderRadius:8,overflow:"hidden"}},
            pages.map(function(p,i){return rowEl("w:"+i,p.n||p.url||("Page "+(i+1)),p.url||"",p.critCount,p.totalIssues);}))):null,
        docs.length>0?E("div",null,
          sectionHead("📄 Documents ("+docs.length+")",docKeys),
          E("div",{style:{border:"1px solid "+P.bdr,borderRadius:8,overflow:"hidden"}},
            docs.map(function(d,i){return rowEl("d:"+i,d.name||d.url||("Doc "+(i+1)),(d.tagged?"tagged":"untagged")+(d.ext?(" · "+d.ext):""),d.critCount,d.totalIssues);}))):null,
        !loaded?E("div",{style:{padding:14,color:P.slateL,fontSize:12}},"Nothing to triage — load a scan first."):null)
    ]);

    var auditStage=card([
      E("div",{key:"h",style:{fontSize:13,fontWeight:800,color:P.slate,marginBottom:6}},"🔎 Audit "+selN+" selected item"+(selN===1?"":"s")),
      E("p",{key:"p",style:{fontSize:11,color:P.slateM,margin:"0 0 10px",lineHeight:1.5}},"Each item is audited by the same three engines (axe-core · HTMLCS · Claude AI) at a fixed 1280×900 desktop viewport, with the same verdict guardrails — the protected four (2.1.1, 2.4.3, 1.2.2, 1.4.1) always escalate to manual review, even aggregated."),
      selList(),
      E("div",{key:"acts",style:{display:"flex",gap:8,flexWrap:"wrap"}},
        btn("🌐 Open Web Audit",function(){setMod("webAudit");}),
        btn("📄 Open Doc Audit",function(){setMod("docAudit");}),
        btn("Continue to Remediate →",function(){goBulkStage("remediate");},true))
    ]);

    var remediateStage=card([
      E("div",{key:"h",style:{fontSize:13,fontWeight:800,color:P.slate,marginBottom:6}},"🛠 Remediate"),
      E("p",{key:"p",style:{fontSize:11,color:P.slateM,margin:"0 0 10px",lineHeight:1.5}},"Drill into an item to fix it with the same inline viewer + click-to-pinpoint as the single-item lab; apply a template fix once to propagate across matching pages."),
      selList(),
      E("div",{key:"acts",style:{display:"flex",gap:8,flexWrap:"wrap"}},
        btn("🔧 Open Remediation Center",function(){setMod("remediation");}),
        btn("🧠 Build Smart Remediation",function(){if(typeof buildRemediationPackage==="function")buildRemediationPackage();}),
        btn("Continue to Package →",function(){goBulkStage("package");},true))
    ]);

    var packageStage=card([
      E("div",{key:"h",style:{fontSize:13,fontWeight:800,color:P.slate,marginBottom:6}},"📦 Package & certify"),
      E("p",{key:"p",style:{fontSize:11,color:P.slateM,margin:"0 0 10px",lineHeight:1.5}},"Export the evaluation across the set: the report suite, VPAT readiness, and per-document veraPDF certification."),
      E("div",{key:"acts",style:{display:"flex",gap:8,flexWrap:"wrap"}},
        btn("📊 Open Reports",function(){setMod("reports");},true))
    ]);

    var body = bulkStage==="triage"?triageStage
      : bulkStage==="audit"?auditStage
      : bulkStage==="remediate"?remediateStage
      : bulkStage==="package"?packageStage
      : loadStage;

    return E("div",null,
      E("div",{style:{marginBottom:4}},
        E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 2px"}},"🗂️ Watchtower — Bulk Workflow"),
        E("p",{style:{color:P.slateL,fontSize:11,margin:0}},"Run the whole scanned set through one staged flow: load, triage, audit, remediate, certify.")),
      chevronFlow(stages, bulkStage, reach, goBulkStage, wAccent),
      (bulkStage!=="triage"&&bulkStage!=="audit"&&bulkStage!=="remediate")
        ? E("div",{style:{maxWidth:680,margin:"0 auto",minHeight:"46vh",display:"flex",flexDirection:"column",justifyContent:"center"}},body)
        : body);
  })();

  /* MONITORING */
  var logs=[{t:"now",msg:"Full audit completed: "+wp.length+" pages, "+dc.length+" documents",ty:"scan"},{t:"2m ago",msg:"3 new PDFs discovered since last scan",ty:"new"},{t:"1h ago",msg:"Smoke-Advisory modified \u2014 re-validated",ty:"chg"},{t:"3h ago",msg:"Continuous monitoring active \u2014 6hr cycle",ty:"mon"}];
  var monitoring = E("div",null,
    E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 14px"}},"\u{1F4E1} Continuous Monitoring"),
    E("div",{className:"rg3",style:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}},
      [{v:"Active",l:"Status",c:P.emerald},{v:"6 hrs",l:"Scan Interval",c:P.pri},{v:dc.length,l:"PDFs Tracked",c:P.slate},{v:"3",l:"New Found",c:P.amber}].map(function(m){return E(MC,{key:m.l,v:m.v,label:m.l,c:m.c})})),
    E(Card,null,
      E("h3",{style:{fontSize:13,fontWeight:700,margin:"0 0 10px"}},"Activity Log"),
      logs.map(function(log,i){
        var cols={scan:P.pri,new:P.amber,chg:P.accent,mon:P.emerald};
        return E("div",{key:i,style:{display:"flex",gap:10,alignItems:"center",padding:"8px 0",borderBottom:i<logs.length-1?"1px solid "+P.bdr:"none"}},
          E("div",{style:{width:8,height:8,borderRadius:"50%",background:cols[log.ty]||P.slateL,flexShrink:0}}),
          E("div",{style:{flex:1,fontSize:12}},log.msg),
          E("div",{style:{fontSize:10,color:P.slateL,flexShrink:0}},log.t))}),
      E("div",{style:{padding:"12px 0 0",fontSize:11,color:P.slateL,borderTop:"1px solid "+P.bdr,marginTop:10}},"ARIS continuously scans your domain every 6 hours. Newly published or modified PDFs are automatically discovered, validated, and flagged if they require remediation. High-traffic documents are prioritized based on access analytics."))
  );

  /* REMEDIATION */
  var remCenter = E("div",null,
    E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}},
      E("div",null,
        E("h2",{style:{fontSize:20,fontWeight:800,margin:0}},"\u{1F527} Remediation Center"),
        E("p",{style:{color:P.slateL,fontSize:11,margin:"2px 0 0"}},"Generate fix scripts, review patches, deploy fixes")),
      E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
        E("button",{onClick:function(){
          /* Batch remediate all web pages */
          showToast("\u{1F527} Generating fix scripts for all "+wp.filter(function(p){return p.issues&&p.issues.length>0&&!p.fixScript}).length+" pages\u2026");
          var queue=[];
          wp.forEach(function(pg,idx){if(pg.issues&&pg.issues.length>0&&!pg.fixScript)queue.push(idx)});
          queue.forEach(function(idx,i){setTimeout(function(){remediatePage(wp[idx])},i*2500)});
          if(queue.length===0)showToast("All pages already have fix scripts generated.");
        },style:{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.emerald+","+P.accent+")",color:"#fff",fontSize:11,fontWeight:700}},"\u26A1 Generate All Fix Scripts ("+wp.filter(function(p){return p.issues&&p.issues.length>0&&!p.fixScript}).length+" pages)"),
        wp.some(function(p){return p.fixScript})?E("button",{onClick:function(){
          /* Download professional PiARIS Fix Delivery Package */
          var pagesWithFixes=wp.filter(function(p){return p.fixScript});
          var totalPatches=pagesWithFixes.reduce(function(x,p){return x+(p.patches?p.patches.length:0)},0);
          var totalIssuesFixed=pagesWithFixes.reduce(function(x,p){return x+p.totalIssues},0);
          var now=new Date().toISOString();
          var safeDomain=domain.replace(/[^a-z0-9]/gi,"-");

          /* Build combined fix script with URL guards */
          var scripts=pagesWithFixes.map(function(p){
            var urlHost=(p.url||"").replace(/^https?:\/\//,"").split("/")[0]||"";
            var urlPath="/";try{urlPath=new URL(p.url).pathname}catch(e){}
            return "/* ═══ Page: "+p.n+" ═══ */\n"+
              "/* URL: "+p.url+" */\n"+
              "/* Issues: "+p.totalIssues+" | Patches: "+(p.patches?p.patches.length:0)+" */\n"+
              "if(window.location.hostname.indexOf('"+urlHost+"')>-1&&window.location.pathname==='"+urlPath+"'){\n"+
              "  try{\n"+
              "    "+p.fixScript.split("\n").join("\n    ")+"\n"+
              "    console.log('[PiARIS] Fixes applied: "+p.n+"');\n"+
              "  }catch(e){console.warn('[PiARIS] Fix error on "+p.n+":', e.message)}\n"+
              "}\n";
          });

          var header=[
            "/**",
            " * ═══════════════════════════════════════════════════════════",
            " * PiARIS Fix Delivery Package",
            " * \u00A9 "+new Date().getFullYear()+" Pi Consultants \u2014 pi-consulting.us",
            " * ═══════════════════════════════════════════════════════════",
            " *",
            " * Domain:    "+domain,
            " * Generated: "+now,
            " * Pages:     "+pagesWithFixes.length+" pages with fixes",
            " * Patches:   "+totalPatches+" total code patches",
            " * Issues:    "+totalIssuesFixed+" accessibility issues addressed",
            " * Standard:  WCAG 2.1 Level AA",
            " *",
            " * DEPLOYMENT INSTRUCTIONS:",
            " * ═══════════════════════════════════════════════════════════",
            " * Add this script tag to your site's <head> or before </body>:",
            " *",
            " *   <script src=\"paris-fix-"+safeDomain+".js\"></"+"script>",
            " *",
            " * How it works:",
            " *   - Runs on every page load",
            " *   - Detects the current page URL",
            " *   - Applies only the fixes relevant to that page",
            " *   - Patches the live DOM: adds alt text, fixes headings,",
            " *     injects ARIA labels, improves contrast",
            " *   - No layout changes, no overlays, no visual disruption",
            " *   - Safe to deploy alongside existing scripts",
            " *",
            " * PLATFORM-SPECIFIC NOTES:",
            " *   WordPress:  Add via functions.php or insert-headers plugin",
            " *   Granicus:   Add to Site Settings \u2192 Header/Footer Code",
            " *   Drupal:     Add to html.html.twig in <head> region",
            " *   Static:     Add to index.html <head> tag",
            " *",
            " * To verify: After deploying, re-scan with PiARIS to confirm",
            " * issue count reduction.",
            " * ═══════════════════════════════════════════════════════════",
            " */",
            "",
            "(function(){",
            "  'use strict';",
            "  if(typeof window==='undefined')return;",
            "  var PiARIS_VERSION='"+APP_VERSION+"';",
            "  var PiARIS_GENERATED='"+now+"';",
            "  console.log('[PiARIS] Fix bundle loaded v'+PiARIS_VERSION+' | '+PiARIS_GENERATED);",
            ""
          ].join("\n");

          var footer=[
            "",
            "  console.log('[PiARIS] Fix bundle complete — "+pagesWithFixes.length+" page handlers registered');",
            "})();",
            "",
            "/* ═══ MANIFEST ═══ */",
            "/* "+JSON.stringify({domain:domain,generated:now,version:APP_VERSION,vendor:"Pi Consultants",pages:pagesWithFixes.map(function(p){return{url:p.url,title:p.n,issues:p.totalIssues,patches:p.patches?p.patches.length:0}})})+" */"
          ].join("\n");

          var bundle=header+scripts.join("\n\n")+footer;
          var b=new Blob([bundle],{type:"application/javascript"});
          var a=document.createElement("a");a.href=URL.createObjectURL(b);
          a.download="paris-fix-"+safeDomain+".js";a.click();
          showToast("\u{1F4E6} Fix Delivery Package downloaded ("+pagesWithFixes.length+" pages, "+totalPatches+" patches)");
        },style:{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.emerald+",#059669)",color:"#fff",fontSize:11,fontWeight:700,boxShadow:"0 2px 8px "+P.emerald+"30"}},"\u{1F4E6} Download Fix Package ("+wp.filter(function(p){return p.fixScript}).length+" pages)"):null,
        wp.some(function(p){return p.fixScript})?E("button",{onClick:function(){
          /* Copy deploy script tag to clipboard */
          var tag='<script src="paris-fix-'+domain.replace(/[^a-z0-9]/gi,"-")+'.js"></'+'script>';
          navigator.clipboard.writeText(tag).then(function(){showToast("\u{1F4CB} Deploy tag copied: "+tag)}).catch(function(){showToast("Copy failed","error")});
        },style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.emerald+"40",background:P.emeraldLt,color:P.emerald,fontSize:11,fontWeight:700}},"\u{1F4CB} Copy Deploy Tag"):null,
        wp.some(function(p){return p.patches&&p.patches.length>0})?E("button",{onClick:function(){
          /* Download combined remediation workbook */
          var lines=["# PiARIS Remediation Workbook","# Domain: "+domain,"# Generated: "+new Date().toISOString(),"# Total pages: "+wp.filter(function(p){return p.patches}).length,""];
          wp.forEach(function(p){
            if(!p.patches||p.patches.length===0)return;
            lines.push("# ═══════════════════════════════════════");
            lines.push("# Page: "+p.url);
            lines.push("# Title: "+p.n);
            lines.push("# Patches: "+p.patches.length);
            lines.push("");
            p.patches.forEach(function(patch,pi){
              lines.push("## Patch "+(pi+1)+": WCAG "+patch.wcag+" ("+patch.severity+")");
              lines.push(patch.description);lines.push("");
              lines.push("### Before:");lines.push("```html");lines.push(patch.before);lines.push("```");lines.push("");
              lines.push("### After:");lines.push("```html");lines.push(patch.after);lines.push("```");lines.push("");
              lines.push("Selector: `"+patch.selector+"` | Fix type: "+patch.fixType);
              lines.push("");lines.push("---");lines.push("");
            });
          });
          var b=new Blob([lines.join("\n")],{type:"text/markdown"});
          var a=document.createElement("a");a.href=URL.createObjectURL(b);
          a.download="paris-workbook-"+domain.replace(/[^a-z0-9]/gi,"-")+".md";a.click();
          showToast("\u{1F4CB} Remediation workbook downloaded");
        },style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid #8B5CF640",background:P.priLt,color:P.pri,fontSize:11,fontWeight:700}},"\u{1F4CB} Download Workbook"):null)),

    /* ═══ INVENTORY STATUS ═══ */
    E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(4,1fr)",gap:12,marginBottom:16}},
      E("div",{style:{padding:"16px",borderRadius:12,background:P.card,border:"1.5px solid "+P.bdr,textAlign:"center"}},
        E("div",{style:{fontSize:24,fontWeight:900,color:P.pri}},wp.length),
        E("div",{style:{fontSize:10,color:P.slateL,fontWeight:600}},"Web Pages Scanned")),
      E("div",{style:{padding:"16px",borderRadius:12,background:P.card,border:"1.5px solid "+P.bdr,textAlign:"center"}},
        E("div",{style:{fontSize:24,fontWeight:900,color:P.emerald}},wp.filter(function(p){return p.fixScript}).length),
        E("div",{style:{fontSize:10,color:P.slateL,fontWeight:600}},"Fix Scripts Generated")),
      E("div",{style:{padding:"16px",borderRadius:12,background:P.card,border:"1.5px solid "+P.bdr,textAlign:"center"}},
        E("div",{style:{fontSize:24,fontWeight:900,color:P.pri}},wp.reduce(function(n,p){return n+(p.patches?p.patches.length:0)},0)),
        E("div",{style:{fontSize:10,color:P.slateL,fontWeight:600}},"Source Patches Ready")),
      E("div",{style:{padding:"16px",borderRadius:12,background:P.card,border:"1.5px solid "+P.bdr,textAlign:"center"}},
        E("div",{style:{fontSize:24,fontWeight:900,color:P.coral}},dc.length),
        E("div",{style:{fontSize:10,color:P.slateL,fontWeight:600}},"Documents Scanned"))),

    /* ═══ PAGE-BY-PAGE FIX STATUS ═══ */
    E("div",{style:{marginBottom:16}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,gap:8}},
        E("h3",{style:{fontSize:13,fontWeight:700,margin:0}},"\u{1F310} Web Pages ("+wp.length+")"),
        E("input",{type:"search",placeholder:"\u{1F50D} Search pages...",value:pageSearch,onChange:function(ev){setPageSearch(ev.target.value)},"aria-label":"Search pages",style:{flex:"0 1 280px",padding:"6px 12px",borderRadius:8,border:"1.5px solid "+P.bdr,fontSize:11,fontFamily:F,outline:"none",background:P.bgS,color:P.slate}})),
      wp.length===0?E("div",{style:{padding:20,textAlign:"center",color:P.slateL,fontSize:12}},"No pages scanned yet. Run an audit from the Dashboard."):
      E("div",{style:{display:"grid",gap:6}},
        wp.map(function(pg,i){return{pg:pg,idx:i}}).filter(function(item){
          if(!pageSearch)return true;
          var q=pageSearch.toLowerCase();
          return(item.pg.n||"").toLowerCase().indexOf(q)>-1||(item.pg.url||"").toLowerCase().indexOf(q)>-1;
        }).sort(function(a,b){
          var aHas=a.pg.totalIssues>0?1:0;var bHas=b.pg.totalIssues>0?1:0;
          if(aHas!==bHas) return bHas-aHas;
          if(a.pg.critCount!==b.pg.critCount) return b.pg.critCount-a.pg.critCount;
          if(a.pg.totalIssues!==b.pg.totalIssues) return b.pg.totalIssues-a.pg.totalIssues;
          return(a.pg.n||a.pg.url||"").localeCompare(b.pg.n||b.pg.url||"");
        }).slice(0,pageSearch?9999:pageListLimit).map(function(item){var pg=item.pg;var idx=item.idx;
          var hasScript=!!pg.fixScript;
          var patchCount=pg.patches?pg.patches.length:0;
          var isGenerating=staging.some(function(s){return s.url===pg.url&&s.status==="generating"});
          return E("div",{key:idx,style:{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,border:"1.5px solid "+(hasScript?P.emerald+"30":P.bdr),background:hasScript?P.emeraldLt+"40":P.card}},
            E("div",{style:{width:8,height:8,borderRadius:"50%",background:hasScript?P.emerald:isGenerating?P.amber:pg.totalIssues>0?"#DC2626":"#94A3B8",flexShrink:0}}),
            E("div",{style:{flex:1,minWidth:0}},
              E("div",{style:{fontSize:11,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},pg.n||pg.url),
              E("div",{style:{fontSize:10,color:P.slateL}},pg.totalIssues+" issues \u00B7 "+pg.critCount+" critical"+
                (hasScript?" \u00B7 "+patchCount+" patches"+(pg.fixSource==="ai"?" (AI)":""):""))),
            E("div",{style:{display:"flex",gap:4,flexShrink:0}},
              isGenerating?E("span",{style:{padding:"4px 10px",borderRadius:6,background:P.amberLt,color:P.amber,fontSize:10,fontWeight:700,animation:"markerPulse 1.5s infinite"}},"\u23F3"):
              !hasScript&&pg.totalIssues>0?E("button",{onClick:function(){remediatePage(pg)},style:{padding:"4px 10px",borderRadius:6,border:"none",background:"linear-gradient(135deg,"+P.emerald+","+P.accent+")",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u{1F527} Fix"):null,
              hasScript?E("button",{onClick:function(){downloadFixScript(pg)},title:"Download fix script",style:{padding:"4px 8px",borderRadius:6,border:"1px solid "+P.emerald+"40",background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700,cursor:"pointer"}},"\u2B07 .js"):null,
              patchCount>0?E("button",{onClick:function(){downloadPatches(pg)},title:"Download patches",style:{padding:"4px 8px",borderRadius:6,border:"1px solid #8B5CF640",background:P.priLt,color:P.pri,fontSize:10,fontWeight:700,cursor:"pointer"}},"\u{1F4CB}"):null,
              pg.fixSummary&&pg.fixSummary.deployTag?E("button",{onClick:function(){copyDeployTag(pg)},title:"Copy deploy tag",style:{padding:"4px 8px",borderRadius:6,border:"1px solid "+P.pri+"40",background:P.priLt,color:P.pri,fontSize:10,fontWeight:700,cursor:"pointer"}},"\u{1F4CB}"):null))
        })),
      !pageSearch&&wp.length>pageListLimit?E("div",{style:{textAlign:"center",padding:"8px 0"}},
        E("button",{onClick:function(){setPageListLimit(pageListLimit+50)},style:{padding:"8px 20px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600,cursor:"pointer",marginRight:8}},
          "Show More ("+Math.min(50,wp.length-pageListLimit)+" of "+(wp.length-pageListLimit)+" remaining)"),
        E("button",{onClick:function(){setPageListLimit(wp.length)},style:{padding:"8px 14px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600,cursor:"pointer"}},
          "Show All "+wp.length)):null),

    /* ═══ DOCUMENTS ═══ */
    dc.length>0?E("div",{style:{marginBottom:16}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}},
        E("h3",{style:{fontSize:13,fontWeight:700,margin:0}},"\u{1F4C4} Documents"),
        E("button",{onClick:function(){sourceRemediateAllDocs()},style:{padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#EF4444,#D97706)",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u{1F527} Batch Remediate All ("+dc.filter(function(d){return d.totalIssues>0&&!d.remediated}).length+")")),
      E("div",{style:{display:"grid",gap:6}},
        dc.map(function(doc,idx){
          var cs=convStatus[idx];
          var isConverting=convLoading[idx];
          var isComplete=cs&&cs.status==="complete";
          var isFailed=cs&&cs.status==="failed";
          return E("div",{key:idx,style:{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,border:"1.5px solid "+(isComplete?P.emerald+"30":isFailed?P.coral+"30":P.bdr),background:isComplete?P.emeraldLt+"40":P.card}},
            E("div",{style:{width:8,height:8,borderRadius:"50%",background:isComplete?"#059669":isConverting?"#F59E0B":isFailed?"#DC2626":doc.totalIssues>0?(doc.tagged?"#D97706":"#DC2626"):"#059669",flexShrink:0,animation:isConverting?"markerPulse 1.5s infinite":"none"}}),
            E("div",{style:{flex:1,minWidth:0}},
              E("div",{style:{fontSize:11,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},doc.name),
              E("div",{style:{fontSize:10,color:P.slateL}},
                isComplete?(cs.complianceGain||85)+"% compliance gain \u00B7 "+doc.pages+" pages converted":
                isConverting?"Converting via DocMaya\u2026":
                isFailed?"Conversion failed: "+(cs.error||"unknown error"):
                doc.totalIssues+" issues \u00B7 "+(doc.tagged?"Tagged":"Untagged")+" \u00B7 "+doc.pages+" pages")),
            E("div",{style:{display:"flex",gap:4,flexShrink:0}},
              isComplete?E("span",{style:{padding:"4px 10px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u2713 Converted"):
              isConverting?E("span",{style:{padding:"4px 10px",borderRadius:6,background:P.amberLt,color:P.amber,fontSize:10,fontWeight:700,animation:"markerPulse 1.5s infinite"}},"\u23F3 Converting\u2026"):
              isFailed?E("button",{onClick:function(){sourceRemediateDoc(doc, idx)},style:{padding:"4px 10px",borderRadius:6,border:"none",background:"#DC2626",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u21BB Retry"):
              doc.totalIssues>0?E("button",{onClick:function(){sourceRemediateDoc(doc, idx)},style:{padding:"4px 10px",borderRadius:6,border:"none",background:"linear-gradient(135deg,"+P.coral+","+P.amber+")",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u{1F527} Fix"):null))
        }))
    ):null,

    /* ── PROFESSIONAL REPORT EXPORT ── */
    E("div",{style:{marginTop:14}},
      E("div",{style:{padding:"18px 20px",borderRadius:14,border:"1.5px solid #8B5CF640",background:"linear-gradient(135deg,#FAFAFE,#F5F3FF)"}},
        E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}},
          E("div",null,
            E("h3",{style:{fontSize:14,fontWeight:700,margin:"0 0 4px",color:P.pri}},"\u{1F4CA} Professional Remediation Report"),
            E("div",{style:{fontSize:10.5,color:P.slateM}},"Industry-standard XLSX: Overview, Detailed Findings (existing + suggested code), Color Suggestions, WCAG Reference.")),
          E("div",{style:{display:"flex",gap:6}},
            E("button",{onClick:exportProfessionalReport,disabled:totalI===0,style:{padding:"10px 20px",borderRadius:8,border:"none",background:totalI>0?"linear-gradient(135deg,#8B5CF6,#6D28D9)":"#CBD5E1",color:"#fff",fontSize:12,fontWeight:700,cursor:totalI>0?"pointer":"not-allowed"}},"\u{1F4CA} Export Professional Report"),
            E("button",{onClick:exportAssessmentReport,disabled:totalI===0||assessLoading,style:{padding:"10px 20px",borderRadius:8,border:"none",background:totalI>0&&!assessLoading?"linear-gradient(135deg,#059669,#047857)":"#CBD5E1",color:"#fff",fontSize:12,fontWeight:700,cursor:totalI>0&&!assessLoading?"pointer":"not-allowed"}},assessLoading?"\u23F3 Generating...":"\u{1F4DD} Assessment Report"),
            E("button",{onClick:function(){
              showToast("\u23F3 Generating compliance certificate...");
              var certPayload={domain:domain,orgName:domain,score:overallScore,scanDate:scanMeta&&scanMeta.completedAt||new Date().toISOString(),
                summary:Object.assign({},{totalPages:wp.length,totalDocuments:dc.length,
                  critical:wp.reduce(function(x,p){return x+p.critCount},0),
                  major:wp.reduce(function(x,p){return x+p.majCount},0),
                  minor:wp.reduce(function(x,p){return x+p.minCount},0),
                  totalElements:wp.reduce(function(x,p){return x+p.totalIssues},0),
                  pagesClean:wp.filter(function(p){return p.totalIssues===0}).length,
                  pagesWithIssues:wp.filter(function(p){return p.totalIssues>0}).length,
                  pdf:{total:dc.filter(function(d){return d.ext==="pdf"}).length,
                    tagged:dc.filter(function(d){return d.isTagged}).length,
                    untagged:dc.filter(function(d){return d.ext==="pdf"&&!d.isTagged}).length,
                    highRisk:dc.filter(function(d){return d.riskLevel==="high"}).length},
                  archive:{pages:wp.filter(function(p){return p.isArchived}).length,
                    issues:wp.filter(function(p){return p.isArchived}).reduce(function(x,p){return x+p.totalIssues},0)}},
                  scanMeta&&scanMeta.templateDedup?{estimatedIssues:scanMeta.templateDedup.estimatedIssues||0,
                    estimatedCritical:scanMeta.templateDedup.estimatedCritical||0,
                    templateSkipped:scanMeta.templateDedup.templateSkipped||0}:{})};
              fetch("/api/compliance-certificate",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(certPayload)}).then(function(res){
                if(!res.ok)throw new Error("Certificate generation failed: "+res.status);return res.blob();
              }).then(function(blob){
                var url=URL.createObjectURL(blob);var a=document.createElement("a");a.href=url;
                a.download="PiARIS-Compliance-Certificate-"+domain.replace(/[^a-zA-Z0-9]/g,"-")+".pdf";
                a.click();URL.revokeObjectURL(url);showToast("\u2713 Compliance certificate downloaded");
              }).catch(function(e){showToast("Certificate failed: "+e.message,"error")});
            },disabled:wp.length===0,style:{padding:"10px 20px",borderRadius:8,border:"none",background:wp.length>0?"linear-gradient(135deg,#D97706,#B45309)":"#CBD5E1",color:"#fff",fontSize:12,fontWeight:700,cursor:wp.length>0?"pointer":"not-allowed"}},"\u{1F3C6} Compliance Certificate"),
            E("button",{onClick:exportReport,disabled:totalI===0,style:{padding:"10px 16px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slate,fontSize:11,fontWeight:600,cursor:totalI>0?"pointer":"not-allowed"}},"\u{1F4BE} Export JSON"))))),
    /* ═══ STAGING ENVIRONMENT ═══ */
    E("div",{style:{marginTop:16}},
      E("div",{style:{padding:"18px 20px",borderRadius:14,border:"1.5px solid "+P.bdr,background:P.card}},
        E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:6}},
          E("div",null,
            E("h3",{style:{fontSize:14,fontWeight:700,margin:0}},"\u{1F680} Staging Queue"),
            E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},staging.length+" items")),
          E("div",{style:{display:"flex",gap:6}},
            staging.filter(function(s){return s.status==="staged"}).length>0?E("button",{onClick:function(){setStaging(function(prev){return prev.map(function(s){return s.status==="staged"?Object.assign({},s,{status:"deployed"}):s})})},style:{padding:"6px 14px",borderRadius:6,border:"none",background:P.emerald,color:"#fff",fontSize:10,fontWeight:700}},"\u{1F680} Deploy All"):null,
            staging.length>0?E("button",{onClick:function(){setStaging([])},style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600}},"\u{1F5D1} Clear"):null)),
        staging.length===0?E("div",{style:{textAlign:"center",padding:"20px 0",color:P.slateL}},
          E("div",{style:{fontSize:28,marginBottom:6}},"\u{1F4E6}"),
          E("div",{style:{fontSize:11,fontWeight:600}},"No items in staging. Click Fix on any page above to generate a fix script.")):
        E("div",{style:{display:"grid",gap:6}},staging.map(function(s,i){
          var statusBg=s.status==="deployed"?P.emeraldLt:s.status==="generating"?P.amberLt:s.status==="failed"?P.coralLt:P.priLt;
          var statusCol=s.status==="deployed"?P.emerald:s.status==="generating"?P.amber:s.status==="failed"?P.coral:P.pri;
          var statusLabel=s.status==="deployed"?"\u2713 Deployed":s.status==="generating"?"\u23F3 Generating":s.status==="failed"?"\u26A0 Failed":"\u{1F4E6} Staged";
          return E("div",{key:i,style:{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,background:s.status==="deployed"?"#F0FDF4":P.card,border:"1px solid "+P.bdr}},
            E("span",{style:{padding:"2px 8px",borderRadius:4,fontSize:9,fontWeight:700,background:statusBg,color:statusCol}},statusLabel),
            E("span",{style:{padding:"1px 6px",borderRadius:4,background:s.type==="web"?P.accentLt:"#FFF1F0",fontSize:9,fontWeight:700,color:s.type==="web"?P.accent:P.coral}},s.type==="web"?"Web":"PDF"),
            E("div",{style:{flex:1,fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},s.name),
            s.patchCount?E("span",{style:{fontSize:10,color:P.pri,fontWeight:600}},s.patchCount+" patches"):null,
            s.source?E("span",{style:{fontSize:9,color:P.slateL}},s.source==="ai"?"\u{1F9E0} AI":"Rules"):null,
            E("div",{style:{display:"flex",gap:4}},
              s.status==="staged"?E("button",{onClick:function(){deployStaged(i)},style:{padding:"3px 8px",borderRadius:4,border:"none",background:P.emerald,color:"#fff",fontSize:9,fontWeight:700,cursor:"pointer"}},"\u{1F680}"):null,
              s.status!=="generating"?E("button",{onClick:function(){rollbackStaged(i)},style:{padding:"3px 8px",borderRadius:4,border:"1px solid "+P.bdr,background:P.card,fontSize:9,cursor:"pointer"}},"\u2715"):null))
        })),
        /* Deploy instructions */
        wp.some(function(p){return p.fixScript})?E("div",{style:{marginTop:12,padding:"12px 14px",borderRadius:8,background:"linear-gradient(135deg,"+P.priLt+","+P.emeraldLt+"40)",border:"1px solid "+P.pri+"20",fontSize:10.5,color:P.slateM,lineHeight:1.7}},
          E("strong",{style:{color:P.pri}},"Deploy: "),
          "Add this script tag to your site's <head>. Fix scripts run on page load and patch the live DOM — adding alt text, fixing headings, injecting ARIA labels, improving contrast. No layout changes, no overlays.",
          E("div",{style:{marginTop:8,padding:"8px 12px",borderRadius:6,background:P.card,border:"1px solid "+P.bdr,fontFamily:FC,fontSize:10,wordBreak:"break-all"}},
            '<script src="https://paris.pi-consulting.us/api/remediate/script/'+encodeURIComponent(domain)+'"></script>')
        ):null))
  );

  /* VPAT */
  var vpatPage = E("div",null,
    E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 14px"}},"\u{1F4DC} VPAT \u2014 Voluntary Product Accessibility Template"),
    E(Card,{sx:{marginBottom:14}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}},
        E("div",null,E("h3",{style:{fontSize:15,fontWeight:700,margin:0}},"Section 508 / WCAG 2.1 AA Conformance Report"),E("div",{style:{fontSize:11,color:P.slateL,marginTop:2}},domain+" \u2014 "+new Date().toLocaleDateString())),
        E("div",{style:{display:"flex",gap:6}},
          E("button",{onClick:function(){setVpat(true)},style:{padding:"10px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:12,fontWeight:700}},vpat?"\u2713 VPAT Generated":"Generate VPAT"),
          vpat?E("button",{onClick:function(){
            /* Download VPAT as formatted text document */
            var lines=["VOLUNTARY PRODUCT ACCESSIBILITY TEMPLATE (VPAT)","Revised Section 508 Edition \u2014 WCAG 2.1 Level AA","","="
.repeat(60),"","PRODUCT INFORMATION","  Name: "+(domain||"Web Application"),"  Version: 1.0","  URL: "+(domain?"https://"+domain:""),"  Vendor: Pi Consultants","  Date: "+new Date().toLocaleDateString(),"  Standard: WCAG 2.1 Level AA + Revised Section 508","","EVALUATION METHODS","  Automated testing with PiARIS (axe-core 4.10 + HTML_CodeSniffer),","  supplemented by manual review. "+wp.length+" pages and "+dc.length+" documents evaluated.","","CONFORMANCE SUMMARY","  "+Object.entries(W).filter(function(e){return!wp.some(function(p){return p.issues.some(function(x){return x.wcag===e[0]&&!x.remediated})})&&!dc.some(function(d){return d.issues.some(function(x){return x.wcag===e[0]&&!x.remediated})})}).length+" of "+Object.keys(W).length+" criteria: Supports","  Compliance Score: "+overallScore+"%","","="
.repeat(60),"","WCAG 2.1 LEVEL A & AA CONFORMANCE TABLE","",
              String("Criterion").padEnd(12)+String("Name").padEnd(35)+String("Level").padEnd(7)+String("Conformance").padEnd(28)+"Remarks","_".repeat(120)];
            Object.entries(W).forEach(function(entry){
              var id=entry[0],w=entry[1];
              var webFail=wp.some(function(p){return p.issues.some(function(x){return x.wcag===id&&!x.remediated})});
              var docFail=dc.some(function(d){return d.issues.some(function(x){return x.wcag===id&&!x.remediated})});
              var wasFixed=wp.some(function(p){return p.issues.some(function(x){return x.wcag===id&&x.remediated})})||dc.some(function(d){return d.issues.some(function(x){return x.wcag===id&&x.remediated})});
              var status=!webFail&&!docFail?(wasFixed?"Supports (Remediated)":"Supports"):"Does Not Support";
              var remarks=webFail||docFail?w.r:"Meets requirements.";
              lines.push(id.padEnd(12)+w.n.substring(0,33).padEnd(35)+w.l.padEnd(7)+status.padEnd(28)+remarks.substring(0,80));
            });
            lines.push("","_".repeat(120),"","DISCLAIMER","This VPAT is generated from automated accessibility testing results. Automated","tools detect approximately 30-40% of WCAG criteria. Manual testing is recommended","for complete conformance verification.","","\u00A9 "+new Date().getFullYear()+" Pi Consultants \u2014 pi-consulting.us \u2014 PiARIS\u2122");
            var b=new Blob([lines.join("\n")],{type:"text/plain"});
            var a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="VPAT-"+(domain||"site").replace(/[^a-z0-9]/gi,"-")+"-"+new Date().toISOString().split("T")[0]+".txt";a.click();
            showToast("\u{1F4DC} VPAT downloaded");
          },style:{padding:"10px 20px",borderRadius:8,border:"1.5px solid "+P.pri+"40",background:P.priLt,color:P.pri,fontSize:12,fontWeight:700}},"\u{2B07} Download VPAT"):null))),
    vpat?E(Card,null,
      E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:11}},
        E("thead",null,E("tr",null,["WCAG Criteria","Level","Conformance","Remarks"].map(function(h){return E("th",{key:h,style:{padding:"8px 10px",textAlign:"left",borderBottom:"2px solid "+P.bdr,fontWeight:700,fontSize:10,color:P.slateL,textTransform:"uppercase"}},h)}))),
        E("tbody",null,
          Object.entries(W).map(function(entry,i){
            var id=entry[0],w=entry[1];
            var webFail=wp.some(function(p){return p.issues.some(function(x){return x.wcag===id&&!x.remediated})});
            var docFail=dc.some(function(d){return d.issues.some(function(x){return x.wcag===id&&!x.remediated})});
            var wasFixed=wp.some(function(p){return p.issues.some(function(x){return x.wcag===id&&x.remediated})})||dc.some(function(d){return d.issues.some(function(x){return x.wcag===id&&x.remediated})});
            var status=!webFail&&!docFail?(wasFixed?"Supports (Remediated)":"Supports"):"Does Not Support";
            var col=status==="Supports"?P.emerald:status.indexOf("remediated")>-1?P.amber:P.sCrit;
            return E("tr",{key:id,style:{background:i%2?P.bgS:P.card}},
              E("td",{style:{padding:"6px 10px",borderBottom:"1px solid "+P.bdr}},E("strong",null,id)," "+w.n),
              E("td",{style:{padding:"6px 10px",borderBottom:"1px solid "+P.bdr}},E("span",{style:{padding:"1px 6px",borderRadius:4,background:w.l==="AA"?P.priLt:P.accentLt,fontSize:10,fontWeight:700}},w.l)),
              E("td",{style:{padding:"6px 10px",borderBottom:"1px solid "+P.bdr,color:col,fontWeight:700}},status),
              E("td",{style:{padding:"6px 10px",borderBottom:"1px solid "+P.bdr,fontSize:10,color:P.slateM}},webFail||docFail?w.r:"Meets requirements."));
          }))))
    :E(Card,{sx:{textAlign:"center",padding:40}},E("div",{style:{fontSize:36,marginBottom:8}},"\u{1F4DC}"),E("p",{style:{color:P.slateL,fontSize:13}},"Click Generate VPAT above. Remediate first for best results. Score: "+overallScore+"%"))
  );

  /* PAC 2026 */
  var pacPage = E("div",null,
    E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 14px"}},"\u2699\uFE0F PAC 2026 Integration"),
    E("div",{className:"rg2",style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}},
      E(Card,{sx:{borderTop:"3px solid "+P.pri}},
        E("h3",{style:{fontSize:14,fontWeight:700,margin:"0 0 8px"}},"PAC 2026 \u2014 AI-Powered PDF Validation"),
        E("ul",{style:{margin:0,paddingLeft:16,fontSize:12,color:P.slate,lineHeight:2}},
          E("li",null,E("strong",null,"AI Semantic Analysis")," \u2014 Validates heading/table semantics via ML"),
          E("li",null,E("strong",null,"PDF/UA-1 & PDF/UA-2")," \u2014 Full ISO 14289"),
          E("li",null,E("strong",null,"WCAG 2.1 AA")," \u2014 All machine-testable criteria"),
          E("li",null,E("strong",null,"Screen Reader Preview")," \u2014 Visual AT simulation"),
          E("li",null,E("strong",null,"Free")," \u2014 pac.pdf-accessibility.org"))),
      E(Card,{sx:{borderTop:"3px solid "+P.accent}},
        E("h3",{style:{fontSize:14,fontWeight:700,margin:"0 0 8px"}},"ARIS + PAC Workflow"),
        E("ol",{style:{margin:0,paddingLeft:16,fontSize:12,color:P.slate,lineHeight:2}},
          E("li",null,"ARIS discovers all PDFs on your domain"),
          E("li",null,"Batch-export PDF list for PAC processing"),
          E("li",null,"PAC validates structural compliance with AI"),
          E("li",null,"Import PAC results into ARIS dashboard"),
          E("li",null,"Unified remediation across both toolsets")),
        E("pre",{style:{margin:"10px 0 0",padding:10,borderRadius:6,background:P.bgS,fontSize:10.5,fontFamily:FC,whiteSpace:"pre-wrap",border:"1.5px solid "+P.bdr}},"# PAC 2026 batch validation\nfor %%f in (*.pdf) do PAC.exe \"%%f\" --wcag21 --json"))),
    E(Card,null,
      E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:11}},
        E("thead",null,E("tr",null,["Capability","PiARIS","PAC 2026"].map(function(h){return E("th",{key:h,style:{padding:"7px 10px",textAlign:"left",borderBottom:"2px solid "+P.bdr,fontWeight:700,fontSize:10,textTransform:"uppercase",color:P.slateL}},h)}))),
        E("tbody",null,
          [["WCAG criteria per doc","\u2713","\u2713"],["Issue count + severity","\u2713","\u2713"],["Remediation steps","\u2713","\u2717"],["Web scanning","\u2713","\u2717"],["Auto-remediation","\u2713","\u2717"],["VPAT generation","\u2713","\u2717"],["Continuous monitoring","\u2713","\u2717"],["AI semantic checks","\u2713","\u2713"],["AI alt text generation","\u2713","\u2717"],["AI code remediation","\u2713","\u2717"],["PDF Quick-Check","\u2713","\u2713"],["Free","\u2713","\u2713"]].map(function(r,i){
            return E("tr",{key:i,style:{background:i%2?P.bgS:P.card}},
              r.map(function(c,j){return E("td",{key:j,style:{padding:"6px 10px",borderBottom:"1px solid "+P.bdr,color:c==="\u2713"?P.emerald:c.indexOf("\u2717")>-1?P.sCrit:P.slate,fontWeight:j===0?600:500}},c)}));
          }))))
  );

  /* ═══ PA11Y LAB ═══ */
  /* PA11Y LAB — MULTI-ENGINE ACCESSIBILITY TESTING BENCH */
  var engBadge=function(name,color){return E("span",{style:{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:6,background:color+"18",border:"1px solid "+color+"30",fontSize:10,fontWeight:700,color:color}},"\u2713",name)};
  var engBtn=function(label,color,onClick,running,done){return E("button",{onClick:onClick,disabled:running||!aiHtml.trim(),style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+(done?color+"40":P.bdr),background:running?"#eee":done?"linear-gradient(135deg,"+color+"15,"+color+"08)":P.card,color:running?"#999":P.slate,fontSize:11,fontWeight:700,cursor:running||!aiHtml.trim()?"wait":"pointer",display:"flex",alignItems:"center",gap:6}},running?"\u23F3 Running...":done?"\u2713 "+label:"\u25B6 "+label)};

  /** Render issue list for PA11Y Lab — shows issues from a specific engine (axe, HTMLCS, AI) */
  /* PR-6.1 — per-image alt-text triage (web audit). For each page image flagged
     under WCAG 1.1.1, the user marks it Decorative, writes alt text, or gets an
     AI suggestion — which sends ONLY that one image to /api/image-alt (server
     fetches it SSRF-safe, then vision). Decisions live in altDecisions[src]. */
  function triBtn(label,active,onClick){
    return E("button",{onClick:onClick,style:{padding:"4px 10px",borderRadius:6,border:"1.5px solid "+(active?P.pri:P.bdr),background:active?P.priLt:P.card,color:active?P.pri:P.slateM,fontSize:10,fontWeight:700,cursor:"pointer"}},label);
  }
  function webAltCandidates(){
    var out={},base=labUrl||"";
    function scan(issues){(issues||[]).forEach(function(i){
      var w=i.wcag||"",txt=((i.desc||i.description||"")+" "+(i.fix||"")).toLowerCase();
      if(w.indexOf("1.1.1")<0 && !/missing alt|alt text|alternative text|image.*alt/.test(txt))return;
      var html=i.el||i.element||"";
      var m=/<img[^>]*\bsrc=["']([^"']+)["']/i.exec(html);
      if(!m)return;
      var abs;try{abs=base?new URL(m[1],base).href:m[1];}catch(e){abs=m[1];}
      if(!/^https?:/i.test(abs))return;
      if(!out[abs])out[abs]={src:abs,raw:m[1],context:(i.desc||i.description||"").slice(0,160)};
    });}
    scan(labAxeRes&&labAxeRes.issues);
    scan(labHtmlcsRes&&labHtmlcsRes.issues);
    scan(aiRes&&aiRes.issues&&aiRes.issues.map(function(x){return{wcag:x.wcag,el:x.element,description:x.description};}));
    return Object.keys(out).map(function(k){return out[k];});
  }
  function setAltDecorative(src){setAltDecisions(function(p){var n=Object.assign({},p);n[src]={state:"decorative",alt:""};return n;});}
  function setAltManual(src,text){setAltDecisions(function(p){var n=Object.assign({},p);n[src]={state:"manual",alt:text};return n;});}
  async function runWebAltAi(src,context){
    setAltBusy(src);
    try{
      var r=await fetch("/api/image-alt",{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify({url:src,contextText:context||""})});
      var d={};try{d=await r.json();}catch(e){}
      setAltDecisions(function(p){var n=Object.assign({},p);
        n[src]=r.ok?{state:"ai",alt:d.alt||"",longDesc:d.longDesc||"",isDecorative:!!d.isDecorative,confidence:d.confidence||""}
                   :{state:"ai",error:(d&&d.error)?d.error:("HTTP "+r.status)};
        return n;});
    }catch(e){setAltDecisions(function(p){var n=Object.assign({},p);n[src]={state:"ai",error:e.message};return n;});}
    finally{setAltBusy(null);}
  }
  function renderWebAltTriage(){
    var cands=webAltCandidates();
    if(!cands.length)return null;
    return E("div",{"data-testid":"web-alt-triage",style:{marginTop:16,padding:14,borderRadius:10,border:"1.5px solid "+P.bdr,background:P.bgS}},
      E("div",{style:{fontSize:12,fontWeight:800,color:P.slate,marginBottom:2}},"\u{1F5BC} Alt Text Triage — "+cands.length+" image"+(cands.length!==1?"s":"")+" need a decision"),
      E("div",{style:{fontSize:10,color:P.slateL,marginBottom:10}},"Per image: mark Decorative, write alt text, or get an AI suggestion (sends only that one image)."),
      cands.map(function(c,ci){
        var d=altDecisions[c.src]||{state:"pending"};
        return E("div",{key:"alt-"+ci,style:{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 0",borderTop:ci?"1px solid "+P.bdr:"none"}},
          E("img",{src:c.src,alt:"",style:{width:48,height:48,objectFit:"contain",border:"1px solid "+P.bdr,borderRadius:4,background:"#fff",flexShrink:0}}),
          E("div",{style:{flex:1,minWidth:0}},
            E("div",{style:{fontSize:10,color:P.slateL,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:6}},c.raw),
            E("div",{style:{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}},
              triBtn("Decorative",d.state==="decorative",function(){setAltDecorative(c.src);}),
              triBtn("Write",d.state==="manual",function(){setAltManual(c.src,d.alt||"");}),
              E("button",{onClick:function(){runWebAltAi(c.src,c.context);},disabled:altBusy===c.src,style:{padding:"4px 10px",borderRadius:6,border:"none",background:altBusy===c.src?"#ccc":"linear-gradient(135deg,#8B5CF6,#EC4899)",color:"#fff",fontSize:10,fontWeight:700,cursor:altBusy===c.src?"wait":"pointer"}},altBusy===c.src?"✨ …":"✨ AI alt")),
            d.error?E("div",{style:{fontSize:10,color:P.sCrit}},"AI error: "+d.error):
            d.state==="decorative"?E("div",{style:{fontSize:10,color:P.slateM}},"Marked decorative — alt=\"\" aria-hidden"):
            d.state==="manual"?E("input",{value:d.alt||"",placeholder:"Type alt text…",onChange:function(e){setAltManual(c.src,e.target.value);},style:{width:"100%",fontSize:11,padding:"4px 6px",border:"1px solid "+P.bdr,borderRadius:4,boxSizing:"border-box"}}):
            (d.state==="ai"&&d.alt!=null)?E("div",null,
              E("div",{style:{fontSize:11,fontWeight:600,color:P.slate}},"“"+d.alt+"”"+(d.confidence?" ("+d.confidence+")":"")),
              d.isDecorative?E("div",{style:{fontSize:10,color:P.amber}},"AI suggests this may be decorative"):null):
            null));
      }));
  }

  /* PR-6.5 — document image alt-text triage. Doc images have no URL, but the
     client already renders every page (labPdfPages). To get AI alt text we crop
     the image's bbox region out of the rendered page and POST the bytes to
     /api/image-alt (no server PDF-fetch, no SSRF, no BLOB). Decisions reuse
     altDecisions, keyed per doc-image. */
  function cropDocImage(page,bbox){
    return new Promise(function(resolve,reject){
      var pg=labPdfPages[page];
      if(!pg||!pg.url||!bbox){reject(new Error("page not rendered"));return;}
      var im=new Image();
      im.onload=function(){
        var scale=im.naturalWidth/((pg.ptW)||im.naturalWidth);
        var sw=Math.max(1,Math.round(bbox.width*scale)),sh=Math.max(1,Math.round(bbox.height*scale));
        var sx=Math.max(0,Math.round(bbox.x*scale));
        var sy=Math.max(0,Math.round(((pg.ptH||0)-(bbox.y+bbox.height))*scale));
        try{var c=document.createElement("canvas");c.width=sw;c.height=sh;
          c.getContext("2d").drawImage(im,sx,sy,sw,sh,0,0,sw,sh);
          resolve(c.toDataURL("image/png").split(",")[1]);
        }catch(e){reject(e);}
      };
      im.onerror=function(){reject(new Error("page image load failed"));};
      im.src=pg.url;
    });
  }
  function docImageCandidates(){
    if(!pdfStructRes||!Array.isArray(pdfStructRes.issues))return [];
    var k=0;
    return pdfStructRes.issues.filter(function(i){return (i.cat||"")==="Images"||(i.wcag||"")==="1.1.1";})
      .map(function(i){var inst=i.instance||{};k++;
        var page=inst.page!=null?inst.page:(i.page!=null?i.page:null);
        return {key:"docimg-"+k,page:page,bbox:inst.bbox||null,
          label:(inst.elementType||"Image")+(page!=null?" · p."+page:""),context:(i.desc||"").slice(0,140)};});
  }
  async function runDocAltAi(cand){
    if(!cand.bbox)return;
    setAltBusy(cand.key);
    try{
      var b64=await cropDocImage(cand.page,cand.bbox);
      var r=await fetch("/api/image-alt",{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify({imageBase64:b64,contentType:"image/png",contextText:cand.context})});
      var d={};try{d=await r.json();}catch(e){}
      setAltDecisions(function(p){var n=Object.assign({},p);
        n[cand.key]=r.ok?{state:"ai",alt:d.alt||"",longDesc:d.longDesc||"",isDecorative:!!d.isDecorative,confidence:d.confidence||""}:{state:"ai",error:(d&&d.error)?d.error:("HTTP "+r.status)};
        return n;});
    }catch(e){setAltDecisions(function(p){var n=Object.assign({},p);n[cand.key]={state:"ai",error:e.message};return n;});}
    finally{setAltBusy(null);}
  }
  function renderDocImageTriage(){
    var cands=docImageCandidates();
    if(!cands.length)return null;
    return E("div",{"data-testid":"doc-alt-triage",style:{marginBottom:14}},
      E("div",{style:{fontSize:11,fontWeight:800,color:P.slate,marginBottom:6}},"🖼 Images — alt text ("+cands.length+")"),
      cands.map(function(c,ci){
        var d=altDecisions[c.key]||{state:"pending"};
        return E("div",{key:c.key,style:{display:"flex",gap:10,alignItems:"flex-start",padding:"6px 0",borderTop:ci?"1px solid "+P.bdr:"none"}},
          E("div",{style:{flex:1,minWidth:0}},
            E("div",{style:{fontSize:10,color:P.slateM,marginBottom:4}},c.label+(c.bbox?"":" — location not resolved")),
            E("div",{style:{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}},
              triBtn("Decorative",d.state==="decorative",function(){setAltDecorative(c.key);}),
              triBtn("Write",d.state==="manual",function(){setAltManual(c.key,d.alt||"");}),
              c.bbox?E("button",{onClick:function(){runDocAltAi(c);},disabled:altBusy===c.key,style:{padding:"4px 10px",borderRadius:6,border:"none",background:altBusy===c.key?"#ccc":"linear-gradient(135deg,#8B5CF6,#EC4899)",color:"#fff",fontSize:10,fontWeight:700,cursor:altBusy===c.key?"wait":"pointer"}},altBusy===c.key?"✨ …":"✨ AI alt"):null),
            d.error?E("div",{style:{fontSize:10,color:P.sCrit}},"AI error: "+d.error):
            d.state==="decorative"?E("div",{style:{fontSize:10,color:P.slateM}},"Decorative — alt=\"\" aria-hidden"):
            d.state==="manual"?E("input",{value:d.alt||"",placeholder:"Type alt text…",onChange:function(e){setAltManual(c.key,e.target.value);},style:{width:"100%",fontSize:11,padding:"4px 6px",border:"1px solid "+P.bdr,borderRadius:4,boxSizing:"border-box"}}):
            (d.state==="ai"&&d.alt!=null)?E("div",{style:{fontSize:11,fontWeight:600,color:P.slate}},"“"+d.alt+"”"+(d.confidence?" ("+d.confidence+")":"")+(d.isDecorative?" — may be decorative":"")):
            null));
      }));
  }
  function renderDocStructureGuidance(){
    if(!pdfStructRes||!pdfStructRes.checks)return null;
    var c=pdfStructRes.checks,items=[];
    if(!c.tagged)items.push("Tag the document (Acrobat: Accessibility → Add Tags) so screen readers get reading order.");
    if(!c.structTree)items.push("No structure tree — run Acrobat's Make Accessible wizard to build one.");
    if(!c.headings)items.push("Add heading tags (H1–H6) for navigable structure.");
    if(!c.bookmarks&&(c.pageCount||0)>4)items.push("Add bookmarks for the main sections ("+c.pageCount+" pages).");
    if(!items.length)return null;
    return E("div",{style:{marginBottom:14}},
      E("div",{style:{fontSize:11,fontWeight:800,color:P.slate,marginBottom:6}},"🏗 Structure — needs document tagging"),
      E("div",{style:{fontSize:10,color:P.slateL,marginBottom:6}},"These can't be auto-fixed here (they need tag-tree changes). What to do:"),
      E("ul",{style:{margin:0,paddingLeft:18,fontSize:11,color:P.slateM,lineHeight:1.6}},items.map(function(t,i){return E("li",{key:"st-"+i},t);})));
  }

  /* PR-J: shared source-PDF remediation pair — Adobe Auto-Tag + alt
     injection. Extracted from runFullRemediation so the Watchtower
     Doc Audit handlers can reuse the same Adobe-backed pipeline
     (instead of the DocMaya HTML-conversion path they used to call).

     Inputs:
       pdfBase64  — the (already metadata-baked, if applicable) PDF
       alts       — ordered alt-decision list, same shape pdf-inject-alts
                    accepts: [{state, alt, page?}]. Pass [] for tag-only.
     Returns:
       { taggedPdfBase64, remediatedPdfBase64, figureCoverage,
         figuresFound, injected, skipped, perPage, figuresWithPage,
         pageAware }
     Throws on any failure. The thrown Error carries:
       err.kind            — "autotag" | "inject"
       err.status          — HTTP status (or 0)
       err.upgrade         — preset id when status===402 (credit gate)
       err.serverMessage   — the server's error message verbatim
     so callers can render a 402 upgrade prompt vs a generic failure. */
  async function runAutotagPlusInject(pdfBase64, alts){
    if(!pdfBase64) throw Object.assign(new Error("pdfBase64 is required"),{kind:"autotag",status:0});
    var altsArr=Array.isArray(alts)?alts:[];

    /* Step 1: Adobe Auto-Tag — preserves the existing credit-gate
       error shape so a 402 surfaces the upgrade hint, not a stack. */
    var r1, tagResp;
    try{
      r1=await fetch("/api/pdf-autotag",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pdfBase64:pdfBase64})});
      tagResp=await r1.json().catch(function(){return null;});
    }catch(e){
      throw Object.assign(new Error(errText(e)),{kind:"autotag",status:0});
    }
    if(r1.status===402||r1.status===403){
      var upMsg=(tagResp&&tagResp.error)||"Tagged-PDF credits required.";
      throw Object.assign(new Error(upMsg),{kind:"autotag",status:r1.status,upgrade:(tagResp&&tagResp.upgrade)||null,serverMessage:upMsg});
    }
    if(!r1.ok){
      var aErr=(tagResp&&tagResp.error)||("HTTP "+r1.status);
      throw Object.assign(new Error(aErr),{kind:"autotag",status:r1.status,serverMessage:aErr});
    }
    var taggedPdfBase64=tagResp&&tagResp.taggedPdfBase64;
    if(!taggedPdfBase64) throw Object.assign(new Error("autotag returned no PDF"),{kind:"autotag",status:r1.status});

    /* Step 2: inject alts into the tagged figures. */
    var r2, injectResp;
    try{
      r2=await fetch("/api/pdf-inject-alts",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pdfBase64:taggedPdfBase64,alts:altsArr})});
      injectResp=await r2.json().catch(function(){return null;});
    }catch(e){
      throw Object.assign(new Error(errText(e)),{kind:"inject",status:0});
    }
    if(!r2.ok){
      var iErr=(injectResp&&injectResp.error)||("HTTP "+r2.status);
      throw Object.assign(new Error(iErr),{kind:"inject",status:r2.status,serverMessage:iErr});
    }

    var figuresFound=injectResp&&typeof injectResp.figuresFound==="number"?injectResp.figuresFound:0;
    var injected=injectResp&&typeof injectResp.injected==="number"?injectResp.injected:0;
    var figureCoverage=figuresFound>0?(injected/figuresFound):0;

    return {
      taggedPdfBase64:taggedPdfBase64,
      remediatedPdfBase64:injectResp&&injectResp.pdfBase64,
      figureCoverage:figureCoverage,
      figuresFound:figuresFound,
      injected:injected,
      skipped:injectResp&&injectResp.skipped,
      perPage:(injectResp&&injectResp.perPage)||null,
      figuresWithPage:injectResp&&injectResp.figuresWithPage,
      pageAware:!!(injectResp&&injectResp.pageAware)
    };
  }

  /* PR-6.6c: the one-button full remediation pipeline. Runs:
       1. bake metadata via pdf-lib (client-side, same as labRemediatePdf)
       2. POST tagged-PDF flow to /api/pdf-autotag (Adobe Auto-Tag)
       3. POST tagged PDF + altDecisions to /api/pdf-inject-alts
       4. re-audit the output client-side via auditPdfBase64
       5. trigger download of the final remediated PDF
       6. surface before/after diff via diffAudits

     Each step posts a progress entry so the inline panel renders the
     pipeline as it runs. On any error the pipeline stops and the
     failed step's reason becomes the visible message.

     PR-J: steps 2+3 now delegate to the shared runAutotagPlusInject
     helper. Behavior identical — this function still renders progress
     entries and re-audits, the helper just consolidates the fetch pair. */

  /* runUnifiedRemediation — the one-call individual path. Bakes
     metadata client-side, then POSTs /api/pdf-remediate which runs the
     whole server pipeline (OCR → Adobe tag → headings → reading-order →
     /Lang → AUTO alt-gen, human-wins) and re-audits with the REAL
     engine. The Before/After card is built from the SERVER audit
     (truth), not the client regex — so the panel matches the payload.
     Workbench decisions ride along as priorDecisions: human alt is
     preserved, automation fills the rest. */
  async function runUnifiedRemediation(){
    if(!aiPdf||!aiPdf.data){setRemPipeline({step:"error",progress:[],error:"load a PDF first"});return;}
    setRemResult(null);
    var progress=[];
    function push(label,state,detail){progress=progress.concat([{label:label,state:state,detail:detail||""}]);setRemPipeline({step:label,progress:progress.slice()});}
    var currentB64=aiPdf.data;
    var name=(aiPdf.name||"document").replace(/\.pdf$/i,"");

    /* Step 1: bake form metadata client-side (the server pipeline does
       not apply the Title/Lang/Author form). */
    push("metadata","loading");
    try{
      if(!window.PDFLib){await loadPdfLib();}
      var PDFLib=window.PDFLib;var mbytes=Uint8Array.from(atob(currentB64),function(c){return c.charCodeAt(0)});
      var mdoc=await PDFLib.PDFDocument.load(mbytes,{ignoreEncryption:true});
      var meta=pdfRemForm||{};
      if(meta.title)mdoc.setTitle(meta.title,{showInWindowTitleBar:true});
      if(meta.lang)mdoc.setLanguage(meta.lang);
      if(meta.author)mdoc.setAuthor(meta.author);
      if(meta.subject)mdoc.setSubject(meta.subject);
      mdoc.setProducer("PiARIS PA11Y Lab - pi-consulting.us");mdoc.setModificationDate(new Date());
      currentB64=await mdoc.saveAsBase64({dataUri:false,useObjectStreams:false});
      progress[progress.length-1]={label:"metadata",state:"done",detail:"baked title, lang, author"};
    }catch(e){progress[progress.length-1]={label:"metadata",state:"error",detail:errText(e)};setRemPipeline({step:"error",progress:progress.slice(),error:"metadata: "+errText(e)});return;}

    /* Workbench (human) decisions → priorDecisions. Server preserves
       'manual'/'decorative' verbatim and auto-gens the rest. */
    var imgs=docImageCandidates();
    var priorDecisions=imgs.map(function(im){var d=altDecisions[im.key]||{state:"pending"};return {state:d.state,alt:d.alt||"",page:im.page,source:(d.state==="manual"||d.state==="decorative")?"manual":(d.state==="ai"?"ai":null)};});

    /* Step 2: ASYNC remediation — enqueue + poll + download.
       A heavy doc (e.g. 102 figures) runs ~94s server-side, which the SWA
       gateway kills on a synchronous proxy ("Backend call failure") even
       though the linked Function App finishes 200. So POST the bytes to
       /api/pdf-remediate-async (fast: stashes them in blob + enqueues a
       one-doc job), then poll the read-only status and download the
       result from blob — the same machinery bulk uses. No long
       synchronous request to time out. */
    push("remediate","loading","queued — remediating all figures in the background (≈1–2 min)…");
    var sresp,sdata;
    try{
      sresp=await fetch("/api/pdf-remediate-async",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({pdfBase64:currentB64,priorDecisions:priorDecisions})});
      sdata=await sresp.json();
    }catch(e){progress[progress.length-1]={label:"remediate",state:"error",detail:errText(e)};setRemPipeline({step:"error",progress:progress.slice(),error:"remediate: "+errText(e)});return;}
    if(sresp.status===402||sresp.status===403){
      progress[progress.length-1]={label:"remediate",state:"error",detail:(sdata&&sdata.error)||"credits required"};
      setRemPipeline({step:"error",progress:progress.slice(),error:"upgrade_needed",upgradeMsg:(sdata&&sdata.error)||"Remediation requires credits.",upgrade:(sdata&&sdata.upgrade)||null});
      return;
    }
    if(!sresp.ok||!sdata||!sdata.jobId){
      var em=(sdata&&sdata.error)||("HTTP "+sresp.status);
      progress[progress.length-1]={label:"remediate",state:"error",detail:em};
      setRemPipeline({step:"error",progress:progress.slice(),error:"remediate: "+em});
      return;
    }
    /* poll the read-only status until the background worker finishes this
       one doc (worker timer ~30s pickup + ~1–2 min processing). */
    var jobId=sdata.jobId,report=null,pollErr=null;
    for(var attempt=0;attempt<240;attempt++){
      await new Promise(function(r){setTimeout(r,3000);});
      var pr,pd;
      try{pr=await fetch("/api/pdf-remediate-batch-status?id="+encodeURIComponent(jobId),{method:"POST",credentials:"include"});pd=await pr.json();}
      catch(e){continue;/* transient network — keep polling */}
      if(!pr.ok){pollErr=(pd&&pd.error)||("HTTP "+pr.status);break;}
      var res0=(pd.results&&pd.results[0])||null;
      progress[progress.length-1]={label:"remediate",state:"loading",detail:(res0?"remediating — generating alt text…":"queued — waiting for the worker…")};
      setRemPipeline({step:"remediate",progress:progress.slice()});
      if(pd.done){report=res0;break;}
    }
    if(pollErr){progress[progress.length-1]={label:"remediate",state:"error",detail:pollErr};setRemPipeline({step:"error",progress:progress.slice(),error:"remediate: "+pollErr});return;}
    if(!report){progress[progress.length-1]={label:"remediate",state:"error",detail:"timed out waiting for the background worker"};setRemPipeline({step:"error",progress:progress.slice(),error:"remediate: timed out waiting for the background worker"});return;}
    if(report.ok===false){var rerr=report.error||"remediation failed";progress[progress.length-1]={label:"remediate",state:"error",detail:rerr};setRemPipeline({step:"error",progress:progress.slice(),error:"remediate: "+rerr});return;}
    /* fetch the finished PDF from blob */
    var fr,fd;
    try{fr=await fetch("/api/pdf-remediate-batch-result?id="+encodeURIComponent(jobId)+"&docIndex=0",{credentials:"include"});fd=await fr.json();}
    catch(e){progress[progress.length-1]={label:"remediate",state:"error",detail:errText(e)};setRemPipeline({step:"error",progress:progress.slice(),error:"download: "+errText(e)});return;}
    if(!fr.ok||!fd.pdfBase64){var ferr=(fd&&fd.error)||("HTTP "+fr.status);progress[progress.length-1]={label:"remediate",state:"error",detail:ferr};setRemPipeline({step:"error",progress:progress.slice(),error:"download: "+ferr});return;}
    var ag=report.altAutogen||{};var inj=report.altInjection||{};
    var detailParts=[];
    if(report.headingRemediation&&report.headingRemediation.rePromoted&&report.headingRemediation.rePromoted.length)detailParts.push(report.headingRemediation.rePromoted.length+" heading(s) restored");
    if(ag.generated)detailParts.push(ag.generated+" alt generated");
    if(ag.kept)detailParts.push(ag.kept+" human alt kept");
    progress[progress.length-1]={label:"remediate",state:"done",detail:detailParts.length?detailParts.join(", "):"remediated"};
    setRemPipeline({step:"done",progress:progress.slice()});

    /* Reflect the server's per-figure alt back into the workbench so each
       figure shows its AI/manual/decorative decision instead of looking
       "unrun" (the report is figure-ordered, same as docImageCandidates).
       Adobe may consolidate figures (102→101), so map by index up to the
       shorter length; leftover figures stay as the user left them. */
    if(report.altDecisions&&report.altDecisions.length){
      setAltDecisions(function(p){var n=Object.assign({},p);
        for(var di=0;di<imgs.length&&di<report.altDecisions.length;di++){var dd=report.altDecisions[di];if(!dd)continue;
          if(dd.state==="decorative")n[imgs[di].key]={state:"decorative",alt:"",source:dd.source||"ai"};
          else if(dd.alt)n[imgs[di].key]={state:(dd.source==="manual"?"manual":"ai"),alt:dd.alt,source:dd.source||"ai"};}
        return n;});
    }

    /* Before/After from the job's slim report (server canonical summaries). */
    function snap(s){s=s||{};var cr=s.critical||0,mj=s.major||0,mn=s.minor||0;var sc=100-cr*25-mj*10-mn*3;if(sc<0)sc=0;return {summary:{total:s.total||0,critical:cr,major:mj,minor:mn,remediable:0},score:sc,issues:[]};}
    var before=snap(report.beforeSummary),after=snap(report.afterSummary);
    var dlt=before.summary.total-after.summary.total;
    var summaryStr=dlt>0?("Reduced "+before.summary.total+" issues to "+after.summary.total+"."):(dlt<0?("Tagging surfaced issues: "+before.summary.total+" → "+after.summary.total+"."):("No net change ("+after.summary.total+" issues)."));
    if(report.warning)summaryStr+=" "+report.warning;
    var diff={summary:summaryStr,counts:{before:before.summary,after:after.summary,fixed:Math.max(0,dlt),remaining:after.summary.total,new:Math.max(0,-dlt)},scoreDelta:after.score-before.score};

    /* download */
    var raw=atob(fd.pdfBase64);var arr=new Uint8Array(raw.length);for(var i=0;i<raw.length;i++)arr[i]=raw.charCodeAt(i);
    var blob=new Blob([arr],{type:"application/pdf"});var url=URL.createObjectURL(blob);
    var dl=document.createElement("a");dl.href=url;dl.download=name+"-remediated.pdf";document.body.appendChild(dl);dl.click();document.body.removeChild(dl);URL.revokeObjectURL(url);

    setRemResult({
      before:before,after:after,diff:diff,
      finalBase64:fd.pdfBase64,finalBytes:arr.length,finalName:name+"-remediated.pdf",
      injectedCount:(inj.injected!=null?inj.injected:(ag.generated||0)),
      figuresFound:(inj.figuresFound!=null?inj.figuresFound:(ag.imagesFound||0)),
      injectSkipped:inj.skipped||0,perPage:null,pageAware:false,figuresWithPage:null,
      workbenchFigureCount:imgs.length,
      altAutogen:ag,complete:report.complete,altTextNeeded:report.altTextNeeded,warning:report.warning
    });
  }

  /* ── Bulk remediation modal handlers ──────────────────────────────
     openBulkRem fetches the preflight list; the user excludes any docs
     (X); startBulkRemediation kicks off the job; pollBulk drives it one
     doc per status call. Human alt is preserved server-side (the
     preflight's 'review' recommendation flags docs with hand edits). */
  async function openBulkRem(sessionId){
    if(!sessionId){showToast("No scan session for bulk remediation");return;}
    setBulkRem({sessionId:sessionId,loading:true,error:null,docs:[],excluded:{},phase:"select"});
    try{
      var r=await fetch("/api/pdf-remediate-batch-preflight?sessionId="+encodeURIComponent(sessionId),{credentials:"include"});
      var d=await r.json();
      if(!r.ok){setBulkRem(function(p){return p?Object.assign({},p,{loading:false,error:(d&&d.error)||("HTTP "+r.status)}):p});return;}
      /* pre-exclude docs the engine recommends skipping (fully-AI +
         complete) so the default selection is the useful work. */
      var ex={};(d.docs||[]).forEach(function(doc){if(doc.recommend==="skip")ex[doc.docIndex]=true;});
      setBulkRem(function(p){return p?Object.assign({},p,{loading:false,docs:d.docs||[],excluded:ex,policy:d.policy}):p});
    }catch(e){setBulkRem(function(p){return p?Object.assign({},p,{loading:false,error:errText(e)}):p});}
  }
  function toggleBulkExclude(docIndex){
    setBulkRem(function(p){if(!p)return p;var ex=Object.assign({},p.excluded);if(ex[docIndex])delete ex[docIndex];else ex[docIndex]=true;return Object.assign({},p,{excluded:ex});});
  }
  async function startBulkRemediation(){
    var bm=bulkRem;if(!bm)return;
    var selected=(bm.docs||[]).filter(function(d){return !bm.excluded[d.docIndex];}).map(function(d){return d.docIndex;});
    if(selected.length===0){setBulkRem(function(p){return p?Object.assign({},p,{error:"Select at least one document (uncheck the X)."}):p});return;}
    setBulkRem(function(p){return p?Object.assign({},p,{phase:"running",error:null,processed:0,total:selected.length,results:[]}):p});
    var sr,sd;
    try{
      sr=await fetch("/api/pdf-remediate-batch",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({sessionId:bm.sessionId,docIndices:selected})});
      sd=await sr.json();
    }catch(e){setBulkRem(function(p){return p?Object.assign({},p,{phase:"select",error:errText(e)}):p});return;}
    if(!sr.ok||!sd.jobId){setBulkRem(function(p){return p?Object.assign({},p,{phase:"select",error:(sd&&sd.error)||("HTTP "+sr.status)}):p});return;}
    setBulkRem(function(p){return p?Object.assign({},p,{jobId:sd.jobId,total:sd.total}):p});
    pollBulk(sd.jobId);
  }
  async function pollBulk(jobId){
    try{
      var r=await fetch("/api/pdf-remediate-batch-status?id="+encodeURIComponent(jobId),{method:"POST",credentials:"include"});
      var d=await r.json();
      /* stop if the user closed the modal or switched jobs */
      var stale=false;
      setBulkRem(function(p){if(!p||p.jobId!==jobId){stale=true;return p;}
        if(!r.ok)return Object.assign({},p,{phase:"select",error:(d&&d.error)||("HTTP "+r.status)});
        return Object.assign({},p,{processed:d.processed,total:d.total,results:d.results||[],remediated:d.remediated,incomplete:d.incomplete,failed:d.failed,done:d.done,phase:d.done?"done":"running"});
      });
      /* Merge finished worker results back into the doc list so the state
         machine (and per-doc pill/cert line) reflects bulk remediation —
         results[].conformance is the same shape the sync Certify produces. */
      if(d.done&&Array.isArray(d.results)){
        setDc(function(prev){return prev.map(function(x,i){
          var res=d.results.find(function(rr){return rr.docIndex===i;});
          if(!res||!res.ok) return x;
          return Object.assign({},x,{remediated:true,jobId:jobId,conformance:res.conformance||x.conformance||null});
        })});
      }
      if(stale||!r.ok)return;
      if(!d.done)pollBulk(jobId);
    }catch(e){setBulkRem(function(p){return p?Object.assign({},p,{phase:"select",error:errText(e)}):p});}
  }
  /* Download one bulk-remediated PDF. In worker mode each finished doc
     is in blob (res.hasResult); pull it via the result endpoint. */
  async function downloadBulkResult(jobId,docIndex,name){
    try{
      var r=await fetch("/api/pdf-remediate-batch-result?id="+encodeURIComponent(jobId)+"&docIndex="+docIndex,{credentials:"include"});
      var d=await r.json();
      if(!r.ok||!d.pdfBase64){showToast((d&&d.error)||"Result not ready yet","error");return;}
      var raw=atob(d.pdfBase64);var arr=new Uint8Array(raw.length);for(var i=0;i<raw.length;i++)arr[i]=raw.charCodeAt(i);
      var blob=new Blob([arr],{type:"application/pdf"});var url=URL.createObjectURL(blob);
      var a=document.createElement("a");a.href=url;a.download=((name||("doc-"+docIndex)).replace(/\.pdf$/i,""))+"-remediated.pdf";document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
    }catch(e){showToast(errText(e),"error");}
  }

  async function runFullRemediation(){
    if(!aiPdf||!aiPdf.data){setRemPipeline({step:"error",progress:[],error:"load a PDF first"});return;}
    if(!pdfStructRes||pdfStructRes.error){setRemPipeline({step:"error",progress:[],error:"run a structural audit first (Run Audit)"});return;}
    setRemResult(null);
    /* Capture the BEFORE snapshot using auditPdfBase64 — the same
       engine we'll use on the OUTPUT in step 4. Critical for the
       diff to be apples-to-apples: pdfStructRes is server-enhanced
       (per-instance issues), so it counts issues at a finer
       granularity than the client-only audit and produces a totally
       different score. Comparing the two engines was why re-run #3
       reported "score 69 → 0" alongside an actually-improved PDF. */
    var beforeSnapshot=auditPdfBase64(aiPdf.data);
    var progress=[];
    function push(label,state,detail){progress=progress.concat([{label:label,state:state,detail:detail||""}]);setRemPipeline({step:label,progress:progress.slice()});}

    var currentB64=aiPdf.data;
    var name=(aiPdf.name||"document").replace(/\.pdf$/i,"");

    /* Step 1: bake metadata (skip silently if no form values to apply) */
    push("metadata","loading");
    try{
      if(!window.PDFLib){await loadPdfLib();}
      var PDFLib=window.PDFLib;var bytes=Uint8Array.from(atob(currentB64),function(c){return c.charCodeAt(0)});
      var pdfDoc=await PDFLib.PDFDocument.load(bytes,{ignoreEncryption:true});
      var meta=pdfRemForm||{};
      if(meta.title)pdfDoc.setTitle(meta.title,{showInWindowTitleBar:true});
      if(meta.lang)pdfDoc.setLanguage(meta.lang);
      if(meta.author)pdfDoc.setAuthor(meta.author);
      if(meta.subject)pdfDoc.setSubject(meta.subject);
      if(meta.keywords){var kw=typeof meta.keywords==="string"?meta.keywords.split(",").map(function(k){return k.trim()}):meta.keywords;pdfDoc.setKeywords(kw)}
      pdfDoc.setProducer("PiARIS PA11Y Lab - pi-consulting.us");pdfDoc.setModificationDate(new Date());
      /* saveAsBase64 — chunk-safe encode (vs spread+apply which blows
         the stack on PDFs with embedded rasters). useObjectStreams:false
         keeps metadata markers visible to the post-pipeline regex audit. */
      currentB64=await pdfDoc.saveAsBase64({dataUri:false,useObjectStreams:false});
      progress[progress.length-1]={label:"metadata",state:"done",detail:"baked title, lang, author"};
    }catch(e){progress[progress.length-1]={label:"metadata",state:"error",detail:errText(e)};setRemPipeline({step:"error",progress:progress.slice(),error:"metadata: "+progress[progress.length-1].detail});return;}

    /* Steps 2+3 (Adobe Auto-Tag + alt inject) — delegated to the
       shared runAutotagPlusInject helper. Workbench builds the page-
       aware altsOrdered exactly as before; the helper does both fetches
       and throws a typed error for 402/403 / network / non-2xx. */
    var imgs=docImageCandidates();
    /* Pass each decision's PAGE NUMBER so the server can match by page —
       Adobe Auto-Tag consolidates figures (47 detected → 25 tagged),
       so a pure order match drops decisions onto the wrong page or
       beyond the surviving set. docImageCandidates() already tracks
       page per image. */
    var altsOrdered=imgs.map(function(im){var d=altDecisions[im.key]||{state:"pending"};return {state:d.state,alt:d.alt||"",page:im.page};});

    push("autotag","loading");
    var pairResult;
    try{
      pairResult=await runAutotagPlusInject(currentB64, altsOrdered);
    }catch(e){
      if(e&&e.kind==="autotag"){
        if(e.status===402||e.status===403){
          /* Out of (or never had) tagged-PDF credits. Stop with a clear
             upgrade message rather than a generic error. */
          progress[progress.length-1]={label:"autotag",state:"error",detail:e.serverMessage||e.message};
          setRemPipeline({step:"error",progress:progress.slice(),error:"upgrade_needed",upgradeMsg:e.serverMessage||e.message,upgrade:e.upgrade||null});
          return;
        }
        progress[progress.length-1]={label:"autotag",state:"error",detail:errText(e)};
        setRemPipeline({step:"error",progress:progress.slice(),error:"autotag: "+progress[progress.length-1].detail});
        return;
      }
      /* Inject-phase failure — surface under the inject row instead. */
      push("inject","loading");
      progress[progress.length-1]={label:"inject",state:"error",detail:errText(e)};
      setRemPipeline({step:"error",progress:progress.slice(),error:"alt inject: "+progress[progress.length-1].detail});
      return;
    }
    currentB64=pairResult.remediatedPdfBase64;
    /* Forge a synthetic injectResp so the downstream audit + UI code
       (which reads injectResp.injected / .figuresFound / .perPage /
       .pageAware / .figuresWithPage / .skipped) keeps working unchanged. */
    var injectResp={
      pdfBase64:pairResult.remediatedPdfBase64,
      injected:pairResult.injected,
      figuresFound:pairResult.figuresFound,
      skipped:pairResult.skipped,
      perPage:pairResult.perPage,
      figuresWithPage:pairResult.figuresWithPage,
      pageAware:pairResult.pageAware
    };
    progress[progress.length-1]={label:"autotag",state:"done",detail:"tagged"};
    setRemPipeline({step:"autotag",progress:progress.slice()});

    push("inject","loading");
    var injectDetail="injected "+injectResp.injected+" of "+injectResp.figuresFound+" figure"+(injectResp.figuresFound===1?"":"s");
    /* Surface page-aware status so a tester sees whether per-page
       matching engaged. pageAware false on a multi-page doc means the
       figures didn't resolve a /Pg page (order-mode fallback). */
    if(injectResp.pageAware)injectDetail+=" (page-aware)";
    else if(injectResp.figuresFound>0)injectDetail+=" (order-mode: "+(injectResp.figuresWithPage||0)+"/"+injectResp.figuresFound+" figures had a page)";
    progress[progress.length-1]={label:"inject",state:"done",detail:injectDetail};
    setRemPipeline({step:"inject",progress:progress.slice()});

    /* Step 4: re-audit the output client-side for the before/after diff */
    push("audit","loading");
    var afterSnapshot=auditPdfBase64(currentB64);
    /* Suppress the regex-based "N image(s) have no alt text" critical
       when the inject step confirms every (or nearly every) Adobe-tagged
       Figure got alt. The regex auditor counts /Subtype /Image vs /Alt,
       but Adobe-tagged PDFs contain many decorative-artifact image
       XObjects that don't need alt per PDF/UA-1. The diff card already
       reports the honest figure-based coverage; the issue list now
       agrees so we don't show a misleading "1 critical remains" for
       what is actually a measurement artifact.
       Threshold: 95% — leaves a real critical visible if injection
       genuinely missed a Figure (e.g. order-mode mismatch on a
       complex doc). PR-6.6e (veraPDF) will replace this entirely
       with a proper PDF/UA-1 verdict. */
    if(injectResp&&injectResp.figuresFound>0){
      var figureCoverage=injectResp.injected/injectResp.figuresFound;
      if(figureCoverage>=0.95&&Array.isArray(afterSnapshot.issues)){
        var filtered=afterSnapshot.issues.filter(function(iss){
          return !(iss.cat==="Images"&&iss.wcag==="1.1.1");
        });
        if(filtered.length!==afterSnapshot.issues.length){
          var cr=filtered.filter(function(i){return i.sev==="critical"}).length;
          var mj=filtered.filter(function(i){return i.sev==="major"}).length;
          var mn=filtered.filter(function(i){return i.sev==="minor"}).length;
          /* Recompute score on the filtered list, same formula as
             auditPdfBase64. */
          var sc=100-cr*25-mj*10-mn*3;if(sc<0)sc=0;
          afterSnapshot=Object.assign({},afterSnapshot,{
            issues:filtered,
            summary:{total:filtered.length,critical:cr,major:mj,minor:mn,
              remediable:filtered.filter(function(i){return i.remediable}).length},
            score:sc,
            _figureAltSuppressed:true
          });
        }
      }
    }
    var diff=diffAudits(beforeSnapshot,afterSnapshot);
    progress[progress.length-1]={label:"audit",state:"done",detail:diff.summary};
    setRemPipeline({step:"done",progress:progress.slice()});

    /* Step 5: trigger download */
    var raw=atob(currentB64);var arr=new Uint8Array(raw.length);
    for(var i=0;i<raw.length;i++)arr[i]=raw.charCodeAt(i);
    var blob=new Blob([arr],{type:"application/pdf"});var url=URL.createObjectURL(blob);
    var a=document.createElement("a");a.href=url;a.download=name+"-remediated.pdf";document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);

    setRemResult({
      before:beforeSnapshot,after:afterSnapshot,diff:diff,
      finalBase64:currentB64,finalBytes:arr.length,finalName:name+"-remediated.pdf",
      injectedCount:injectResp.injected,figuresFound:injectResp.figuresFound,
      injectSkipped:injectResp.skipped,perPage:injectResp.perPage||null,
      pageAware:!!injectResp.pageAware,figuresWithPage:injectResp.figuresWithPage,
      /* Workbench saw N figures; Adobe Auto-Tag may have consolidated to
         M. Surface both so the user can see if their triage work was
         partially lost to Adobe's structuring decisions. */
      workbenchFigureCount:imgs.length
    });
  }

  /* Re-download from the already-built remediated PDF without re-running
     the pipeline (avoids burning another Adobe Auto-Tag call). Triggered
     from the diff card's persistent download button. */
  function redownloadRemediated(){
    if(!remResult||!remResult.finalBase64)return;
    var raw=atob(remResult.finalBase64);var arr=new Uint8Array(raw.length);
    for(var i=0;i<raw.length;i++)arr[i]=raw.charCodeAt(i);
    var blob=new Blob([arr],{type:"application/pdf"});var url=URL.createObjectURL(blob);
    var a=document.createElement("a");a.href=url;a.download=remResult.finalName;
    document.body.appendChild(a);a.click();document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /* PR-6.5 (a): bundle the remediation decisions into a downloadable report —
     metadata + per-image alt decisions + structure to-dos. Metadata is also baked
     into the PDF by labRemediatePdf; alt/tagging are applied in Acrobat from this
     report until deep tagging (PR-6.6). */
  function buildRemediationReport(){
    function esc(s){return String(s==null?"":s).replace(/[&<>"]/g,function(ch){return {"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;"}[ch];});}
    var meta=pdfRemForm||{},c=(pdfStructRes&&pdfStructRes.checks)||{};
    var imgs=docImageCandidates().map(function(im){
      var d=altDecisions[im.key]||{state:"pending"};
      var res=d.state==="decorative"?"Decorative (alt=\"\", aria-hidden)":(((d.state==="manual"||d.state==="ai")&&d.alt)?"Alt: "+esc(d.alt):"<em>PENDING — needs a decision</em>");
      return "<tr><td>"+esc(im.label)+"</td><td>"+res+"</td></tr>";
    }).join("");
    var struct=[];
    if(!c.tagged)struct.push("Tag the document (Acrobat: Accessibility → Add Tags).");
    if(!c.structTree)struct.push("Build a structure tree (Acrobat: Make Accessible).");
    if(!c.headings)struct.push("Add heading tags (H1–H6).");
    var name=((aiPdf&&aiPdf.name)||"document").replace(/\.pdf$/i,"");
    var html="<!doctype html><html><head><meta charset=\"utf-8\"><title>Remediation report — "+esc(name)+"</title>"
      +"<style>body{font:14px system-ui,sans-serif;margin:32px;color:#222}h1{font-size:20px}h2{font-size:15px;margin-top:24px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:13px}</style></head><body>"
      +"<h1>Accessibility remediation report</h1><p><strong>"+esc(name)+".pdf</strong> · score "+(pdfStructRes?pdfStructRes.score:"?")+"%</p>"
      +(remResult?("<h2>Remediation outcome</h2><ul>"
        +"<li>Issues: "+((remResult.before&&remResult.before.summary)?remResult.before.summary.total:"?")+" → "+((remResult.after&&remResult.after.summary)?remResult.after.summary.total:"?")+"</li>"
        +"<li>Score: "+(remResult.before?remResult.before.score:"?")+"% → "+(remResult.after?remResult.after.score:"?")+"%</li>"
        +"<li>Figure alt text added: "+(remResult.injectedCount||0)+" of "+(remResult.figuresFound||0)+"</li>"
        +(remResult.altTextNeeded?("<li>Figures still needing alt: "+remResult.altTextNeeded+"</li>"):"")
        +"</ul>"):"")
      +"<h2>Metadata (baked into the PDF on Apply metadata)</h2><ul>"
        +"<li>Title: "+esc(meta.title)+"</li><li>Language: "+esc(meta.lang)+"</li><li>Author: "+esc(meta.author)+"</li><li>Subject: "+esc(meta.subject)+"</li></ul>"
      +"<h2>Images — alt text</h2>"+(imgs?"<table><tr><th>Image</th><th>Resolution</th></tr>"+imgs+"</table>":"<p>No image issues.</p>")
      +"<h2>Structure — apply in Acrobat</h2>"+(struct.length?"<ul>"+struct.map(function(s){return "<li>"+esc(s)+"</li>";}).join("")+"</ul>":"<p>No structural issues.</p>")
      +"</body></html>";
    var blob=new Blob([html],{type:"text/html"});var a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=name+"-remediation-report.html";a.click();
    showToast("Remediation report downloaded");
  }

  /* ── Click-to-pinpoint: locate a finding's element(s) in the report-view live
     preview iframe, outline ALL matches, scroll to the first, and let the user
     step through. Same-origin blob iframe → contentDocument is queryable. ── */
  function pinpointDoc(){
    var fr=previewFrameRef.current; try{ if(fr&&fr.contentDocument&&fr.contentDocument.body)return fr.contentDocument; }catch(e){}
    var fs=document.querySelectorAll("iframe");
    for(var i=0;i<fs.length;i++){ try{ if(fs[i].contentDocument&&fs[i].contentDocument.body)return fs[i].contentDocument; }catch(e){} }
    return null;
  }
  function pinpointSel(s){
    if(!s)return null;
    var esc=function(v){ try{ return (window.CSS&&CSS.escape)?CSS.escape(v):v; }catch(e){ return v; } };
    var idm=s.match(/\bid=["']([^"']+)["']/); if(idm)return "#"+esc(idm[1]);
    var tagm=s.match(/<\s*([a-zA-Z][\w-]*)/); var tag=tagm?tagm[1].toLowerCase():"";
    var clsm=s.match(/\bclass=["']([^"']+)["']/);
    var cls=clsm?clsm[1].trim().split(/\s+/).filter(Boolean).map(function(c){return "."+esc(c);}).join(""):"";
    return (tag+cls)||null;
  }
  function pinpointClear(doc){ try{ Array.prototype.forEach.call(doc.querySelectorAll("[data-paris-hl]"),function(n){ n.style.outline=n.getAttribute("data-paris-o")||""; n.style.outlineOffset=""; n.removeAttribute("data-paris-hl"); n.removeAttribute("data-paris-o"); }); }catch(e){} }
  function pinpointMark(nodes,i){ nodes.forEach(function(n,k){ n.style.outline=(k===i?"3px solid #DC2626":"2px dashed #F59E0B"); n.style.outlineOffset="2px"; }); try{ nodes[i].scrollIntoView({behavior:"smooth",block:"center"}); }catch(e){} }
  function pinpoint(snippet){
    var doc=pinpointDoc(); if(!doc){ showToast("Preview not ready — run the engines so the page renders.","error"); return; }
    pinpointClear(doc);
    var sel=pinpointSel(snippet),matches=[];
    if(sel){ try{ matches=Array.prototype.slice.call(doc.querySelectorAll(sel)); }catch(e){} }
    if(!matches.length){ var norm=String(snippet||"").replace(/\s+/g," ").trim().slice(1,40);
      if(norm)matches=Array.prototype.slice.call(doc.querySelectorAll("*")).filter(function(n){ return (n.outerHTML||"").replace(/\s+/g," ").indexOf(norm)>=0; }).slice(0,40); }
    if(!matches.length){ showToast("Couldn't locate that element in the preview.","error"); setHlInfo(null); return; }
    matches.forEach(function(n){ n.setAttribute("data-paris-hl","1"); n.setAttribute("data-paris-o",n.style.outline||""); });
    pinpointMark(matches,0);
    setHlInfo({snippet:snippet,total:matches.length,idx:0});
  }
  function pinpointStep(dir){
    var doc=pinpointDoc(); if(!doc||!hlInfo)return;
    var nodes=Array.prototype.slice.call(doc.querySelectorAll("[data-paris-hl]")); if(!nodes.length)return;
    var ni=((hlInfo.idx+dir)%nodes.length+nodes.length)%nodes.length;
    pinpointMark(nodes,ni);
    setHlInfo(Object.assign({},hlInfo,{idx:ni}));
  }
  function renderIssueList(issues,engineName,color){
    if(!issues||!issues.length)return E("div",{style:{padding:12,color:P.slateL,fontSize:12,fontStyle:"italic"}},"No issues found by "+engineName+" -- looking good!");
    return issues.map(function(iss,i){
      var sc=iss.sev==="critical"?SC.critical:iss.sev==="major"?SC.major:SC.minor;
      var catLabel=issueCategoryLabel(iss);
      return E("div",{key:engineName+"-"+i,
        onClick:function(){ if(iss.el)pinpoint(iss.el); },
        title:iss.el?"Click to pinpoint this element in the live preview":"",
        style:{padding:"10px 14px",borderRadius:8,border:"1.5px solid "+sc[0]+"20",background:sc[1],marginBottom:6,cursor:iss.el?"pointer":"default",transition:"all .15s"}},
        E("div",{style:{display:"flex",gap:6,alignItems:"center",marginBottom:4,flexWrap:"wrap"}},
          E(SevB,{s:iss.sev}),
          E("span",{style:{padding:"2px 6px",borderRadius:4,background:color+"15",fontSize:9,fontWeight:700,color:color}},engineName),
          iss.wcag?E("span",{style:{fontSize:11,fontWeight:800}},"WCAG "+iss.wcag):null,
          E("span",{style:{fontSize:10,fontWeight:700,color:sc[0]}},catLabel),
          E("span",{style:{fontSize:10,color:P.slateM}},iss.name||iss.ruleId||"")),
        E("div",{style:{fontSize:12,color:P.slateM,marginBottom:iss.el?6:0}},iss.description||iss.desc||""),
        iss.count>1?E("span",{style:{fontSize:10,color:P.slateL}},iss.count+" elements"):null,
        iss.el?E("pre",{style:{margin:"4px 0",padding:"6px 8px",borderRadius:6,border:"1px solid "+P.bdr,fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap",background:P.bgS,maxHeight:80,overflow:"auto"}},iss.el):null,
        iss.fix?E("div",{style:{marginTop:4,padding:"6px 10px",borderRadius:6,background:P.accentLt}},E("span",{style:{fontSize:10,fontWeight:700,color:P.accent}},"\u{1F527} "),E("span",{style:{fontSize:10.5,color:P.slate}},iss.fix)):
        iss.remediation?E("div",{style:{marginTop:4,padding:"6px 10px",borderRadius:6,background:P.accentLt}},E("span",{style:{fontSize:10,fontWeight:700,color:P.accent}},"\u{1F527} "),E("span",{style:{fontSize:10.5,color:P.slate}},iss.remediation)):null);
    });
  }

  /* ─────────────────────────────────────────────────────────────
   * PR-5.15b: Grouped, expandable issue list for the PA11Y Lab Doc
   * Audit. Renders the page→WCAG→instances hierarchy from
   * groupWorkbenchIssues: PDFs nest by page, each WCAG group has a
   * +/- toggle, and expanding reveals one sub-row per instance.
   *
   * This is the "see all 20 violations individually represented"
   * surface. The click-to-highlight-on-canvas wiring is PR-5.15c —
   * here, sub-rows show the instance locator (element type, page,
   * and a 📍 marker when bbox geometry is available) so the auditor
   * can see exactly how many distinct elements failed and where.
   *
   * @param {object[]} issues - issues carrying the PR-5.15a `instance` block
   * @param {string} engineName
   * @param {string} color
   * @param {object} opts - { groupBy: "page"|"none" }
   * ───────────────────────────────────────────────────────────── */

  /* Collect resolved-geometry instances per page for the overlay layer.
     Only issues whose instance carries a non-null bbox can be drawn;
     the rest are reachable through the grouped list's page navigation. */
  function labPdfBboxesByPage(){
    var byPage={};
    var issues=(pdfStructRes&&pdfStructRes.issues)||[];
    for(var i=0;i<issues.length;i++){
      var iss=issues[i];var inst=iss&&iss.instance;
      if(!inst||!inst.bbox)continue;
      var pg=(inst.page!=null)?inst.page:(iss.page!=null?iss.page:null);
      if(pg==null)continue;
      if(!byPage[pg])byPage[pg]=[];
      byPage[pg].push({bbox:inst.bbox,sev:iss.sev||"minor",
        label:(inst.elementType||"Element")+(inst.pageElementIndex?" #"+inst.pageElementIndex:"")+" · p."+pg});
    }
    return byPage;
  }

  /* ── PR-5.15c 3/3: interactive click-to-pinpoint over the bbox overlay.
     The doc viewer is a page-image + percent-positioned overlay (not a
     queryable DOM), so this can't reuse the web pinpoint(); it works on the
     finding's derived instances instead. ── */
  /* Located members of a finding group: instances with a known page (bbox
     optional — page-only instances still navigate, just without a box).
     Carries the source index `i` so a clicked sub-row maps to its member. */
  function docPinMembers(group){
    if(!group||!Array.isArray(group.instances))return [];
    var out=[];
    for(var i=0;i<group.instances.length;i++){
      var inst=group.instances[i];
      if(!inst||inst.page==null)continue;
      out.push({i:i,page:inst.page,bbox:inst.bbox||null,sev:group.sev||"minor",
        label:inst.label||("Element "+(i+1)),text:inst.text||""});
    }
    return out;
  }
  /* Move the viewer's pager to a given PDF page (no-op if not rendered). */
  function jumpDocToPage(page){
    var nums=Object.keys(labPdfPages).map(Number).sort(function(a,b){return a-b});
    var k=nums.indexOf(page);
    if(k>=0)setLabPrevIdx(k);
  }
  /* Pin one instance of a finding group → jump to its page + highlight it.
     Re-clicking the active instance clears the pin (toggle), matching the
     web pinpoint's click-to-clear feel. */
  function pinpointDocInstance(rowKey,group,instIdx){
    var members=docPinMembers(group);
    if(!members.length){ showToast("No page location resolved for this finding.","error"); return; }
    var idx=-1; for(var i=0;i<members.length;i++){ if(members[i].i===instIdx){ idx=i; break; } }
    if(idx<0)idx=0;
    var pinKey=rowKey+"#"+instIdx;
    if(labDocPin&&labDocPin.key===pinKey){ setLabDocPin(null); return; }
    setLabDocPin({key:pinKey,idx:idx,members:members});
    jumpDocToPage(members[idx].page);
  }
  /* ‹ / › through a pinned finding's located instances. */
  function pinpointDocStep(dir){
    if(!labDocPin||!labDocPin.members||!labDocPin.members.length)return;
    var n=labDocPin.members.length;
    var ni=((labDocPin.idx+dir)%n+n)%n;
    setLabDocPin(Object.assign({},labDocPin,{idx:ni}));
    jumpDocToPage(labDocPin.members[ni].page);
  }
  function clearDocPin(){ setLabDocPin(null); }

  /* PR-5.15c 2/2 — the PA11Y Lab PDF viewer. Renders the uploaded PDF's
     pages (from labPdfPages) stacked, each in a position:relative frame
     so the highlight overlay can sit on top as percent-positioned divs.
     This is the SHELL + a static highlight layer: every resolved bbox is
     drawn (calibration artifact). Interactive click-to-highlight (row →
     pin, click-outside-clears) lands once the geometry is visually
     confirmed against this layer. */
  function renderLabPdfViewer(){
    if(labPdfStatus==="none")return null;
    var frame={borderRadius:10,border:"1.5px solid "+P.bdr,background:P.bgS,padding:12,marginBottom:16};
    if(labPdfStatus==="loading")
      return E("div",{"data-testid":"pa11y-pdf-viewer",style:frame},
        E("div",{style:{fontSize:12,color:P.slateL,fontStyle:"italic"}},"⏳ Rendering PDF preview…"));
    if(labPdfStatus==="error")
      return E("div",{"data-testid":"pa11y-pdf-viewer",style:frame},
        E("div",{style:{fontSize:12,color:P.slateL}},"PDF preview unavailable — couldn't render in-browser. Issue locations are still listed by page below."));
    var byPage=labPdfBboxesByPage();
    var pageNums=Object.keys(labPdfPages).map(Number).sort(function(a,b){return a-b});
    if(!pageNums.length)return null;
    var totalBoxes=0;Object.keys(byPage).forEach(function(k){totalBoxes+=byPage[k].length;});
    /* Single-page pager. A 36-page doc stacked in one scroll was
       unusable ("rendered in one piece") — show ONE page at a time with
       Prev/Next + a jump box, and a shortcut to step straight to the
       next page that actually has flagged issues. */
    var totalPages=labPdfNumPages||pageNums[pageNums.length-1];
    /* When a finding instance is pinned, the pager follows it and the
       overlay draws ONLY that finding's located instances (active solid,
       siblings dashed). Otherwise it shows every resolved box. */
    var pin=labDocPin;
    var activeMember=(pin&&pin.members&&pin.members[pin.idx])||null;
    var curIdx;
    if(activeMember&&activeMember.page!=null){var aIx=pageNums.indexOf(activeMember.page);curIdx=aIx>=0?aIx:Math.max(0,Math.min(labPrevIdx,pageNums.length-1));}
    else{curIdx=Math.max(0,Math.min(labPrevIdx,pageNums.length-1));}
    var p=pageNums[curIdx];
    var pg=labPdfPages[p];
    var boxes;
    if(pin&&pin.members){boxes=pin.members.filter(function(m){return m.page===p&&m.bbox;}).map(function(m){return {bbox:m.bbox,sev:m.sev,label:m.label,active:m===activeMember};});}
    else{boxes=(byPage[p]||[]).map(function(b){return {bbox:b.bbox,sev:b.sev,label:b.label,active:false};});}
    var issuePages=pageNums.filter(function(n){return (byPage[n]||[]).length>0;});
    function gotoIdx(j){setLabPrevIdx(Math.max(0,Math.min(j,pageNums.length-1)));}
    function jumpToPage(n){var k=pageNums.indexOf(n);if(k>=0)setLabPrevIdx(k);}
    function nextIssuePage(){for(var s=1;s<=pageNums.length;s++){var j=curIdx+s;if(j>=pageNums.length)j-=pageNums.length;if((byPage[pageNums[j]]||[]).length>0&&j!==curIdx){setLabPrevIdx(j);return;}}}
    var navBtn={padding:"5px 12px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,color:P.slate,fontSize:12,fontWeight:700,cursor:"pointer"};
    var navDis=Object.assign({},navBtn,{opacity:.4,cursor:"not-allowed"});
    var pinBtn={padding:"1px 8px",borderRadius:5,border:"1.5px solid "+P.bdr,background:"#fff",cursor:"pointer",fontSize:12,fontWeight:800,color:P.pri,lineHeight:1.4};
    return E("div",{"data-testid":"pa11y-pdf-viewer",style:frame},
      E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}},
        E("span",{style:{fontSize:12,fontWeight:800,color:P.slate}},"\u{1F4C4} PDF Preview"),
        E("span",{style:{fontSize:10,color:P.slateL}},labPdfNumPages+" page"+(labPdfNumPages!==1?"s":"")),
        totalBoxes>0?E("span",{"data-testid":"pa11y-pdf-pin-count",style:{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:P.priLt,color:P.pri}},
          "\u{1F4CD} "+totalBoxes+" location"+(totalBoxes!==1?"s":"")+" pinned"+(issuePages.length?" on "+issuePages.length+" page"+(issuePages.length!==1?"s":""):"")):
          E("span",{style:{fontSize:10,color:P.slateL,fontStyle:"italic"}},"No element geometry resolved yet"),
        (pin&&pin.members&&pin.members.length)?E("div",{"data-testid":"pa11y-pdf-pin-stepper",style:{display:"flex",alignItems:"center",gap:5,marginLeft:"auto",fontSize:10,fontWeight:700,color:P.pri}},
          E("span",{style:{color:P.slateM,fontWeight:600}},"\u{1F4CD} Pinpoint:"),
          pin.members.length>1?E("button",{onClick:function(){pinpointDocStep(-1)},title:"Previous instance","aria-label":"Previous instance",style:pinBtn},"‹"):null,
          E("span",null,(pin.idx+1)+" / "+pin.members.length),
          pin.members.length>1?E("button",{onClick:function(){pinpointDocStep(1)},title:"Next instance","aria-label":"Next instance",style:pinBtn},"›"):null,
          E("button",{onClick:clearDocPin,title:"Clear pinpoint","aria-label":"Clear pinpoint",style:Object.assign({},pinBtn,{color:P.slateM})},"✕ clear")):null),
      /* pager controls */
      E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}},
        E("button",{onClick:function(){gotoIdx(curIdx-1)},disabled:curIdx<=0,style:curIdx<=0?navDis:navBtn},"← Prev"),
        E("span",{style:{fontSize:12,fontWeight:700,color:P.slate,minWidth:90,textAlign:"center"}},"Page "+p+" of "+totalPages),
        E("button",{onClick:function(){gotoIdx(curIdx+1)},disabled:curIdx>=pageNums.length-1,style:curIdx>=pageNums.length-1?navDis:navBtn},"Next →"),
        boxes.length>0?E("span",{style:{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:P.sCritBg||"#FEE2E2",color:P.sCrit}},"⚠ "+boxes.length+" issue"+(boxes.length!==1?"s":"")+" on this page"):null,
        E("span",{style:{flex:1}}),
        issuePages.length>0?E("button",{onClick:nextIssuePage,title:"Jump to the next page with flagged issues",
          style:Object.assign({},navBtn,{background:P.priLt,color:P.pri,border:"1.5px solid "+P.pri+"40"})},"⚠ Next issue page"):null,
        E("label",{style:{fontSize:10,color:P.slateL,display:"inline-flex",alignItems:"center",gap:4}},"Go to",
          E("input",{type:"number",min:pageNums[0],max:pageNums[pageNums.length-1],value:p,
            onChange:function(ev){var n=parseInt(ev.target.value,10);if(!isNaN(n))jumpToPage(n);},
            style:{width:60,padding:"4px 6px",borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:12}}))),
      /* single page */
      E("div",{style:{position:"relative",margin:"0 auto",width:"100%",maxWidth:640,boxShadow:"0 1px 6px rgba(0,0,0,.12)",borderRadius:4,overflow:"hidden"}},
        E("img",{src:pg.url,alt:"Page "+p+" of the uploaded PDF",style:{display:"block",width:"100%",height:"auto"}}),
        /* page number chip */
        E("span",{style:{position:"absolute",top:6,left:6,fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,background:"#000a",color:"#fff"}},"p."+p),
        /* highlight overlay layer */
        boxes.map(function(b,bi){
          var pct=bboxToOverlayPct(b.bbox,pg.ptW,pg.ptH);
          if(!pct)return null;
          var col=b.sev==="critical"?P.sCrit:b.sev==="major"?P.sMaj:P.sMin;
          var solid=b.active||!pin;   /* overview = all solid; pinned = only the active box solid */
          return E("div",{key:"box-"+bi,"data-testid":"pa11y-pdf-highlight","data-active":b.active?"true":undefined,title:b.label,
            style:{position:"absolute",left:pct.left+"%",top:pct.top+"%",width:pct.width+"%",height:pct.height+"%",
              border:(b.active?"3px ":"2px ")+(solid?"solid ":"dashed ")+col,
              background:b.active?col+"33":(pin?"transparent":col+"22"),
              borderRadius:2,boxSizing:"border-box",pointerEvents:"none",transition:"all .15s",
              boxShadow:b.active?"0 0 0 2px "+col+"55":"none"}});
        })));
  }

  function renderGroupedIssueList(issues, engineName, color, opts){
    opts = opts || {};
    if(!issues||!issues.length)return E("div",{style:{padding:12,color:P.slateL,fontSize:12,fontStyle:"italic"}},"No issues found by "+engineName+" -- looking good!");
    var grouped=groupWorkbenchIssues(issues,{groupBy:opts.groupBy||"page"});
    return E("div",{"data-testid":"pa11y-grouped-issues"},
      grouped.containers.map(function(container,ci){
        return E("div",{key:"cont-"+ci,style:{marginBottom:14}},
          /* Page/container header */
          E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:6,paddingBottom:4,borderBottom:"1.5px solid "+P.bdr}},
            E("span",{style:{fontSize:12,fontWeight:800,color:P.slate}},container.label),
            E("span",{style:{fontSize:10,color:P.slateL}},
              container.groups.length+" issue type"+(container.groups.length!==1?"s":""))),
          container.groups.map(function(group,gi){
            var rowKey=container.key+"::"+group.groupKey;
            var isOpen=!!labExpandedGroups[rowKey];
            var sc=group.sev==="critical"?SC.critical:group.sev==="major"?SC.major:SC.minor;
            var n=group.instances.length;
            return E("div",{key:"grp-"+gi,style:{marginBottom:6,borderRadius:8,border:"1.5px solid "+sc[0]+"20",background:sc[1],overflow:"hidden"}},
              /* Group header row — click toggles expand/collapse */
              E("div",{role:"button",tabIndex:0,
                "aria-expanded":isOpen,
                "data-testid":"pa11y-group-header",
                onClick:function(){toggleLabGroup(rowKey)},
                onKeyDown:function(ev){if(ev.key==="Enter"||ev.key===" "){ev.preventDefault();toggleLabGroup(rowKey)}},
                style:{display:"flex",gap:8,alignItems:"center",padding:"10px 12px",cursor:"pointer",flexWrap:"wrap"}},
                /* +/- expand affordance */
                E("span",{"aria-hidden":"true",style:{fontSize:14,fontWeight:800,color:sc[0],width:16,textAlign:"center",flexShrink:0}},isOpen?"−":"+"),
                E(SevB,{s:group.sev}),
                /* Category label (📋 Metadata, 🖼 Image: Missing Alt Text, …).
                   Derived from the group's own cat/wcag — NOT the engine
                   name. PR-5.15b's grouped renderer dropped this; without it
                   every structural issue read only as the "Structural" engine
                   badge, hiding the real category. Restored in PR-5.15c. */
                (group.cat||group.wcag||group.desc)?E("span",{"data-testid":"pa11y-group-cat",style:{fontSize:11,fontWeight:800,color:P.slate}},
                  issueCategoryLabel({wcag:group.wcag,desc:group.desc,cat:group.cat})):null,
                E("span",{style:{padding:"2px 6px",borderRadius:4,background:color+"15",fontSize:9,fontWeight:700,color:color}},engineName),
                group.wcag?E("span",{style:{fontSize:11,fontWeight:800}},"WCAG "+group.wcag):null,
                E("span",{style:{fontSize:12,color:P.slate,flex:"1 1 200px"}},group.desc||""),
                /* Instance count pill — "20 elements" */
                E("span",{style:{fontSize:10,fontWeight:800,color:sc[0],padding:"2px 8px",borderRadius:10,background:"#fff8",flexShrink:0}},
                  n+" "+(n===1?"element":"elements"))),
              /* Expanded: one sub-row per instance */
              isOpen?E("div",{style:{borderTop:"1px solid "+sc[0]+"15",background:"#fff6"}},
                group.instances.map(function(inst,ii){
                  var canPin=inst.page!=null;
                  var pinKey=rowKey+"#"+ii;
                  var isActive=!!(labDocPin&&labDocPin.key===pinKey);
                  return E("div",{key:"inst-"+ii,
                    "data-testid":"pa11y-instance-row",
                    role:canPin?"button":undefined,
                    tabIndex:canPin?0:undefined,
                    "aria-pressed":canPin?(isActive?"true":"false"):undefined,
                    title:canPin?(inst.bbox?"Click to pinpoint this element on the PDF page":"Click to jump to page "+inst.page):"",
                    onClick:canPin?function(){pinpointDocInstance(rowKey,group,ii)}:undefined,
                    onKeyDown:canPin?function(ev){if(ev.key==="Enter"||ev.key===" "){ev.preventDefault();pinpointDocInstance(rowKey,group,ii)}}:undefined,
                    style:{display:"flex",gap:8,alignItems:"center",padding:"7px 12px 7px 36px",
                      borderTop:ii>0?"1px solid "+P.bdr+"60":"none",fontSize:11,
                      cursor:canPin?"pointer":"default",background:isActive?P.priLt:"transparent",
                      boxShadow:isActive?("inset 3px 0 0 "+P.pri):"none",transition:"background .12s"}},
                    E("span",{style:{fontSize:10,fontWeight:700,color:P.slateL,minWidth:22}},"#"+(ii+1)),
                    /* 📍 = bbox geometry resolved (draws a box); 📄 = page-only
                       locator (jumps to the page, no box). Both click-to-pinpoint. */
                    inst.bbox?E("span",{title:"Location pinned on page","aria-hidden":"true",style:{fontSize:11}},"\u{1F4CD}"):
                      (canPin?E("span",{title:"Page-level location","aria-hidden":"true",style:{fontSize:11,opacity:.5}},"\u{1F4C4}"):null),
                    E("span",{style:{fontWeight:600,color:isActive?P.pri:P.slate}},inst.label||("Element "+(ii+1))),
                    inst.text?E("span",{style:{fontSize:10,color:P.slateM,fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}},"“"+inst.text+"”"):null,
                    isActive?E("span",{style:{marginLeft:"auto",fontSize:9,fontWeight:800,color:P.pri}},"◉ pinned"):null);
                })
              ):null,
              /* Fix guidance — always shown under the group header */
              group.fix?E("div",{style:{padding:"6px 12px 8px 36px",background:"#fff4"}},
                E("span",{style:{fontSize:10,fontWeight:700,color:P.accent}},"\u{1F527} "),
                E("span",{style:{fontSize:10.5,color:P.slate}},group.fix)):null);
          })
        );
      })
    );
  }

  var labHasResults=!!(labAxeRes||labHtmlcsRes||aiRes);
  var labAllIssues=[];
  if(labAxeRes&&labAxeRes.issues)labAllIssues=labAllIssues.concat(labAxeRes.issues);
  if(labHtmlcsRes&&labHtmlcsRes.issues){var axeW={};labAllIssues.forEach(function(i){if(i.wcag)axeW[i.wcag]=true});labHtmlcsRes.issues.forEach(function(i){if(!i.wcag||!axeW[i.wcag])labAllIssues.push(i)});}
  var labTC=labAllIssues.filter(function(i){return i.sev==="critical"}).reduce(function(x,i){return x+(i.count||1)},0);
  var labTM=labAllIssues.filter(function(i){return i.sev==="major"}).reduce(function(x,i){return x+(i.count||1)},0);
  var labTN=labAllIssues.filter(function(i){return i.sev==="minor"}).reduce(function(x,i){return x+(i.count||1)},0);

  /* ── PA11YLab Web Audit sub-tab strip ── */
  /* ── chevronFlow — the diagrammatic Add→Audit→Remediate→Package stage bar.
     Done stages show a check, the current is filled + lifted, upcoming are
     muted; reachable stages are clickable (back-nav). Reusable web + doc. ── */
  /* measuredRef — a ref callback that tracks an element's live width (mount +
     ResizeObserver), so the scaled preview knows how much room it has to fit. */
  function measuredRef(setW){
    return function(el){
      if(!el)return;
      try{
        var w=Math.round(el.getBoundingClientRect().width); if(w)setW(w);
        if(typeof ResizeObserver!=="undefined"&&!el.__plRO){
          el.__plRO=new ResizeObserver(function(){try{var nw=Math.round(el.getBoundingClientRect().width);if(nw)setW(nw)}catch(e){}});
          el.__plRO.observe(el);
        }
      }catch(e){}
    };
  }
  /* deviceBar — Mobile / Tablet / Desktop viewport toggle for the live preview. */
  function deviceBar(cur,onPick){
    return E("div",{role:"group","aria-label":"Preview viewport",style:{display:"flex",alignItems:"center",gap:4}},
      E("span",{style:{fontSize:10,fontWeight:600,color:P.slateM,marginRight:2}},"View:"),
      ["mobile","tablet","desktop"].map(function(dv){
        var d=DEVICE_VP[dv],a=cur===dv;
        return E("button",{key:dv,onClick:function(){onPick(dv)},title:d.l+" — "+d.w+"px wide","aria-pressed":a?"true":"false",
          style:{padding:"3px 9px",borderRadius:5,border:"1px solid "+(a?P.pri:P.bdr),background:a?P.priLt:P.card,color:a?P.pri:P.slateM,fontSize:10,fontWeight:a?700:500,cursor:"pointer"}},
          d.i+" "+d.w);
      }));
  }
  /* scaledViewport — render build(vw,vh) inside a TRUE device viewport scaled to
     fit paneW. The child lays out at the device's real width; we only shrink the
     picture, so a desktop site looks like the desktop site (just smaller) instead
     of being reflowed to its mobile breakpoint. */
  function scaledViewport(deviceKey,paneW,visH,build){
    if(deviceKey==="full")return E("div",{style:{width:"100%",height:visH||"100%",overflow:"hidden",borderRadius:10,border:"1.5px solid "+P.bdr,background:"#fff"}},build("100%","100%"));
    var d=DEVICE_VP[deviceKey]||DEVICE_VP.desktop;
    var s=Math.min(1,(paneW||440)/d.w);
    var vis=visH||Math.round(d.h*s);
    var vh=Math.round(vis/s);            // device-px height that, scaled by s, fills vis
    return E("div",{style:{width:Math.round(d.w*s),height:vis,overflow:"hidden",margin:"0 auto",borderRadius:10,border:"1.5px solid "+P.bdr,background:"#fff",boxShadow:"0 1px 8px rgba(15,23,42,.06)"}},
      E("div",{style:{width:d.w,height:vh,transform:"scale("+s+")",transformOrigin:"top left"}},
        build(d.w,vh)));
  }
  function chevronFlow(stages, currentId, reach, onPick, accent){
    var curIdx=stages.map(function(s){return s.id}).indexOf(currentId);
    return E("div",{role:"group","aria-label":"Workflow progress",style:{display:"flex",margin:"16px 0 14px",overflowX:"auto",paddingBottom:2}},
      stages.map(function(s,i){
        var done=i<curIdx, cur=s.id===currentId, ok=!!reach[s.id];
        var clip=i===0?"polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%)"
          :(i===stages.length-1?"polygon(18px 0, 100% 0, 100% 100%, 18px 100%, 0 50%)"
            :"polygon(18px 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 18px 100%, 0 50%)");
        var bg=cur?accent:(done?accent+"1f":P.bgS), fg=cur?"#fff":(done?accent:(ok?P.slateM:P.slateL));
        return E("div",{key:s.id,role:"button",tabIndex:(ok||cur)?0:-1,
          "aria-current":cur?"step":undefined,"aria-disabled":(ok||cur)?undefined:true,
          "aria-label":(i+1)+" of "+stages.length+": "+s.l+(cur?" (current)":done?" (done)":ok?"":" (locked)"),
          onClick:ok?function(){onPick(s.id)}:null,
          onKeyDown:ok?function(e){if(e.key==="Enter"||e.key===" "){e.preventDefault();onPick(s.id)}}:null,
          style:{flex:"1 1 0",minWidth:138,marginLeft:i?-16:0,zIndex:cur?3:(done?2:1),clipPath:clip,WebkitClipPath:clip,
            background:bg,color:fg,padding:"11px 20px 11px "+(i?32:18)+"px",cursor:ok?"pointer":"default",transition:"all .18s",
            display:"flex",alignItems:"center",gap:10,boxShadow:cur?"0 3px 12px "+accent+"66":"none",opacity:ok||cur||done?1:0.7}},
          E("div",{style:{width:26,height:26,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,
            background:cur?"rgba(255,255,255,.25)":(done?accent:P.card),color:(cur||done)?"#fff":P.slateM,border:cur||done?"none":"1.5px solid "+P.bdr}},done?"✓":String(i+1)),
          E("div",{style:{minWidth:0,lineHeight:1.2}},
            E("div",{style:{fontSize:12.5,fontWeight:cur?800:700,whiteSpace:"nowrap"}},s.i+" "+s.l),
            s.sub?E("div",{style:{fontSize:9.5,opacity:.82,whiteSpace:"nowrap",marginTop:1}},s.sub):null));
      }));
  }
  function goWebStage(id){ setLabWebStage(id); if(id==="remediate"&&lastEvalId&&!reviewPanel){ openReviewPanel(lastEvalId); } }   /* Audit effect auto-runs engines; Remediate opens the guidance station */
  var webStages=[
    {id:"add",l:"Add Page",sub:"Fetch or paste",i:"\u{1F310}"},
    {id:"audit",l:"Audit",sub:"axe · HTMLCS · AI",i:"\u{1F50E}"},
    {id:"remediate",l:"Remediate",sub:"Guide & fix",i:"\u{1F6E0}"},
    {id:"package",l:"Package",sub:"Report & export",i:"\u{1F4E6}"}
  ];
  var webReach={ add:true, audit:!!(aiHtml&&aiHtml.trim()), remediate:labHasResults, package:labHasResults };
  var labWebChevrons=chevronFlow(webStages, labWebStage, webReach, goWebStage, P.pri);

  /* ── Doc Audit chevron — same component + a11y as Web, relabeled
     Upload → Audit → Remediate → Package. Stage drives the render (the old
     Document/Audit-Report two-tab strip is retired); labDocTab is kept in
     sync only so the -report deep link / back-forward URL stays correct.
     Leaving the "add" stage clears any active pinpoint. ── */
  function goDocStage(id){ setLabDocStage(id); setLabDocTab(id==="add"?"list":"report"); if(id==="add")setLabDocPin(null); }
  var docStages=[
    {id:"add",l:"Upload",sub:"Choose a PDF",i:"\u{1F4C4}"},
    {id:"audit",l:"Audit",sub:"Structural · AI",i:"\u{1F50E}"},
    {id:"remediate",l:"Remediate",sub:"Pinpoint & fix",i:"\u{1F6E0}"},
    {id:"package",l:"Package",sub:"Report & certify",i:"\u{1F4E6}"}
  ];
  var docHasResults=!!(pdfStructRes||aiPdfRes);
  var docReach={ add:true, audit:docHasResults, remediate:docHasResults, package:docHasResults };
  var labDocChevrons=chevronFlow(docStages, labDocStage, docReach, goDocStage, P.coral);

  var labWebTabStrip=E("div",{style:{display:"flex",gap:4,marginTop:14,marginBottom:0}},
    [{id:"list",l:"Web Page",i:"\u{1F310}"},{id:"report",l:"Audit Report",i:"\u{1F4CB}"}].map(function(t){
      var active=labWebTab===t.id;
      return E("button",{key:t.id,onClick:function(){setLabWebTab(t.id)},
        "aria-current":active?"page":undefined,
        style:{padding:"8px 18px",borderRadius:"8px 8px 0 0",border:"1.5px solid "+(active?P.pri:P.bdr),borderBottom:active?"2px solid "+P.card:"2px solid "+P.bdr,background:active?P.card:P.bgS,color:active?P.pri:P.slateL,fontSize:12,fontWeight:active?700:500,cursor:"pointer",transition:"all .15s"}},t.i+" "+t.l);
    })
  );

  /* ── PA11YLab Doc Audit sub-tab strip ── */
  var labDocTabStrip=E("div",{style:{display:"flex",gap:4,marginTop:14,marginBottom:0}},
    [{id:"list",l:"Document",i:"\u{1F4C4}"},{id:"report",l:"Audit Report",i:"\u{1F4CB}"}].map(function(t){
      var active=labDocTab===t.id;
      return E("button",{key:t.id,onClick:function(){setLabDocTab(t.id)},
        "aria-current":active?"page":undefined,
        style:{padding:"8px 18px",borderRadius:"8px 8px 0 0",border:"1.5px solid "+(active?P.coral:P.bdr),borderBottom:active?"2px solid "+P.card:"2px solid "+P.bdr,background:active?P.card:P.bgS,color:active?P.coral:P.slateL,fontSize:12,fontWeight:active?700:500,cursor:"pointer",transition:"all .15s"}},t.i+" "+t.l);
    })
  );

  /* ═══════════════════════════════════════════════════════════════
   * MODULE: pa11y-lab-web-ui                      [status: ready]
   * PURPOSE: React render tree for PA11YLab Web Audit module
   *
   * READS:     labWebTab, labSession, labUrl, aiHtml, labPreview,
   *            labAxeRes, labHtmlcsRes, aiRes, aiFixed, labRunning,
   *            labFetchErr, labShowPreview, labSessions, labHasResults,
   *            labAllIssues, labTC/labTM/labTN, aiImg, aiAlt, P (palette),
   *            isMobile, panesLocked
   * WRITES:    None (calls setters via onClick handlers)
   * CALLS:     labFetchPage, labRunAxe/Htmlcs/Ai/AiFix/AllWeb, labClearWeb,
   *            setLabWebTab, savePageSession, loadPageSession,
   *            navigateTo, generateAltText, handleImgUpload
   * EFFECTS:   iframe creation via ref callback (blob URLs)
   * EMITS:     DOM content within <main>
   *
   * EXPORTS:   JSX/E tree — when extracted becomes <Pa11yLabWeb /> component
   * IMPORTS:   All READS above as props + all CALLS as handlers
   *
   * CLOSURE:   Uses E = React.createElement (module-level constant)
   * ENTANGLED: SubViewHero, Card, MC, engBadge, engBtn, renderIssueList,
   *            paneLabel are shared UI primitives — extract these first.
   * ═══════════════════════════════════════════════════════════════ */

  /* ═══ PA11Y LAB — WEB AUDIT MODULE ═══ */
  var aiLabWeb = E("div",{className:"pa11yStageShell"},
    E(SubViewHero,{
      product:"a11ylab",
      title:"PA11Y Lab \u2014 Web Audit",
      subtitle:labSession?"\u{1F552} "+new Date(labSession.createdAt).toLocaleString("en-US",{year:"numeric",month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})+(labSession.sourceUrl?" \u2014 "+labSession.sourceUrl:""):"Multi-engine accessibility testing bench. Fetch a URL or paste HTML.",
      right:E("div",{style:{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}},
        labAxeRes&&labAxeRes.success?engBadge("axe-core","#fff"):null,
        labHtmlcsRes&&labHtmlcsRes.success?engBadge("HTMLCS","#fff"):null,
        aiRes&&!aiRes.error?engBadge("Claude AI","#fff"):null,
        labSession&&!labSession.saved?E("button",{onClick:savePageSession,style:{padding:"4px 12px",borderRadius:6,border:"1px solid rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"},title:"Sessions auto-save while you work. Click to keep this session beyond its 24h default expiration."},"\u{1F4BE} Keep session"):null,
        labSession&&labSession.saved?E("span",{style:{fontSize:10,color:"rgba(255,255,255,0.85)",fontWeight:700}},"\u2713 Saved"):null,
        Object.keys(labRunning).length>0?E("span",{style:{fontSize:10,color:"rgba(255,255,255,0.85)",alignSelf:"center"}},"\u23f3 Running..."):null)
    }),
    labWebChevrons,

    /* WEB PAGE sub-tab (list) */
    labWebStage==="add"?E(Card,{sx:{borderTop:"2px solid "+P.pri,borderRadius:12}},
      /* First-run intro — an empty view should explain what this does */
      !aiHtml?E("div",{style:{padding:"14px 16px",marginBottom:14,borderRadius:12,border:"1.5px solid "+P.priLt,background:"linear-gradient(135deg,#EEF2FF,#F5F3FF)"}},
        E("div",{style:{fontSize:13,fontWeight:800,color:P.pri,marginBottom:4}},"🧪 Audit any web page for WCAG 2.1 AA"),
        E("div",{style:{fontSize:11,color:P.slateM,lineHeight:1.5}},"Fetch a URL (or paste HTML) and run three engines side by side — axe-core, HTML CodeSniffer, and Claude AI — then see a blended score, per-rule issues with element locations, and the manual-review patterns only a human can verify."),
        E("div",{style:{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}},
          E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.priLt,color:P.pri,fontSize:10,fontWeight:700}},"axe-core"),
          E("span",{style:{padding:"2px 8px",borderRadius:6,background:"#FFE9D6",color:"#E65100",fontSize:10,fontWeight:700}},"HTMLCS"),
          E("span",{style:{padding:"2px 8px",borderRadius:6,background:"#EDE9FE",color:"#8B5CF6",fontSize:10,fontWeight:700}},"Claude AI"))):null,
      /* Recent Sessions */
      labSessions.length>0?E("details",{style:{marginBottom:14}},
        E("summary",{style:{fontSize:11,fontWeight:700,color:P.slateM,cursor:"pointer",marginBottom:6}},"\u{1F4C2} Recent Web Sessions ("+labSessions.length+")"),
        E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
          labSessions.map(function(s){return E("button",{key:s.id,onClick:function(){_labSessionId.current=s.id;loadPageSession(s.id);navigateTo("aiLabWeb",s.id,labWebTab);setView("app")},
            style:{padding:"6px 12px",borderRadius:8,border:"1.5px solid "+(s.id===(_labSessionId.current||"")?"#4F46E5":P.bdr),background:s.id===(_labSessionId.current||"")?P.priLt:P.bgS,fontSize:10,fontWeight:600,cursor:"pointer",color:P.slateM,textAlign:"left",maxWidth:200}},
            E("div",{style:{fontWeight:700,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},s.sourceUrl||s.sourceFileName||s.id),
            E("div",{style:{fontSize:9,color:P.slateL}},new Date(s.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}))
          )}))
      ):null,
      E("div",{style:{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}},
        E("input",{value:labUrl,onChange:function(ev){setLabUrl(ev.target.value)},onKeyDown:function(ev){if(ev.key==="Enter")labFetchPage()},placeholder:"https://pi-consulting.us",style:{flex:1,minWidth:200,padding:"10px 14px",borderRadius:8,border:"1.5px solid "+P.bdr,fontSize:12,background:P.bgS,color:P.slate}}),
        E("button",{onClick:labFetchPage,disabled:!!labRunning.fetch||!labUrl.trim(),style:{padding:"10px 20px",borderRadius:8,border:"none",background:labRunning.fetch?"#ccc":"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:12,fontWeight:700,cursor:labRunning.fetch?"wait":"pointer",whiteSpace:"nowrap"}},labRunning.fetch?"\u23f3 Fetching...":"\u{1F517} Fetch Page"),
        aiHtml?E("button",{onClick:labClearWeb,style:{padding:"10px 14px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,fontSize:11,fontWeight:600,cursor:"pointer"}},"\u{1F5D1} Clear"):null),
      labFetchErr?E("div",{style:{padding:"8px 12px",borderRadius:8,background:P.sCritBg,color:P.sCrit,fontSize:12,marginBottom:10}},"\u26a0 "+labFetchErr):null,
      labPreview?null:E("div",{style:{fontSize:10,fontWeight:600,color:P.slateL,marginBottom:4}},"Or paste HTML directly:"),
      labPreview?E("details",{style:{marginTop:8}},E("summary",{style:{fontSize:10,fontWeight:600,color:P.slateL,cursor:"pointer",marginBottom:4}},"View / Edit Source ("+Math.round(aiHtml.length/1024)+"KB)"),
        E("textarea",{value:aiHtml,onChange:function(ev){setAiHtml(ev.target.value)},style:{width:"100%",minHeight:200,padding:10,borderRadius:8,border:"1.5px solid "+P.bdr,fontFamily:FC,fontSize:11,resize:"vertical",background:P.bgS,color:P.slate}})):
      E("textarea",{value:aiHtml,onChange:function(ev){setAiHtml(ev.target.value)},placeholder:"<!DOCTYPE html>\\n<html>\\n  <body>...</body>\\n</html>",style:{width:"100%",minHeight:aiHtml?200:80,padding:10,borderRadius:8,border:"1.5px solid "+P.bdr,fontFamily:FC,fontSize:11,resize:"vertical",background:P.bgS,color:P.slate,transition:"min-height .2s"}}),
      (aiHtml.trim()&&!labPreview)?E("button",{onClick:runPasteAudit,style:{marginTop:8,padding:"10px 18px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.coral+")",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}},"⚡ Analyze HTML"):null,
      /* HTML/Page Preview — Split-pane: Original vs AI-Remediated */
      labPreview?E("div",{style:{marginTop:12,marginBottom:12}},
        E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}},
          E("span",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"\u{1F50D} Page Preview"+(aiFixed?" \u2014 Split View":""))),
        labShowPreview?E("div",null,
        /* Sync button — centered above both panes */
        aiFixed&&!isMobile?E("div",{style:{display:"flex",justifyContent:"center",padding:"6px 0"}},
          E("button",{onClick:function(){setPanesLocked(!panesLocked)},title:panesLocked?"Panes scroll together":"Panes scroll independently",
            style:{padding:"6px 18px",borderRadius:8,border:"2px solid "+(panesLocked?"#1D4ED8":"#DC2626"),background:panesLocked?"#1D4ED8":"#FFFFFF",color:panesLocked?"#FFFFFF":"#DC2626",fontSize:11,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:6,minHeight:32,transition:"all .15s",boxShadow:panesLocked?"0 2px 8px rgba(29,78,216,.2)":"0 2px 8px rgba(220,38,38,.1)"}},panesLocked?"\u{1F512} Synced":"\u{1F513} Independent")):null,
        E("div",{style:{display:"grid",gridTemplateColumns:aiFixed&&!isMobile?"1fr 4px 1fr":"1fr",border:"1.5px solid "+P.bdr,borderRadius:10,overflow:"hidden",minHeight:isMobile?350:"calc(100vh - 380px)",maxHeight:"calc(100vh - 300px)"}},
          /* Left pane: Original */
          E("div",{style:{display:"flex",flexDirection:"column",overflow:"hidden"}},
            paneLabel("\u{1F4CB}","Baseline","Pre-remediation snapshot",P.sCrit),
            E("iframe",{key:"lab-orig-"+labPreview.length,ref:function(el){
              if(el&&labPreview){var blob=new Blob([labPreview],{type:"text/html;charset=utf-8"});el.src=URL.createObjectURL(blob)}
            },style:{width:"100%",flex:1,border:"none",background:P.card,minHeight:300}})),
          /* Divider */
          aiFixed&&!isMobile?E("div",{style:{background:P.bdr,display:"flex",alignItems:"center",justifyContent:"center"}},
            E("div",{style:{width:4,height:32,borderRadius:2,background:"#6366F1"}})):null,
          /* Right pane: AI-Remediated (only when aiFixed exists) */
          aiFixed?E("div",{style:{display:"flex",flexDirection:"column",overflow:"hidden"}},
            paneLabel("\u{1F6E0}","Simulated Deliverable","Fixes applied",P.emerald),
            E("iframe",{key:"lab-fixed-"+aiFixed.length,ref:function(el){
              if(el&&aiFixed){var blob=new Blob([aiFixed],{type:"text/html;charset=utf-8"});el.src=URL.createObjectURL(blob)}
            },style:{width:"100%",flex:1,border:"none",background:P.card,minHeight:300}})):null
        )):E("div",{style:{display:"flex",gap:6}},
          E("button",{onClick:function(){setLabShowPreview(true)},style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600,cursor:"pointer"}},"\u25B6 Show Preview"),
          E("button",{onClick:function(){setLabPreview("");setAiHtml("")},style:{padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600,cursor:"pointer",color:P.sCrit}},"\u2715 Close"))
      ):null
    ):null,

    /* AUDIT REPORT sub-tab (report) */
    labWebStage!=="add"?E(Card,{sx:{borderTop:"2px solid "+P.pri,borderRadius:12}},
      aiHtml?E("div",{style:{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}},
        labWebStage==="audit"?engBtn("axe-core",P.pri,labRunAxe,!!labRunning.axe,!!labAxeRes):null,
        labWebStage==="audit"?engBtn("HTMLCS","#E65100",labRunHtmlcs,!!labRunning.htmlcs,!!labHtmlcsRes):null,
        labWebStage==="audit"?engBtn("AI Analysis","#8B5CF6",labRunAi,aiLoading,!!aiRes):null,
        labWebStage==="audit"?E("div",{style:{width:1,height:24,background:P.bdr,margin:"0 4px"}}):null,
        labWebStage==="audit"?E("button",{onClick:labRunAllWeb,disabled:Object.keys(labRunning).length>0||aiLoading,style:{padding:"8px 20px",borderRadius:8,border:"none",background:Object.keys(labRunning).length>0||aiLoading?"#ccc":"linear-gradient(135deg,"+P.pri+","+P.coral+")",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}},"\u26a1 Run All Engines"):null,(labWebStage==="package"&&labHasResults)?E("button",{onClick:generateWcagReport,style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.pri,background:"#fff",color:P.pri,fontSize:12,fontWeight:700,cursor:"pointer"}},"📋 Evaluation Report"):null,
        (labWebStage==="package"&&reportPdf)?E("button",{onClick:function(){downloadB64Pdf(reportPdf.b64,reportPdf.name)},title:"Download the evidence-first WCAG evaluation as a PDF",style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.emerald,background:"#fff",color:P.emerald,fontSize:12,fontWeight:700,cursor:"pointer"}},"↓ Download PDF"):null,
        (labWebStage==="audit"&&aiModelUsed)?E("span",{title:aiModelUsed==="haiku-fallback"?"Sonnet exceeded the gateway budget; the faster model produced this result (interim until async lands)":"AI analysis ran on Sonnet",style:{padding:"3px 9px",borderRadius:12,fontSize:10,fontWeight:700,color:aiModelUsed==="haiku-fallback"?P.amberDk:P.slateM,background:aiModelUsed==="haiku-fallback"?P.sMajBg:P.bgS,border:"1px solid "+(aiModelUsed==="haiku-fallback"?P.amber+"55":P.bdr)}},aiModelUsed==="haiku-fallback"?"AI: Haiku (Sonnet timed out)":"AI: Sonnet"):null,
        (labWebStage==="remediate"&&lastEvalId)?E("button",{onClick:function(){openReviewPanel(lastEvalId)},title:"Open the Human Inspection Station — the AI's high-confidence calls auto-pass; you guide the rest",style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid #8B5CF6",background:reviewPanel?"#8B5CF6":"#fff",color:reviewPanel?"#fff":"#8B5CF6",fontSize:12,fontWeight:700,cursor:"pointer"}},"🔬 Human guidance"+(typeof reviewCount==="number"?(" ("+reviewCount+")"):"")):null,E("div",{style:{flex:1}}),(labWebStage==="audit"&&labHasResults)?E("button",{onClick:function(){goWebStage("remediate")},style:{padding:"9px 22px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.coral+")",color:"#fff",fontSize:12.5,fontWeight:800,cursor:"pointer",boxShadow:"0 2px 10px "+P.pri+"44"}},"Remediate →"):null,labWebStage==="remediate"?E("button",{onClick:function(){goWebStage("package")},style:{padding:"9px 22px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.coral+")",color:"#fff",fontSize:12.5,fontWeight:800,cursor:"pointer",boxShadow:"0 2px 10px "+P.pri+"44"}},"Package →"):null):E("div",{style:{padding:20,textAlign:"center",color:P.slateL,fontSize:12}},"Add a page in the first stage."),

      (labWebStage==="audit"&&aiLoading)?E("div",{style:{margin:"-4px 0 12px",fontSize:11,color:P.slateM,display:"flex",alignItems:"center",gap:6}},
        E("span",{style:{fontSize:13}},"\u{1F9E0}"),
        "AI reads the full page body for grounded findings — content-rich pages can take up to ~2 minutes."):null,

      /* ── The human inspection station: walk the prioritized review queue ── */
      (labWebStage==="remediate"&&reviewPanel)?(function(){
        var rp=reviewPanel;
        var total=rp.total||(rp.queue?rp.queue.length:0), reviewed=rp.reviewed||0;
        var done=!rp.loading && rp.queue && rp.idx>=rp.queue.length && rp.queue.length>0;
        var empty=!rp.loading && (!rp.queue || rp.queue.length===0);
        var cur=(!rp.loading && !done && !empty && rp.queue)?rp.queue[rp.idx]:null;
        var VC={"supports":{l:"Supports",c:P.emerald},"does-not-support":{l:"Does Not Support",c:P.sCrit},"needs-review":{l:"Needs Manual Review",c:P.amberDk},"not-applicable":{l:"Not Applicable",c:P.slateM}};
        function chip(t,c,bg){return E("span",{style:{padding:"2px 8px",borderRadius:6,fontSize:10,fontWeight:700,color:c,background:bg||(c+"1A"),border:"1px solid "+c+"40"}},t)}
        function vbtn(c,dis){return {padding:"9px 16px",borderRadius:8,border:"none",background:dis?"#CBD5E1":c,color:"#fff",fontSize:12,fontWeight:700,cursor:dis?"default":"pointer"}}
        return E("div",{style:{marginTop:16,border:"2px solid #8B5CF6",borderRadius:12,overflow:"hidden",background:"#fff"}},
          E("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"12px 16px",background:"#F5F3FF",borderBottom:"1px solid #8B5CF633"}},
            E("div",null,
              E("div",{style:{fontSize:13,fontWeight:800,color:"#5B21B6"}},"\u{1F52C} Human Inspection Station"),
              E("div",{style:{fontSize:10,color:P.slateM,marginTop:2}},"The AI's high-confidence calls auto-passed; you guide the rest — and your guidance trains it to need you less."+(rp.url?("  ·  "+rp.url):""))),
            E("button",{onClick:function(){setReviewPanel(null)},title:"Close",style:{padding:"4px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,background:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",color:P.slateM}},"✕ Close")),
          (typeof rp.autoPassCount==="number"&&rp.autoPassCount>0)?E("div",{style:{borderBottom:"1px solid "+P.bdr}},
            E("div",{onClick:function(){setReviewPanel(function(p){return p?Object.assign({},p,{autoOpen:!p.autoOpen}):p})},style:{padding:"7px 16px",background:P.emeraldLt,fontSize:10,fontWeight:700,color:P.emerald,display:"flex",alignItems:"center",gap:6,cursor:"pointer"}},
              E("span",null,(rp.autoOpen?"▾":"▸")+" ✓ Auto-passed "+rp.autoPassCount+" criteria"),
              E("span",{style:{color:P.slateM,fontWeight:600}},"— deterministic engines or AI ≥98%; click to "+(rp.autoOpen?"hide":"audit"))),
            rp.autoOpen?E("div",{style:{maxHeight:200,overflowY:"auto",padding:"4px 16px 8px",background:"#fff"}},
              (rp.autoPassed||[]).map(function(a,i){
                var fail=a.suggestedVerdict==="does-not-support",na=a.suggestedVerdict==="not-applicable";
                var basis=a.deterministic?(na?"N/A — no element":fail?"engine — FAIL":"engine"):(na?"N/A — absent":"AI ≥98%");
                if(a.scopeNote)basis="N/A — single-page scope";
                if(a.contrastFix&&a.contrastFix.suggested)basis="fix → "+a.contrastFix.suggested;
                var col=fail?P.sCrit:na?P.slateM:P.emerald;
                return E("div",{key:i,style:{display:"flex",alignItems:"center",gap:8,padding:"3px 0",fontSize:10,borderBottom:"1px solid "+P.bgS}},
                  E("span",{style:{fontWeight:700,color:P.slate,minWidth:46}},a.sc),
                  E("span",{style:{flex:1,color:P.slateM,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},a.name),
                  E("span",{style:{fontWeight:700,color:col,minWidth:66,textAlign:"right"}},fail?"✗ fail":na?"⊘ N/A":"✓ supports"),
                  E("span",{style:{color:P.slateL,fontSize:9,minWidth:88,textAlign:"right"}},basis));
              })):null):null,
          E("div",{style:{padding:"8px 16px",borderBottom:"1px solid "+P.bdr}},
            E("div",{style:{display:"flex",justifyContent:"space-between",fontSize:10,fontWeight:700,color:P.slateM,marginBottom:4}},
              E("span",null,"Guided "+reviewed+" of "+total+" needing human guidance"),
              E("span",null,done?"Complete":empty?"":("Item "+Math.min(rp.idx+1,total)+" · least-confident first"))),
            E("div",{style:{height:6,borderRadius:3,background:P.bgS,overflow:"hidden"}},
              E("div",{style:{height:"100%",width:(total?Math.round(reviewed/total*100):0)+"%",background:"linear-gradient(90deg,#8B5CF6,"+P.pri+")",transition:"width .3s"}}))),
          rp.loading?E("div",{style:{padding:24,textAlign:"center",color:P.slateM,fontSize:12}},"Loading the guidance queue…")
          :empty?E("div",{style:{padding:24,textAlign:"center",color:P.slateM,fontSize:12}},"Nothing needs your guidance — the AI auto-passed every criterion at ≥98% confidence (and engines decided the rest).")
          :done?E("div",{style:{padding:28,textAlign:"center"}},
              E("div",{style:{fontSize:30}},"✅"),
              E("div",{style:{fontSize:14,fontWeight:800,color:P.emerald,marginTop:6}},"All "+total+" guided"),
              E("div",{style:{fontSize:11,color:P.slateM,marginTop:4,maxWidth:460,marginLeft:"auto",marginRight:"auto"}},"Every item that needed human judgment now carries your call. Confirmations and overrides both feed the corpus — overrides are the strongest signal, and they raise the criteria the AI can auto-pass next time."),
              E("button",{onClick:function(){setReviewPanel(function(p){return p?Object.assign({},p,{idx:0}):p})},style:{marginTop:12,padding:"7px 14px",borderRadius:7,border:"1.5px solid "+P.bdr,background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:P.slateM}},"↺ Review again"))
          :cur?(function(){
              var g=testGuide(cur.sc);
              var sv=cur.suggestedVerdict||cur.verdict;
              var svMeta=VC[sv]||{l:(sv==="needs-human"?"Needs human judgment":sv),c:P.amberDk};
              var conf=typeof cur.confidence==="number"?Math.round(cur.confidence*100):null;
              var reasoning=cur.ai&&cur.ai.reasoning;
              var hv=cur.human&&cur.human.verdict;
              return E("div",{style:{padding:16}},
                E("div",{style:{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap"}},
                  E("span",{style:{fontSize:15,fontWeight:800,color:P.slate}},cur.sc),
                  E("span",{style:{fontSize:13,fontWeight:700,color:P.slate}},cur.name),
                  chip("Level "+cur.level,P.pri)),
                E("div",{style:{display:"flex",gap:6,flexWrap:"wrap",marginTop:8,alignItems:"center"}},
                  E("span",{style:{fontSize:10,color:P.slateL,fontWeight:600}},"AI proposes:"),
                  chip(svMeta.l,svMeta.c),
                  conf!==null?chip("Confidence "+conf+"%",conf>=98?P.emerald:conf>=70?P.amberDk:P.sCrit):null,
                  chip("below the 98% auto-pass bar",P.slateM)),
                reasoning?E("div",{style:{marginTop:8,padding:10,borderRadius:8,background:"#F5F3FF",border:"1px solid #8B5CF633"}},
                  E("div",{style:{fontSize:10,fontWeight:700,color:"#5B21B6",marginBottom:3}},"AI reasoning (uncalibrated — your guidance calibrates it)"),
                  E("div",{style:{fontSize:11,color:P.slate,lineHeight:1.45}},String(reasoning).slice(0,400))):null,
                cur.method?E("div",{style:{fontSize:10,color:P.slateM,marginTop:6,fontStyle:"italic"}},cur.method):null,
                (cur.evidence&&cur.evidence.length)?E("div",{style:{marginTop:10,padding:10,borderRadius:8,background:P.sCritBg,border:"1px solid "+P.sCrit+"33"}},
                  E("div",{style:{fontSize:10,fontWeight:700,color:P.sCrit,marginBottom:4}},"Evidence ("+cur.evidence.length+")"),
                  cur.evidence.slice(0,6).map(function(e,i){return E("div",{key:i,style:{fontSize:10,color:P.slate,marginBottom:3}},
                    "• "+String(e.desc||"").slice(0,160)+" ",
                    e.el?E("code",{key:"c",style:{fontSize:9,color:P.slateM}},"["+String(e.el).slice(0,48)+"]"):null,
                    e.engine?E("span",{key:"g",style:{marginLeft:4,fontSize:9,color:P.slateL}},"· "+e.engine):null)})
                ):null,
                E("div",{style:{marginTop:10,padding:10,borderRadius:8,background:"#F5F3FF",border:"1px solid #8B5CF633"}},
                  E("div",{style:{fontSize:10,fontWeight:700,color:"#5B21B6",marginBottom:3}},g.p+"  ·  How to verify"),
                  E("div",{style:{fontSize:11,color:P.slate,lineHeight:1.45}},g.h)),
                hv?E("div",{style:{marginTop:10,padding:"6px 10px",borderRadius:6,background:cur.overturned?P.sMajBg:P.emeraldLt,fontSize:10,fontWeight:700,color:cur.overturned?P.amberDk:P.emerald}},
                  "✓ You recorded: "+((VC[hv]||{l:hv}).l)+(cur.overturned?"  ·  ⚠ overrode the AI (a training signal)":"  ·  confirmed the AI")):null,
                (cur.contrastFix&&(cur.contrastFix.candidates||cur.contrastFix.suggested))?E("div",{style:{marginTop:10,padding:10,borderRadius:8,background:P.bgS,border:"1px solid "+P.bdr}},
                  E("div",{style:{fontSize:10,fontWeight:700,color:P.slate,marginBottom:5}},"🎨 Contrast — "+(cur.contrastFix.ratio||"?")+":1, needs "+(cur.contrastFix.required||4.5)+":1 · "+(cur.contrastFix.fixable?"small nudge":"large change — pick a colour")),
                  E("div",{style:{display:"flex",gap:8,flexWrap:"wrap"}},
                    (cur.contrastFix.candidates||[cur.contrastFix.suggested]).filter(Boolean).map(function(hex,i){return E("button",{key:i,onClick:function(){setReviewPanel(function(p){return p?Object.assign({},p,{note:"contrast fix → "+hex}):p})},title:"Pick "+hex,style:{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:6,border:"1.5px solid "+(((rp.note||"").indexOf(hex)>=0)?P.pri:P.bdr),background:"#fff",cursor:"pointer",fontSize:10,fontWeight:700}},
                      E("span",{style:{width:14,height:14,borderRadius:3,background:hex,border:"1px solid "+P.bdr}}),hex);}))):null,
                E("textarea",{value:rp.note||"",onChange:function(ev){var v=ev.target.value;setReviewPanel(function(p){return p?Object.assign({},p,{note:v}):p})},placeholder:"Guidance note (optional) — what you observed when testing",style:{width:"100%",marginTop:10,padding:8,borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:11,fontFamily:"inherit",resize:"vertical",minHeight:46,boxSizing:"border-box"}}),
                E("div",{style:{display:"flex",gap:8,flexWrap:"wrap",marginTop:10,alignItems:"center"}},
                  (sv&&sv!=="needs-human")?E("button",{onClick:function(){submitReviewVerdict(sv)},disabled:!!rp.submitting,style:vbtn("#7C3AED",!!rp.submitting)},"✓ Confirm AI — "+svMeta.l):null,
                  E("span",{style:{fontSize:10,color:P.slateL,fontWeight:600}},(sv&&sv!=="needs-human")?"or override:":"Your call:"),
                  E("button",{onClick:function(){submitReviewVerdict("supports")},disabled:!!rp.submitting,style:vbtn(P.emerald,!!rp.submitting)},"✓ Supports"),
                  E("button",{onClick:function(){submitReviewVerdict("does-not-support")},disabled:!!rp.submitting,style:vbtn(P.sCrit,!!rp.submitting)},"✗ Does Not Support"),
                  E("button",{onClick:function(){submitReviewVerdict("not-applicable")},disabled:!!rp.submitting,style:vbtn(P.slateM,!!rp.submitting)},"⊘ N/A"),
                  E("div",{style:{flex:1}}),
                  rp.idx>0?E("button",{onClick:function(){setReviewPanel(function(p){return p?Object.assign({},p,{idx:Math.max(0,p.idx-1),note:""}):p})},style:{padding:"8px 12px",borderRadius:7,border:"1.5px solid "+P.bdr,background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:P.slateM}},"← Prev"):null,
                  E("button",{onClick:function(){setReviewPanel(function(p){return p?Object.assign({},p,{idx:Math.min(p.idx+1,p.queue.length),note:""}):p})},style:{padding:"8px 12px",borderRadius:7,border:"1.5px solid "+P.bdr,background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:P.slateM}},"Skip →")));
            })()
          :null);
      })():null,

      labHasResults?E("div",{style:{marginTop:16}},
        /* Headline score + honest WCAG verdict — automated engines catch only
           part of WCAG; the manual-review list names the rest. */
        (function(){
          var axeOk=labAxeRes&&!labAxeRes.error, htmlcsOk=labHtmlcsRes&&!labHtmlcsRes.error, aiOk=aiRes&&!aiRes.error;
          var ranOk=axeOk||htmlcsOk||aiOk;                 // at least one engine produced a real result
          var errs=[labAxeRes&&labAxeRes.error?"axe-core":null,labHtmlcsRes&&labHtmlcsRes.error?"HTMLCS":null,aiRes&&aiRes.error?"Claude AI":null].filter(Boolean);
          var hasFails=(labTC+labTM+labTN)>0||labAllIssues.length>0;
          var engNames=[axeOk?"axe-core":null,htmlcsOk?"HTMLCS":null,aiOk?"Claude AI":null].filter(Boolean);
          var mrN=(aiOk&&aiRes.needsManualReview&&aiRes.needsManualReview.length)||0;
          /* NEVER green when the scanner failed: all engines errored, OR a
             "clean" result where some engine didn't complete (it might have
             caught what the others missed). A broken run is not a pass. */
          if(!ranOk || (!hasFails && errs.length>0)){
            return E("div",{style:{display:"flex",alignItems:"center",gap:14,padding:16,marginBottom:14,borderRadius:12,border:"2px solid "+P.amber+"66",background:P.sMajBg}},
              E("div",{style:{fontSize:30}},"⚠️"),
              E("div",{style:{flex:1,minWidth:0}},
                E("div",{style:{fontSize:15,fontWeight:900,color:P.amberDk}},!ranOk?"Couldn't analyze — no engine completed":"Inconclusive — some engines didn't complete"),
                E("div",{style:{fontSize:11,color:P.slateM,marginTop:3}},(errs.length?(errs.join(", ")+" failed. "):"")+"This is NOT a pass — see the per-engine errors below and re-run."),
                E("div",{style:{fontSize:10,color:P.slateL,marginTop:4}},"A page is never reported conformant because the scanner failed to run.")));
          }
          var sevW=labTC*5+labTM*2+labTN;
          var webScore=(aiOk&&typeof aiRes.score==="number")?aiRes.score:Math.max(0,100-Math.min(100,sevW));
          var clean=!hasFails;                             // reachable only when every engine that exists ran OK
          return E("div",{style:{display:"flex",alignItems:"center",gap:16,padding:16,marginBottom:14,borderRadius:12,border:"2px solid "+(clean?P.emerald:P.sCrit)+"33",background:clean?P.emeraldLt:P.sCritBg}},
            E(Ring,{v:webScore,sz:84,sw:8}),
            E("div",{style:{flex:1,minWidth:0}},
              E("div",{style:{fontSize:16,fontWeight:900,color:clean?P.emerald:P.sCrit}},clean?"✓ No automated WCAG 2.1 AA failures":"✗ WCAG 2.1 AA issues found"),
              E("div",{style:{fontSize:11,color:P.slateM,marginTop:3}},labAllIssues.length+" rule"+(labAllIssues.length===1?"":"s")+(engNames.length?(" · "+engNames.join(" · ")):"")+(mrN?(" · "+mrN+" need manual review"):"")),
              E("div",{style:{fontSize:10,color:P.slateL,marginTop:4}},"Automated engines catch ~30–40% of WCAG — the manual-review list below names what only keyboard / screen-reader testing can verify.")));
        })(),
        E("div",{className:"rg6",style:{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginBottom:14}},
          E(MC,{v:labAllIssues.length,label:"Unique Rules",c:labAllIssues.length?P.sCrit:P.emerald}),
          E(MC,{v:labTC+labTM+labTN,label:"Total Elements",c:labTC?P.sCrit:P.emerald}),
          E(MC,{v:labTC,label:"Critical",c:P.sCrit}),E(MC,{v:labTM,label:"Major",c:P.amber}),E(MC,{v:labTN,label:"Minor",c:P.sMin})),
        E("div",{style:{display:"grid",gridTemplateColumns:(labWebStage==="remediate"&&!isMobile)?"minmax(0,1.1fr) minmax(0,1fr)":"1fr",gap:14,alignItems:"start"}},
        E("div",{style:{minWidth:0}},
        labAxeRes?E("div",{style:{marginBottom:16}},E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:8}},engBadge("axe-core "+(labAxeRes.version?("v"+labAxeRes.version+" · "):"")+((labAxeRes.summary?labAxeRes.summary.total:0)+" rules"),P.pri),labAxeRes.summary?E("span",{style:{fontSize:10,color:P.slateL}},labAxeRes.summary.elements+" elements"):null),renderIssueList(labAxeRes.issues,"axe-core",P.pri)):null,
        labAxeRes&&labAxeRes.error?E("div",{style:{padding:10,borderRadius:8,background:P.sCritBg,color:P.sCrit,fontSize:12,marginBottom:12}},"\u26a0 axe error: "+labAxeRes.error):null,
        labHtmlcsRes?E("div",{style:{marginBottom:16}},E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:8}},engBadge("HTMLCS "+((labHtmlcsRes.summary?labHtmlcsRes.summary.total:0)+" rules"),"#E65100"),labHtmlcsRes.summary?E("span",{style:{fontSize:10,color:P.slateL}},labHtmlcsRes.summary.elements+" elements"):null),renderIssueList(labHtmlcsRes.issues,"HTMLCS","#E65100")):null,
        labHtmlcsRes&&labHtmlcsRes.error?E("div",{style:{padding:10,borderRadius:8,background:P.sCritBg,color:P.sCrit,fontSize:12,marginBottom:12}},"\u26a0 HTMLCS error: "+labHtmlcsRes.error):null,
        aiRes&&!aiRes.error?E("div",{style:{marginBottom:16}},E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:8}},engBadge("Claude AI "+((aiRes.issues?aiRes.issues.length:0)+" rules"),"#8B5CF6"),aiRes.score!=null?E("span",{style:{fontSize:10,color:P.slateL}},"Score: "+aiRes.score+"%"):null),aiRes.issues?renderIssueList(aiRes.issues.map(function(i){return{engine:"ai",wcag:i.wcag,sev:i.severity,name:i.name,description:i.description,el:i.element,fix:i.fix}}),"Claude AI","#8B5CF6"):null,
          /* Honesty layer: surface AI-detected patterns whose accessibility
             cannot be fully proven from static HTML (carousels, modals,
             hover-only content, focus management). What separates this
             tool from "just another scanner" — we name what only manual
             keyboard / screen-reader testing can verify. */
          aiRes.needsManualReview&&aiRes.needsManualReview.length?E("div",{style:{marginTop:14,padding:12,borderRadius:8,border:"1.5px dashed #D97706",background:"#FFFBEB"}},
            E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:8}},
              E("span",{style:{fontSize:11,fontWeight:800,color:"#92400E"}},"🔍 Needs manual review — "+aiRes.needsManualReview.length+" pattern"+(aiRes.needsManualReview.length===1?"":"s")+" detected"),
              E("span",{style:{fontSize:10,color:"#92400E",opacity:0.7}},"(complex widgets require keyboard / screen-reader testing)")),
            aiRes.needsManualReview.map(function(m,mi){return E("div",{key:"mr-"+mi,style:{padding:"8px 10px",marginBottom:6,background:"#FEF3C7",borderRadius:6,fontSize:11}},
              E("div",{style:{fontWeight:700,color:"#78350F",marginBottom:3}},
                m.category?"["+m.category+"] ":"",m.description||"Pattern detected",
                m.wcag?E("span",{style:{marginLeft:6,fontSize:10,color:"#92400E",opacity:0.7}},"WCAG "+m.wcag):null),
              m.why?E("div",{style:{color:"#78350F",marginBottom:4}},"Why automation can't verify: "+m.why):null,
              m.checkpoints&&m.checkpoints.length?E("ul",{style:{margin:"4px 0 0 16px",padding:0,color:"#78350F",lineHeight:1.5}},
                m.checkpoints.map(function(cp,ci){return E("li",{key:"cp-"+mi+"-"+ci,style:{fontSize:11}},cp);})):null);})):null
        ):null,
        aiRes&&aiRes.error?(function(){var g=classifyAiError(aiRes.error);return E("div",{style:{padding:10,borderRadius:8,background:P.sCritBg,color:P.sCrit,fontSize:12,marginBottom:12}},
          E("div",{style:{fontWeight:700,marginBottom:2}},g.title),
          E("div",{style:{fontSize:11}},g.detail),
          g.showRaw?E("pre",{style:{margin:"4px 0 0",fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap"}},typeof aiRes.error==="string"?aiRes.error:JSON.stringify(aiRes.error)):null);})():null
        ),
        /* ── Right column: the live preview, click-to-pinpoint (Remediate only) ── */
        labWebStage==="remediate"?E("div",{ref:measuredRef(setLabPvW),style:{position:"sticky",top:8,minWidth:0}},
          E("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap",marginBottom:6}},
            E("span",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"🔍 Live preview"+(hlInfo?(" — "+hlInfo.total+" match"+(hlInfo.total===1?"":"es")+" highlighted"):" — click any finding to pinpoint")),
            E("div",{style:{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}},
              deviceBar(labPvDev,setLabPvDev),
              (hlInfo&&hlInfo.total>1)?E("div",{style:{display:"flex",alignItems:"center",gap:5,fontSize:10,fontWeight:700,color:P.pri}},
                E("button",{onClick:function(){pinpointStep(-1)},title:"Previous match",style:{padding:"2px 9px",borderRadius:5,border:"1.5px solid "+P.bdr,background:"#fff",cursor:"pointer",fontWeight:800}},"‹"),
                E("span",null,(hlInfo.idx+1)+" / "+hlInfo.total),
                E("button",{onClick:function(){pinpointStep(1)},title:"Next match",style:{padding:"2px 9px",borderRadius:5,border:"1.5px solid "+P.bdr,background:"#fff",cursor:"pointer",fontWeight:800}},"›")):null)),
          labPreview?scaledViewport(labPvDev,labPvW,Math.max(380,(typeof window!=="undefined"?window.innerHeight:900)-300),function(vw,vh){
            return E("iframe",{key:"report-preview",sandbox:"allow-same-origin",ref:function(el){previewFrameRef.current=el;if(el&&el.getAttribute("data-pl")!==String(labPreview.length)){el.setAttribute("data-pl",String(labPreview.length));try{var b=new Blob([labPreview],{type:"text/html;charset=utf-8"});el.src=URL.createObjectURL(b);}catch(e){}}},style:{width:"100%",height:"100%",border:"none",background:"#fff",display:"block"}});
          })
          :E("div",{style:{padding:24,textAlign:"center",color:P.slateL,fontSize:11,border:"1.5px dashed "+P.bdr,borderRadius:10,background:P.bgS}},"No preview yet — fetch a URL or paste HTML in the Web Page tab, then run the engines."),
          labPreview?E("div",{style:{fontSize:9.5,color:P.slateL,marginTop:4,textAlign:"center"}},"Engines audit at desktop 1280px — switch views to inspect responsive layouts."):null
        ):null)
      ):aiHtml?E("div",{style:{marginTop:16,padding:24,textAlign:"center",borderRadius:12,border:"1.5px dashed "+P.bdr,background:P.bgS}},
        E("div",{style:{fontSize:28,marginBottom:8}},"⚡"),
        E("div",{style:{fontSize:13,fontWeight:700,marginBottom:4}},"Ready to analyze"),
        E("div",{style:{fontSize:11,color:P.slateM}},"Click an engine above — or Run All Engines — to audit this page for WCAG 2.1 AA.")
      ):null,

      /* Funnel handoff: after a single-page audit, offer bulk scanning in Watchtower
         (PA11Y = learn the process on one asset → Watchtower = remediate the whole site). */
      labHasResults?E("div",{style:{marginTop:16,paddingTop:16,borderTop:"1.5px solid "+P.bdr}},
        E("div",{style:{padding:"14px 16px",borderRadius:12,background:P.priLt+"55",border:"1px solid "+P.pri+"33",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}},
          E("div",null,
            E("div",{style:{fontSize:12.5,fontWeight:800,color:P.pri}},"Now do this for your whole site"),
            E("div",{style:{fontSize:11,color:P.slateM,marginTop:2}},"You've audited one page. PiARIS Watchtower scans — and remediates — every page in bulk.")),
          E("button",{onClick:function(){setView("app");setMod("webAudit")},style:{padding:"9px 16px",borderRadius:9,border:"none",background:P.pri,color:"#fff",fontSize:12,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap"}},"Continue in Watchtower →"))):null,

      /* PR-6.1: per-image alt-text triage — Decorative / Write / AI (sends only that
         one image to /api/image-alt). Auto-detected from the page's WCAG 1.1.1 issues. */
      renderWebAltTriage(),

      aiHtml&&labHasResults?E("div",{style:{marginTop:16,paddingTop:16,borderTop:"1.5px solid "+P.bdr}},
        E("div",{style:{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}},
          E("button",{onClick:labRunAiFix,disabled:aiLoading,style:{padding:"10px 20px",borderRadius:8,border:"none",background:aiLoading?"#ccc":"linear-gradient(135deg,#8B5CF6,"+P.emerald+")",color:"#fff",fontSize:12,fontWeight:700,cursor:aiLoading?"wait":"pointer"}},aiLoading?"\u23f3 Generating fix...":"\u{1F9E0} AI Build Fix"),
          aiFixed?E("button",{onClick:downloadFixed,style:{padding:"10px 20px",borderRadius:8,border:"none",background:P.emerald,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}},"\u2B07 Download Fixed HTML"):null,
          aiFixed?E("button",{onClick:function(){setAiHtml(aiFixed);setLabPreview(aiFixed);setLabAxeRes(null);setLabHtmlcsRes(null);setAiRes(null);setAiFixed("")},style:{padding:"10px 16px",borderRadius:8,border:"1.5px solid "+P.pri,background:P.priLt,color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer"}},"\u21bb Load Fix > Re-test"):null,
          aiFixed?E("span",{style:{padding:"6px 14px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u2713 AI-Remediated HTML visible in right pane"):null),
        aiFixed&&!labShowPreview?E("div",{style:{marginTop:8}},
          E("button",{onClick:function(){setLabShowPreview(true)},style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.emerald+"40",background:P.emeraldLt,color:P.emerald,fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{1F50D} Show Split-Pane Comparison")):null):null,

      /* Alt Text AI — folded into Web Audit remediation */
      labHasResults?E("div",{style:{marginTop:16,paddingTop:16,borderTop:"1.5px solid "+P.bdr}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10}},
          E("span",{style:{fontSize:16}},"\u{1F5BC}"),
          E("h3",{style:{margin:0,fontSize:14,fontWeight:800,color:P.accent}},"Alt Text AI"),
          E("span",{style:{fontSize:10,color:P.slateL}},"Generate alt text for images on this page")),
        E("div",{style:{display:"flex",gap:12,flexWrap:"wrap",alignItems:"start"}},
          E("div",{style:{flex:"0 0 auto"}},
            E("label",{style:{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 20px",borderRadius:8,border:"2px dashed "+P.bdr,background:P.bgS,cursor:"pointer",fontSize:12,fontWeight:600,color:P.slateM}},"\u{1F4F7} Choose Image",E("input",{type:"file",accept:"image/*",onChange:handleImgUpload,style:{display:"none"}})),
            aiImg?E("div",{style:{marginTop:8}},E("img",{src:aiImg.preview,alt:"Upload preview",style:{maxWidth:200,maxHeight:150,borderRadius:8,border:"1.5px solid "+P.bdr}}),E("div",{style:{fontSize:10,color:P.slateL,marginTop:4}},aiImg.name)):null),
          E("div",{style:{flex:1,minWidth:200}},
            aiImg?E("button",{onClick:generateAltText,disabled:aiLoading,style:{padding:"10px 24px",borderRadius:8,border:"none",background:aiLoading?"#ccc":"linear-gradient(135deg,"+P.accent+","+P.emerald+")",color:"#fff",fontSize:12,fontWeight:700,marginBottom:12,cursor:aiLoading?"wait":"pointer"}},aiLoading?"Analyzing image...":"\u{1F9E0} Generate Alt Text"):null,
            aiAlt&&aiAlt.alt?E("div",null,
              E(Card,{sx:{background:P.emeraldLt,border:"1.5px solid "+P.emerald+"30",marginBottom:8}},E("div",{style:{fontSize:10,fontWeight:700,color:P.emerald,textTransform:"uppercase",marginBottom:4}},"Generated Alt Text"),E("div",{style:{fontSize:14,fontWeight:600,color:P.slate,fontFamily:FC,padding:"8px 0"}},"\""+aiAlt.alt+"\""),E("div",{style:{fontSize:10,color:P.slateL}},"Length: "+aiAlt.alt.length+" chars "+(aiAlt.alt.length<=125?"\u2713 Under 125":"Over 125"))),
              E(Card,{sx:{marginTop:8,background:P.bgS}},E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,marginBottom:4}},"Copy-paste ready:"),E("pre",{style:{margin:0,fontSize:11,fontFamily:FC,whiteSpace:"pre-wrap"}},"<img src=\"your-image.jpg\" alt=\""+aiAlt.alt+"\""+(aiAlt.longDesc?" aria-describedby=\"desc-1\"":"")+">")))
            :null))
      ):null
    ):null
  );

  /* ═══════════════════════════════════════════════════════════════
   * MODULE: pa11y-lab-doc-ui                      [status: ready]
   * PURPOSE: React render tree for PA11YLab Doc Audit module
   *
   * READS:     labDocTab, labSession, aiPdf, pdfStructRes, aiPdfRes,
   *            pdfEngines, pdfRemForm, pdfRemStatus, labRunning,
   *            labDocs, labSelDoc, labSessions, aiImg, aiAlt, P, aiLoading
   * WRITES:    None (calls setters via onClick handlers)
   * CALLS:     handlePdfUpload, handlePdfUploadAdd, labDownloadPdf,
   *            labClearPdf, labRunAllPdf, labRunPdfStructural, analyzePdf,
   *            labRemediatePdf, setLabDocTab, setLabSelDoc, savePageSession,
   *            loadPageSession, navigateTo, generateAltText, handleImgUpload,
   *            setPdfEngines, setPdfRemForm
   * EFFECTS:   iframe creation via ref callback (PDF blob URL)
   * EMITS:     DOM content within <main>
   *
   * EXPORTS:   JSX/E tree — when extracted becomes <Pa11yLabDoc /> component
   * IMPORTS:   All READS above as props + all CALLS as handlers
   *
   * CLOSURE:   Uses E = React.createElement
   * ENTANGLED: Shares SubViewHero, Card, MC, engBadge, renderIssueList
   *            with pa11y-lab-web-ui. Also shares Alt Text AI block which
   *            duplicates logic with web-ui — consider a shared <AltTextAi/>.
   * ═══════════════════════════════════════════════════════════════ */

  /* ═══ PA11Y LAB — DOC AUDIT MODULE ═══ */
  var aiLabDoc = E("div",{className:"pa11yStageShell"},
    E(SubViewHero,{
      product:"a11ylab",
      title:"PA11Y Lab \u2014 Doc Audit",
      subtitle:labSession?"\u{1F552} "+new Date(labSession.createdAt).toLocaleString("en-US",{year:"numeric",month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})+(labSession.sourceFileName?" \u2014 "+labSession.sourceFileName:""):"Upload a PDF to run structural checks and AI analysis.",
      right:E("div",{style:{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}},
        pdfStructRes&&!pdfStructRes.error?engBadge("Structural","#fff"):null,
        aiPdfRes&&!aiPdfRes.error?engBadge("Claude AI","#fff"):null,
        labSession&&!labSession.saved?E("button",{onClick:savePageSession,style:{padding:"4px 12px",borderRadius:6,border:"1px solid rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"},title:"Sessions auto-save while you work. Click to keep this session beyond its 24h default expiration."},"\u{1F4BE} Keep session"):null,
        labSession&&labSession.saved?E("span",{style:{fontSize:10,color:"rgba(255,255,255,0.85)",fontWeight:700}},"\u2713 Saved"):null,
        Object.keys(labRunning).length>0?E("span",{style:{fontSize:10,color:"rgba(255,255,255,0.85)",alignSelf:"center"}},"\u23f3 Running..."):null)
    }),
    labDocChevrons,

    /* UPLOAD stage — choose a PDF, pick a recent session, add documents */
    labDocStage==="add"?E(Card,{sx:{borderTop:"2px solid "+P.coral,borderRadius:"0 12px 12px 12px"}},
      /* PR-5.13: top-of-tab AI availability hint. Surfaces BEFORE
         the user uploads a PDF so they know upfront that AI Analysis
         won't run in this environment. The engine selector
         (post-upload) also shows the UNAVAILABLE badge; this is the
         pre-upload mirror so the signal isn't gated on the upload
         step. data-testid pins it for the e2e suite. */
      aiAvailable===false?E("div",{
        "data-testid":"pa11y-ai-unavailable-hint",
        role:"status",
        style:{display:"flex",gap:10,padding:"10px 14px",borderRadius:8,
          background:P.amberLt,border:"1px solid "+(P.amber)+"40",
          color:P.amberDk||P.amber,fontSize:11,fontWeight:600,
          marginBottom:12,alignItems:"flex-start"}},
        E("span",{style:{fontSize:14}},"ⓘ"),
        E("div",{style:{flex:1,lineHeight:1.5}},
          E("strong",null,"AI Analysis engine is unavailable in this environment."),
          " Structural Checks will still run on any PDF you upload. ",
          "Set ",E("code",{style:{fontFamily:FC,background:"#fff8",padding:"1px 4px",borderRadius:3}},"ANTHROPIC_API_KEY"),
          " in the Function App configuration to enable AI Analysis.")
      ):null,
      /* Recent Sessions */
      labSessions.length>0?E("details",{style:{marginBottom:14}},
        E("summary",{style:{fontSize:11,fontWeight:700,color:P.slateM,cursor:"pointer",marginBottom:6}},"\u{1F4C2} Recent Doc Sessions ("+labSessions.length+")"),
        E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
          labSessions.map(function(s){return E("button",{key:s.id,onClick:function(){_labSessionId.current=s.id;loadPageSession(s.id);navigateTo("aiLabDoc",s.id,labDocTab);setView("app")},
            style:{padding:"6px 12px",borderRadius:8,border:"1.5px solid "+(s.id===(_labSessionId.current||"")?"#EA580C":P.bdr),background:s.id===(_labSessionId.current||"")?P.bgS:P.bgS,fontSize:10,fontWeight:600,cursor:"pointer",color:P.slateM,textAlign:"left",maxWidth:200}},
            E("div",{style:{fontWeight:700,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},s.sourceFileName||s.sourceUrl||s.id),
            E("div",{style:{fontSize:9,color:P.slateL}},new Date(s.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}))
          )}))
      ):null,
      /* Upload row */
      E("div",{style:{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap",marginBottom:12}},
        E("label",{style:{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 20px",borderRadius:8,border:"2px dashed "+P.bdr,background:P.bgS,cursor:"pointer",fontSize:12,fontWeight:600,color:P.slateM}},"\u{1F4C4} Choose PDF",E("input",{type:"file",accept:".pdf,.docx,.xlsx,.pptx",onChange:handlePdfUpload,style:{display:"none"}})),
        aiPdf?E("span",{style:{fontSize:11,color:P.slateM}},"\u2713 "+aiPdf.name):null,
        aiPdf?E("button",{onClick:labDownloadPdf,style:{padding:"6px 12px",borderRadius:6,border:"1.5px solid "+P.emerald+"40",background:P.emeraldLt,fontSize:10,fontWeight:700,color:P.emerald,cursor:"pointer"}},"\u2B07 Download"):null,
        aiPdf?E("button",{onClick:labClearPdf,style:{padding:"6px 12px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600,cursor:"pointer"}},"\u{1F5D1} Clear"):null),

      /* Engine checkboxes */
      aiPdf?E("div",{style:{display:"flex",gap:16,alignItems:"center",flexWrap:"wrap",marginBottom:14,padding:"10px 14px",borderRadius:8,background:P.bgS,border:"1.5px solid "+P.bdr}},
        E("span",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"Audit:"),
        /* "Include AI analysis" checkbox removed \u2014 AI Audit retired. */
        E("div",{style:{flex:1}}),
        /* PR-5.13: per-engine status badges + spinner button text.
           While engines are in flight, the button shows "\u23f3 Running\u2026"
           and badges next to it show per-engine progress so the user
           can see exactly which engine is doing what (and which
           failed). Previously the button stayed labelled "Run
           Selected Engines" for the full ~30\u201360s of structural+AI
           processing with no feedback whatsoever. */
        labRunning.pdfStruct?E("span",{style:{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:P.priLt,color:P.priDk||P.pri}},"\u23f3 Structural"):
          pdfStructRes&&!pdfStructRes.error?E("span",{style:{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:P.emeraldLt,color:P.emeraldDk||P.emerald}},"\u2713 Structural"):
          pdfStructRes&&pdfStructRes.error?E("span",{style:{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:P.coralLt,color:P.coralDk||P.coral}},"\u2717 Structural"):null,
        aiLoading?E("span",{style:{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:"#8B5CF620",color:"#8B5CF6"}},"\u23f3 AI"):
          aiPdfRes&&!aiPdfRes.error?E("span",{style:{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:P.emeraldLt,color:P.emeraldDk||P.emerald}},"\u2713 AI"):
          aiPdfRes&&aiPdfRes.error?E("span",{style:{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:P.coralLt,color:P.coralDk||P.coral}},"\u2717 AI"):null,
        E("button",{onClick:labRunAllPdf,disabled:Object.keys(labRunning).length>0||aiLoading,style:{padding:"8px 20px",borderRadius:8,border:"none",background:Object.keys(labRunning).length>0||aiLoading?"#ccc":"linear-gradient(135deg,"+P.coral+","+P.amber+")",color:"#fff",fontSize:12,fontWeight:700,cursor:Object.keys(labRunning).length>0||aiLoading?"wait":"pointer"}},
          Object.keys(labRunning).length>0||aiLoading?"\u23f3 Auditing\u2026":"\u26a1 Run Audit")):null,

      /* PDF Preview */
      aiPdf?E("div",{style:{marginBottom:14}},
        E("div",{style:{fontSize:11,fontWeight:700,color:P.slateM,marginBottom:6}},"\u{1F50D} PDF Preview"),
        E("iframe",{ref:function(el){
          if(el&&aiPdf&&aiPdf.data){
            var bytes=atob(aiPdf.data);var arr=new Uint8Array(bytes.length);for(var i=0;i<bytes.length;i++)arr[i]=bytes.charCodeAt(i);
            var blob=new Blob([arr],{type:"application/pdf"});var url=URL.createObjectURL(blob);
            if(el.getAttribute("data-pdf")!==aiPdf.name){el.src=url;el.setAttribute("data-pdf",aiPdf.name);}
          }
        },style:{width:"100%",height:500,border:"1.5px solid "+P.bdr,borderRadius:8}})):null,

      /* Add Document button */
      labDocs.length>0?E("div",{style:{marginTop:12}},
        E("label",{style:{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:8,border:"1.5px dashed "+P.bdr,background:P.bgS,cursor:"pointer",fontSize:11,fontWeight:600,color:P.slateM}},"\u2795 Add Another Document",E("input",{type:"file",accept:".pdf",onChange:handlePdfUploadAdd,style:{display:"none"}})),
        labDocs.length>1?E("div",{style:{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}},
          labDocs.map(function(d,di){return E("button",{key:di,onClick:function(){setLabSelDoc(di)},style:{padding:"4px 12px",borderRadius:6,border:"1.5px solid "+(di===labSelDoc?P.coral:P.bdr),background:di===labSelDoc?P.card:P.bgS,fontSize:11,fontWeight:di===labSelDoc?700:500,cursor:"pointer",color:di===labSelDoc?P.coral:P.slateL}},d.name||"Doc "+(di+1))})):null
      ):null
    ):null,

    /* AUDIT / REMEDIATE / PACKAGE stages — results, pinpoint viewer, workbench */
    labDocStage!=="add"?E(Card,{sx:{borderTop:"2px solid "+P.coral,borderRadius:"0 12px 12px 12px"}},
      /* PR-5.13: only show the empty-state copy when the session has
         NEITHER PDF bytes in memory NOR any prior audit results. On
         a reopened session the bytes are gone (we don't persist them
         yet — that's PR-5.14) but the results survive, and the
         previous unconditional `!aiPdf` check rendered "Upload a PDF
         in the Document tab first." directly above a fully-populated
         Structural summary, which was the contradiction the discovery
         pass caught. */
      (!aiPdf && !pdfStructRes && !aiPdfRes)?E("div",{style:{padding:20,textAlign:"center",color:P.slateL,fontSize:12}},"Upload a PDF in the Document tab first."):null,

      /* PR-5.13 follow-up: in-flight progress on the REPORT view. The
         engine badges live on the Document tab's run row, but the run
         auto-routes here — so during the 20-40s AI pass the report was
         a blank, frozen-looking screen. Surface a live status banner
         where the user actually lands. */
      (aiLoading||Object.keys(labRunning).length>0)?E("div",{role:"status",
        style:{display:"flex",gap:10,padding:"12px 14px",borderRadius:10,
          background:P.priLt,border:"1px solid "+P.pri+"40",color:P.priDk||P.pri,
          alignItems:"center",marginBottom:14}},
        E("span",null,"⏳"),
        E("span",{style:{fontSize:12,fontWeight:600}},
          aiLoading?"Running AI Analysis… image-heavy PDFs can take 20–40s":"Running checks…")):null,

      /* PR-5.13: top-of-report engine-failure banner. The previous
         AI-error UI rendered a tiny pink one-liner BELOW the
         Remediation Workbench, which was the lowest-visibility
         spot on the entire report — users would scroll past the
         Workbench and miss it entirely. Surface engine failures
         at the TOP of the report instead, with the error message
         + a retry affordance. Currently surfaces the AI engine
         failure; same pattern is ready to take Structural failures
         too if those ever need their own banner. */
      aiPdfRes && aiPdfRes.error ? (function(){
        /* Map the raw upstream error to plain-language guidance so the
           banner stops saying "set ANTHROPIC_API_KEY" when the key is
           fine and the account is simply out of credit (PR-5.16). */
        var g=classifyAiError(aiPdfRes.error);
        return E("div",{role:"alert",
        style:{display:"flex",gap:10,padding:"12px 14px",borderRadius:10,
          background:P.coralLt,border:"1px solid "+(P.coral)+"40",
          color:P.coralDk||P.coral,alignItems:"flex-start",
          marginBottom:14,flexWrap:"wrap"}},
        E("span",{style:{fontSize:18}},"⚠"),
        E("div",{style:{flex:"1 1 320px",fontSize:12,lineHeight:1.5}},
          E("div",{"data-testid":"pa11y-ai-error-title",style:{fontWeight:700,marginBottom:4}},g.title),
          E("div",{style:{fontSize:11,marginTop:2}},g.detail),
          /* Raw upstream text only when we couldn't classify it — keeps
             the unknown-error case debuggable without dumping JSON at
             users for the known billing/auth/rate cases. */
          g.showRaw?E("details",{style:{marginTop:6}},
            E("summary",{style:{fontSize:10,cursor:"pointer",color:P.slateM}},"Technical detail"),
            E("pre",{style:{margin:"4px 0 0",fontSize:10,fontFamily:FC,whiteSpace:"pre-wrap"}},
              typeof aiPdfRes.error==="string"?aiPdfRes.error:JSON.stringify(aiPdfRes.error,null,2))):null),
        aiAvailable!==false&&g.kind!=="auth"?E("button",{type:"button",onClick:function(){analyzePdf()},
          disabled:aiLoading,
          style:{padding:"6px 12px",borderRadius:6,border:"1px solid "+P.coral,
            background:"#fff",color:P.coralDk||P.coral,fontSize:11,fontWeight:700,
            cursor:aiLoading?"wait":"pointer"}},
          aiLoading?"⏳ Retrying…":"↻ Retry AI"):null
      );})() : null,

      /* Structural Results */
      pdfStructRes&&!pdfStructRes.error?E("div",{style:{marginBottom:16}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}},
          engBadge("Structural & AI Checks",P.pri),E("span",{style:{fontSize:10,color:P.slateL}},"Score: "+pdfStructRes.score+"%"),
          pdfStructRes.summary.remediable?E("span",{style:{fontSize:10,padding:"2px 8px",borderRadius:10,background:P.emeraldLt,color:P.emerald,fontWeight:700}},pdfStructRes.summary.remediable+" fixable here"):null),
        /* Expanded check grid */
        E("div",{className:"rg6",style:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8,marginBottom:12}},
          E(MC,{v:pdfStructRes.checks.tagged?"\u2713":"No",label:"Tagged",c:pdfStructRes.checks.tagged?P.emerald:P.sCrit}),
          E(MC,{v:pdfStructRes.checks.structTree?"\u2713":"No",label:"Struct Tree",c:pdfStructRes.checks.structTree?P.emerald:P.sCrit}),
          E(MC,{v:pdfStructRes.checks.title?"\u2713":"No",label:"Title",c:pdfStructRes.checks.title?P.emerald:P.amber}),
          E(MC,{v:pdfStructRes.checks.lang?"\u2713":"No",label:"Language",c:pdfStructRes.checks.lang?P.emerald:P.amber}),
          E(MC,{v:pdfStructRes.checks.bookmarks?"\u2713":"No",label:"Bookmarks",c:pdfStructRes.checks.bookmarks?P.emerald:P.sMin}),
          E(MC,{v:pdfStructRes.checks.headings?"\u2713":"No",label:"Headings",c:pdfStructRes.checks.headings?P.emerald:P.amber}),
          E(MC,{v:pdfStructRes.checks.altCoverage!=null?pdfStructRes.checks.altCoverage+"%":"N/A",label:"Alt Text",c:pdfStructRes.checks.altCoverage>=100?P.emerald:pdfStructRes.checks.altCoverage>0?P.amber:P.sCrit}),
          E(MC,{v:pdfStructRes.checks.displayTitle?"\u2713":"No",label:"Show Title",c:pdfStructRes.checks.displayTitle?P.emerald:P.sMin}),
          E(MC,{v:pdfStructRes.checks.fontsEmbedded?"\u2713":"?",label:"Fonts",c:pdfStructRes.checks.fontsEmbedded?P.emerald:P.slateM}),
          E(MC,{v:pdfStructRes.checks.pageCount||"?",label:"Pages",c:P.slateM}),
          E(MC,{v:"v"+pdfStructRes.checks.pdfVersion,label:"PDF Ver",c:P.slateM}),
          E(MC,{v:Math.round(pdfStructRes.checks.fileSize/1024)+"K",label:"Size",c:P.slateM})),
        /* Metadata detail */
        E("div",{style:{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10,fontSize:10,color:P.slateM}},
          pdfStructRes.checks.titleValue?E("span",null,"Title: "+pdfStructRes.checks.titleValue):null,
          pdfStructRes.checks.langValue?E("span",null," | Lang: "+pdfStructRes.checks.langValue):null,
          pdfStructRes.checks.author?E("span",null," | Author: "+pdfStructRes.checks.author):null,
          pdfStructRes.checks.producer?E("span",null," | Producer: "+pdfStructRes.checks.producer):null),
        /* Remediate stage gets the side-by-side inline viewer (findings left,
           sticky PDF preview right); Audit/Package keep the stacked layout. */
        (labDocStage==="remediate"&&!isMobile)?E("div",{style:{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1.05fr)",gap:14,alignItems:"start",marginBottom:6}},
          E("div",{style:{minWidth:0}},
            E("div",{style:{fontSize:11,fontWeight:700,color:P.slateM,marginBottom:6}},"📋 Findings — click an instance to pinpoint it on the page"),
            renderGroupedIssueList(pdfStructRes.issues,"Structural",P.pri,{groupBy:"page"})),
          E("div",{style:{position:"sticky",top:8,minWidth:0}},renderLabPdfViewer())
        ):E("div",null,
          renderLabPdfViewer(),
          renderGroupedIssueList(pdfStructRes.issues,"Structural",P.pri,{groupBy:"page"})),

        /* REMEDIATION PANEL */
        (pdfStructRes.issues&&pdfStructRes.issues.length)?E("div",{style:{marginTop:16,padding:16,borderRadius:10,border:"2px solid "+P.emerald+"30",background:"linear-gradient(135deg,"+P.emeraldLt+",#fff)"}},
          E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:12}},
            E("span",{style:{fontSize:16}},"\u{1F527}"),
            E("h3",{style:{margin:0,fontSize:14,fontWeight:800,color:P.emerald}},"Remediation Workbench"),
            E("span",{style:{fontSize:10,color:P.slateL}},"Resolve issues by type, then build the remediated document")),
          E("div",{style:{fontSize:11,fontWeight:800,color:P.slate,marginBottom:6}},"📋 Metadata"),
          pdfRemForm?E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}},
            E("div",null,
              E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:2}},"Document Title *"),
              E("input",{value:pdfRemForm.title,onChange:function(ev){setPdfRemForm(function(p){return Object.assign({},p,{title:ev.target.value})})},placeholder:"Enter document title",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+(pdfRemForm.title?P.emerald+"40":P.sCrit+"40"),fontSize:12,background:P.card}})),
            E("div",null,
              E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:2}},"Language *"),
              E("select",{value:pdfRemForm.lang,onChange:function(ev){setPdfRemForm(function(p){return Object.assign({},p,{lang:ev.target.value})})},style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+(pdfRemForm.lang?P.emerald+"40":P.sCrit+"40"),fontSize:12,background:P.card}},
                E("option",{value:"en-US"},"English (US)"),E("option",{value:"en-GB"},"English (UK)"),E("option",{value:"es"},"Spanish"),E("option",{value:"fr"},"French"),E("option",{value:"de"},"German"),E("option",{value:"zh"},"Chinese"),E("option",{value:"ja"},"Japanese"),E("option",{value:"ko"},"Korean"),E("option",{value:"pt"},"Portuguese"),E("option",{value:"it"},"Italian"))),
            E("div",null,
              E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:2}},"Author"),
              E("input",{value:pdfRemForm.author,onChange:function(ev){setPdfRemForm(function(p){return Object.assign({},p,{author:ev.target.value})})},placeholder:"Document author",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:12,background:P.card}})),
            E("div",null,
              E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:2}},"Subject"),
              E("input",{value:pdfRemForm.subject,onChange:function(ev){setPdfRemForm(function(p){return Object.assign({},p,{subject:ev.target.value})})},placeholder:"Document subject/description",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:12,background:P.card}})),
            E("div",{style:{gridColumn:"1 / -1"}},
              E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:2}},"Keywords"),
              E("input",{value:pdfRemForm.keywords,onChange:function(ev){setPdfRemForm(function(p){return Object.assign({},p,{keywords:ev.target.value})})},placeholder:"keyword1, keyword2, keyword3",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:12,background:P.card}}))
          ):E("div",{style:{fontSize:11,color:P.slateL,marginBottom:8}},"No metadata fixes needed."),
          renderDocImageTriage(),
          renderDocStructureGuidance(),
          E("div",{style:{display:"flex",gap:8,marginTop:12,alignItems:"center",flexWrap:"wrap"}},
            /* PR-6.6c: primary action. One click runs the full pipeline:
               metadata bake -> Adobe Auto-Tag -> alt injection -> re-audit ->
               download. Available to all signed-in users; the server
               meters the Adobe call against the user's tagged-PDF
               credits (Free / PA11Y-only get a clean upgrade prompt). */
              E("button",{onClick:runUnifiedRemediation,disabled:!!(remPipeline.step!=="idle"&&remPipeline.step!=="done"&&remPipeline.step!=="error")||!aiPdf||!aiPdf.data,
                title:"Bake metadata, tag the PDF (Adobe), inject your approved alt text, then re-audit and download the remediated file.",
                style:{padding:"10px 24px",borderRadius:8,border:"none",
                  background:(remPipeline.step!=="idle"&&remPipeline.step!=="done"&&remPipeline.step!=="error")?"#ccc":"linear-gradient(135deg,#8B5CF6,"+P.emerald+")",
                  color:"#fff",fontSize:12,fontWeight:700,
                  cursor:(remPipeline.step!=="idle"&&remPipeline.step!=="done"&&remPipeline.step!=="error")?"wait":"pointer"}},
                (remPipeline.step==="idle"||remPipeline.step==="done"||remPipeline.step==="error")
                  ? "\u2728 Build fully-remediated PDF"
                  : "\u23f3 "+(remPipeline.step==="metadata"?"Baking metadata\u2026":remPipeline.step==="remediate"?"Remediating (tag + alt + audit)\u2026":remPipeline.step==="autotag"?"Tagging (Adobe)\u2026":remPipeline.step==="inject"?"Injecting alt text\u2026":remPipeline.step==="audit"?"Re-auditing\u2026":"Working\u2026")),
            /* "Apply metadata only" removed \u2014 the full build bakes metadata too. */
            (function(){
              var ready=!!remResult;   /* the full remediation has completed */
              return E("button",{onClick:buildRemediationReport,disabled:!ready,
                title:ready?"Download an HTML remediation report (before/after + per-figure decisions)":"Build the remediated PDF first \u2014 then the report is available",
                style:{padding:"10px 20px",borderRadius:8,border:"1.5px solid "+(ready?P.pri:P.bdr),background:ready?P.priLt:P.bgS,color:ready?P.pri:P.slateL,fontSize:12,fontWeight:700,cursor:ready?"pointer":"not-allowed",opacity:ready?1:0.55}},"\ud83d\udcc4 Download remediation report");
            })(),
            pdfRemStatus==="done"?E("span",{style:{fontSize:11,color:P.emerald,fontWeight:700}},"\u2713 Metadata applied! Re-run checks to verify."):null,
            pdfRemStatus&&pdfRemStatus.indexOf("error")===0?E("span",{style:{fontSize:11,color:P.sCrit}},pdfRemStatus):null),
          /* PR-6.6c: pipeline progress + before/after diff panel. Renders
             only when the full-remediation button has been clicked. */
          (remPipeline.step!=="idle")?E("div",{style:{marginTop:12,padding:12,borderRadius:8,border:"1.5px solid "+(remPipeline.step==="error"?P.sCrit:remPipeline.step==="done"?P.emerald:"#8B5CF6"),background:remPipeline.step==="error"?P.sCritBg:remPipeline.step==="done"?P.emeraldLt:"#F5F3FF"}},
            E("div",{style:{fontSize:11,fontWeight:700,color:remPipeline.step==="error"?(remPipeline.error==="upgrade_needed"?P.amber||"#D97706":P.sCrit):remPipeline.step==="done"?P.emerald:"#6D28D9",marginBottom:8}},
              remPipeline.step==="error"?(remPipeline.error==="upgrade_needed"?"\u2728 Upgrade needed for tagged PDFs":"\u26a0 Remediation failed"):remPipeline.step==="done"?"\u2713 Remediation complete":"\u23f3 Building fully-remediated PDF\u2026"),
            /* Tagged-PDF credit gate (402): clean upgrade prompt rather
               than a raw error string. */
            (remPipeline.error==="upgrade_needed")?E("div",{style:{fontSize:12,color:P.slateM,marginBottom:8,padding:8,borderRadius:6,background:P.card,border:"1px solid "+P.bdr}},
              remPipeline.upgradeMsg||"Building a fully-tagged PDF requires tagged-PDF credits.",
              remPipeline.upgrade?E("div",{style:{marginTop:6,fontWeight:700,color:P.pri}},"Recommended plan: "+(remPipeline.upgrade==="watchtower_remediate"?"Watchtower \u2014 Scan + Remediate":remPipeline.upgrade==="standard"?"Standard":remPipeline.upgrade)):null,
              E("div",{style:{marginTop:4,fontSize:11,color:P.slateL}},"Contact your administrator or Pi Consultants to upgrade.")):null,
            (remPipeline.progress||[]).map(function(p,i){
              var icon=p.state==="done"?"\u2713":p.state==="error"?"\u2717":p.state==="loading"?"\u23f3":"\u25cb";
              var color=p.state==="done"?P.emerald:p.state==="error"?P.sCrit:p.state==="loading"?"#8B5CF6":P.slateL;
              return E("div",{key:"ps-"+i,style:{display:"flex",gap:8,fontSize:11,color:P.slateM,marginBottom:3}},
                E("span",{style:{color:color,fontWeight:700,width:14}},icon),
                E("span",{style:{fontWeight:600,minWidth:80}},p.label),
                p.detail?E("span",{style:{color:P.slateL}},"\u2014 "+p.detail):null);
            }),
            (remPipeline.error&&remPipeline.error!=="upgrade_needed")?E("div",{style:{fontSize:11,color:P.sCrit,marginTop:6}},remPipeline.error):null,
            /* Before/after diff card \u2014 shown only after a successful run */
            (remPipeline.step==="done"&&remResult)?E("div",{style:{marginTop:12,padding:10,borderRadius:6,background:P.card,border:"1px solid "+P.bdr}},
              E("div",{style:{fontSize:12,fontWeight:700,color:P.navy||P.slateD||"#0F172A",marginBottom:6}},"\ud83d\udcca Before vs After"),
              E("div",{style:{fontSize:12,color:P.slateM,marginBottom:8}},remResult.diff.summary),
              E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:11}},
                E("div",{style:{padding:8,borderRadius:6,background:P.sCritBg}},
                  E("div",{style:{fontWeight:700,color:P.sCrit,marginBottom:3}},"Before: "+remResult.diff.counts.before.total+" issues"),
                  E("div",{style:{color:P.slateM}},remResult.diff.counts.before.critical+" critical \u00b7 "+remResult.diff.counts.before.major+" major \u00b7 "+remResult.diff.counts.before.minor+" minor")),
                E("div",{style:{padding:8,borderRadius:6,background:P.emeraldLt}},
                  E("div",{style:{fontWeight:700,color:P.emerald,marginBottom:3}},"After: "+remResult.diff.counts.after.total+" issues"),
                  E("div",{style:{color:P.slateM}},remResult.diff.counts.after.critical+" critical \u00b7 "+remResult.diff.counts.after.major+" major \u00b7 "+remResult.diff.counts.after.minor+" minor"))),
              E("div",{style:{fontSize:11,color:P.slateL,marginTop:8}},
                "Score: "+(remResult.before.score||0)+" \u2192 "+(remResult.after.score||0)+" ("+(remResult.diff.scoreDelta>=0?"+":"")+remResult.diff.scoreDelta+")  \u00b7  "+
                remResult.finalName+" ("+Math.round(remResult.finalBytes/1024)+" KB)"),
              /* HONEST figure coverage: of the N Figure tags Adobe
                 emitted, how many now have /Alt. This is the
                 PDF/UA-relevant metric. The pre-remediation 'altCoverage'
                 from auditPdfBase64 counts every /Subtype /Image in the
                 PDF (including decorative artifacts that aren't Figure-
                 tagged and don't need alts), so it lowballs the result
                 \u2014 a 4-page PDF with charts can have 60+ image XObjects
                 but only 25 Figure tags. Show both so the user has the
                 honest picture. */
              E("div",{style:{fontSize:11,color:P.slateM,marginTop:8,padding:8,borderRadius:6,background:P.bgS}},
                E("div",{style:{fontWeight:700,color:P.emerald,marginBottom:3}},
                  "\u2713 Figure alt coverage: "+(remResult.figuresFound>0?Math.round(remResult.injectedCount/remResult.figuresFound*100):100)+"% \u2014 "+remResult.injectedCount+" of "+remResult.figuresFound+" Adobe-tagged figure"+(remResult.figuresFound===1?"":"s")+" got alt text"),
                (remResult.workbenchFigureCount>remResult.figuresFound)?
                  E("div",{style:{fontSize:10,color:P.slateL,marginTop:3}},
                    "Note: Workbench detected "+remResult.workbenchFigureCount+" images but Adobe Auto-Tag consolidated to "+remResult.figuresFound+" Figure tags (the rest were treated as decorative artifacts, no alt needed)."):null,
                (remResult.injectSkipped>0)?
                  E("div",{style:{fontSize:10,color:P.amber||"#D97706",marginTop:3}},
                    "\u26a0 "+remResult.injectSkipped+" decision"+(remResult.injectSkipped===1?"":"s")+" could not be matched to a surviving figure."):null),
              /* Per-page breakdown when page-aware matching ran */
              remResult.perPage?E("div",{style:{fontSize:11,color:P.slateM,marginTop:8}},
                E("div",{style:{fontWeight:700,marginBottom:4}},"Per-page breakdown:"),
                Object.keys(remResult.perPage).sort(function(a,b){return Number(a)-Number(b);}).map(function(pgKey){
                  var pg=remResult.perPage[pgKey];
                  var allMatched=pg.injected===pg.decisions&&pg.skipped===0;
                  return E("div",{key:"pp-"+pgKey,style:{fontSize:11,color:allMatched?P.emerald:P.slateM,marginBottom:2}},
                    (allMatched?"\u2713":"\u00b7")+" Page "+pgKey+": "+pg.injected+" of "+pg.decisions+" decisions landed"+
                    (pg.skipped>0?" ("+pg.skipped+" couldn't match \u2014 Adobe had "+pg.figures+" figure"+(pg.figures===1?"":"s")+" on this page)":"")
                  );
                })):null,
              /* Persistent re-download button so a blocked download
                 (file already open with same name, browser interrupted,
                 etc.) can be retried without burning another Adobe
                 Auto-Tag call. */
              E("button",{onClick:redownloadRemediated,
                style:{marginTop:8,padding:"6px 14px",borderRadius:6,border:"1.5px solid "+P.emerald,background:P.emeraldLt,color:P.emerald,fontSize:11,fontWeight:700,cursor:"pointer"}},
                "\u2b07 Download remediated PDF again"),
              remResult.diff.counts.remaining>0?E("div",{style:{fontSize:11,color:P.slateM,marginTop:8,paddingTop:8,borderTop:"1px solid "+P.bdr}},
                E("b",null,remResult.diff.counts.remaining+" issue"+(remResult.diff.counts.remaining===1?"":"s")+" still remaining \u2014 these need Acrobat-level fixes (run veraPDF when available)")):null):null,
            E("div",{style:{fontSize:10,color:P.slateL,marginTop:8}},"Metadata baked. Adobe Auto-Tag builds the struct tree + figure tags. Alt injection writes your decisions into those figures. Final file is the fully-remediated PDF.")):null
        ):null
      ):pdfStructRes&&pdfStructRes.error?E("div",{style:{padding:10,borderRadius:8,background:P.sCritBg,color:P.sCrit,fontSize:12,marginBottom:12}},"\u26a0 "+pdfStructRes.error):null,

      /* AI Results */
      aiPdfRes&&!aiPdfRes.error?E("div",{style:{marginBottom:16}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10}},engBadge("Claude AI","#8B5CF6"),aiPdfRes.score!=null?E("span",{style:{fontSize:10,color:P.slateL}},"Score: "+aiPdfRes.score+"%"):null),
        E("div",{className:"rg6",style:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(100px,1fr))",gap:8,marginBottom:12}},
          E(MC,{v:aiPdfRes.tagged?"Yes":"No",label:"Tagged",c:aiPdfRes.tagged?P.emerald:P.sCrit}),
          E(MC,{v:aiPdfRes.pdfua||"--",label:"PDF/UA",c:aiPdfRes.pdfua==="pass"?P.emerald:P.sCrit}),
          E(MC,{v:aiPdfRes.certification||"--",label:"Cert",c:aiPdfRes.certification==="Compliant"?P.emerald:P.sCrit}),
          E(MC,{v:aiPdfRes.summary?aiPdfRes.summary.total:0,label:"AI Issues",c:P.sCrit})),
        E("div",{style:{padding:"8px 12px",borderRadius:8,background:aiPdfRes.certification==="Compliant"?P.emeraldLt:P.sCritBg,border:"1.5px solid "+(aiPdfRes.certification==="Compliant"?P.emerald:P.sCrit)+"20",marginBottom:12,fontSize:12,fontWeight:700,color:aiPdfRes.certification==="Compliant"?P.emerald:P.sCrit}},aiPdfRes.certification==="Compliant"?"\u2713 Meets WCAG 2.1 AA":"\u2717 Does NOT meet WCAG 2.1 AA -- "+((aiPdfRes.summary&&aiPdfRes.summary.critical)||0)+" critical"),
        aiPdfRes.issues?renderIssueList(aiPdfRes.issues.map(function(i){return{engine:"ai",wcag:i.wcag,sev:i.severity,cat:i.category,description:i.description,remediation:i.remediation}}),"Claude AI","#8B5CF6"):null,
        /* PDF-side honesty layer (mirror of web). Items only a human
           keyboard / screen-reader pass on the actual document can
           verify — complex tables, math equations, multi-column reading
           order, footnote linkage, color-only meaning. Renders only
           when the AI returned this field. */
        aiPdfRes.needsManualReview&&aiPdfRes.needsManualReview.length?E("div",{style:{marginTop:14,padding:12,borderRadius:8,border:"1.5px dashed #D97706",background:"#FFFBEB"}},
          E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:8}},
            E("span",{style:{fontSize:11,fontWeight:800,color:"#92400E"}},"🔍 Needs manual review — "+aiPdfRes.needsManualReview.length+" pattern"+(aiPdfRes.needsManualReview.length===1?"":"s")+" detected"),
            E("span",{style:{fontSize:10,color:"#92400E",opacity:0.7}},"(complex content requires a screen-reader pass on the actual PDF)")),
          aiPdfRes.needsManualReview.map(function(m,mi){return E("div",{key:"mrp-"+mi,style:{padding:"8px 10px",marginBottom:6,background:"#FEF3C7",borderRadius:6,fontSize:11}},
            E("div",{style:{fontWeight:700,color:"#78350F",marginBottom:3}},
              m.category?"["+m.category+"] ":"",m.description||"Pattern detected",
              m.wcag?E("span",{style:{marginLeft:6,fontSize:10,color:"#92400E",opacity:0.7}},"WCAG "+m.wcag):null),
            m.why?E("div",{style:{color:"#78350F",marginBottom:4}},"Why automation can't verify: "+m.why):null,
            m.checkpoints&&m.checkpoints.length?E("ul",{style:{margin:"4px 0 0 16px",padding:0,color:"#78350F",lineHeight:1.5}},
              m.checkpoints.map(function(cp,ci){return E("li",{key:"cpp-"+mi+"-"+ci,style:{fontSize:11}},cp);})):null);})):null):null,

      /* Funnel handoff (doc): after a single-doc audit, offer bulk doc remediation in Watchtower. */
      docHasResults?E("div",{style:{marginTop:16,paddingTop:16,borderTop:"1.5px solid "+P.bdr}},
        E("div",{style:{padding:"14px 16px",borderRadius:12,background:P.priLt+"55",border:"1px solid "+P.pri+"33",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}},
          E("div",null,
            E("div",{style:{fontSize:12.5,fontWeight:800,color:P.pri}},"Have a folder of PDFs?"),
            E("div",{style:{fontSize:11,color:P.slateM,marginTop:2}},"You've remediated one document. PiARIS Watchtower audits and remediates your whole library in bulk.")),
          E("button",{onClick:function(){setView("app");setMod("docAudit")},style:{padding:"9px 16px",borderRadius:9,border:"none",background:P.pri,color:"#fff",fontSize:12,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap"}},"Continue in Watchtower →"))):null,
      /* PR-5.13: the AI error used to render a tiny pink one-liner
         HERE at the bottom of the report. Moved to the top-of-report
         banner above (search for "AI Analysis engine did not
         complete") so users actually see it. */

      /* PR-6.5: the standalone "Alt Text AI" upload tool is retired — alt text is
         now resolved per-image in the Remediation Workbench's Images section above.
         Gated off here; the dead block is removed in the follow-up cleanup. */
      false?E("div",{style:{marginTop:16,paddingTop:16,borderTop:"1.5px solid "+P.bdr}},
        E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:10}},
          E("span",{style:{fontSize:16}},"\u{1F5BC}"),
          E("h3",{style:{margin:0,fontSize:14,fontWeight:800,color:P.accent}},"Alt Text AI"),
          E("span",{style:{fontSize:10,color:P.slateL}},"Generate alt text for images in this document")),
        E("div",{style:{display:"flex",gap:12,flexWrap:"wrap",alignItems:"start"}},
          E("div",{style:{flex:"0 0 auto"}},
            E("label",{style:{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 20px",borderRadius:8,border:"2px dashed "+P.bdr,background:P.bgS,cursor:"pointer",fontSize:12,fontWeight:600,color:P.slateM}},"\u{1F4F7} Choose Image",E("input",{type:"file",accept:"image/*",onChange:handleImgUpload,style:{display:"none"}})),
            aiImg?E("div",{style:{marginTop:8}},E("img",{src:aiImg.preview,alt:"Upload preview",style:{maxWidth:240,maxHeight:180,borderRadius:8,border:"1.5px solid "+P.bdr}}),E("div",{style:{fontSize:10,color:P.slateL,marginTop:4}},aiImg.name)):null),
          E("div",{style:{flex:1,minWidth:200}},
            aiImg?E("button",{onClick:generateAltText,disabled:aiLoading,style:{padding:"10px 24px",borderRadius:8,border:"none",background:aiLoading?"#ccc":"linear-gradient(135deg,"+P.accent+","+P.emerald+")",color:"#fff",fontSize:12,fontWeight:700,marginBottom:12,cursor:aiLoading?"wait":"pointer"}},aiLoading?"Analyzing image...":"\u{1F9E0} Generate Alt Text"):null,
            aiAlt&&aiAlt.alt?E("div",null,
              E(Card,{sx:{background:P.emeraldLt,border:"1.5px solid "+P.emerald+"30",marginBottom:8}},E("div",{style:{fontSize:10,fontWeight:700,color:P.emerald,textTransform:"uppercase",marginBottom:4}},"Generated Alt Text"),E("div",{style:{fontSize:14,fontWeight:600,color:P.slate,fontFamily:FC,padding:"8px 0"}},"\""+aiAlt.alt+"\""),E("div",{style:{fontSize:10,color:P.slateL}},"Length: "+aiAlt.alt.length+" chars "+(aiAlt.alt.length<=125?"\u2713 Under 125":"Over 125"))),
              aiAlt.longDesc?E(Card,{sx:{marginBottom:8}},E("div",{style:{fontSize:10,fontWeight:700,color:P.pri,textTransform:"uppercase",marginBottom:4}},"Long Description"),E("div",{style:{fontSize:12,color:P.slate,lineHeight:1.6}},aiAlt.longDesc)):null,
              E("div",{style:{display:"flex",gap:8}},E("div",{style:{padding:"4px 10px",borderRadius:6,background:aiAlt.isDecorative?P.sMajBg:P.emeraldLt,fontSize:10,fontWeight:700,color:aiAlt.isDecorative?P.sMaj:P.emerald}},aiAlt.isDecorative?"Decorative":"Informative"),E("div",{style:{padding:"4px 10px",borderRadius:6,background:P.priLt,fontSize:10,fontWeight:700,color:P.pri}},"Confidence: "+(aiAlt.confidence||"--"))),
              E(Card,{sx:{marginTop:8,background:P.bgS}},E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,marginBottom:4}},"Copy-paste ready:"),E("pre",{style:{margin:0,fontSize:11,fontFamily:FC,whiteSpace:"pre-wrap"}},"<img src=\"your-image.jpg\" alt=\""+aiAlt.alt+"\""+(aiAlt.longDesc?" aria-describedby=\"desc-1\"":"")+">")))
            :null))
      ):null
    ):null
  );

  /* ═══ REPORT CENTER ═══ */
  var rptDate=new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
  var rptTime=new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"});
  var RPT_TABS=[
    {id:"executive",l:"Executive Summary",i:"\u{1F3DB}"},
    {id:"webCompliance",l:"Web Compliance",i:"\u{1F310}"},
    {id:"docCompliance",l:"Document Compliance",i:"\u{1F4C4}"},
    {id:"checkpoint",l:"Checkpoint Detail",i:"\u2611\uFE0F"},
    {id:"remediation",l:"Remediation Workbook",i:"\u{1F527}"},
    {id:"certification",l:"Certification",i:"\u{1F3C5}"},
    {id:"trend",l:"Trend Analysis",i:"\u{1F4C8}"}
  ];

  /* Download current report as HTML */
  /** Download printable HTML report from the rendered report content */
  function downloadReport(title){
    var el=document.getElementById("rpt-content");
    if(!el)return;
    var html='<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>'+title+' - PiARIS Report\u00A9 Pi Consultants</title><style>body{font-family:Inter,system-ui,sans-serif;max-width:1100px;margin:0 auto;padding:24px;color:#1E293B;font-size:13px;line-height:1.6}h1{font-size:22px}h2{font-size:16px;border-bottom:2px solid #E2E8F0;padding-bottom:6px;margin-top:24px}h3{font-size:13px}table{width:100%;border-collapse:collapse;margin:12px 0;font-size:11px}th,td{border:1px solid #E2E8F0;padding:6px 10px;text-align:left}th{background:#F8FAFC;font-weight:700}.pass{color:#059669;font-weight:700}.fail{color:#DC2626;font-weight:700}.warn{color:#D97706;font-weight:700}.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700}.crit{background:#FEF2F2;color:#DC2626}.maj{background:#FFFBEB;color:#D97706}.min{background:#EFF6FF;color:#2563EB}.score-circle{display:inline-flex;align-items:center;justify-content:center;width:64px;height:64px;border-radius:50%;font-size:22px;font-weight:900;color:#fff}.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #4F46E5;padding-bottom:16px;margin-bottom:24px}.stamp{border:3px solid;padding:8px 16px;border-radius:8px;font-size:14px;font-weight:900;text-transform:uppercase;letter-spacing:.1em}.footer{text-align:center;padding:24px 0;border-top:1px solid #E2E8F0;margin-top:32px;font-size:10px;color:#94A3B8}@media print{body{font-size:11px}}</style></head><body>'+el.innerHTML+'<div class="footer">\u00A9 2026 Pi Consultants. All rights reserved. | pi-consulting.us | PiARIS\u2122 Accessibility Remediation Intelligence &amp; Scanning</div></body></html>';
    var b=new Blob([html],{type:"text/html"});
    var a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="piaris-"+title.toLowerCase().replace(/\s+/g,"-")+"-"+Date.now()+".html";a.click();
  }
  function downloadJSON(){
    var out={platform:"PiARIS",vendor:"Pi Consultants",website:"pi-consulting.us",version:"3.0",exported:new Date().toISOString(),domain:domain,standards:["WCAG 2.1 AA","WCAG 2.2 AA","Section 508","PDF/UA","ADA Title II","EAA EN 301 549"],overallScore:overallScore,totalAssets:wSt.n+dSt.n,totalIssues:totalI,remediated:totalFixed,
      webPages:wp.map(function(p){return{name:p.n,url:p.url,depth:p.d,issues:p.totalIssues,critical:p.critCount,major:p.majCount,minor:p.minCount,wcagsFailed:p.wcagsFailed,details:p.issues.map(function(i){return{wcag:i.wcag,criterion:i.info.n,severity:i.sev,instances:i.count,description:i.desc,element:i.el,fix:i.fix,remediation:i.info.r}})}}),
      documents:dc.map(function(d){return{name:d.name,pages:d.pages,tagged:d.tagged,producer:d.producer,priority:d.priority,monthlyViews:d.views,issues:d.totalIssues,critical:d.critCount,major:d.majCount,minor:d.minCount,details:d.issues.map(function(i){return{wcag:i.wcag,criterion:i.info.n,severity:i.sev,instances:i.count,description:i.desc,remediation:i.info.r}}),validation:(Array.isArray(d.valResults)&&d.valResults.length)?d.valResults.map(function(v){return{category:v.cat.id,score:v.score,passed:v.passed,total:v.total,checkpoints:v.results.map(function(r){return{check:r.check,pass:r.pass}})}}):undefined}})};
    var b=new Blob([JSON.stringify(out,null,2)],{type:"application/json"});
    var a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="paris-full-export-"+Date.now()+".json";a.click();
  }

  /* Shared report header */
  function rptHeader(title,subtitle){
    return E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",borderBottom:"3px solid "+P.pri,paddingBottom:16,marginBottom:20}},
      E("div",null,
        E("div",{style:{display:"flex",alignItems:"center",gap:8,marginBottom:6}},
          E(PiArisLockup,{size:"small"}),
          E("span",{style:{fontSize:10,color:P.slateL,fontWeight:600,letterSpacing:".05em"}},"v"+APP_VERSION+" \u2014 "+APP_FULL+((typeof window!=="undefined"&&window.__PARIS_BUILD__&&window.__PARIS_BUILD__.sha&&window.__PARIS_BUILD__.sha!=="dev")?(" \u00b7 "+window.__PARIS_BUILD__.sha):""))),
        E("h1",{style:{fontSize:18,fontWeight:800,margin:"4px 0 2px"}},title),
        E("div",{style:{fontSize:11,color:P.slateM}},subtitle)),
      E("div",{style:{textAlign:"right",fontSize:10,color:P.slateL}},
        E("div",null,E("strong",null,"Domain: "),domain||"N/A"),
        E("div",null,E("strong",null,"Generated: "),rptDate+" "+rptTime),
        E("div",null,E("strong",null,"Standards: "),"WCAG 2.1 AA \u00B7 Section 508 \u00B7 PDF/UA"),
        E("div",null,E("strong",null,"Report ID: "),"PAR-"+Math.random().toString(36).substr(2,8).toUpperCase()),
        E("div",{style:{marginTop:4,fontSize:10,color:P.slateL}},"\u00A9 2026 "+PI_NAME)));
  }
  function rptSection(title,children){
    return E("div",{style:{marginBottom:20}},
      E("h2",{style:{fontSize:14,fontWeight:800,borderBottom:"2px solid "+P.bdr,paddingBottom:6,margin:"20px 0 10px",color:P.slate}},title),
      children);
  }
  function rptTable(headers,rows,opts){
    opts=opts||{};
    return E("div",{style:{overflowX:"auto",marginBottom:12}},
      E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:10.5}},
        E("thead",null,E("tr",null,headers.map(function(h,i){
          return E("th",{key:i,style:{background:P.bgS,border:"1px solid "+P.bdr,padding:"6px 10px",fontWeight:700,fontSize:10,textAlign:opts.align&&opts.align[i]||"left",whiteSpace:"nowrap"}},h)}))),
        E("tbody",null,rows.map(function(row,ri){
          return E("tr",{key:ri,style:{background:ri%2===0?P.card:"#FAFBFE"}},row.map(function(cell,ci){
            return E("td",{key:ci,style:{border:"1px solid "+P.bdr,padding:"5px 10px",fontSize:10.5,textAlign:opts.align&&opts.align[ci]||"left"}},cell)}))}))));
  }
  function sevBadge(s){
    var c=s==="critical"?[P.sCrit,P.sCritBg]:s==="major"?[P.amber,"#FFFBEB"]:[" #2563EB","#EFF6FF"];
    return E("span",{style:{padding:"1px 7px",borderRadius:4,background:c[1],color:c[0],fontSize:10,fontWeight:700}},s);
  }
  function pfBadge(pass){return E("span",{style:{fontWeight:700,fontSize:10,color:pass?"#059669":"#DC2626"}},pass?"\u2713 PASS":"\u2717 FAIL")}
  function scoreCircle(score,size){
    size=size||56;
    var col=score>=80?P.emerald:score>=50?P.amber:P.sCrit;
    return E("div",{style:{width:size,height:size,borderRadius:"50%",background:col,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:size*.36,fontWeight:900,color:"#fff"}},score);
  }

  /* === 1. EXECUTIVE SUMMARY === */
  var allWcagMap={};
  wp.forEach(function(p){p.issues.forEach(function(i){allWcagMap[i.wcag]=(allWcagMap[i.wcag]||0)+i.count})});
  dc.forEach(function(d){d.issues.forEach(function(i){allWcagMap[i.wcag]=(allWcagMap[i.wcag]||0)+i.count})});
  var topWcagAll=Object.entries(allWcagMap).sort(function(a,b){return b[1]-a[1]}).slice(0,10);
  var webPass=wp.filter(function(p){return p.critCount===0}).length;
  var docPass=dc.filter(function(d){return d.critCount===0}).length;
  var compRate=Math.round((webPass+docPass)/Math.max(1,wp.length+dc.length)*100);

  var rptExec=E("div",{id:"rpt-content"},
    rptHeader("Executive Summary Report","Organization-wide accessibility compliance overview across all digital assets"),
    /* KPIs row */
    E("div",{style:{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:20}},
      [{v:overallScore+"%",l:"Remediation Rate",c:overallScore>=50?P.emerald:P.sCrit},{v:compRate+"%",l:"Compliance Rate",c:compRate>=70?P.emerald:P.sCrit},{v:wSt.n+dSt.n,l:"Total Assets",c:P.slate},{v:totalI.toLocaleString(),l:"Total Issues",c:P.sCrit},{v:(wSt.tC+dSt.tC).toLocaleString(),l:"Critical Issues",c:P.sCrit},{v:totalFixed.toLocaleString(),l:"Remediated",c:P.emerald}].map(function(k,i){
        return E("div",{key:i,style:{textAlign:"center",padding:10,borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card}},
          E("div",{style:{fontSize:20,fontWeight:900,color:k.c}},k.v),
          E("div",{style:{fontSize:10.5,color:P.slateL,marginTop:2,fontWeight:600}},k.l));})),
    rptSection("Compliance Status by Asset Type",
      rptTable(["Asset Type","Total","Passing (0 Critical)","Failing","Compliance Rate","Issues","Critical"],
        [["Web Pages",wSt.n,webPass,wSt.n-webPass,Math.round(webPass/Math.max(1,wSt.n)*100)+"%",wSt.tI,wSt.tC],
         ["PDF Documents",dSt.n,docPass,dSt.n-docPass,Math.round(docPass/Math.max(1,dSt.n)*100)+"%",dSt.tI,dSt.tC],
         [E("strong",null,"TOTAL"),wSt.n+dSt.n,webPass+docPass,(wSt.n-webPass)+(dSt.n-docPass),compRate+"%",totalI,wSt.tC+dSt.tC]],
        {align:["left","center","center","center","center","center","center"]})),
    rptSection("Top 10 Most Violated WCAG Criteria (Combined Web + Documents)",
      rptTable(["Rank","WCAG","Criterion Name","Instances","Severity Impact"],
        topWcagAll.map(function(w,i){return[i+1,"WCAG "+w[0],W[w[0]]?W[w[0]].n:"Uncategorized",w[1],W[w[0]]?W[w[0]].r.substring(0,80)+"...":""]}))),
    rptSection("Severity Distribution",
      E("div",{style:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}},
        [{s:"Critical",c:P.sCrit,bg:P.sCritBg,v:wSt.tC+dSt.tC},{s:"Major",c:P.amber,bg:"#FFFBEB",v:wSt.tMa+dSt.tMa},{s:"Minor",c:"#2563EB",bg:"#EFF6FF",v:wSt.tMi+dSt.tMi}].map(function(x){
          return E("div",{key:x.s,style:{padding:14,borderRadius:8,background:x.bg,border:"1.5px solid "+x.c+"20",textAlign:"center"}},
            E("div",{style:{fontSize:28,fontWeight:900,color:x.c}},x.v),
            E("div",{style:{fontSize:10,fontWeight:700,color:x.c,marginTop:2}},x.s+" Issues"),
            E("div",{style:{fontSize:10,color:P.slateM,marginTop:4}},Math.round(x.v/Math.max(1,totalI)*100)+"% of total"))}))),
    rptSection("Document Authoring Analysis",
      rptTable(["Authoring Application","Documents","Tagged","Untagged","Issues"],
        (function(){var apps={};dc.forEach(function(d){var k=d.producer;if(!apps[k])apps[k]={n:0,t:0,u:0,i:0};apps[k].n++;if(d.tagged)apps[k].t++;else apps[k].u++;apps[k].i+=d.totalIssues});return Object.entries(apps).sort(function(a,b){return b[1].n-a[1].n}).map(function(e){return[e[0],e[1].n,e[1].t,e[1].u?E("span",{style:{color:P.sCrit,fontWeight:700}},e[1].u):0,e[1].i]})})())),
    rptSection("Standards Applicability",
      rptTable(["Standard","Version","Applicability","Status"],
        [["WCAG","2.1 Level AA","Web + PDF","Testing Complete"],["WCAG","2.2 Level AA","Web + PDF","Testing Complete"],["Section 508","Revised 2017","Federal compliance","Testing Complete"],["PDF/UA","ISO 14289-1","PDF documents","Testing Complete"],["ADA Title II","2024 Rule","State/local gov","Applicable"],["EAA","EN 301 549","EU compliance","Applicable"]])),
    /* Template Deduplication Analysis */
    templateStats.templateGroupCount>0?rptSection("Template Deduplication Analysis",
      E("div",null,
        E("div",{style:{padding:14,borderRadius:8,background:P.priLt,border:"1.5px solid #C7D2FE",marginBottom:12}},
          E("div",{style:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,textAlign:"center"}},
            [{v:totalI.toLocaleString(),l:"Total Instances",c:P.sCrit},
             {v:templateStats.actionableCount,l:"Actionable Fixes",c:"#4F46E5"},
             {v:templateStats.templateGroupCount,l:"Template Fixes",c:"#6366F1"},
             {v:templateStats.pageSpecificCount,l:"Page-Specific",c:P.slate}].map(function(k,i){
              return E("div",{key:i},E("div",{style:{fontSize:20,fontWeight:900,color:k.c}},k.v),
                E("div",{style:{fontSize:10,color:P.slateM,fontWeight:600}},k.l))}))),
        E("div",{style:{fontSize:11,color:P.slate,lineHeight:1.6,marginBottom:10}},
          "Template issues are accessibility failures in shared page components (headers, footers, navigation) that repeat identically across multiple pages. Fixing the template source resolves all instances simultaneously. "+
          "This site has "+templateStats.templateGroupCount+" template patterns generating "+templateStats.templateInstanceCount.toLocaleString()+" of the "+totalI.toLocaleString()+" total instances. "+
          "Only "+templateStats.actionableCount+" distinct fixes are needed."),
        rptTable(["Template Issue","WCAG","Severity","Pages Affected","Instances","Fix Strategy"],
          Object.keys(templateStats.groups)
            .filter(function(k){return templateStats.groups[k].pages.length>1&&templateStats.groups[k].isSpecific})
            .sort(function(a,b){return templateStats.groups[b].pages.length-templateStats.groups[a].pages.length})
            .slice(0,10)
            .map(function(k){
              var g=templateStats.groups[k];
              return[(g.info&&g.info.n)||g.desc.substring(0,60),g.wcag,sevBadge(g.sev),
                g.pages.length+"/"+wp.length,g.count,
                "Fix once in "+(g.nSel&&g.nSel.indexOf("header")>-1?"header":g.nSel&&g.nSel.indexOf("footer")>-1?"footer":g.nSel&&g.nSel.indexOf("nav")>-1?"navigation":"shared template")+" \u2192 resolves all "+g.count]})))):null,
    /* Fix Classification Methodology */
    rptSection("Fix Classification Methodology",
      E("div",null,
        E("div",{style:{fontSize:11,color:P.slate,lineHeight:1.6,marginBottom:10}},
          "PiARIS classifies each issue into a fix category to determine the optimal remediation approach. "+
          "Categories marked \u2018PiARIS Engine\u2019 are resolved by deterministic rule-based fix scripts (contrast calculations, ARIA attributes, HTML structure corrections). "+
          "Categories marked \u2018AI-Powered\u2019 require contextual understanding (image descriptions, heading rewording, link purpose inference) and are handled by Claude Vision and language models."),
        rptTable(["Category","Description","Default Engine","Typical WCAG Criteria"],
          [["Color / Contrast","Insufficient text-to-background contrast ratios","PiARIS Engine","1.4.3, 1.4.11"],
           ["ARIA / Roles","Missing or incorrect ARIA attributes, roles, states","PiARIS Engine","4.1.2, 4.1.3"],
           ["HTML Parsing","Duplicate IDs, malformed nesting, validation errors","PiARIS Engine","4.1.1"],
           ["Language","Missing or incorrect lang attributes","PiARIS Engine","3.1.1, 3.1.2"],
           ["Semantic Structure","Missing landmarks, reading order, list structure","PiARIS Engine","1.3.1, 1.3.2, 2.4.1"],
           ["Alt Text","Missing or inadequate image descriptions","AI-Powered","1.1.1"],
           ["Link Purpose","Vague link text (\u2018click here\u2019, \u2018read more\u2019)","AI-Powered","2.4.4"],
           ["Headings","Incorrect heading hierarchy or vague heading text","AI-Powered","2.4.6, 1.3.1"],
           ["Form Labels","Missing or unclear form field labels","AI-Powered","3.3.2, 1.3.1"],
           ["Other","Issues not matching above categories","PiARIS Engine","Various"]]),
        E("div",{style:{fontSize:10,color:P.slateM,fontStyle:"italic",marginTop:6}},
          "When both PiARIS Engine and AI produce fixes for the same issue, the default engine\u2019s fix is pre-selected. Auditors can override by accepting the alternative fix or providing a manual override in the workbench.")))
  );

  /* === 2. WEB COMPLIANCE === */
  var rptWeb=E("div",{id:"rpt-content"},
    rptHeader("Web Compliance Report","Page-by-page WCAG 2.1 AA compliance status for "+domain),
    E("div",{style:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}},
      [{v:wSt.n,l:"Pages Scanned"},{v:wSt.tI,l:"Total Issues"},{v:wSt.wc,l:"WCAG Criteria Failed"},{v:webPass+"/"+wSt.n,l:"Pages Passing"}].map(function(k,i){
        return E("div",{key:i,style:{textAlign:"center",padding:10,borderRadius:8,border:"1.5px solid "+P.bdr}},
          E("div",{style:{fontSize:18,fontWeight:900}},k.v),E("div",{style:{fontSize:10,color:P.slateL}},k.l))})),
    rptSection("Page Compliance Matrix",
      rptTable(["Page","URL","Depth","Issues","Critical","Major","Minor","Status"],
        wp.map(function(p){return[E("strong",null,p.n),E("span",{style:{fontFamily:FC,fontSize:10}},p.url),p.d,p.totalIssues,
          p.critCount>0?E("span",{style:{color:P.sCrit,fontWeight:700}},p.critCount):0,
          p.majCount,p.minCount,
          p.critCount===0?E("span",{style:{color:P.emerald,fontWeight:700}},"\u2713 PASS"):E("span",{style:{color:P.sCrit,fontWeight:700}},"\u2717 FAIL")]}),
        {align:["left","left","center","center","center","center","center","center"]})),
    rptSection("WCAG Criteria Breakdown (Web)",
      rptTable(["WCAG","Criterion","Pages Affected","Total Instances","Remediation Guidance"],
        wSt.tw.map(function(w){return["WCAG "+w[0],W[w[0]]?W[w[0]].n:"—",wp.filter(function(p){return p.issues.some(function(i){return i.wcag===w[0]})}).length,w[1],W[w[0]]?W[w[0]].r:""]})))
  );

  /* === 3. DOCUMENT COMPLIANCE === */
  var sortedDcRpt=useMemo(function(){return dc.slice().sort(function(a,b){return b.totalIssues-a.totalIssues})},[dc]);
  var rptDocSel=rptDoc!==null?sortedDcRpt[rptDoc]:null;

  var rptDocPage=rptDocSel?E("div",{id:"rpt-content"},
    rptHeader("PDF Compliance Report: "+rptDocSel.name,"Individual document checkpoint analysis with certification status"),
    E("button",{onClick:function(){setRptDoc(null)},style:{padding:"4px 12px",borderRadius:6,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,marginBottom:12}},"\u2190 Back to document list"),
    /* Document metadata */
    E("div",{style:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}},
      [{l:"File",v:rptDocSel.name},{l:"Pages",v:rptDocSel.pages},{l:"Producer",v:rptDocSel.producer},{l:"Tagged",v:rptDocSel.tagged?"\u2713 Yes":"\u2717 No"},{l:"Total Issues",v:rptDocSel.totalIssues},{l:"Critical",v:rptDocSel.critCount},{l:"Monthly Views",v:rptDocSel.views.toLocaleString()},{l:"Priority",v:rptDocSel.priority}].map(function(k,i){
        return E("div",{key:i,style:{padding:8,borderRadius:6,border:"1px solid "+P.bdr,background:P.card}},
          E("div",{style:{fontSize:10,color:P.slateL,fontWeight:600,textTransform:"uppercase"}},k.l),
          E("div",{style:{fontSize:12,fontWeight:700,marginTop:2}},k.v))})),
    /* Certification stamp */
    E("div",{style:{textAlign:"center",margin:"16px 0",padding:16,borderRadius:10,border:"3px solid "+(rptDocSel.critCount===0?P.emerald:P.sCrit),background:rptDocSel.critCount===0?"#F0FDF4":"#FEF2F2"}},
      E("div",{style:{fontSize:16,fontWeight:900,color:rptDocSel.critCount===0?P.emerald:P.sCrit}},rptDocSel.critCount===0?"\u2713 COMPLIANT":"\u2717 NON-COMPLIANT"),
      E("div",{style:{fontSize:10,color:P.slateM,marginTop:4}},"WCAG 2.1 AA \u00B7 Section 508 \u00B7 PDF/UA Assessment"),
      E("div",{style:{fontSize:10,color:P.slateL,marginTop:4}},"Assessed: "+rptDate+" \u00B7 Report ID: PIA-"+Math.random().toString(36).substr(2,8).toUpperCase())),
    /* Checkpoint results */
    rptSection("Checkpoint-by-Checkpoint Results",
      (Array.isArray(rptDocSel.valResults)&&rptDocSel.valResults.length)?rptDocSel.valResults.map(function(vr,vi){
        return E("div",{key:vi,style:{marginBottom:12}},
          E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",borderRadius:"6px 6px 0 0",background:vr.score===100?"#F0FDF4":vr.score>=60?"#FFFBEB":"#FEF2F2",border:"1px solid "+P.bdr}},
            E("strong",{style:{fontSize:11}},vr.cat.id.toUpperCase()+" — "+vr.cat.checks.length+" checkpoints"),
            E("span",{style:{fontSize:11,fontWeight:700,color:vr.score===100?"#059669":vr.score>=60?P.amber:P.sCrit}},vr.score+"%  ("+vr.passed+"/"+vr.total+" passed)")),
          E("table",{style:{width:"100%",borderCollapse:"collapse",fontSize:10.5}},
            E("thead",null,E("tr",null,
              E("th",{style:{background:P.bgS,border:"1px solid "+P.bdr,padding:"4px 8px",width:"60%"}},"Checkpoint"),
              E("th",{style:{background:P.bgS,border:"1px solid "+P.bdr,padding:"4px 8px",width:"20%",textAlign:"center"}},"Result"),
              E("th",{style:{background:P.bgS,border:"1px solid "+P.bdr,padding:"4px 8px",width:"20%",textAlign:"center"}},"Action Required"))),
            E("tbody",null,vr.results.map(function(r,ri){
              return E("tr",{key:ri,style:{background:ri%2===0?P.card:"#FAFBFE"}},
                E("td",{style:{border:"1px solid "+P.bdr,padding:"4px 8px"}},r.check),
                E("td",{style:{border:"1px solid "+P.bdr,padding:"4px 8px",textAlign:"center"}},pfBadge(r.pass)),
                E("td",{style:{border:"1px solid "+P.bdr,padding:"4px 8px",textAlign:"center",fontSize:10}},r.pass?"None":"Remediate"))}))));
      }):E("div",{style:{color:P.slateM,fontSize:11}},"No validation data available")),
    /* Issue details */
    rptSection("Issue Details",
      rptTable(["#","WCAG","Criterion","Severity","Instances","Description","Remediation"],
        rptDocSel.issues.map(function(iss,i){return[i+1,"WCAG "+iss.wcag,iss.info.n,sevBadge(iss.sev),iss.count,iss.desc,iss.info.r]})))
  ):E("div",{id:"rpt-content"},
    rptHeader("Document Compliance Report","Per-document accessibility analysis with checkpoint verification"),
    E("div",{style:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}},
      [{v:dSt.n,l:"Documents"},{v:dc.filter(function(d){return d.tagged}).length,l:"Tagged"},{v:dc.filter(function(d){return!d.tagged}).length,l:"Untagged"},{v:docPass+"/"+dSt.n,l:"Passing"}].map(function(k,i){
        return E("div",{key:i,style:{textAlign:"center",padding:10,borderRadius:8,border:"1.5px solid "+P.bdr}},
          E("div",{style:{fontSize:18,fontWeight:900}},k.v),E("div",{style:{fontSize:10,color:P.slateL}},k.l))})),
    E("p",{style:{fontSize:11,color:P.slateM,marginBottom:10}},"\u{1F449} Click any document to generate its full checkpoint-by-checkpoint certification report."),
    rptTable(["Document","Pages","Producer","Tagged","Issues","Critical","Status",""],
      sortedDcRpt.map(function(d,i){return[
        E("strong",null,d.name),d.pages,E("span",{style:{fontSize:10}},d.producer),
        d.tagged?"\u2713":E("span",{style:{color:P.sCrit,fontWeight:700}},"\u2717 UNTAGGED"),
        d.totalIssues,d.critCount>0?E("span",{style:{color:P.sCrit,fontWeight:700}},d.critCount):0,
        d.critCount===0?E("span",{style:{color:P.emerald,fontWeight:700}},"\u2713 PASS"):E("span",{style:{color:P.sCrit,fontWeight:700}},"\u2717 FAIL"),
        E("button",{onClick:function(){setRptDoc(i)},style:{padding:"3px 10px",borderRadius:4,border:"1px solid "+P.bdr,background:P.card,fontSize:10,cursor:"pointer"}},"Full Report \u2192")]}),
      {align:["left","center","left","center","center","center","center","center"]})
  );

  /* === 4. CHECKPOINT DETAIL === */
  var allChecks={};
  wp.forEach(function(p){p.issues.forEach(function(i){
    if(!allChecks[i.wcag])allChecks[i.wcag]={wcag:i.wcag,name:i.info.n,rem:i.info.r,web:[],doc:[]};
    allChecks[i.wcag].web.push({asset:p.n+" ("+p.url+")",sev:i.sev,count:i.count,desc:i.desc,el:i.el,fix:i.fix})})});
  dc.forEach(function(d){d.issues.forEach(function(i){
    if(!allChecks[i.wcag])allChecks[i.wcag]={wcag:i.wcag,name:i.info.n,rem:i.info.r,web:[],doc:[]};
    allChecks[i.wcag].doc.push({asset:d.name,sev:i.sev,count:i.count,desc:i.desc})})});
  var checkArr=Object.values(allChecks).sort(function(a,b){return(b.web.length+b.doc.length)-(a.web.length+a.doc.length)});

  var rptCheck=E("div",{id:"rpt-content"},
    rptHeader("Checkpoint Detail Report","Object-level failure details organized by WCAG success criterion"),
    E("div",{style:{fontSize:11,color:P.slateM,marginBottom:16}},checkArr.length+" unique WCAG criteria violated across "+wSt.n+" web pages and "+dSt.n+" documents"),
    checkArr.map(function(ch,ci){
      var totalAff=ch.web.length+ch.doc.length;
      var totalInst=ch.web.reduce(function(s,w){return s+w.count},0)+ch.doc.reduce(function(s,d){return s+d.count},0);
      return E("div",{key:ci,style:{marginBottom:16,border:"1.5px solid "+P.bdr,borderRadius:8,overflow:"hidden"}},
        E("div",{style:{padding:"8px 12px",background:P.bgS,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}},
          E("div",{style:{flex:1}},
            E("strong",{style:{fontSize:12}},"WCAG "+ch.wcag+" — "+ch.name),
            E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},ch.rem),
            WCAG_KB[ch.wcag]?E("div",{style:{fontSize:10,color:P.amberDk,marginTop:4,padding:"4px 8px",borderRadius:4,background:P.sMajBg,border:"1px solid #FDE68A",lineHeight:1.5}},
              "\u{1F4A1} ",WCAG_KB[ch.wcag].fix," ",
              E("a",{href:WCAG_KB[ch.wcag].url,target:"_blank",rel:"noopener",onClick:function(ev){ev.stopPropagation()},
                style:{fontSize:9,color:P.sMin,fontWeight:600,textDecoration:"underline",marginLeft:4}},"\u{1F4D6} W3C Guide \u2197")):null),
          E("div",{style:{display:"flex",gap:8,flexShrink:0}},
            E("span",{style:{fontSize:10,fontWeight:700}},totalAff+" assets affected"),
            E("span",{style:{fontSize:10,fontWeight:700,color:P.sCrit}},totalInst+" total instances"))),
        rptTable(["Asset","Type","Severity","Instances","Description","Element"],
          ch.web.map(function(w){return[w.asset,"Web",sevBadge(w.sev),w.count,w.desc,w.el?E("code",{style:{fontSize:10,fontFamily:FC}},w.el):"—"]}).concat(
          ch.doc.map(function(d){return[d.asset,"PDF",sevBadge(d.sev),d.count,d.desc,"—"]})),
          {align:["left","center","center","center","left","left"]}));
    })
  );

  /* === 5. REMEDIATION WORKBOOK === */
  var allIssuesList=[];
  wp.forEach(function(p){p.issues.forEach(function(i){allIssuesList.push({asset:p.n,url:p.url,type:"Web",wcag:i.wcag,name:i.info.n,sev:i.sev,count:i.count,desc:i.desc,el:i.el,fix:i.fix,rem:i.info.r})})});
  dc.forEach(function(d){d.issues.forEach(function(i){allIssuesList.push({asset:d.name,url:"",type:"PDF",wcag:i.wcag,name:i.info.n,sev:i.sev,count:i.count,desc:i.desc,el:"",fix:"",rem:i.info.r})})});
  allIssuesList.sort(function(a,b){var so={critical:0,major:1,minor:2};return(so[a.sev]||2)-(so[b.sev]||2)});

  var rptRemediation=E("div",{id:"rpt-content"},
    rptHeader("Remediation Workbook","Prioritized, actionable issue list for remediation teams — sorted by severity"),
    E("div",{style:{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:16}},
      [{v:allIssuesList.length,l:"Total Issues"},{v:allIssuesList.filter(function(i){return i.sev==="critical"}).length,l:"Critical (Fix First)"},{v:allIssuesList.filter(function(i){return i.sev==="major"}).length,l:"Major"},{v:allIssuesList.filter(function(i){return i.sev==="minor"}).length,l:"Minor"},
       {v:templateStats.actionableCount,l:"Actionable Fixes"}].map(function(k,i){
        return E("div",{key:i,style:{textAlign:"center",padding:10,borderRadius:8,border:"1.5px solid "+P.bdr}},
          E("div",{style:{fontSize:18,fontWeight:900}},k.v),E("div",{style:{fontSize:10,color:P.slateL}},k.l))})),
    /* Fix Category Breakdown */
    rptSection("Issues by Fix Category",
      E("div",null,
        E("div",{style:{fontSize:11,color:P.slate,lineHeight:1.6,marginBottom:8}},
          "Issues are classified into fix categories to determine the optimal remediation approach. PiARIS Engine handles deterministic fixes (contrast, ARIA, parsing). AI handles contextual fixes (alt text, link purpose, headings)."),
        rptTable(["Category","Issues","Engine","Approach"],
          (function(){
            var cats={};
            allIssuesList.forEach(function(iss){
              var cat=classifyFixCategory(iss);
              if(!cats[cat])cats[cat]={count:0,engine:FIX_DEFAULTS[cat]||"piaris"};
              cats[cat].count+=iss.count;
            });
            return Object.keys(cats).sort(function(a,b){return cats[b].count-cats[a].count}).map(function(cat){
              return[fixCategoryLabel(cat),cats[cat].count,
                cats[cat].engine==="ai"?E("span",{style:{color:P.pri,fontWeight:700}},"\u{1F9E0} AI-Powered"):E("span",{style:{color:P.emerald,fontWeight:700}},"\u2699\uFE0F PiARIS Engine"),
                cats[cat].engine==="ai"?"Contextual analysis via Claude Vision/language model":"Deterministic rule-based fix script generation"];
            });
          })()))),
    /* Full issue list with KB guidance */
    rptSection("Issue Detail \u2014 Sorted by Severity",
      rptTable(["#","Asset","Type","WCAG","Criterion","Sev","Category","Instances","Description","How to Fix","Scope"],
        allIssuesList.map(function(iss,i){
          var cat=classifyFixCategory(iss);
          var kb=WCAG_KB[iss.wcag];
          var nSel=normalizeSelector(iss.el||"");
          var descKey=(iss.desc||"").substring(0,60).toLowerCase().replace(/\s+/g," ");
          var tKey=(iss.wcag||"")+"||"+nSel+"||"+descKey;
          var tg=templateStats.groups[tKey];
          var scope=tg&&tg.pages.length>1&&tg.isSpecific?E("span",{style:{color:P.pri,fontWeight:700}},"\u{1F501} Template ("+tg.pages.length+" pages)"):E("span",{style:{color:P.slateM}},"Page-specific");
          return[i+1,iss.asset,iss.type,"WCAG "+iss.wcag,iss.name,sevBadge(iss.sev),
            fixCategoryLabel(cat),iss.count,
            (iss.desc||"").substring(0,80),
            kb?E("span",null,E("span",{style:{fontSize:10}},kb.fix.substring(0,100)+(kb.fix.length>100?"...":"")),
              E("br",null),E("a",{href:kb.url,target:"_blank",rel:"noopener",style:{fontSize:9,color:P.sMin}},"\u{1F4D6} W3C Guide"))
              :E("span",{style:{fontSize:10}},iss.rem||""),
            scope]})))
  );

  /* === 6. CERTIFICATION === */
  var certStatus=comp.status.toUpperCase();
  var certCol=P[comp.statusColor];
  var rptCert=E("div",{id:"rpt-content"},
    rptHeader("Accessibility Certification Report","Formal compliance assessment against WCAG 2.1 AA, Section 508, and PDF/UA"),
    /* Big certification stamp */
    E("div",{style:{textAlign:"center",margin:"20px 0 24px",padding:24,borderRadius:12,border:"4px solid "+certCol,background:certCol+"08"}},
      scoreCircle(overallScore,72),
      E("div",{style:{fontSize:22,fontWeight:900,color:certCol,marginTop:10}},certStatus),
      E("div",{style:{fontSize:11,color:P.slateM,marginTop:6}},"Based on automated analysis of "+wSt.n+" web pages and "+dSt.n+" PDF documents"),
      E("div",{style:{fontSize:10,color:P.slateL,marginTop:8,fontStyle:"italic"}},"This certification reflects automated testing results. Manual testing by accessibility experts is recommended for full conformance verification per WCAG 2.1 \u00A75.2.2.")),
    rptSection("Standard-by-Standard Assessment",
      rptTable(["Standard","Scope","Checkpoints Tested","Pass Rate","Status"],
        [["WCAG 2.1 AA","Web + PDF",Object.keys(allChecks).length+" criteria",compRate+"%",compRate>=90?pfBadge(true):pfBadge(false)],
         ["Section 508 (Revised)","Federal web + docs",Object.keys(allChecks).length+" criteria",compRate+"%",compRate>=90?pfBadge(true):pfBadge(false)],
         ["PDF/UA (ISO 14289-1)","PDF documents",dc.length+" documents",Math.round(docPass/Math.max(1,dc.length)*100)+"%",docPass===dc.length?pfBadge(true):pfBadge(false)],
         ["ADA Title II (2024)","Public-facing web",wSt.wc+" criteria",Math.round(webPass/Math.max(1,wp.length)*100)+"%",webPass===wp.length?pfBadge(true):pfBadge(false)],
         ["EAA EN 301 549","EU digital access",Object.keys(allChecks).length+" criteria",compRate+"%",compRate>=90?pfBadge(true):pfBadge(false)]],
        {align:["left","left","center","center","center"]})),
    rptSection("Findings Summary",
      E("div",null,
        E("div",{style:{fontSize:11,lineHeight:1.7}},
          "This assessment identified ",E("strong",null,totalI+" accessibility issues")," across ",E("strong",null,(wSt.n+dSt.n)+" digital assets"),
          ". Of these, ",E("strong",{style:{color:P.sCrit}},(wSt.tC+dSt.tC)+" are critical violations")," requiring immediate remediation. ",
          E("strong",null,totalFixed+" issues")," (",overallScore,"%) have been auto-remediated. ",
          "The most common violation is ",E("strong",null,"WCAG "+(topWcagAll[0]?topWcagAll[0][0]:"N/A"))," (",
          W[topWcagAll[0]?topWcagAll[0][0]:""]?W[topWcagAll[0][0]].n:"",")",
          " with ",E("strong",null,(topWcagAll[0]?topWcagAll[0][1]:0)+" instances"),"."))),
    rptSection("Recommendations",
      E("div",{style:{fontSize:11,lineHeight:1.7}},
        E("div",{style:{marginBottom:8}},E("strong",null,"1. Immediate: "),"Remediate all ",wSt.tC+dSt.tC," critical issues — particularly missing alt text (WCAG 1.1.1) and keyboard access (WCAG 2.1.1). These pose the highest legal and usability risk."),
        E("div",{style:{marginBottom:8}},E("strong",null,"2. Short-term: "),"Tag all ",dc.filter(function(d){return!d.tagged}).length," untagged PDFs and establish a pre-publication PDF validation workflow."),
        E("div",{style:{marginBottom:8}},E("strong",null,"3. Ongoing: "),"Schedule monthly automated scans via ARIS Monitoring. Train content authors on accessible document creation. Establish VPAT documentation for procurement compliance.")))
  );

  /* === 7. TREND ANALYSIS === */
  /** Build trend data from real scan history — combines savedScans (local) and
      userSessions (cloud) for the current domain. Falls back to current-scan-only
      if no history exists. Sorted chronologically. */
  var trendData=useMemo(function(){
    var dom=(domain||"").toLowerCase();
    var points=[];
    /* Local saved scans for this domain */
    savedScans.forEach(function(s){
      if((s.domain||"").toLowerCase()===dom){
        points.push({
          ts:s.id,
          month:s.date||new Date(s.id).toLocaleDateString("en-US",{month:"short",day:"numeric"}),
          issues:s.totalIssues||0,
          critical:s.criticalIssues||0,
          fixed:s.remediated||0,
          score:s.score||0,
          pages:s.webPages||0,
          docs:s.documents||0,
          source:"local"
        });
      }
    });
    /* Cloud sessions for this domain */
    userSessions.forEach(function(s){
      if((s.domain||"").toLowerCase()===dom){
        /* Avoid duplicates — skip if a local scan exists within 60s of this session */
        var ts=s.createdAt?new Date(s.createdAt).getTime():(s.id?parseInt(s.id.replace(/\D/g,"").substring(0,13)):0);
        var isDupe=points.some(function(p){return Math.abs(p.ts-ts)<60000});
        if(!isDupe&&ts>0){
          var summary=s.summary||{};
          points.push({
            ts:ts,
            month:new Date(ts).toLocaleDateString("en-US",{month:"short",day:"numeric"}),
            issues:s.totalIssues||summary.total||s.streamedIssues||0,
            critical:summary.critical||0,
            fixed:summary.remediated||0,
            score:s.overallScore||0,
            pages:s.streamedPageCount||s.webPages||0,
            docs:s.documents||0,
            source:"cloud"
          });
        }
      }
    });
    /* Sort chronologically */
    points.sort(function(a,b){return a.ts-b.ts});
    /* If no history, create a single entry from current scan */
    if(points.length===0&&wp.length>0){
      points.push({
        ts:Date.now(),
        month:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}),
        issues:totalI,critical:wSt.tC+dSt.tC,fixed:totalFixed,
        score:overallScore,pages:wp.length,docs:dc.length,source:"current"
      });
    }
    return points;
  },[domain,savedScans,userSessions,wp,dc,totalI,totalFixed,overallScore,wSt,dSt]);

  var rptTrend=E("div",{id:"rpt-content"},
    rptHeader("Trend Analysis Report","Accessibility compliance progress for "+domain),
    trendData.length<2?E("div",{style:{padding:24,textAlign:"center",color:P.slateM,background:P.bgS,borderRadius:10,marginBottom:16}},
      E("div",{style:{fontSize:24,marginBottom:8}},"\u{1F4C8}"),
      E("div",{style:{fontSize:13,fontWeight:700,marginBottom:4}},"Trend data requires multiple scans"),
      E("div",{style:{fontSize:11}},"Save your current scan and run at least one more scan of "+domain+" to see progress over time. Each saved scan becomes a data point.")):
    E("div",null,
      E("div",{style:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}},
        [{l:"Current Score",v:trendData[trendData.length-1].score+"%",c:trendData[trendData.length-1].score>=50?P.emerald:P.sCrit},
         {l:"First \u2192 Latest",v:(trendData[trendData.length-1].score-trendData[0].score>=0?"+":"")+(trendData[trendData.length-1].score-trendData[0].score)+"%",
          c:trendData[trendData.length-1].score>=trendData[0].score?P.emerald:P.sCrit},
         {l:"Scans Recorded",v:trendData.length,c:P.pri},
         {l:"Issues Resolved",v:totalFixed.toLocaleString(),c:P.emerald}].map(function(k,i){
          return E("div",{key:i,style:{textAlign:"center",padding:14,borderRadius:8,border:"1.5px solid "+P.bdr}},
            E("div",{style:{fontSize:22,fontWeight:900,color:k.c}},k.v),
            E("div",{style:{fontSize:10,color:P.slateL,marginTop:2}},k.l))})),
      rptSection("Compliance Score Trend",
        E(ResponsiveContainer,{width:"100%",height:220},
          E(BarChart,{data:trendData},
            E(XAxis,{dataKey:"month",tick:{fontSize:10,fill:P.slateM}}),
            E(YAxis,{tick:{fontSize:10,fill:P.slateL},domain:[0,100]}),
            E(Tooltip,{contentStyle:{background:P.card,border:"1px solid "+P.bdr,borderRadius:6,fontSize:11}}),
            E(Bar,{dataKey:"score",fill:P.pri,radius:[4,4,0,0],name:"Score %"})))),
      rptSection("Issues vs Remediated Over Time",
        E(ResponsiveContainer,{width:"100%",height:220},
          E(BarChart,{data:trendData},
            E(XAxis,{dataKey:"month",tick:{fontSize:10,fill:P.slateM}}),
            E(YAxis,{tick:{fontSize:10,fill:P.slateL}}),
            E(Tooltip,{contentStyle:{background:P.card,border:"1px solid "+P.bdr,borderRadius:6,fontSize:11}}),
            E(Bar,{dataKey:"issues",fill:P.sCrit,radius:[4,4,0,0],name:"Total Issues"}),
            E(Bar,{dataKey:"fixed",fill:P.emerald,radius:[4,4,0,0],name:"Remediated"}),
            E(Bar,{dataKey:"critical",fill:"#F97316",radius:[4,4,0,0],name:"Critical"})))),
      rptSection("Scan History Table",
        rptTable(["Date","Pages","Docs","Issues","Critical","Remediated","Score","Trend"],
          trendData.map(function(t,i){
            var prev=i>0?trendData[i-1]:null;
            var delta=prev?(t.issues-prev.issues):0;
            return[t.month,t.pages,t.docs,t.issues,t.critical,t.fixed,t.score+"%",
              prev?(delta<0?E("span",{style:{color:P.emerald,fontWeight:700}},"\u2193 "+Math.abs(delta)+" fewer"):
                delta>0?E("span",{style:{color:P.sCrit,fontWeight:700}},"\u2191 "+delta+" more"):
                E("span",{style:{color:P.slateM}},"\u2014 no change")):
              E("span",{style:{color:P.slateL,fontStyle:"italic"}},"baseline")];}),
          {align:["left","center","center","center","center","center","center","center"]})))
  );

  /* Report Center Shell */
  var rptContent=rptTab==="executive"?rptExec:rptTab==="webCompliance"?rptWeb:rptTab==="docCompliance"?rptDocPage:rptTab==="checkpoint"?rptCheck:rptTab==="remediation"?rptRemediation:rptTab==="certification"?rptCert:rptTab==="trend"?rptTrend:rptExec;
  var rptTitles={executive:"Executive Summary",webCompliance:"Web Compliance",docCompliance:"Document Compliance",checkpoint:"Checkpoint Detail",remediation:"Remediation Workbook",certification:"Certification",trend:"Trend Analysis"};

  var reportsPage=E("div",null,
    E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}},
      E("div",null,E("h2",{style:{fontSize:20,fontWeight:800,margin:0}},"\u{1F4CA} Report Center"),E("p",{style:{color:P.slateL,fontSize:11,margin:"2px 0 0"}},"Generate, view, and download compliance reports \u00B7 7 report types \u00B7 PiARIS\u2122 by "+PI_NAME)),
      E("div",{style:{display:"flex",gap:6}},
        E("button",{onClick:function(){downloadReport(rptTitles[rptTab]||"report")},style:{padding:"7px 14px",borderRadius:8,border:"none",background:P.pri,color:"#fff",fontSize:10,fontWeight:700}},"\u2193 Download HTML"),
        E("button",{onClick:downloadJSON,style:{padding:"7px 14px",borderRadius:8,border:"none",background:P.emerald,color:"#fff",fontSize:10,fontWeight:700}},"\u2193 Export JSON"),
        E("button",{onClick:function(){window.print()},style:{padding:"7px 14px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,fontSize:10,fontWeight:600}},"\u{1F5A8} Print"))),
    /* Report type tabs */
    E("div",{style:{display:"flex",gap:3,marginBottom:14,flexWrap:"wrap"}},
      RPT_TABS.map(function(t){return E("button",{key:t.id,onClick:function(){setRptTab(t.id);setRptDoc(null)},style:{padding:"6px 12px",borderRadius:6,border:"1.5px solid "+(rptTab===t.id?P.pri:P.bdr),background:rptTab===t.id?P.priLt:P.card,color:rptTab===t.id?P.pri:P.slateM,fontSize:10,fontWeight:rptTab===t.id?700:500,cursor:"pointer"}},t.i+" "+t.l)})),
    /* Report content */
    E(Card,{sx:{padding:20,overflow:"auto",maxHeight:"75vh"}},rptContent)
  );

  /* ═══ LICENSES ADMIN PANEL + STRIPE CHECKOUT ═══ */
  var _licList=useState([]),licList=_licList[0],setLicList=_licList[1];
  var _licLoading=useState(false),licLoading=_licLoading[0],setLicLoading=_licLoading[1];
  var _licError=useState(null),licError=_licError[0],setLicError=_licError[1];
  var _upgradeModal=useState(null),upgradeModal=_upgradeModal[0],setUpgradeModal=_upgradeModal[1];
  var _checkoutLoading=useState(false),checkoutLoading=_checkoutLoading[0],setCheckoutLoading=_checkoutLoading[1];
  /* AbortController + retry guard. Module-scoped via ref so that
     remounts cancel any in-flight requests and never stack fetches. */
  var _licAbortRef=useRef(null);
  var _licLoggedErrorRef=useRef(false);

  /* loadLicenses — single request with exponential backoff (max 3 retries).
     - Cancels any in-flight request first.
     - Logs the error exactly once across the retry cycle.
     - Sets licError on terminal failure so the UI can show a Retry button
       instead of looping back into loadLicenses on every render. */
  function loadLicenses(){
    if(_licAbortRef.current){try{_licAbortRef.current.abort()}catch(_){}}
    var ctrl=(typeof AbortController!=="undefined")?new AbortController():null;
    _licAbortRef.current=ctrl;
    _licLoggedErrorRef.current=false;
    setLicError(null);
    setLicLoading(true);
    var DELAYS=[500,1500,4500]; /* 3 retries after the initial attempt */
    function attempt(n){
      return fetch("/api/license/list",{credentials:"include",signal:ctrl?ctrl.signal:undefined})
        .then(function(r){
          if(!r.ok){var e=new Error("HTTP "+r.status);e.status=r.status;throw e;}
          return r.json();
        })
        .then(function(data){
          if(ctrl&&ctrl.signal.aborted)return;
          setLicList(data.licenses||[]);
          setLicError(null);
          setLicLoading(false);
        })
        .catch(function(err){
          if(err&&err.name==="AbortError")return; /* unmount / re-trigger */
          if(n<DELAYS.length){
            return new Promise(function(res){setTimeout(res,DELAYS[n])}).then(function(){return attempt(n+1)});
          }
          if(!_licLoggedErrorRef.current){
            _licLoggedErrorRef.current=true;
            /* one log per cycle, not per render */
            console.error("[PiARIS] license/list failed after retries:",err&&err.message);
          }
          setLicError((err&&err.message)||"Could not reach the license service.");
          setLicLoading(false);
        });
    }
    attempt(0);
  }

  /* Tab activation: load licenses exactly once when the admin panel
     becomes active. The previous render-time trigger created a fetch storm
     because licList stayed empty after a failed request and every render
     re-fired the request. */
  useEffect(function(){
    if(mod!=="licenses")return;
    if(licList.length>0)return;
    if(licLoading||licError)return;
    loadLicenses();
    return function(){
      if(_licAbortRef.current){try{_licAbortRef.current.abort()}catch(_){}}
    };
  },[mod]);

  /* Admin tab-driven loads. Each tab pulls only the data it needs the first
     time it's activated. The error/loading flags in the dependencies prevent
     re-firing on transient failure (the page surfaces a Retry button instead).
     PR-5.7: depend on authUser too. When this effect fires on initial mount,
     authUser may still be null (recheckAuth hasn't resolved yet). Without
     auth cookies forwarded, /api/manage/users returns 401, usersList stays
     empty, and the Users tab shows "No users loaded. Click Refresh." That
     was the live regression. Re-running once authUser lands ensures the
     fetch fires AFTER the session is established. */
  useEffect(function(){
    if(mod!=="licenses")return;
    if(!authUser)return;                    // wait for auth to land
    var tab=adminTab;
    /* Users tab + Overview both need users + licenses. */
    if((tab==="users"||tab==="overview") && usersList.length===0 && !usersLoading){ loadUsers(); }
    /* DocMaya tab needs keys. */
    if(tab==="docmaya-keys" && dmKeys.length===0 && !dmKeyLoading){ loadDocMayaKeys(); }
    /* Activity log + Overview both need recent activity. */
    if((tab==="activity"||tab==="overview") && activity.length===0 && !activityLoading && !activityError){ loadActivity(); }
  /* eslint-disable-next-line */
  },[mod,adminTab,authUser]);

  /* createLicenseFromPayload — called by the v2 NewLicenseModal with a
     payload in the form { preset, scope, products?, billing?, validity,
     maxRedemptions, notes }. Forwarded as-is to POST /api/license/create;
     the server also accepts the legacy {domain, tier, ...} shape so
     callers that haven't migrated yet still work.
     Returns the created license doc on success (so the modal's success
     view can show the generated code), or false on failure. */
  function createLicenseFromPayload(payload){
    /* Defensive copy + only forward defined fields — keeps the JSON
       small and lets the server's defaults take over for omitted ones. */
    var body = {};
    if(payload.preset)         body.preset         = payload.preset;
    if(payload.scope)          body.scope          = payload.scope;
    if(payload.products)       body.products       = payload.products;
    if(payload.billing)        body.billing        = payload.billing;
    if(payload.validity)       body.validity       = payload.validity;
    if(typeof payload.maxRedemptions === "number") body.maxRedemptions = payload.maxRedemptions;
    if(payload.notes)          body.notes          = payload.notes;
    /* Legacy passthrough for any caller still on the v1.1 shape. */
    if(payload.domain && !payload.scope) {
      body.domain     = payload.domain;
      body.orgName    = payload.orgName || payload.domain;
      body.tier       = payload.tier || "starter";
      body.startsAt   = payload.startsAt;
      body.expiresAt  = payload.expiresAt;
    }
    return fetch("/api/license/create",{
      method:"POST", credentials:"include",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify(body)
    }).then(function(r){
      return r.json().then(function(data){return {ok:r.ok,status:r.status,data:data};});
    }).then(function(res){
      /* PR-5.9: surface the dispatcher's error string both as a toast
         (for the parent's status area) AND back to the modal so it can
         render an inline red banner. Was swallowing the response on
         data.error and returning a plain `false`, which the modal had
         no way to expand into a real error message. */
      var data = res.data || {};
      if(!res.ok || data.error){
        var msg = data.error || ("HTTP "+res.status);
        showToast(msg,"error");
        return { error: msg, status: res.status };
      }
      var who = (data.scope && data.scope.key) || payload.domain || "license";
      showToast("✅ License created for "+who);
      loadLicenses();
      /* Hand the doc back so the modal can show the generated code. */
      return data && data.code ? data : true;
    }).catch(function(e){
      showToast("Error: "+e.message,"error");
      return { error: String((e && e.message) || e) };
    });
  }


  /* PR-5.9: lifecycle by license id — works for every scope (user,
     domain, unbound) since the by-id routes don't require a domain
     URL segment. The legacy {domain}/suspend etc. only worked for
     domain-scoped rows. Callers pass the whole license object now
     so the toast can name the row meaningfully (scope.key or code). */
  function _licWho(lic){
    if (!lic) return "license";
    if (lic.scope && lic.scope.key && lic.scope.kind !== "unbound") return lic.scope.key;
    return lic.code || lic.id || "license";
  }
  function suspendLicense(lic){
    var id = (typeof lic === "string") ? lic : (lic && (lic.id || lic.licenseId));
    if (!id) { showToast("Cannot suspend — missing license id", "error"); return; }
    return fetch("/api/license/suspend-by-id/"+encodeURIComponent(id),{method:"POST",credentials:"include"})
      .then(function(r){return r.json().then(function(d){return {ok:r.ok,data:d};});})
      .then(function(res){
        if(!res.ok || res.data.error){showToast(res.data.error||"Suspend failed","error");return;}
        showToast("License suspended: "+_licWho(typeof lic==="string"?null:lic));
        loadLicenses();
      });
  }
  function activateLicense(lic){
    var id = (typeof lic === "string") ? lic : (lic && (lic.id || lic.licenseId));
    if (!id) { showToast("Cannot resume — missing license id", "error"); return; }
    return fetch("/api/license/activate-by-id/"+encodeURIComponent(id),{method:"POST",credentials:"include"})
      .then(function(r){return r.json().then(function(d){return {ok:r.ok,data:d};});})
      .then(function(res){
        if(!res.ok || res.data.error){showToast(res.data.error||"Resume failed","error");return;}
        showToast("License resumed: "+_licWho(typeof lic==="string"?null:lic));
        loadLicenses();
      });
  }
  /* HARD delete — caller must have already shown a typed-confirmation
     dialog. Idempotent: 200 even when the doc is already gone. */
  function deleteLicense(lic){
    var id = (typeof lic === "string") ? lic : (lic && (lic.id || lic.licenseId));
    if (!id) { showToast("Cannot delete — missing license id", "error"); return; }
    return fetch("/api/license/delete/"+encodeURIComponent(id),{method:"DELETE",credentials:"include"})
      .then(function(r){return r.json().then(function(d){return {ok:r.ok,data:d};});})
      .then(function(res){
        if(!res.ok || res.data.error){showToast(res.data.error||"Delete failed","error");return;}
        showToast("License deleted: "+_licWho(typeof lic==="string"?null:lic));
        loadLicenses();
      });
  }

  var TIER_COLORS={free:"#94A3B8",starter:"#3B82F6",standard:"#8B5CF6",enterprise:"#F59E0B"};
  var TIER_LABELS={free:"Free (25 pg, 1 scan/yr)",starter:"Starter (200 pg, 2 scans/yr)",standard:"Standard (1K pg, 4 scans/yr)",enterprise:"Enterprise (10K pg, 4 scans/yr)"};

  /* ── Stripe Checkout ── */
  var STRIPE_PRICES={
    starter:{monthly:"price_1TBAIhPF79Ljr07aUq9nJqjs",label:"Starter",price:"$149/mo",pages:"200 pages/mo"},
    standard:{monthly:"price_1TBALsPF79Ljr07aR8xbveKy",label:"Standard",price:"$499/mo",pages:"1,000 pages/mo"}
  };

  function startCheckout(domain,tier){
    if(!STRIPE_PRICES[tier]){
      if(tier==="enterprise"){setUpgradeModal({domain:domain,tier:"enterprise"});return;}
      showToast("Invalid tier for checkout","error");return;
    }
    setCheckoutLoading(true);
    fetch("/api/license/_/stripe-checkout",{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      credentials:"include",
      body:JSON.stringify({
        domain:domain,
        tier:tier,
        priceId:STRIPE_PRICES[tier].monthly,
        successUrl:window.location.origin+"/?view=app&mod=licenses&checkout=success&domain="+encodeURIComponent(domain),
        cancelUrl:window.location.origin+"/?view=app&mod=licenses&checkout=cancel"
      })
    })
    .then(function(r){return r.json()})
    .then(function(data){
      if(data.checkoutUrl){
        window.location.href=data.checkoutUrl;
      } else if(data.error){
        showToast("Checkout error: "+data.error,"error");
      } else {
        showToast("Checkout not available yet \u2014 Stripe integration pending. Contact Piaris.Support@piconsultants.us to upgrade.","error");
      }
    })
    .catch(function(e){showToast("Checkout failed: "+e.message,"error")})
    .finally(function(){setCheckoutLoading(false)});
  }

  /* Upgrade Modal */
  /* ── Bulk remediation modal ───────────────────────────────────────
     A list of the corpus PDFs with each doc's remediation state
     (human-edited / AI / mixed / none). Human-edited docs are flagged
     "review" (don't clobber); fully-AI complete ones are pre-excluded.
     Click the row's ✕ to exclude/include. "Remediate selected" starts a
     poll-driven job (one doc per status call). */
  function bulkBadge(doc){
    if(!doc.remediated)return E("span",{style:{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,background:P.bgS,color:P.slateL}},"not remediated");
    var color=doc.altSource==="manual"||doc.altSource==="mixed"?(P.amber||"#D97706"):P.emerald;
    var bg=doc.altSource==="manual"||doc.altSource==="mixed"?"#FEF3C7":P.emeraldLt;
    var label=doc.altSource==="manual"?"human-edited":doc.altSource==="mixed"?"mixed (human+AI)":doc.altSource==="ai"?"AI alt":"remediated";
    return E("span",{style:{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,background:bg,color:color}},label);
  }
  var bulkRemModalEl=bulkRem?E("div",{style:{position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.5)",backdropFilter:"blur(4px)"},onClick:function(){if(bulkRem.phase!=="running")setBulkRem(null);}},
    E("div",{role:"dialog","aria-label":"Bulk remediation",onClick:function(e){e.stopPropagation();},style:{background:P.card,borderRadius:12,padding:20,width:"min(680px,92vw)",maxHeight:"86vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.3)"}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}},
        E("div",null,
          E("h3",{style:{fontSize:17,fontWeight:800,margin:0,color:P.slateD||"#0F172A"}},"♿ Bulk remediation"),
          E("p",{style:{fontSize:11,color:P.slateL,margin:"4px 0 0"}},bulkRem.policy||"Human-edited alt is preserved; automation fills the gaps. Exclude any doc to leave it untouched.")),
        E("button",{onClick:function(){if(bulkRem.phase!=="running")setBulkRem(null);},disabled:bulkRem.phase==="running",style:{background:"none",border:"none",fontSize:18,cursor:bulkRem.phase==="running"?"not-allowed":"pointer",color:P.slateM}},"✕")),
      bulkRem.error?E("div",{style:{padding:8,borderRadius:6,background:P.sCritBg,color:P.sCrit,fontSize:11,marginBottom:10}},"⚠ "+bulkRem.error):null,
      bulkRem.loading?E("div",{style:{fontSize:12,color:P.slateM,padding:20,textAlign:"center"}},"Loading documents…"):
      (bulkRem.phase==="select"?E("div",null,
        E("div",{style:{maxHeight:"46vh",overflow:"auto",border:"1px solid "+P.bdr,borderRadius:8}},
          (bulkRem.docs||[]).map(function(doc){
            var excluded=!!bulkRem.excluded[doc.docIndex];
            return E("div",{key:"brd-"+doc.docIndex,style:{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderBottom:"1px solid "+P.bdr,opacity:excluded?0.45:1}},
              E("button",{onClick:function(){toggleBulkExclude(doc.docIndex);},title:excluded?"Include":"Exclude",style:{width:24,height:24,borderRadius:6,border:"1.5px solid "+(excluded?P.bdr:P.emerald),background:excluded?P.bgS:P.emeraldLt,color:excluded?P.slateL:P.emerald,fontSize:12,fontWeight:800,cursor:"pointer",flexShrink:0}},excluded?"+":"✓"),
              E("div",{style:{flex:1,minWidth:0}},
                E("div",{style:{fontSize:12,fontWeight:600,color:P.slateD||"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}},doc.name||("doc "+doc.docIndex)),
                E("div",{style:{fontSize:10,color:P.slateL,marginTop:2}},doc.recommend==="review"?"has human edits — preserved on re-run":doc.recommend==="skip"?"already complete (AI) — excluded by default":"will be remediated")),
              bulkBadge(doc));
          })),
        E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14}},
          E("div",{style:{fontSize:11,color:P.slateL}},
            (bulkRem.docs||[]).filter(function(d){return !bulkRem.excluded[d.docIndex];}).length+" of "+(bulkRem.docs||[]).length+" selected"),
          E("button",{onClick:startBulkRemediation,style:{padding:"9px 18px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#8B5CF6,"+P.emerald+")",color:"#fff",fontSize:12,fontWeight:800,cursor:"pointer"}},
            "✨ Remediate selected ("+(bulkRem.docs||[]).filter(function(d){return !bulkRem.excluded[d.docIndex];}).length+")"))):
      /* running / done */
      E("div",null,
        E("div",{style:{fontSize:12,fontWeight:700,color:bulkRem.phase==="done"?P.emerald:"#6D28D9",marginBottom:8}},
          bulkRem.phase==="done"?("✓ Done — "+(bulkRem.remediated||0)+" remediated"+(bulkRem.incomplete?", "+bulkRem.incomplete+" need alt review":"")+(bulkRem.failed?", "+bulkRem.failed+" failed":"")):("⏳ Remediating… "+(bulkRem.processed||0)+" of "+(bulkRem.total||0))),
        E("div",{style:{height:8,borderRadius:4,background:P.bgS,overflow:"hidden",marginBottom:12}},
          E("div",{style:{height:"100%",width:(bulkRem.total?Math.round((bulkRem.processed||0)/bulkRem.total*100):0)+"%",background:"linear-gradient(90deg,#8B5CF6,"+P.emerald+")",transition:"width .3s"}})),
        E("div",{style:{maxHeight:"40vh",overflow:"auto"}},
          (bulkRem.results||[]).map(function(res,i){
            return E("div",{key:"brr-"+i,style:{display:"flex",alignItems:"center",gap:8,fontSize:11,padding:"6px 8px",borderBottom:"1px solid "+P.bdr,color:res.ok===false?P.sCrit:(res.complete===false?(P.amber||"#D97706"):P.emerald)}},
              E("span",{style:{flex:1}},
                (res.ok===false?"✗ ":(res.complete===false?"⚠ ":"✓ "))+"doc "+res.docIndex+" — "+
                (res.ok===false?("failed: "+(res.error||"unknown")):((res.beforeIssues!=null?res.beforeIssues:"?")+" → "+(res.afterIssues!=null?res.afterIssues:"?")+" issues"+(res.altTextNeeded?(" · "+res.altTextNeeded+" figures need alt"):"")))),
              res.hasResult?E("button",{onClick:function(){downloadBulkResult(bulkRem.jobId,res.docIndex,res.name)},style:{padding:"3px 10px",borderRadius:6,border:"1.5px solid "+P.emerald,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700,cursor:"pointer",flexShrink:0}},"⬇ PDF"):null);
          })),
        bulkRem.phase==="done"?E("button",{onClick:function(){setBulkRem(null);},style:{marginTop:12,padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.emerald,background:P.emeraldLt,color:P.emerald,fontSize:12,fontWeight:700,cursor:"pointer"}},"Close"):
          E("div",{style:{fontSize:10,color:P.slateL,marginTop:8}},"Processing one document per step — keep this open."))
    )
  )):null;

  var upgradeModalEl=upgradeModal?E("div",{style:{position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.5)",backdropFilter:"blur(4px)"},onClick:function(){setUpgradeModal(null)}},
    E("div",{style:{background:P.card,borderRadius:16,padding:24,maxWidth:560,width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,.3)"},onClick:function(e){e.stopPropagation()}},
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}},
        E("div",null,
          E("h3",{style:{fontSize:18,fontWeight:800,margin:0}},upgradeModal.tier==="enterprise"?"\u{1F3E2} Enterprise Plan":"\u2B06 Upgrade: "+upgradeModal.domain),
          E("p",{style:{fontSize:11,color:P.slateL,margin:"4px 0 0"}},upgradeModal.tier==="enterprise"?"Custom pricing for large organizations":"Choose a plan to unlock more conversions")),
        E("button",{onClick:function(){setUpgradeModal(null)},style:{background:"none",border:"none",fontSize:18,cursor:"pointer",color:P.slateM}},"\u2715")),

      upgradeModal.tier==="enterprise"?
        /* Enterprise contact form */
        E("div",null,
          E("div",{style:{padding:16,borderRadius:10,background:P.sMajBg,border:"1.5px solid #FDE68A",marginBottom:16}},
            E("div",{style:{fontSize:12,fontWeight:700,color:P.amberDk,marginBottom:6}},"\u{1F4E9} Contact Sales"),
            E("div",{style:{fontSize:11,color:"#78716C",lineHeight:1.5}},"Enterprise includes unlimited pages, unlimited subdomains, dedicated onboarding, SLA guarantee, and invoice billing. We\u2019ll configure a custom plan for "+upgradeModal.domain+".")),
          E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}},
            E("div",null,
              E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:3}},"Your Name"),
              E("input",{id:"ent-name",placeholder:"Jane Doe",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:11}})),
            E("div",null,
              E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:3}},"Email"),
              E("input",{id:"ent-email",type:"email",placeholder:"jane@agency.gov",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:11}}))),
          E("div",{style:{marginBottom:12}},
            E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,display:"block",marginBottom:3}},"Estimated monthly pages"),
            E("select",{id:"ent-volume",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1.5px solid "+P.bdr,fontSize:11}},
              E("option",{value:"5000"},"5,000 - 10,000"),
              E("option",{value:"10000"},"10,000 - 50,000"),
              E("option",{value:"50000"},"50,000 - 100,000"),
              E("option",{value:"100000"},"100,000+"))),
          E("button",{onClick:function(){
            var name=document.getElementById("ent-name").value;
            var email=document.getElementById("ent-email").value;
            var volume=document.getElementById("ent-volume").value;
            if(!email){showToast("Email is required","error");return;}
            fetch("/api/license/_/enterprise-inquiry",{
              method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
              body:JSON.stringify({domain:upgradeModal.domain,name:name,email:email,estimatedVolume:volume})
            }).then(function(){
              showToast("\u2705 Enterprise inquiry sent! We\u2019ll be in touch within 24 hours.");
              setUpgradeModal(null);
            }).catch(function(){
              /* Fallback: mailto */
              window.open("mailto:Piaris.Support@piconsultants.us?subject=PiARIS Enterprise Inquiry - "+upgradeModal.domain+"&body=Name: "+encodeURIComponent(name)+"%0AEmail: "+encodeURIComponent(email)+"%0ADomain: "+upgradeModal.domain+"%0AEstimated Volume: "+volume+" pages/mo");
              showToast("\u{1F4E7} Opening email to sales team...");
              setUpgradeModal(null);
            });
          },style:{width:"100%",padding:"12px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#F59E0B,#D97706)",color:"#fff",fontSize:12,fontWeight:700}},"\u{1F4E9} Request Enterprise Quote")):

        /* Starter / Standard tier selection */
        E("div",null,
          E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}},
            ["starter","standard"].map(function(tier){
              var info=STRIPE_PRICES[tier];
              var isSelected=upgradeModal.selectedTier===tier;
              return E("div",{key:tier,onClick:function(){setUpgradeModal(Object.assign({},upgradeModal,{selectedTier:tier}))},
                style:{padding:16,borderRadius:10,border:"2px solid "+(isSelected?TIER_COLORS[tier]:P.bdr),
                  background:isSelected?TIER_COLORS[tier]+"08":P.card,cursor:"pointer",transition:"all .15s"}},
                E("div",{style:{fontSize:10,fontWeight:700,color:TIER_COLORS[tier],textTransform:"uppercase",marginBottom:4}},info.label),
                E("div",{style:{fontSize:24,fontWeight:800,marginBottom:2}},info.price),
                E("div",{style:{fontSize:10,color:P.slateM,marginBottom:8}},info.pages),
                tier==="starter"?E("div",{style:{fontSize:10,color:P.slateL}},"\u2713 3 subdomains\n\u2713 $0.05/overage page\n\u2713 Email support"):
                E("div",{style:{fontSize:10,color:P.slateL}},"\u2713 10 subdomains\n\u2713 $0.04/overage page\n\u2713 Priority support\n\u2713 Auto-convert"),
                isSelected?E("div",{style:{marginTop:8,textAlign:"center",padding:"4px",borderRadius:6,background:TIER_COLORS[tier],color:"#fff",fontSize:10,fontWeight:700}},"\u2713 Selected"):null);
            })),
          E("div",{style:{display:"flex",gap:8}},
            E("button",{onClick:function(){setUpgradeModal(Object.assign({},upgradeModal,{tier:"enterprise"}))},
              style:{flex:1,padding:"10px",borderRadius:8,border:"1.5px solid #FDE68A",background:P.sMajBg,color:P.amberDk,fontSize:11,fontWeight:600}},"\u{1F3E2} Need Enterprise?"),
            E("button",{onClick:function(){
              var t=upgradeModal.selectedTier||"starter";
              startCheckout(upgradeModal.domain,t);
            },disabled:checkoutLoading,
              style:{flex:2,padding:"12px",borderRadius:8,border:"none",
                background:checkoutLoading?"#94A3B8":"linear-gradient(135deg,#8B5CF6,#3B82F6)",
                color:"#fff",fontSize:12,fontWeight:700,opacity:checkoutLoading?.7:1}},
              checkoutLoading?"\u23F3 Redirecting to Stripe\u2026":"\u{1F4B3} Upgrade Now")))
    )):null;
  /* ── Admin Panel tab state ──
     adminTab values are URL slugs ("overview", "licenses", "users",
     "docmaya-keys", "revenue", "activity") — same vocabulary the admin
     module and the #/admin/<section> hash speak, so no mapping table. */
  /* PR-K.4: intake-queue added so setAdminTab("intake-queue") writes the
     canonical hash AND parseAdminHash recognises the inbound deep link.
     Mirrors the _readAdminHashSection allowlist above. */
  var ADMIN_SECTIONS=["overview","licenses","users","intake-queue","docmaya-keys","revenue","activity"];
  function parseAdminHash(h){
    if(!h)return null;
    var m=String(h).match(/^#\/admin(?:\/([a-z0-9-]+))?\/?$/i);
    if(!m)return null;
    var section=(m[1]||"overview").toLowerCase();
    return ADMIN_SECTIONS.indexOf(section)>=0?section:"overview";
  }
  var _adminInitSection=(typeof window!=="undefined")?parseAdminHash(window.location.hash):null;
  var _adminInitTab=_adminInitSection||"overview";
  var _adminTab=useState(_adminInitTab),adminTab=_adminTab[0],_setAdminTabRaw=_adminTab[1];
  /* setAdminTab — wrapper that also writes the canonical hash so back/forward
     and reload behave. Pass {silent:true} to suppress hash writes during
     hashchange handling (prevents a write-loop). */
  function setAdminTab(tab,opts){
    _setAdminTabRaw(tab);
    if(opts&&opts.silent)return;
    if(typeof window==="undefined")return;
    var want="#/admin/"+tab;
    if(window.location.hash!==want){
      try{window.history.replaceState(null,"",want)}catch(_){window.location.hash=want}
    }
  }
  var _dmKeys=useState([]),dmKeys=_dmKeys[0],setDmKeys=_dmKeys[1];
  var _dmKeyLoading=useState(false),dmKeyLoading=_dmKeyLoading[0],setDmKeyLoading=_dmKeyLoading[1];
  var _usersList=useState([]),usersList=_usersList[0],setUsersList=_usersList[1];
  var _usersLoading=useState(false),usersLoading=_usersLoading[0],setUsersLoading=_usersLoading[1];
  var _userUpdating=useState(null),userUpdating=_userUpdating[0],setUserUpdating=_userUpdating[1];

  /* ── Re-scan Verification state ── */
  var _verifyBaseline=useState(null),verifyBaseline=_verifyBaseline[0],setVerifyBaseline=_verifyBaseline[1]; /* {domain, fingerprints:{url:[fp,...]}, totalIssues, critCount, timestamp} */
  var _verifyResult=useState(null),verifyResult=_verifyResult[0],setVerifyResult=_verifyResult[1]; /* {resolved, persistent, regression, scoreBefore, scoreAfter, timestamp} */

  /** Build issue fingerprints from current scan for baseline comparison.
      Fingerprint = wcag + selector/element substring — identifies unique issues. */
  function buildFingerprints(pages){
    var fp={};
    (pages||[]).forEach(function(pg){
      var url=pg.url||pg.p||"";
      fp[url]=(pg.issues||[]).map(function(iss){
        return(iss.wcag||"")+"||"+(iss.selector||iss.el||"").substring(0,60);
      });
    });
    return fp;
  }

  /** Compare current scan against verification baseline.
      Returns {resolved, persistent, regression, scoreBefore, scoreAfter} */
  function computeVerification(baseline, currentPages){
    if(!baseline||!currentPages)return null;
    var newFp=buildFingerprints(currentPages);
    var resolved=0,persistent=0,regression=0;
    /* Check baseline issues against new scan */
    Object.keys(baseline.fingerprints).forEach(function(url){
      var oldIssues=baseline.fingerprints[url]||[];
      var newIssues=newFp[url]||[];
      var newSet={};
      newIssues.forEach(function(f){newSet[f]=(newSet[f]||0)+1});
      oldIssues.forEach(function(f){
        if(newSet[f]&&newSet[f]>0){persistent++;newSet[f]--}
        else{resolved++}
      });
    });
    /* Check for regressions (new issues not in baseline) */
    Object.keys(newFp).forEach(function(url){
      var oldIssues=baseline.fingerprints[url]||[];
      var newIssues=newFp[url]||[];
      var oldSet={};
      oldIssues.forEach(function(f){oldSet[f]=(oldSet[f]||0)+1});
      newIssues.forEach(function(f){
        if(oldSet[f]&&oldSet[f]>0){oldSet[f]--}
        else{regression++}
      });
    });
    var newTotal=currentPages.reduce(function(x,p){return x+(p.totalIssues||0)},0);
    var scoreAfter=newTotal>0?Math.round((newTotal-countOpen(currentPages.reduce(function(a,p){return a.concat(p.issues||[])},[])))/Math.max(1,newTotal)*100):100;
    return{resolved:resolved,persistent:persistent,regression:regression,
      totalBefore:baseline.totalIssues,totalAfter:newTotal,
      scoreBefore:baseline.score,scoreAfter:overallScore,
      timestamp:new Date().toISOString()};
  }

  /** Start verification: snapshot baseline, then re-scan.
      For demo mode: re-runs buildScan immediately.
      For extension scans: saves baseline and shows instructions. */
  function startVerification(){
    /* License enforcement — re-scan counts against page quota */
    if(!licenseGate("verify"))return;
    if(!wp.length){showToast("No scan data to verify against","error");return}
    var totalIss=wp.reduce(function(x,p){return x+(p.totalIssues||0)},0);
    var baseline={
      domain:domain,
      fingerprints:buildFingerprints(wp),
      totalIssues:totalIss,
      score:overallScore,
      pageCount:wp.length,
      timestamp:new Date().toISOString()
    };
    setVerifyBaseline(baseline);
    setVerifyResult(null);
    if(isDemo){
      /* Demo mode: re-run scan immediately */
      showToast("\u{1F504} Re-scanning "+domain+" to verify fixes...");
      scan();
    } else {
      /* Extension mode: instruct user */
      showToast("\u2705 Baseline saved ("+totalIss+" issues). Re-scan "+domain+" with the Chrome extension, then import. PiARIS will auto-compare.","success");
    }
  }

  /** Clear verification state */
  function clearVerification(){setVerifyBaseline(null);setVerifyResult(null)}

  /* Auto-compute verification when new scan data arrives with an active baseline */
  useEffect(function(){
    if(!verifyBaseline||!wp.length)return;
    if(verifyBaseline.domain!==domain){setVerifyBaseline(null);setVerifyResult(null);return}
    /* Only compute if data has changed (different page count or timestamp) */
    if(wp.length>0&&!verifyResult){
      var result=computeVerification(verifyBaseline, wp);
      if(result) setVerifyResult(result);
    }
  },[wp, verifyBaseline, domain]);

  /* ── Activity log state (P2) ── */
  var _activity=useState([]),activity=_activity[0],setActivity=_activity[1];
  var _activityLoading=useState(false),activityLoading=_activityLoading[0],setActivityLoading=_activityLoading[1];
  var _activityError=useState(null),activityError=_activityError[0],setActivityError=_activityError[1];

  function loadActivity(){
    setActivityLoading(true);
    setActivityError(null);
    fetch("/api/manage/activity-log?limit=50",{credentials:"include"})
      .then(function(r){
        if(!r.ok){var e=new Error("HTTP "+r.status);e.status=r.status;throw e;}
        return r.json();
      })
      .then(function(data){
        setActivity(data.entries||[]);
        setActivityLoading(false);
      })
      .catch(function(err){
        setActivityError((err&&err.message)||"Could not reach the activity log.");
        setActivityLoading(false);
      });
  }

  /* ── Invite user — POST /api/manage/users/invite
     The backend sends via Azure Communication Services when COMMS_CONN_STRING
     + COMMS_SENDER_ADDRESS are provisioned. We always copy the invite URL to
     the clipboard as a fallback so admins can hand it off directly if the
     send fails or ACS isn't configured in the current environment. */
  function inviteUser(payload){
    var email = (payload&&payload.email||"").trim();
    if(!email){ showToast("Email is required","error"); return Promise.resolve(false); }
    return fetch("/api/manage/users/invite",{
      method:"POST",credentials:"include",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ email: email, tier: payload.tier || "free" })
    }).then(function(r){return r.ok?r.json():r.json().then(function(b){throw new Error(b.error||"Invite failed")})})
      .then(function(data){
        if(!data.invited){ showToast(data.error||"Invite failed","error"); return false; }
        /* Always copy the link — useful even on a successful send so admins
           can re-share or post in Slack/Teams. */
        if(data.inviteUrl && typeof navigator!=="undefined" && navigator.clipboard){
          navigator.clipboard.writeText(data.inviteUrl).catch(function(){});
        }
        if(data.emailSent){
          showToast("✅ Invite sent to "+email+" (link also copied to clipboard).");
        } else if(data.emailReason==="not_configured"){
          showToast("Invite created — email not configured in this environment. Invite link copied to clipboard.","error");
        } else {
          showToast("Invite created, but email send failed ("+(data.emailReason||"unknown")+"). Invite link copied to clipboard.","error");
        }
        if(adminTab==="activity") loadActivity();
        return true;
      })
      .catch(function(e){ showToast("Invite failed: "+e.message,"error"); return false; });
  }

  function loadDocMayaKeys(){
    setDmKeyLoading(true);
    fetch("/api/manage/docmaya-keys",{credentials:"include"}).then(function(r){return r.ok?r.json():{keys:[]}})
      .then(function(data){setDmKeys(data.keys||[]);setDmKeyLoading(false)})
      .catch(function(){setDmKeyLoading(false)});
  }
  function generateDocMayaKey(domain){
    if(!domain){showToast("Select a licensed domain first","error");return}
    fetch("/api/manage/docmaya-keys",{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({action:"generate",domain:domain})
    }).then(function(r){return r.json()}).then(function(data){
      if(data.siteKey){showToast("DocMaya key generated for "+domain+": "+data.siteKey);loadDocMayaKeys()}
      else{showToast(data.error||"Key generation failed","error")}
    }).catch(function(e){showToast("Error: "+e.message,"error")});
  }
  function revokeDocMayaKey(keyId){
    fetch("/api/manage/docmaya-keys",{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({action:"revoke",keyId:keyId})
    }).then(function(r){return r.json()}).then(function(data){
      if(data.revoked){showToast("Key revoked");loadDocMayaKeys()}
      else{showToast(data.error||"Revoke failed","error")}
    }).catch(function(e){showToast("Error: "+e.message,"error")});
  }

  function loadUsers(){
    setUsersLoading(true);
    /* Pull a single page wide enough for the admin module to filter
       client-side. Server-side tier filtering can be re-introduced
       (?tier=<x>) when the user list grows past one page. */
    fetch("/api/manage/users?limit=200",{credentials:"include"}).then(function(r){return r.ok?r.json():{users:[]}})
      .then(function(data){setUsersList(data.users||[]);setUsersLoading(false)})
      .catch(function(){setUsersLoading(false)});
  }
  function updateUser(id,updates,label){
    setUserUpdating(id);
    fetch("/api/manage/user/"+encodeURIComponent(id),{method:"PATCH",credentials:"include",
      headers:{"Content-Type":"application/json"},body:JSON.stringify(updates)
    }).then(function(r){return r.json()}).then(function(data){
      setUserUpdating(null);
      if(data.updated){
        showToast("\u2705 "+(label||"User updated"));
        // Merge server reply; re-derive enriched licenses from licList
        setUsersList(usersList.map(function(u){
          if(u.id!==id)return u;
          var merged=Object.assign({},u,data.user);
          var ids=merged.assignedLicenses||u.assignedLicenses||[];
          merged.licenses=ids.map(function(lid){
            var lic=(licList||[]).find(function(l){return l.id===lid});
            return lic?{id:lic.id,domain:lic.domain,orgName:lic.orgName,tier:lic.tier,status:lic.status,product:lic.product||"watchtower"}
                       :{id:lid,domain:"(missing)",tier:"unknown",status:"missing"};
          });
          return merged;
        }));
      }else{showToast(data.error||"Update failed","error")}
    }).catch(function(e){setUserUpdating(null);showToast("Error: "+e.message,"error")});
  }

  /* Admin panel — now powered by the app/src/admin module tree.
     State + fetch lifecycles stay in App.jsx for this commit (so the
     existing helpers, toasts, and auth flow continue to work); AdminPanel
     owns its own modal UI state and presentation. */
  var licensesPage = E(AdminPanel, {
    adminTab: adminTab,
    setAdminTab: setAdminTab,
    showToast: showToast,
    data: {
      licenses: licList,
      licensesLoading: licLoading,
      licensesError: licError,
      users: usersList,
      usersLoading: usersLoading,
      usersError: null,
      usersUpdatingId: userUpdating,
      dmKeys: dmKeys,
      dmKeysLoading: dmKeyLoading,
      dmKeysError: null,
      activity: activity,
      activityLoading: activityLoading,
      activityError: activityError
    },
    actions: {
      loadLicenses: loadLicenses,
      createLicense: createLicenseFromPayload,
      /* PR-5.9: pass the whole license obj so the by-id endpoints get
         the right id regardless of scope. The legacy `l.domain` route
         only worked for domain-scoped rows; the new APIs key on
         license.id so user-scoped and unbound coupons work too. */
      suspendLicense:  function(l){ return suspendLicense(l);  },
      activateLicense: function(l){ return activateLicense(l); },
      deleteLicense:   function(l){ return deleteLicense(l);   },
      loadUsers: loadUsers,
      changeUserTier: function(u,t){ updateUser(u.id,{tier:t},"Tier → "+t); },
      toggleUserApproved: function(u){ updateUser(u.id,{approved:!u.approved},u.approved?"Un-approved":"Approved"); },
      assignLicenses: function(u,ids){ updateUser(u.id,{assignedLicenses:ids},"Licenses updated for "+(u.email||u.id)); },
      loadDmKeys: loadDocMayaKeys,
      generateDmKey: generateDocMayaKey,
      revokeDmKey: function(k){ revokeDocMayaKey(k.id); },
      loadActivity: loadActivity,
      inviteUser: inviteUser
    }
  });

  /* ═══ PiARIS DELTA AUDITOR — Domain-guided scan comparison ═══ */
  /** Load a scan session for Delta Recon comparison (side = "A" or "B") */
  function loadCompScan(sessionId, side){
    if(!sessionId){if(side==="A"){setCmpDataA(null);setCmpResult(null)}else{setCmpDataB(null);setCmpResult(null)}return}
    setCmpLoading(true);
    fetch("/api/session-stream/"+sessionId+"/export",{credentials:"include"})
      .then(function(r){return r.ok?r.json():null})
      .then(function(data){
        if(!data||!data.webPages){showToast("Failed to load scan data","error");setCmpLoading(false);return}
        /* Domain validation — backend checkpoint */
        var loadedDomain=(data.scan&&data.scan.domain)||"";
        if(cmpDomain&&loadedDomain&&loadedDomain!==cmpDomain){
          showToast("\u26A0 Domain mismatch: expected "+cmpDomain+", got "+loadedDomain+". Comparison requires same domain.","error");
          setCmpLoading(false);return;
        }
        if(side==="A"){setCmpDataA(data);setCmpA(sessionId)}else{setCmpDataB(data);setCmpB(sessionId)}
        setCmpLoading(false);
        if(side==="A"&&!cmpB||side==="B"&&!cmpA){setCmpStep(2)}
      }).catch(function(e){showToast("Load error: "+e.message,"error");setCmpLoading(false)});
  }
  /** Execute Delta Recon comparison — diff two loaded scans, compute resolved/persistent/regression */
  function runCompare(){
    if(!cmpDataA||!cmpDataB){showToast("Select two scans to compare","error");return}
    /* Final domain match check */
    var domA=(cmpDataA.scan&&cmpDataA.scan.domain)||"";
    var domB=(cmpDataB.scan&&cmpDataB.scan.domain)||"";
    if(domA&&domB&&domA!==domB){
      showToast("\u26A0 Cannot compare different domains: "+domA+" vs "+domB,"error");
      setCmpResult(null);return;
    }
    var pagesA={};var pagesB={};
    (cmpDataA.webPages||[]).forEach(function(p){pagesA[p.url||p.p]=p});
    (cmpDataB.webPages||[]).forEach(function(p){pagesB[p.url||p.p]=p});
    var allUrls={};
    Object.keys(pagesA).forEach(function(u){allUrls[u]=true});
    Object.keys(pagesB).forEach(function(u){allUrls[u]=true});
    var resolved=0,persistent=0,regression=0,removedPages=0,addedPages=0;
    var issuesA=0,issuesB=0,critA=0,critB=0;
    var pageResults=[];
    Object.keys(allUrls).forEach(function(url){
      var a=pagesA[url];var b=pagesB[url];
      if(a)issuesA+=(a.issues||[]).length;
      if(b)issuesB+=(b.issues||[]).length;
      if(a)(a.issues||[]).forEach(function(i){if(i.sev==="critical")critA++});
      if(b)(b.issues||[]).forEach(function(i){if(i.sev==="critical")critB++});
      if(!a&&b){addedPages++;regression+=(b.issues||[]).length;pageResults.push({url:url,title:b.n||url,status:"added",issuesA:0,issuesB:(b.issues||[]).length,resolved:0,persistent:0,regression:(b.issues||[]).length});return}
      if(a&&!b){removedPages++;resolved+=(a.issues||[]).length;pageResults.push({url:url,title:a.n||url,status:"removed",issuesA:(a.issues||[]).length,issuesB:0,resolved:(a.issues||[]).length,persistent:0,regression:0});return}
      var fpA={};var fpB={};
      (a.issues||[]).forEach(function(i){var fp=(i.wcag||"")+"||"+(i.selector||i.el||"").substring(0,60);fpA[fp]=(fpA[fp]||0)+1});
      (b.issues||[]).forEach(function(i){var fp=(i.wcag||"")+"||"+(i.selector||i.el||"").substring(0,60);fpB[fp]=(fpB[fp]||0)+1});
      var pRes=0,pPer=0,pReg=0;
      Object.keys(fpA).forEach(function(fp){if(fpB[fp]){pPer++}else{pRes++}});
      Object.keys(fpB).forEach(function(fp){if(!fpA[fp]){pReg++}});
      resolved+=pRes;persistent+=pPer;regression+=pReg;
      var st=pReg>0?"worse":pRes>0?"better":"same";
      pageResults.push({url:url,title:a.n||b.n||url,status:st,issuesA:(a.issues||[]).length,issuesB:(b.issues||[]).length,resolved:pRes,persistent:pPer,regression:pReg});
    });
    pageResults.sort(function(a,b){return(b.regression-b.resolved)-(a.regression-a.resolved)});
    var scoreA=(cmpDataA.summary&&cmpDataA.summary.score)||0;
    var scoreB=(cmpDataB.summary&&cmpDataB.summary.score)||0;
    setCmpResult({resolved:resolved,persistent:persistent,regression:regression,
      issuesA:issuesA,issuesB:issuesB,critA:critA,critB:critB,
      scoreA:scoreA,scoreB:scoreB,scoreDelta:scoreB-scoreA,
      pagesA:Object.keys(pagesA).length,pagesB:Object.keys(pagesB).length,
      addedPages:addedPages,removedPages:removedPages,pages:pageResults,
      domain:cmpDomain||domA||domB||"Unknown",
      dateA:(cmpDataA.scan&&cmpDataA.scan.completedAt)||"",
      dateB:(cmpDataB.scan&&cmpDataB.scan.completedAt)||""});
    setCmpStep(3);
  }
  useEffect(function(){if(cmpDataA&&cmpDataB)runCompare()},[cmpDataA,cmpDataB]);

  function resetCompare(){setCmpDomain(null);setCmpA(null);setCmpB(null);setCmpDataA(null);setCmpDataB(null);setCmpResult(null);setCmpStep(1)}

  /* Build domain list from sessions */
  var cmpDomains={};
  (userSessions||[]).forEach(function(s){
    var d=s.domain||"";if(!d)return;
    if(!cmpDomains[d])cmpDomains[d]={domain:d,sessions:[],latestDate:""};
    cmpDomains[d].sessions.push(s);
    var dt=s.completedAt||s.createdAt||"";
    if(dt>cmpDomains[d].latestDate)cmpDomains[d].latestDate=dt;
  });
  var cmpDomainList=Object.values(cmpDomains).filter(function(d){return d.sessions.length>=2}).sort(function(a,b){return a.latestDate>b.latestDate?-1:1});
  var cmpDomainSessions=cmpDomain?((cmpDomains[cmpDomain]&&cmpDomains[cmpDomain].sessions)||[]).sort(function(a,b){var da=a.completedAt||a.createdAt||"";var db=b.completedAt||b.createdAt||"";return da>db?-1:1}):[];

  var comparePage=E("div",null,
    E(SubViewHero,{
      product:"deltarecon",
      title:"Delta Recon",
      subtitle:"Track remediation progress by comparing scans of the same domain",
      right:E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
        E("button",{onClick:function(){openScanPicker("compare")},style:{minHeight:38,padding:"8px 14px",borderRadius:8,border:"1.5px solid rgba(255,255,255,0.4)",background:"rgba(255,255,255,0.12)",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}},"\u{1F4C2} Load Scan"),
        cmpStep>1?E("button",{onClick:resetCompare,style:{minHeight:38,padding:"8px 14px",borderRadius:8,border:"1.5px solid rgba(255,255,255,0.4)",background:"rgba(255,255,255,0.12)",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}},"\u21BB Start Over"):null)
    }),
    /* Step indicator */
    E("div",{style:{display:"flex",gap:0,marginBottom:16,borderRadius:8,overflow:"hidden",border:"1.5px solid "+P.bdr}},
      [1,2,3].map(function(s){
        var labels=["1. Select Domain","2. Pick Two Scans","3. View Results"];
        var active=cmpStep===s;var done=cmpStep>s;
        return E("div",{key:s,style:{flex:1,padding:"8px 12px",textAlign:"center",fontSize:11,fontWeight:active?800:done?700:500,
          background:active?"linear-gradient(135deg,"+P.pri+","+P.accent+")":done?P.emeraldLt:P.card,
          color:active?"#fff":done?P.emerald:P.slateL,borderRight:s<3?"1px solid "+P.bdr:"none"}},
          (done?"\u2713 ":"")+labels[s-1]);
      })),

    /* Step 1: Select Domain */
    cmpStep===1?E("div",null,
      cmpDomainList.length===0?E("div",{style:{textAlign:"center",padding:"40px 20px",borderRadius:14,border:"2px dashed "+P.bdr,color:P.slateL}},
        E("div",{style:{fontSize:40,marginBottom:12}},"\u{1F504}"),
        E("div",{style:{fontSize:14,fontWeight:700,marginBottom:6}},"No Domains with Multiple Scans"),
        E("div",{style:{fontSize:11}},"Compare requires at least 2 scans of the same domain. Run additional scans to enable comparison."),
        userSessions.length===0?E("div",{style:{marginTop:12}},E("button",{onClick:loadUserSessions,style:{padding:"8px 16px",borderRadius:8,border:"none",background:P.pri,color:"#fff",fontSize:11,fontWeight:700}},"\u2601\uFE0F Load Cloud Sessions")):null
      ):E("div",null,
        E("div",{style:{fontSize:12,fontWeight:700,marginBottom:10}},"Select a domain to compare scans:"),
        E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10}},
          cmpDomainList.map(function(d){
            return E("div",Object.assign({key:d.domain,style:{padding:"16px",borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,cursor:"pointer",transition:"all .15s"}},kbNav(function(){setCmpDomain(d.domain);setCmpStep(2)})),
              E("div",{style:{fontSize:14,fontWeight:800,marginBottom:4}},d.domain),
              E("div",{style:{display:"flex",gap:6,flexWrap:"wrap"}},
                E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.priLt,color:P.pri,fontSize:10,fontWeight:700}},d.sessions.length+" scans"),
                E("span",{style:{padding:"2px 8px",borderRadius:6,background:P.bgS,color:P.slateM,fontSize:10,fontWeight:600}},"Latest: "+(d.latestDate?new Date(d.latestDate).toLocaleDateString():"unknown"))));
          })))
    ):null,

    /* Step 2: Pick Two Scans */
    cmpStep===2?E("div",null,
      E("div",{style:{padding:"10px 14px",borderRadius:8,background:P.priLt,border:"1px solid "+P.pri+"20",marginBottom:14,display:"flex",alignItems:"center",gap:8}},
        E("span",{style:{fontSize:14}},"\u{1F310}"),
        E("span",{style:{fontSize:12,fontWeight:700,color:P.pri}},cmpDomain),
        E("span",{style:{fontSize:10,color:P.slateM}},cmpDomainSessions.length+" scans available"),
        E("button",{onClick:function(){setCmpStep(1);setCmpDomain(null);setCmpA(null);setCmpB(null);setCmpDataA(null);setCmpDataB(null)},style:{marginLeft:"auto",padding:"4px 10px",borderRadius:6,border:"1px solid "+P.bdr,background:P.card,fontSize:10,cursor:"pointer"}},"Change Domain")),
      E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 40px 1fr",gap:10,marginBottom:16,alignItems:"end"}},
        E("div",null,
          E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,marginBottom:4,display:"block"}},"SCAN A (Earlier)"),
          E("select",{value:cmpA||"",onChange:function(ev){loadCompScan(ev.target.value,"A")},style:{width:"100%",padding:"10px 12px",borderRadius:8,border:"1.5px solid "+P.bdr,fontSize:12,fontFamily:F,background:P.card}},
            E("option",{value:""},"Select scan..."),
            cmpDomainSessions.map(function(s){
              var dt=s.completedAt?new Date(s.completedAt).toLocaleDateString():s.createdAt?new Date(s.createdAt).toLocaleDateString():"";
              var pgs=s.summary?s.summary.totalPages||0:s.pageCount||0;
              var iss=s.summary?s.summary.totalRules||0:0;
              return E("option",{key:s.id,value:s.id,disabled:s.id===cmpB},dt+" \u2014 "+pgs+" pages, "+iss+" issues");
            }))),
        !isMobile?E("div",{style:{textAlign:"center",fontSize:18,fontWeight:800,color:P.slateL,paddingBottom:10}},"vs"):null,
        E("div",null,
          E("label",{style:{fontSize:10,fontWeight:700,color:P.slateM,marginBottom:4,display:"block"}},"SCAN B (Later)"),
          E("select",{value:cmpB||"",onChange:function(ev){loadCompScan(ev.target.value,"B")},style:{width:"100%",padding:"10px 12px",borderRadius:8,border:"1.5px solid "+P.bdr,fontSize:12,fontFamily:F,background:P.card}},
            E("option",{value:""},"Select scan..."),
            cmpDomainSessions.map(function(s){
              var dt=s.completedAt?new Date(s.completedAt).toLocaleDateString():s.createdAt?new Date(s.createdAt).toLocaleDateString():"";
              var pgs=s.summary?s.summary.totalPages||0:s.pageCount||0;
              var iss=s.summary?s.summary.totalRules||0:0;
              return E("option",{key:s.id,value:s.id,disabled:s.id===cmpA},dt+" \u2014 "+pgs+" pages, "+iss+" issues");
            })))),
      cmpLoading?E("div",{style:{textAlign:"center",padding:30,color:P.slateL}},"\u23F3 Loading scan data..."):null
    ):null,

    /* Step 3: Results */
    cmpStep===3&&cmpResult?E("div",null,
      /* Domain banner */
      E("div",{style:{padding:"10px 14px",borderRadius:8,background:"linear-gradient(135deg,"+P.priLt+","+P.emeraldLt+"40)",border:"1px solid "+P.pri+"20",marginBottom:14,display:"flex",alignItems:"center",gap:8}},
        E("span",{style:{fontSize:14}},"\u{1F310}"),
        E("span",{style:{fontSize:13,fontWeight:800,color:P.pri}},cmpResult.domain),
        E("span",{style:{fontSize:10,color:P.slateM}},(cmpResult.dateA?new Date(cmpResult.dateA).toLocaleDateString():"?")+" \u2192 "+(cmpResult.dateB?new Date(cmpResult.dateB).toLocaleDateString():"?")),
        E("button",{onClick:function(){setCmpStep(2);setCmpResult(null)},style:{marginLeft:"auto",padding:"4px 10px",borderRadius:6,border:"1px solid "+P.bdr,background:P.card,fontSize:10,cursor:"pointer"}},"Change Scans")),
      /* Export buttons */
      E("div",{style:{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}},
        E("button",{onClick:function(){
          /* JSON export */
          var exportData={platform:"PiARIS Delta Recon",vendor:"Pi Consultants",website:"pi-consulting.us",generated:new Date().toISOString(),version:APP_VERSION,
            domain:cmpResult.domain,scanA:{sessionId:cmpA,date:cmpResult.dateA,pages:cmpResult.pagesA,issues:cmpResult.issuesA,critical:cmpResult.critA,score:cmpResult.scoreA},
            scanB:{sessionId:cmpB,date:cmpResult.dateB,pages:cmpResult.pagesB,issues:cmpResult.issuesB,critical:cmpResult.critB,score:cmpResult.scoreB},
            summary:{resolved:cmpResult.resolved,persistent:cmpResult.persistent,regression:cmpResult.regression,scoreDelta:cmpResult.scoreDelta,addedPages:cmpResult.addedPages,removedPages:cmpResult.removedPages},
            pages:cmpResult.pages};
          var b=new Blob([JSON.stringify(exportData,null,2)],{type:"application/json"});
          var a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="paris-compare-"+(cmpResult.domain||"report").replace(/[^a-z0-9]/gi,"-")+"-"+new Date().toISOString().split("T")[0]+".json";a.click();
          showToast("\u{1F4BE} Compare data exported as JSON");
        },style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.pri+"40",background:P.priLt,color:P.pri,fontSize:11,fontWeight:700}},"\u{1F4BE} Export JSON"),
        E("button",{onClick:function(){
          /* Generate printable HTML report */
          var dateA=cmpResult.dateA?new Date(cmpResult.dateA).toLocaleDateString():"Scan A";
          var dateB=cmpResult.dateB?new Date(cmpResult.dateB).toLocaleDateString():"Scan B";
          var improved=cmpResult.pages.filter(function(p){return p.status==="better"}).length;
          var worse=cmpResult.pages.filter(function(p){return p.status==="worse"}).length;
          var same=cmpResult.pages.filter(function(p){return p.status==="same"}).length;
          var html='<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>PiARIS Delta Recon Report \u2014 '+cmpResult.domain+'</title>'
            +'<style>body{font-family:Inter,system-ui,sans-serif;max-width:900px;margin:0 auto;padding:24px;color:#1E293B;font-size:13px}h1{font-size:20px;border-bottom:3px solid #4F46E5;padding-bottom:12px}h2{font-size:15px;margin-top:24px;border-bottom:1px solid #E2E8F0;padding-bottom:6px}.cards{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:16px 0}.card{padding:16px;border-radius:10px;text-align:center;border:1.5px solid #E2E8F0}.card .num{font-size:28px;font-weight:900}.res{background:#F0FDF4;border-color:#05966530}.res .num{color:#059669}.per{background:#FFFBEB;border-color:#D9770630}.per .num{color:#D97706}.reg{background:#FEF2F2;border-color:#DC262630}.reg .num{color:#DC2626}.sco{background:#EEF2FF;border-color:#6366F130}.sco .num{color:#6366F1}table{width:100%;border-collapse:collapse;margin:12px 0;font-size:11px}th,td{border:1px solid #E2E8F0;padding:6px 10px;text-align:left}th{background:#F8FAFC;font-weight:700}.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700}.better{background:#ECFDF5;color:#059669}.worse{background:#FEE2E2;color:#DC2626}.same{background:#F1F5F9;color:#64748B}.footer{text-align:center;padding:20px 0;border-top:1px solid #E2E8F0;margin-top:32px;font-size:10px;color:#94A3B8}@media print{body{font-size:11px}.cards{grid-template-columns:repeat(4,1fr)}}</style></head><body>'
            +'<h1>\u{1F504} PiARIS Delta Recon Report</h1>'
            +'<p><strong>Domain:</strong> '+cmpResult.domain+' | <strong>Scan A:</strong> '+dateA+' | <strong>Scan B:</strong> '+dateB+' | <strong>Generated:</strong> '+new Date().toLocaleDateString()+'</p>'
            +'<div class="cards"><div class="card res"><div class="num">'+cmpResult.resolved+'</div><div>\u2713 Resolved</div></div><div class="card per"><div class="num">'+cmpResult.persistent+'</div><div>\u23F8 Persistent</div></div><div class="card reg"><div class="num">'+cmpResult.regression+'</div><div>\u26A0 Regressions</div></div><div class="card sco"><div class="num">'+(cmpResult.scoreDelta>0?"+":"")+cmpResult.scoreDelta+'%</div><div>Score \u0394</div></div></div>'
            +'<h2>Scan Comparison</h2><table><tr><th>Metric</th><th>Scan A ('+dateA+')</th><th>Scan B ('+dateB+')</th><th>Change</th></tr>'
            +'<tr><td>Pages</td><td>'+cmpResult.pagesA+'</td><td>'+cmpResult.pagesB+'</td><td>'+(cmpResult.pagesB-cmpResult.pagesA)+'</td></tr>'
            +'<tr><td>Total Issues</td><td>'+cmpResult.issuesA+'</td><td>'+cmpResult.issuesB+'</td><td>'+(cmpResult.issuesB-cmpResult.issuesA)+'</td></tr>'
            +'<tr><td>Critical</td><td>'+cmpResult.critA+'</td><td>'+cmpResult.critB+'</td><td>'+(cmpResult.critB-cmpResult.critA)+'</td></tr>'
            +'<tr><td>Score</td><td>'+cmpResult.scoreA+'%</td><td>'+cmpResult.scoreB+'%</td><td>'+(cmpResult.scoreDelta>0?"+":"")+cmpResult.scoreDelta+'%</td></tr></table>'
            +'<h2>Page-Level Changes ('+cmpResult.pages.length+' pages)</h2><p>'+improved+' improved | '+worse+' worse | '+same+' unchanged</p>'
            +'<table><tr><th>Status</th><th>Page</th><th>Resolved</th><th>Persistent</th><th>Regressions</th><th>A\u2192B</th></tr>'
            +cmpResult.pages.map(function(pg){var cls=pg.status==="better"?"better":pg.status==="worse"?"worse":"same";return'<tr><td><span class="badge '+cls+'">'+pg.status+'</span></td><td>'+pg.title+'</td><td>'+pg.resolved+'</td><td>'+pg.persistent+'</td><td>'+pg.regression+'</td><td>'+pg.issuesA+'\u2192'+pg.issuesB+'</td></tr>'}).join("")
            +'</table>'
            +'<div class="footer">\u00A9 '+new Date().getFullYear()+' Pi Consultants \u00B7 pi-consulting.us \u00B7 PiARIS\u2122 v'+APP_VERSION+'</div></body></html>';
          var w=window.open("","_blank");if(w){w.document.write(html);w.document.close()}
        },style:{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:11,fontWeight:700,boxShadow:"0 2px 8px "+P.pri+"30"}},"\u{1F4CA} View Compare Report"),
        E("button",{onClick:function(){
          showToast("\u23F3 Generating DOCX report...");
          var payload={
            domain:cmpResult.domain||domain,
            scanA:{date:cmpResult.dateA,pages:cmpResult.pagesA,issues:cmpResult.issuesA,critical:cmpResult.critA,score:cmpResult.scoreA},
            scanB:{date:cmpResult.dateB,pages:cmpResult.pagesB,issues:cmpResult.issuesB,critical:cmpResult.critB,score:cmpResult.scoreB},
            summary:{resolved:cmpResult.resolved,persistent:cmpResult.persistent,regression:cmpResult.regression,scoreDelta:cmpResult.scoreDelta},
            pages:cmpResult.pages||[]
          };
          fetch("/api/delta-recon-report",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)}).then(function(res){
            if(!res.ok)throw new Error("Report generation failed: "+res.status);
            return res.blob();
          }).then(function(blob){
            var url=URL.createObjectURL(blob);var a=document.createElement("a");a.href=url;
            a.download="PiARIS-Delta-Recon-"+(cmpResult.domain||domain).replace(/[^a-zA-Z0-9]/g,"-")+".docx";
            a.click();URL.revokeObjectURL(url);showToast("\u2713 DOCX report downloaded");
          }).catch(function(e){showToast("Report failed: "+e.message,"error")});
        },style:{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#312E81,"+P.pri+")",color:"#fff",fontSize:11,fontWeight:700}},"\u{1F4C4} DOCX Report")),
      E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10,marginBottom:16}},
        E("div",{style:{padding:"16px",borderRadius:12,background:"linear-gradient(135deg,"+P.emeraldLt+","+P.emeraldLt+")",border:"1.5px solid "+P.emerald+"30",textAlign:"center"}},
          E("div",{style:{fontSize:28,fontWeight:900,color:P.emerald}},cmpResult.resolved),
          E("div",{style:{fontSize:11,fontWeight:700,color:P.emerald}},"\u2713 Resolved")),
        E("div",{style:{padding:"16px",borderRadius:12,background:"linear-gradient(135deg,"+P.sMajBg+","+P.amberLt+")",border:"1.5px solid "+P.amber+"30",textAlign:"center"}},
          E("div",{style:{fontSize:28,fontWeight:900,color:P.amber}},cmpResult.persistent),
          E("div",{style:{fontSize:11,fontWeight:700,color:P.amber}},"\u23F8 Persistent")),
        E("div",{style:{padding:"16px",borderRadius:12,background:"linear-gradient(135deg,"+P.sCritBg+","+P.coralLt+")",border:"1.5px solid "+P.sCrit+"30",textAlign:"center"}},
          E("div",{style:{fontSize:28,fontWeight:900,color:P.sCrit}},cmpResult.regression),
          E("div",{style:{fontSize:11,fontWeight:700,color:P.sCrit}},"\u26A0 Regressions")),
        E("div",{style:{padding:"16px",borderRadius:12,background:"linear-gradient(135deg,"+P.priLt+","+P.priLt+")",border:"1.5px solid #6366F130",textAlign:"center"}},
          E("div",{style:{fontSize:28,fontWeight:900,color:cmpResult.scoreDelta>0?"#059669":cmpResult.scoreDelta<0?"#DC2626":"#6366F1"}},
            (cmpResult.scoreDelta>0?"+":"")+cmpResult.scoreDelta+"%"),
          E("div",{style:{fontSize:11,fontWeight:700,color:P.pri}},"Score \u0394"))),
      /* Side-by-side scan stats */
      E("div",{style:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}},
        E("div",{style:{padding:"12px 16px",borderRadius:10,border:"1.5px solid "+P.sCrit+"20",background:P.sCritBg+"30"}},
          E("div",{style:{fontSize:10,fontWeight:700,color:P.sCrit,marginBottom:4}},"SCAN A \u2014 "+(cmpResult.dateA?new Date(cmpResult.dateA).toLocaleDateString():"")),
          E("div",{style:{display:"flex",gap:8,fontSize:10,color:P.slateM,flexWrap:"wrap"}},
            E("span",null,cmpResult.pagesA+" pages"),E("span",null,cmpResult.issuesA+" issues"),E("span",{style:{color:P.sCrit}},cmpResult.critA+" critical"),E("span",null,"Score: "+cmpResult.scoreA+"%"))),
        E("div",{style:{padding:"12px 16px",borderRadius:10,border:"1.5px solid "+P.emerald+"20",background:P.emeraldLt+"30"}},
          E("div",{style:{fontSize:10,fontWeight:700,color:P.emerald,marginBottom:4}},"SCAN B \u2014 "+(cmpResult.dateB?new Date(cmpResult.dateB).toLocaleDateString():"")),
          E("div",{style:{display:"flex",gap:8,fontSize:10,color:P.slateM,flexWrap:"wrap"}},
            E("span",null,cmpResult.pagesB+" pages"),E("span",null,cmpResult.issuesB+" issues"),E("span",{style:{color:P.sCrit}},cmpResult.critB+" critical"),E("span",null,"Score: "+cmpResult.scoreB+"%")))),
      /* Page-level breakdown */
      E("div",{style:{borderRadius:12,border:"1.5px solid "+P.bdr,overflow:"hidden"}},
        E("div",{style:{padding:"10px 14px",background:P.bgS,borderBottom:"1px solid "+P.bdr,display:"flex",justifyContent:"space-between",alignItems:"center"}},
          E("span",{style:{fontSize:12,fontWeight:700}},"\u{1F4C4} Page-Level Changes ("+cmpResult.pages.length+" pages)"),
          E("div",{style:{display:"flex",gap:4}},
            E("span",{style:{padding:"2px 8px",borderRadius:4,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},cmpResult.pages.filter(function(p){return p.status==="better"}).length+" improved"),
            E("span",{style:{padding:"2px 8px",borderRadius:4,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700}},cmpResult.pages.filter(function(p){return p.status==="worse"}).length+" worse"),
            E("span",{style:{padding:"2px 8px",borderRadius:4,background:P.bgS,color:P.slateM,fontSize:10,fontWeight:700}},cmpResult.pages.filter(function(p){return p.status==="same"}).length+" unchanged"))),
        E("div",{style:{maxHeight:400,overflow:"auto"}},
          cmpResult.pages.slice().sort(function(a,b){
            var order={worse:0,added:1,better:2,same:3,removed:4};
            var oa=order[a.status]!=null?order[a.status]:3;
            var ob=order[b.status]!=null?order[b.status]:3;
            if(oa!==ob)return oa-ob;
            if((a.regression||0)!==(b.regression||0))return(b.regression||0)-(a.regression||0);
            return(a.title||"").localeCompare(b.title||"");
          }).map(function(pg,pi){
            var statusCol=pg.status==="better"?P.emerald:pg.status==="worse"?P.sCrit:pg.status==="added"?P.pri:pg.status==="removed"?P.slateM:P.slateL;
            var statusLabel=pg.status==="better"?"\u2191 Improved":pg.status==="worse"?"\u2193 Worse":pg.status==="added"?"\u2795 New":pg.status==="removed"?"\u2796 Removed":"\u2014 Same";
            return E("div",{key:pi,style:{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",borderBottom:"1px solid "+P.bdr,background:pi%2===0?P.card:"#FAFBFE"}},
              E("span",{style:{padding:"2px 8px",borderRadius:4,background:statusCol+"15",color:statusCol,fontSize:10,fontWeight:700,flexShrink:0,minWidth:75,textAlign:"center"}},statusLabel),
              E("div",{style:{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:11,fontWeight:600}},pg.title),
              pg.resolved>0?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u2713 "+pg.resolved):null,
              pg.persistent>0?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.sMajBg,color:P.amber,fontSize:10,fontWeight:700}},"\u23F8 "+pg.persistent):null,
              pg.regression>0?E("span",{style:{padding:"2px 6px",borderRadius:4,background:P.sCritBg,color:P.sCrit,fontSize:10,fontWeight:700}},"\u26A0 "+pg.regression):null,
              E("span",{style:{fontSize:10,color:P.slateL,flexShrink:0}},pg.issuesA+"\u2192"+pg.issuesB));
          })))
    ):null
  );

  /* PR-K Free Scan CTA — slim banner above main content reminding visitors
     they can request a free WCAG 2.2 AA scan. Renders on every view so the
     signed-out marketing surface AND the signed-in dashboard both surface
     the offer. Dismiss persists in localStorage so admin staff don't see
     it after they've closed it once. */
  var fsCtaSt = useState(function(){
    try { return typeof window !== "undefined" && window.localStorage &&
      window.localStorage.getItem("piaris.freeScanCta.dismissed") === "1"; }
    catch (_) { return false; }
  });
  var freeScanCtaDismissed = fsCtaSt[0], setFreeScanCtaDismissed = fsCtaSt[1];

  /* PR-K.13: chat-bubble FAB. Persistent floater (bottom-right) that
     expands into a mini scan-request form. Implemented in-DOM, not
     window.open(), so ad blockers can't kill it. Three state slots:
       fabDismissed (persisted) — long-term "never show me again"
       fabOpen      (transient) — panel expand/collapse
       fabForm      (transient) — { name, email, domain, consent,
                                    submitting, sent, error } */
  var fabDsm = useState(function(){
    try { return typeof window !== "undefined" && window.localStorage &&
      window.localStorage.getItem("piaris.scanFab.dismissed.v2") === "1"; }
    catch (_) { return false; }
  });
  var fabDismissed = fabDsm[0], setFabDismissed = fabDsm[1];

  var fabOp = useState(false);
  var fabOpen = fabOp[0], setFabOpen = fabOp[1];

  var fabFm = useState({
    name: "", email: "", domain: "", message: "", consent: false,
    submitting: false, sent: false, sentKind: null, error: null
  });
  var fabForm = fabFm[0], setFabForm = fabFm[1];
  var fabTb = useState("scan"), fabTab = fabTb[0], setFabTab = fabTb[1];   /* "scan" | "contact" */

  function setFabField(field, value){
    setFabForm(function(s){
      var n = {}; for (var k in s) n[k] = s[k];
      n[field] = value; return n;
    });
  }
  function dismissFabForever(){
    try { window.localStorage.setItem("piaris.scanFab.dismissed.v2", "1"); } catch (_) {}
    setFabDismissed(true);
    setFabOpen(false);
  }
  function submitFabForm(ev){
    if (ev && ev.preventDefault) ev.preventDefault();
    var name = String(fabForm.name || "").trim();
    var email = String(fabForm.email || "").trim();
    var domain = String(fabForm.domain || "").trim();
    if (!name) { setFabField("error", "Please enter your name."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFabField("error", "Please enter a valid work email."); return;
    }
    if (!domain || !/\.[a-zA-Z]{2,}/.test(domain)) {
      setFabField("error", "Please enter a valid domain."); return;
    }
    if (!fabForm.consent) {
      setFabField("error", "Please confirm we can email you."); return;
    }
    setFabForm(function(s){
      var n = {}; for (var k in s) n[k] = s[k];
      n.submitting = true; n.error = null; return n;
    });
    fetch("/api/intake/free-scan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        name: name, email: email, domain: domain,
        notes: "", source: "paris", consent: true,
        homepage_url: "", website_url: ""
      })
    })
      .then(function(r){
        return r.json().catch(function(){ return {}; }).then(function(b){
          return { status: r.status, body: b };
        });
      })
      .then(function(out){
        if (out.status >= 200 && out.status < 300 && out.body && out.body.ok) {
          setFabForm(function(s){
            var n = {}; for (var k in s) n[k] = s[k];
            n.submitting = false; n.sent = true; n.error = null; return n;
          });
          return;
        }
        var msg = (out.body && out.body.error) || "Something went wrong. Please try again.";
        setFabForm(function(s){
          var n = {}; for (var k in s) n[k] = s[k];
          n.submitting = false; n.error = msg; return n;
        });
      })
      .catch(function(e){
        setFabForm(function(s){
          var n = {}; for (var k in s) n[k] = s[k];
          n.submitting = false;
          n.error = "Network error. Please check your connection.";
          return n;
        });
      });
  }

  function submitFabInquiry(ev){
    if (ev && ev.preventDefault) ev.preventDefault();
    var name = String(fabForm.name || "").trim();
    var email = String(fabForm.email || "").trim();
    var message = String(fabForm.message || "").trim();
    if (!name || !email || !message) {
      setFabForm(function(s){ var n={}; for(var k in s)n[k]=s[k]; n.error="Please add your name, email, and a message."; return n; });
      return;
    }
    setFabForm(function(s){ var n={}; for(var k in s)n[k]=s[k]; n.submitting=true; n.error=null; return n; });
    fetch("/api/intake/inquiry", {
      method:"POST", headers:{"Content-Type":"application/json"}, credentials:"omit",
      body: JSON.stringify({ name:name, email:email, message:message, source:"paris", homepage_url:"", website_url:"" })
    })
      .then(function(r){ return r.json().catch(function(){return {};}).then(function(b){ return {status:r.status, body:b}; }); })
      .then(function(out){
        if (out.status>=200 && out.status<300 && out.body && out.body.ok) {
          setFabForm(function(s){ var n={}; for(var k in s)n[k]=s[k]; n.submitting=false; n.sent=true; n.sentKind="contact"; n.error=null; return n; });
          return;
        }
        var msg=(out.body && out.body.error) || "Something went wrong. Please try again.";
        setFabForm(function(s){ var n={}; for(var k in s)n[k]=s[k]; n.submitting=false; n.error=msg; return n; });
      })
      .catch(function(){
        setFabForm(function(s){ var n={}; for(var k in s)n[k]=s[k]; n.submitting=false; n.error="Network error. Please check your connection."; return n; });
      });
  }
  function dismissFreeScanCta(){
    try { window.localStorage.setItem("piaris.freeScanCta.dismissed", "1"); } catch (_) {}
    setFreeScanCtaDismissed(true);
  }
  /* PR-K.17: the banner is a marketing CTA for prospective customers
     (view==="home" = signed-out landing). Once a user is signed in
     and inside the app shell (view==="app") or running a scan
     (view==="scanning"), the banner becomes inbox noise — they're
     already a customer. The FAB bubble below stays unconditionally
     so signed-in users still have a one-click path to refer the
     offer or kick a sibling-domain scan. */
  var freeScanCta = (freeScanCtaDismissed || view !== "home") ? null : E("div",{
    role:"region","aria-label":"Free accessibility scan offer",
    style:{
      position:"relative",
      background:"linear-gradient(90deg,#4338CA 0%,#6366F1 35%,#06B6D4 100%)",
      color:"#FFF",
      padding:isMobile?"14px 14px":"18px 28px",
      display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",
      boxShadow:"0 2px 0 rgba(0,0,0,0.08)",
      overflow:"hidden"
    }
  },
    /* PR-K.11: scoped keyframes for the CTA pulse + a respectful
       prefers-reduced-motion shutoff. Inline so this stays self-
       contained inside the banner element — no shared CSS coupling. */
    E("style",null,
      "@keyframes piaris-cta-pulse { 0%,100% { transform: scale(1); box-shadow: 0 4px 16px rgba(0,0,0,0.18), 0 0 0 0 rgba(255,255,255,0.55); } 50% { transform: scale(1.04); box-shadow: 0 6px 20px rgba(0,0,0,0.22), 0 0 0 10px rgba(255,255,255,0); } }" +
      "@keyframes piaris-cta-icon-spin { 0% { transform: rotate(0); } 100% { transform: rotate(360deg); } }" +
      ".piaris-cta-button { animation: piaris-cta-pulse 2.4s ease-in-out infinite; }" +
      ".piaris-cta-icon-ring { animation: piaris-cta-icon-spin 8s linear infinite; }" +
      "@media (prefers-reduced-motion: reduce) { .piaris-cta-button, .piaris-cta-icon-ring { animation: none !important; transform: none !important; } }"
    ),
    /* Illustration — accessibility person glyph inside a soft white halo.
       The dashed ring slowly rotates so the banner has some life without
       being distracting (reduced-motion users see it static). */
    E("div",{"aria-hidden":"true",
      style:{position:"relative",flexShrink:0,
        display:"flex",alignItems:"center",justifyContent:"center",
        width:44,height:44}
    },
      E("svg",{className:"piaris-cta-icon-ring",
        width:44,height:44,viewBox:"0 0 44 44",fill:"none",
        style:{position:"absolute",inset:0}
      },
        E("circle",{cx:22,cy:22,r:20,stroke:"rgba(255,255,255,0.35)",strokeWidth:1.5,strokeDasharray:"3 5"})
      ),
      E("svg",{width:24,height:24,viewBox:"0 0 24 24",fill:"none",
        stroke:"#FFF",strokeWidth:2,strokeLinecap:"round",strokeLinejoin:"round"
      },
        E("circle",{cx:12,cy:5,r:2.2}),
        E("path",{d:"M5 11 L12 9.5 L19 11"}),
        E("path",{d:"M12 9.5 L12 15"}),
        E("path",{d:"M8 21 L12 15 L16 21"})
      )
    ),
    /* Two-line copy block — headline + value-prop subtext so the banner
       reads as a real offer rather than a marketing tagline. */
    E("div",{style:{flex:1,minWidth:220,lineHeight:1.35}},
      E("div",{style:{fontWeight:800,fontSize:isMobile?15:17,letterSpacing:"-0.005em"}},
        "Free WCAG 2.2 AA accessibility scan"),
      E("div",{style:{fontSize:isMobile?12:13,opacity:0.92,fontWeight:500,marginTop:3}},
        "Up to 50 pages of your site. Remediation-ready report in your inbox within 48 hours.")
    ),
    /* Pulsing CTA — animation handled by the .piaris-cta-button class
       above so prefers-reduced-motion can disable it. */
    E("a",{
      href:"/free-scan",
      className:"piaris-cta-button",
      style:{
        background:"#FFFFFF",color:"#4338CA",
        padding:"11px 22px",borderRadius:999,
        fontWeight:800,fontSize:13,
        textDecoration:"none",whiteSpace:"nowrap",
        display:"inline-flex",alignItems:"center",gap:8,flexShrink:0
      }
    },
      "Get my free scan",
      E("span",{"aria-hidden":"true",style:{fontSize:15,fontWeight:900}},"→")
    ),
    E("button",{
      type:"button",
      "aria-label":"Dismiss free scan reminder",
      onClick:dismissFreeScanCta,
      style:{background:"transparent",border:"none",color:"#FFF",
        cursor:"pointer",fontSize:20,opacity:.75,padding:6,marginLeft:4,
        flexShrink:0,lineHeight:1,minWidth:32,minHeight:32}
    },"✕")
  );

  /* Module selector */
  var content = null;
  if(view==="home") content = E(PageWithSideRails,null,homePage);
  else if(view==="scanning") content = scanPage;
  else if(view==="app"){
    if(mod==="dashboard") content = dashboard;
    /* Legacy support: /audit-report/xxx redirects into Web Audit > Report sub-tab */
    else if(mod==="audit"){ setTimeout(function(){setWebAuditTab("report");_setModRaw("webAudit")},0); content=null; }
    else if(mod==="webAudit"){
      /* Sub-tab strip: Pages (list) | Audit Report */
      var tabStrip=E("div",{style:{display:"flex",gap:4,padding:"0 0 10px 0",borderBottom:"1px solid "+P.bdr,marginBottom:12}},
        [{id:"list",l:"Pages",i:"\u{1F4C4}"},{id:"report",l:"Audit Report",i:"\u{1F4CB}"}].map(function(t){
          var active=webAuditTab===t.id;
          return E("button",{key:t.id,onClick:function(){setWebAuditTab(t.id);setSel(null)},"aria-current":active?"page":undefined,
            style:{padding:"8px 16px",borderRadius:"8px 8px 0 0",border:"none",borderBottom:active?"3px solid "+P.pri:"3px solid transparent",
              background:"transparent",color:active?P.pri:P.slateM,fontSize:12,fontWeight:active?800:600,cursor:"pointer",
              display:"flex",alignItems:"center",gap:6,transition:"all .15s"}},
            E("span",{"aria-hidden":"true"},t.i),t.l);
        }));
      /* Watchtower Web Audit chevron — parity with PA11Y Lab. Wraps the
         Pages|Report tab strip so it shows in both tab states; Load/Audit
         switch the in-surface tab, Remediate/Package jump to the sibling
         Watchtower modules. */
      tabStrip=E("div",null,chevronFlow(
        [{id:"load",l:"Load",sub:"Pages",i:"\u{1F4E5}"},{id:"audit",l:"Audit",sub:"Findings",i:"\u{1F50E}"},{id:"remediate",l:"Remediate",sub:"Guide & fix",i:"\u{1F6E0}"},{id:"package",l:"Package",sub:"Reports",i:"\u{1F4E6}"}],
        webAuditTab==="report"?"audit":"load",
        {load:true,audit:wp.length>0,remediate:wp.length>0,package:wp.length>0},
        function(id){if(id==="load"){setWebAuditTab("list");setSel(null);}else if(id==="audit"){setWebAuditTab("report");}else if(id==="remediate"){setMod("remediation");}else if(id==="package"){setMod("reports");}},
        (typeof PRODUCT_COLORS!=="undefined"&&PRODUCT_COLORS.watchtower&&PRODUCT_COLORS.watchtower.c1)||P.pri),tabStrip);
      if(webAuditTab==="report"){
        var auditBanner=auditProgress?E("div",{style:{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderRadius:10,background:"linear-gradient(135deg,#EFF6FF,#DBEAFE)",border:"1.5px solid #3B82F630",marginBottom:14,animation:"pulse-fade 1.8s ease-in-out infinite"}},
          E("span",{style:{fontSize:15,display:"inline-block",animation:"spin 2s linear infinite"}},"\u2699"),
          E("div",{style:{flex:1}},
            E("div",{style:{fontSize:12,fontWeight:700,color:"#2563EB"}},"Loading full issue details\u2026"),
            E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},auditProgress.loaded+"/"+auditProgress.total+" pages")),
          E("div",{style:{width:60,height:4,borderRadius:2,background:"#3B82F620",overflow:"hidden"}},
            E("div",{style:{width:Math.round(auditProgress.loaded/Math.max(1,auditProgress.total)*100)+"%",height:"100%",background:"#3B82F6",transition:"width .3s"}}))
        ):null;
        /* v1.0: Pi Consultants AI Review button + live progress banner. */
        var hasExecSummary=!!(scanMeta&&scanMeta.aiExecutiveSummary);
        var aiBanner=aiProgress?E("div",{style:{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderRadius:10,background:"linear-gradient(135deg,#EEF2FF,#F0F9FF)",border:"1.5px solid "+P.pri+"40",marginBottom:14,animation:aiProgress.phase==="done"?"none":"pulse-fade 1.8s ease-in-out infinite"}},
          E("span",{style:{fontSize:15,display:"inline-block",animation:aiProgress.phase==="done"?"none":"spin 2s linear infinite"}},aiProgress.phase==="done"?"\u2705":"\uD83C\uDFAF"),
          E("div",{style:{flex:1}},
            E("div",{style:{fontSize:12,fontWeight:700,color:P.pri}},
              aiProgress.phase==="done"?"Pi Consultants AI Review complete":
              aiProgress.phase==="summary"?"Generating executive summary\u2026":
              "Reviewing issues with Pi Consultants AI\u2026"),
            E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},
              (aiProgress.processed||0)+"/"+(aiProgress.total||0)+" pages reviewed"+
              (aiProgress.costUsd>0?" \u00B7 $"+aiProgress.costUsd.toFixed(3)+" spent":""))),
          aiProgress.total>0?E("div",{style:{width:60,height:4,borderRadius:2,background:P.pri+"20",overflow:"hidden"}},
            E("div",{style:{width:Math.round(aiProgress.processed/Math.max(1,aiProgress.total)*100)+"%",height:"100%",background:P.pri,transition:"width .3s"}})):null
        ):null;
        var aiButton=!aiProgress&&!hasExecSummary?E("div",{style:{marginBottom:14,padding:"14px 18px",borderRadius:12,background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}},
          E("span",{style:{fontSize:22},"aria-hidden":"true"},"\uD83C\uDFAF"),
          E("div",{style:{flex:1,minWidth:200}},
            E("div",{style:{fontSize:13,fontWeight:800}},"Pi Consultants AI Review"),
            E("div",{style:{fontSize:10.5,opacity:0.9,marginTop:2}},"Validate findings, rewrite in plain English, generate executive summary with Pi Consultants remediation offer. ~$0.08 per 25-page scan.")),
          E("button",{onClick:runAiReview,style:{padding:"9px 18px",borderRadius:8,border:"none",background:"#fff",color:P.pri,fontSize:11,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap"}},"\uD83C\uDFAF Run AI Review")
        ):(!aiProgress&&hasExecSummary?E("div",{style:{marginBottom:14,padding:"10px 14px",borderRadius:10,background:P.priLt,border:"1px solid "+P.pri+"30",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}},
          E("span",{style:{fontSize:14},"aria-hidden":"true"},"\u2705"),
          E("div",{style:{flex:1,minWidth:200,fontSize:11,color:P.pri,fontWeight:600}},"AI Review is active \u2014 see the Pi Consultants Executive Summary below and the \uD83C\uDFAF badges on each issue card."),
          E("button",{onClick:runAiReview,style:{padding:"6px 12px",borderRadius:6,border:"1px solid "+P.pri+"40",background:"#fff",color:P.pri,fontSize:10,fontWeight:700,cursor:"pointer"}},"\u21BB Re-run")
        ):null);
        content=E("div",null,tabStrip,auditBanner,aiBanner,aiButton,
          E(AuditReportView,{wp:wp,dc:[],templateStats:templateStats,domain:domain,scanMeta:scanMeta||{},darkMode:darkMode,
            onLoadScan:function(){openScanPicker("watchtower")}}));
      } else {
        content=E("div",null,tabStrip,webAudit);
      }
    }
    else if(mod==="docAudit"){
      var docTabStrip=E("div",{style:{display:"flex",gap:4,padding:"0 0 10px 0",borderBottom:"1px solid "+P.bdr,marginBottom:12}},
        [{id:"list",l:"Documents",i:"\u{1F4C4}"},{id:"report",l:"Audit Report",i:"\u{1F4CB}"}].map(function(t){
          var active=docAuditTab===t.id;
          return E("button",{key:t.id,onClick:function(){setDocAuditTab(t.id);setSel(null)},"aria-current":active?"page":undefined,
            style:{padding:"8px 16px",borderRadius:"8px 8px 0 0",border:"none",borderBottom:active?"3px solid "+P.pri:"3px solid transparent",
              background:"transparent",color:active?P.pri:P.slateM,fontSize:12,fontWeight:active?800:600,cursor:"pointer",
              display:"flex",alignItems:"center",gap:6,transition:"all .15s"}},
            E("span",{"aria-hidden":"true"},t.i),t.l);
        }));
      /* Watchtower Doc Audit chevron — parity with PA11Y Lab + Web Audit.
         Wraps the Documents|Report tab strip; Load/Audit switch the
         in-surface tab, Remediate/Package jump to the sibling modules. */
      docTabStrip=E("div",null,chevronFlow(
        [{id:"load",l:"Load",sub:"Documents",i:"\u{1F4E5}"},{id:"audit",l:"Audit",sub:"Findings",i:"\u{1F50E}"},{id:"remediate",l:"Remediate",sub:"Pinpoint & fix",i:"\u{1F6E0}"},{id:"package",l:"Package",sub:"Reports",i:"\u{1F4E6}"}],
        docAuditTab==="report"?"audit":"load",
        {load:true,audit:dc.length>0,remediate:dc.length>0,package:dc.length>0},
        function(id){if(id==="load"){setDocAuditTab("list");setSel(null);}else if(id==="audit"){setDocAuditTab("report");}else if(id==="remediate"){setMod("remediation");}else if(id==="package"){setMod("reports");}},
        (typeof PRODUCT_COLORS!=="undefined"&&PRODUCT_COLORS.watchtower&&PRODUCT_COLORS.watchtower.c1)||P.pri),docTabStrip);
      if(docAuditTab==="report"){
        var docAuditBanner=auditProgress?E("div",{style:{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderRadius:10,background:"linear-gradient(135deg,#EFF6FF,#DBEAFE)",border:"1.5px solid #3B82F630",marginBottom:14,animation:"pulse-fade 1.8s ease-in-out infinite"}},
          E("span",{style:{fontSize:15,display:"inline-block",animation:"spin 2s linear infinite"}},"\u2699"),
          E("div",{style:{flex:1}},
            E("div",{style:{fontSize:12,fontWeight:700,color:"#2563EB"}},"Loading full issue details\u2026"),
            E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},auditProgress.loaded+"/"+auditProgress.total+" pages"))
        ):null;

        /* v1.0: Doc Remediation button + per-doc guide rendering.
           Each doc with issues can get an AI-generated step-by-step
           Acrobat Pro remediation guide. Batch mode polls a scan-job.
           Hooks (_docRemRunning, _docRemProgress) are at top-level. */
        function runDocRemediation(){
          var sid=(scanMeta&&scanMeta.sessionId)||_currentScanId.current;
          if(!sid){showToast("No session loaded","error");return}
          if(_docRemRunning.current){showToast("Doc remediation already running","error");return}
          _docRemRunning.current=true;
          setDocRemProgress({processed:0,total:0,costUsd:0});
          showToast("\u{1F4C4} Generating PDF remediation instructions\u2026");
          fetch("/api/doc-remediate",{method:"POST",credentials:"include",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify({sessionId:sid})})
            .then(function(r){return r.json()})
            .then(function(d){
              if(!d||!d.ok||!d.jobId){_docRemRunning.current=false;setDocRemProgress(null);showToast("Failed: "+(d&&d.error||"unknown"),"error");return}
              (function poll(){
                fetch("/api/scan-jobs/"+d.jobId+"/run",{method:"POST",credentials:"include"})
                  .then(function(r){return r.json()})
                  .then(function(res){
                    if(!res){_docRemRunning.current=false;setDocRemProgress(null);return}
                    var p=res.progress||{};
                    setDocRemProgress({processed:p.processed||0,total:p.total||0,costUsd:p.costUsd||0});
                    if(res.status==="complete"){
                      _docRemRunning.current=false;
                      setTimeout(function(){setDocRemProgress(null)},3000);
                      showToast("\u2705 Doc remediation guides generated for "+(p.updated||0)+" documents");
                    } else if(res.status==="failed"){
                      _docRemRunning.current=false;setDocRemProgress(null);
                      showToast("Doc remediation failed: "+(res.error||"unknown"),"error");
                    } else { setTimeout(poll,500); }
                  }).catch(function(){_docRemRunning.current=false;setDocRemProgress(null)});
              })();
            }).catch(function(e){_docRemRunning.current=false;setDocRemProgress(null);showToast("Error: "+e.message,"error")});
        }

        /* Doc remediation progress banner */
        var docRemBanner=docRemProgress?E("div",{style:{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderRadius:10,
          background:"linear-gradient(135deg,#EEF2FF,#ECFDF5)",border:"1.5px solid "+P.emerald+"40",marginBottom:14,
          animation:docRemProgress.processed>=docRemProgress.total&&docRemProgress.total>0?"none":"pulse-fade 1.8s ease-in-out infinite"}},
          E("span",{style:{fontSize:15,display:"inline-block",animation:docRemProgress.processed>=docRemProgress.total?"none":"spin 2s linear infinite"}},
            docRemProgress.processed>=docRemProgress.total&&docRemProgress.total>0?"\u2705":"\u{1F4C4}"),
          E("div",{style:{flex:1}},
            E("div",{style:{fontSize:12,fontWeight:700,color:P.emerald}},
              docRemProgress.processed>=docRemProgress.total&&docRemProgress.total>0?"Doc remediation guides ready":"Generating Acrobat remediation instructions\u2026"),
            E("div",{style:{fontSize:10.5,color:P.slateM,marginTop:2}},
              docRemProgress.processed+"/"+docRemProgress.total+" docs"+(docRemProgress.costUsd>0?" \u00B7 $"+docRemProgress.costUsd.toFixed(3):""))),
          docRemProgress.total>0?E("div",{style:{width:60,height:4,borderRadius:2,background:P.emerald+"20",overflow:"hidden"}},
            E("div",{style:{width:Math.round(docRemProgress.processed/Math.max(1,docRemProgress.total)*100)+"%",height:"100%",background:P.emerald,transition:"width .3s"}})):null
        ):null;

        /* Doc remediation button — only show when not running and docs have issues */
        var docsWithIssues=dc.filter(function(d){return(d.issues||[]).length>0});
        var hasDocRem=dc.some(function(d){return d.docRemediation});
        var docRemButton=!docRemProgress&&docsWithIssues.length>0?E("div",{style:{marginBottom:14,padding:"14px 18px",borderRadius:12,
          background:"linear-gradient(135deg,"+P.emerald+","+P.accent+")",color:"#fff",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}},
          E("span",{style:{fontSize:22},"aria-hidden":"true"},"\u{1F4C4}"),
          E("div",{style:{flex:1,minWidth:200}},
            E("div",{style:{fontSize:13,fontWeight:800}},hasDocRem?"Re-generate Doc Remediation":"Generate Doc Remediation"),
            E("div",{style:{fontSize:10.5,opacity:0.9,marginTop:2}},docsWithIssues.length+" PDF"+(docsWithIssues.length===1?"":"s")+" with issues \u2014 generate step-by-step Acrobat Pro instructions. ~$0.05 per 10 docs.")),
          E("button",{onClick:runDocRemediation,style:{padding:"9px 18px",borderRadius:8,border:"none",background:"#fff",color:P.emerald,fontSize:11,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap"}},"\u{1F4C4} Generate Guides")
        ):null;

        /* Per-doc remediation guide cards — rendered inline below the audit report */
        var docGuides=dc.filter(function(d){return d.docRemediation&&d.docRemediation.guide}).length>0?E("div",{style:{marginTop:20}},
          E("h3",{style:{fontSize:14,fontWeight:800,color:P.slate,marginBottom:12}},"\u{1F4C4} PDF Remediation Guides"),
          dc.map(function(d,di){
            if(!d.docRemediation||!d.docRemediation.guide)return null;
            var g=d.docRemediation.guide;
            return E("div",{key:di,style:{marginBottom:16,padding:"16px 20px",borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card}},
              /* Doc header */
              E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10,flexWrap:"wrap",gap:8}},
                E("div",null,
                  E("div",{style:{fontSize:13,fontWeight:800,color:P.slate}},d.name||"Document "+(di+1)),
                  E("div",{style:{fontSize:10,color:P.slateL,fontFamily:"monospace",marginTop:2}},d.url||"")),
                g.estimatedMinutes!=null?E("div",{style:{padding:"6px 12px",borderRadius:8,background:P.priLt,border:"1px solid "+P.pri+"30",textAlign:"center"}},
                  E("div",{style:{fontSize:9,color:P.slateL,fontWeight:700,textTransform:"uppercase"}},"Est. Time"),
                  E("div",{style:{fontSize:14,fontWeight:800,color:P.pri}},g.estimatedMinutes+" min")
                ):null),
              /* Summary */
              g.documentSummary?E("div",{style:{fontSize:12,color:P.slateM,lineHeight:1.6,marginBottom:12,padding:"10px 12px",borderRadius:8,background:P.bgS,border:"1px dashed "+P.bdr}},
                E("span",{"aria-hidden":"true",style:{marginRight:4}},"\u{1F4D0}"),g.documentSummary):null,
              /* Steps */
              Array.isArray(g.steps)&&g.steps.length>0?E("div",{style:{marginBottom:12}},
                E("div",{style:{fontSize:10,fontWeight:800,color:P.slate,textTransform:"uppercase",letterSpacing:".04em",marginBottom:8}},"Steps"),
                g.steps.map(function(step,si){
                  var sevColor=step.severity==="critical"?P.coral:step.severity==="major"?P.amber:P.slateL;
                  return E("div",{key:si,style:{display:"flex",gap:10,marginBottom:10,padding:"10px 12px",borderRadius:8,border:"1px solid "+P.bdr,background:"#fff"}},
                    E("div",{style:{width:28,height:28,borderRadius:"50%",background:P.pri,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,flexShrink:0}},step.stepNumber||si+1),
                    E("div",{style:{flex:1,minWidth:0}},
                      E("div",{style:{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:4}},
                        E("span",{style:{fontSize:12,fontWeight:700,color:P.slate}},step.title||"Step "+(si+1)),
                        step.wcag?E("span",{style:{padding:"1px 6px",borderRadius:4,background:P.bgS,fontSize:9,fontWeight:600,color:P.slateM}},"WCAG "+step.wcag):null,
                        E("span",{style:{padding:"1px 6px",borderRadius:4,background:sevColor+"15",color:sevColor,fontSize:9,fontWeight:700}},step.severity||"minor"),
                        step.estimatedMinutes?E("span",{style:{fontSize:9,color:P.slateL}},"\u23F1 ~"+step.estimatedMinutes+"m"):null,
                        step.canAutoFix?E("span",{style:{padding:"1px 6px",borderRadius:4,background:P.emeraldLt,color:P.emerald,fontSize:9,fontWeight:700}},"\u26A1 Auto-fixable"):null),
                      E("div",{style:{fontSize:11.5,color:P.slateM,lineHeight:1.6,whiteSpace:"pre-wrap"}},step.instructions||""),
                      step.menuPath?E("div",{style:{fontSize:10,color:P.pri,fontWeight:600,fontFamily:"monospace",marginTop:4,padding:"4px 8px",borderRadius:4,background:P.priLt,display:"inline-block"}},"\u{1F5C2} "+step.menuPath):null,
                      step.canAutoFix&&step.autoFixNote?E("div",{style:{fontSize:10,color:P.emerald,fontStyle:"italic",marginTop:4}},"\u26A1 "+step.autoFixNote):null
                    ));
                })
              ):null,
              /* Pi note + disclaimer */
              g.piConsultantsNote?E("div",{style:{padding:"10px 14px",borderRadius:8,background:"linear-gradient(135deg,"+P.pri+"10,"+P.accent+"10)",border:"1px solid "+P.pri+"20",marginTop:8}},
                E("div",{style:{fontSize:10,fontWeight:800,color:P.pri,textTransform:"uppercase",letterSpacing:".04em",marginBottom:4}},"\uD83E\uDD1D Pi Consultants"),
                E("div",{style:{fontSize:11,color:P.slateM,lineHeight:1.55}},g.piConsultantsNote)
              ):null,
              g.disclaimer?E("div",{style:{fontSize:9,color:P.slateL,fontStyle:"italic",marginTop:8}},g.disclaimer):null
            );
          })
        ):null;

        content=E("div",null,docTabStrip,docAuditBanner,docRemBanner,docRemButton,
          E(AuditReportView,{wp:[],dc:dc,templateStats:templateStats,domain:domain,scanMeta:scanMeta||{},darkMode:darkMode,
            onLoadScan:function(){openScanPicker("watchtower")}}),
          docGuides);
      } else {
        content=E("div",null,docTabStrip,docAudit);
      }
    }
    else if(mod==="compare"){ if(userSessions.length===0&&!sessionsLoading) loadUserSessions(); content = comparePage; }
    else if(mod==="dmOverview"){ content = E("div",null,
      E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 6px"}},"\u{1F4DD} DocMaya"),
      E("p",{style:{color:P.slateL,fontSize:12,marginBottom:16}},"Runtime PDF accessibility layer \u2014 your clients add one script tag, public users get accessible PDF viewing."),
      E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr",gap:12,marginBottom:20}},
        E("div",{style:{padding:20,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card}},
          E("div",{style:{fontSize:28,fontWeight:900,color:P.pri}},dmKeys.length),E("div",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"Active Site Keys")),
        E("div",{style:{padding:20,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card}},
          E("div",{style:{fontSize:28,fontWeight:900,color:P.emerald}},dc.filter(function(d){return convStatus[dc.indexOf(d)]&&convStatus[dc.indexOf(d)].status==="complete"}).length),E("div",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"Converted PDFs")),
        E("div",{style:{padding:20,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card}},
          E("div",{style:{fontSize:28,fontWeight:900,color:P.accent}},dc.length),E("div",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"Total Documents"))),
      E("div",{style:{padding:20,borderRadius:14,background:"linear-gradient(135deg,"+P.priLt+",#E0E7FF)",border:"1.5px solid "+P.pri+"20",marginBottom:16}},
        E("h3",{style:{fontSize:14,fontWeight:800,margin:"0 0 8px",color:P.pri}},"How DocMaya Works"),
        E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(4,1fr)",gap:12}},
          [["1\uFE0F\u20E3","Purchase","Choose a plan and get a site license for your domain"],
           ["2\uFE0F\u20E3","Get Script","A unique script tag is generated for your domain"],
           ["3\uFE0F\u20E3","Deploy","Add the script tag to your site's HTML"],
           ["4\uFE0F\u20E3","Live","Public users see an accessibility toggle on every PDF"]].map(function(step,si){
            return E("div",{key:si,style:{textAlign:"center"}},
              E("div",{style:{fontSize:24,marginBottom:4}},step[0]),
              E("div",{style:{fontSize:12,fontWeight:700,color:P.slate}},step[1]),
              E("div",{style:{fontSize:10,color:P.slateM}},step[2]));
          }))),
      E("div",{style:{display:"flex",gap:8,flexWrap:"wrap"}},
        E("button",{onClick:function(){setMod("dmSetup")},style:{padding:"10px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,"+P.pri+","+P.accent+")",color:"#fff",fontSize:12,fontWeight:700}},"\u{1F680} Setup & Deploy"),
        E("button",{onClick:function(){setMod("dmConversions")},style:{padding:"10px 20px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,fontSize:12,fontWeight:700,color:P.slateM}},"\u{1F504} View Conversions"))); }
    else if(mod==="dmSetup"){ content = E("div",null,
      E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 6px"}},"\u{1F680} DocMaya Setup & Deploy"),
      E("p",{style:{color:P.slateL,fontSize:12,marginBottom:16}},"Generate a site key and add the script tag to your website."),
      /* Key generation */
      E("div",{style:{padding:20,borderRadius:14,border:"1.5px solid "+P.bdr,background:P.card,marginBottom:16}},
        E("h3",{style:{fontSize:14,fontWeight:700,margin:"0 0 10px"}},"Step 1: Generate Site Key"),
        E("div",{style:{display:"flex",gap:8,alignItems:"center",marginBottom:12}},
          E("input",{value:domain,onChange:function(ev){setDomain(ev.target.value)},placeholder:"Enter your domain (e.g., saccounty.gov)",style:{flex:1,padding:"10px 14px",borderRadius:8,border:"1.5px solid "+P.bdr,fontSize:12,fontFamily:F}}),
          E("button",{onClick:function(){if(!domain){showToast("Enter a domain first","error");return}generateDocMayaKey(domain)},style:{padding:"10px 20px",borderRadius:8,border:"none",background:P.pri,color:"#fff",fontSize:12,fontWeight:700,whiteSpace:"nowrap"}},"\u{1F511} Generate Key")),
        dmKeys.length>0?E("div",{style:{marginTop:8}},dmKeys.map(function(k,ki){
          return E("div",{key:ki,style:{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:8,background:P.bgS,border:"1px solid "+P.bdr,marginBottom:6}},
            E("span",{style:{fontSize:10,fontWeight:700,color:P.emerald}},"\u2713"),
            E("span",{style:{fontSize:11,fontWeight:600}},k.domain),
            E("code",{style:{fontSize:10,background:P.bgS,padding:"2px 8px",borderRadius:4,fontFamily:"monospace",flex:1,overflow:"hidden",textOverflow:"ellipsis"}},k.siteKey),
            E("button",{onClick:function(){revokeDocMayaKey(k.id)},style:{padding:"2px 8px",borderRadius:4,border:"1px solid "+P.sCrit+"30",background:P.sCritBg,color:P.sCrit,fontSize:9,fontWeight:700,cursor:"pointer"}},"\u2715 Revoke"));
        })):E("div",{style:{fontSize:11,color:P.slateL,fontStyle:"italic"}},"No keys generated yet")),
      /* Deploy instructions */
      E("div",{style:{padding:20,borderRadius:14,border:"1.5px solid "+P.bdr,background:P.card,marginBottom:16}},
        E("h3",{style:{fontSize:14,fontWeight:700,margin:"0 0 10px"}},"Step 2: Add Script to Your Site"),
        E("div",{style:{padding:"12px 16px",borderRadius:8,background:"#1E293B",color:"#E2E8F0",fontFamily:"monospace",fontSize:11,marginBottom:10,position:"relative"}},
          '<script src="https://paris.pi-consulting.us/docmaya.js" data-key="'+(dmKeys.length>0?dmKeys[0].siteKey:"YOUR_SITE_KEY")+'"></'+'script>',
          E("button",{onClick:function(){
            var tag='<script src="https://paris.pi-consulting.us/docmaya.js" data-key="'+(dmKeys.length>0?dmKeys[0].siteKey:"YOUR_SITE_KEY")+'"></'+'script>';
            navigator.clipboard.writeText(tag).then(function(){showToast("\u{1F4CB} Script tag copied!")}).catch(function(){showToast("Copy failed","error")});
          },style:{position:"absolute",top:8,right:8,padding:"4px 10px",borderRadius:4,border:"none",background:"#4F46E5",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u{1F4CB} Copy")),
        E("div",{style:{fontSize:11,color:P.slateM,lineHeight:1.6}},
          "Add this script tag to your site's <head> or before </body>. ",
          "Platform-specific instructions: ",
          E("strong",null,"WordPress")," \u2014 Appearance \u2192 Theme Editor \u2192 header.php, or use Insert Headers plugin. ",
          E("strong",null,"Granicus")," \u2014 Site Settings \u2192 Header/Footer Code. ",
          E("strong",null,"Drupal")," \u2014 html.html.twig in <head> region.")),
      E("div",{style:{padding:20,borderRadius:14,border:"1.5px solid "+P.emerald+"30",background:P.emeraldLt+"40"}},
        E("h3",{style:{fontSize:14,fontWeight:700,margin:"0 0 8px",color:P.emerald}},"Step 3: Verify"),
        E("div",{style:{fontSize:11,color:P.slate}},"After deploying, visit any PDF link on your site. You should see a DocMaya accessibility toggle appear. Click it to verify the accessible HTML view renders correctly."))); }
    else if(mod==="dmConversions"){ content = E("div",null,
      E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 6px"}},"\u{1F504} DocMaya Conversions"),
      E("p",{style:{color:P.slateL,fontSize:12,marginBottom:16}},"Track PDF-to-accessible-HTML conversions across your domain."),
      dc.length===0?E("div",{style:{textAlign:"center",padding:"40px 20px",borderRadius:14,border:"2px dashed "+P.bdr,color:P.slateL}},
        E("div",{style:{fontSize:40,marginBottom:12}},"\u{1F4C4}"),
        E("div",{style:{fontSize:14,fontWeight:700}},"No documents scanned yet"),
        E("div",{style:{fontSize:11,marginTop:6}},"Run a scan with the PiARIS extension to discover PDFs on your site.")):
      E("div",{style:{display:"grid",gap:6}},dc.map(function(d,di){
        var cs=convStatus[di];
        var isComplete=cs&&cs.status==="complete";
        return E("div",{key:di,style:{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:"1px solid "+P.bdr,background:isComplete?P.emeraldLt+"20":P.card}},
          E("span",{style:{fontSize:14}},isComplete?"\u2705":"\u{1F4C4}"),
          E("div",{style:{flex:1}},
            E("div",{style:{fontSize:12,fontWeight:700}},d.name),
            E("div",{style:{fontSize:10,color:P.slateM}},d.pages+" pages \u00B7 "+(d.tagged?"Tagged":"Untagged")+(isComplete?" \u00B7 Converted":""))),
          !isComplete&&d.totalIssues>0?E("button",{onClick:function(){remediateDoc(d)},style:{padding:"6px 14px",borderRadius:6,border:"none",background:"linear-gradient(135deg,"+P.coral+","+P.amber+")",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}},"\u{1F527} Convert"):null,
          isComplete?E("span",{style:{padding:"4px 10px",borderRadius:6,background:P.emeraldLt,color:P.emerald,fontSize:10,fontWeight:700}},"\u2713 Accessible HTML Ready"):null);
      }))); }
    /* ── DocMaya Monitoring Dashboard (Session 46, Feature #6) ──
       Shows conversion analytics: total conversions, success/failure rate, quota usage,
       most-accessed PDFs, and conversion history. Fetches from /api/docmaya/analytics
       or falls back to local convStatus data. Emerald-accented to match DocMaya brand. */
    else if(mod==="dmMonitoring"){ if(!dmAnalytics&&!dmAnalyticsLoading) fetchDmAnalytics(); content = E("div",null,
      E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}},
        E("div",null,
          E("h2",{style:{fontSize:20,fontWeight:800,margin:0}},"\u{1F4C8} DocMaya Monitoring"),
          E("p",{style:{color:P.slateL,fontSize:11,margin:"2px 0 0"}},"Conversion analytics and usage tracking"+(domain?" \u2014 "+domain:""))),
        E("button",{onClick:fetchDmAnalytics,disabled:dmAnalyticsLoading,
          style:{padding:"8px 16px",borderRadius:8,border:"1px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:600}},
          dmAnalyticsLoading?"\u23F3 Loading...":"\u{1F504} Refresh")),
      dmAnalyticsLoading?E("div",{style:{textAlign:"center",padding:40,color:P.slateL}},
        E("div",{style:{width:32,height:32,border:"3px solid "+P.emerald,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 1s linear infinite",margin:"0 auto 12px"}}),
        E("div",{style:{fontSize:12}},"Loading analytics...")):
      dmAnalytics?E("div",null,
        /* Source indicator — shows whether data is from server or local fallback */
        dmAnalytics.source==="local"?E("div",{style:{marginBottom:10,padding:"8px 14px",borderRadius:8,background:P.amberLt,border:"1px solid "+P.amber+"30",fontSize:10,color:P.amberDk,display:"flex",alignItems:"center",gap:6}},
          E("span",null,"\u26A0\uFE0F"),
          "Analytics API not yet deployed \u2014 showing local conversion data. Full server-side analytics coming soon."):null,
        /* Metric cards row */
        E("div",{style:{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(5,1fr)",gap:10,marginBottom:16}},
          E("div",{style:{padding:16,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,textAlign:"center"}},
            E("div",{style:{fontSize:28,fontWeight:900,color:P.emerald}},dmAnalytics.totalConversions||0),
            E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,textTransform:"uppercase"}},"Total Conversions")),
          E("div",{style:{padding:16,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,textAlign:"center"}},
            E("div",{style:{fontSize:28,fontWeight:900,color:P.emerald}},dmAnalytics.successful||0),
            E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,textTransform:"uppercase"}},"Successful")),
          E("div",{style:{padding:16,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,textAlign:"center"}},
            E("div",{style:{fontSize:28,fontWeight:900,color:dmAnalytics.failed>0?P.coral:P.slateL}},dmAnalytics.failed||0),
            E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,textTransform:"uppercase"}},"Failed")),
          E("div",{style:{padding:16,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,textAlign:"center"}},
            E("div",{style:{fontSize:28,fontWeight:900,color:dmAnalytics.successRate>=90?P.emerald:dmAnalytics.successRate>=70?P.amber:P.coral}},dmAnalytics.successRate+"%"),
            E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,textTransform:"uppercase"}},"Success Rate")),
          E("div",{style:{padding:16,borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,textAlign:"center"}},
            E("div",{style:{fontSize:28,fontWeight:900,color:P.accent}},dmAnalytics.totalPagesConverted||0),
            E("div",{style:{fontSize:10,fontWeight:700,color:P.slateM,textTransform:"uppercase"}},"Pages Converted"))),
        /* Quota usage panel */
        E("div",{style:{padding:"14px 18px",borderRadius:12,border:"1.5px solid "+P.emerald+"30",background:P.emeraldLt+"40",marginBottom:16}},
          E("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}},
            E("div",{style:{fontSize:13,fontWeight:700,color:P.emerald}},"Quota Usage This Month"),
            E("div",{style:{fontSize:12,fontWeight:800,color:P.slate}},
              (dmAnalytics.quotaUsed||0)+" / "+(dmAnalytics.quotaLimit>0?dmAnalytics.quotaLimit:"\u221E")+" conversions")),
          /* Quota progress bar */
          dmAnalytics.quotaLimit>0?E("div",null,
            E("div",{style:{height:8,borderRadius:4,background:P.bdr,overflow:"hidden"}},
              E("div",{style:{height:8,borderRadius:4,transition:"width .5s",
                width:Math.min(100,Math.round((dmAnalytics.quotaUsed||0)/dmAnalytics.quotaLimit*100))+"%",
                background:(dmAnalytics.quotaUsed||0)>=dmAnalytics.quotaLimit?P.coral:
                  (dmAnalytics.quotaUsed||0)>=dmAnalytics.quotaLimit*0.8?P.amber:P.emerald}})),
            E("div",{style:{display:"flex",justifyContent:"space-between",marginTop:4,fontSize:10,color:P.slateM}},
              E("span",null,Math.round((dmAnalytics.quotaUsed||0)/Math.max(dmAnalytics.quotaLimit,1)*100)+"% used"),
              E("span",null,Math.max(0,dmAnalytics.quotaLimit-(dmAnalytics.quotaUsed||0))+" remaining"))
          ):null),
        /* Most-accessed PDFs */
        dmAnalytics.topPdfs&&dmAnalytics.topPdfs.length>0?E("div",{style:{padding:"14px 18px",borderRadius:12,border:"1.5px solid "+P.bdr,background:P.card,marginBottom:16}},
          E("div",{style:{fontSize:13,fontWeight:700,marginBottom:10}},"Most Accessed PDFs"),
          E("table",{style:{width:"100%",borderCollapse:"collapse"}},
            E("thead",null,E("tr",null,
              E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.slateM,textAlign:"left",borderBottom:"1px solid "+P.bdr}},"Document"),
              E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.slateM,textAlign:"right",borderBottom:"1px solid "+P.bdr}},"Views"),
              E("th",{style:{padding:"5px 8px",fontSize:10,fontWeight:600,color:P.slateM,textAlign:"center",borderBottom:"1px solid "+P.bdr}},"Status"))),
            E("tbody",null,dmAnalytics.topPdfs.map(function(pdf,pi){
              return E("tr",{key:"tp-"+pi},
                E("td",{style:{padding:"6px 8px",fontSize:11,borderBottom:"1px solid "+P.bdr}},pdf.name),
                E("td",{style:{padding:"6px 8px",fontSize:11,fontWeight:700,textAlign:"right",borderBottom:"1px solid "+P.bdr}},
                  (pdf.views||0).toLocaleString()),
                E("td",{style:{padding:"6px 8px",textAlign:"center",borderBottom:"1px solid "+P.bdr}},
                  pdf.converted?E("span",{style:{padding:"2px 8px",borderRadius:4,background:P.emeraldLt,color:P.emerald,fontSize:9,fontWeight:700}},"\u2713 Converted"):
                  E("span",{style:{padding:"2px 8px",borderRadius:4,background:P.bgS,color:P.slateM,fontSize:9,fontWeight:600}},"Pending")));
            })))
        ):null,
        /* Quick actions */
        E("div",{style:{display:"flex",gap:8,flexWrap:"wrap"}},
          E("button",{onClick:function(){setMod("dmConversions")},style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:700}},"\u{1F504} View All Conversions"),
          E("button",{onClick:function(){setMod("dmSetup")},style:{padding:"8px 16px",borderRadius:8,border:"1.5px solid "+P.bdr,background:P.card,color:P.slateM,fontSize:11,fontWeight:700}},"\u{1F680} Setup & Deploy"),
          domainLicense&&domainLicense.tier==="free"?E("button",{onClick:function(){setUpgradeModal({domain:domain,selectedTier:"starter"})},
            style:{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#8B5CF6,#3B82F6)",color:"#fff",fontSize:11,fontWeight:700}},"\u2B06 Upgrade for More Conversions"):null)
      ):E("div",{style:{textAlign:"center",padding:"40px 20px",borderRadius:14,border:"2px dashed "+P.bdr,color:P.slateL}},
        E("div",{style:{fontSize:40,marginBottom:12}},"\u{1F4C8}"),
        E("div",{style:{fontSize:14,fontWeight:700,marginBottom:6}},"No analytics data"),
        E("div",{style:{fontSize:11}},"Scan a domain and convert some PDFs to see monitoring data here."))
    ); }
    else if(mod==="import"){ content = E("div",null,
      E("h2",{style:{fontSize:20,fontWeight:800,margin:"0 0 6px"}},"\u{1F4C2} Import Scan Data"),
      E("p",{style:{color:P.slateL,fontSize:12,marginBottom:16}},"Upload scan data from external tools (Siteimprove, WAVE, Deque, manual audit) or a previous PiARIS export."),
      E("div",{style:{padding:"40px 20px",borderRadius:14,border:"2px dashed "+P.bdr,textAlign:"center",background:P.bgS}},
        E("div",{style:{fontSize:40,marginBottom:12}},"\u{1F4C2}"),
        E("div",{style:{fontSize:14,fontWeight:700,marginBottom:8}},"Drop a file here or click to browse"),
        E("div",{style:{fontSize:11,color:P.slateL,marginBottom:16}},"Supported: PiARIS JSON, CSV, Siteimprove export, WAVE export"),
        E("button",{onClick:function(){fileInputRef.current&&fileInputRef.current.click()},style:{padding:"10px 24px",borderRadius:8,border:"none",background:P.pri,color:"#fff",fontSize:12,fontWeight:700}},"\u{1F4C2} Choose File"))); }
    /* ═══ MODULE: content-dispatch [status: entangled] ═══
     * PURPOSE: Switch render tree based on `mod`.
     * ENTANGLED: Each branch references a monolithic render var built above.
     *            Must extract render modules individually before this can
     *            become a clean <Router>/<Outlet> pattern. */
    else if(mod==="aiLabWeb") content = aiLabWeb;
    else if(mod==="aiLabDoc") content = aiLabDoc;
    else if(mod==="validator") content = validator;
    else if(mod==="monitoring") content = monitoring;
    else if(mod==="bulkflow") content = bulkWorkflow;
    else if(mod==="remediation") content = remCenter;
    else if(mod==="reports") content = reportsPage;
    else if(mod==="licenses"){ content = licensesPage; }
    else if(mod==="vpat") content = vpatPage;
    else if(mod==="pac") content = pacPage;
  }

  return E("div",{key:"theme-"+(darkMode?"dark":"light"),lang:"en",style:{minHeight:"100vh",background:P.bg,color:P.slate,fontFamily:F,display:"flex",flexDirection:"column"}},
    E("style",null,CSS),
    E("link",{href:"https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=IBM+Plex+Mono:wght@400;500;600&display=swap",rel:"stylesheet"}),
    E("a",{href:"#main",className:"skip"},"Skip to main content"),
    nav,
    subNav,
    navDrawer,
    freeScanCta,
    E("main",{id:"main",style:{maxWidth:(mod==="webAudit"&&sel!==null)?"none":(mod==="docAudit"&&sel!==null)?"none":(mod==="dashboard"||mod==="audit")?"none":1500,margin:"0 auto",padding:(mod==="webAudit"&&sel!==null)?"8px 8px":(mod==="docAudit"&&sel!==null)?"8px 8px":(mod==="dashboard")?(isMobile?"12px 10px":"16px 28px"):(mod==="audit")?(isMobile?"12px 14px":"20px 32px"):(isMobile?"12px 14px":"16px 20px"),flex:1,transition:"padding .2s"}},content),
    view==="app"?E("footer",{style:{textAlign:"center",padding:"12px 20px",borderTop:"1px solid "+P.bdr,background:P.card}},
      E("div",{style:{display:"flex",alignItems:"center",justifyContent:"center",gap:8}},
        E(PiArisLockup,{size:"small",opacity:.4}),
        E("span",{style:{fontSize:10,color:P.slateM}},"\u00A9 2026 "+PI_NAME+" \u00B7 "+PI_WEB+" \u00B7 v"+APP_VERSION))):null,
    /* Stripe upgrade modal */
    /* Platform-level scan picker modal (Session 46) */
    renderScanPicker(),
    upgradeModalEl,
    bulkRemModalEl,

    /* PR-K.13 \u2014 Chat-bubble FAB. Persistent floater that lives in the
       bottom-right corner across every view; click expands into a
       mini-form for instant submission without leaving the page.
       In-DOM (position:fixed) so ad blockers can't kill it.

       Element tree: when not dismissed, render EITHER the closed
       bubble OR the open panel \u2014 never both at once. Pulse animation
       on the closed bubble draws the eye; panel slides up on open. */
    fabDismissed ? null : E("div",{
      style:{position:"fixed",bottom:isMobile?16:24,right:isMobile?16:24,zIndex:9998}
    },
      E("style",null,
        "@keyframes piaris-fab-pulse { 0%,100% { box-shadow: 0 4px 14px rgba(67,56,202,0.35), 0 0 0 0 rgba(99,102,241,0.6); } 50% { box-shadow: 0 4px 14px rgba(67,56,202,0.35), 0 0 0 14px rgba(99,102,241,0); } }" +
        "@keyframes piaris-fab-rise { 0% { opacity:0; transform: translateY(20px) scale(0.95); } 100% { opacity:1; transform: translateY(0) scale(1); } }" +
        ".piaris-fab-bubble { animation: piaris-fab-pulse 2.4s ease-in-out infinite; }" +
        ".piaris-fab-panel { animation: piaris-fab-rise 0.25s ease-out both; transform-origin: bottom right; }" +
        "@media (prefers-reduced-motion: reduce) { .piaris-fab-bubble, .piaris-fab-panel { animation: none !important; } }"
      ),

      /* CLOSED state \u2014 circular bubble. */
      !fabOpen ? E("button",{
        type:"button",
        className:"piaris-fab-bubble",
        "aria-label":"Open free accessibility scan request",
        onClick:function(){ setFabOpen(true); },
        style:{
          width:60,height:60,borderRadius:"50%",
          background:"linear-gradient(135deg,#4338CA 0%,#6366F1 50%,#06B6D4 100%)",
          border:"none",cursor:"pointer",color:"#FFF",
          display:"flex",alignItems:"center",justifyContent:"center",
          padding:0
        }
      },
        E("svg",{width:26,height:26,viewBox:"0 0 24 24",fill:"none",
          stroke:"#FFF",strokeWidth:2,strokeLinecap:"round",strokeLinejoin:"round","aria-hidden":"true"
        },
          E("path",{d:"M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"})
        )
      ) : null,

      /* OPEN state \u2014 panel. */
      fabOpen ? E("div",{
        className:"piaris-fab-panel",
        role:"dialog",
        "aria-labelledby":"piaris-fab-title",
        style:{
          width:isMobile?"calc(100vw - 32px)":340,
          maxWidth:380,
          background:"#FFFFFF",
          borderRadius:16,
          boxShadow:"0 24px 60px rgba(0,0,0,0.25)",
          border:"1px solid "+P.bdr,
          overflow:"hidden"
        }
      },
        /* Panel header */
        E("div",{style:{
          background:"linear-gradient(135deg,#4338CA 0%,#6366F1 50%,#06B6D4 100%)",
          color:"#FFF",padding:"14px 16px",
          display:"flex",alignItems:"center",justifyContent:"space-between",gap:10
        }},
          E("div",{style:{display:"flex",alignItems:"center",gap:10,minWidth:0,flex:1}},
            E(ProductLockup,{size:"tiny",white:true}),
            E("div",{style:{minWidth:0}},
              E("div",{id:"piaris-fab-title",style:{fontSize:14,fontWeight:800,lineHeight:1.2}},
                fabTab==="contact"?"Get in touch":"Free accessibility scan"),
              E("div",{style:{fontSize:11,opacity:0.9,marginTop:2}},
                fabTab==="contact"?"We'll reply by email":"Report in your inbox in 48 hrs")
            )
          ),
          E("button",{
            type:"button",
            "aria-label":"Close",
            onClick:function(){ setFabOpen(false); },
            style:{background:"transparent",border:"none",color:"#FFF",
              cursor:"pointer",fontSize:18,opacity:0.85,padding:6,
              flexShrink:0,minWidth:32,minHeight:32,lineHeight:1}
          },"\u2715")
        ),

        /* Panel body \u2014 form or success state */
        !fabForm.sent ? E("div",{role:"tablist","aria-label":"Bubble sections",style:{display:"flex",borderBottom:"1px solid "+P.bdr}},[["scan","Free scan"],["contact","Contact us"]].map(function(t){var active=fabTab===t[0];return E("button",{key:t[0],type:"button",role:"tab","aria-selected":active?"true":"false",onClick:function(){setFabTab(t[0]);setFabForm(function(s){var n={};for(var k in s)n[k]=s[k];n.error=null;return n;});},style:{flex:1,padding:"10px 8px",border:"none",borderBottom:active?"2px solid #4338CA":"2px solid transparent",background:active?"#EEF2FF":"#FFF",color:active?"#4338CA":P.slateM,fontSize:12,fontWeight:active?800:600,cursor:"pointer",fontFamily:"inherit"}},t[1]);})) : null,
        fabForm.sent ? E("div",{style:{padding:"24px 18px",textAlign:"center"}},
          E("div",{"aria-hidden":"true",style:{
            display:"inline-flex",width:48,height:48,
            alignItems:"center",justifyContent:"center",
            background:"#059669",color:"#FFF",borderRadius:"50%",
            fontSize:24,fontWeight:800,marginBottom:10
          }},"\u2713"),
          E("div",{style:{fontSize:15,fontWeight:800,color:P.slate,marginBottom:6}},
            "Thanks \u2014 your "+(fabForm.sentKind==="contact"?"message":"request")+" is in."),
          E("div",{style:{fontSize:12,color:P.slateM,lineHeight:1.5}},
            (fabForm.sentKind==="contact"?"We'll get back to you by email shortly.":"We'll email you a free accessibility scan report within 48 hours."))
) : fabTab==="contact" ? E("form",{onSubmit:submitFabInquiry,style:{padding:"14px 16px"}},
          E("div",{style:{marginBottom:10}},E("label",{htmlFor:"piaris-fab-cname",style:{display:"block",fontSize:11,fontWeight:700,color:P.slate,marginBottom:4}},"Your name"),E("input",{id:"piaris-fab-cname",type:"text",autoComplete:"name",required:true,maxLength:120,value:fabForm.name,onChange:function(e){setFabField("name",e.target.value);},style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid "+P.bdr,fontSize:13,minHeight:36,fontFamily:"inherit"}})),
          E("div",{style:{marginBottom:10}},E("label",{htmlFor:"piaris-fab-cemail",style:{display:"block",fontSize:11,fontWeight:700,color:P.slate,marginBottom:4}},"Email"),E("input",{id:"piaris-fab-cemail",type:"email",autoComplete:"email",inputMode:"email",required:true,maxLength:254,value:fabForm.email,onChange:function(e){setFabField("email",e.target.value);},style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid "+P.bdr,fontSize:13,minHeight:36,fontFamily:"inherit"}})),
          E("div",{style:{marginBottom:12}},E("label",{htmlFor:"piaris-fab-cmsg",style:{display:"block",fontSize:11,fontWeight:700,color:P.slate,marginBottom:4}},"Message"),E("textarea",{id:"piaris-fab-cmsg",required:true,maxLength:4000,rows:4,value:fabForm.message,onChange:function(e){setFabField("message",e.target.value);},placeholder:"How can we help?",style:{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid "+P.bdr,fontSize:13,fontFamily:"inherit",resize:"vertical",minHeight:80}})),
          fabForm.error?E("div",{role:"alert",style:{padding:"8px 10px",marginBottom:10,background:"#FEE2E2",border:"1px solid #FCA5A5",borderRadius:6,color:"#991B1B",fontSize:11,fontWeight:600}},fabForm.error):null,
          E("button",{type:"submit",disabled:fabForm.submitting,"aria-busy":fabForm.submitting?"true":"false",style:{width:"100%",padding:"11px 16px",borderRadius:8,border:"none",background:fabForm.submitting?"#94A3B8":"#4338CA",color:"#FFF",fontSize:13,fontWeight:800,cursor:fabForm.submitting?"wait":"pointer",minHeight:42,fontFamily:"inherit"}},fabForm.submitting?"Sending…":"Send message")
        ) : E("form",{onSubmit:submitFabForm,style:{padding:"14px 16px"}},
          E("div",{style:{marginBottom:10}},
            E("label",{htmlFor:"piaris-fab-name",style:{display:"block",fontSize:11,fontWeight:700,color:P.slate,marginBottom:4}},"Your name"),
            E("input",{
              id:"piaris-fab-name",type:"text",autoComplete:"name",
              required:true,maxLength:120,
              value:fabForm.name,
              onChange:function(e){ setFabField("name", e.target.value); },
              style:{width:"100%",padding:"8px 10px",borderRadius:6,
                border:"1px solid "+P.bdr,fontSize:13,minHeight:36,fontFamily:"inherit"}
            })
          ),
          E("div",{style:{marginBottom:10}},
            E("label",{htmlFor:"piaris-fab-email",style:{display:"block",fontSize:11,fontWeight:700,color:P.slate,marginBottom:4}},"Work email"),
            E("input",{
              id:"piaris-fab-email",type:"email",autoComplete:"email",inputMode:"email",
              required:true,maxLength:254,
              value:fabForm.email,
              onChange:function(e){ setFabField("email", e.target.value); },
              style:{width:"100%",padding:"8px 10px",borderRadius:6,
                border:"1px solid "+P.bdr,fontSize:13,minHeight:36,fontFamily:"inherit"}
            })
          ),
          E("div",{style:{marginBottom:12}},
            E("label",{htmlFor:"piaris-fab-domain",style:{display:"block",fontSize:11,fontWeight:700,color:P.slate,marginBottom:4}},"Website domain"),
            E("input",{
              id:"piaris-fab-domain",type:"text",autoComplete:"url",
              required:true,placeholder:"example.gov",
              value:fabForm.domain,
              onChange:function(e){ setFabField("domain", e.target.value); },
              style:{width:"100%",padding:"8px 10px",borderRadius:6,
                border:"1px solid "+P.bdr,fontSize:13,minHeight:36,fontFamily:"inherit"}
            })
          ),
          E("label",{
            htmlFor:"piaris-fab-consent",
            style:{display:"flex",gap:8,alignItems:"flex-start",
              padding:"8px 10px",background:P.bgS,borderRadius:8,
              fontSize:11,color:P.slate,cursor:"pointer",marginBottom:12,lineHeight:1.45}
          },
            E("input",{
              id:"piaris-fab-consent",type:"checkbox",required:true,
              checked:fabForm.consent,
              onChange:function(e){ setFabField("consent", e.target.checked); },
              style:{marginTop:2,flexShrink:0}
            }),
            E("span",null,"OK to email me about my scan results.")
          ),
          fabForm.error ? E("div",{
            role:"alert",
            style:{padding:"8px 10px",marginBottom:10,background:"#FEE2E2",
              border:"1px solid #FCA5A5",borderRadius:6,color:"#991B1B",
              fontSize:11,fontWeight:600}
          }, fabForm.error) : null,
          E("button",{
            type:"submit",
            disabled:fabForm.submitting,
            "aria-busy":fabForm.submitting?"true":"false",
            style:{
              width:"100%",padding:"11px 16px",borderRadius:8,
              border:"none",
              background:fabForm.submitting?"#94A3B8":"#4338CA",
              color:"#FFF",fontSize:13,fontWeight:800,
              cursor:fabForm.submitting?"wait":"pointer",
              minHeight:42,fontFamily:"inherit"
            }
          }, fabForm.submitting ? "Submitting\u2026" : "Request my free scan"),
          E("div",{style:{textAlign:"center",marginTop:10}},
            E("button",{
              type:"button",
              onClick:dismissFabForever,
              style:{background:"transparent",border:"none",
                color:P.slateM,fontSize:10,cursor:"pointer",
                textDecoration:"underline",padding:4}
            },"Don't show this again")
          )
        )
      ) : null
    ),

    /* Toast notification */
    toast?E("div",{role:"alert","aria-live":"assertive",style:{position:"fixed",bottom:24,right:24,zIndex:9999,padding:"12px 20px",borderRadius:10,
      background:toast.type==="error"?"#DC2626":"#059669",color:"#fff",fontSize:12,fontWeight:700,
      boxShadow:"0 8px 32px rgba(0,0,0,.25)",display:"flex",alignItems:"center",gap:8,
      animation:"slideIn .3s ease-out"}},
      E("span",null,toast.msg),
      E("button",{onClick:function(){setToast(null)},"aria-label":"Dismiss notification",style:{background:"none",border:"none",color:"#fff",fontSize:14,cursor:"pointer",padding:0,marginLeft:8}},"\u2715")):null
  );
}
